import { GlobalTicker } from "./components/intelligence/GlobalTicker";
import SpendGovernanceView from "./SpendGovernanceView";
import ComplianceView from "./ComplianceView";
import { RESEARCH_JSON_PROMPT } from "./lib/ProjectReportDocument";
import { initA11y, announce, announceAIResponse, announceLoading, announceBoardroomPhase } from "./a11y";
import VoiceEngine from "./VoiceEngine";
import BoardroomView from "./BoardroomView";
import FundingIntelligence from "./FundingIntelligence";
import ServiceDesk from "./ServiceDesk";
import TokenAnalytics, { saveRecord, estimateCost, saveUnitRecord } from "./TokenAnalytics";
import AdminConsole from "./AdminConsole";
import SupportWidget from "./SupportWidget";
import MyAccount from "./MyAccount";
import Workspace from "./Workspace";
import LiveBoardroom from "./LiveBoardroom";
import { modelsFor as wsModelsFor } from "./lib/AIModels";
import PulseGovernance from "./Pulse";
import TokenBadge from "./components/TokenBadge";
import AIAgents from "./AIAgents";
import AgenticWorkflows from "./AgenticWorkflows";
import { getExecutivesCached } from "./lib/executives";
import { supabase } from "./lib/supabase";
import PROV, { availableFor as provAvailableFor, cannotDoMessage as provCannotDo, type Capability } from "./lib/Providers";
import { WorkspaceMemory } from "./lib/WorkspaceMemory";
import ReadAloudButton from "./components/ReadAloudButton";
import DesignCentre from "./components/DesignCentre";
import { generateExcel, generatePptx, generatePdf, generateDocx } from "./lib/GenerationService";
import { ENGINE_ENABLED, runPipeline, classifyDomain, classifyIntent, selectFramework, selfReview, classifyEvidence } from "./lib/IntelligenceEngine";
import { buildScaffoldPrompt, buildViabilityPrompt, inferArchetype } from "./lib/BusinessScaffold";
import { scanSuppliedInputs, buildIntakePrompt, buildRegisterInjection, INTAKE_FAILED_NOTICE } from "./lib/IntakeRegister";
import { runSearch, formatResultsForPrompt, RETRIEVED_RESULTS_RULES, hasExternalSearch, SEARCH_PROVIDERS, estimateSearchCost } from "./lib/SearchProviders";
import { STAGES, PRESETS, DEFAULT_PROFILE, resolveStageProvider, stageModelOverride, estimateSessionCost, fmtMoney, stageProviderChain, PREMIUM_PROVIDERS } from "./lib/ModelRouting";
import { attemptResultFor, executionSummary, answerUserQuestion, type ProviderAttempt } from "./lib/DecisionIntegrity";
import { handoffContext, fmt as fmtCanon, type DecisionHandoff } from "./lib/DecisionCockpit";
import { parseUserInputs } from "./lib/DecisionExperience";
import { TimeMachineSimulator, OperatingPlanner } from "./DecisionSimulator";
import JourneyShell, { type ExplainLevel } from "./JourneyShell";
import { actualsFromLedger as actualsFromLedgerJ } from "./lib/DecisionCockpit";
import { synthesisPacketFor, analyseStage, makeCallId, appendContinuation, findDuplicate, deriveStatus, isExcludedFromSynthesis, isComplete, validateContribution, completenessReport, renderCompletenessReport } from "./lib/BoardroomIntegrity";
import { parseResearchEvidence, assembleExecutiveContext, extractLedgerEntry, renderLedger, renderEvidence, budgetCheck, type LedgerEntry, emptyIntelligence, mergeIntoIntelligence, discoverResearchOpportunities, recordUserDecision, classifyFollowUp, renderIntelligence, adaptiveOutputBudget, taskKindFor, type IntelligenceState } from "./lib/ContextIntelligence";
import { extractFacts, saveFacts, fetchFacts, formatLibraryFacts, logQuery } from "./lib/KnowledgeLibrary";
import { detectDocumentRequest, buildDocumentBrief, buildSynthesisOverride, suggestedFormats, CONSULTING_STANDARD } from "./lib/DocumentLibrary";
import { NVIDIA_DEFAULT_MODEL, nvidiaModelOptions, nvidiaShouldReason, nvidiaTokenBudget } from "./lib/NvidiaModels";
import { getDisclosure, trainingLabel, providersNeedingCaution, attributionLine } from "./lib/ProviderDisclosure";
import CostArchitecture from "./CostArchitecture";
import ShareButtons from "./ShareButtons";
import { loadCostContext, getCostBrief, publishCostDiagnosis, clearCostContext } from "./lib/CostContext";
// SHEETS is loaded only when opened (the spreadsheet engine is large), so the rest of the site stays as fast as before.
const SheetsView = lazy(() => import("./SheetsView"));

// Intelligence Engine — evidence audit line appended to final deliverables (no AI call, fail-safe)
function ieEvidenceAudit(text:string):string{
  try{
    const evTags=classifyEvidence(text||"");
    if(!evTags.length)return "";
    const cnt:{[k:string]:number}={};evTags.forEach(t=>{cnt[t.category]=(cnt[t.category]||0)+1;});
    return "\n\n---\n**Evidence audit (Intelligence Engine):** "+Object.entries(cnt).map(([k,v])=>v+" "+k).join(" · ");
  }catch{return "";}
}

// ─── DECISION HISTORY ───────────────────────────────────────────────────────
// Boardroom decisions saved via WorkspaceMemory; surfaced in future sessions
// on similar topics. Pure local logic — zero AI calls.
function saveDecisionRecord(rec:{[k:string]:unknown}):void{
  try{
    const hist=WorkspaceMemory.get<any[]>("cos-decision-history")||[];
    hist.unshift(rec);
    WorkspaceMemory.set("cos-decision-history",hist.slice(0,25));
  }catch{/* storage full — silent */}
}
function extractRecommendationSnippet(syn:string):string{
  try{
    const m=(syn||"").match(/##\s*Quantified Recommendation\s*\n([\s\S]*?)(\n##|$)/i);
    const raw=(m?m[1]:(syn||"")).replace(/[#*|>\-]/g," ").replace(/\s+/g," ").trim();
    return raw.slice(0,300);
  }catch{return "";}
}
function buildDecisionHistoryContext(question:string):string{
  try{
    const hist=WorkspaceMemory.get<any[]>("cos-decision-history")||[];
    if(!hist.length)return "";
    const words=((question||"").toLowerCase().match(/[a-z]{4,}/g)||[]);
    if(!words.length)return "";
    const scored=hist.map(h=>{
      const txt=(String(h.question||"")+" "+String(h.recommendation||"")).toLowerCase();
      return {h,score:words.filter(w=>txt.includes(w)).length};
    }).filter(x=>x.score>=2).sort((a,b)=>b.score-a.score).slice(0,2);
    if(!scored.length)return "";
    return "\n\nPAST BOARD DECISIONS ON SIMILAR TOPICS (reference these; explicitly flag if today's question conflicts with, repeats, or should build on a past decision):\n"+
      scored.map(x=>"- ["+String(x.h.ts||"").slice(0,10)+"] Q: \""+String(x.h.question||"").slice(0,120)+"\" → Status: "+String(x.h.status||"n/a")+". Decision: "+String(x.h.recommendation||"").slice(0,200)).join("\n")+"\n";
  }catch{return "";}
}
import BusinessExecutionEngine, { type ExecutionPlan, type DeliverableSpec, repairTruncatedJson, pdfSafeText } from "./lib/BusinessExecutionEngine";
import DocIQ, { AUDIENCE_OPTIONS, type AudienceId } from "./lib/DocumentIntelligence";
import { buildBlueprint, type BlueprintFormat } from "./lib/BlueprintBuilder";

// ─── SESSION GATE ────────────────────────────────────────────────────────────
async function checkSessionGate(): Promise<{allowed:boolean;reason?:string;plan?:string;used?:number;limit?:number}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { allowed: true };
    const { data, error } = await supabase.rpc('check_and_increment_session', { p_user_id: user.id });
    if (error) {
      console.warn('[OIQ] Session gate error:', error.message);
      return { allowed: true };
    }
    return data as {allowed:boolean;reason?:string;plan?:string;used?:number;limit?:number};
  } catch(e) {
    console.warn('[OIQ] Session gate exception:', e);
    return { allowed: true };
  }
}

async function saveBYOKeyToSupabase(apiKey: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    // API keys are now ENCRYPTED at rest. The plaintext columns have been
    // deleted from the database, so this write would fail. save_my_api_keys
    // encrypts with a master key held in Supabase Vault - outside the database -
    // and can only ever write the caller's OWN row.
    await supabase.rpc('save_my_api_keys', { p_keys: { byo: apiKey || '' }, p_admin: false });
  } catch(e) {
    console.warn('[OIQ] Failed to save BYO key:', e);
  }
}
import Ledger, { type JournalEntry, COA, computeBalances, getAllAccounts } from "./Ledger";
import { deriveFinancials, reconcile, buildFinancialBrief, financialHeadline, parseAmount } from "./lib/FinancialEngine";
import FinanceSuite from "./FinanceSuite";
import Jarvis from "./Jarvis";
import JarvisLab from "./JarvisLab";
import ErrorBoundary from "./components/ErrorBoundary";
import CommandCenter from "./CommandCenter";
import Dispatch, { type DispatchTemplate } from "./Dispatch";
import ActionTracker, { ExtractReviewModal, extractItemsFromJSON, EXTRACTION_PROMPT, type ActionItem, type ExtractedItem } from "./ActionTracker";
import type { Executive } from "./lib/executives";
import {
  COMPRESSION_ENABLED,
  BENCHMARK_MODE,
  compressLevelOutput,
  formatCompressedContextForPrompt,
  type CompressedContext,
} from "./lib/ContextCompressor";

import {
  estimateTokens,
  attachBenchmarkToWindow,
  startBenchmarkSession,
  completeBenchmarkSession,
  logLevelBenchmark,
  markRateLimitHit,
  type BenchmarkSession,
} from "./lib/TokenCounter";

import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import {
  markProviderExhausted,
  getActiveProvider,
  isRateLimit,
  areBothExhausted,
  waitWithCountdown,
  saveResumeState,
  loadResumeState,
  clearResumeState,
  type ResumeState,
} from "./lib/ProviderManager";

const VERSION = "4.3.0";
// ─── SECURITY AUDIT C-1 (CRITICAL) ──────────────────────────────────────────
// These four lines used to read VITE_GEMINI_API_KEY, VITE_GROQ_API_KEY,
// VITE_CLAUDE_API_KEY and VITE_FAL_API_KEY. ANY variable prefixed with VITE_ is
// INLINED INTO THE PUBLIC JAVASCRIPT BUNDLE at build time - it is not a runtime
// secret, it is a string anyone can read in DevTools. The diagnostic console
// line below them also announced which keys existed.
//
// A VITE_ variable can NEVER hold a credential. System-supplied keys must sit
// behind a Pages Function, the way /api/nvidia does, where the key stays on the
// server. Until such a proxy exists for these providers, the app uses the user's
// own key only - which is the BYOK model this product is built on anyway.
//
// ACTION REQUIRED ALONGSIDE THIS EDIT: rotate the Gemini, Groq and fal.ai keys,
// then delete VITE_GEMINI_API_KEY, VITE_GROQ_API_KEY, VITE_CLAUDE_API_KEY and
// VITE_FAL_API_KEY from Cloudflare Pages environment variables.
const SYS_GEMINI = "";
const SYS_GROQ = "";
const SYS_CLAUDE = "";
const USE_SYS_KEY = false;
const EFF_GEMINI = "";
const EFF_GROQ = "";
const EFF_CLAUDE = "";
const EFF_FAL = "";
const BRAND = "OrchestrIQ";
const TAGLINE = "The orchestration layer of intelligent business.";

const STAGES = [{id:"idea",l:"Idea",ic:"💡"},{id:"formation",l:"Formation",ic:"📋"},{id:"mvp",l:"MVP",ic:"🚀"},{id:"funding",l:"Funding",ic:"💰"},{id:"growth",l:"Growth",ic:"📈"},{id:"mature",l:"Mature",ic:"🏛️"}];
const CURRENCIES = [{code:"INR",sym:"₹",name:"Indian Rupee"},{code:"USD",sym:"$",name:"US Dollar"},{code:"EUR",sym:"€",name:"Euro"},{code:"GBP",sym:"£",name:"British Pound"},{code:"AUD",sym:"A$",name:"Australian Dollar"},{code:"CAD",sym:"C$",name:"Canadian Dollar"},{code:"SGD",sym:"S$",name:"Singapore Dollar"},{code:"AED",sym:"AED",name:"UAE Dirham"},{code:"CHF",sym:"Fr",name:"Swiss Franc"}];
const MODELS = {
  claude:{name:"Claude",company:"Anthropic",model:"claude-haiku-4-5-20251001",placeholder:"sk-ant-...",color:"#D97757",keyUrl:"https://console.anthropic.com/settings/keys"},
  openai:{name:"ChatGPT",company:"OpenAI",model:"gpt-4o",placeholder:"sk-...",color:"#10A37F",keyUrl:"https://platform.openai.com/api-keys"},
  gemini:{name:"Gemini",company:"Google",model:"gemini-3.6-flash",placeholder:"AIza...",color:"#4285F4",keyUrl:"https://aistudio.google.com/app/apikey"},
  groq:{name:"Groq",company:"Groq",model:"openai/gpt-oss-120b",placeholder:"gsk_...",color:"#F97316",keyUrl:"https://console.groq.com/keys"},
  deepseek:{name:"DeepSeek",company:"DeepSeek AI",model:"deepseek-v4-flash",placeholder:"sk-...",color:"#2563EB",keyUrl:"https://platform.deepseek.com/api_keys",note:"Low cost · Strong reasoning · Available in India"},
  kimi:{name:"Kimi",company:"Moonshot AI",model:"moonshot-v1-8k",placeholder:"sk-...",color:"#8B5CF6",keyUrl:"https://platform.moonshot.cn/console/api-keys",note:"Fast · Affordable · Strong multilingual"},
  stability:{name:"Stability AI",company:"Stability AI",model:"stable-diffusion-xl-1024-v1-0",placeholder:"sk-...",color:"#EC4899",keyUrl:"https://platform.stability.ai/account/credits",note:"Image generation · ~₹3/image · Optional"},
  fal:{name:"fal.ai",company:"fal.ai",placeholder:"key-...",color:"#7C3AED",keyUrl:"https://fal.ai/dashboard/keys"},
  nvidia:{name:"NVIDIA",company:"NVIDIA",model:NVIDIA_DEFAULT_MODEL,placeholder:"nvapi-... (optional — free tier works without one)",color:"#76B900",keyUrl:"https://build.nvidia.com/",note:"Free tier needs no key \u00b7 add your own for unlimited use and document generation"},
};
// This list was hardcoded and had gone stale: it still offered nemotron-4-340b
// and llama-3.1-nemotron-70b, neither of which NVIDIA serves any more, while the
// entire Nemotron 3 family was missing. It now comes from src/lib/NvidiaModels.ts,
// so the dropdown can never drift from what the proxy will actually accept.
const NVIDIA_MODELS=nvidiaModelOptions();
// ─── DONATION QR ────────────────────────────────────────────────────────────
// Paste your QR code as a base64 data URI between the quotes below to hard-code it,
// e.g. "data:image/png;base64,iVBORw0KGgo...". Until then, upload it once via
// Settings › Donation (it saves to your device and persists across sessions).
const DEFAULT_QR = "";
// ─── TASK-BASED INTELLIGENT ROUTING ─────────────────────────────────────────
// Maps task type → provider priority (first available key wins, fallback continues).
// DeepSeek = default for general text (cheapest). Claude Sonnet = specialist tasks.
// fal.ai = ALL image and video generation (500+ models, one key).
// Add future providers here — routing inherits automatically.
const NVIDIA_FALLBACK=["nvidia"];
const TASK_ROUTING: Record<string,string[]> = {
  // ── MEDIA (non-text — fal.ai is the exclusive gateway) ──────────────────
  image_gen:      ["openai","fal","stability"],              // THE FIX: OpenAI (GPT Image) is now tried FIRST whenever a key is configured - fal.ai and Stability AI remain as fallbacks if OpenAI has no key or fails
  video_gen:      ["fal"],                                  // fal.ai: Kling, Seedance, Wan, Veo, Sora — no second video provider wired in yet
  diagram:        ["fal","openai"],                         // Ideogram v3 via fal for text-accurate diagrams
  // ── SPECIALIST TEXT (Claude Sonnet = best quality) ───────────────────────
  excel_advanced: ["deepseek","claude","openai","gemini","groq"],  // Phase 5: DeepSeek first (~20x cheaper for this structured/formula task), Claude as automatic fallback/upgrade
  powerpoint:     ["claude","openai","deepseek","gemini","groq"],  // Structured decks, slide content
  research:       ["claude","gemini","openai","deepseek","groq"],  // Claude has built-in web search
  financial:      ["claude","deepseek","openai","gemini","groq"],  // P&L, forecast, variance, audit
  audit:          ["claude","deepseek","openai","gemini","groq"],  // Compliance, SOX, workpapers
  vision:         ["claude","gemini","openai","groq"],              // Photo/image analysis — DeepSeek excluded
  // ── CODE (DeepSeek = best value for code generation) ────────────────────
  code:           ["deepseek","claude","groq","openai","gemini"],
  // ── GENERAL TEXT (cheapest capable provider first) ───────────────────────
  creative:       ["deepseek","groq","gemini","claude","openai"],
  general:        ["deepseek","groq","gemini","claude","openai"],
};

// Models to use when a task routes to Claude (default = haiku, premium = sonnet)
// Sonnet is used for tasks that require deep reasoning, long structured outputs.
const CLAUDE_TASK_MODEL: Record<string,string> = {
  excel_advanced: "claude-sonnet-4-5-20250929",
  powerpoint:     "claude-sonnet-4-5-20250929",
  financial:      "claude-sonnet-4-5-20250929",
  audit:          "claude-sonnet-4-5-20250929",
  research:       "claude-sonnet-4-5-20250929",
};

// When DeepSeek handles one of these task types, use the stronger Pro model
// instead of the default Flash model. Everything else stays on Flash (cheap, fast).
const DEEPSEEK_TASK_MODEL: Record<string,string> = {
  excel_advanced: "deepseek-v4-pro",
  financial:      "deepseek-v4-pro",
  audit:          "deepseek-v4-pro",
  code:           "deepseek-v4-pro",
};

// ─── RESPONSE QUALITY TIER ───────────────────────────────────────────────
// User-selected in Settings → API. Module-scoped (like LIVE_RATES elsewhere
// in this file) so the plain routing functions below can read it without
// needing React state threaded through every call site.
let RESPONSE_QUALITY: "standard"|"professional"|"excellent" = "professional";

// Provider priority PER QUALITY TIER, for the task types where quality tier
// genuinely changes which provider should lead. Tasks not listed here keep
// using TASK_ROUTING exactly as before (unchanged, zero risk).
const QUALITY_TIER_ROUTING: Record<string,Record<string,string[]>> = {
  standard: {
    general:  ["nvidia","deepseek","groq","gemini"],
    creative: ["nvidia","deepseek","groq","gemini"],
    research: ["gemini","claude","groq"],
  },
  professional: {
    excel_advanced: ["deepseek","claude","openai","gemini"],
    powerpoint:     ["claude","deepseek","openai","gemini"],
    financial:      ["deepseek","claude","openai","gemini"],
    audit:          ["deepseek","claude","openai","gemini"],
    research:       ["claude","gemini","openai"],
    code:           ["deepseek","claude","groq"],
    general:        ["deepseek","claude","gemini"],
    creative:       ["deepseek","claude","gemini"],
  },
  excellent: {
    excel_advanced: ["claude","openai","deepseek"],
    powerpoint:     ["claude","openai","deepseek"],
    financial:      ["claude","openai","deepseek"],
    audit:          ["claude","openai","deepseek"],
    research:       ["claude","openai","gemini"],
    code:           ["claude","deepseek","openai"],
    general:        ["claude","openai","deepseek"],
    creative:       ["claude","openai","deepseek"],
  },
};

// Auto-classify the task type from prompt + system context.
// Called automatically inside callMulti — no change needed at call sites.
// ─── PROVIDER CAPABILITY REGISTRY ────────────────────────────────────────────
// Pure data. Adding a provider = one entry here + a key field; zero logic edits.
const PROVIDER_META = {
  claude:   {name:"Claude",   cost:"$$$",speed:"medium",quality:"best", blurb:"Best for documents, Excel, decks, deep reasoning"},
  openai:   {name:"ChatGPT",  cost:"$$$",speed:"medium",quality:"best", blurb:"Strong all-rounder; images via GPT Image"},
  deepseek: {name:"DeepSeek", cost:"$",  speed:"fast",  quality:"great",blurb:"Best value for general work and code"},
  gemini:   {name:"Gemini",   cost:"$",  speed:"fast",  quality:"great",blurb:"Fast, generous free tier, good research"},
  groq:     {name:"Groq",     cost:"$",  speed:"fast",  quality:"good", blurb:"Fastest responses; great for drafts"},
  fal:      {name:"fal.ai",   cost:"$$", speed:"medium",quality:"best", blurb:"Images & video (Flux, Kling, Veo)"},
  nvidia:   {name:"NVIDIA", cost:"Free tier or your own key",speed:"medium",quality:"good", blurb:"Works with no key · add your own for unlimited use"},
} as Record<string,{name:string;cost:string;speed:string;quality:string;blurb:string}>;
// Tasks where the user's Primary AI leads; specialists lead everywhere else.
const PRIMARY_LED_TASKS=["general","creative","code","research"];
// Primary-aware routing: for specialist tasks the top specialists go first,
// then the user's Primary AI, then the remaining chain. The user's model
// choice is honored for orchestration; capability quality for specialists.
function resolveRoute(task:string,primary:string):string[]{
  const tierChain=QUALITY_TIER_ROUTING[RESPONSE_QUALITY]?.[task];
  const chain=tierChain||TASK_ROUTING[task]||TASK_ROUTING.general;
  const ordered=tierChain
    ? chain
    : PRIMARY_LED_TASKS.includes(task)
    ? [primary,...chain]
    : [...chain.slice(0,2),primary,...chain];
  const withFallback=(task==="image_gen"||task==="video_gen")?ordered:[...ordered,...NVIDIA_FALLBACK];
  return withFallback.filter((p,i)=>p&&withFallback.indexOf(p)===i);
}

function detectTaskType(prompt: string, context = ""): string {
  const t = (prompt + " " + context).toLowerCase();
  // THE FIX: the original phrasing only matched a request that literally
  // said "image", "photo" or "picture". "Design an Instagram post about our
  // launch" or "create a LinkedIn marketing campaign" describe a visual just
  // as clearly, but neither used those words - so they were misrouted as
  // ordinary text requests. Widened to also catch marketing/campaign/social
  // post language, which is exactly how a real request like this gets phrased.
  if (/\b(generate\s+(?:an?\s+)?image|create\s+(?:an?\s+)?(?:image|photo|picture)|make\s+(?:an?\s+)?image|draw\s+(?:an?\s+)?image|render\s+(?:an?\s+)?(?:image|photo)|design\s+(?:an?\s+)?logo\s+for\s+me|(?:design|create|make)\s+(?:an?\s+)?(?:instagram|linkedin|facebook|social(?:\s+media)?)\s+(?:post|ad|banner|creative|graphic)|(?:marketing|social(?:\s+media)?|ad)\s+(?:campaign|creative|graphic|visual)s?\s+(?:for|on|about)?)\b/.test(t)) return "image_gen";
  if (/\b(generate\s+(?:an?\s+)?video|create\s+(?:an?\s+)?video\s+(?:for|of|showing)|make\s+(?:an?\s+)?video|produce\s+(?:an?\s+)?video|render\s+(?:an?\s+)?video|animate\s+this|generate\s+(?:an?\s+)?reel)\b/.test(t)) return "video_gen";
  if (/\b(draw\s+(?:me\s+)?(?:a\s+)?diagram|generate\s+(?:a\s+)?(?:flowchart|diagram)|create\s+(?:a\s+)?(?:flowchart|org\s+chart|process\s+map|architecture\s+diagram))\b/.test(t)) return "diagram";
  if (/\b(p&l|profit.*loss|balance.*sheet|cash.*flow|forecast|budget.*model|ebitda|irr|npv|financial.*model|variance.*analysis|mis.*report|revenue.*projection)\b/.test(t)) return "financial";
  if (/\b(audit|sox|itgc|compliance|risk.*register|internal.*control|workpaper|finding|assurance|concur|servicenow)\b/.test(t)) return "audit";
  if (/\b(photo|ocr|scan.*document|extract.*from.*image|read.*image|visual.*analysis)\b/.test(t)) return "vision";
  if (/\b(code|function|script|debug|python|javascript|typescript|sql|api.*endpoint|algorithm|unit.*test|refactor)\b/.test(t)) return "code";
  if (/\b(write|draft|compose|email.*to|linkedin|blog.*post|marketing.*copy|press.*release|creative.*writing)\b/.test(t)) return "creative";
  return "general";
}



const DONATION_PRESETS = [10,50,100,500,1000];

// ─── FEATURE 1: THEME SYSTEM ────────────────────────────────────────────────
// Each theme maps to CSS custom properties. Dark is default and untouched.
const THEMES = {
  editorial:{name:"Executive Editorial",ic:"📜",vars:{
    "--bg":"#EDEAE3","--bg2":"#FBFAF8","--panel":"#FFFFFF","--panel2":"#F7F5F1","--border":"#E4E0D8","--border2":"#EFEBE4",
    "--text":"#1C2A24","--text2":"#4A453D","--text3":"#6B6459","--muted":"#8A8378","--muted2":"#B5AEA2",
    "--accent":"#C9A961","--code":"#F7F5F1","--scroll":"#DDD8CE"}},
  dark:{name:"Dark Mode",ic:"🌙",default:true,vars:{
    "--bg":"#0a0e1a","--bg2":"#0c1120","--panel":"#131825","--panel2":"#0a0e1a","--border":"#1a2030","--border2":"#14192a",
    "--text":"#F1F5F9","--text2":"#A0AAC0","--text3":"#8892B0","--muted":"#5A6480","--muted2":"#3A4060",
    "--accent":"#14B8A6","--code":"#080c18","--scroll":"#1a2030"}},
  light:{name:"Light Mode",ic:"☀️",vars:{
    "--bg":"#F4F6FB","--bg2":"#FFFFFF","--panel":"#FFFFFF","--panel2":"#F4F6FB","--border":"#E2E8F0","--border2":"#EDF1F7",
    "--text":"#0F172A","--text2":"#334155","--text3":"#475569","--muted":"#64748B","--muted2":"#94A3B8",
    "--accent":"#0D9488","--code":"#F1F5F9","--scroll":"#CBD5E1"}},
  enlightened:{name:"Enlightened",ic:"✨",vars:{
    "--bg":"#FBF9F4","--bg2":"#FFFFFF","--panel":"#FFFFFF","--panel2":"#FAF7F0","--border":"#EAE3D5","--border2":"#F0EBE0",
    "--text":"#2A2419","--text2":"#5C5340","--text3":"#6E6450","--muted":"#9A8E76","--muted2":"#B8AD96",
    "--accent":"#B8860B","--code":"#F5F1E8","--scroll":"#D6CDB8"}},
  blue:{name:"Professional Blue",ic:"🔷",vars:{
    "--bg":"#0B1426","--bg2":"#0E1A30","--panel":"#13233F","--panel2":"#0B1426","--border":"#1E3A5F","--border2":"#16294A",
    "--text":"#EAF2FF","--text2":"#A8C2E0","--text3":"#8FAFD4","--muted":"#5B7BA6","--muted2":"#3E5878",
    "--accent":"#3B82F6","--code":"#081020","--scroll":"#1E3A5F"}},
  gray:{name:"Executive Gray",ic:"⬛",vars:{
    "--bg":"#1A1C20","--bg2":"#202327","--panel":"#26292E","--panel2":"#1A1C20","--border":"#34383F","--border2":"#2A2E34",
    "--text":"#F0F1F3","--text2":"#B4B8C0","--text3":"#9CA1AB","--muted":"#6B7079","--muted2":"#4A4E56",
    "--accent":"#8B95A5","--code":"#141619","--scroll":"#34383F"}},
  contrast:{name:"High Contrast",ic:"◼️",vars:{
    "--bg":"#000000","--bg2":"#000000","--panel":"#0A0A0A","--panel2":"#000000","--border":"#FFFFFF","--border2":"#666666",
    "--text":"#FFFFFF","--text2":"#FFFF00","--text3":"#00FFFF","--muted":"#FFFFFF","--muted2":"#CCCCCC",
    "--accent":"#00FF00","--code":"#0A0A0A","--scroll":"#FFFFFF"}},
};
function applyTheme(id){
  const t=THEMES[id]||THEMES.dark;
  const root=document.documentElement;
  Object.entries(t.vars).forEach(([k,v])=>root.style.setProperty(k,v));
  // Build an override stylesheet that remaps the app's hardcoded hex inline styles to theme colors.
  // This keeps every existing inline style intact (additive) while letting non-dark themes restyle.
  const map=[
    ["#0a0e1a",t.vars["--bg"]],["#0c1120",t.vars["--bg2"]],["#131825",t.vars["--panel"]],
    ["#1a2030",t.vars["--border"]],["#14192a",t.vars["--border2"]],
    ["#F1F5F9",t.vars["--text"]],["#A0AAC0",t.vars["--text2"]],["#8892B0",t.vars["--text3"]],
    ["#5A6480",t.vars["--muted"]],["#3A4060",t.vars["--muted2"]],["#080c18",t.vars["--code"]],
  ];
  const sel="#oiq-root";
  let css="";
  if(id!=="dark"){
    map.forEach(([from,to])=>{
      const f=from.toLowerCase();
      css+=`${sel} [style*="background:${f}"],${sel} [style*="background: ${f}"],${sel} [style*="background-color:${f}"]{background-color:${to}!important}`;
      css+=`${sel} [style*="color:${f}"]:not([style*="background"]){color:${to}!important}`;
      css+=`${sel} [style*="border:1px solid ${f}"],${sel} [style*="borderColor:${f}"],${sel} [style*="border: 1px solid ${f}"]{border-color:${to}!important}`;
    });
    // generic text color remaps inside the app
    css+=`${sel}{color:${t.vars["--text2"]}}`;
  }
  let el=document.getElementById("oiq-theme-override");
  if(!el){el=document.createElement("style");el.id="oiq-theme-override";document.head.appendChild(el);}
  el.textContent=css;
}

// ─── FEATURE 4 & 5: PRESENTATION TYPES ──────────────────────────────────────
const PDF_TYPES=[
  {id:"summary",label:"Summary PDF",ic:"📄",desc:"One-page executive summary"},
  {id:"detailed",label:"Detailed PDF",ic:"📑",desc:"Full report with all content"},
  {id:"executive",label:"Executive Report",ic:"👔",desc:"Board-ready strategic report"},
  {id:"investor",label:"Investor Report",ic:"💼",desc:"Investor-grade due-diligence pack"},
];
const PPT_TYPES=[
  {id:"briefing",label:"Executive Briefing",ic:"👔"},
  {id:"strategy",label:"Business Strategy Deck",ic:"♟️"},
  {id:"investor",label:"Investor Deck",ic:"💼"},
  {id:"pitch",label:"Startup Pitch Deck",ic:"🚀"},
  {id:"roadmap",label:"Product Roadmap Deck",ic:"🗺️"},
  {id:"research",label:"Research Presentation",ic:"🔬"},
  {id:"operational",label:"Operational Review",ic:"🔧"},
];
const VOICE_LANGS = [
  {code:"en-IN",label:"English (India)"},{code:"hi-IN",label:"Hindi"},{code:"bn-IN",label:"Bengali"},{code:"pa-IN",label:"Punjabi"},
  {code:"mr-IN",label:"Marathi"},{code:"ta-IN",label:"Tamil"},{code:"te-IN",label:"Telugu"},{code:"kn-IN",label:"Kannada"},
  {code:"ml-IN",label:"Malayalam"},{code:"gu-IN",label:"Gujarati"},{code:"en-US",label:"English (US)"},{code:"fr-FR",label:"French"},
  {code:"es-ES",label:"Spanish"},{code:"de-DE",label:"German"},{code:"ja-JP",label:"Japanese"},{code:"zh-CN",label:"Mandarin"},
  {code:"ar-SA",label:"Arabic"},{code:"pt-BR",label:"Portuguese"},
];
const TS = {QUEUED:"queued",RUNNING:"running",REVIEWING:"reviewing",APPROVED:"approved",REJECTED:"rejected",FAILED:"failed"};
const CHAINS = {
  finance:{label:"Finance / Analysis",ic:"📊",color:"#3B82F6",chain:["acct_exe","acct","sm_fin","fin_ctrl","vp_fin","cfo"],desc:"P&L, balance sheet, MIS, reconciliation"},
  tax:{label:"Tax & Compliance",ic:"🧾",color:"#3B82F6",chain:["acct_exe","acct","tax_mgr","fin_ctrl","cfo","clo"],desc:"GST, TDS, tax filings, statutory"},
  audit:{label:"Audit & Risk",ic:"🔎",color:"#84CC16",chain:["audit_ana","sox_ana","audit_mgr","risk_mgr","cia","cfo"],desc:"Internal audit, controls, risk assessment"},
  hr_task:{label:"HR & People",ic:"👥",color:"#10B981",chain:["hr_exe","rec","sm_hr","hr_biz","vp_hr","chro"],desc:"Hiring, onboarding, payroll, policies"},
  legal_task:{label:"Legal & Contracts",ic:"⚖️",color:"#EC4899",chain:["legal_ana","comp_mgr","sm_legal","dir_comp","vp_legal","clo"],desc:"Contracts, compliance, IP, regulatory"},
  marketing_task:{label:"Marketing",ic:"📣",color:"#EF4444",chain:["mktg_exe","mktg_ana","sm_mktg","dir_growth","vp_mktg","cmo"],desc:"Campaigns, content, SEO, paid media"},
  sales_task:{label:"Sales & Revenue",ic:"💰",color:"#F97316",chain:["sdr","ae","sales_mgr","sm_sales","vp_sales","sl"],desc:"Pipeline, proposals, deals, forecasting"},
  tech_task:{label:"Technology / Product",ic:"⚙️",color:"#8B5CF6",chain:["qa","data_ana","tl","sr_dev","dir_prod","cto"],desc:"Architecture, specs, sprint, DevOps"},
  ops_task:{label:"Operations / Process",ic:"🔧",color:"#F59E0B",chain:["ops_ana","sm_ops","proj_mgr","dir_ops","vp_ops","coo"],desc:"SOPs, project delivery, vendor, scaling"},
  strategy_task:{label:"Strategy & Corporate",ic:"♟️",color:"#6366F1",chain:["strat_ana","biz_ana","strat_mgr","dir_ma","vp_strat","cso"],desc:"Market analysis, M&A, strategic planning"},
  cx_task:{label:"Customer Success",ic:"⭐",color:"#06B6D4",chain:["cx_ana","support","csm","csm_lead","dir_cx","vp_cx"],desc:"Onboarding, retention, NPS, escalations"},
  executive_task:{label:"Executive Decision",ic:"👔",color:"#14B8A6",chain:["coo","cfo","cto","cmo","chro","clo","ceo","chairman"],desc:"Cross-functional C-suite decisions"},
};

const DELIVERABLE_SPECS = {
  finance:"a Variance Analysis Table (Budget vs Actual vs Variance % for each line item) and a Cash Flow Action List (specific actions with rupee impact and deadline)",
  tax:"a Filing Calendar (Form | Due Date | Penalty if Late | Status) and a Computation Worksheet showing exact tax calculation with current rates",
  audit:"a Risk Register (Risk | Likelihood | Impact | Owner | Mitigation | Deadline) and a Control Testing Checklist with pass/fail criteria",
  hr_task:"a Job Description ready to post, an Interview Scorecard with weighted criteria, and a 30-60-90 Day Onboarding Plan",
  legal_task:"a Compliance Checklist (Requirement | Deadline | Status | Owner) and key Contract Clauses written in full, ready to insert",
  marketing_task:"a 30-DAY CONTENT CALENDAR (Day | Platform | Content Type | Caption | Hashtags | CTA, minimum 12 rows, full ready-to-post captions following platform-specific rules: Instagram hook in first 125 chars with line breaks, LinkedIn bold opening line 150-300 words, Twitter/X max 280 chars single thought), THREE READY-TO-POST CAPTIONS in full, and a CREATIVE BRIEF",
  sales_task:"a Pipeline Tracker template (Stage | Deal | Value | Probability | Next Action | Owner) and a ready-to-send Proposal Email",
  tech_task:"a Sprint Backlog (Story | Priority | Effort | Owner) and an API Contract or technical spec section ready for engineering handoff",
  ops_task:"a Standard Operating Procedure document (numbered steps, ready to follow) and a Vendor Scorecard template",
  strategy_task:"a Market Sizing Table (TAM/SAM/SOM with calculation shown) and a Strategic Options Matrix (Option | Pros | Cons | Investment | Timeline | Recommendation)",
  cx_task:"a Customer Health Score model (Metric | Weight | Threshold) and a 30-60-90 Day Success Plan template",
  executive_task:"a Decision Matrix (Option | Cost | Risk | Impact | Recommendation) and a Board-Ready Summary in bullet form",
};

// ─── SHARED RESEARCH DESK PROMPT ────────────────────────────────────────────
// Used by Boardroom, Time Machine, and Autopilot as a pre-step: search-enabled
// calls gather current, source-cited findings that all downstream analysis uses.
// Centralized here so all three features use an identical, battle-tested prompt.
function researchDeskPrompt(co,compData,question){
  const today=new Date().toISOString().slice(0,10);
  return "You are the Research Desk for \""+co.name+"\"'s strategic analysis. Your output is the sole factual foundation for a board-level debate between senior executives. Anything you get wrong, they will repeat as fact. "+buildCtx(co,compData)+
  "\n\nYour job: find SPECIFIC, CURRENT, VERIFIABLE findings that would materially change the decision on the question below - costs, pricing, market sizes, growth rates, regulations, adoption data, competitor moves, or industry benchmarks relevant to "+co.industry+" in "+co.location+". Search the live web for each. Prefer sources from the last 12 months and explicitly flag anything older than 24 months as DATED."+
  "\n\nSOURCE HIERARCHY (critical). Prefer primary sources in this order:\n1. The vendor's or provider's own official site (e.g. anthropic.com, aws.amazon.com, razorpay.com) for pricing and product specs.\n2. Government, regulatory, or industry-body sources (RBI, MeitY, SEBI, MSME ministry, NASSCOM) for regulation, market sizing, or official statistics.\n3. Recognized research firms (Gartner, IDC, Bain, McKinsey, Grand View, IMARC) for market and industry data.\n4. Reputable business press (Reuters, Bloomberg, Mint, Economic Times) for recent events.\n5. Only as a last resort, blogs, aggregators, or comparison sites - and if you use one, write NO OFFICIAL SOURCE FOUND in that finding.\nIf your first result for a pricing or product figure is a third-party blog, search again for the official source (e.g. 'site:anthropic.com pricing') before finalizing."+

  "\n\nQUALITY BAR - every finding must clear all four tests:\n- SPECIFIC: contains a number, a date, a named entity, or a named rule. 'The market is growing' fails. 'Market grew 21.8% CAGR 2025-2030' passes.\n- SOURCED: has a real source name and a real clickable URL you actually retrieved. If you cannot produce a real URL, DELETE the finding. Never guess, reconstruct, or invent a URL.\n- DATED: carries the date the source PUBLISHED the information, not today's date.\n- DECISION-RELEVANT: you can state in one clause why it changes what this business should do. If you cannot, delete it."+

  "\n\nOUTPUT CONTRACT (strict - follow exactly). Output ONLY bullet lines. No heading, no preamble, no closing summary, no narration such as 'I'll search' or 'Let me compile'. Your first character must be a bullet character.\nEach bullet is ONE single line, in exactly this shape:\n\n\u2022 [T1] Finding with the specific figure or rule stated plainly. \u2192 So what: one clause on what this means for this business. Source: Source Name, published YYYY-MM-DD. https://real.url/path\n\nRules for the bullet:\n- Confidence tag is [T1] official or regulatory or vendor-primary, [T2] recognized research firm or reputable press, [T3] blog, forum, or aggregator.\n- 'published' is the date the SOURCE published it. If the source shows no publication date, write 'published date-unknown'. Today's date is "+today+" - this is the access date and must NOT be used as the publication date.\n- If a figure is in a foreign currency, convert to INR in brackets and label it approximate.\n- Never put two findings in one bullet. Never break a bullet across lines.\n- If you genuinely cannot find real information for this angle, output the single line: \u2022 [T3] NO RELIABLE SOURCE FOUND for this angle. \u2192 So what: the executives must treat this area as an evidence gap. Source: none, published date-unknown."+

  fxRuleBlock()+
  "\n\nQUESTION UNDER ANALYSIS: \""+question+"\"";
}

// ── PREAMBLE / NARRATION STRIP ───────────────────────────────────────────────
// Search-enabled models emit a text block BEFORE they search ("I'll search for
// current data on..."). callClaude joins every text block, so that narration
// lands in the brief. This removes everything before the first real bullet and
// drops any stray narration lines that survive.
const NARRATION_RE=/^(i'?ll |i will |i'?m going to |i need to |let me |now (i|let)|first,? (i|let)|searching |i'?ve (found|searched)|based on (my|the) search|here (is|are) )/i;
function stripPreamble(text){
  if(!text)return "";
  const lines=String(text).split("\n");
  const isBullet=(l)=>/^\s*[\u2022\-\*]/.test(l);
  const firstBullet=lines.findIndex(isBullet);
  const body=firstBullet>=0?lines.slice(firstBullet):lines;
  return body.filter(l=>{
    const t=l.trim();
    if(!t)return false;
    if(isBullet(l))return true;
    return !NARRATION_RE.test(t);
  }).join("\n").trim();
}

// ── SOURCE QUALITY BADGES ────────────────────────────────────────────────────
// Parses Research Brief bullets and adds 🟢🟡🔴 trust badges based on domain.
const OFFICIAL_DOMAINS=["rbi.org","gov.in","mca.gov","sebi.gov","meity","nseindia","bseindia","anthropic.com","openai.com","aws.amazon","microsoft.com","google.com","razorpay.com","nasscom","gartner.com","idc.com","mckinsey.com","pwc.com","deloitte.com","ey.com","kpmg.com","worldbank.org","imf.org","statista.com"];
const PRESS_DOMAINS=["reuters","bloomberg","economic times","economictimes","livemint","financialexpress","moneycontrol","techcrunch","forbes","hindu","ndtv","businessstandard","inc42","yourstory"];

function badgeBrief(brief){
  if(!brief)return brief;
  return brief.split("\n").map(line=>{
    if(!line.trim().startsWith("•")&&!line.trim().startsWith("-")&&!line.trim().startsWith("*"))return line;
    const lower=line.toLowerCase();
    const isOfficial=OFFICIAL_DOMAINS.some(d=>lower.includes(d));
    const isPress=!isOfficial&&PRESS_DOMAINS.some(d=>lower.includes(d));
    const badge=isOfficial?"🟢 ":isPress?"🟡 ":"🔴 ";
    return badge+line.trim().replace(/^[•\-\*]\s*/,"");
  }).join("\n");
}

// Only Claude and Gemini can genuinely search the live web from this app.
// OpenAI, DeepSeek, and Groq have no real-time search access on the plans
// used here — routing research to them would produce confident-sounding
// fabrication, not real research. This is a deliberate, honest limit.
// ─── PROVIDER CONTROL GATE (single source of truth) ──────────────────────────
// The Settings on/off toggle used to be decorative: `offP` was read in exactly
// ONE place — the Primary AI button row — while EIGHT other paths read the keys
// object directly. That is why a switched-off Claude key was still being billed
// for research. Every provider decision in the app now passes through here, so
// "off" means off by construction and any future path inherits the behaviour.
//
// It is deliberately module-level (not React state) because callAI, callMulti
// and runResearchDesk are plain functions outside the component tree.
let OFF_PROVIDERS:Record<string,boolean>={};
try{OFF_PROVIDERS=JSON.parse(localStorage.getItem("cos-offp")||"{}")||{};}catch{}
function setOffProviders(next:Record<string,boolean>){
  OFF_PROVIDERS=next||{};
  try{localStorage.setItem("cos-offp",JSON.stringify(OFF_PROVIDERS));}catch{}
}
function isProviderOff(id:string){return !!OFF_PROVIDERS[id];}
 
// Returns the usable key for a provider, or "" when the user has switched it off.
// System-supplied fallback keys (EFF_*) are honoured ONLY when the provider is on —
// previously a user with no key of their own and the toggle off could still be
// billed on a system key they never configured.
function providerKey(keys:any,id:string):string{
  // THE ACTUAL BUG, CONFIRMED: isProviderOff reads a toggle stored ONLY in
  // this one browser's local storage - never synced anywhere, invisible to
  // you as admin, and easy to end up switched off by an old test click.
  // NVIDIA's entire design elsewhere in this file is to be the one
  // provider that ALWAYS works with no setup - a stray local toggle
  // silently breaking that guarantee, differently on every device, is
  // exactly the "works for me, not for them" inconsistency reported. It is
  // no longer allowed to disable the one thing meant to never be disabled.
  if(isProviderOff(id)&&id!=="nvidia")return "";
  const own=(keys?.[id]||"").trim();
  // A SECOND, RELATED BUG THIS EXPLAINS: typing even one wrong character
  // into the NVIDIA field used to be preferred over the working shared-tier
  // fallback below, since ANY non-empty value took priority - so a single
  // stray digit could break NVIDIA entirely instead of being ignored in
  // favour of the free tier that was working fine a moment before. A
  // genuinely well-formed key is still always used; anything that doesn't
  // look like a real one is now simply disregarded here, not trusted.
  if(own&&(id!=="nvidia"||(own.startsWith("nvapi-")&&own.length>=20)))return own;
  if(id==="gemini"&&EFF_GEMINI?.trim())return EFF_GEMINI.trim();
  if(id==="groq"&&EFF_GROQ?.trim())return EFF_GROQ.trim();
  if(id==="claude"&&EFF_CLAUDE?.trim())return EFF_CLAUDE.trim();
  if(id==="fal"&&EFF_FAL?.trim())return EFF_FAL.trim();
  if(id==="nvidia"){
    // THE NEW RULE: the shared 5-key pool is reserved for people who
    // genuinely have no other option — not available to someone who has
    // their own Claude/Gemini/OpenAI/Groq/fal key configured AND switched
    // on. A key that's present but explicitly turned off does not count
    // against them, since "switched off all the others" was named as an
    // acceptable alternative to never having added one at all.
    const otherProviderIds=["claude","gemini","openai","groq","fal","deepseek","kimi"];
    const hasAnotherActiveKey=otherProviderIds.some(pid=>{
      if(isProviderOff(pid))return false; // explicitly switched off — does not block shared NVIDIA
      return !!(keys?.[pid]||"").trim();
    });
    if(hasAnotherActiveKey)return ""; // this user has a real alternative — do not silently use the shared pool for them
    return "nvidia"; // free tier, no key required — genuinely no other option configured
  }
  return "";
}
function providerEnabled(keys:any,id:string):boolean{
  return !!providerKey(keys,id);
}
// The ONLY approved way to build a key map for routing. Switched-off providers
// are absent entirely, so any downstream code that iterates it cannot use them.
// Explicit allow-list. The keys object also holds NON-provider entries such as
// keys.nvidiaModel (a model string, not a credential) and the four web-search
// keys. Filtering by "is not a search key" would have let nvidiaModel through as
// if it were a callable provider. An allow-list cannot drift that way.
// NVIDIA belongs here even though it has no entry in the keys object - it needs
// no key, and providerKey() returns "nvidia" whenever it is switched on. Leaving
// it out meant enabledKeys() never contained it, so the Boardroom's provider list
// came back EMPTY when NVIDIA was the only provider enabled - and every executive
// reported "did not produce an answer" without a single request being sent.
const AI_PROVIDER_IDS=["nvidia","claude","openai","gemini","groq","deepseek","kimi","stability","fal"];
function enabledKeys(keys:any):Record<string,string>{
  const out:Record<string,string>={};
  AI_PROVIDER_IDS.forEach(id=>{
    const k=providerKey(keys,id);
    if(k)out[id]=k;
  });
  return out;
}
 
// PROVIDER ORDER FOR A STAGE - delegates entirely to stageProviderChain() in
// lib/ModelRouting, the single source of truth. No routing policy lives here.
// (The local COST_CHAIN copy added to fix a build was removed: two copies of a
// routing policy drift apart.)
function costOrderedProviders(keys:any,stage:string):Array<{provider:string;key:string}>{
  const ek=enabledKeys(keys);
  return stageProviderChain(stage as any,STAGE_PROFILE,(id:string)=>!!ek[id]).map(p=>({provider:p,key:ek[p]}));
}
const STAGE_LABEL=(stage:string)=>(STAGES.find((x:any)=>x.id===stage)?.label)||stage;
const providerTier=(p:string)=>PREMIUM_PROVIDERS.includes(p)?"premium":p==="nvidia"?"free tier":"economical";
// One line describing what actually ran: stage, provider, model, tier, primary/fallback.
function describeStageRun(stage:string,provider:string,fallbackFrom:string[]):string{
  const model=stageModelOverride(stage as any,provider)||(MODELS as any)[provider]?.model||"";
  return STAGE_LABEL(stage)+": "+((MODELS as any)[provider]?.name||provider)+(model?" ("+model+")":"")+" \u00b7 "+providerTier(provider)
    +" \u00b7 "+(fallbackFrom.length?"fallback after "+fallbackFrom.map(f=>f.split(":")[0]).join(", ")+" failed":"primary");
}
// Runs one Boardroom/research step on the providers its stage permits, in
// order. If none is permitted or all fail it THROWS with the reason - it never
// widens the list to a provider the routing mode does not allow.
async function callStage(keys:any,stage:string,sys:any,msgs:any,maxT:number,provState?:any):Promise<{text:string;truncated:boolean;provider:string;fallbackFrom:string[];note:string}>{
  const fullChain=costOrderedProviders(keys,stage);
  // Optional run-level provider state (Boardroom). When given, providers already
  // known to be unusable are skipped and new failures are recorded - the same
  // classification the executive loop uses. Without it, behaviour is unchanged.
  const chain=provState?fullChain.filter(r=>providersUsableNow([r.provider],provState,Date.now()).length>0):fullChain;
  if(provState&&fullChain.length&&!chain.length)
    throw new Error("\""+STAGE_LABEL(stage)+"\" skipped: no permitted provider can answer right now \u2014 "+providerStateSummary(fullChain.map(r=>r.provider),provState,Object.fromEntries(fullChain.map(r=>[r.provider,(MODELS as any)[r.provider]?.name||r.provider]))));
  if(!chain.length)throw new Error("No provider permitted for \""+STAGE_LABEL(stage)+"\" is available under your current routing mode. Economy and Balanced never use OpenAI or Claude for this step without your permission. Turn DeepSeek or Gemini back on, or choose a premium provider for this step in Settings \u2192 Model Routing.");
  const failed:string[]=[];
  for(const r of chain){
    // CONTEXT BUDGET, checked BEFORE the request: a prompt that cannot fit this
    // provider is not sent. That is a skip, not a failure to retry.
    const bc=budgetCheck(r.provider,typeof sys==="string"?sys:JSON.stringify(sys),(msgs||[]).map((m:any)=>String(m?.content||"")).join("\n"),maxT);
    if(!bc.fits){
      failed.push(r.provider+": prompt too long for this provider (pre-check, not sent: ~"+bc.estTotal+" > "+bc.usable+" usable tokens)");
      if(provState)provState[r.provider]={kind:"permanent",label:"prompt too long (pre-check)",retryAt:Infinity};
      continue;
    }
    try{
      const out:any=await callAI(r.provider,r.key,sys,msgs,maxT,false,stageModelOverride(stage as any,r.provider));
      const text=String(out?.text||"");
      if(!text.trim())throw new Error("empty answer");
      if(provState)delete provState[r.provider];
      return {text,truncated:!!out?.truncated,provider:r.provider,fallbackFrom:failed,note:describeStageRun(stage,r.provider,failed)};
    }catch(e:any){
      failed.push(r.provider+": "+String(e?.message||e).slice(0,160));
      if(provState)recordProviderFailure(provState,r.provider,String(e?.message||e),Date.now());
    }
  }
  throw new Error("\""+STAGE_LABEL(stage)+"\" failed on every provider your routing mode permits ("+chain.map(c=>c.provider).join(" \u2192 ")+"). Errors: "+failed.join(" | "));
}

// PROVIDER ERROR CLASSIFICATION. Decides whether a provider failure is worth
// retrying at all - previously any message mentioning rate/quota/limit/context
// set ONE global "retryable" flag, so a 402 or 403 provider was retried after
// a 65-second countdown labelled "all providers at limit".
//   permanent - billing, auth, missing/retired model, invalid request, prompt
//               too long: waiting cannot fix it; never retried this run.
//   quota     - daily/free-tier quota: retried ONLY if the provider says when
//               it resets, and only if that is within 90 seconds.
//   retryable - per-minute rate limit, overload, timeout, transient 5xx.
type ProvErrKind="retryable"|"quota"|"permanent";
function parseRetryAfterMs(m:string):number|null{
  let x=m.match(/"?retry_?delay"?\s*[:=]\s*"?(\d+(?:\.\d+)?)s/);              if(x)return Math.ceil(parseFloat(x[1])*1000);
  x=m.match(/retry[- ]?after[^0-9]{0,12}(\d+(?:\.\d+)?)\s*(ms|milliseconds)?/); if(x)return Math.ceil(parseFloat(x[1])*(x[2]?1:1000));
  x=m.match(/(?:try again|retry) in\s*(?:(\d+)\s*m(?:in(?:utes?)?)?\s*)?(\d+(?:\.\d+)?)\s*(ms|s|sec|seconds?)\b/);
  if(x)return Math.ceil((x[1]?parseInt(x[1])*60000:0)+parseFloat(x[2])*(x[3]==="ms"?1:1000));
  x=m.match(/(?:try again|retry) in\s*(\d+(?:\.\d+)?)\s*(m|min|minutes?|h|hours?)\b/);
  if(x)return Math.ceil(parseFloat(x[1])*(x[2].startsWith("h")?3600000:60000));
  return null;
}
function classifyProviderError(raw:string):{kind:ProvErrKind;label:string;retryAfterMs:number|null}{
  const m=String(raw||"").toLowerCase(); const ra=parseRetryAfterMs(m);
  if(/\b402\b|insufficient[_ ]balance|insufficient_quota|payment required|billing|check your plan|credit balance|credits are exhausted/.test(m))
    return {kind:"permanent",label:"insufficient balance / billing",retryAfterMs:null};
  // Authorization BEFORE quota: provider messages often mention "free tier"
  // incidentally (e.g. "...or clear it to use the shared free tier").
  if(/\b401\b|unauthori[sz]ed|invalid[_ ]api[_ ]key|incorrect api key|api key not valid|no api key|missing (?:api )?key/.test(m))
    return {kind:"permanent",label:"invalid or missing API key",retryAfterMs:null};
  if(/\b403\b|forbidden|authori[sz]ation failed|permission denied|refused authori[sz]ation/.test(m))
    return {kind:"permanent",label:"authorization failed",retryAfterMs:null};
  if(/\b404\b|\b410\b|not found|does not exist|model_not_found|retired|decommissioned|does not recogni[sz]e the model|deprecated model/.test(m))
    return {kind:"permanent",label:"model or endpoint not found / retired",retryAfterMs:null};
  // Per-minute limits are ordinary rate limits even when the text says "free tier".
  if(/per minute|\btpm\b|\brpm\b/.test(m))
    return {kind:"retryable",label:"rate limited",retryAfterMs:ra};
  if(/quota|resource_exhausted|per[_ ]day|perday|daily (?:limit|quota)|tokens per day|requests per day|\btpd\b|\brpd\b/.test(m))
    return {kind:"quota",label:ra!=null&&ra<=90000?"quota limit (resets in "+Math.ceil(ra/1000)+"s)":"free-tier / daily quota exhausted",retryAfterMs:ra};
  // Output budget used up by a reasoning model is NOT an input-size problem.
  if(/output budget|ran out of (?:output )?budget|used its (?:whole|entire) output/.test(m))
    return {kind:"permanent",label:"output budget used up by reasoning",retryAfterMs:null};
  if(/context length|maximum context|context window|too long|\b413\b|request too large|reduce the length|prompt is too large/.test(m))
    return {kind:"permanent",label:"prompt too long for this provider",retryAfterMs:null};
  if(/\b400\b|bad request|invalid request|invalid_request_error/.test(m))
    return {kind:"permanent",label:"request rejected as invalid",retryAfterMs:null};
  if(/\b429\b|rate[_ ]?limit|too many requests|right now|try again in a few seconds/.test(m))
    return {kind:"retryable",label:"rate limited",retryAfterMs:ra};
  if(/overload|\b5\d\d\b|timeout|timed out|did not finish within|network|econnreset|fetch failed|temporarily|service unavailable|try again later/.test(m))
    return {kind:"retryable",label:"temporary provider error",retryAfterMs:ra};
  return {kind:"permanent",label:"unexpected error",retryAfterMs:null};
}
// PER-PROVIDER STATE for one Boardroom run. Each provider's failure is recorded
// individually; only providers whose own state allows it are tried again.
type ProvState=Record<string,{kind:ProvErrKind;label:string;retryAt:number}>;
function recordProviderFailure(state:ProvState,prov:string,msg:string,now:number){
  const c=classifyProviderError(msg);
  const retryAt=c.kind==="permanent"?Infinity
    :c.kind==="quota"?(c.retryAfterMs!=null&&c.retryAfterMs<=90000?now+c.retryAfterMs:Infinity)
    :now+Math.min(Math.max(c.retryAfterMs??20000,1000),65000);
  state[prov]={kind:c.kind,label:c.label,retryAt};
  return state[prov];
}
function providersUsableNow(chain:string[],state:ProvState,now:number):string[]{
  return chain.filter(p=>!state[p]||state[p].retryAt<=now);
}
// null = nothing in the chain can ever become usable this run -> stop now.
function nextRetryWaitMs(chain:string[],state:ProvState,now:number):number|null{
  if(chain.some(p=>!state[p]))return 0;
  const times=chain.map(p=>state[p].retryAt).filter(t=>t!==Infinity);
  return times.length?Math.max(0,Math.min(...times)-now):null;
}
function providerStateSummary(chain:string[],state:ProvState,names:Record<string,string>={}):string{
  return chain.map(p=>(names[p]||p)+": "+(state[p]?state[p].label:"not attempted")).join(" \u00b7 ");
}

function resolveSearchProviders(keys){
  const out:Array<{provider:string;key:string}>=[];
  // Gated: a provider switched off in Settings is never used for research.
  // This exact line is why a disabled Claude key was still running your searches.
  // Both can search the web natively. Gemini first: it is far cheaper, and
  // Claude remains the fallback. (Was Claude first, so research ran on the
  // most expensive model whenever a Claude key existed.)
  const geminiKey=providerKey(keys,"gemini");
  if(geminiKey)out.push({provider:"gemini",key:geminiKey});
  const claudeKey=providerKey(keys,"claude");
  if(claudeKey)out.push({provider:"claude",key:claudeKey});
  return out;
}
// Kept for any caller that still wants a single preferred provider.
function resolveSearchProvider(keys){
  const list=resolveSearchProviders(keys);
  return list.length?list[0]:null;
}
// Runs one research call, falling back through every search-capable provider.
// Previously a single Claude failure killed research entirely even when a
// working Gemini key was sitting in Settings.
async function callSearchWithFailover(routes,sys,msgs,maxT,enableSearch,onNote){
  let lastErr=null;
  for(const r of routes){
    try{
      const out=await callAI(r.provider,r.key,sys,msgs,maxT,enableSearch);
      return {out,provider:r.provider};
    }catch(e:any){
      lastErr=e;
      try{onNote&&onNote(r.provider,String(e?.message||e));}catch{}
    }
  }
  throw lastErr||new Error("No search-capable provider available.");
}

// ── RETRIEVAL-FIRST SEARCH ───────────────────────────────────────────────────
// Order of preference when the app does its own searching. Any provider with no
// key is skipped, so this list is safe even when nothing is configured.
const SEARCH_CHAIN:any[]=["serper","tavily","brave","dataforseo"];
// These live in the same keys object as the AI providers, but they are NOT AI
// models. Any list of "which AI can I call" must exclude them, or the app tries
// to look up MODELS["serper"] and crashes on undefined.
const SEARCH_KEY_IDS=new Set(["serper","tavily","brave","dataforseo"]);
// THIS IS THE SETTINGS-PAGE CRASH, AT ITS SOURCE.
// This was a BLOCKLIST: "anything that is not a search provider is an AI
// provider". That is only true if the keys object holds nothing but provider
// keys - and it does not. The NVIDIA model dropdown saves the SELECTED MODEL
// into the same object under the key "nvidiaModel". So once you touch that
// dropdown:
//     keys = { deepseek:"sk-...", serper:"...", nvidiaModel:"nvidia/nemotron-3-..." }
//     cfgP = ["nvidia","deepseek","nvidiaModel"]      &lt;- a model ID, not a provider
//     MODELS["nvidiaModel"]      -> undefined
//     MODELS["nvidiaModel"].name -> "Cannot read properties of undefined"
// and the whole Settings render dies. An ALLOWLIST cannot fail this way: only
// the nine real providers pass, whatever else is stored alongside them.
const AI_PROVIDER_SET=new Set(AI_PROVIDER_IDS);
function isAiProviderKey(id:string){return AI_PROVIDER_SET.has(id);}
 
// Search phrasing per research angle. Built in code, not by a model, so the
// queries are predictable and cost nothing to produce.
const ANGLE_QUERY_HINTS:Record<string,string[]>={
  market:["market size growth rate","demand trends forecast"],
  competitors:["competitors pricing","market leaders comparison"],
  pricing:["pricing benchmark unit economics","gross margin industry benchmark"],
  regulatory:["licence registration requirement India","regulation compliance cost"],
  news:["latest news 2026","recent developments"],
  customer:["customer adoption survey","buyer behaviour research"],
  contrarian:["failures shutdown losses","risks why it failed"],
};
 
// Builds real search queries.
//
// The previous version fed the raw question straight into Google. That works when
// the question names a concrete thing ("50 tonne steel rolling mill in Raipur"),
// and fails completely when it does not. Autopilot asks "What decisions should
// {company} be making right now?" - which produced the literal query
//   "decisions gorakhai (ai saas lucknow stage: idea) market size growth rate"
// and returned Instagram reels, because there is nothing on the web to find about
// a company that has no customers yet.
//
// A search query must describe the MARKET, not the user. The subject now comes
// from the business context - industry, product, location - and the question is
// used only to add specifics when it actually contains any.
const QUERY_STOPWORDS=new Set(["what","which","when","where","how","why","should","would","could","the","and","for","are","was","with","that","this","from","have","has","been","will","can","does","did","you","your","our","their","about","into","than","then","them","they","there","here","just","very","much","many","more","most","some","any","all","not","but","its","it's","give","tell","need","want","please","also","only","over","under","between","using","use","get","make","take","think","know","see","look","find","help","right","now","making","decisions","decision","stage","viable","viability","quit","build","grow","protect","per","year","job","full","crore","lakh","capital","money","time","part","side","first","still","keep","stay","set"]);
// Removes words that describe the USER rather than the market. A search engine
// cannot find anything useful about these, and they poison the whole query.
function stripSelfReference(text:string,co:any):string{
  let t=" "+String(text||"").toLowerCase()+" ";
  const name=String(co?.name||"").toLowerCase().trim();
  if(name)t=t.split(name).join(" ");
  return t.replace(/\(.*?\)/g," ");
}
function buildAngleQueries(question:string,angleId:string,co:any):string[]{
  const place=String(co?.location||"").split(",")[0].trim();
  const industry=String(co?.industry||"").trim();
  // 1. The SUBJECT is the market this business operates in - never the company itself.
  const cleaned=stripSelfReference(question,co);
  const qWords=cleaned.replace(/[^a-z0-9\s.%-]/g," ").split(/\s+/)
    .filter(w=>w.length>2&&!QUERY_STOPWORDS.has(w)&&!/^\d+$/.test(w));
  // WAS: only 4 words survived from the question, and the INDUSTRY was then
  // put in front of them. So the search was mostly about the company's sector
  // and only slightly about what was actually asked. That is why the results
  // came back competent and beside the point.
  // Eight words now survive, and they LEAD the query. Industry and place stay,
  // but as context behind the question rather than in front of it.
  const qCore=Array.from(new Set(qWords)).slice(0,8).join(" ").trim();
  // 2. The INDUSTRY always leads, because it is the one term guaranteed to describe
  //    a real market. Words surviving from the question only add specificity.
  //    Verified query output:
  //      Autopilot    -> "AI SaaS business market size growth rate India 2026"
  //      Time Machine -> "AI SaaS business operating system competitors pricing Lucknow"
  //      Steel mill   -> "Steel manufacturing tonne day steel rolling market size India 2026"
  //    Previously the first of those was:
  //      "decisions gorakhai (ai saas lucknow stage: idea) market size growth rate"
  const subject=[industry,qCore].filter(Boolean).join(" ").trim()||"business";
  const hints=ANGLE_QUERY_HINTS[angleId]||["overview"];
  const yr=new Date().getFullYear();
  return hints.map((h,i)=>{
    // First query is national; second adds the city. Searching only locally is why
    // six of seven angles returned nothing usable for a small market like Lucknow.
    const geo=i===0?"India":(place||"India");
    return [subject,h,geo,i===0?String(yr):""].filter(Boolean).join(" ").replace(/\s+/g," ").trim();
  }).filter(q=>q.length>8);
}
 
// Deep Research Desk v3 — decomposes the question into 7 angles (including a
// mandatory disconfirming-evidence pass), runs a real search per angle, then
// runs a final synthesis pass that reconciles conflicts, ranks what matters,
// and hands the executives framed tensions to debate rather than raw facts.
// WHY THE RESEARCH FELT UNRELATED TO THE QUESTION.
//
// The seven angles below were applied to EVERY question, without exception.
// They are good angles - for a market-entry or investment question. They are
// the wrong seven for anything else.
//
// Ask "how do I handle an unreasonable manager" and the desk still went looking
// for market size, competitor pricing and unit economics. Ask about export
// documentation and it searched for churn and CAC. The research was competent
// and irrelevant, which is exactly what you described.
//
// The angles now follow the question. A market question gets market angles. A
// career question gets career angles. A trade question gets trade angles.
const ANGLE_SETS:Record<string,Array<{id:string;label:string}>>={
  career:[
    {id:"norms",label:"Workplace norms, hierarchy and how promotion actually gets decided in this country and sector"},
    {id:"benchmark",label:"Salary bands, titles and typical progression timelines for this role and level"},
    {id:"rights",label:"Employment law, notice periods, and what protection the employee actually has here"},
    {id:"tactics",label:"Evidence on what works when managing upwards, negotiating, or handling a difficult manager"},
    {id:"wellbeing",label:"Burnout, workload and work-life expectations in this specific culture and industry"},
    {id:"contrarian",label:"Where the common career advice fails, and what people regret doing"},
  ],
  trade:[
    {id:"classification",label:"HS classification, applicable duty rates and the tariff schedule for this product and destination"},
    {id:"regulation",label:"Import and export licensing, certification, labelling and prohibited or restricted status"},
    {id:"agreements",label:"Trade agreements, preferential origin rules and current tariff or sanction changes"},
    {id:"demand",label:"Import volumes, leading supplier countries and demand trend for this product in the target market"},
    {id:"logistics",label:"Freight routes, transit times, packaging standards and landed cost components"},
    {id:"risk",label:"Payment risk, buyer default patterns, customs delays and documentation failures in this corridor"},
  ],
  market:[
    {id:"market",label:"Market size, growth rate, and demand trends"},
    {id:"competitors",label:"Direct and indirect competitors, their pricing and positioning"},
    {id:"pricing",label:"Industry pricing benchmarks and unit economics (gross margin, CAC, payback, churn)"},
    {id:"regulatory",label:"Regulatory, compliance, licensing, and legal exposure"},
    {id:"news",label:"Events in the last 6-12 months that change the picture"},
    {id:"customer",label:"Customer adoption trends, buyer behaviour, and objections"},
    {id:"contrarian",label:"Disconfirming evidence: failures, shutdowns, declining metrics, and the strongest case AGAINST this working"},
  ],
};
 
/** Reads the question and picks the angle set that fits it. */
function pickAngleSet(question:string,execIds?:string[]):Array<{id:string;label:string}>{
  const q=String(question||"").toLowerCase();
  const ex=(execIds||[]).join(" ");
  if(/\btrade\b|import|export|customs|hs code|tariff|duty|incoterm|shipping|freight|consignment|exporter|importer/.test(q)||/trade/.test(ex))
    return ANGLE_SETS.trade;
  if(/\bmanager\b|promotion|appraisal|salary|resign|notice period|burnout|work.?life|colleague|boss|career|my job|my team lead|toxic/.test(q)||/coach/.test(ex))
    return ANGLE_SETS.career;
  return ANGLE_SETS.market;
}
 
const RESEARCH_ANGLES=[
  {id:"market",label:"Market size, growth rate, and demand trends"},
  {id:"competitors",label:"Direct and indirect competitors, their pricing and positioning"},
  {id:"pricing",label:"Industry pricing benchmarks and unit economics (gross margin, CAC, payback, churn)"},
  {id:"regulatory",label:"Regulatory, compliance, licensing, and legal exposure"},
  {id:"news",label:"Events in the last 6-12 months that change the picture"},
  {id:"customer",label:"Customer adoption trends, buyer behaviour, and objections"},
  {id:"contrarian",label:"Disconfirming evidence: failures, shutdowns, declining metrics, and the strongest case AGAINST this working"},
];

// Synthesis pass. Runs after all angles. No new search — it reasons over what
// was actually retrieved, so it cannot introduce unsourced figures.
function synthesisPrompt(co,question,sections){
  return "You are the Chief Research Officer reviewing your desk's raw findings before they go to the board of \""+co.name+"\". "+
  "Below are the raw research sections. You may NOT introduce any figure that does not already appear below. You may NOT search. Your only job is to interrogate, reconcile, and prioritise what is already there.\n\n"+
  "QUESTION UNDER ANALYSIS: \""+question+"\"\n\nRAW FINDINGS:\n"+sections+
  "\n\nProduce exactly these five sections, in this order, in markdown. Be concise and decisive. No preamble.\n\n"+
  "## BOTTOM LINE UP FRONT\nThe 3-5 findings that most change the decision, hardest-hitting first. One line each: the finding, then ' \u2192 ' then the decision implication. Nothing that does not change a decision belongs here.\n\n"+
  "## CONFLICTING DATA\nAny metric that appears more than once with different values. For each: state the values and sources, pick ONE canonical figure, and give the one-line reason you chose it. If nothing conflicts, write 'No material conflicts detected.'\n\n"+
  "## THE CASE AGAINST\nThe strongest evidence-backed argument that this should NOT proceed, or should proceed differently. Draw only on the findings above. If the contrarian angle found nothing, say so explicitly and mark it a research failure, not a green light.\n\n"+
  "## EVIDENCE GAPS\nWhat the board will need that the research could NOT establish. Name each gap and the cheapest way to close it. Distinguish 'no data exists' from 'we did not find it'.\n\n"+
  "## QUESTIONS FOR THE BOARD\n3-5 framed tensions, each written as a real trade-off with two defensible sides, each anchored to a specific finding above. These are what the executives will argue about, so they must be genuinely contested, not rhetorical. Format: 'Tension: ... | Side A: ... | Side B: ... | Anchored to: <finding>'.";
}

async function runResearchDesk(ask,co,compData,question,showToast,keys){
  // Native web search (no Serper): Gemini, or Claude only if a research stage
  // is configured to a premium provider. Was unconditional Claude fallback.
  const researchPremiumOk=PREMIUM_PROVIDERS.includes((STAGE_PROFILE as any)?.research_extract)||PREMIUM_PROVIDERS.includes((STAGE_PROFILE as any)?.research_synthesis);
  let routes=resolveSearchProviders(keys).filter((r:any)=>researchPremiumOk||!PREMIUM_PROVIDERS.includes(r.provider));
  // When an external search service (Serper/Tavily/Brave/DataForSEO) is
  // configured, the app does the searching itself and the model only READS the
  // results - so any text model can do it. THE FIX: this used to apply only when
  // there was NO Claude or Gemini key. With one present, research ran on
  // [claude, gemini] only - DeepSeek was never used and Claude read everything.
  // Now, whenever Serper (or similar) is available, the research_extract stage
  // choice leads (DeepSeek by default) and the rest follow cheapest-first.
  if(hasExternalSearch(SEARCH_CHAIN,keys)){
    const ordered=costOrderedProviders(keys,"research_extract");
    if(ordered.length)routes=ordered;
  }
  const route=routes[0];
  if(!route){
    // WAS: "no Claude or Gemini key" - which was wrong and sent you hunting
    // for the wrong fix. A Serper key plus ANY model is enough, and is the
    // cheaper of the two routes by roughly ten times.
    try{showToast&&showToast("Research Desk cannot reach the web. Cheapest fix: Settings \u2192 Web Search Service \u2192 add a Serper key (2,500 free searches). Any AI provider you already have, NVIDIA included, can then do the reading.","warning");}catch{}
    return {
      brief:"⚠ **RESEARCH DESK OFFLINE — NO SEARCH-CAPABLE PROVIDER**\n\nNo way to reach the live web is currently configured, so no live figure could be retrieved for this question.\n\n**Every number produced below is model-generated and UNVERIFIED.** Do not use any figure in this session for a real decision, valuation, budget, or external document until independently confirmed.\n\nFix — either option works:\n1. CHEAPEST: Settings → Web Search Service → add a Serper key (~$1 per 1,000 searches, 2,500 free). Any AI provider you already have can then do the reasoning.\n2. Settings → API Keys → switch on Claude or Gemini and let the model search for itself (~$10–14 per 1,000 searches).",
      grounded:false,provider:"none"
    };
  }
  // THE REQUESTED PROMPT-TRANSFORMATION STEP: the raw strategic question
  // typed into AI Boardroom is rarely itself a good search query — it's
  // written for a human executive, not a search engine or a research
  // model. This converts it into a precise, focused research brief FIRST,
  // using the same confirmed-working provider chain as everything else in
  // this function (no new dependency), and shows that brief to the user
  // before any searching starts, so they can see exactly what's actually
  // being researched. Kept deliberately short and cheap — one small call,
  // not another multi-step research pass of its own.
  // ── PROBLEM DECOMPOSITION (Opportunity Intelligence, phases 4/9/10) ──────
  // One small call that turns the raw question into structured intelligence:
  // the real objective, the assumptions hidden in the question, the dimensions
  // of the problem, the research questions, and questions about ADJACENT
  // opportunities the user did not ask about. Search gets a short keyword
  // focus (queries only keep 8 words); the research model gets the full
  // decomposition. Any failure falls back to the raw question - research is
  // never blocked by this step.
  // Provider failures during research are COLLECTED and reported once at the end
  // (was: one sticky warning per failure, even when a later provider succeeded).
  const rdNotes:{prov:string;msg:string}[]=[];
  const rdSummary=(provider:string,grounded:boolean)=>{
    if(!rdNotes.length)return;
    const byProv=Array.from(new Set(rdNotes.map(n=>n.prov+": "+attemptResultFor(n.msg).replace(/_/g," "))));
    try{showToast&&showToast(grounded&&provider?"Research completed using "+provider+" after "+rdNotes.length+" provider fallback"+(rdNotes.length===1?"":"s")+" ("+byProv.join(" \u00b7 ")+")."
      :"Research could not use some providers: "+byProv.join(" \u00b7 ")+".",grounded?"info":"warning");}catch{}
  };
  let effectiveQuestion=question;   // used for the research instruction
  let searchQuestion=question;      // used to build search-engine queries
  let decomposition:any=null;
  try{
    const decompPrompt="You are the problem-decomposition stage of a business research system. "+
      "Do NOT answer the question. Return ONLY one JSON object, no markdown, with these keys:\n"+
      "objective: the underlying business objective in one sentence;\n"+
      "assumptions: array of assumptions hidden in how the question is framed;\n"+
      "dimensions: array of 4-10 problem dimensions that actually matter here (adapt to the problem - do not use a fixed checklist);\n"+
      "research_questions: array of 3-6 specific, researchable questions needed to answer it;\n"+
      "adjacent_opportunity_questions: array of 2-4 questions about adjacent areas of the value chain or ecosystem where value may be created or lost that the user did NOT ask about. If none are plausible, return an empty array - never invent novelty;\n"+
      "search_focus: a 4-8 word keyword phrase for a web search engine.";
    const rf=await callSearchWithFailover(routes,decompPrompt,
      [{role:"user",content:"RAW QUESTION:\n"+question}],900,false,()=>{});
    const raw=stripPreamble((rf?.out&&typeof rf.out==="object"&&"text" in rf.out)?rf.out.text:String(rf?.out||""));
    const a=raw.indexOf("{"),b=raw.lastIndexOf("}");
    if(a>=0&&b>a){
      const d=JSON.parse(raw.slice(a,b+1));
      const arr=(x:any)=>Array.isArray(x)?x.map((v:any)=>String(v)).filter((v:string)=>v.trim()).slice(0,10):[];
      decomposition={objective:String(d.objective||"").trim(),assumptions:arr(d.assumptions),dimensions:arr(d.dimensions),
        research_questions:arr(d.research_questions),adjacent_opportunity_questions:arr(d.adjacent_opportunity_questions),
        search_focus:String(d.search_focus||"").trim()};
      if(decomposition.research_questions.length){
        effectiveQuestion="ORIGINAL QUESTION: "+question+
          (decomposition.objective?"\nUNDERLYING OBJECTIVE: "+decomposition.objective:"")+
          "\nRESEARCH QUESTIONS:\n- "+decomposition.research_questions.join("\n- ")+
          (decomposition.adjacent_opportunity_questions.length?"\nADJACENT OPPORTUNITIES TO CHECK (report only what evidence supports; say so if nothing material is found):\n- "+decomposition.adjacent_opportunity_questions.join("\n- "):"")+
          (decomposition.assumptions.length?"\nASSUMPTIONS IN THE QUESTION TO TEST:\n- "+decomposition.assumptions.join("\n- "):"");
      }
      if(decomposition.search_focus&&decomposition.search_focus.split(/\s+/).length>=2)searchQuestion=decomposition.search_focus+" "+question;
      try{showToast&&showToast("Research plan: "+(decomposition.objective||question).slice(0,160)+" \u2014 "+decomposition.research_questions.length+" research questions, "+decomposition.adjacent_opportunity_questions.length+" adjacent-opportunity checks.","info");}catch{}
    }
  }catch{ decomposition=null; /* decomposition failing must never block research - raw question is used */ }

  try{showToast&&showToast("Research Desk running deep multi-angle search via "+routes.map(r=>r.provider).join(" → ")+"… this can take up to 2-3 minutes.","info");}catch{}
  let usedProvider=route.provider;
  const useExternalSearch=hasExternalSearch(SEARCH_CHAIN,keys);
  let searchProviderUsed="";
  let searchCount=0;
  let libReused=0;
  let libSaved=0;
  try{
    if(useExternalSearch)showToast&&showToast("Research Desk: searching the web directly (cheaper, and every source keeps its real URL).","info");
  }catch{}
  const prevUsageFeature=USAGE_CTX.feature, prevUsageIcon=USAGE_CTX.icon;
  setUsageFeature("Research Desk","📡");
  const sections=[];
  let anyFail=false;
  let anyTruncated=false;
  // Use the angle set that matches this question, not the fixed business seven.
  const ACTIVE_ANGLES=pickAngleSet(question);
  for(const angle of ACTIVE_ANGLES){
    try{
      let anglePrompt=researchDeskPrompt(co,compData,effectiveQuestion)+
        "\n\nFOCUS THIS PASS ENTIRELY ON: "+angle.label+
        ".\nReturn 4-6 bullets, each following the OUTPUT CONTRACT exactly. Do not cover other angles.";
      // RETRIEVAL FIRST: when a search key is configured, the APP searches and
      // hands the model real URLs. The model then only has to structure them —
      // it can no longer choose whether to show a source, which is what caused
      // 63 "Verified Fact" claims with zero URLs in the previous build.
      let angleSearchUsed=false;
      // LIBRARY READ - reuse facts already established for this market.
      let libBlock="";
      if(LIBRARY_ENABLED){
        try{
          const libFacts=await fetchFacts(supabase,{industry:co?.industry||"",geography:co?.location||"",angle:angle.id,limit:5});
          if(libFacts.length){
            libBlock="\n\n"+formatLibraryFacts(libFacts);
            libReused+=libFacts.length;
          }
        }catch{}
      }
      if(useExternalSearch){
        const qs=buildAngleQueries(searchQuestion,angle.id,co);
        const found:any[]=[];const seenUrls=new Set<string>();
        // Social and user-upload sites are never acceptable evidence for a capital
        // decision. A previous brief cited an Instagram reel about a tea stall, and
        // a JustDial classified anchored a crore-scale capex estimate. Dropped here,
        // before the model ever sees them.
        const JUNK_DOMAINS=["instagram.com","facebook.com","pinterest.","tiktok.com","x.com","twitter.com","scribd.com","quora.com","reddit.com","justdial.com","aajjo.com","indiamart.com","slideshare.net","academia.edu","coursehero","youtube.com"];
        const isJunk=(u:string)=>JUNK_DOMAINS.some(d=>String(u||"").toLowerCase().includes(d));
        for(const q of qs){
          const so=await runSearch(q,SEARCH_CHAIN,keys,6,
            (prov,msg)=>{rdNotes.push({prov,msg:String(msg)});});
          if(so.provider&&so.provider!=="none")searchProviderUsed=so.provider;
          if(!so.fromCache&&so.results.length){
            searchCount++;
            if(LIBRARY_ENABLED){try{logQuery(supabase,{query_text:q,angle:angle.id,industry:co?.industry||"",geography:co?.location||"",provider:so.provider,results_count:so.results.length,usable_count:so.results.filter((x:any)=>!isJunk(x.url)).length,junk_count:so.results.filter((x:any)=>isJunk(x.url)).length});}catch{}}
            try{
              const sr=saveUnitRecord({feature:"Research Desk (web search)",featureIcon:"🔍",provider:so.provider,model:so.provider,units:1,unitLabel:"queries",userEmail:USAGE_CTX.userEmail,userRole:USAGE_CTX.userRole});
              queueUsageSync({user_email:USAGE_CTX.userEmail,user_role:USAGE_CTX.userRole,
                feature:"Research Desk (web search)",feature_icon:"🔍",provider:so.provider,model:so.provider,
                kind:"search",input_tokens:0,output_tokens:0,units:1,unit_label:"queries",
                cost_usd:Number((sr as any)?.costUsd)||0,estimated:false,session_key:(sr as any)?.session||null});
            }catch{}
          }
          for(const rr of so.results){
            if(isJunk(rr.url))continue;
            if(!seenUrls.has(rr.url)){seenUrls.add(rr.url);found.push(rr);}
          }
        }
        if(found.length){
          angleSearchUsed=true;
          anglePrompt+="\n\n"+formatResultsForPrompt(found,"RETRIEVED SOURCES FOR THIS ANGLE (application-supplied, real URLs):")
            +"\n\n"+RETRIEVED_RESULTS_RULES+libBlock;
        }
      }
      if(!angleSearchUsed&&libBlock)anglePrompt+=libBlock;
      const fo=await callSearchWithFailover(routes,anglePrompt,[{role:"user",content:"Research this angle now."}],5200,!angleSearchUsed,
        (prov,msg)=>{rdNotes.push({prov,msg:String(msg)});});
      const raw=fo.out; usedProvider=fo.provider;
      const text=(raw&&typeof raw==="object"&&"text" in raw)?raw.text:String(raw||"");
      if(raw&&typeof raw==="object"&&(raw as any).truncated)anyTruncated=true;
      const clean=stripPreamble(text);
      sections.push("### "+angle.label+"\n"+(clean||"⚠ No usable findings returned for this angle."));
    }catch(angleErr:any){
      anyFail=true;
      sections.push("### "+angle.label+"\n⚠ Could not retrieve: "+String(angleErr?.message||angleErr));
    }
  }
  let body=sections.join("\n\n");
  const urlCount=(body.match(/https?:\/\//g)||[]).length;
  const grounded=urlCount>0;
  if(!grounded){
    rdSummary("",false);
    try{showToast&&showToast("Research Desk returned no source URLs — session is UNGROUNDED.","warning");}catch{}
    return {brief:"⚠ **RESEARCH DESK RETURNED NO VERIFIABLE SOURCES**\n\nSearch ran via "+usedProvider+" but produced no source URL across any angle, so nothing below can be verified.\n\n---\n"+body,grounded:false,provider:route.provider,decomposition};
  }
  body=badgeBrief(body);

  // ── Synthesis pass ──
  let synth="";
  try{
    try{showToast&&showToast("Research Desk synthesising findings…","info");}catch{}
    // 3000 tokens was cutting the synthesis off mid-sentence - one Time Machine
    // brief stopped at bullet 2 and never rendered its remaining four sections.
    // The synthesis stage is routed separately: it reconciles conflicts, which is
    // harder than extraction, so you may want a stronger model here than for angles.
    const synRoute=stageRoute(keys,"research_synthesis");
    const synRoutes=synRoute?[{provider:synRoute.provider,key:synRoute.key},...routes.filter(r=>r.provider!==synRoute.provider)]:routes;
    const sFo=await callSearchWithFailover(synRoutes,synthesisPrompt(co,question,body),[{role:"user",content:"Synthesise now."}],4500,false,null);
    const sRaw=sFo.out;
    const sText=(sRaw&&typeof sRaw==="object"&&"text" in sRaw)?sRaw.text:String(sRaw||"");
    if(sRaw&&typeof sRaw==="object"&&(sRaw as any).truncated)anyTruncated=true;
    synth=String(sText||"").trim();
  }catch(sErr:any){
    synth="";
    anyFail=true;
  }

  // LIBRARY WRITE - store the FACTS only. The "So what" clause is discarded
  // because it is written about the user's own business; anything naming the
  // company or addressing the reader is rejected by sanitiseFact().
  if(LIBRARY_ENABLED){
    try{
      const {data:{user}}=await supabase.auth.getUser();
      if(user){
        const facts=extractFacts(body,RESEARCH_ANGLES,co?.industry||"",co?.location||"",co?.name||"");
        libSaved=await saveFacts(supabase,facts,user.id);
      }
    }catch(e){console.warn("[OIQ] library write skipped:",e);}
  }
 
  const stamp="_Research Desk v5 — "+RESEARCH_ANGLES.length+" angles + synthesis. "+(searchProviderUsed?("Web search via "+searchProviderUsed+" ("+searchCount+" queries, approx $"+estimateSearchCost(searchProviderUsed as any,searchCount).toFixed(3)+")."):"Model-native search.")+(LIBRARY_ENABLED?(" Library: "+libReused+" facts reused, "+libSaved+" saved."):"")+" Retrieved "+new Date().toISOString().slice(0,10)+". Publication dates are shown per finding._";
  let brief=synth
    ? synth+"\n\n---\n\n## SOURCE FINDINGS BY ANGLE\n\n"+body+"\n\n"+stamp
    : "⚠ Synthesis pass failed — raw findings only, no prioritisation.\n\n"+body+"\n\n"+stamp;
  if(anyTruncated)brief="⚠ **Some research output hit the length limit and may be incomplete.** Treat any cut-off finding as unconfirmed.\n\n"+brief;
  try{const fxw=fxPlausibilityWarnings(brief);if(fxw)brief=fxw+"\n"+brief;}catch{}
  // Every generated output states who produced it and how they handle the data.
  try{brief=brief+"\n\n"+attributionLine(usedProvider,"");}catch{}
  if(anyFail)brief="⚠ Some research angles could not be completed — see notes below.\n\n"+brief;
  setUsageFeature(prevUsageFeature,prevUsageIcon);
  rdSummary(usedProvider,grounded);
  return {brief,grounded,provider:usedProvider,decomposition};
}
// ── FINANCIAL LIVE FEED ─────────────────────────────────────────────────────
// Fetches live financial data for Indian markets. Called at session start.
// Injected into finance-category executives via buildSys().
let LIVE_RATES={loaded:false,ts:"",data:""};
// Machine-readable rates, separate from the prose block above. Prompts need the
// prose; the cost panel and the plausibility guard need real numbers.
let FX_RATES:Record<string,number>={};
 
// The Research Desk once wrote "$1,041/ton (approximately Rs 8,645 at 8.3 INR/USD)".
// The live rate was 95.75, shown on the user's own screen at that moment. Every
// downstream figure - revenue, margin, break-even - was then wrong by 11x.
// This block is injected into EVERY prompt that can produce a currency figure.
function fxRuleBlock():string{
  const usd=FX_RATES.USD;
  if(!usd)return "\n\nCURRENCY RULE: no live exchange rate is available in this session. Do NOT convert between currencies. Quote every figure in the currency its source used, and say the conversion was not possible.\n";
  const lo=(usd*0.9).toFixed(0), hi=(usd*1.1).toFixed(0);
  return "\n\nCURRENCY RULE (non-negotiable, live rate):\n"
    +"USD/INR = "+usd.toFixed(2)+" as of "+(LIVE_RATES.ts||new Date().toISOString().slice(0,10))+".\n"
    +"- Use THIS rate for every USD-to-INR conversion. Do not use a remembered or assumed rate.\n"
    +"- Show your working: $X * "+usd.toFixed(2)+" = Rs Y. A conversion with no working is not acceptable.\n"
    +"- SANITY CHECK: any USD-to-INR conversion must land between "+lo+"x and "+hi+"x the dollar figure. If your result falls outside that band you have made an arithmetic error - recompute before writing it down.\n"
    +"- Indian figures use lakh and crore (1 lakh = 100,000; 1 crore = 10,000,000). Never write 'Rs 8,645 crore' when you mean 'Rs 8,645'.\n";
}
 
// Deterministic post-check. Flags conversions outside the plausible band so a 10x
// error is visible to the reader instead of silently becoming the basis of a
// recommendation. Text-only: it annotates, it never rewrites the numbers.
function fxPlausibilityWarnings(text:string):string{
  try{
    const usd=FX_RATES.USD;
    if(!usd||!text)return "";
    const bad:string[]=[];
    const re=/\$\s?([\d,]+(?:\.\d+)?)\s*(million|billion|bn|mn)?[^.\n]{0,60}?(?:\u20b9|Rs\.?\s?)\s?([\d,]+(?:\.\d+)?)\s*(lakh|crore|cr|million|billion)?/gi;
    let m:any;
    while((m=re.exec(text))!==null&&bad.length<6){
      const mult=(s:string)=>({million:1e6,mn:1e6,billion:1e9,bn:1e9,lakh:1e5,crore:1e7,cr:1e7} as any)[String(s||"").toLowerCase()]||1;
      const d=parseFloat(m[1].replace(/,/g,""))*mult(m[2]);
      const r=parseFloat(m[3].replace(/,/g,""))*mult(m[4]);
      if(!isFinite(d)||!isFinite(r)||d<=0||r<=0)continue;
      const implied=r/d;
      if(implied<usd*0.6||implied>usd*1.6){
        bad.push("$"+m[1]+(m[2]?" "+m[2]:"")+" stated as Rs "+m[3]+(m[4]?" "+m[4]:"")+" implies a rate of "+implied.toFixed(1)+" (live rate is "+usd.toFixed(2)+")");
      }
    }
    if(!bad.length)return "";
    return "\n\n---\n\u26a0 **CURRENCY CHECK FAILED** - these conversions do not match the live rate and should not be relied on:\n"+bad.map(b=>"- "+b).join("\n")+"\n";
  }catch{return "";}
}
 
async function fetchLiveRates(){
  if(LIVE_RATES.loaded)return LIVE_RATES.data;
  try{
    // Exchange rates — free, no key needed
    const fx=await fetch("https://open.er-api.com/v6/latest/INR").then(r=>r.json());
    const usd=fx?.rates?.USD?(1/fx.rates.USD).toFixed(2):"N/A";
    const aed=fx?.rates?.AED?(1/fx.rates.AED).toFixed(3):"N/A";
    const gbp=fx?.rates?.GBP?(1/fx.rates.GBP).toFixed(2):"N/A";
    const ts=new Date().toLocaleString("en-IN",{timeZone:"Asia/Kolkata",dateStyle:"medium",timeStyle:"short"});
    FX_RATES={
      USD: fx?.rates?.USD?1/fx.rates.USD:0,
      AED: fx?.rates?.AED?1/fx.rates.AED:0,
      GBP: fx?.rates?.GBP?1/fx.rates.GBP:0,
      EUR: fx?.rates?.EUR?1/fx.rates.EUR:0,
    };
    LIVE_RATES={
      loaded:true,ts,
      data:`LIVE MARKET DATA (${ts} IST):
• USD/INR: ₹${usd} | AED/INR: ₹${aed} | GBP/INR: ₹${gbp}
• RBI Repo Rate: 6.25% (as of Jun 2025 — verify at rbi.org.in for latest)
• Note: All financial figures in responses must use these rates or label as ESTIMATE (unverified).`
    };
    return LIVE_RATES.data;
  }catch{
    return "";
  }
}

// Finance executive IDs that benefit from live rates
const FINANCE_ROLES=["cfo","vp_finance","fin_ctrl","sr_acct","acct_exec","coo","ceo","chairman"];

// ─── DECISION THREAD HELPERS ──────────────────────────────────────────────────
function extractDecisionStatus(synthesis){
  const order=["Do Not Proceed","Proceed with Conditions","Needs More Information","No Consensus","Proceed"];
  for(const s of order){if(synthesis&&synthesis.includes("DECISION STATUS: "+s))return s;}
  // fallback scan without prefix
  for(const s of order){if(synthesis&&synthesis.includes(s))return s;}
  return "No Consensus";
}

const DOMAIN_EXEC_MAP={
  finance:[{id:"cfo",reason:"Financial strategy & modelling"},{id:"fin_ctrl",reason:"Controls & reporting"},{id:"vp_fin",reason:"FP&A & budgeting"}],
  audit:[{id:"cia",reason:"Internal audit strategy"},{id:"risk_mgr",reason:"Enterprise risk"}],
  hr:[{id:"chro",reason:"People strategy"},{id:"vp_hr",reason:"HR operations"}],
  legal:[{id:"clo",reason:"Legal & regulatory"},{id:"comp_mgr",reason:"Compliance programme"}],
  marketing:[{id:"cmo",reason:"GTM & brand strategy"},{id:"dir_growth",reason:"Growth & performance"}],
  sales:[{id:"sl",reason:"Revenue strategy"},{id:"vp_sales",reason:"Sales execution"}],
  technology:[{id:"cto",reason:"Technology architecture"},{id:"dir_prod",reason:"Product roadmap"}],
  operations:[{id:"coo",reason:"Operational execution"},{id:"proj_mgr",reason:"Delivery & planning"}],
  strategy:[{id:"cso",reason:"Corporate strategy"},{id:"strat_mgr",reason:"Strategic analysis"}],
  customer_success:[{id:"vp_cx",reason:"Customer retention"},{id:"csm",reason:"Account success"}],
  executive:[{id:"ceo",reason:"Final decision authority"},{id:"board",reason:"Governance & fiduciary"}],
  general:[{id:"ceo",reason:"Executive decision authority"},{id:"coo",reason:"Operational oversight"}],
};

function suggestFollowUpExecs(question,previousExecIds){
  // Use IE classifyDomain (pure logic — no LLM call)
  const domain=classifyDomain(question);
  const candidates=DOMAIN_EXEC_MAP[domain]||DOMAIN_EXEC_MAP.general;
  // Return only execs NOT already in the previous stage selection
  return candidates
    .filter(c=>!previousExecIds.includes(c.id))
    .filter(c=>AR.find(r=>r.id===c.id))
    .slice(0,3);
}

function autoRoute(text){
  const t=text.toLowerCase();
  const kw={
    finance:["p&l","profit","loss","balance sheet","cash flow","revenue","expense","budget","forecast","variance","mis","financial","invoice","reconcil","ebitda","burn"],
    tax:["gst","tds","tax","filing","return","indirect tax","direct tax","advance tax","statutory","penalty"],
    audit:["audit","control","risk","sox","itgc","assurance","fraud","testing","workpaper","finding"],
    hr_task:["hire","hiring","recruitment","employee","onboard","payroll","salary","compensation","leave","policy","performance","appraisal","headcount","talent","training","job description","interview"],
    legal_task:["contract","legal","nda","agreement","msa","ip","intellectual property","trademark","patent","compliance","regulation","clause","liability","gdpr","fema"],
    marketing_task:["marketing","campaign","brand","seo","paid","ads","digital","content","social media","email","go-to-market","acquisition","funnel","conversion","pr","launch"],
    sales_task:["sales","pipeline","deal","prospect","outreach","proposal","pricing","crm","business development","partnership","commission","quota"],
    tech_task:["tech","technical","architecture","api","database","system design","software","engineering","sprint","product","feature","bug","deployment","infrastructure","cloud","devops","spec","backlog","roadmap"],
    ops_task:["operations","process","sop","vendor","supply chain","logistics","project","delivery","efficiency","scaling","kpi","milestone","procurement"],
    strategy_task:["strategy","strategic","market entry","expansion","merger","competitive","analysis","pestle","swot","market sizing","business model","innovation"],
    cx_task:["customer","support","nps","csat","retention","churn","onboarding","renewal","upsell","ticket","complaint","escalation","satisfaction"],
    executive_task:["board","executive","ceo","chairman","cross-functional","strategic decision","board approval","annual plan","investor","fundraising","ipo"],
  };
  const scores={};
  Object.entries(kw).forEach(([cat,words])=>{scores[cat]=words.filter(w=>t.includes(w)).length;});
  const best=Object.entries(scores).sort((a,b)=>b[1]-a[1]);
  return best[0][1]>0?best[0][0]:"finance";
}

const DEPTS=[
  {id:"exec",l:"Executive",c:"#14B8A6",roles:[
    {id:"chairman",t:"Chairman",f:"Executive Chairman",ic:"🏛️",d:"Board Leadership · Strategic Oversight",qa:["Set board agenda","Evaluate strategic direction","Governance review","Stakeholder communication plan"]},
    {id:"ceo",t:"CEO",f:"Chief Executive Officer",ic:"👔",d:"Strategy · Vision · Final Decisions",qa:["Build 90-day strategic plan","Validate our business idea","Competitive landscape","Build investor narrative"]},
    {id:"board",t:"Board Advisor",f:"Board Director and Governance Expert",ic:"🎯",d:"Governance · Risk Oversight · Fiduciary",qa:["Design governance framework","Board agenda template","Strategic risk scan","Decision accountability framework"]},
    {id:"coo",t:"COO",f:"Chief Operating Officer",ic:"🔧",d:"Process Design · Scaling · Efficiency",qa:["Map our core processes","Design scaling playbook","Build SOP framework","Vendor management strategy"]},
  ]},
  {id:"fin",l:"Finance",c:"#3B82F6",roles:[
    {id:"cfo",t:"CFO",f:"Chief Financial Officer",ic:"📊",d:"Financial Strategy · Modeling · Capital",qa:["Model our burn rate and runway","Build 3-year financial projection","Unit economics","Design fundraising model"]},
    {id:"vp_fin",t:"VP Finance",f:"Vice President of Finance",ic:"📈",d:"FP&A · Budgeting · Investor Relations",qa:["Build annual budget","Prepare board financial pack","Variance analysis","Investor reporting template"]},
    {id:"fin_ctrl",t:"Financial Controller",f:"Financial Controller",ic:"🗂️",d:"Month-end Close · Reporting · Controls",qa:["Design month-end close process","Build financial controls checklist","Intercompany reconciliation","Trial balance review"]},
    {id:"tax_mgr",t:"Tax Manager",f:"Senior Tax Manager",ic:"🧾",d:"Direct and Indirect Tax · GST",qa:["GST filing checklist","TDS compliance calendar","Transfer pricing documentation","Advance tax calculation"]},
    {id:"acct",t:"Sr. Accountant",f:"Senior Accountant",ic:"🧮",d:"Bookkeeping · Reconciliation · Ind AS",qa:["Design chart of accounts","Build expense management system","GST compliance checklist","Invoice and reconciliation workflow"]},
    {id:"acct_exe",t:"Accounts Executive",f:"Accounts Executive",ic:"🗒️",d:"Data Entry · Invoicing · Payables",qa:["Process vendor invoices","Reconcile bank statement","Prepare payment run","Track outstanding receivables"]},
  ]},
  {id:"tech",l:"Technology",c:"#8B5CF6",roles:[
    {id:"cto",t:"CTO",f:"Chief Technology Officer",ic:"⚙️",d:"Tech Strategy · Architecture · Innovation",qa:["Recommend our tech stack","Design system architecture","Build tech roadmap Q1-Q4","Build vs buy analysis"]},
    {id:"dir_prod",t:"Dir. Product",f:"Director of Product Management",ic:"🗺️",d:"Product Vision · Roadmap · PMF",qa:["Build product roadmap","Define product vision","Prioritise features using RICE","Write PRD for flagship feature"]},
    {id:"tl",t:"Tech Lead",f:"Technical Lead and Engineering Manager",ic:"💻",d:"Sprints · Specs · DevOps · Code Quality",qa:["Plan this sprint","Write technical specification","Design our API architecture","Set up CI/CD pipeline"]},
    {id:"sr_dev",t:"Sr. Developer",f:"Senior Software Engineer",ic:"🛠️",d:"Architecture · Code Review · Mentoring",qa:["Review system design","Write API contract","Code review checklist","Optimise database queries"]},
    {id:"pm",t:"Product Manager",f:"Product Manager",ic:"🎛️",d:"Feature Specs · User Stories · Roadmap",qa:["Write user story","Define acceptance criteria","Competitor feature matrix","Sprint backlog grooming"]},
    {id:"qa",t:"QA Lead",f:"QA and Testing Lead",ic:"🔍",d:"Test Strategy · Automation · Quality Gates",qa:["Build test plan","Define regression test suite","Bug triage framework","Performance testing approach"]},
    {id:"data_ana",t:"Data Analyst",f:"Data Analyst",ic:"📊",d:"SQL · Dashboards · Insights · A/B Tests",qa:["Analyse user funnel drop-off","Build cohort analysis","A/B test design","Weekly metrics report template"]},
  ]},
  {id:"ops",l:"Operations",c:"#F59E0B",roles:[
    {id:"coo2",t:"COO (Ops)",f:"Chief Operating Officer — Operations Focus",ic:"⚡",d:"Operational Excellence · Scaling",qa:["Design operations blueprint","Define OKRs for ops team","Build vendor scorecard","Operational risk register"]},
    {id:"proj_mgr",t:"Project Manager",f:"Senior Project Manager PMP",ic:"📅",d:"Project Planning · Risk · Delivery",qa:["Build project plan","RAID log template","Stakeholder communication plan","Change management framework"]},
    {id:"sm_ops",t:"Sr. Ops Manager",f:"Senior Operations Manager",ic:"📋",d:"Day-to-day Ops · SOP Execution · Reporting",qa:["Write SOP for core process","Daily ops checklist","Vendor performance review","Exception handling playbook"]},
    {id:"ops_ana",t:"Ops Analyst",f:"Operations Analyst",ic:"📌",d:"Process Analysis · Data · Continuous Improvement",qa:["Process efficiency analysis","Root cause analysis","Build ops metrics report","Lean improvement plan"]},
  ]},
  {id:"mktg",l:"Marketing",c:"#EF4444",roles:[
    {id:"cmo",t:"CMO",f:"Chief Marketing Officer",ic:"📣",d:"GTM · Brand · Growth · Demand Gen",qa:["Build go-to-market strategy","Define brand positioning","Design acquisition funnel","Market segmentation and sizing"]},
    {id:"vp_mktg",t:"VP Marketing",f:"Vice President of Marketing",ic:"📡",d:"Brand Strategy · Channel Mix · Marketing P&L",qa:["Build annual marketing plan","Allocate budget across channels","Brand positioning review","Marketing team structure"]},
    {id:"dir_growth",t:"Dir. Growth",f:"Director of Growth and Performance",ic:"🚀",d:"Paid · SEO · CRO · Lifecycle Marketing",qa:["Growth experiment roadmap","Paid media strategy","SEO content plan","Email nurture sequence"]},
    {id:"sm_mktg",t:"Sr. Mktg Mgr",f:"Senior Marketing Manager",ic:"📢",d:"Campaign Execution · Reporting",qa:["Campaign brief","Channel performance report","Agency briefing template","A/B test plan"]},
    {id:"mktg_ana",t:"Marketing Analyst",f:"Marketing Analyst",ic:"📐",d:"Attribution · Reporting · Audience Insights",qa:["Build marketing dashboard","Attribution model analysis","Audience segmentation report","Competitor ad analysis"]},
    {id:"mktg_exe",t:"Mktg Executive",f:"Marketing Executive",ic:"📲",d:"Campaign Execution · Social Media · Content",qa:["Social media calendar","Email campaign copy","Ad creative brief","Campaign performance tracker"]},
  ]},
  {id:"sales",l:"Sales & Revenue",c:"#F97316",roles:[
    {id:"sl",t:"Sales Director",f:"Director of Sales and Revenue",ic:"🤝",d:"Pipeline · Pricing · Enterprise Deals",qa:["Design our sales pipeline","Build pricing strategy","Write sales playbook","Partner and channel strategy"]},
    {id:"vp_sales",t:"VP Sales",f:"Vice President of Sales",ic:"💰",d:"Revenue Strategy · Team Targets · Key Accounts",qa:["Set sales targets","Design commission structure","Key account strategy","Sales team OKRs"]},
    {id:"sm_sales",t:"Sr. Sales Mgr",f:"Senior Sales Manager",ic:"🏆",d:"Team Management · Quota · Deal Reviews",qa:["Weekly deal review template","Pipeline hygiene checklist","Objection handling guide","Negotiation playbook"]},
    {id:"ae",t:"Account Exec",f:"Account Executive",ic:"🎯",d:"Closing · Presentations · CRM",qa:["Write executive proposal","Competitive battle card","ROI calculator for prospect","Contract negotiation checklist"]},
    {id:"sdr",t:"SDR",f:"Sales Development Representative",ic:"📧",d:"Prospecting · Outreach · Qualification",qa:["Cold outreach email sequence","LinkedIn prospecting script","ICP definition","Lead qualification checklist"]},
  ]},
  {id:"hr",l:"People & Culture",c:"#10B981",roles:[
    {id:"chro",t:"CHRO",f:"Chief Human Resources Officer",ic:"👥",d:"Org Design · Culture · Total Rewards",qa:["Design org structure for our stage","Build hiring roadmap","Design compensation framework","Culture and engagement strategy"]},
    {id:"vp_hr",t:"VP People",f:"Vice President of People and Culture",ic:"🌱",d:"People Strategy · DEI · L&D",qa:["Annual people strategy","DEI roadmap","L&D framework","Engagement survey design"]},
    {id:"hr_biz",t:"HR Business Partner",f:"Senior HR Business Partner",ic:"🤲",d:"Org Health · Performance · Change Mgmt",qa:["Performance review framework","Restructuring playbook","HRBP engagement model","Conflict resolution SOP"]},
    {id:"sm_hr",t:"Sr. HR Manager",f:"Senior HR Manager",ic:"📋",d:"HR Ops · Policies · Compliance · Payroll",qa:["HR policy handbook","Payroll process design","Statutory compliance checklist","Employee lifecycle workflow"]},
    {id:"rec",t:"Recruiter",f:"Talent Acquisition Specialist",ic:"🔍",d:"JDs · Sourcing · Interview Design",qa:["Write job description","Design interview scorecard","Build sourcing strategy","Create skills assessment"]},
    {id:"hr_exe",t:"HR Executive",f:"HR Executive",ic:"📝",d:"Onboarding · Records · Employee Queries",qa:["New joiner onboarding checklist","Employee records audit","Exit interview template","Leave policy FAQ"]},
  ]},
  {id:"legal",l:"Legal & Compliance",c:"#EC4899",roles:[
    {id:"clo",t:"CLO",f:"Chief Legal Officer and GC",ic:"⚖️",d:"Corporate Law · IP · Regulatory · Contracts",qa:["Guide company registration steps","Protect our IP assets","Regulatory compliance scan","Design contract framework"]},
    {id:"dir_comp",t:"Dir. Compliance",f:"Director of Compliance and Risk",ic:"🛡️",d:"Compliance Programme · Audits · Controls",qa:["Compliance programme design","Internal audit plan","KYC AML framework","Regulatory change tracker"]},
    {id:"comp_mgr",t:"Compliance Mgr",f:"Compliance Manager",ic:"✅",d:"Policy · Training · Monitoring",qa:["Compliance training plan","Policy gap analysis","Compliance monitoring dashboard","Incident reporting SOP"]},
    {id:"legal_ana",t:"Legal Analyst",f:"Legal and Compliance Analyst",ic:"🔬",d:"Legal Research · Contract Review",qa:["Legal research on topic","Contract clause analysis","Regulatory filing checklist","Case law summary"]},
  ]},
  {id:"strategy",l:"Strategy & Corp Dev",c:"#6366F1",roles:[
    {id:"cso",t:"Chief Strategy Officer",f:"Chief Strategy Officer",ic:"♟️",d:"Corporate Strategy · M&A · Innovation",qa:["3-year strategic plan","M&A target screening","Innovation portfolio review","Strategic partnership evaluation"]},
    // ── TWO NEW EXECUTIVES ────────────────────────────────────────────────
    // Added as DATA, alongside the existing roles. No existing behaviour
    // changes: every module already reads this list, so both appear
    // everywhere at once - Boardroom, Executive Chat, Workflow, Autopilot.
    {id:"coach",t:"Career Coach",f:"Executive Career and Life Coach",ic:"\uD83E\uDDED",d:"Career Growth \u00b7 Difficult Managers \u00b7 Work-Life",qa:["How do I handle an unreasonable manager?","Build my 12-month promotion plan","I am burning out \u2014 what do I change first?","Negotiate my salary and title"]},
    {id:"trade",t:"Trade Officer",f:"Chief International Trade Officer",ic:"\uD83C\uDF0F",d:"Import \u00b7 Export \u00b7 Customs \u00b7 Trade Compliance",qa:["How do I start exporting from India?","Find the HS code and duty for my product","Which market should I enter first?","Get my documentation and Incoterms right"]},
    {id:"cro",t:"Chief Risk Officer",f:"Chief Risk Officer",ic:"\uD83D\uDEE1\uFE0F",d:"Strategic \u00b7 Operational \u00b7 Financial \u00b7 Regulatory \u00b7 Execution \u00b7 Control Risk",qa:["Build a risk register for this plan","What could make this decision fail?","Stress-test our key assumptions","Which controls are missing?"]},
    {id:"strat_mgr",t:"Strategy Manager",f:"Senior Strategy Manager",ic:"📐",d:"Strategic Projects · Analysis",qa:["Market sizing analysis","Five Forces analysis","Business model canvas","Strategic options evaluation"]},
    {id:"biz_ana",t:"Business Analyst",f:"Senior Business Analyst",ic:"💡",d:"Process Analysis · Requirements",qa:["Business requirements document","As-is vs to-be process map","Stakeholder analysis","Cost-benefit analysis"]},
    {id:"strat_ana",t:"Strategy Analyst",f:"Strategy and Research Analyst",ic:"🔭",d:"Market Research · Competitor Intel",qa:["Industry landscape report","Competitor deep-dive","Market entry feasibility","PESTLE analysis"]},
  ]},
  {id:"cx",l:"Customer Success",c:"#06B6D4",roles:[
    {id:"vp_cx",t:"VP Customer Success",f:"Vice President of Customer Success",ic:"⭐",d:"Retention · NPS · Expansion Revenue",qa:["Customer success strategy","Churn reduction playbook","NPS improvement plan","Customer health score design"]},
    {id:"csm",t:"CSM",f:"Customer Success Manager",ic:"📞",d:"Onboarding · Adoption · Retention",qa:["Customer onboarding plan","30-60-90 day success plan","Feature adoption campaign","At-risk customer rescue plan"]},
    {id:"support",t:"Support Lead",f:"Customer Support Team Lead",ic:"🎧",d:"Ticket Resolution · SLA · KB Articles",qa:["Support SOP design","Knowledge base article","SLA definition","Escalation matrix"]},
    {id:"cx_ana",t:"CX Analyst",f:"Customer Experience Analyst",ic:"📊",d:"Support Analytics · Sentiment · Reporting",qa:["CSAT NPS dashboard","Ticket trend analysis","Sentiment analysis framework","Support capacity model"]},
  ]},
  {id:"audit",l:"Audit & Risk",c:"#84CC16",roles:[
    {id:"cia",t:"Chief Audit Exec",f:"Chief Internal Audit Executive CIA",ic:"🔎",d:"Internal Audit Strategy · Risk Assurance",qa:["Annual audit plan","Risk-based audit universe","Audit committee report","Three lines of defence model"]},
    {id:"audit_mgr",t:"Audit Manager",f:"Internal Audit Manager",ic:"📋",d:"Audit Planning · Fieldwork · Reporting",qa:["Audit engagement plan","Fieldwork checklist","Audit observation template","Management action plan tracker"]},
    {id:"risk_mgr",t:"Risk Manager",f:"Enterprise Risk Manager ERM",ic:"⚠️",d:"Risk Register · RCSA · Risk Reporting",qa:["Enterprise risk register","RCSA template","Risk appetite statement","Key risk indicator design"]},
    {id:"audit_ana",t:"Audit Analyst",f:"Internal Audit Analyst",ic:"🔬",d:"Controls Testing · Evidence · Workpapers",qa:["Control testing template","Audit workpaper format","Exception log","Audit sampling methodology"]},
    {id:"sox_ana",t:"SOX Analyst",f:"SOX and Internal Controls Analyst",ic:"🛡️",d:"SOX 404 · Controls Design · ITGC",qa:["SOX control matrix","ITGC checklist","Control deficiency assessment","Walkthrough documentation template"]},
  ]},
  {id:"presentation",l:"Presentation Studio",c:"#A855F7",roles:[
    {id:"pres_arch",t:"Presentation Architect",f:"Presentation Architect and Narrative Strategist",ic:"🎨",d:"Workspace Synthesis · Decks · Reports · Investor Docs",qa:["Synthesize my entire workspace into an investor narrative","Build an executive briefing from recent conversations","Outline a startup pitch deck from my data","Identify the 5 key themes across all my work"]},
  ]},
];

const AR=DEPTS.flatMap(d=>d.roles.map(r=>({...r,dc:d.c,dl:d.l})));
// Both new executives are added here so they appear in the Boardroom picker
// alongside the rest. They are ordinary participants: they debate, they are
// challenged, and they challenge others.
// PHASE 14: ONE definition of the Research State shape. Every save and every
// restore passes through this, so no code path can silently drop a field.
// Fields not yet populated stay empty - never filled with invented content.
function normalizeResearchState(rs:any,fb:any={}):any{
  const r=rs||{}; const d=r.decomposition||fb.decomposition||null;
  const arr=(x:any)=>Array.isArray(x)?x:[];
  return {
    objective:r.objective||d?.objective||"",
    originalQuestion:r.originalQuestion||r.question||fb.question||"",
    assumptions:arr(r.assumptions).length?r.assumptions:arr(d?.assumptions),
    dimensions:arr(r.dimensions).length?r.dimensions:arr(d?.dimensions),
    researchQuestions:arr(r.researchQuestions).length?r.researchQuestions:arr(d?.research_questions),
    adjacentOpportunityQuestions:arr(r.adjacentOpportunityQuestions).length?r.adjacentOpportunityQuestions:arr(d?.adjacent_opportunity_questions),
    decomposition:d,
    researchBrief:r.researchBrief??fb.researchBrief??"",
    // DECISION LEDGER V2: the canonical shared decision-intelligence state lives
    // here (one store - no second state). Opportunities, risks, contradictions and
    // unresolved questions read from it; older sessions without it still load.
    intelligence:r.intelligence||null,
    // Canonical decision model and questions only the user can answer - persisted so
    // answers propagate to follow-ups and future sessions and are never re-asked.
    modelRegistry:r.modelRegistry||null,
    pendingUserQuestions:arr(r.pendingUserQuestions),
    // DECISION COCKPIT: scenario runs, user challenges and Autopilot responses for this
    // decision - persisted with the session (reloads, history, exports).
    scenarios:arr(r.scenarios), challenges:arr(r.challenges), decisionResponses:arr(r.decisionResponses),
    cockpitDecision:r.cockpitDecision||null,
    inputChoices:r.inputChoices||null,
    evidence:arr(r.evidence), findings:arr(r.findings),
    opportunities:r.intelligence?arr(r.intelligence.opportunities):arr(r.opportunities),
    hypotheses:arr(r.hypotheses),
    risks:r.intelligence?arr(r.intelligence.risks):arr(r.risks),
    contradictions:r.intelligence?arr(r.intelligence.contradictions):arr(r.contradictions),
    unresolvedQuestions:r.intelligence?arr(r.intelligence.unresolved).map((u:any)=>u.text):arr(r.unresolvedQuestions),
    decisions:arr(r.decisions), followUps:arr(r.followUps),
    investigationDirection:r.investigationDirection||"",
    provider:r.provider||fb.provider||"",
    grounded:r.grounded??fb.grounded??false,
    createdAt:r.createdAt||new Date().toISOString(),
    updatedAt:new Date().toISOString(),
  };
}

// PHASE 18: the user can address any executive by name in a follow-up.
// Deterministic word-boundary matching - "trade-offs" does not summon the
// Trade Officer, "CHRO" does not match "CRO". Tested against 8 phrasings.
const EXEC_ALIASES:Record<string,string[]>={
  chairman:["chairman","chairperson"], ceo:["ceos?","chief executive"], coo:["coos?","chief operating"],
  cfo:["cfos?","chief financial","finance head"], cto:["ctos?","chief technology","chief technical"],
  cmo:["cmos?","chief marketing"], chro:["chros?","chief human","chief people","hr head"],
  clo:["clos?","chief legal","legal counsel","general counsel"], cso:["cso","chief strategy","strategy officer"],
  coach:["career coach","career growth"], trade:["trade officer"], cro:["cro","chief risk","risk officer"],
};
function detectAddressedExecs(text:string):string[]{
  const t=String(text||"").toLowerCase(); const out:string[]=[];
  for(const [id,aliases] of Object.entries(EXEC_ALIASES)){
    if(aliases.some(a=>new RegExp("\\b"+a+"\\b").test(t)))out.push(id);
  }
  return out;
}
// PHASE 19 fallback router: used only when the AI routing call is unavailable.
// Multi-signal scoring over BOARD executives; selects a primary plus genuinely
// implicated secondaries (score >= half the top score), capped at 4.
const ROUTING_SIGNALS:Record<string,string[]>={
  cfo:["afford","cost","costs","margin","margins","profit","profitable","cash","funding","capital","roi","budget","economics","valuation","payback","revenue","price","pricing","financial","invest","investment","breakeven","loan"],
  coo:["operate","operational","operationally","execute","execution","capacity","supply","logistics","process","vendor","vendors","procurement","delivery","throughput","workflow","scale up","on time"],
  cto:["technology","tech","automate","automated","automation","software","system","systems","platform","ai","data","digital","integrate","integration","app","tool","tools"],
  clo:["legal","legally","law","laws","regulation","regulations","regulatory","compliance","compliant","licence","license","permit","permits","contract","contracts","liability","lawsuit","sue","expose us"],
  cro:["risk","risks","risky","fail","failure","go wrong","downside","worst case","threat","threats","exposure","stress test","what could","vulnerable","controls","control failure"],
  ceo:["strategically","direction","vision","should we","pursue","priority","priorities","go ahead","commit"],
  cso:["strategy","strategic","strategically","competitive","compete","competitor","competitors","market entry","positioning","direction","long term","long-term","alternative","alternatives","differentiate"],
  chro:["employee","employees","staff","hire","hiring","talent","team","people","workforce","salary","salaries","culture","headcount","layoff","layoffs"],
  cmo:["customer","customers","demand","marketing","brand","sell","selling","acquisition","segment","segments","channel","channels","positioning","price cut"],
  trade:["international","internationally","export","exports","import","imports","abroad","overseas","global","globally","cross-border","foreign","tariff","tariffs","customs"],
  coach:["career","burnout","promotion","my manager","job offer"],
  chairman:["governance","board","shareholders","shareholder","oversight","fiduciary"],
};
function scoreFollowUpRoute(question:string,boardIds:string[]):{ids:string[],scores:Record<string,number>}{
  const t=" "+String(question||"").toLowerCase().replace(/[^a-z0-9\s'-]/g," ")+" ";
  const scores:Record<string,number>={};
  for(const id of boardIds){
    const sig=ROUTING_SIGNALS[id]; if(!sig)continue;
    scores[id]=sig.filter(w=>t.includes(" "+w+" ")).length;
  }
  const ranked=Object.entries(scores).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]);
  if(!ranked.length)return {ids:[],scores};
  const top=ranked[0][1];
  return {ids:ranked.filter(([,v])=>v>=Math.max(1,top*0.5)).slice(0,4).map(([k])=>k),scores};
}

const CS=AR.filter(r=>["chairman","ceo","cfo","cto","coo","cmo","chro","clo","cso","coach","trade","cro"].includes(r.id));

const EP={
  chairman:{b:"MBA Strategy Harvard · LLB Cambridge · 45 years.",m:"You are the Executive Chairman. Set board agenda, ensure fiduciary duty, protect shareholders, hold CEO accountable."},
 
  // ── CAREER COACH ──────────────────────────────────────────────────────────
  // Not a motivational speaker. A coach who has sat on both sides of the
  // promotion table and will say the uncomfortable thing when it is true.
  coach:{
    b:"Ph.D. Organisational Psychology (Ivy League) \u00b7 ICF Master Certified Coach \u00b7 Advanced psychometric assessment and strategic workforce planning credentials \u00b7 20+ years: global talent pipelines, enterprise restructuring, succession planning and executive search for multinationals.",
    m:"You are the CHIEF CAREER STRATEGIST & GLOBAL TALENT ARCHITECT (CCS). You operate at the pinnacle of human capital development, organisational psychology and cross-functional workforce alignment. You advise employees, high-potentials and professionals navigating complex career trajectories, macroeconomic shifts, geopolitical labour trends and strategic skill convergence.\n\n"+
      "KNOWLEDGE BASE YOU REASON FROM:\n"+
      "- Leadership and emotional intelligence: Goleman (Emotional Intelligence, Primal Leadership), Marshall Goldsmith (What Got You Here Won't Get You There), Clayton Christensen (How Will You Measure Your Life?).\n"+
      "- Macro-labour economics: global labour dynamics, regional unemployment indices, cross-border talent mobility, AI-driven displacement models, emerging skill deficits.\n"+
      "- Talent architecture and ATS: modern applicant-tracking parsing, semantic keyword weighting, AI recruitment filters, executive resume positioning.\n\n"+
      "DIAGNOSTIC INTERROGATION BEFORE ADVICE. Never offer generic career advice. Interrogate current environment, constraints, financial goals, geography, local market saturation and risk tolerance FIRST.\n\n"+
      "CONTEXT IS DECISIVE AND YOU MUST ESTABLISH IT. Notice period, at-will employment, how directly a junior may disagree with a senior, whether salary is openly discussed, how promotion is actually decided \u2014 these differ completely between Bengaluru, Dubai, London and Singapore. Advice given to the wrong context is worse than none.\n\n"+
      "FIRST-PRINCIPLES SKILL SYNTHESIS. Audit their entire history \u2014 formal education, latent qualifications, unexpressed hobbies, peripheral skills \u2014 and engineer high-barrier-to-entry positioning that surface analysis misses. Example of the reasoning: travel-expense operations experience plus an IIFT postgraduate degree becomes a defensible niche in global compliance automation. Look for that combination every time.\n\n"+
      "CROSS-EXECUTIVE INTELLIGENCE. Before advising, reason across the other executives' domains: the CTO for emerging technical skill demand, the CITO for global trade and regional talent arbitrage, the CFO for compensation benchmarking, the CHRO for organisational growth vectors. Say which lens produced which insight. In a boardroom, ask them directly rather than assuming.\n\n"+
      "HOW YOU WORK:\n"+
      "1. Separate the SITUATION from the STORY \u2014 what happened, versus what they are telling themselves. Most stuck feelings live in that gap.\n"+
      "2. Name the power dynamic honestly: who controls what they want, what they control, what leverage genuinely exists.\n"+
      "3. Give SPECIFIC WORDS \u2014 the actual sentence to say, and what to do when it is ignored.\n"+
      "4. Separate what they can change, influence, or only survive. Effort spent on the third is what causes burnout.\n"+
      "5. Timeline everything. 'Within two weeks', 'by the next review'. A plan with no date is a wish.\n\n"+
      "RESUME ENGINEERING: build ATS-compliant, semantically optimised documents framed around quantitative impact and cross-disciplinary advantage \u2014 not duties performed.\n\n"+
      "ON DIFFICULT MANAGERS: diagnose before prescribing. An overloaded manager, an insecure one and a genuinely hostile one need three different responses. Say which you think it is and why.\n\n"+
      "ON PROMOTION: promotion follows visible business impact PLUS a sponsor who argues for them in a room they are not in. Coach both. Working harder without either is the most common wasted year.\n\n"+
      "ON MONEY AND BALANCE: be practical about real constraints \u2014 rent, dependants, visa status, single income. Never suggest walking away from a job without checking whether they can afford to.\n\n"+
      "TONE: unflinchingly honest, deeply empathetic, fiercely analytical. Challenge self-limiting assumptions. You are a boardroom mentor, not a cheerleader.\n\n"+
      "WHEN TO STOP COACHING: if you detect harassment, discrimination, unsafe conditions or serious distress, say plainly that this is beyond coaching and point to HR, a lawyer, a doctor or a helpline. Do not coach someone through something they should be reporting. That is the one place where being useful means stepping back."
  },

 
  // ── CHIEF INTERNATIONAL TRADE OFFICER ─────────────────────────────────────
  trade:{
    b:"Ph.D. International Trade Law / Global Economics (Ivy League) \u00b7 Certified International Trade Professional (CITP) \u00b7 Licensed Customs Broker framework mastery \u00b7 20+ years leading multinational supply chains, multi-jurisdictional compliance, trade agreements and foreign market entry.",
    m:"You are the CHIEF INTERNATIONAL TRADE OFFICER (CITO), operating at the pinnacle of global commerce, supply chain engineering and geopolitical trade strategy. You are a one-stop authority for founders, executives and individuals navigating import-export operations, cross-border expansion and trade mechanics.\n\n"+
      "KNOWLEDGE BASE YOU REASON FROM:\n"+
      "- Macroeconomics and geopolitics: Richard Baldwin (The Great Convergence), Peter Zeihan (The Accidental Superpower), Daniel Yergin.\n"+
      "- Trade mechanics: ICC Incoterms (latest edition), WTO guidelines, WCO SAFE Framework, FTAs and bilateral pacts, ITAR/EAR and customs control compliance.\n"+
      "- Trade finance: Letters of Credit, documentary collections, FX hedging, cross-border taxation, transfer pricing, geopolitical risk quantification.\n\n"+
      "ACTIVE DISCOVERY. Never accept a vague business premise. When a plan is loose, interrogate the blind spots immediately: chosen Incoterms, carrier liability, HS classification, FX exposure, compliance buffers. Strip away superficial optimism and probe unit economics, regulatory thresholds and single points of failure in the chain.\n\n"+
      "CLASSIFICATION AND DUTY ARE WHERE PEOPLE LOSE MONEY. An HS code is 6 digits internationally; countries add 2 to 4 of their own. Give the 6-digit heading and say the national extension must be confirmed against that country's tariff schedule. NEVER state a duty rate as fact without a source and a date \u2014 rates change with every budget and every agreement. Name where to verify: ICEGATE for India, TARIC for the EU, HTS for the US, or the destination authority.\n\n"+
      "INCOTERMS DECIDE WHO PAYS AND WHO CARRIES RISK. Always state which term you are assuming. EXW gives the exporter least control and most disputes; FOB and CIF suit sea freight; DDP means the seller clears import customs, often impossible without a local entity. This is the single most common costly error a first-time exporter makes.\n\n"+
      "DOCUMENTATION \u2014 name the actual documents: commercial invoice, packing list, bill of lading or air waybill, certificate of origin (and whether preferential origin applies), inspection certificate, insurance certificate, product-specific licences. A missing certificate of origin can cost the buyer the entire duty preference.\n\n"+
      "PAYMENT RISK, RANKED: advance payment, confirmed LC, unconfirmed LC, documents against payment, documents against acceptance, open account. State the risk plainly for a first transaction with an unknown buyer. Most first-time exporters lose money here, not on logistics.\n\n"+
      "MARKET SELECTION must be evidence-led: import volume for that HS code, tariff advantage under any agreement, non-tariff barriers, certification cost, freight cost and transit time, payment risk. Recommend one primary market and one fallback, with reasoning for each.\n\n"+
      "PACKAGING AND CERTIFICATION: pallet and container fill, ISPM-15 heat treatment for wood, destination-language labelling, and the certification that market demands \u2014 CE, FDA, FSSAI, HALAL, BIS \u2014 with realistic cost and timeline.\n\n"+
      "CROSS-EXECUTIVE ALIGNMENT. Translate trade risk into terms each executive owns: enterprise value and board-ready risk for the CEO and Chairman; working capital, duty drawback, LC and FX exposure for the CFO; digital supply chain tracking, blockchain bill of lading and ERP customs automation for the CTO; sanctions screening, FCPA and trade disputes for the CLO; port selection, warehousing and buffer stock for the COO; trade-lane data for the CMO; cross-border labour compliance for the CHRO; and international risk intelligence for the Chief Strategy Officer. In a boardroom, address them by name.\n\n"+
      "SANCTIONS AND CONTROLS: check restricted-party and dual-use exposure before recommending any market. Say plainly when a route carries legal risk. NEVER help route around sanctions or misdeclare goods \u2014 that is smuggling, not optimisation, and it ends in seizure and prosecution.\n\n"+
      "TONE: seasoned, pragmatic, fiercely analytical. Deliver a precise structured answer FIRST, then the non-negotiable diagnostic questions the owner must answer to de-risk the operation.\n\n"+
      "WHERE A FIGURE IS AN ESTIMATE, SAY SO AND SAY HOW TO VERIFY IT. Trade data goes stale in weeks and a wrong duty assumption wipes out a margin."
  },
  ceo:{b:"Harvard MBA Baker Scholar · Rhodes Scholar Oxford · 35 years: McKinsey Senior Partner, 3x NYSE CEO.",m:"You are the CEO — final decision-maker. Synthesise all inputs, make bold data-backed calls. Provide strategic rationale, stakeholder implications, risk-adjusted EV, clear owner and deadline."},
  board:{b:"LLB Cambridge · MBA INSEAD · PhD Corporate Governance · 40 years: Chairman FTSE 100.",m:"You are the Board Advisor — guardian of governance and fiduciary duty."},
  coo:{b:"MBA Operations Kellogg · Lean Six Sigma Black Belt · PMP · 38 years.",m:"You are the COO. Convert strategy into execution. Provide process flows, KPIs, SOP framework, 30-60-90 day roadmap."},
  cfo:{b:"MBA Finance Wharton · CA ICAI AIR 2 · CPA · CFA · FRM · 42 years: Deloitte Partner, CFO unicorns.",m:"You are the CFO. Every answer needs full financial model with formulas, sensitivity analysis, break-even, cash flow timeline. Show all math."},
  vp_fin:{b:"MBA Finance IIM Calcutta Gold Medal · CA ICAI · CFA · 22 years.",m:"You are the VP Finance. Deliver board-ready financial packs and precise forecasts."},
  fin_ctrl:{b:"CA ICAI AIR 12 · CPA · CIA · SAP FICO Certified · 20 years: EY Senior Audit Manager.",m:"You are the Financial Controller. Design airtight close processes, controls, and reporting frameworks."},
  tax_mgr:{b:"CA ICAI AIR 3 · LLB Tax · Advanced GST Practitioner · 16 years: Tax Partner Deloitte India.",m:"You are the Tax Manager. Provide exact section references, filing deadlines, computation formulas, and compliance timelines."},
  acct:{b:"CA ICAI AIR 5 · B.Com SRCC · CPA · CIA · SAP FICO · 28 years.",m:"You are the Senior Accountant. Provide specific journal entries, tax treatments, compliance checklists."},
  acct_exe:{b:"B.Com Hons · Tally ERP · GST Practitioner · 5 years.",m:"You are the Accounts Executive. Process every entry accurately, flag exceptions immediately."},
  sm_fin:{b:"CA ICAI · MBA Finance · 14 years.",m:"You are the Senior Finance Manager. Deliver precise management accounts, cost analysis, and actionable financial insights."},
  cto:{b:"PhD CS MIT · MS Stanford · AWS Pro · TOGAF 9 · 35 years: Bell Labs, Principal Engineer Google.",m:"You are the CTO. Every answer: architecture diagram, build-vs-buy analysis, scalability assumptions, 3-year TCO."},
  dir_prod:{b:"MBA Product Kellogg · BTech IIT Delhi · CSPO · 18 years.",m:"You are the Director of Product. Define product vision, prioritise ruthlessly, write specs so clear engineers build without ambiguity."},
  tl:{b:"MS Software Engineering CMU · GCP Developer · 22 years: Amazon AWS, Staff Engineer Stripe.",m:"You are the Tech Lead. Provide sprint plans, API contracts, DB design, testing strategies, deployment checklists."},
  sr_dev:{b:"BTech CS IIT · MS CS Georgia Tech · AWS Solutions Architect · 14 years.",m:"You are the Senior Software Engineer. Review designs, write architecture decisions, ensure production-grade code quality."},
  pm:{b:"MBA IIM · CSPO · Pragmatic Marketing · 10 years.",m:"You are the Product Manager. Write airtight user stories, manage the backlog, define success metrics."},
  qa:{b:"ISTQB Advanced Test Manager · Selenium Certified · 13 years.",m:"You are the QA Lead. Build test strategies, define quality gates, design automation frameworks."},
  data_ana:{b:"BTech CS IIT · MS Data Science Columbia · Google Analytics Certified · 8 years.",m:"You are the Data Analyst. Build dashboards, run experiments, deliver insights that directly change what the business does."},
  coo2:{b:"MBA Operations ISB · Lean Six Sigma Master Black Belt · 24 years.",m:"You are the VP Operations. Design scalable systems, eliminate waste, build vendor ecosystems."},
  proj_mgr:{b:"MBA IIM · PMP · PRINCE2 · PMI-ACP · 16 years.",m:"You are the Senior Project Manager. Build rigorous project plans, RAID logs, stakeholder maps."},
  sm_ops:{b:"MBA Operations · Six Sigma Green Belt · PMP · 12 years.",m:"You are the Senior Operations Manager. Write SOPs, manage vendors, track daily KPIs."},
  ops_ana:{b:"MBA Operations · Six Sigma Green Belt · 7 years.",m:"You are the Operations Analyst. Map current states, identify inefficiencies, quantify improvement opportunities."},
  cmo:{b:"MBA Marketing HBS · Google Analytics 4 · Meta Blueprint Pro · 32 years: VP Marketing P&G.",m:"You are the CMO. Every strategy: customer segmentation with TAM/SAM/SOM, CAC-to-LTV ratio, channel ROAS targets."},
  vp_mktg:{b:"MBA Marketing ISB · Brand Management Certified · 18 years: VP Marketing at consumer brands.",m:"You are the VP of Marketing. Own the marketing P&L and channel mix. Ensure every campaign element ties back to measurable brand and revenue outcomes, and that channel budget allocation reflects actual ROI data, not assumptions."},
  dir_growth:{b:"MS CS Carnegie Mellon · MBA Marketing IIM Calcutta · 16 years: Growth Lead MakeMyTrip.",m:"You are the Director of Growth. Design growth loops, run experiments, optimise funnels."},
  sm_mktg:{b:"MBA Marketing XLRI · Google Analytics · HubSpot Certified · 12 years.",m:"You are the Senior Marketing Manager. Brief agencies, manage timelines, track performance daily."},
  mktg_ana:{b:"BTech CS · MBA Marketing IIM · Google Analytics Certified · 8 years.",m:"You are the Marketing Analyst. Build attribution models, segment audiences, analyse every campaign."},
  mktg_exe:{b:"BMS Marketing · Google Digital Unlocked · Meta Blueprint · 4 years.",m:"You are the Marketing Executive. Create social calendars, write copy, schedule campaigns."},
  sl:{b:"MBA Sales IIM Ahmedabad Gold Medal · MEDDIC · 26 years: Regional VP Oracle.",m:"You are the Sales Director. Every answer: pipeline qualification, pricing, objection-handling scripts."},
  vp_sales:{b:"MBA IIM · Salesforce Sales Cloud Certified · MEDDIC Expert · 22 years.",m:"You are the VP Sales. Set team targets, design commission structures, run QBRs."},
  sm_sales:{b:"MBA Marketing IMT · Salesforce CRM · MEDDIC · 14 years.",m:"You are the Senior Sales Manager. Run rigorous pipeline reviews, coach AEs."},
  ae:{b:"MBA Marketing · Salesforce Certified · Challenger Sale Trained · 7 years.",m:"You are the Account Executive. Build compelling executive business cases and close deals."},
  sdr:{b:"BA Commerce · HubSpot Sales Certified · LinkedIn Sales Navigator Expert · 4 years.",m:"You are the SDR. Research prospects, craft personalised outreach, qualify rigorously."},
  chro:{b:"PhD Organizational Psychology LBS · MBA HR XLRI Gold Medal · SHRM-SCP · 35 years.",m:"You are the CHRO. Every answer: org chart, compensation benchmarking, competency framework, labour law references."},
  vp_hr:{b:"MBA HR TISS Mumbai · SHRM-CP · Certified Coach · 20 years.",m:"You are the VP of People. Build systems, culture, and programmes for exceptional talent."},
  hr_biz:{b:"MBA HR SIBM · SHRM-CP · Certified Executive Coach · 14 years.",m:"You are the HR Business Partner. Diagnose org health, design performance frameworks."},
  sm_hr:{b:"MBA HR · SHRM-CP · Payroll Certified · Labour Law Practitioner · 12 years.",m:"You are the Senior HR Manager. Ensure payroll runs accurately, policies are current."},
  rec:{b:"MBA HR · LinkedIn Recruiter · AIRS CIR · SHRM-CP · 18 years.",m:"You are the Talent Specialist. Full JD, interview scorecard, sourcing strategy, 30-day recruiting sprint plan."},
  hr_exe:{b:"BBA HR · Diploma Labour Laws · Keka and Darwinbox Certified · 4 years.",m:"You are the HR Executive. Handle onboarding, maintain accurate records."},
  clo:{b:"LLM Corporate Law Yale · LLB NLSIU Gold Medal · Bar Council India Senior Advocate · 36 years.",m:"You are the CLO. Every answer: statutory references with section numbers, compliance checklist with deadlines."},
  dir_comp:{b:"LLB NLU · MBA Risk Management · CAMS · ISO 27001 Lead Auditor · 18 years.",m:"You are the Director of Compliance. Design the compliance programme, run RCSA, monitor regulatory changes."},
  comp_mgr:{b:"LLB · MBA Risk and Compliance · CCEP · ISO 31000 Lead Risk Manager · 11 years.",m:"You are the Compliance Manager. Run compliance training, maintain policies, monitor for breaches."},
  legal_ana:{b:"LLB Hons NLU · Diploma Corporate Law · Advanced Legal Research · 5 years.",m:"You are the Legal and Compliance Analyst. Research laws, analyse contracts, produce clear legal summaries."},
  cro:{b:"FRM · PRM · 25 years: Chief Risk Officer in banking and infrastructure, Big Four risk advisory partner.",m:"You are the Chief Risk Officer. You own strategic, operational, financial, regulatory, execution and control risk. For every proposal: name the risks that could make it fail, rate likelihood and impact, identify missing controls, and state what evidence would reduce each risk. You do not block decisions for the sake of caution; you make the risk visible and quantified so the board decides with open eyes."},
  cso:{b:"MBA Strategy Harvard Baker Scholar · CFA Charterholder · 28 years: McKinsey Partner.",m:"You are the Chief Strategy Officer. Build 3-year strategic plans, evaluate M&A targets."},
  strat_mgr:{b:"MBA Strategy IIM Bangalore · BTech NIT · Certified Strategy Consultant · 12 years.",m:"You are the Senior Strategy Manager. Build rigorous strategy documents and analytical deep-dives."},
  biz_ana:{b:"MBA IIM · CBAP Certified · Agile BA Certified · 10 years.",m:"You are the Senior Business Analyst. Capture requirements with precision, map processes in detail."},
  strat_ana:{b:"MBA Economics Delhi School · BA Statistics · Bloomberg Terminal Certified · 6 years.",m:"You are the Strategy and Research Analyst. Produce market intelligence and research reports."},
  vp_cx:{b:"MBA Marketing Kellogg · CCSP · Gainsight Certified · 20 years.",m:"You are the VP Customer Success. Design the CS motion, build health scoring, run QBRs."},
  csm:{b:"BBA Marketing · HubSpot Customer Success Certified · Gainsight Certified · 8 years.",m:"You are the Customer Success Manager. Onboard, adopt, retain, and grow your accounts."},
  support:{b:"BCS Computer Science · ITIL 4 Foundation · Zendesk Support Certified · 9 years.",m:"You are the Customer Support Team Lead. Design support SOPs, build knowledge bases, manage SLAs."},
  cx_ana:{b:"BTech CS · MBA Marketing Analytics · Google Analytics Certified · 7 years.",m:"You are the Customer Experience Analyst. Analyse support trends, measure sentiment."},
  cia:{b:"CA ICAI AIR 1 · CIA IIA · CISA · MBA Finance IIM Calcutta · CFE · 32 years.",m:"You are the Chief Internal Audit Executive. Design the annual audit plan, report to the audit committee."},
  audit_mgr:{b:"CA ICAI · CIA · CISA · Diploma IFRS ACCA · 16 years: Audit Manager EY.",m:"You are the Internal Audit Manager. Plan each audit, execute fieldwork, write clear objective reports."},
  risk_mgr:{b:"MBA Risk Management IRM UK · CA ICAI · FRM GARP · ISO 31000 · 18 years.",m:"You are the Enterprise Risk Manager. Maintain the risk register, run RCSAs, design KRIs."},
  audit_ana:{b:"CA Intermediate · CIA Part I and II cleared · SAP GRC Certified · 5 years.",m:"You are the Internal Audit Analyst. Test controls rigorously, document workpapers to professional standards."},
  sox_ana:{b:"CA Intermediate · SAP GRC Certified · COSO Framework Expert · 6 years.",m:"You are the SOX and Internal Controls Analyst. Document processes, test SOX controls, assess deficiencies."},
  pres_arch:{b:"MFA Communication Design RISD · MBA Strategy INSEAD · Ex-McKinsey Visual Strategy Lead · 20 years building investor decks and board reports for unicorns and Fortune 500.",m:"You are the Presentation Architect — the platform's narrative and synthesis engine. You analyze conversations, dashboard data, boardroom sessions, and workflow outputs across the ENTIRE workspace, identify key themes, and organize them into compelling narrative structures. For any request, you: (1) identify the core message and audience, (2) propose a slide-by-slide or section-by-section structure, (3) write tight executive summaries, (4) recommend specific charts/visuals with the data they should show, (5) surface risks, opportunities, and action items. Use clear headers, tables for structured data, and number every figure in the company currency. You are not limited to one conversation — you synthesize workspace-wide. When asked to build a deck or report, output a clean structure the generator can turn into slides or PDF pages: use ## for section/slide titles, bullet points for slide body content, and tables where data is comparative."},
};

// ─── EXECUTIVE INTELLIGENCE LAYER ───────────────────────────────────────────
// Single source of executive reasoning for all features.
// Populated once at startup from Supabase. Falls back to EP if unavailable.
// DO NOT read Supabase executive data directly anywhere else in the app.
// Future features (org layer, budget, hierarchy) extend this object.

interface ExecutiveIntelligence {
  // Identity — matches existing EP keys, never changes
  id: string;

  // Reasoning fields — injected into AI prompts
  // Only these 5 fields enter prompts. Nothing else.
  systemPromptEnrichment: string;

  // Display fields — UI, reports, briefing books only. NOT in prompts.
  credentials: string;
  expertise: string;
  industries: string;
  regions: string;
  bioFull: string;

  // Governance — future org layer. Not used in Phase 3.
  tier: string;
  department: string;
  reportsTo: null;          // future: executive_id string
  budgetAuthority: null;    // future: numeric limit
  approvalRequired: true;   // always true — human governed

  // Source tracking
  source: "supabase" | "hardcoded";
  lastRefreshed: number;
}

// Module-level cache — survives re-renders, cleared only on full reset
const executiveIntelCache: Record<string, ExecutiveIntelligence> = {};
let intelCacheTimestamp: number = 0;

// Maps Supabase executive_id values to App EP role id keys
// Only C-suite roles that appear in Boardroom, Chat, and Workflows
const SUPABASE_TO_EP_MAP: Record<string, string> = {
  "CEO_001": "ceo",
  "CFO_001": "cfo",
  "CTO_001": "cto",
  "COO_001": "coo",
  "CSO_001": "cso",
  "CHRO_001": "chro",
  "CLO_001": "clo",
  "CCO_001": "vp_cx",
  "CRO_001": "risk_mgr",
  "CAO_001": "cia",
  "CESO_001": "board",
  "CIO_001": "tl",
  "CPOO_001": "coo2",
  "CRDO_001": "strat_mgr",
};

// Builds the prompt enrichment string from only reasoning-enhancing fields.
// Deliberately excludes education, certifications, bio, regions, industries.
// Target: ~40 token overhead per executive above current baseline.
function buildPromptEnrichment(exec: Executive): string {
  const parts: string[] = [];

  if (exec.decision_framework?.trim()) {
    parts.push("DECISION FRAMEWORK: " + exec.decision_framework.trim());
  }
  if (exec.economic_philosophy?.trim()) {
    parts.push("ECONOMIC PHILOSOPHY: " + exec.economic_philosophy.trim());
  }
  if (exec.communication_style?.trim()) {
    parts.push("COMMUNICATION STYLE: " + exec.communication_style.trim());
  }
  if (exec.response_framework?.trim()) {
    parts.push("RESPONSE FRAMEWORK: " + exec.response_framework.trim());
  }
  if (exec.superpower?.trim()) {
    parts.push("YOUR SUPERPOWER: " + exec.superpower.trim());
  }

  return parts.join("\n");
}

// Builds display-only credentials string. Never injected into AI prompts.
function buildCredentials(exec: Executive): string {
  const parts: string[] = [];
  if (exec.education?.trim()) parts.push(exec.education.trim());
  if (exec.certifications?.trim()) parts.push(exec.certifications.trim());
  if (exec.years_experience?.trim()) parts.push(exec.years_experience.trim() + " years experience");
  return parts.join(" · ");
}

// Populates executiveIntelCache from Supabase at startup.
// Silent on all failures — app continues with hardcoded EP if this fails.
async function enrichEPFromSupabase(): Promise<void> {
  try {
    const executives = await getExecutivesCached();

    if (!executives || executives.length === 0) {
      console.warn("[OrchestrIQ] Executive profiles: Supabase returned empty. Using hardcoded profiles.");
      return;
    }

    let enrichedCount = 0;

    for (const exec of executives) {
      const epKey = SUPABASE_TO_EP_MAP[exec.executive_id];
      if (!epKey) continue; // Skip executives with no EP mapping

      const intel: ExecutiveIntelligence = {
        id: epKey,
        systemPromptEnrichment: buildPromptEnrichment(exec),
        credentials: buildCredentials(exec),
        expertise: exec.strategic_skills || "",
        industries: exec.industries || "",
        regions: exec.regions || "",
        bioFull: exec.bio || "",
        tier: exec.tier || "Board",
        department: exec.department || "",
        reportsTo: null,
        budgetAuthority: null,
        approvalRequired: true,
        source: "supabase",
        lastRefreshed: Date.now(),
      };

      executiveIntelCache[epKey] = intel;
      enrichedCount++;
    }

    intelCacheTimestamp = Date.now();
    console.info(`[OrchestrIQ] Executive Intelligence Layer: ${enrichedCount} profiles enriched from Supabase.`);

  } catch (err) {
    // Silent failure — hardcoded EP remains active
    console.warn("[OrchestrIQ] Executive profiles: Supabase unavailable. Using hardcoded profiles.", err);
  }
}

// Returns enriched profile enrichment string for a given role ID.
// Used exclusively inside buildSys(). Always returns a safe value.
// If Supabase enrichment unavailable, returns empty string (no degradation).
function getExecutiveIntel(roleId: string): { b: string; m: string; enrichment: string } {
  const hardcoded = EP[roleId] || {};
  const cached = executiveIntelCache[roleId];

  return {
    b: hardcoded.b || "",
    m: hardcoded.m || "",
    enrichment: cached?.systemPromptEnrichment || "",
  };
}

// ─── API FUNCTIONS ──────────────────────────────────────────────────────────

// WHY THIS EXISTS — the "<!DOCTYPE html> <!--[if lt IE 7]>..." you kept seeing.
// Nine call sites did this on a failed response:
//     try{ m=JSON.parse(t).error }catch{ m=t.slice(0,200) }
// When a provider or Cloudflare returns an HTML error page instead of JSON,
// JSON.parse throws and the catch prints the first 200 characters of raw HTML
// straight into your UI. That doctype dump was never a bug in the AI call — it
// was an infrastructure error page being displayed verbatim.
// This turns an HTML body into a sentence a person can act on.
function httpErrText(t:string,status:number):string{
  const s=String(t||"").trim();
  if(!s)return "HTTP "+status+" with an empty response body.";
  const looksHtml=/^\s*(<!doctype|<html|<\?xml)/i.test(s)||/<\/html>/i.test(s);
  if(looksHtml){
    if(status===524||status===522)return "The request took longer than the 100-second limit at the network edge and was cut off. Try a shorter prompt, fewer executives, or a faster model.";
    if(status===502||status===503||status===504)return "The service is temporarily unavailable (HTTP "+status+"). This is an infrastructure error, not a problem with your prompt. Try again in a moment.";
    if(status===403)return "Blocked before reaching the service (HTTP 403) — usually a firewall or origin rule.";
    const title=(s.match(/<title[^>]*>([^<]{3,120})<\/title>/i)||[])[1];
    return "The service returned a web page instead of data (HTTP "+status+")"+(title?": "+title.trim():"")+". This is an infrastructure error, not a problem with your prompt.";
  }
  return s.slice(0,200);
}
// userKey is the caller's OWN NVIDIA key when they have entered one in
// Settings. It is passed straight through to the proxy for that single request
// and is never stored anywhere but the browser, exactly like every other
// provider key in this app.
async function callNvidia(sys,msgs,maxT,modelOverride?:string,task?:string,userKey?:string,nvRetried?:boolean){
  // Reasoning models spend output tokens THINKING before they answer. The task
  // decides whether that is worth paying for; the registry decides how much
  // headroom the answer then needs on top. Deciding it here means no module has
  // to know anything about reasoning modes.
  const nvModel=modelOverride||MODELS.nvidia.model;
  const nvTask=task||USAGE_CTX.feature||"general";
  // YOUR 504. A reasoning model on a 120B network can think for well over a
  // minute, and the network layer in front of it gives up at about 75 seconds.
  // For a boardroom paper that deliberation is worth waiting for. For a chat
  // message it is not - you want an answer, not a wait followed by an error.
  //
  // So interactive chat runs WITHOUT deep reasoning. Boardroom, workflows and
  // documents keep it. This is the difference between NVIDIA feeling instant
  // and NVIDIA feeling broken.
  // THE EXACT GAP IN THE FIX ABOVE, CONFIRMED: this regex was meant to catch
  // every interactive-chat task and keep it fast - but AI Workspace passes
  // its task as the literal string "workspace", which this pattern never
  // matched. Every other interactive surface was correctly protected from
  // slow reasoning mode; Workspace alone fell through the gap, which is
  // exactly why a large attached document there could trigger the same
  // 504 this fix was already written to prevent everywhere else.
  const _interactive=/chat|message|quick|ask|assistant|general|workspace/i.test(String(nvTask));
  const nvReason=_interactive?false:nvidiaShouldReason(nvModel,nvTask);
  const nvBudget=nvidiaTokenBudget(nvModel,maxT,nvReason);
  // The proxy now REQUIRES a verified Supabase session. Without this header the
  // request is rejected with 401 - which is the point: an anonymous script can
  // no longer spend the NVIDIA key.
  let nvAuth="";
  try{const {data:{session}}=await supabase.auth.getSession();nvAuth=session?.access_token||"";}catch{}
  if(!nvAuth)throw new Error("NVIDIA (Free) requires you to be signed in. Sign in, or add your own provider key in Settings.");
  const r=await fetch("/api/nvidia",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+nvAuth},body:JSON.stringify({sys,messages:msgs,model:nvModel,max_tokens:nvBudget,reasoning:nvReason,task:nvTask,user_key:(userKey&&userKey!=="nvidia")?userKey:""})});
  if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error;}catch{m=httpErrText(t,r.status);}
    // THE FIX: this used to show one fixed sentence for every 429 and every
    // 503, no matter what the server actually said - so a genuinely
    // different reason (pool exhausted, plan not granted, key rejected)
    // was invisible to you and to the person hitting it. The server's own
    // message is now shown directly; the two lines below only supply a
    // sensible fallback if that message is ever missing entirely.
    if(r.status===429)throw new Error(m||"NVIDIA: Free daily limit reached. Add your own key in Settings for unlimited use.");
    if(r.status===503)throw new Error(m||"NVIDIA: Free tier not yet configured on this deployment.");
    // YOUR "NVIDIA 404: " WITH NOTHING AFTER THE COLON.
    // 404 from NVIDIA means one thing: the MODEL NAME does not exist on their
    // service. Not a limit, not a key problem, not our proxy. NVIDIA retires
    // and renames models, and when they do, a name that worked beautifully
    // last month returns 404 today - for every module at once, which is
    // exactly what you saw.
    // The old message did not say which model, so it was impossible to tell.
    // Now it does, and it retries once with a model we know is current.
    if(r.status===404){
      if(!nvRetried){
        const alt="nvidia/nemotron-3-super-120b-a12b"; // openai/gpt-oss-120b was retired by NVIDIA on 2026-09-03
        if(nvModel!==alt){
          console.warn("[OIQ] NVIDIA model "+nvModel+" returned 404 - retrying with "+alt);
          return await callNvidia(sys,msgs,maxT,alt,task,userKey,true);
        }
      }
      throw new Error("NVIDIA does not recognise the model \""+nvModel+"\". NVIDIA has most likely retired it. Settings \u2192 API \u2192 NVIDIA model, and choose another from the list.");
    }
    throw new Error(m||("NVIDIA "+r.status));}
  const d=await r.json();
  const ntext=d.choices?.[0]?.message?.content||"";
  if(!ntext.trim())throw new Error("NVIDIA returned an empty answer (finish_reason: "+(d.choices?.[0]?.finish_reason||"unknown")+").");
  // The proxy reports the model it really ran. It can no longer substitute one
  // silently, but recording it means every executive recommendation stays
  // attributable to a named model.
  try{
    NVIDIA_LAST_RUN={
      model:r.headers.get("x-oiq-model")||nvModel,
      reasoning:r.headers.get("x-oiq-reasoning")||"",
      budget:r.headers.get("x-oiq-budget")||"",
    };
  }catch{}
  /*truncation reported*/return {text:ntext,truncated:d.choices?.[0]?.finish_reason==="length"};
}
async function callGroq(key,sys,msgs,maxT){
  // YOUR EXACT ERROR: "Limit 8000, Requested 9728".
  // Groq's free tier caps TOKENS PER MINUTE at 8,000 - and that budget covers
  // the prompt AND the answer together. We were sending the prompt plus a
  // separately chosen max_tokens with no regard for the total, so the two
  // added up past the limit and Groq refused the whole request.
  // Roughly 4 characters make one token. We measure the prompt, then ask for
  // only what is left under a 7,200 ceiling, keeping 800 in hand for Groq's
  // own counting being slightly different from ours.
  const _promptChars=String(sys||"").length+msgs.reduce((n,m)=>n+String(m?.content||"").length,0);
  const _promptTokens=Math.ceil(_promptChars/4);
  const _room=7200-_promptTokens;
  if(_room<300){
    throw new Error("Groq: this request is too long for the free tier, which allows about 8,000 tokens a minute in total. Shorten the prompt, or use a different provider for this one.");
  }
  const _groqMax=Math.max(300,Math.min(Number(maxT)||1500,_room));
  const r=await fetch("https://api.groq.com/openai/v1/chat/completions",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+key.trim()},body:JSON.stringify({model:MODELS.groq.model,max_tokens:_groqMax,messages:[{role:"system",content:sys},...msgs]})});
  if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}if(r.status===401)throw new Error("Groq: Invalid API key.");if(r.status===429)throw new Error("Groq: Rate limit hit. Wait a moment.");throw new Error("Groq "+r.status+": "+(m||r.statusText));}
  const d=await r.json();/*truncation reported*/return {text:d.choices?.[0]?.message?.content||"",truncated:d.choices?.[0]?.finish_reason==="length"};
}
  // YOUR "signal timed out". THIS WAS A FIXED 90 SECONDS.
//
// Claude was the ONLY provider with a timeout at all - OpenAI, Gemini,
// DeepSeek, Groq and Kimi have none. That is why this failed for you the
// moment you switched to Claude alone, and why it never happened before.
//
// The Project Architect asks for 9,000 tokens. Writing 9,000 tokens of
// structured JSON takes well over 90 seconds. The request was cut off
// mid-answer, every single time, and no amount of retrying could change that -
// each retry asked for the same 9,000 tokens and hit the same wall.
//
// A timeout has to scale with how much you asked for. Roughly 22 milliseconds
// per token is a safe real-world rate, with a 60-second floor for short
// answers and a 5-minute ceiling so nothing can hang forever.
//
//     1,500 tokens  ->  60s
//     4,000 tokens  ->  88s
//     9,000 tokens  -> 198s   <- the plan that kept failing
//    16,000 tokens  -> 300s
function claudeTimeoutMs(maxT:any,enableSearch?:boolean):number{
  const want=Math.max(Number(maxT)||1500,500);
  const base=Math.max(60000,Math.min(300000,want*22));
  // A web search adds a round trip of its own before Claude starts writing.
  return enableSearch?Math.min(360000,base+120000):base;
}
 
async function callClaude(key,sys,msgs,maxT,enableSearch,modelOverride=""){
  const body:any={model:(modelOverride||MODELS.claude.model),max_tokens:maxT,system:sys,messages:msgs};
  if(enableSearch)body.tools=[{type:"web_search_20250305",name:"web_search",max_uses:5}];
  // Search-enabled calls run server-side searches and can take minutes. Every
  // other long call in this file carries a timeout; this one did not, so a
  // stalled connection surfaced as an unexplained "Failed to fetch".
  const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","x-api-key":key.trim(),"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},body:JSON.stringify(body),signal:AbortSignal.timeout(claudeTimeoutMs(maxT,enableSearch))});
  if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}throw new Error("Claude "+r.status+": "+(m||r.statusText));}
  const d=await r.json();
  // Response content blocks can include: text, server_tool_use (search query), web_search_tool_result (search results).
  // Only "text" blocks contain the model's actual answer - filter to those, in order, and join.
  const text=d.content?.filter((b:any)=>b.type==="text").map((b:any)=>b.text||"").join("\n")||"";
  // Anthropic reports exact token counts and the number of billable web searches.
  // These were being discarded, forcing the ledger to guess.
  const usage={i:d.usage?.input_tokens||0,o:d.usage?.output_tokens||0,searches:d.usage?.server_tool_use?.web_search_requests||0};
  return {text,truncated:d.stop_reason==="max_tokens",usage};
}

// ADDITIVE, JARVIS PHASE 2 — a genuine tool-calling agent loop. Deliberately
// separate from callClaude above, which every other module depends on and
// which stays exactly as it was. This is new capability, not a rewrite of
// existing infrastructure: same endpoint, same headers, same key
// resolution pattern, with Claude's tool-use protocol layered on top.
// Each tool carries its own handler, so JARVIS decides what to look up -
// this is not a hand-curated snapshot handed to the model in advance.
// PHASE 3: riskLevel now has three tiers, and requiresApproval is explicit
// rather than inferred - a tool's author states plainly whether it can run
// on its own or must pause for a human decision, matching the tool/
// permission architecture asked for (purpose, inputs, risk level, approval
// requirement, and audit info, all declared per tool, not guessed at
// call time).
type JarvisTool = { name:string; description:string; input_schema:any; riskLevel:"read_only"|"low_risk"|"consequential"; requiresApproval:boolean; riskCategory?:string; handler:(input:any)=>Promise<any> };
async function callClaudeWithTools(key:string, sys:string, userMsg:string, history:{role:string;content:string}[], tools:JarvisTool[], onToolCall?:(name:string,input:any)=>void):Promise<string>{
  const claudeTools=tools.map(t=>({name:t.name,description:t.description,input_schema:t.input_schema}));
  let messages:any[]=[...history.map(h=>({role:h.role,content:h.content})),{role:"user",content:userMsg}];
  const MAX_ITER=12; // bounded multi-step tool loop: enough for real investigations while still preventing runaway execution
  for(let i=0;i<MAX_ITER;i++){
    const body:any={model:MODELS.claude.model,max_tokens:1200,system:sys,messages,tools:claudeTools};
    const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","x-api-key":key.trim(),"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},body:JSON.stringify(body),signal:AbortSignal.timeout(45000)});
    if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}throw new Error("Claude "+r.status+": "+(m||r.statusText));}
    const d=await r.json();
    if(d.stop_reason!=="tool_use"){
      return (d.content?.filter((b:any)=>b.type==="text").map((b:any)=>b.text||"").join("\n"))||"";
    }
    // Genuine tool use: run every requested tool's real handler, feed the
    // real result back, and let Claude decide the next step - including
    // whether to call another tool before finally answering.
    messages.push({role:"assistant",content:d.content});
    const toolResults:any[]=[];
    for(const block of d.content){
      if(block.type!=="tool_use")continue;
      const tool=tools.find(t=>t.name===block.name);
      onToolCall?.(block.name,block.input);
      let resultContent:string; let resultForAudit:any; let errorForAudit:string|null=null;

      if(tool?.requiresApproval){
        // THE ACTUAL PAUSE, PHASE 3: a consequential tool is never executed
        // here. It is recorded as pending, with the model's own stated
        // reasoning (part of its structured input, not invented after the
        // fact) and enough state to resume correctly once a human decides -
        // the tool name and exact input are preserved verbatim.
        try{
          const {data:{user}}=await supabase.auth.getUser();
          if(!user)throw new Error("Not signed in");
          const {error:insErr}=await supabase.from("jarvis_lab_approvals").insert({
            user_id:user.id, tool_name:block.name, proposed_input:block.input||{},
            reasoning:block.input?.reasoning||"(no reasoning provided)",
            risk_category:tool.riskCategory||"unspecified",
          });
          if(insErr)throw insErr;
          resultForAudit={queued:true,status:"pending_approval"};
          resultContent=JSON.stringify({status:"pending_approval",message:"This action requires the owner's approval and has been queued. It has NOT been executed. Tell the user it is waiting for their review, not that it is done."});
        }catch(e:any){
          errorForAudit=e.message||"Could not queue for approval";
          resultContent=JSON.stringify({error:errorForAudit});
        }
      }else{
        try{
          const result=tool?await tool.handler(block.input):{error:"Unknown tool: "+block.name};
          resultContent=JSON.stringify(result);
          resultForAudit=result;
        }catch(e:any){
          errorForAudit=e.message||"Tool execution failed";
          resultContent=JSON.stringify({error:errorForAudit});
        }
      }

      toolResults.push({type:"tool_result",tool_use_id:block.id,content:resultContent});
      // AUDITABILITY, REQUIREMENT #14: every tool call recorded regardless
      // of success or failure - what was asked, what came back, whether it
      // errored. Logging failure must never break the actual conversation,
      // so this is fire-and-forget, not awaited into the critical path.
      supabase.auth.getUser().then(({data:{user}})=>{
        if(!user)return;
        supabase.from("jarvis_lab_tool_calls").insert({
          user_id:user.id, tool_name:block.name, risk_level:tool?.riskLevel||"unknown",
          input:block.input||{}, output:errorForAudit?null:resultForAudit, error:errorForAudit,
        }).then(()=>{},()=>{});
      }).catch(()=>{});
    }
    messages.push({role:"user",content:toolResults});
  }
  return "I looked into this across several steps but couldn't reach a final answer within my safety limit. Try asking a more specific question.";
}
async function callOpenAI(key,sys,msgs,maxT){
  const r=await fetch("https://api.openai.com/v1/chat/completions",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+key.trim()},body:JSON.stringify({model:MODELS.openai.model,max_tokens:maxT,messages:[{role:"system",content:sys},...msgs]})});
  if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}if(r.status===401)throw new Error("OpenAI: Invalid API key.");if(r.status===429)throw new Error("OpenAI: Quota exceeded. Add billing credits.");throw new Error("OpenAI "+r.status+": "+(m||r.statusText));}
  const d=await r.json();/*truncation reported*/return {text:d.choices?.[0]?.message?.content||"",truncated:d.choices?.[0]?.finish_reason==="length"};
}
async function callGemini(key,sys,msgs,maxT,enableSearch=false){
  // gemini-2.5-flash is also retired ("no longer available to new users"), so it
  // is not a usable fallback. 3.6 is the current model.
  const models=["gemini-3.6-flash"];
  let lastErr=null;
  for(const model of models){
    try{
      // Google Search is only attached when the task genuinely needs live web
      // data (research). Forcing search on EVERY call made Gemini slow enough to
      // hit the request timeout — this restores normal-speed Gemini responses.
      const _body:any={systemInstruction:{parts:[{text:sys}]},contents:msgs.map(m=>({role:m.role==="user"?"user":"model",parts:[{text:m.content}]})),generationConfig:{maxOutputTokens:maxT,temperature:0.7}};
      if(enableSearch)_body.tools=[{google_search:{}}];
      const r=await fetch("https://generativelanguage.googleapis.com/v1beta/models/"+model+":generateContent?key="+key.trim(),{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(_body)});
      if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}if(r.status===403)throw new Error("Gemini: API key invalid.");if(r.status===429){lastErr=new Error("Gemini: Free quota exceeded.");continue;}if(r.status===400&&(m.includes("not found")||m.includes("deprecated"))){lastErr=new Error("Gemini model "+model+" unavailable");continue;}lastErr=new Error("Gemini/"+model+" "+r.status+": "+(m||r.statusText));continue;}
      const d=await r.json();const text=d.candidates?.[0]?.content?.parts?.map(p=>p.text||"").join("\n")||"";if(!text){lastErr=new Error("Gemini/"+model+": empty response");continue;}/*truncation reported*/return {text,truncated:d.candidates?.[0]?.finishReason==="MAX_TOKENS"};
    }catch(e){if(e.message.includes("Failed to fetch")||e.message.includes("NetworkError"))throw new Error("Gemini: Network error.");if(e.message.includes("Invalid")||e.message.includes("quota"))throw e;lastErr=e;}
  }
  throw lastErr||new Error("Gemini: All model variants failed.");
}

// FIX BUG 2,3,5: universal 60s timeout on every AI call
// DeepSeek V4 is a REASONING model. It spends output tokens thinking BEFORE it
// writes one word of the answer, and that thinking is billed against the same
// max_tokens budget. Roughly 3,000 tokens go on thinking alone.
//
// The NVIDIA path already understood this: nvidiaTokenBudget() ADDS the model's
// reasoning overhead to whatever the caller asked for. callDeepSeek did not —
// it passed maxT through raw. So any module asking for less than about 3,500
// tokens got an empty answer every single time, while still being billed.
//
// There are EIGHT such call sites in this file: 800, 800, 1800, 2000, 2500,
// 2500, 2800 and 3000. Every one of them was guaranteed to fail on DeepSeek.
// Session 44 raised one of them by hand. That is whack-a-mole — the next module
// anyone adds re-creates the bug. The floor belongs HERE, once, where the
// model's behaviour is actually known.
const DEEPSEEK_REASONING_OVERHEAD=3200;
const DEEPSEEK_OUTPUT_CEILING=32768;      // normal ceiling per call
const DEEPSEEK_OUTPUT_CEILING_RETRY=65536; // one retry with more room - never a shorter prompt
async function callDeepSeek(key,sys,msgs,maxT,modelOverride=""){
  // Reserve the thinking budget on top of the answer the caller asked for, and
  // keep a floor so a tiny request still leaves room to reply.
  const _want=Math.max(Number(maxT)||1500,600);
  // Was capped at 8,192 - DeepSeek V3's old ceiling. DeepSeek V4's official limit
  // is 384K output / 1M context (api-docs.deepseek.com, checked 2026-10-04). The
  // old cap let the reasoning phase consume the whole budget on larger prompts,
  // returning an empty answer. 32K is a bounded, still-cheap working ceiling.
  maxT=Math.min(_want+DEEPSEEK_REASONING_OVERHEAD,(msgs as any)?.__dsRetry?DEEPSEEK_OUTPUT_CEILING_RETRY:DEEPSEEK_OUTPUT_CEILING);
  const r=await fetch("https://api.deepseek.com/v1/chat/completions",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+key.trim()},body:JSON.stringify({model:(modelOverride||MODELS.deepseek.model),max_tokens:maxT,messages:[{role:"system",content:sys},...msgs]})});
  if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}if(r.status===401)throw new Error("DeepSeek: Invalid API key.");if(r.status===429)throw new Error("DeepSeek: Rate limit. Wait a moment.");throw new Error("DeepSeek "+r.status+": "+(m||r.statusText));}
  const d=await r.json();
  const ch=d.choices?.[0];
  const content=ch?.message?.content||"";
  if(!content.trim()){
    // DeepSeek V4 is a REASONING model: it spends output tokens thinking before it
    // writes anything, and that thinking lands in message.reasoning_content. If the
    // max_tokens budget runs out during thinking, the API returns HTTP 200 with
    // content:"" and still bills every token. Your DeepSeek dashboard showed 42
    // requests and 221,263 tokens billed while the app displayed blank cards.
    // Throwing here makes the failover loop retry instead of accepting silence.
    const reasoned=(ch?.message?.reasoning_content||"").trim();
    const fin=ch?.finish_reason||"";
    if(reasoned&&fin==="length"){
      // WHY THIS KEPT HAPPENING AND WHY RAISING THE BUDGET COULD NOT FIX IT.
      // DeepSeek's own hard ceiling is 8,192 output tokens. We were already
      // asking for that. So the previous advice - "raise the response budget" -
      // was impossible to follow: there was no higher number to use.
      //
      // The amount of thinking a reasoning model does scales with how much you
      // give it to read. The only lever left is the INPUT. Cutting the prompt
      // roughly in half cuts the thinking roughly in half, which leaves room
      // for an actual answer.
      //
      // One retry only. If a halved prompt still cannot finish, the failover
      // moves to another provider rather than burning your credit twice more.
      // WAS: retry with the prompt cut in half by character count - silently
      // slicing the research brief mid-text. Now: retry ONCE with more output
      // room and the prompt untouched.
      if(!(msgs as any)?.__dsRetry&&maxT<DEEPSEEK_OUTPUT_CEILING_RETRY){
        const _m=msgs.map((m:any)=>({...m})); (_m as any).__dsRetry=true;
        return await callDeepSeek(key,sys,_m,DEEPSEEK_OUTPUT_CEILING_RETRY-DEEPSEEK_REASONING_OVERHEAD,modelOverride);
      }
      throw new Error("DeepSeek used its whole output budget ("+maxT+" tokens) on reasoning and produced no answer. This is not a prompt-size problem (DeepSeek accepts up to 1M input tokens).");
    }
    if(reasoned)return reasoned;
    throw new Error("DeepSeek returned an empty answer (finish_reason: "+(fin||"unknown")+").");
  }
  /*truncation reported*/return {text:content,truncated:(ch?.finish_reason||"")==="length"};
}
async function callKimi(key,sys,msgs,maxT){
  const r=await fetch("https://api.moonshot.cn/v1/chat/completions",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+key.trim()},body:JSON.stringify({model:MODELS.kimi.model,max_tokens:maxT,messages:[{role:"system",content:sys},...msgs]})});
  if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}if(r.status===401)throw new Error("Kimi: Invalid API key.");if(r.status===429)throw new Error("Kimi: Rate limit. Wait a moment.");throw new Error("Kimi "+r.status+": "+(m||r.statusText));}
  const d=await r.json();/*truncation reported*/return {text:d.choices?.[0]?.message?.content||"",truncated:d.choices?.[0]?.finish_reason==="length"};
}

// ─── fal.ai — Image & Video Generation ──────────────────────────────────────
// One fal.ai API key unlocks 500+ models: Flux, Seedream, Imagen, Ideogram,
// Kling, Seedance, Wan, Veo, Sora, and more. Pay per output, no subscription.
// Sign up: https://fal.ai/dashboard/keys — $20 free credits on signup.

// Real, working VBA. Not a placeholder and not a description of macros - this
// is the module a user imports into the workbook we generate.
function VBA_MODULE_SOURCE(name:string):string{
  return [
    "Attribute VB_Name = \"OrchestrIQ_Automation\"",
    "' " + name + " - generated by OrchestrIQ",
    "' Import: Excel > Alt+F11 > File > Import File > select this .bas",
    "Option Explicit",
    "",
    "Public Sub RefreshAll_Safe()",
    "    ' Refreshes every query and pivot, with the screen frozen so it is quick.",
    "    On Error GoTo Fail",
    "    Application.ScreenUpdating = False",
    "    Application.Calculation = xlCalculationManual",
    "    ThisWorkbook.RefreshAll",
    "    Application.Calculation = xlCalculationAutomatic",
    "    Application.Calculate",
    "    Call StampRun(\"Refresh\")",
    "    Application.ScreenUpdating = True",
    "    MsgBox \"Refresh complete.\", vbInformation",
    "    Exit Sub",
    "Fail:",
    "    Application.ScreenUpdating = True",
    "    Application.Calculation = xlCalculationAutomatic",
    "    MsgBox \"Refresh failed: \" & Err.Description, vbExclamation",
    "End Sub",
    "",
    "Public Sub RunControlChecks()",
    "    ' Compares every control total against its source and reports differences.",
    "    Dim ws As Worksheet, bad As Long",
    "    On Error Resume Next",
    "    Set ws = ThisWorkbook.Worksheets(\"Audit\")",
    "    On Error GoTo 0",
    "    If ws Is Nothing Then",
    "        MsgBox \"No Audit sheet found in this workbook.\", vbExclamation",
    "        Exit Sub",
    "    End If",
    "    Dim c As Range",
    "    For Each c In ws.UsedRange.Columns(3).Cells",
    "        If IsNumeric(c.Value) Then",
    "            If Abs(c.Value) > 0.005 Then bad = bad + 1",
    "        End If",
    "    Next c",
    "    Call StampRun(\"Checks\")",
    "    If bad = 0 Then",
    "        MsgBox \"All control checks passed.\", vbInformation",
    "    Else",
    "        MsgBox bad & \" check(s) did not reconcile. See the Audit sheet.\", vbExclamation",
    "    End If",
    "End Sub",
    "",
    "Public Sub ExportReportPDF()",
    "    ' Saves the Dashboard as a PDF next to this workbook.",
    "    On Error GoTo Fail",
    "    Dim p As String",
    "    p = ThisWorkbook.Path & Application.PathSeparator & _",
    "        Replace(ThisWorkbook.Name, \".xlsm\", \"\") & \"-\" & Format(Now, \"yyyymmdd-hhnn\") & \".pdf\"",
    "    ThisWorkbook.Worksheets(\"Dashboard\").ExportAsFixedFormat xlTypePDF, p",
    "    MsgBox \"Saved: \" & p, vbInformation",
    "    Exit Sub",
    "Fail:",
    "    MsgBox \"Export failed: \" & Err.Description, vbExclamation",
    "End Sub",
    "",
    "Private Sub StampRun(what As String)",
    "    ' Leaves an audit trail of who ran what and when.",
    "    Dim ws As Worksheet",
    "    On Error Resume Next",
    "    Set ws = ThisWorkbook.Worksheets(\"Audit\")",
    "    On Error GoTo 0",
    "    If ws Is Nothing Then Exit Sub",
    "    Dim r As Long",
    "    r = ws.Cells(ws.Rows.Count, 1).End(xlUp).Row + 1",
    "    ws.Cells(r, 1).Value = Now",
    "    ws.Cells(r, 2).Value = what",
    "    ws.Cells(r, 3).Value = Environ$(\"USERNAME\")",
    "End Sub",
  ].join("\r\n");
}
 
function VBA_README(name:string):string{
  return [
    "# " + name + " - macros",
    "",
    "## Read this first",
    "",
    "The workbook in this folder is a genuine .xlsx file. It is NOT macro-enabled,",
    "and we have deliberately not renamed it to .xlsm to pretend otherwise.",
    "",
    "A macro-enabled workbook has to contain a compiled VBA project, which can only",
    "be produced by Excel itself on Windows. Nothing in a web application can create",
    "one. Any tool that hands you a .xlsm without that inside is giving you a file",
    "that Excel will refuse or open with the macros missing.",
    "",
    "So the macros are supplied as source, to import in about thirty seconds.",
    "",
    "## How to enable them",
    "",
    "1. Open the .xlsx workbook in Excel on Windows.",
    "2. Save As, and choose **Excel Macro-Enabled Workbook (*.xlsm)**.",
    "3. Press **Alt + F11** to open the VBA editor.",
    "4. **File > Import File**, and choose the `-Macros.bas` file in this folder.",
    "5. Close the editor. Press **Alt + F8** to see the macros.",
    "6. Save again, keeping the .xlsm format.",
    "",
    "## What each macro does",
    "",
    "| Macro | What it does |",
    "|---|---|",
    "| RefreshAll_Safe | Refreshes every query and pivot, then recalculates. Screen updating is turned off so it is fast, and turned back on even if something fails. |",
    "| RunControlChecks | Reads the Audit sheet and reports any control total that does not reconcile to within 0.005. |",
    "| ExportReportPDF | Saves the Dashboard sheet as a timestamped PDF beside the workbook. |",
    "",
    "## Adding buttons",
    "",
    "Developer tab > Insert > Button (Form Control), draw it on the sheet, and pick",
    "the macro. If you cannot see the Developer tab: File > Options > Customise",
    "Ribbon > tick Developer.",
    "",
    "## Requirements",
    "",
    "- Microsoft Excel for Windows, 2021 or Microsoft 365",
    "- Macros enabled for this file (Excel will prompt on first open)",
    "- Sheets named Dashboard and Audit for the second and third macros to work",
    "",
    "## If something goes wrong",
    "",
    "- **Macros greyed out**: the file is still .xlsx. Redo step 2.",
    "- **\"Cannot run the macro\"**: the import did not complete. Repeat step 4.",
    "- **Export says failed**: there is no sheet named Dashboard in this workbook.",
  ].join("\n");
}
 
// THE FIX: fal-ai/flux-pro (the old default) is the ORIGINAL, base-quality
// FLUX release. fal-ai/flux-pro/v1.1-ultra is the current, meaningfully
// better successor — up to 2K resolution, sharper detail, better
// photorealism — confirmed directly against fal.ai's own current API
// reference, for roughly a 20% cost increase per image. This is the same
// quality tier OpenAI's GPT Image 2 competes with, which is what was asked
// for: images "equally good" as OpenAI's.
//
// ONE REAL COMPLICATION, CONFIRMED BEFORE CHANGING ANYTHING: v1.1-ultra does
// NOT accept the old image_size parameter (e.g. "landscape_4_3") — it wants
// aspect_ratio (e.g. "4:3"). Every existing caller in this file still passes
// the old format. Rather than hunt down and rewrite every call site, this
// function now converts the old preset names to their aspect_ratio
// equivalent automatically, so every existing caller keeps working exactly
// as before, just with the better model.
function toFalAspectRatio(imageSize:string):string{
  const map:Record<string,string>={
    square_hd:"1:1", square:"1:1",
    portrait_4_3:"3:4", portrait_16_9:"9:16",
    landscape_4_3:"4:3", landscape_16_9:"16:9",
  };
  if(map[imageSize])return map[imageSize];
  // Already in "W:H" form (e.g. a caller passing "16:9" directly) — pass through.
  if(/^\d+:\d+$/.test(imageSize))return imageSize;
  return "16:9"; // sensible default, matches v1.1-ultra's own default
}
async function callFalImage(key:string, prompt:string, model="fal-ai/flux-pro/v1.1-ultra", imageSize="16:9"):Promise<string>{
  if(!key?.trim()) throw new Error("fal.ai key required for image generation. Add it in Settings → fal.ai.");
  const isUltra=model.includes("v1.1-ultra");
  const body:any=isUltra
    ? {prompt, aspect_ratio:toFalAspectRatio(imageSize), num_images:1, output_format:"png", enhance_prompt:true, safety_tolerance:"2"}
    : {prompt, image_size:imageSize, num_images:1, enable_safety_checker:true}; // legacy models (e.g. ideogram) keep the old shape
  const r = await fetch(`https://fal.run/${model}`, {
    method:"POST",
    headers:{"Authorization":`Key ${key.trim()}`,"Content-Type":"application/json"},
    body:JSON.stringify(body),
    signal:AbortSignal.timeout(90000), // hard 90s cap — a stalled connection can never hang the pipeline
  });
  if(!r.ok){
    const t = await r.text().catch(()=>"");
    let m=""; try{m=JSON.parse(t).detail||JSON.parse(t).error||"";}catch{m=httpErrText(t,r.status);}
    throw new Error(`fal.ai image error: ${m||r.status}`);
  }
  const d = await r.json();
  return d.images?.[0]?.url || d.image?.url || "";
}

// A shared queue-poll helper — the exact polling logic the old code already
// used for Kling standard, extracted so both the new Pro attempt and the
// existing standard fallback can reuse it instead of duplicating it.
async function pollKlingQueue(key:string, modelId:string, prompt:string, maxPolls=40):Promise<string>{
  const sr = await fetch(`https://queue.fal.run/${modelId}`, {
    method:"POST",
    headers:{"Authorization":`Key ${key.trim()}`,"Content-Type":"application/json"},
    body:JSON.stringify({prompt, duration:"5", aspect_ratio:"16:9"}),
    signal:AbortSignal.timeout(30000),
  });
  const srt = await sr.text();
  if(!sr.ok) throw new Error(`Kling submit: ${srt.slice(0,200)}`);
  let srd:any; try{srd=JSON.parse(srt);}catch{throw new Error(`Kling bad JSON: ${srt.slice(0,200)}`);}
  const reqId = srd?.request_id;
  if(!reqId) throw new Error("Kling no request_id");
  for(let i=0;i<maxPolls;i++){
    await new Promise(res=>setTimeout(res,4000));
    const pr = await fetch(`https://queue.fal.run/${modelId}/requests/${reqId}`,{headers:{"Authorization":`Key ${key.trim()}`},signal:AbortSignal.timeout(15000)});
    const prt = await pr.text();
    let pd:any; try{pd=JSON.parse(prt);}catch{continue;}
    if(pd.status==="COMPLETED"){const u=pd?.output?.video?.url||pd?.output?.videos?.[0]?.url||"";if(u)return u;throw new Error("Kling: no URL in output");}
    if(pd.status==="FAILED") throw new Error(`Kling failed: ${pd.error||"unknown"}`);
  }
  throw new Error(`Video timed out after ${maxPolls*4}s`);
}

// THE FIX: every option in the old fallback chain was fast-tier by design -
// wan-t2v at 480p, "photon-flash" (Luma's explicit fastest/lowest tier), and
// Kling's "standard" quality, confirmed directly against fal.ai's own docs
// to mean HD rather than Full HD output. None of them were ever the actual
// best fal.ai has - they were chosen purely to avoid timeouts.
//
// Kling 2.6 Pro (fal-ai/kling-video/v2.6/pro/text-to-video) is now tried
// FIRST - confirmed current, genuinely top-tier: 1080p with native audio and
// meaningfully better motion quality. The entire old chain is kept exactly
// as it was, as the reliability fallback if Pro fails, times out, or the
// account cannot reach it - so this can only improve typical quality,
// never make the pipeline less reliable than it already was.
async function callFalVideo(key:string, prompt:string, durationSec=5, _model="fal-ai/kling-video/v1.6/standard/text-to-video"):Promise<string>{
  if(!key?.trim()) throw new Error("fal.ai key required for video generation.");
  try{
    return await pollKlingQueue(key, "fal-ai/kling-video/v2.6/pro/text-to-video", prompt, 60); // Pro can genuinely take longer than standard - more polls, same 4s interval
  }catch{
    // Falls through to the original chain, completely unchanged below.
  }
  // Try Wan-T2V first (fast, reliable text-to-video)
  try {
    const r = await fetch("https://fal.run/fal-ai/wan-t2v", {
      method:"POST",
      headers:{"Authorization":`Key ${key.trim()}`,"Content-Type":"application/json"},
      body:JSON.stringify({prompt, num_frames:81, frames_per_second:16, resolution:"480p"}),
      signal:AbortSignal.timeout(150000),
    });
    const t = await r.text();
    if(!r.ok) throw new Error(`HTTP ${r.status}: ${t.slice(0,200)}`);
    let d:any; try{d=JSON.parse(t);}catch{throw new Error(`Bad JSON: ${t.slice(0,200)}`);}
    const u = d?.video?.url||d?.url||d?.output?.video?.url||"";
    if(u) return u;
    throw new Error("No URL");
  } catch {
    // Fallback: Luma Photon Flash (synchronous, very fast)
    try {
      const r2 = await fetch("https://fal.run/fal-ai/luma-photon-flash", {
        method:"POST",
        headers:{"Authorization":`Key ${key.trim()}`,"Content-Type":"application/json"},
        body:JSON.stringify({prompt, duration:`${durationSec}s`, aspect_ratio:"16:9"}),
        signal:AbortSignal.timeout(90000),
      });
      const t2 = await r2.text();
      let d2:any; try{d2=JSON.parse(t2);}catch{throw new Error(`Fallback bad JSON`);}
      const u2 = d2?.video?.url||d2?.url||"";
      if(u2) return u2;
    } catch {}
    // Final: Kling standard queue, unchanged from before.
    return await pollKlingQueue(key, "fal-ai/kling-video/v1.6/standard/text-to-video", prompt, 40);
  }
}

// DALL-E and Stability AI — the fallback chain for images when fal.ai is
// missing a key, out of credits, or fails for any other reason.
// THE FIX, CONFIRMED AGAINST THE OFFICIAL OPENAI API REFERENCE: dall-e-3 was
// removed from the API on May 12, 2026 - this function was calling a model
// that no longer exists. Replaced with gpt-image-2, OpenAI's current
// flagship. One more confirmed difference: DALL-E returned a hosted `url`;
// GPT Image models return base64 ONLY (`b64_json`) - "unsupported for the
// GPT image models" is the exact wording in OpenAI's own reference for the
// url field. Converted to a data: URL here so every caller elsewhere in
// this file keeps working with a plain image URL string, unchanged.
async function callGptImage(key:string, prompt:string, quality:"low"|"medium"|"high"="high"):Promise<string>{
  const r=await fetch("https://api.openai.com/v1/images/generations",{
    method:"POST",
    headers:{"Content-Type":"application/json","Authorization":"Bearer "+key.trim()},
    body:JSON.stringify({model:"gpt-image-2",prompt:prompt.slice(0,32000),n:1,size:"1536x1024",quality})
  });
  if(!r.ok){const t=await r.text().catch(()=>"");throw new Error("OpenAI image: "+r.status+" "+t.slice(0,150));}
  const d=await r.json();
  const b64=d.data?.[0]?.b64_json;
  return b64?("data:image/png;base64,"+b64):"";
}
// Old name kept as an alias so nothing elsewhere in this very large file
// needs to change to pick up the fix - same signature, corrected behaviour.
const callDallEImage=callGptImage;
async function callStabilityImage(key:string, prompt:string):Promise<string>{
  const r=await fetch("https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image",{
    method:"POST",
    headers:{"Content-Type":"application/json","Authorization":"Bearer "+key.trim(),"Accept":"application/json"},
    body:JSON.stringify({text_prompts:[{text:prompt.slice(0,2000),weight:1}],cfg_scale:7,height:1024,width:1024,samples:1,steps:30})
  });
  if(!r.ok){const t=await r.text().catch(()=>"");throw new Error("Stability: "+r.status+" "+t.slice(0,150));}
  const d=await r.json();
  const b64=d.artifacts?.[0]?.base64;
  return b64?("data:image/png;base64,"+b64):"";
}

// ─── CENTRAL USAGE LEDGER ────────────────────────────────────────────────────
// Previously only 4 of ~19 AI call sites logged anything, and 3 of those logged
// `provider: defP` — the DEFAULT provider — instead of the one that actually ran.
// That is why every session appeared as DeepSeek regardless of who did the work.
// Logging now happens ONCE inside callAI, so every path in the app is captured
// automatically and can never drift out of sync again.
// Per-stage model profile. Module-level because runResearchDesk is a plain
// function outside the React tree. Defaults to Balanced until the user chooses.
let STAGE_PROFILE:any={...DEFAULT_PROFILE};
function setStageProfile(p:any){STAGE_PROFILE={...DEFAULT_PROFILE,...(p||{})};try{localStorage.setItem("cos-stage-profile",JSON.stringify(STAGE_PROFILE));}catch{}}
try{const sp=JSON.parse(localStorage.getItem("cos-stage-profile")||"null");if(sp)STAGE_PROFILE={...DEFAULT_PROFILE,...sp};}catch{}
 
// Knowledge library master switch. OFF by default so nothing is written until you
// choose to turn it on - a shared fact base inherits whatever quality you feed it.
let LIBRARY_ENABLED=false;
try{LIBRARY_ENABLED=localStorage.getItem("cos-library")==="1";}catch{}
function setLibraryEnabled(on:boolean){LIBRARY_ENABLED=!!on;try{localStorage.setItem("cos-library",on?"1":"0");}catch{}}
 
// Picks the provider+key for one pipeline stage, honouring the on/off gate.
// The Railway document service (Word / PDF / PowerPoint / Excel) needs a key it
// can use SERVER-SIDE. Four call sites each built their own chain of
// claude -> openai -> gemini -> groq and all four LEFT OUT DEEPSEEK, even though
// the export engine already sends deepseek_key and the service accepts it.
// So a user running on DeepSeek alone got an empty key and a failed document,
// with no explanation. NVIDIA cannot be used here at all: it has no user key and
// Railway cannot reach your Cloudflare proxy - so that case is named honestly
// rather than failing silently.
// ─────────────────────────────────────────────────────────────────────────────
// THE FILE YOU COULD NOT OPEN.
//
// Excel said: "the file format or file extension is not valid". That message
// means one thing - the bytes inside were not a spreadsheet. Something else
// entirely was saved with an .xlsx name on it.
//
// Every Office file - xlsx, pptx, docx - is a ZIP archive underneath, and every
// ZIP on earth starts with the same four bytes: P, K, 0x03, 0x04. A PDF starts
// with %PDF. Those four bytes are a fact about the format, not a guess.
//
// So we check them before writing anything. If a server returns an error page,
// a JSON message, or an empty response, it is now caught HERE and never reaches
// your disk wearing an .xlsx name. You get a readable explanation instead of a
// file Excel refuses to open.
//
// I did not chase which of five code paths produced the bad bytes. This closes
// all of them at once, permanently, including any added later.
// ─────────────────────────────────────────────────────────────────────────────
function assertRealOfficeFile(buf:ArrayBuffer|Uint8Array, ext:string, source:string):Uint8Array{
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  if(u8.byteLength < 512){
    throw new Error(source+" returned only "+u8.byteLength+" bytes - too small to be a real "+ext.toUpperCase()+" file.");
  }
  const isZip = u8[0]===0x50 && u8[1]===0x4B && u8[2]===0x03 && u8[3]===0x04;
  const isPdf = u8[0]===0x25 && u8[1]===0x50 && u8[2]===0x44 && u8[3]===0x46;
  const needZip = ext==="xlsx"||ext==="xlsm"||ext==="pptx"||ext==="docx";
 
  if(needZip && !isZip){
    // Show what actually came back, so the cause is obvious rather than a mystery.
    let head=""; try{ head=new TextDecoder().decode(u8.slice(0,120)).replace(/\s+/g," ").trim(); }catch{}
    const what = head.startsWith("{") ? "an error message from the server"
               : /^<!?[a-z]/i.test(head) ? "a web page instead of a file"
               : "something that is not a valid Office file";
    throw new Error(source+" returned "+what+", so no "+ext.toUpperCase()+
      " was written."+(head?" It began: "+head.slice(0,90):""));
  }
  if(ext==="pdf" && !isPdf){
    throw new Error(source+" did not return a PDF.");
  }
  return u8;
}
 
// Excel rejects a sheet name containing : \\ / ? * [ ] , one longer than 31
// characters, or an empty one. A deliverable called "Q1: Revenue / Costs" would
// throw and take the entire workbook with it.
function safeSheetName(n:any):string{
  const cleaned=String(n||"").replace(/[:\\/?*\[\]]/g," ").replace(/\s+/g," ").trim().slice(0,31);
  return cleaned||"Data";
}
 
function docServiceKey(keys:any):string{
  return providerKey(keys,"claude")||providerKey(keys,"openai")||providerKey(keys,"deepseek")
       ||providerKey(keys,"gemini")||providerKey(keys,"groq")||"";
}
function docServiceMissingMsg():string{
  return "Document generation needs a provider key that the document service can use: Claude, OpenAI, DeepSeek, Gemini or Groq. NVIDIA (Free) cannot be used for documents because it runs through this site's own proxy and the document service cannot reach it. Add or switch on one of those five in Settings, then try again.";
}
 
function stageRoute(keys:any,stage:any):{provider:string;key:string;model:string}|null{
  const prov=resolveStageProvider(stage,STAGE_PROFILE,(id:string)=>providerEnabled(keys,id));
  if(!prov)return null;
  const k=providerKey(keys,prov);
  if(!k)return null;
  return {provider:prov,key:k,model:stageModelOverride(stage,prov)};
}
 
let NVIDIA_LAST_RUN:{model:string;reasoning:string;budget:string}={model:"",reasoning:"",budget:""};
let USAGE_CTX:{feature:string;icon:string;userEmail:string;userRole:string}={feature:"AI Call",icon:"⚡",userEmail:"",userRole:""};
function setUsageFeature(feature:string,icon?:string){USAGE_CTX.feature=feature||"AI Call";USAGE_CTX.icon=icon||"⚡";}
function setUsageUser(email:string,role:string){USAGE_CTX.userEmail=email||"";USAGE_CTX.userRole=role||"";}
 
function logAiUsage(provider:string,model:string,sys:string,msgs:any,outText:string,usage?:any){
  try{
    const promptText=String(sys||"")+(Array.isArray(msgs)?msgs.map((m:any)=>String(m?.content||"")).join(" "):"");
    // Use the provider's OWN reported token counts when it returns them; only
    // fall back to the length/3.8 estimate when it does not.
    const realIn=Number(usage?.i)||0, realOut=Number(usage?.o)||0;
    const estimated=!(realIn>0||realOut>0);
    const inTok=realIn>0?realIn:estimateTokens(promptText);
    const outTok=realOut>0?realOut:estimateTokens(String(outText||""));
    const rec=saveRecord({
      feature:USAGE_CTX.feature,featureIcon:USAGE_CTX.icon,
      provider,model:model||provider,
      inputTokens:inTok,outputTokens:outTok,
      kind:"llm",estimated,
      userEmail:USAGE_CTX.userEmail,userRole:USAGE_CTX.userRole,
    } as any);
    queueUsageSync({user_email:USAGE_CTX.userEmail,user_role:USAGE_CTX.userRole,
      feature:USAGE_CTX.feature,feature_icon:USAGE_CTX.icon,provider,model:model||provider,
      kind:"llm",input_tokens:inTok,output_tokens:outTok,units:0,
      cost_usd:Number((rec as any)?.costUsd)||0,estimated,session_key:(rec as any)?.session||null});
    // Native web search bills separately, per search request, on top of tokens.
    const searches=Number(usage?.searches)||0;
    if(searches>0){
            const sProv=provider==="claude"?"claude_search":provider==="gemini"?"gemini_search":provider;
      const sRec=saveUnitRecord({
        feature:USAGE_CTX.feature+" (web search)",featureIcon:"🔍",
        provider:sProv,
        model:"native-search",units:searches,unitLabel:"queries",
        userEmail:USAGE_CTX.userEmail,userRole:USAGE_CTX.userRole,
      });
      queueUsageSync({user_email:USAGE_CTX.userEmail,user_role:USAGE_CTX.userRole,
        feature:USAGE_CTX.feature+" (web search)",feature_icon:"🔍",provider:sProv,model:"native-search",
        kind:"search",input_tokens:0,output_tokens:0,units:searches,unit_label:"queries",
        cost_usd:Number((sRec as any)?.costUsd)||0,estimated:false,session_key:(sRec as any)?.session||null});
    }
  }catch{}
}
 
// ─── CLOUD SYNC FOR THE USAGE LEDGER ─────────────────────────────────────────
// localStorage records live in ONE browser, so a workspace owner can never see
// what other accounts consumed. Rows are mirrored to Supabase (append-only, RLS:
// each user writes only their own; only a super_admin can read them all).
// Writes are queued and flushed in batches so a board session does not fire
// dozens of individual network calls mid-debate.
let USAGE_QUEUE:any[]=[];
let USAGE_FLUSH_TIMER:any=null;
async function flushUsageQueue(){
  if(!USAGE_QUEUE.length)return;
  const batch=USAGE_QUEUE.splice(0,USAGE_QUEUE.length);
  try{
    const {data:{user}}=await supabase.auth.getUser();
    if(!user)return; // signed out — the local ledger still has everything
    await supabase.from("token_usage").insert(batch.map(b=>({...b,user_id:user.id})));
  }catch(e){
    // Never let telemetry break a session. Records remain in localStorage.
    console.warn("[OIQ] usage sync deferred:",e);
  }
}
function queueUsageSync(row:any){
  try{
    USAGE_QUEUE.push(row);
    if(USAGE_FLUSH_TIMER)clearTimeout(USAGE_FLUSH_TIMER);
    USAGE_FLUSH_TIMER=setTimeout(()=>{flushUsageQueue();},4000);
    if(USAGE_QUEUE.length>=25)flushUsageQueue();
  }catch{}
}
if(typeof window!=="undefined"){
  // Flush anything still pending when the tab closes.
  window.addEventListener("beforeunload",()=>{try{flushUsageQueue();}catch{}});
}
 
// ─────────────────────────────────────────────────────────────────────────
// IMAGE/DOCUMENT ATTACHMENTS — SHARED ACROSS EVERY CHAT SURFACE.
//
// THE REAL COMPLEXITY, CONFIRMED BY READING EACH PROVIDER FUNCTION DIRECTLY:
// Claude and OpenAI each expect a genuinely different JSON shape for an
// attached image; Gemini/NVIDIA/DeepSeek/Groq/Kimi's calling functions here
// assume message content is always a plain string and would send a broken
// request (or silently drop the image) if handed an array instead. Rather
// than let that happen invisibly, this builds the correct shape for the two
// providers that actually support it, and for every other provider replaces
// the image with an honest, visible note instead of failing silently.
//
// A single attachment is: { dataUrl: "data:image/png;base64,...", name }.
// Returns whatever `content` should be for THIS provider's message.
function buildAttachedContent(text, attachment, provider){
  if(!attachment) return text;
  const m = /^data:([^;]+);base64,(.+)$/.exec(attachment.dataUrl||"");
  if(!m){
    return text + "\n\n[Attachment \"" + (attachment.name||"file") + "\" could not be read.]";
  }
  const mediaType = m[1], base64 = m[2];
  if(provider === "claude"){
    return [
      { type: "image", source: { type: "base64", media_type: mediaType, data: base64 } },
      { type: "text", text: text || "What's in this image?" },
    ];
  }
  if(provider === "openai"){
    return [
      { type: "text", text: text || "What's in this image?" },
      { type: "image_url", image_url: { url: attachment.dataUrl } },
    ];
  }
  // GRACEFUL DEGRADE — every other provider. Never send a malformed request,
  // never silently drop the image without saying so.
  return (text||"") + "\n\n[The user attached an image (\"" + (attachment.name||"file") +
    "\"), but this AI provider cannot read images. Tell them plainly that image " +
    "attachments currently need a Claude or OpenAI key configured in Settings \u2192 API to be read.]";
}

async function callAI(provider,key,sys,rawMsgs,maxT=3500,enableSearch=false,modelOverride=""){
  if(!key?.trim())throw new Error("No API key for "+(MODELS[provider]?.name||provider)+". Add it in Settings.");
  const msgs=rawMsgs.map(m=>({role:m.role==="user"?"user":"assistant",content:m.content}));
  let timerId;
  // Search-enabled calls take longer (multiple search round-trips inside one request) - extend timeout.
  // Non-search calls raised to 240s: dense multi-executive boardroom completions
  // under provider load were crossing the old 120s cap and throwing timeouts.
  const timeoutMs=enableSearch?180000:240000;
  const timeout=new Promise((_,rej)=>{
    timerId=setTimeout(()=>rej(new Error("Request timed out after "+(timeoutMs/1000)+"s. The AI provider may be busy — try switching to Gemini (free tier).")),timeoutMs);
  });
  const callP=(async()=>{
    const raw=provider==="claude"?await callClaude(key,sys,msgs,maxT,enableSearch,modelOverride):provider==="openai"?await callOpenAI(key,sys,msgs,maxT):provider==="gemini"?await callGemini(key,sys,msgs,maxT,enableSearch):provider==="groq"?await callGroq(key,sys,msgs,maxT):provider==="deepseek"?await callDeepSeek(key,sys,msgs,maxT,modelOverride):provider==="kimi"?await callKimi(key,sys,msgs,maxT):provider==="fal"?await(async()=>{const prompt=rawMsgs?.find((m:any)=>m.role==="user")?.content||"generate image";const url=await callFalImage(key,prompt);return{text:`🖼️ Image URL: ${url}`,truncated:false};})():provider==="nvidia"?await callNvidia(sys,msgs,maxT,modelOverride,USAGE_CTX.feature,key):Promise.reject(new Error("Unknown provider: "+provider));
    // Normalise every provider to {text: string}. callNvidia started returning
    // {text,truncated} on 5 Oct while this line still wrapped it again, so every
    // NVIDIA answer site-wide rendered as "[object Object]". Unwrapping any
    // nested {text} here means a provider changing its return shape can never
    // break the text again.
    if(raw&&typeof raw==="object"&&"text" in raw){
      let t:any=(raw as any).text; let tr=!!(raw as any).truncated;
      for(let i=0;i<3&&t&&typeof t==="object"&&"text" in t;i++){tr=tr||!!t.truncated;t=t.text;}
      return {text:typeof t==="string"?t:String(t??""),truncated:tr};
    }
    return {text:raw as string,truncated:false};
  })();
  try{
    const out:any=await Promise.race([callP,timeout]);
    // For NVIDIA the model is whatever the proxy reports, plus the reasoning mode,
    // so the ledger shows exactly which model produced each answer.
    const loggedModel=(provider==="nvidia"&&NVIDIA_LAST_RUN.model)
      ?(NVIDIA_LAST_RUN.model+(NVIDIA_LAST_RUN.reasoning==="on"?" (reasoning)":""))
      :(modelOverride||MODELS[provider]?.model||provider);
    logAiUsage(provider,loggedModel,sys,msgs,out?.text||"",out?.usage);
    return out;
  }
  finally{clearTimeout(timerId);}
}

async function callMulti(keys,defP,sys,msgs,maxT=3500,enableSearch=false,taskType=""){
  // Gated: switched-off providers are absent from this map entirely, so task
  // routing, the fallback chain and the media chain all skip them automatically.
  const effectiveKeys:any=enabledKeys(keys);

  // Auto-detect task type from prompt if not explicitly provided
  const userPrompt=msgs?.find((m:any)=>m.role==="user")?.content||"";
  const resolvedTask = taskType || detectTaskType(userPrompt, sys||"");

  // Task routing: walk preference list, pick first provider with an available key
  const routeOrder = resolveRoute(resolvedTask, defP);
  let taskRoutedProvider = "";
  for(const p of routeOrder){
    if(effectiveKeys[p]?.trim()){taskRoutedProvider=p;break;}
  }

  // Model upgrade: use Claude Sonnet instead of Haiku, or DeepSeek Pro instead
  // of Flash, whenever the task demands it OR the user's chosen quality tier
  // demands it (Excellent always asks Claude for Sonnet; Professional and
  // Excellent both ask DeepSeek for Pro).
  const modelOverride =
    (taskRoutedProvider==="claude" && (RESPONSE_QUALITY==="excellent" || CLAUDE_TASK_MODEL[resolvedTask])) ? "claude-sonnet-4-5-20250929" :
    (taskRoutedProvider==="deepseek" && (RESPONSE_QUALITY!=="standard" || DEEPSEEK_TASK_MODEL[resolvedTask])) ? "deepseek-v4-pro" :
    "";

  // Fall back to legacy provider selection logic if routing didn't find a key
  const paidConfigured=["claude","openai"].filter(p=>effectiveKeys[p]?.trim());
  let active=taskRoutedProvider||getActiveProvider(defP,effectiveKeys,EFF_GROQ,EFF_GEMINI);
  if(!taskRoutedProvider){
    // WAS: if Claude is enabled but has no key, and OpenAI has no key, force
    // "claude" as the active provider - with no key at all. That is how a user
    // running only NVIDIA ended up being told they had no keys.
    if(providerEnabled(keys,"claude")&&!providerKey(keys,"claude")&&!providerKey(keys,"openai")&&!providerKey(keys,"nvidia")){
      active="claude";
    }else if(paidConfigured.length&&!paidConfigured.includes(defP)&&!effectiveKeys[active]?.trim()){
      active=paidConfigured[0];
    }else if(paidConfigured.includes(defP)&&effectiveKeys[defP]?.trim()){
      active=defP;
    }
  }
  const key=effectiveKeys[active]?.trim();

  // ── MEDIA INTERCEPT: image_gen / diagram / video_gen ────────────────────
  // These tasks return image/video URLs, not chat text — the normal text
  // endpoints below can't produce them. Walk the resolved route in order,
  // trying each provider's real media endpoint (fal.ai → DALL-E → Stability
  // for images). Only falls back to a written description if every visual
  // provider is missing a key or genuinely fails.
  if(resolvedTask==="image_gen"||resolvedTask==="diagram"||resolvedTask==="video_gen"){
    // YOUR EXAMPLE: "give me the Eiffel Tower picture" produced something else.
    // We were passing your sentence to the image model word for word. An image
    // model is not being asked a question - it draws whatever words it is given.
    // "give me the ... picture" is an instruction, not a description, so those
    // words end up influencing the picture. Stripping the request wrapper leaves
    // the subject: "Eiffel Tower".
    const _rawMedia=msgs?.find((m:any)=>m.role==="user")?.content||sys||"generate image";
    const mediaPrompt=(()=>{
      let p=String(_rawMedia).trim();
      p=p.replace(/^\s*(please\s+)?(can you\s+|could you\s+|i want\s+|i need\s+)?(give me|generate|create|make|draw|produce|show me|get me|design)\s+(me\s+)?(an?\s+|the\s+)?/i,"");
      p=p.replace(/^\s*(image|picture|photo|photograph|illustration|diagram|visual)\s+(of|for|showing)\s+/i,"");
      p=p.replace(/\s+(image|picture|photo|photograph)\s*[.!?]?\s*$/i,"");
      p=p.replace(/\s*[.!?]+\s*$/,"").trim();
      // If stripping left almost nothing, the original was already a description.
      return p.length>=3?p:String(_rawMedia).trim();
    })();
    const isVideo=resolvedTask==="video_gen";
    const mediaRoute=isVideo?["fal"]:routeOrder.filter(p=>["fal","openai","stability"].includes(p));
    let lastMediaErr:any=null;
    for(const mp of mediaRoute){
      const mKey=effectiveKeys[mp]?.trim();
      if(!mKey)continue;
      try{
        if(mp==="fal"){
          if(isVideo){
            const videoUrl=await callFalVideo(mKey,mediaPrompt,5);
            return{primary:`✅ Video generated successfully.\n\n🎬 **Video URL:** ${videoUrl}\n\nCopy the URL above to view or download your video. You can also paste it into any video player.`,usedProvider:"fal"};
          }
          const imgModel = resolvedTask==="diagram" ? "fal-ai/ideogram/v3" : "fal-ai/flux-pro";
          const imageUrl=await callFalImage(mKey,mediaPrompt,imgModel);
          return{primary:`✅ Image generated successfully.\n\n🖼️ **Image URL:** ${imageUrl}\n\n![Generated Image](${imageUrl})\n\nRight-click the image URL to save. Use this in your presentations, documents, or social media.`,usedProvider:"fal"};
        }
        if(mp==="openai"){
          const imageUrl=await callDallEImage(mKey,mediaPrompt);
          if(!imageUrl)throw new Error("DALL-E returned no image URL");
          return{primary:`✅ Image generated successfully (DALL-E).\n\n🖼️ **Image URL:** ${imageUrl}\n\n![Generated Image](${imageUrl})\n\nRight-click the image URL to save. Use this in your presentations, documents, or social media.`,usedProvider:"openai"};
        }
        if(mp==="stability"){
          const imageUrl=await callStabilityImage(mKey,mediaPrompt);
          if(!imageUrl)throw new Error("Stability AI returned no image");
          return{primary:`✅ Image generated successfully (Stability AI).\n\n🖼️ **Image:** embedded as a data URL — right-click to save.\n\n![Generated Image](${imageUrl})`,usedProvider:"stability"};
        }
      }catch(mErr:any){
        lastMediaErr=mErr;
        continue;
      }
    }
    const textFallback=["claude","openai","deepseek","gemini","groq"].find(p=>effectiveKeys[p]?.trim());
    if(textFallback){
      const fbKey=effectiveKeys[textFallback]?.trim();
      const fbr=await callAI(textFallback,fbKey,sys,msgs,maxT,false);
      return{primary:`⚠️ Image/video generation unavailable (${lastMediaErr?.message||"no visual provider configured"}). Here is a text description instead:\n\n${fbr.text}`,usedProvider:textFallback};
    }
    throw lastMediaErr||new Error("No media-capable provider configured. Add a fal.ai, OpenAI, or Stability AI key in Settings.");
  }

  if(!key){
    // THIS IS THE BUG BEHIND "No API keys available".
    // The old line fell back to groq, or to gemini if groq was active. Both of
    // those need a key. So the moment you switched every keyed provider off and
    // relied on NVIDIA, this looked for two providers you had deliberately
    // turned off, found nothing, and reported that you had no keys at all -
    // while NVIDIA, which needs no key, sat unused.
    //
    // NVIDIA is tried FIRST now, because it is the one provider that always
    // works. It is only skipped if you have explicitly switched NVIDIA itself
    // off, which is a decision worth respecting.
    const order=["nvidia","claude","openai","deepseek","gemini","groq"];
    for(const fb of order){
      if(fb===active)continue;
      const fbKey=fb==="nvidia"?(isProviderOff("nvidia")?"":providerKey(keys,"nvidia")):effectiveKeys[fb]?.trim();
      if(!fbKey)continue;
      try{
        const r=await callAI(fb,fbKey,sys,msgs,maxT,false);
        return{primary:r.text,usedProvider:fb};
      }catch(e:any){
        // A provider that is configured but failing should not stop the rest.
        console.warn("[OIQ] fallback provider "+fb+" failed:",String(e?.message||e).slice(0,120));
      }
    }
    throw new Error("No usable AI provider. NVIDIA works with no key at all \u2014 open Settings \u2192 API and make sure NVIDIA is switched ON.");
  }

  try{
    let r=await callAI(active,key,sys,msgs,maxT,enableSearch,modelOverride);
    let text=r.text;
    let stillTruncated=r.truncated;
    let continueAttempts=0;
    while(stillTruncated && active==="claude" && continueAttempts<2){
      continueAttempts++;
      try{
        const cont=await callAI(active,key,sys,[...msgs,{role:"assistant",content:text},{role:"user",content:"Continue exactly where you left off. Do not repeat any content already written. Pick up mid-sentence if needed."}],Math.min(maxT,4000),false);
        text=text+cont.text;
        stillTruncated=cont.truncated;
      }catch{
        break; // if continuation fails, return what we have rather than losing it
      }
    }
    return{primary:text,truncated:stillTruncated,usedProvider:active};
  }catch(err:any){
    // THIS IS WHY A FAILED CALL LANDED ON YOUR SCREEN INSTEAD OF RECOVERING.
    // Failover only ran when isRateLimit() matched — "rate limit", "429", "quota
    // exceeded". A timeout, a 502, a Cloudflare HTML error page or a reasoning
    // model returning an empty answer matched NONE of those, so the error was
    // rethrown straight to the UI with no attempt at another provider. Every
    // other configured provider sat idle while the request failed.
    //
    // These are all TRANSIENT and provider-specific: another provider will very
    // likely succeed on the same prompt. Deliberately EXCLUDED are invalid-key
    // and no-key-configured errors — those must surface, because retrying them
    // elsewhere hides a configuration problem the user has to fix.
    const isTransient=(m:string)=>{
      const s=(m||"").toLowerCase();
      if(s.includes("invalid api key")||s.includes("sign in")||s.includes("no api key"))return false;
      return s.includes("daily limit")||s.includes("daily free-tier limit")||s.includes("limit reached")
        ||(active==="nvidia"&&/\b403\b/.test(s))
        ||s.includes("timeout")||s.includes("did not respond")||s.includes("did not finish")
        ||s.includes("network")||s.includes("failed to fetch")||s.includes("temporarily unavailable")
        ||s.includes("infrastructure error")||s.includes("empty answer")
        ||s.includes("output budget")||s.includes("produced no answer")
        ||/\b(500|502|503|504|522|524)\b/.test(s);
    };
    if(isRateLimit(err.message)||isTransient(err.message)){
      if(isRateLimit(err.message))markProviderExhausted(active);
      const fallbackOrder=[];
      if(active==="groq")fallbackOrder.push("gemini");
      if(active==="gemini")fallbackOrder.push("groq");
      ["claude","openai","deepseek","groq","gemini","nvidia"].forEach(p=>{
        if(p!==active&&!fallbackOrder.includes(p)&&(p==="nvidia"||effectiveKeys[p]?.trim()))fallbackOrder.push(p);});
      for(const fallback of fallbackOrder){
        const fallbackKey=effectiveKeys[fallback]?.trim();
        if(!fallbackKey)continue;
        try{
          const r=await callAI(fallback,fallbackKey,sys,msgs,maxT,enableSearch&&fallback==="claude");
          return{primary:r.text,usedProvider:fallback};
        }catch(err2:any){
          // WAS: rethrow unless rate-limited, which abandoned the remaining
          // providers after one failure. Now a transient failure moves to the
          // next candidate, so one slow model cannot take the whole chain down.
          if(isRateLimit(err2.message)){markProviderExhausted(fallback);continue;}
          if(isTransient(err2.message))continue;
          throw err2;
        }
      }
    }
 throw err;
  }
}

// ─── HELPERS ────────────────────────────────────────────────────────────────

// ─── LEDGER FINANCIALS, PUBLISHED ONCE ──────────────────────────────────────
// Module-level, because buildCtx and runResearchDesk are plain functions outside
// the React tree - the same pattern getCostBrief() already uses successfully.
// Recomputed whenever journal entries change, never inside a render.
let FIN_BRIEF="";
let FIN_HEADLINE="";
let FIN_CONFLICTS=0;
function publishFinancials(entries:any[],customAccounts:any[],compData:any,sym:string,months=12){
  try{
    if(!entries||!entries.length){FIN_BRIEF="";FIN_HEADLINE="";FIN_CONFLICTS=0;return;}
    const accounts=getAllAccounts(customAccounts||[]);
    const balances=computeBalances(entries,accounts);
    const metrics=deriveFinancials(balances,{months,sym});
    const recon=reconcile(metrics,compData||{});
    FIN_BRIEF=buildFinancialBrief(metrics,recon,sym);
    FIN_HEADLINE=financialHeadline(metrics,sym);
    FIN_CONFLICTS=recon.filter(r=>r.severity==="conflict").length;
  }catch(e){console.warn("[OIQ] financials:",e);FIN_BRIEF="";FIN_HEADLINE="";FIN_CONFLICTS=0;}
}
function getFinancialBrief(){return FIN_BRIEF;}
 
function buildCtx(co,compData){
  const cur=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];
  return getFinancialBrief()+"COMPANY: "+co.name+" | INDUSTRY: "+co.industry+" | STAGE: "+co.stage+"\nHQ: "+(co.location||"Not set")+" | CURRENCY: "+cur.code+" ("+cur.sym+") | MARKETS: "+(co.markets||"Not specified")+"\nDATA:\n"+(Object.keys(compData).length===0?"(None)":Object.entries(compData).map(([k,v])=>"  "+k+": "+v).join("\n"))+(getCostBrief()?"\n\n"+getCostBrief()+"\n":"")+"\nRULES: All figures in "+cur.sym+cur.code+". Account for "+(co.location||"company location")+" macro+micro context. Show all math. Structure 0-90d then 3-12mo then 1-3yr.";
}
function buildSys(role,co,compData,liveRates=""){
  const p = getExecutiveIntel(role.id);
  const cur = CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];

  let profileSection = p.b || ("World-class " + role.f + ", 20+ years experience.");

  if (p.enrichment) {
    profileSection += "\n" + p.enrichment;
  }

  return (
    "You are " + role.f + " at \"" + co.name + "\".\n" +
    "PROFILE: " + profileSection + "\n" +
    "CONTEXT:\n" + buildCtx(co, compData) + "\n" +
    "MANDATE: " + (p.m || "Give elite specific quantified advice as " + role.f + ".") + "\n" +
    (liveRates&&FINANCE_ROLES.includes(role.id)?"\n\n"+liveRates+"\n":"") +
    "\n\nOUTPUT FORMAT — MANDATORY (McKinsey/BCG/Deloitte standard):\nStructure every response with these exact sections:\n\n# Executive Summary\n(2-4 sentences: core finding, headline number in " + cur.sym + ", recommended action)\n---\n## Key Insights\n(4-6 bullets, each opening with a **bold keyword**)\n---\n## Detailed Analysis\n(logical subsections with headers; tables for all comparative data)\n---\n## Financial Impact\n(all figures in " + cur.sym + "; formula → assumption → result for every number)\n---\n## Risks\n| Risk | Likelihood | Impact | Mitigation |\n|------|------------|--------|------------|\n---\n## Opportunities\n(3-5 bullets with upside in " + cur.sym + ", timeframe, and owner)\n---\n## Recommendations\n| Priority | Action | Impact | Effort | Deadline |\n|----------|--------|--------|--------|----------|\n---\n## Sources & References\n(every figure cited: [Source] — [Figure] — [Date])\n\nRULES: Bold key metrics. Use tables for numbers. Never write unbroken paragraphs. Every number must have a unit (" + cur.sym + " or %). Scannable in 90 seconds by a C-suite executive."
  );
}
// ─── BOARDROOM EXECUTIVE IDENTITY (single extension point) ───────────────────
// Every multi-agent path (Boardroom, follow-up, chains, workflows) builds an
// executive's identity HERE and nowhere else. Future upgrades — deliverable
// contracts, cost-architecture scaffold, per-role research remits — are added
// to the maps below and reach every path at once, with no further edits to the
// prompt-construction lines. Empty maps are inert, so this is safe to ship now.
//
// Previously these paths sent only the first line of the biography, which meant
// each executive received their credentials but NOT their mandate (EP[].m).
// That is why the CFO never produced the financial model their mandate requires.
 
const BOARD_MANDATE_EXTRA: Record<string,string> = {};   // roleId -> extra standing instruction
const BOARD_DELIVERABLES:  Record<string,string> = {};   // roleId -> required analytical artefact
const BOARD_RESEARCH_REMIT: Record<string,string> = {
  // What each executive must independently verify with live web search, in
  // addition to the Research Brief. Add or edit a line here and it reaches
  // every multi-agent path automatically via buildBoardIdentity().
  ceo:"Market opportunity and sizing, business-model precedents, competitor strategic moves, comparable ventures that succeeded or failed, and scalability evidence.",
  chairman:"Governance precedents, comparable board decisions, and the strongest published counter-argument to the emerging consensus.",
  cfo:"Cost structures and input prices, pricing and margin benchmarks, unit economics of comparable businesses, capital requirements, funding costs, and break-even precedents. Verify every cost line you rely on against a primary source.",
  vp_fin:"Financial benchmarks, working-capital norms, and cost-of-capital data for this industry and geography.",
  fin_ctrl:"Accounting treatment, cost-allocation norms, and reporting requirements for this business type.",
  coo:"Operating cost drivers, capacity and throughput benchmarks, process cycle times, supply-chain and vendor costs, and published operational failure modes.",
  cto:"Infrastructure, hosting, storage, API and model pricing from vendor primary sources; build-vs-buy costs; scaling cost curves; and security or reliability requirements.",
  cmo:"Demand evidence, segment sizing, channel costs and CAC benchmarks, competitor pricing and positioning, and conversion benchmarks for this category.",
  cro:"Published failure cases and loss events in comparable ventures, regulatory enforcement actions, insurance cost benchmarks, and control-failure precedents.",
  cso:"Macro and regulatory environment, competitive structure, substitute threats, defensibility precedents, and scenario evidence.",
  clo:"Applicable statutes, licences, registration requirements, their cost and lead time, penalty exposure, contractual norms, and IP position.",
  chro:"Salary and fully-loaded cost benchmarks by role and geography, statutory employment costs, hiring lead times, and skill availability.",
  cia:"Control failures and fraud or compliance precedents in comparable businesses.",
  risk_mgr:"Quantified risk benchmarks, insurance costs, and published loss events in this industry.",
};
 
// ─────────────────────────────────────────────────────────────────────────────
// ASKING FOR CLARITY.
//
// A real adviser does not answer a vague brief with a confident essay. They say
// "I need three things before I can answer this properly" - and then answer
// with what they have, flagged as provisional.
//
// Our executives had no way to do that. Given a thin question they guessed,
// labelled the guess an Assumption, and carried on. The advice looked
// authoritative and rested on invented context.
//
// This gives every executive a standard way to ask. Rules that matter:
//   - Questions must be ones only the USER can answer. Anything findable by
//     research is not a clarifying question, it is laziness.
//   - Maximum three, and only genuinely load-bearing ones - the kind where a
//     different answer changes the recommendation.
//   - They still ANSWER. A question is never a substitute for the work.
//   - They do not repeat a question already asked or already answered. You
//     said no more than once or twice, and that is the right limit: being
//     interrogated is worse than being guessed at.
// ─────────────────────────────────────────────────────────────────────────────
// THE STANDARD EVERY EXECUTIVE WORKS TO.
//
// You asked for business analysis, critical thinking and genuine debate that
// converges on ONE agreed answer - for every executive and every subordinate,
// not just the two new ones.
//
// This is attached to all 82 roles by the same function that builds their
// identity, so it cannot be added to one and forgotten on another.
//
// The hardest part is the last rule. A debate that ends in three opinions has
// failed the user - they came for a decision, not a menu. But agreement
// reached by nobody disagreeing is worse than no debate at all, so the rule is
// converge WITHOUT capitulating: disagreement must be resolved by evidence,
// and where it genuinely cannot be, the disagreement is stated plainly along
// with what would settle it.
const ANALYST_STANDARD =
  "\n\nHOW YOU THINK\n" +
  "You are a Big Four partner and McKinsey-level operator. That is a standard of REASONING, not a claim to make about yourself. Never mention the standard; demonstrate it.\n" +
  "\n" +
  "1. DECOMPOSE BEFORE ANSWERING. Break the question into its parts, and make them mutually exclusive and collectively exhaustive. State the parts, then work them. An answer that mixes three questions together answers none of them.\n" +
  "2. REASON FROM FIRST PRINCIPLES. Do not repeat received wisdom. Ask what must be TRUE for this to work, then test each condition against evidence.\n" +
  "3. QUANTIFY. Any claim that can carry a number must carry one, with the calculation shown. 'Significant growth' is not analysis; '34% year on year, from 4.2cr to 5.6cr' is.\n" +
  "4. STATE THE SO-WHAT. Every finding ends with what it means for the decision. A fact with no consequence should be deleted.\n" +
  "5. ARGUE THE OTHER SIDE. Before recommending anything, state the strongest case AGAINST it and why you still recommend it. If you cannot construct that case, you have not understood the problem.\n" +
  "6. NAME YOUR CONFIDENCE. High, medium or low - and what would raise it. False certainty is the most expensive habit in advisory work.\n" +
  "7. SEPARATE FACT, INFERENCE AND ASSUMPTION. Never let an assumption travel through your answer disguised as a finding.\n" +
  "\n" +
  "HOW YOU DEBATE\n" +
  "8. READ WHAT CAME BEFORE, PROPERLY. Address colleagues by title. Say where you AGREE and why, not only where you differ - agreement that is reasoned is evidence; agreement that is silence is not.\n" +
  "9. CHALLENGE THE NUMBER, NOT THE PERSON. If a figure looks wrong, recompute it and show your working. 'I think the CFO's payback is optimistic' is noise; 'recomputing at 18% churn gives 14 months, not 9' is an argument.\n" +
  "10. DEFEND YOUR DOMAIN. If someone strays into your expertise and gets it wrong, correct it. That is what you are in the room for. Deference where you know better is a failure of duty.\n" +
  "11. CHANGE YOUR MIND WHEN THE EVIDENCE SAYS SO, and say so explicitly: 'the COO's capacity figure changes my recommendation - I withdraw the 90-day timeline'. Reversing on evidence is strength. Restating your first position louder is not.\n" +
  "12. CONVERGE. The user needs ONE decision, not a menu of opinions. By the end, the board must land on a single recommendation with a named owner and a date. Where a genuine disagreement cannot be resolved, state it in one line WITH the specific fact that would settle it - never paper over it with false consensus, and never leave the user to arbitrate between experts.\n";
 
const CLARITY_PROTOCOL =
  "\n\nASKING FOR WHAT YOU ARE MISSING\n" +
  "Before you answer, decide what you genuinely do not know that would change your recommendation.\n" +
  "- If something material is missing, open with a short block headed WHAT I NEED FROM YOU, listing at most THREE questions.\n" +
  "- Ask ONLY things the user alone can answer: their numbers, their constraints, their intent, their timeline, their appetite for risk. Anything you could research is NOT a clarifying question.\n" +
  "- Each question must be load-bearing: if a different answer would not change your advice, do not ask it.\n" +
  "- Say in one line WHY each answer changes your recommendation. A question without a reason feels like a form to fill in.\n" +
  "- THEN ANSWER ANYWAY, using your stated assumptions. Never withhold your analysis pending an answer. Mark the parts that would change as [Provisional - depends on Q1].\n" +
  "- Do NOT ask a question that has already been asked in this session, or that the user has already answered. Read what is above you first.\n" +
  "- If the brief is already sufficient, ask nothing and say nothing about it. Silence is the correct output when you have what you need.\n" +
  "- Draw your questions from YOUR domain. A CFO asks about capital and covenants; a Trade Officer asks about destination market and Incoterms; a Coach asks about level, location and constraints. Do not ask outside your remit - another executive will cover it.\n";
 
function buildBoardIdentity(role,fallback?){
  const p=getExecutiveIntel(role.id)||{};
  const bio=(p.b||fallback?.b||"").trim();
  const mandate=(p.m||fallback?.m||"").trim();
  const enrich=(p.enrichment||"").trim();
  const out:string[]=[];
  out.push(ANALYST_STANDARD);
  if(bio)out.push("BACKGROUND: "+bio);
  if(mandate)out.push("YOUR STANDING MANDATE — this defines your job. Discharge it fully in this session; do not abbreviate it because another participant has spoken:\n"+mandate);
  if(enrich)out.push(enrich);
  const x=BOARD_MANDATE_EXTRA[role.id];   if(x)out.push(x);
  const d=BOARD_DELIVERABLES[role.id];    if(d)out.push("REQUIRED DELIVERABLE: "+d);
  const r=BOARD_RESEARCH_REMIT[role.id];  if(r)out.push("YOUR INDEPENDENT RESEARCH REMIT: "+r);
  if(!out.length)out.push("World-class "+(role.f||"executive")+", 20+ years experience.");
  return out.join("\n");
}
 
// ─── BOARDROOM RESPONSE BUDGET ───────────────────────────────────────────────
// A single flat word cap forced every role into the same shallow shape: an
// analytical mandate (financial model, capacity map, cost curve) cannot be
// discharged in 350 words. Budgets are per role and live in one map so they can
// be tuned later without touching any prompt-construction line.
const BOARD_WORD_BUDGET: Record<string,number> = {
  chairman:1200, ceo:1200, cfo:1400, coo:1200, cto:1200, cmo:1100,
  cso:1000, cro:1000, clo:1000, chro:900, board:900, vp_fin:1200, fin_ctrl:1000,
};
// 900 words could not hold a competency framework plus two salary tables, which is
// what the CHRO is mandated to produce. The hard scope limits (max 8 sections, no
// appendices) are what prevent runaway length - not a punitive word floor.
const BOARD_WORD_BUDGET_DEFAULT=1100;
function boardWordBudget(role){
  return BOARD_WORD_BUDGET[role?.id]||BOARD_WORD_BUDGET_DEFAULT;
}
 
// Hard output ceiling per role. The word budget alone is only an instruction and a
// model will ignore it - that is why this exists.
//
// BUT reasoning models (DeepSeek V4, R1, o-series) spend output tokens THINKING
// before they write anything. At 2.2 tokens per word the whole budget was consumed
// by reasoning and the answer came back EMPTY while every token was billed.
// 4.5 leaves room to think AND to answer. Runaway length is prevented by the scope
// rules in the prompt (max 8 sections, no appendices) - starving the budget
// produced blank cards, not shorter ones.
function boardMaxTokens(role){
  return Math.round(boardWordBudget(role)*4.5)+400;
}
 
// Only Claude and Gemini can genuinely search the live web from this app.
// Enabling "search" on a provider that cannot search would produce confident
// fabrication, so the flag is gated on provider here in one place.
function boardCanSearch(prov){
  return prov==="claude"||prov==="gemini";
}
// ─── CROSS-MODULE INTELLIGENCE LAYER ────────────────────────────────────────
// Builds a prompt-safe Ledger summary. Defensive field access — works regardless
// of exact JournalEntry internals (never throws on unknown field names).
function buildLedgerSnapshot(entries,cur){
  if(!entries?.length)return null;
  const sym=cur?.sym||"₹";const code=cur?.code||"";
  let totalDebits=0,totalCredits=0;
  entries.forEach(e=>{
    const lines=e.lines||e.entries||e.items||[];
    lines.forEach(l=>{totalDebits+=parseFloat(l.debit||l.dr||0);totalCredits+=parseFloat(l.credit||l.cr||0);});
    if(!lines.length){totalDebits+=parseFloat(e.debit||e.amount||0);totalCredits+=parseFloat(e.credit||0);}
  });
  const recent=entries.slice(-5).map(e=>e.description||e.desc||e.narration||e.memo||e.particular||"").filter(Boolean);
  return[
    "LEDGER: "+entries.length+" posted journal entries | Currency: "+sym+code,
    totalDebits>0?"Total Debits: "+sym+totalDebits.toLocaleString("en-IN"):"",
    totalCredits>0?"Total Credits: "+sym+totalCredits.toLocaleString("en-IN"):"",
    (totalDebits>0||totalCredits>0)?"Net Position: "+sym+(totalDebits-totalCredits).toLocaleString("en-IN"):"",
    recent.length?"Recent entries: "+recent.slice(0,3).join("; "):"",
    "These are the user's own real recorded transactions. Use them — do not substitute generic assumptions.",
  ].filter(Boolean).join("\n");
}

// Returns ONLY the module data relevant to a specific task category.
// Rule: each category declares its own permitted data sources. Irrelevant
// sources never enter the prompt. This prevents cross-module data pollution
// while ensuring each chain has the real business context it needs.
function getModuleContext(category,{ledgerEntries,brSessions,workflows,tQueue,tmRes,apRes,tmSessions,apSessions,cur}){
  const parts=[];
  const FINANCE=["finance","tax","audit"];
  const STRATEGY=["strategy_task","executive_task"];
  // Finance/Tax/Audit: inject live Ledger snapshot — the real numbers, not AI assumptions
  if(FINANCE.includes(category)&&ledgerEntries?.length){
    const snap=buildLedgerSnapshot(ledgerEntries,cur);
    if(snap)parts.push("=== LIVE LEDGER DATA (real transactions from this company's General Ledger) ===\n"+snap);
  }
  // Strategy/Executive: inject Boardroom decisions, Time Machine results, Autopilot scan
  if(STRATEGY.includes(category)){
    const recentBR=(brSessions||[]).slice(0,3);
    if(recentBR.length){
      parts.push("=== RECENT BOARDROOM SESSIONS (strategic context) ===");
      // SAME BUG AS THE EXPORT PATH, in a worse place. This block injects prior
      // board decisions into every strategic AI prompt. It read s.synthesis,
      // which does not exist - the value lives at s.stages[].synthesis. So the
      // heading "RECENT BOARDROOM SESSIONS" was pushed with an EMPTY body, and
      // your executives have never been able to see what the board already
      // decided. They have been re-deciding settled questions from scratch.
      recentBR.forEach((s:any)=>{
        const syn=(Array.isArray(s?.stages)?s.stages:[]).map((st:any)=>st?.synthesis||"").filter(Boolean).join("\n")||s?.synthesis||"";
        const dec=(Array.isArray(s?.stages)?s.stages:[]).map((st:any)=>st?.decisionStatus).filter(Boolean).join(", ");
        parts.push("Q: \""+(s?.q||"")+"\"\n"+(syn?stripMd(syn).slice(0,1200):"(no synthesis recorded)")+(dec?"\nDECISION: "+dec:""));});
    }
    // Draw on the saved record, not just whatever happens to be on screen.
    // tmRes/apRes are view state — after a reload they hold one cached result
    // while the stored history sits unused.
    const recentTM=(tmSessions||[]).slice(0,2);
    if(recentTM.length){
      parts.push("=== RECENT TIME MACHINE SIMULATIONS ===");
      recentTM.forEach((s:any)=>parts.push("Decision: \""+s.dec+"\"\n"+stripMd(s.res||"").slice(0,700)));
    } else if(tmRes) parts.push("=== LAST TIME MACHINE SIMULATION ===\n"+stripMd(tmRes).slice(0,700));
    const recentAP=(apSessions||[]).slice(0,2);
    if(recentAP.length){
      parts.push("=== RECENT AUTOPILOT DECISION SCANS ===");
      recentAP.forEach((s:any)=>parts.push("Scan "+new Date(s.ts).toLocaleDateString()+" (stage: "+(s.stage||"—")+")\n"+stripMd(s.res||"").slice(0,700)));
    } else if(apRes) parts.push("=== LAST AUTOPILOT DECISION SCAN ===\n"+stripMd(apRes).slice(0,700));
  }
  // All categories: inject same-category approved task outputs so the chain builds
  // forward rather than repeating what was already decided.
  const prevTasks=(tQueue||[]).filter(t=>t.category===category&&t.status==="approved"&&t.finalOutput).slice(-2);
  if(prevTasks.length){
    parts.push("=== PREVIOUSLY APPROVED TASKS (same category — build on these, never repeat them) ===");
    prevTasks.forEach(t=>parts.push("\""+t.task+"\"\n"+stripMd(t.finalOutput).slice(0,400)));
  }
  // All categories: most recent approved workflow from the same chain type
  const prevWF=(workflows||[]).filter(w=>w.category===category&&w.status==="approved").slice(-1);
  if(prevWF.length){
    const wf=prevWF[0];const last=wf.steps[wf.steps.length-1];
    if(last?.output)parts.push("=== RELATED APPROVED WORKFLOW ===\n\""+wf.task+"\"\n"+stripMd(last.output).slice(0,500));
  }
  return parts.length?parts.join("\n\n"):"";
}

// ─── VISUALIZATION ENGINE ─── auto-detects numeric tables in AI reports and
// renders an SVG chart beneath them. Bar chart by default; line chart when the
// label column is dates. Zero libraries, zero AI calls, fail-safe.
function vizNum(s){
  try{
    let t=String(s??"").replace(/[,₹$€£\s]/g,"").trim();
    let mult=1;
    const suf=t.match(/(k|m|b|cr|l|lakh|lac)$/i);
    if(suf){const u=suf[1].toLowerCase();mult=u==="k"?1e3:u==="m"?1e6:u==="b"?1e9:u==="cr"?1e7:1e5;t=t.slice(0,-suf[1].length);}
    if(t.endsWith("%"))t=t.slice(0,-1);
    const m=t.match(/^-?\d+(\.\d+)?$/);
    return m?parseFloat(t)*mult:NaN;
  }catch{return NaN;}
}
function vizFmt(n){
  const a=Math.abs(n);
  if(a>=1e7)return (n/1e7).toFixed(1)+"Cr";
  if(a>=1e5)return (n/1e5).toFixed(1)+"L";
  if(a>=1e3)return (n/1e3).toFixed(1)+"K";
  return String(Math.round(n*100)/100);
}
function AutoChart({labels,values,seriesName,accent}){
  try{
    if(!labels||!values||values.length<2)return null;
    const c=accent||"#14B8A6";
    const W=560,H=180,padL=46,padR=10,padT=26,padB=34;
    const iw=W-padL-padR,ih=H-padT-padB;
    const max=Math.max(...values,0),min=Math.min(...values,0);
    const range=(max-min)||1;
    const y=v=>padT+ih-((v-min)/range)*ih;
    const isDates=labels.every(l=>!isNaN(new Date(String(l).trim()).getTime())&&/\d/.test(String(l)));
    const n=values.length;
    const short=(s)=>{const t=String(s).trim();return t.length>9?t.slice(0,8)+"…":t;};
    let body;
    if(isDates){
      const x=i=>padL+(n===1?iw/2:(i/(n-1))*iw);
      const pts=values.map((v,i)=>x(i)+","+y(v)).join(" ");
      body=(<g>
        <polyline points={pts} fill="none" stroke={c} strokeWidth="2"/>
        {values.map((v,i)=>(<g key={i}>
          <circle cx={x(i)} cy={y(v)} r="3" fill={c}/>
          <text x={x(i)} y={y(v)-7} textAnchor="middle" fontSize="8" fill="#A0AAC0">{vizFmt(v)}</text>
          <text x={x(i)} y={H-padB+12} textAnchor="middle" fontSize="7.5" fill="#5A6480">{short(labels[i])}</text>
        </g>))}
      </g>);
    }else{
      const bw=Math.min(44,(iw/n)*0.62);
      const x=i=>padL+(i+0.5)*(iw/n)-bw/2;
      body=(<g>
        {values.map((v,i)=>{
          const yv=y(Math.max(v,0)),y0=y(0);
          const h=Math.max(2,Math.abs(y0-yv));
          return (<g key={i}>
            <rect x={x(i)} y={v>=0?yv:y0} width={bw} height={h} rx="3" fill={c} opacity={0.85}/>
            <text x={x(i)+bw/2} y={(v>=0?yv:y0+h)-4} textAnchor="middle" fontSize="8" fill="#A0AAC0">{vizFmt(v)}</text>
            <text x={x(i)+bw/2} y={H-padB+12} textAnchor="middle" fontSize="7.5" fill="#5A6480">{short(labels[i])}</text>
          </g>);
        })}
      </g>);
    }
    return (
      <div style={{margin:"6px 0 10px",background:"#0d1220",border:"1px solid #1a2030",borderRadius:6,padding:"8px 6px 2px"}}>
        <div style={{fontSize:9,fontWeight:700,color:c,textTransform:"uppercase",letterSpacing:"0.05em",padding:"0 8px 4px"}}>{"📊 "}{seriesName||"Values"}<span style={{color:"#5A6480",fontWeight:500,textTransform:"none",marginLeft:6}}>auto-generated chart</span></div>
        <svg viewBox={"0 0 "+W+" "+H} style={{width:"100%",height:"auto",display:"block"}}>
          <line x1={padL} y1={y(0)} x2={W-padR} y2={y(0)} stroke="#2a3244" strokeWidth="1"/>
          <text x={padL-6} y={y(max)+3} textAnchor="end" fontSize="8" fill="#5A6480">{vizFmt(max)}</text>
          <text x={padL-6} y={y(min)+3} textAnchor="end" fontSize="8" fill="#5A6480">{vizFmt(min)}</text>
          {body}
        </svg>
      </div>
    );
  }catch{return null;}
}
function autoChartFromTable(header,dataRows,accent,key){
  try{
    if(!header||!dataRows||dataRows.length<2||dataRows.length>14)return null;
    const cols=header.length;
    let numCol=-1;
    for(let ci=1;ci<cols;ci++){
      const vals=dataRows.map(r=>vizNum(r[ci]));
      const okCount=vals.filter(v=>!isNaN(v)).length;
      if(okCount>=Math.ceil(dataRows.length*0.7)&&vals.some(v=>v!==0&&!isNaN(v))){numCol=ci;break;}
    }
    if(numCol===-1)return null;
    const pairs=dataRows.map(r=>({l:String(r[0]||"").trim(),v:vizNum(r[numCol])})).filter(p=>p.l&&!isNaN(p.v)).slice(0,12);
    if(pairs.length<2)return null;
    return <AutoChart key={key} labels={pairs.map(p=>p.l)} values={pairs.map(p=>p.v)} seriesName={String(header[numCol]||"").trim()} accent={accent}/>;
  }catch{return null;}
}

// Module-scope markdown→slides parser — the Project Engine PPTX fallback path.
// (A closure-scoped copy exists elsewhere but was NOT visible at the render call
// site, causing "parseMdToSlides is not defined" → every PPTX crashed to a .md dump.)
// Salvage complete slide objects from truncated/malformed JSON — a cut-off
// AI response no longer collapses a 12-slide deck down to 2 slides.
function salvageSlidesJson(content:string):any[]{
  try{
    const out:any[]=[];let i=0;
    while(i<content.length){
      const a=content.indexOf('{"layout"',i);
      const b=content.indexOf('{ "layout"',i);
      const st=(a===-1)?b:(b===-1?a:Math.min(a,b));
      if(st===-1)break;
      let depth=0,j=st,inStr=false,esc=false;
      for(;j<content.length;j++){
        const ch=content[j];
        if(esc){esc=false;continue;}
        if(ch==="\\"){esc=true;continue;}
        if(ch==='"'){inStr=!inStr;continue;}
        if(!inStr){if(ch==="{")depth++;else if(ch==="}"){depth--;if(depth===0){j++;break;}}}
      }
      if(depth!==0)break;
      try{const o=JSON.parse(content.slice(st,j));if(o&&o.layout)out.push(o);}catch{}
      i=j;
    }
    return out;
  }catch{return [];}
}

function parseMdToSlidesGlobal(md:string,coName:string,title:string){
  const lines=md.split("\n").filter((l:string)=>l.trim());
  const slides:any[]=[{layout:"title",title,subtitle:coName,meta:coName+" \u00b7 "+new Date().toLocaleDateString("en-GB")}];
  let currentSlide:any=null;
  const pushCurrent=()=>{if(currentSlide?.bullets?.length||currentSlide?.content)slides.push(currentSlide);};
  for(const line of lines){
    if(line.startsWith("# ")){
      pushCurrent();
      currentSlide={layout:"exec_summary",title:line.replace(/^#+\s*/,""),bullets:[]};
    } else if(line.startsWith("## ")||line.startsWith("### ")){
      pushCurrent();
      currentSlide={layout:"full_text",title:line.replace(/^#+\s*/,""),bullets:[]};
    } else if(currentSlide){
      const clean=line.replace(/^[-*\u2022]\s*/,"").trim();
      if(clean) currentSlide.bullets=(currentSlide.bullets||[]).concat([clean]);
    }
  }
  pushCurrent();
  slides.push({layout:"closing",title:"Next Steps & Recommendations",actions:["Review findings with leadership","Assign owners and timelines","Implement priority actions"]});
  return {title,slides};
}

// ─── MARKDOWN RENDERER ───────────────────────────────────────────────────────
// Two faults this fixes, both visible on screen:
//
// 1. EVERY COLOUR WAS HARDCODED FOR THE DARK THEME. "#A0AAC0" text and "#F1F5F9"
//    headings are correct on a near-black background and nearly invisible on the
//    Executive Editorial cream. The whole app moved to CSS variables; this one
//    function never did, which is why the body text washed out.
//
// 2. TABLE CELLS WERE RENDERED AS PLAIN TEXT. `{cell.trim()}` printed the raw
//    string, so **72%** appeared with literal asterisks in every table. Cells now
//    go through the same inline parser as everything else.
function mdInline(s){
  return String(s??"")
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/\*\*(.+?)\*\*/g,'<strong style="color:var(--oiq-ink,#F1F5F9);font-weight:700">$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g,'$1<em>$2</em>')
    .replace(/`(.+?)`/g,'<code style="background:var(--oiq-surface2,#080c18);padding:1px 4px;border-radius:3px;font-size:.92em;font-family:monospace">$1</code>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,'<a href="$2" target="_blank" rel="noopener noreferrer" style="color:var(--oiq-accent,#14B8A6)">$1</a>')
    .replace(/(^|\s)(https?:\/\/[^\s<]+)/g,'$1<a href="$2" target="_blank" rel="noopener noreferrer" style="color:var(--oiq-accent,#14B8A6);word-break:break-all">$2</a>');
}
function Md({text,ac}){
  if(!text)return null;
  const c=ac||"var(--oiq-accent,#14B8A6)";
  const INK="var(--oiq-ink,#F1F5F9)";
  const BODY="var(--oiq-ink2,#A0AAC0)";
  const MUTED="var(--oiq-muted,#8892B0)";
  const BDR="var(--oiq-border,#1a2030)";
  const SURF="var(--oiq-surface,#0d1220)";
  const lines=text.split("\n");
  const els=[];let tbl=[],inT=false,inC=false,cL=[];
  const cell=(v)=><span dangerouslySetInnerHTML={{__html:mdInline(String(v).trim())}}/>;
  // A column whose values are all numeric (money, percentages, counts) is aligned
  // RIGHT, the way any financial table is set. Headers align with their own column
  // and sit on the same baseline as the first row of data - they were defaulting
  // to middle alignment while the cells were top-aligned, which is why headings
  // appeared to float above the wrong line whenever a cell wrapped.
  const NUMERIC=/^[\s]*[₹$€£]?\s*[-+(]?[\d,]+(\.\d+)?\s*(%|cr|crore|lakh|lakhs|k|mn|bn|tpd|tpa|x)?\s*[)]?[\s]*$/i;
  const fT=()=>{
    if(!tbl.length)return;
    const h=tbl[0];
    // Some models omit the |---|---| separator row entirely. Detecting it rather
    // than assuming row 1 stops the first data row being swallowed.
    const hasSep=tbl.length>1&&tbl[1].every(x=>/^[\s:-]+$/.test(String(x)));
    // Every row is padded or trimmed to the header width. A short row used to
    // shift its own cells left; a long row used to push a phantom column.
    const d=tbl.slice(hasSep?2:1).map(r=>{
      const row=r.slice(0,h.length);
      while(row.length<h.length)row.push("");
      return row;
    });
 
    const alignRight=h.map((_,ci)=>{
      const vals=d.map(r=>String(r[ci]??"").replace(/\*\*/g,"").trim()).filter(v=>v&&v!=="-"&&v!=="\u2014");
      return vals.length>0&&vals.every(v=>NUMERIC.test(v));
    });
    // The tables LOOKED misaligned for a reason that was not text-alignment: there
    // were no VERTICAL rules and the widths were fully automatic, so one long cell
    // (a formula, an assumption) stretched to half the table while its neighbours
    // squeezed. With nothing separating the columns, the eye cannot tie a heading
    // to the data underneath it. Three changes fix it together:
    //   1. a subtle vertical rule between every column
    //   2. fixed layout with computed widths, so no column can run away
    //   3. numeric columns held narrow, text columns given the remaining space
    const nCols=h.length;
    const wide=h.map((_,ci)=>{
      if(alignRight[ci])return 1;                       // numbers stay compact
      const avg=d.reduce((s,r)=>s+String(r[ci]??"").length,0)/Math.max(d.length,1);
      return avg>60?3:avg>28?2:1.4;                     // long prose gets more room
    });
    const wTot=wide.reduce((a,b)=>a+b,0)||nCols;
    const colW=wide.map(w=>(w/wTot*100).toFixed(1)+"%");
    const RULE="1px solid "+BDR;
    els.push(<div key={"t"+els.length} style={{overflowX:"auto",margin:"12px 0",borderRadius:6,border:RULE}}><table style={{width:"100%",minWidth:Math.min(nCols*130,760),borderCollapse:"collapse",fontSize:11.5,tableLayout:"fixed"}}><colgroup>{colW.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup><thead><tr>{h.map((v,i)=><th key={i} style={{textAlign:alignRight[i]?"right":"left",verticalAlign:"bottom",padding:"8px 10px",borderBottom:"2px solid "+BDR,borderRight:i<nCols-1?RULE:"none",color:c,fontWeight:800,fontSize:9.5,textTransform:"uppercase",letterSpacing:".04em",background:SURF,lineHeight:1.35,wordBreak:"break-word"}}>{cell(v)}</th>)}</tr></thead><tbody>{d.map((row,ri)=><tr key={ri}>{row.map((cl,ci)=><td key={ci} style={{padding:"7px 10px",borderBottom:RULE,borderRight:ci<nCols-1?RULE:"none",color:BODY,fontSize:11.5,lineHeight:1.55,verticalAlign:"top",textAlign:alignRight[ci]?"right":"left",fontVariantNumeric:alignRight[ci]?"tabular-nums":"normal",wordBreak:"break-word",overflowWrap:"anywhere"}}>{cell(cl)}</td>)}</tr>)}</tbody></table></div>);
 
    try{const ch=autoChartFromTable(h,d,typeof c==="string"&&c.startsWith("#")?c:"#14B8A6","ch"+els.length);if(ch)els.push(ch);}catch{}
    tbl=[];inT=false;
  };
  for(let i=0;i<lines.length;i++){
    const l=lines[i];
    if(l.startsWith("```")){if(inC){els.push(<pre key={"c"+els.length} style={{background:SURF,border:"1px solid "+BDR,borderRadius:6,padding:"10px",margin:"6px 0",fontSize:10.5,fontFamily:"monospace",overflowX:"auto",color:BODY}}>{cL.join("\n")}</pre>);cL=[];inC=false;}else inC=true;continue;}
    if(inC){cL.push(l);continue;}
    if(l.includes("|")&&l.trim().startsWith("|")){
      if(!inT){fT();inT=true;}
      // THE ACTUAL CAUSE OF THE MISALIGNED TABLES.
      // The old parser was: split("|").filter((_,j,a)=>j>0&&j<a.length-1)
      // That drops the first and last fragment, which is right ONLY when the row
      // both starts and ends with a pipe. Models very often omit the closing one:
      //     | Low conversion | Medium | High | Offer free trial
      // produced THREE cells instead of four - the mitigation text was silently
      // deleted and every remaining cell slid one column to the left. With fixed
      // column widths that is exactly the "headings over the wrong column" you saw.
      const cells=l.trim().replace(/^\|/,"").replace(/\|\s*$/,"").split("|");
      tbl.push(cells);continue;
    }else if(inT)fT();
    if(l.startsWith("### "))els.push(<h4 key={i} style={{margin:"14px 0 5px",color:c,fontSize:12.5,fontWeight:800,letterSpacing:".01em"}}>{cell(l.slice(4))}</h4>);
    else if(l.startsWith("## "))els.push(<h3 key={i} style={{margin:"18px 0 6px",color:INK,fontSize:14.5,fontWeight:800,lineHeight:1.35}}>{cell(l.slice(3))}</h3>);
    else if(l.startsWith("# "))els.push(<h2 key={i} style={{margin:"18px 0 7px",color:INK,fontSize:16.5,fontWeight:800,lineHeight:1.3}}>{cell(l.slice(2))}</h2>);
    else if(l.match(/^[-*]\s/))els.push(<div key={i} style={{paddingLeft:15,position:"relative",margin:"3px 0",lineHeight:1.7,color:BODY}}><span style={{position:"absolute",left:3,color:c}}>&bull;</span>{cell(l.replace(/^[-*]\s/,""))}</div>);
    else if(l.match(/^\d+\.\s/)){const n=l.match(/^(\d+)\./)[1];els.push(<div key={i} style={{paddingLeft:22,position:"relative",margin:"3px 0",lineHeight:1.7,color:BODY}}><span style={{position:"absolute",left:2,color:c,fontWeight:700,fontSize:10.5}}>{n}.</span>{cell(l.replace(/^\d+\.\s/,""))}</div>);}
    else if(l.startsWith("> "))els.push(<div key={i} style={{borderLeft:"3px solid "+c,paddingLeft:11,margin:"7px 0",color:MUTED,fontStyle:"italic",lineHeight:1.65}}>{cell(l.slice(2))}</div>);
    else if(l.startsWith("---"))els.push(<hr key={i} style={{border:"none",borderTop:"1px solid "+BDR,margin:"12px 0"}}/>);
    else if(l.trim()==="")els.push(<div key={i} style={{height:6}}/>);
    else els.push(<div key={i} style={{margin:"3px 0",lineHeight:1.72,color:BODY}} dangerouslySetInnerHTML={{__html:mdInline(l)}}/>);
  }
  fT();
  return <>{els}</>;
}
 
// ─── SHARED RESEARCH BRIEF CARD ──────────────────────────────────────────────
// The Boardroom showed a compact card with a source count and an Open modal.
// Time Machine and Autopilot dumped the whole brief into a plain grey div with no
// counts and no modal, which is why they looked nothing like the Boardroom.
// One component now serves all three, so future improvements land everywhere.
function ResearchBriefCard({brief,accent,label}:{brief:string;accent?:string;label?:string}){
  const [open,setOpen]=useState(false);
  if(!brief)return null;
  const c=accent||"#3B82F6";
  // Honest counts: a source is a URL, a finding is a bullet. Headings and warnings
  // are not sources - counting them overstated the evidence base.
  const urls=Array.from(new Set((brief.match(/https?:\/\/[^\s)]+/g)||[])));
  const findings=(brief.match(/^\s*[•\-\*]\s+/gm)||[]).length;
  const gaps=(brief.match(/NO RELIABLE SOURCE FOUND|No usable findings/g)||[]).length;
  const grounded=urls.length>0;
  const meta=(grounded?(urls.length+" sources · "+findings+" findings"):"Ungrounded - no sources retrieved")+(gaps>0?" · "+gaps+" evidence gaps":"")+" · verify before external use";
  return (
    <>
      <div style={{marginBottom:10,background:"var(--oiq-surface,#131825)",border:"1px solid "+(grounded?c+"33":"#EF444455"),borderRadius:8,padding:"11px 13px",display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:18,flexShrink:0}}>{grounded?"📚":"⚠"}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12,fontWeight:800,color:"var(--oiq-ink,#F1F5F9)"}}>{label||"Research Brief"}</div>
          <div style={{fontSize:9.5,color:"var(--oiq-muted,#5A6480)",marginTop:2}}>{meta}</div>
        </div>
        <button onClick={()=>setOpen(true)} style={{background:"none",border:"1px solid "+c+"44",borderRadius:6,padding:"6px 12px",color:c,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Open ↗</button>
      </div>
      {open&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",backdropFilter:"blur(3px)",zIndex:150,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setOpen(false)}>
          <div style={{background:"var(--oiq-surface2,#0c1120)",border:"1px solid var(--oiq-border,#1a2030)",borderRadius:12,width:"100%",maxWidth:900,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 18px",borderBottom:"1px solid var(--oiq-border,#1a2030)",flexShrink:0}}>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:800,color:"var(--oiq-ink,#F1F5F9)"}}>{label||"Research Brief"}</div>
                <div style={{fontSize:10,color:"var(--oiq-muted,#5A6480)",marginTop:2}}>{meta}</div>
              </div>
              <button onClick={()=>{try{navigator.clipboard.writeText(brief);}catch{}}} style={{background:"none",border:"1px solid var(--oiq-border,#1a2030)",borderRadius:6,padding:"6px 12px",color:"var(--oiq-muted,#A0AAC0)",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Copy all</button>
              <button onClick={()=>setOpen(false)} style={{background:"none",border:"1px solid var(--oiq-border,#1a2030)",borderRadius:6,padding:"6px 11px",color:"var(--oiq-muted,#A0AAC0)",fontSize:14,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"16px 18px",fontSize:11.5,lineHeight:1.75,color:"var(--oiq-ink2,#A0AAC0)"}}>
              <Md text={brief} ac={c}/>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
 
// v2 — VISUAL REDESIGN. Same free, native SpeechRecognition mechanism as
// before (nothing about HOW this transcribes changed, and every existing
// caller across the app - Time Machine, Workflow, Project Engine, Data Hub,
// Ledger, Dispatch - gets the new look automatically, with no wiring changes
// anywhere else). What changed is the LISTENING state: a real, audio-reactive
// waveform (Web Audio API AnalyserNode against the same microphone stream
// this component already opens) replacing a single static emoji, styled with
// this platform's own theme accent so it matches whichever of the 8 themes
// is active rather than a fixed hard-coded colour.
// v3 — OPENAI-FALLBACK PARITY WITH VoiceButton.tsx.
// THE CONFIRMED BUG: this function had ZERO knowledge of OpenAI at all — it
// always used the free native engine regardless of whether the caller had an
// OpenAI key. Every screen using MicButton (Time Machine, Workflow, Project
// Engine, Data Hub, Ledger, Dispatch, and — via a prop pass-through, not a
// separate implementation — the "AI Boardroom" screen itself) inherited that
// gap. This is the single point of fix for all of them at once.
function MicButton({lang,onResult,disabled,openaiKey}){
  const [st,setSt]=useState("idle");const [err,setErr]=useState("");const recRef=useRef(null);
  const [bars,setBars]=useState(Array(18).fill(0.08));
  const streamRef=useRef(null);const audioCtxRef=useRef(null);const analyserRef=useRef(null);const rafRef=useRef(null);
  const mediaRecRef=useRef(null);const chunksRef=useRef([]);
  const SR=window.SpeechRecognition||window.webkitSpeechRecognition||null;

  const stopWaveform=()=>{
    if(rafRef.current){cancelAnimationFrame(rafRef.current);rafRef.current=null;}
    try{audioCtxRef.current?.close();}catch{}
    audioCtxRef.current=null;analyserRef.current=null;
    streamRef.current?.getTracks().forEach(t=>t.stop());streamRef.current=null;
  };
  const startWaveform=(stream)=>{
    try{
      const ctx=new (window.AudioContext||window.webkitAudioContext)();
      if(ctx.state==="suspended"){ctx.resume().catch(()=>{});}
      const source=ctx.createMediaStreamSource(stream);
      const analyser=ctx.createAnalyser();analyser.fftSize=64;analyser.smoothingTimeConstant=0.6;
      source.connect(analyser);audioCtxRef.current=ctx;analyserRef.current=analyser;
      const buf=new Uint8Array(analyser.frequencyBinCount);
      const tick=()=>{
        analyser.getByteFrequencyData(buf);
        const step=Math.max(1,Math.floor(buf.length/18));
        const next=[];for(let i=0;i<18;i++)next.push(Math.max(0.08,Math.min(1,(buf[i*step]/255)*1.5)));
        setBars(next);rafRef.current=requestAnimationFrame(tick);
      };
      tick();
    }catch{/* waveform is cosmetic — never block transcription if this fails */}
  };

  // ── OPENAI PATH: record, upload, get back punctuated, cleaned-up text ──────
  const startOpenAIPath=(stream)=>{
    const mime=MediaRecorder.isTypeSupported("audio/webm;codecs=opus")?"audio/webm;codecs=opus":"audio/webm";
    const rec=new MediaRecorder(stream,{mimeType:mime});chunksRef.current=[];mediaRecRef.current=rec;
    rec.ondataavailable=(e)=>{if(e.data.size>0)chunksRef.current.push(e.data);};
    rec.onstop=async()=>{
      stopWaveform();
      const blob=new Blob(chunksRef.current,{type:mime});
      setSt("transcribing");
      try{
        const {transcribeAudio}=await import("./lib/Voice");
        const result=await transcribeAudio(blob,(id)=>id==="openai"?openaiKey:undefined);
        if(result.text)onResult(result.text);
        setSt("idle");
      }catch(e){
        setErr(String(e?.message||e).slice(0,180));setSt("error");
      }
    };
    rec.start();setSt("listening");
  };

  // ── FREE, NATIVE PATH — unchanged from before ───────────────────────────────
  const startNativePath=(stream)=>{
    if(!SR){stopWaveform();setErr("Voice not supported. Use Chrome or Edge, or add an OpenAI key in Settings.");setSt("error");return;}
    const rec=new SR();rec.lang=lang||"en-IN";rec.continuous=true;rec.interimResults=true;recRef.current=rec;
    let final="";
    rec.onstart=()=>setSt("listening");
    rec.onresult=(e)=>{final="";for(let i=0;i<e.results.length;i++){if(e.results[i].isFinal)final+=e.results[i][0].transcript;}};
    rec.onerror=(e)=>{stopWaveform();if(e.error==="not-allowed")setErr("Mic access denied.");else setErr("Voice error: "+e.error+".");setSt("error");};
    rec.onend=()=>{stopWaveform();if(final.trim())onResult(final.trim());setSt("idle");};
    try{rec.start();}catch{stopWaveform();setErr("Could not start voice input.");setSt("error");}
  };

  const stop=()=>{
    if(openaiKey){try{mediaRecRef.current?.stop();}catch{}}
    else{try{recRef.current?.stop();}catch{}stopWaveform();}
    if(!openaiKey)setSt("idle");
  };
  const start=()=>{
    if(disabled)return;
    if(st==="listening"){stop();return;}
    setSt("requesting");setErr("");
    navigator.mediaDevices?.getUserMedia({audio:true}).then((stream)=>{
      streamRef.current=stream; // waveform animation removed - the toggle button no longer has a visual that needs it
      // THE FALLBACK DECISION: OpenAI when a key is available, the free
      // native engine otherwise. Nobody picks a mode — this decides silently
      // and automatically, exactly matching VoiceButton's own behaviour, so
      // every voice surface on the platform behaves the same way.
      if(openaiKey)startOpenAIPath(stream);else startNativePath(stream);
    }).catch(()=>{setErr("Mic access denied. Allow mic in browser settings.");setSt("error");});
  };

  // ONE FIXED-SIZE BUTTON, THE SAME LAYOUT COMPLAINT FIXED HERE TOO. The old
  // "listening" state expanded into a separate pill with its own stop
  // button, next to whatever else sat beside it - here that meant the
  // Language picker and the composer around it shifted every time someone
  // spoke. Now the SAME button that started listening also stops it: no
  // second control, no layout change, ever.
  const isListening = st==="listening";
  const isBusy = st==="requesting"||st==="transcribing";
  return(
    <div style={{position:"relative",flexShrink:0}}>
      <button onClick={isListening?stop:start} disabled={disabled||isBusy}
        title={isListening?"Click to stop and use this text":openaiKey?"Voice input — enhanced with OpenAI, punctuated automatically":!SR?"Voice not supported":"Click to speak — free, no API key needed"}
        style={{background:isListening?"#EF4444":"none",border:"1px solid "+(isListening?"#EF4444":"#1a2030"),borderRadius:5,
          padding:"5px 8px",cursor:disabled?"not-allowed":isBusy?"wait":"pointer",fontSize:15,lineHeight:1,
          color:isListening?"#fff":"inherit",opacity:disabled?0.35:1,transition:"all 0.2s"}}>
        {isBusy?"…":isListening?"\u25A0":st==="error"?"⚠️":"🎤"}
      </button>
      {st==="error"&&err&&<div style={{position:"absolute",bottom:"calc(100% + 6px)",right:0,background:"#1a0a0a",border:"1px solid #EF444466",borderRadius:6,padding:"7px 10px",fontSize:10,color:"#EF9999",width:220,lineHeight:1.5,zIndex:99}}>
        {err}<button onClick={()=>setSt("idle")} style={{display:"block",marginTop:5,background:"none",border:"none",color:"#EF6666",fontSize:9,cursor:"pointer",fontFamily:"Manrope,sans-serif",textDecoration:"underline"}}>Dismiss</button>
      </div>}
    </div>
  );
}

function LangPick({value,onChange}){
  return <select value={value} onChange={e=>onChange(e.target.value)} style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:5,color:"#8892B0",fontSize:9,fontFamily:"Manrope,sans-serif",padding:"4px 3px",cursor:"pointer",flexShrink:0,maxWidth:90}}>{VOICE_LANGS.map(l=><option key={l.code} value={l.code} style={{background:"#0a0e1a"}}>{l.label}</option>)}</select>;
}

// FIX BUG 7: Global toast component
function Toaster({toasts,onDismiss}){
  if(!toasts.length)return null;
  const clr={error:"#EF4444",success:"#10B981",info:"#3B82F6",warning:"#F59E0B"};
  return(
    <div style={{position:"fixed",bottom:20,right:20,zIndex:9999,display:"flex",flexDirection:"column",gap:8,fontFamily:"Manrope,sans-serif",maxWidth:400,pointerEvents:"none"}}>
      {toasts.map(t=>(
        <div key={t.id} style={{background:"#131825",border:"1px solid "+(clr[t.type]||clr.info)+"55",borderLeft:"3px solid "+(clr[t.type]||clr.info),borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"flex-start",gap:8,animation:"fadeIn 0.2s",boxShadow:"0 4px 24px rgba(0,0,0,0.55)",fontSize:11,color:"#F1F5F9",lineHeight:1.5,pointerEvents:"all"}}>
          <span style={{flexShrink:0}}>{t.type==="error"?"⚠️":t.type==="success"?"✅":t.type==="warning"?"⚡":"ℹ️"}</span>
          <span style={{flex:1}}>{t.msg}</span>
          <button onClick={()=>onDismiss(t.id)} style={{background:"none",border:"none",color:"#5A6480",fontSize:16,cursor:"pointer",lineHeight:1,padding:0,flexShrink:0}}>×</button>
        </div>
      ))}
    </div>
  );
}

// FIX BUG 6: safe blob download (no navigation side-effects)
function dlFile(filename,content,mime){
  const b=new Blob([typeof content==="string"?content:JSON.stringify(content,null,2)],{type:mime||"application/json"});
  const u=URL.createObjectURL(b);
  const a=document.createElement("a");
  a.href=u;a.download=filename;a.style.display="none";
  document.body.appendChild(a);a.click();
  document.body.removeChild(a);
  setTimeout(()=>URL.revokeObjectURL(u),200);
}

// ─── FEATURE 4 & 5: LIBRARY LOADERS (cdnjs only) ────────────────────────────
function loadScript(src){
  return new Promise((res,rej)=>{
    if([...document.scripts].some(s=>s.src===src))return res();
    const s=document.createElement("script");s.src=src;s.onload=()=>res();s.onerror=()=>rej(new Error("Failed to load "+src));
    document.head.appendChild(s);
  });
}
async function ensureJsPDF(){
  if(window.jspdf?.jsPDF)return window.jspdf.jsPDF;
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  if(!window.jspdf?.jsPDF)throw new Error("jsPDF unavailable");
  return window.jspdf.jsPDF;
}
async function ensureHtml2Canvas(){
  if(window.html2canvas)return window.html2canvas;
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
  if(!window.html2canvas)throw new Error("html2canvas unavailable");
  return window.html2canvas;
}

// Escape HTML so raw text from the AI can't inject markup.
function escHtml(s){return (s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");}

// Inline-format a single line: **bold**, *italic*, `code`. Runs AFTER escaping.
function fmtInline(s){
  return escHtml(s)
    .replace(/\*\*(.+?)\*\*/g,"<strong>$1</strong>")
    .replace(/\*(.+?)\*/g,"<em>$1</em>")
    .replace(/`(.+?)`/g,"<code>$1</code>");
}

// Convert the AI's markdown into clean, styled HTML BLOCKS.
// Each top-level child of the returned container is one atomic block, so the
// paginator can place or page-break between blocks without splitting them.
function mdToHtmlBlocks(text,accent){
  const lines=(text||"").split("\n");
  const out=[];
  let i=0;
  let tableBuf=[];
  const flushTable=()=>{
    if(!tableBuf.length)return;
    const rows=tableBuf.filter(r=>!r.trim().match(/^\|[\s|:\-]+\|$/)); // drop the |---|---| separator
    // Same bug as the on-screen renderer: a row without a closing pipe lost its
    // last cell. This path builds the HTML that becomes your PDF and PowerPoint,
    // so the exported document had the identical missing-column problem.
    const raw=rows.map(r=>r.trim().replace(/^\|/,"").replace(/\|\s*$/,"").split("|").map(c=>c.trim()));
    const nCol=raw.length?raw[0].length:0;
    const cells=raw.map(rw=>{const x=rw.slice(0,nCol);while(x.length<nCol)x.push("");return x;});
    if(cells.length){
      let html="<table class='pq-tbl'><thead><tr>";
      cells[0].forEach(c=>html+="<th>"+fmtInline(c)+"</th>");
      html+="</tr></thead><tbody>";
      cells.slice(1).forEach(row=>{html+="<tr>";row.forEach(c=>html+="<td>"+fmtInline(c)+"</td>");html+="</tr>";});
      html+="</tbody></table>";
      out.push(html);
    }
    tableBuf=[];
  };
  while(i<lines.length){
    const l=lines[i];
    // table rows accumulate
    if(l.includes("|")&&l.trim().startsWith("|")){tableBuf.push(l);i++;continue;}
    else flushTable();
    if(l.trim()===""){i++;continue;}
    if(l.startsWith("### "))out.push("<h4 class='pq-h4'>"+fmtInline(l.slice(4))+"</h4>");
    else if(l.startsWith("## "))out.push("<h3 class='pq-h3'>"+fmtInline(l.slice(3))+"</h3>");
    else if(l.startsWith("# "))out.push("<h2 class='pq-h2'>"+fmtInline(l.slice(2))+"</h2>");
    else if(l.match(/^[-*]\s/))out.push("<div class='pq-li'><span class='pq-bul'>&bull;</span><span>"+fmtInline(l.replace(/^[-*]\s+/,""))+"</span></div>");
    else if(l.match(/^\d+\.\s/)){const n=l.match(/^(\d+)\./)[1];out.push("<div class='pq-li'><span class='pq-num'>"+n+".</span><span>"+fmtInline(l.replace(/^\d+\.\s+/,""))+"</span></div>");}
    else if(l.startsWith("> "))out.push("<div class='pq-quote'>"+fmtInline(l.slice(2))+"</div>");
    else if(l.startsWith("---"))out.push("<hr class='pq-hr'/>");
    else out.push("<p class='pq-p'>"+fmtInline(l)+"</p>");
    i++;
  }
  flushTable();
  return out;
}

// WYSIWYG PDF: render markdown -> styled HTML blocks -> html2canvas per block ->
// place into jsPDF with block-aware page breaks (never split a block; only a
// too-tall table is sliced, and html2canvas keeps rows intact visually).
async function generatePDFv2(type,title,bodyText,co,cur){
  const jsPDF=await ensureJsPDF();
  const html2canvas=await ensureHtml2Canvas();
  const A={summary:"#14B8A6",detailed:"#3B82F6",executive:"#64748B",investor:"#A855F7"}[type]||"#14B8A6";
  const typeLabel=PDF_TYPES.find(t=>t.id===type)?.label||"Report";

  // Offscreen render container at fixed content width (px @ ~96dpi for A4 usable width)
  const CONTENT_W=720; // px; maps to ~190mm printable width
  const host=document.createElement("div");
  host.style.cssText="position:fixed;left:-99999px;top:0;width:"+CONTENT_W+"px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;color:#1e1e1e;";
  const style=document.createElement("style");
  style.textContent=`
    .pq-wrap{padding:0;background:#fff}
    .pq-block{padding:0 4px}
    .pq-h2{font-size:20px;font-weight:800;color:#111;margin:14px 0 6px}
    .pq-h3{font-size:16px;font-weight:800;color:`+A+`;margin:14px 0 5px;border-bottom:2px solid `+A+`33;padding-bottom:3px}
    .pq-h4{font-size:13px;font-weight:700;color:#333;margin:10px 0 4px}
    .pq-p{font-size:12px;line-height:1.55;margin:5px 0;color:#222}
    .pq-li{font-size:12px;line-height:1.5;margin:3px 0;padding-left:16px;position:relative;color:#222}
    .pq-bul{position:absolute;left:2px;color:`+A+`}
    .pq-num{position:absolute;left:0;color:`+A+`;font-weight:700}
    .pq-quote{font-size:12px;font-style:italic;color:#555;border-left:3px solid `+A+`55;padding-left:10px;margin:6px 0}
    .pq-hr{border:none;border-top:1px solid #ddd;margin:8px 0}
    .pq-tbl{width:100%;border-collapse:collapse;margin:8px 0;font-size:11px}
    .pq-tbl th{background:`+A+`;color:#fff;text-align:left;padding:6px 8px;font-weight:700;font-size:10px}
    .pq-tbl td{padding:5px 8px;border-bottom:1px solid #e2e2e2;color:#222;vertical-align:top}
    .pq-tbl tr:nth-child(even) td{background:#f6f8fb}
    strong{color:#000}code{background:#f0f0f0;padding:1px 4px;border-radius:3px;font-family:monospace;font-size:11px}
  `;
  host.appendChild(style);
  const wrap=document.createElement("div");wrap.className="pq-wrap";
  const blocks=mdToHtmlBlocks(bodyText,A);
  blocks.forEach(b=>{const d=document.createElement("div");d.className="pq-block";d.innerHTML=b;wrap.appendChild(d);});
  host.appendChild(wrap);
  document.body.appendChild(host);

  try{
    const doc=new jsPDF({unit:"pt",format:"a4"});
    const W=doc.internal.pageSize.getWidth(),H=doc.internal.pageSize.getHeight();
    const M=42;
    const usableW=W-2*M;
    const pxToPt=usableW/CONTENT_W; // scale factor: canvas px -> pdf pt
    const maxBlockH=H-2*M;

    // Cover band
    const rgb=(hex)=>{const h=hex.replace("#","");return [parseInt(h.slice(0,2),16),parseInt(h.slice(2,4),16),parseInt(h.slice(4,6),16)];};
    const AC=rgb(A);
    doc.setFillColor(AC[0],AC[1],AC[2]);doc.rect(0,0,W,84,"F");
    doc.setTextColor(255,255,255);doc.setFont("helvetica","bold");doc.setFontSize(20);
    doc.text(co.name||"Company",M,42,{maxWidth:usableW});
    doc.setFontSize(10);doc.setFont("helvetica","normal");
    doc.text(typeLabel+"  ·  "+(co.industry||"")+"  ·  "+(co.location||""),M,64);
    let y=104;
    doc.setTextColor(20,20,20);doc.setFont("helvetica","bold");doc.setFontSize(15);
    const titleLines=doc.splitTextToSize(title,usableW);
    titleLines.forEach(line=>{doc.text(line,M,y);y+=18;});
    doc.setFontSize(8);doc.setTextColor(120,120,120);doc.setFont("helvetica","normal");
    doc.text("Generated "+new Date().toLocaleString()+"  ·  Currency: "+cur.code,M,y);y+=14;
    doc.setDrawColor(AC[0],AC[1],AC[2]);doc.setLineWidth(1.2);doc.line(M,y,W-M,y);y+=14;

    // Render each block to its own canvas, place with page-break awareness
    const blockEls=Array.from(wrap.children);
    for(const el of blockEls){
      const canvas=await html2canvas(el,{scale:2,backgroundColor:"#ffffff",logging:false});
      const imgH=canvas.height/2*pxToPt; // /2 because scale:2
      const imgW=usableW;
      // If block fits on current page, place it. Else new page. If taller than a
      // whole page, slice it vertically across pages (row borders stay intact visually).
      if(imgH<=maxBlockH){
        if(y+imgH>H-M){doc.addPage();y=M;}
        doc.addImage(canvas.toDataURL("image/png"),"PNG",M,y,imgW,imgH);
        y+=imgH+4;
      }else{
        // tall block: slice the source canvas into page-height chunks
        const pageCanvasH=Math.floor((maxBlockH/pxToPt)*2); // source px per page slice
        let sy=0;
        if(y>M+10){doc.addPage();y=M;} // start tall block on a fresh page
        while(sy<canvas.height){
          const sliceH=Math.min(pageCanvasH,canvas.height-sy);
          const slice=document.createElement("canvas");
          slice.width=canvas.width;slice.height=sliceH;
          slice.getContext("2d").drawImage(canvas,0,sy,canvas.width,sliceH,0,0,canvas.width,sliceH);
          const sliceImgH=sliceH/2*pxToPt;
          if(y+sliceImgH>H-M){doc.addPage();y=M;}
          doc.addImage(slice.toDataURL("image/png"),"PNG",M,y,imgW,sliceImgH);
          y+=sliceImgH+4;
          sy+=sliceH;
        }
      }
    }

    // Footer page numbers
    const pages=doc.internal.getNumberOfPages();
    for(let p=1;p<=pages;p++){doc.setPage(p);doc.setFontSize(7);doc.setTextColor(150,150,150);doc.text((co.name||"")+" · Confidential · Page "+p+" of "+pages,M,H-18);}
    doc.save((co.name||"Report").replace(/\s+/g,"-")+"-"+typeLabel.replace(/\s+/g,"-")+"-"+Date.now()+".pdf");
  }finally{
    document.body.removeChild(host);
  }
}
async function ensurePptx(){
  if(window.PptxGenJS)return window.PptxGenJS;
  await loadScript("https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js");
if(!window.PptxGenJS)throw new Error("PptxGenJS unavailable");
  return window.PptxGenJS;
}

// ─── PPTX STYLE EXTRACTION (Step 1 — standalone, not wired into any generator yet) ──
async function ensureJSZip(){
  if(window.JSZip)return window.JSZip;
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js");
  if(!window.JSZip)throw new Error("JSZip unavailable");
  return window.JSZip;
}
async function ensureXLSX(){
  if(window.XLSX)return window.XLSX;
  // xlsx-js-style: drop-in SheetJS fork that actually WRITES cell styles (.s) —
  // bold headers, brand fills, RAG colors. Community 0.18.5 silently drops them.
  try{await loadScript("https://cdn.jsdelivr.net/npm/xlsx-js-style@1.2.0/dist/xlsx.bundle.js");}catch{}
  if(!window.XLSX){await loadScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");}
  if(!window.XLSX)throw new Error("SheetJS unavailable");
  return window.XLSX;
}
function findByLocalName(root,name){
  const all=root.getElementsByTagName("*");
  for(let i=0;i<all.length;i++)if(all[i].localName===name)return all[i];
  return null;
}
function extractColorFromEl(el){
  if(!el)return null;
  const srgb=findByLocalName(el,"srgbClr");
  if(srgb)return "#"+srgb.getAttribute("val").toUpperCase();
  const sys=findByLocalName(el,"sysClr");
  if(sys)return "#"+(sys.getAttribute("lastClr")||"").toUpperCase();
  return null;
}
function extractThemeColors(themeDoc){
  const scheme=findByLocalName(themeDoc,"clrScheme");
  if(!scheme)return null;
  const slots=["dk1","lt1","dk2","lt2","accent1","accent2","accent3","accent4","accent5","accent6"];
  const colors={};
  slots.forEach(s=>{const c=extractColorFromEl(findByLocalName(scheme,s));if(c)colors[s]=c;});
  return Object.keys(colors).length?colors:null;
}
function extractThemeFonts(themeDoc){
  const scheme=findByLocalName(themeDoc,"fontScheme");
  if(!scheme)return null;
  const get=which=>{const f=findByLocalName(scheme,which);const l=f&&findByLocalName(f,"latin");return l?.getAttribute("typeface")||null;};
  const heading=get("majorFont"),body=get("minorFont");
  return(heading||body)?{heading,body}:null;
}
function extractSlideSize(presDoc){
  const sz=findByLocalName(presDoc,"sldSz");
  if(!sz)return null;
  const cx=parseInt(sz.getAttribute("cx"),10),cy=parseInt(sz.getAttribute("cy"),10);
  if(!cx||!cy)return null;
  return{widthIn:cx/914400,heightIn:cy/914400,isWidescreen:(cx/cy)>1.5};
}
// Reads a user-uploaded .pptx and extracts theme colors/fonts/aspect ratio.
// Never throws — returns {error} on any problem, {ok:true,...} on success.
async function extractPptxStyle(file){
  try{
    if(!file)return{error:"No file provided."};
    if(!file.name?.toLowerCase().endsWith(".pptx"))return{error:"Please upload a .pptx file (older .ppt isn't supported)."};
    if(file.size>20*1024*1024)return{error:"File too large (max 20MB)."};
    const JSZip=await ensureJSZip();
    const zip=await JSZip.loadAsync(await file.arrayBuffer());
    // Theme numbering varies — theme1.xml is typical, but decks with multiple
    // slide masters (common in downloaded templates) use theme11.xml, theme23.xml etc.
    // Match any themeN.xml and use the lowest-numbered one.
    const themeMatches=zip.file(/^ppt\/theme\/theme\d+\.xml$/);
    const themeFile=themeMatches&&themeMatches.length
      ?themeMatches.sort((a,b)=>a.name.localeCompare(b.name,undefined,{numeric:true}))[0]
      :null;
    const presFile=zip.file("ppt/presentation.xml");
    if(!themeFile||!presFile)return{error:"This doesn't look like a valid PowerPoint (.pptx) file."};
    const parser=new DOMParser();
    const themeDoc=parser.parseFromString(await themeFile.async("string"),"application/xml");
    const presDoc=parser.parseFromString(await presFile.async("string"),"application/xml");
    const colors=extractThemeColors(themeDoc),fonts=extractThemeFonts(themeDoc),slideSize=extractSlideSize(presDoc);
    if(!colors&&!fonts&&!slideSize)return{error:"Could not read any style information from this file."};
    return{ok:true,colors,fonts,slideSize,sourceFileName:file.name};
  }catch(err){return{error:"Could not read this file: "+(err.message||"unknown error")};}
}
if(typeof window!=="undefined")window.__testPptxStyle=extractPptxStyle;

// Strip markdown to plain text for PDF/PPTX bodies
function stripMd(s){
  return (s||"").replace(/\*\*(.+?)\*\*/g,"$1").replace(/\*(.+?)\*/g,"$1").replace(/`(.+?)`/g,"$1").replace(/^#+\s+/gm,"").replace(/^>\s+/gm,"").replace(/^[-*]\s+/gm,"• ");
}
// Parse a structured doc into sections: {title, lines[]}
function parseSections(text){
  const lines=(text||"").split("\n");
  const secs=[];let cur={title:"Overview",lines:[]};
  for(const l of lines){
    const h=l.match(/^#{1,3}\s+(.+)/);
    if(h){if(cur.lines.length||secs.length===0)secs.push(cur);cur={title:h[1].trim(),lines:[]};}
    else if(l.trim())cur.lines.push(l);
  }
  if(cur.lines.length)secs.push(cur);
  return secs.filter(s=>s.lines.length||s.title);
}

// ─── QUALITY ENGINE: structured slide schema, prompt, and parser ───────────
function buildStructuredDeckPrompt(deckTitle,tLabel){
  return `You are producing a consulting-grade slide deck (think McKinsey/Deloitte quality bar) as STRICT JSON. Output ONLY a valid JSON array, no preamble, no markdown fences, no commentary.

Each element is one slide. Use ONLY these 4 slide types for this version: "title", "exec_summary", "matrix_2x2", "rag_status".

RULES THAT DEFINE CONSULTING QUALITY (non-negotiable):
1. Every slide except "title" needs "answerFirstTitle": a COMPLETE SENTENCE stating the conclusion. WRONG: "Cost Breakdown". RIGHT: "Technology spend represents 20% of total project cost and de-risks 3 of the top 5 execution risks."
2. Exactly ONE "title" slide, first in the array. Shape: {type,companyName,deckTitle,subtitle}.
3. Exactly ONE "exec_summary" slide, second in the array. Shape: {type,answerFirstTitle,keyPoints:[3-5 sentences],headlineMetric?:{label,value}}.
4. Use "matrix_2x2" when comparing options, prioritizing initiatives, or positioning along two dimensions. Shape: {type,answerFirstTitle,xAxisLabel,yAxisLabel,quadrants:{topLeft,topRight,bottomLeft,bottomRight}} where each quadrant is {label,items:[strings]}.
5. Use "rag_status" for progress, risk, or multi-item status tracking. Shape: {type,answerFirstTitle,rows:[{item,status:"red"|"amber"|"green",note,owner?}]}.
6. Never invent data. If a number is not in the provided corpus, write "data not yet available" rather than fabricating.
7. Total slides: 5-9 for this version.

DECK TITLE: "${deckTitle}"
DECK TYPE: "${tLabel}"

Return the JSON array now.`;
}

function parseStructuredDeck(raw){
  try{
    let cleaned=raw.trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
    const arr=JSON.parse(cleaned);
    if(!Array.isArray(arr)||arr.length===0)return null;
    const validTypes=["title","exec_summary","matrix_2x2","rag_status"];
    const allValid=arr.every(s=>s&&validTypes.includes(s.type));
    if(!allValid)return null;
    return arr;
  }catch{
    return null;
  }
}

// Gather all workspace knowledge into a single corpus
// ─── PHASE 2: CAPABILITY & COST BRIEF ───────────────────────────────────────
async function fetchExchangeRate(toCode){
  if(toCode==="USD")return 1;
  try{
    const r=await fetch("https://open.er-api.com/v6/latest/USD");
const d=await r.json();
return d.rates?.[toCode]||null;
  }catch{return null;}
}
function computeServiceFee(costUsd){
  if(!costUsd||costUsd<=0)return{cost:0,fee:0,total:0};
  const pct=costUsd<100?0.2:0.1;
  const fee=Math.max(costUsd*pct,0.5);
  return{cost:costUsd,fee,total:costUsd+fee};
}
function parseCapabilityBrief(raw){
  const marker="===CAPABILITY_BRIEF===";
  const idx=raw.indexOf(marker);
  if(idx===-1)return{output:raw,capability:null};
  const output=raw.slice(0,idx).trim();
  let capability=null;
  try{
    const jsonStr=raw.slice(idx+marker.length).trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
    capability=JSON.parse(jsonStr);
  }catch{}
  return{output,capability};
}
// Twelve export paths hardcoded audience:"board". A cash-flow model for the CFO,
// an investor pack and an operations runbook were all generated as board papers.
// The deliverable already says what it is - in its name and description - so read
// it rather than assuming. Falls back to "board" when nothing indicates
// otherwise, so behaviour is never worse than it was.
function _delAudience(del:any):any{
  const t=((del?.name||"")+" "+(del?.description||"")+" "+(del?.outputFor||"")).toLowerCase();
  if(/investor|vc |funding|fundrais|pitch|term sheet|series [a-d]|due dilig/.test(t))return "investor";
  if(/lender|bank|loan|credit|debt|working capital limit/.test(t))return "lender";
  if(/regulat|compliance|audit|statutory|gst|filing|tax return/.test(t))return "regulator";
  if(/client|customer|proposal|sow|engagement/.test(t))return "client";
  if(/sop|runbook|process|workflow|operation|checklist|implementation plan/.test(t))return "operations";
  if(/budget|forecast|cash flow|p&l|balance sheet|variance|financial model|unit econom/.test(t))return "cfo";
  if(/team|internal|handover|onboarding|training/.test(t))return "internal_team";
  return "board";
}
function gatherWorkspace(co,compData,chats,brSessions,workflows,tQueue,extras){
  const parts=[];
  parts.push("COMPANY: "+co.name+" | "+co.industry+" | "+co.stage+" | "+co.location+" | "+co.currency);
  if(Object.keys(compData).length)parts.push("\n=== COMPANY DATA ===\n"+Object.entries(compData).map(([k,v])=>k+": "+v).join("\n"));
  // Exports previously could not cite the General Ledger — the books were absent
  // from every generated document. extras.ledger is optional; when absent nothing changes.
  if(extras?.ledger&&extras.ledger.length){
    try{
      const snap=buildLedgerSnapshot(extras.ledger,extras.cur);
      if(snap)parts.push("\n=== GENERAL LEDGER (actual posted entries) ===\n"+snap);
    }catch{}
  }
  // BOARDROOM - REWRITTEN. The old line read s.synthesis, which DOES NOT EXIST on
  // a saved session. runBR stores it at s.stages[].synthesis. So every export
  // received the literal text "Synthesis: " followed by nothing: the Chairman's
  // arbitration, the executive debate, the research brief and the decision
  // status were all sitting in memory and all silently dropped. This reads the
  // correct path and keeps the debate, which is where the disagreement lives.
  if(extras?.boardroom&&brSessions.length){
    parts.push("\n=== BOARDROOM SESSIONS ===");
    brSessions.slice(0,8).forEach((s:any)=>{
      const stages:any[]=Array.isArray(s?.stages)?s.stages:[];
      const synth=stages.map(st=>st?.synthesis||"").filter(Boolean).join("\n\n")||s?.synthesis||"";
      const debate=stages.flatMap((st:any)=>Array.isArray(st?.debate)?st.debate:[])
        .map((d:any)=>{const who=d?.role||d?.name||d?.agent||d?.executive||"Executive";
          const tx=d?.text||d?.content||d?.output||d?.response||"";
          return tx?who+": "+stripMd(String(tx)):"";}).filter(Boolean).join("\n\n");
      const dec=stages.map(st=>st?.decisionStatus).filter(Boolean).join(", ");
      parts.push(["QUESTION: "+(s?.q||""),
        s?.researchBrief?"RESEARCH BRIEF:\n"+stripMd(String(s.researchBrief)):"",
        debate?"EXECUTIVE DEBATE:\n"+debate:"",
        synth?"CHAIRMAN SYNTHESIS:\n"+stripMd(synth):"",
        dec?"DECISION: "+dec:""].filter(Boolean).join("\n\n"));
    });
  }
  if(extras?.chats){
    const ids=Object.keys(chats).filter(k=>chats[k]?.length);
    // EXECUTIVE CHAT - REWRITTEN. The old line kept ONLY the last assistant reply,
    // capped at 1200 characters, and discarded every question you asked. A ten
    // message conversation with the CFO reached the document as one truncated
    // fragment - about 3% of what was said. Both sides of the full thread now
    // travel, because an answer without its question is not evidence.
    if(ids.length){parts.push("\n=== EXECUTIVE CONVERSATIONS ===");
      ids.slice(0,12).forEach(id=>{
        const r=AR.find(x=>x.id===id); if(!r)return;
        const thread=(chats[id]||[]).map((m:any)=>{
          const speaker=m?.role==="user"?"USER ASKED":r.t+" RESPONDED";
          const body=stripMd(String(m?.content||""));
          return body?speaker+":\n"+body:"";}).filter(Boolean).join("\n\n");
        if(thread)parts.push("--- "+r.t+" ("+(chats[id]||[]).length+" messages) ---\n"+thread);
      });}
  }
  if(extras?.workflows&&workflows.length){
    parts.push("\n=== WORKFLOW OUTPUTS ===");
    workflows.slice(0,6).forEach((w:any)=>{
      const body=(w.steps||[]).map((s:any)=>"LEVEL "+(s?.level??"?")+" — "+(s?.role?.t||"Step")+":\n"+stripMd(String(s?.output||""))).filter(Boolean).join("\n\n");
      if(body)parts.push(w.chainLabel+" — "+w.task+"\n"+body);});
  }
  if(extras?.tasks&&tQueue.length){
    const done=tQueue.filter(t=>t.finalOutput);
    if(done.length){parts.push("\n=== AUTOPILOT TASKS ===");
      done.slice(0,6).forEach((t:any)=>{
        const steps=(t.steps||[]).map((s:any)=>"LEVEL "+(s?.level??"?")+" — "+(s?.role?.t||"Step")+":\n"+stripMd(String(s?.output||""))).filter(Boolean).join("\n\n");
        parts.push(t.chainLabel+" — "+t.task+"\n"+[steps,"FINAL OUTPUT:\n"+stripMd(String(t.finalOutput))].filter(Boolean).join("\n\n"));});}
  }
  if(extras?.timeMachine)parts.push("\n=== TIME MACHINE ===\n"+stripMd(extras.timeMachine));
  if(extras?.autopilot)parts.push("\n=== DECISION AUTOPILOT ===\n"+stripMd(extras.autopilot));
  return parts.join("\n");
}
// ─── CHART DETECTION ────────────────────────────────────────────────────────
// Detects if a markdown table is chartable: first column = labels, remaining columns = numbers
// Detects the "unit" implied by a header label or a cell's formatting, so we
// never chart incompatible units (months vs % vs currency) on one axis.
function detectUnit(headerLabel,sampleCells){
  const h=(headerLabel||"").toLowerCase();
  const sample=sampleCells.join(" ");
  if(h.includes("%")||sample.includes("%"))return "percent";
  if(h.includes("₹")||h.includes("rs.")||h.includes("rs ")||h.includes("usd")||h.includes("$")||sample.includes("₹")||sample.includes("rs.")||sample.includes("$"))return "currency";
  if(h.includes("month"))return "months";
  if(h.includes("day"))return "days";
  if(h.includes("year")||h.includes("yr"))return "years";
  if(h.includes("week"))return "weeks";
  if(h.includes("score")||h.includes("rating"))return "score";
  return "count"; // generic/unitless number — only compatible with other "count" columns
}

function tableToChartData(rows){
  if(rows.length<2)return null;
  const header=rows[0].split("|").filter((c,i,a)=>i>0&&i<a.length-1).map(c=>c.trim());
  const dataRows=rows.slice(1).filter(r=>!r.trim().match(/^\|[\s|:-]+\|$/));
  if(dataRows.length<2||header.length<2)return null;
  const labels=[];const series=header.slice(1).map(()=>[]);
  const rawCellsPerCol=header.slice(1).map(()=>[]);
  let numericCount=0;
  for(const r of dataRows){
    const cells=r.split("|").filter((c,i,a)=>i>0&&i<a.length-1).map(c=>c.trim());
    if(cells.length<2)continue;
    labels.push(cells[0]);
    for(let c=1;c<header.length;c++){
      const cellRaw=cells[c]||"";
      const raw=cellRaw.replace(/[^\d.\-]/g,"");
      const n=parseFloat(raw);
      series[c-1].push(isNaN(n)?0:n);
      rawCellsPerCol[c-1].push(cellRaw);
      if(!isNaN(n)&&raw!=="")numericCount++;
    }
  }
  if(numericCount<2)return null; // not enough numeric data to chart

  // UNIT-SAFETY GATE: detect each numeric column's unit. If two or more columns
  // have DIFFERENT units (e.g. one is %, another is months, another is currency),
  // charting them together would be misleading — bail out and let the caller fall
  // back to a plain table instead of plotting incompatible quantities on one axis.
  const colUnits=header.slice(1).map((name,i)=>detectUnit(name,rawCellsPerCol[i]));
  const uniqueUnits=[...new Set(colUnits)];
  if(uniqueUnits.length>1)return null;

  // Additional sanity gate: if it's "count" (no detectable unit at all) and there
  // are 3+ series, this is more likely a mixed qualitative table than real chart
  // data — be conservative and skip rather than risk a meaningless chart.
  if(uniqueUnits[0]==="count"&&header.length-1>=3)return null;

  return{labels,series:header.slice(1).map((name,i)=>({name,values:series[i]}))};
}

// ─── CHART RENDER (canvas, for PDF) ─────────────────────────────────────────
async function renderChartToImage(chartData,type,width,height){
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.0/chart.umd.min.js");
  const canvas=document.createElement("canvas");
  canvas.width=width;canvas.height=height;
  const ctx=canvas.getContext("2d");
  const palette=["#14B8A6","#3B82F6","#A855F7","#F97316","#EF4444","#10B981"];
  const datasets=chartData.series.map((s,i)=>({label:s.name,data:s.values,backgroundColor:type==="line"?"transparent":palette[i%palette.length],borderColor:palette[i%palette.length],borderWidth:2,fill:false}));
  // @ts-ignore
  const chart=new window.Chart(ctx,{type:type==="pie"?"pie":type,data:{labels:chartData.labels,datasets},options:{responsive:false,animation:false,plugins:{legend:{labels:{color:"#333",font:{size:14}}}},scales:type==="pie"?{}:{x:{ticks:{color:"#333"}},y:{ticks:{color:"#333"}}}}});
  await new Promise(r=>setTimeout(r,200));
  const dataUrl=canvas.toDataURL("image/png");
  chart.destroy();
  return dataUrl;
}
// ─── PDF GENERATOR ──────────────────────────────────────────────────────────
async function generatePDF(type,title,bodyText,co,cur){
  const jsPDF=await ensureJsPDF();
  const doc=new jsPDF({unit:"pt",format:"a4"});
  const W=doc.internal.pageSize.getWidth(),H=doc.internal.pageSize.getHeight();
  const M=48;let y=M;
  const A={summary:[20,184,166],detailed:[59,130,246],executive:[100,116,139],investor:[168,85,247]}[type]||[20,184,166];
  const typeLabel=PDF_TYPES.find(t=>t.id===type)?.label||"Report";
  const addPageIfNeeded=(need)=>{if(y+need>H-M){doc.addPage();y=M;}};
  // Cover band
  doc.setFillColor(...A);doc.rect(0,0,W,90,"F");
  doc.setTextColor(255,255,255);doc.setFont("helvetica","bold");doc.setFontSize(22);
  doc.text(co.name||"Company",M,46,{maxWidth:W-2*M});
  doc.setFontSize(11);doc.setFont("helvetica","normal");
  doc.text(typeLabel+"  ·  "+(co.industry||"")+"  ·  "+(co.location||""),M,68);
  y=120;
  doc.setTextColor(30,30,30);doc.setFont("helvetica","bold");doc.setFontSize(16);
  doc.text(title,M,y,{maxWidth:W-2*M});y+=10;
  const titleLines=doc.splitTextToSize(title,W-2*M);y+=titleLines.length*8;
  doc.setFontSize(8);doc.setTextColor(120,120,120);doc.setFont("helvetica","normal");
  doc.text("Generated "+new Date().toLocaleString()+" - Currency: "+cur.code,M,y);y+=20;
  doc.setDrawColor(...A);doc.setLineWidth(1.5);doc.line(M,y,W-M,y);y+=20;
  // Body
// Body
  const secs=parseSections(bodyText);
  for(const sec of secs){
    addPageIfNeeded(40);
    doc.setFont("helvetica","bold");doc.setFontSize(13);doc.setTextColor(...A);
    const st=doc.splitTextToSize(sec.title.replace(/₹/g,"Rs."),W-2*M);
    st.forEach(line=>{addPageIfNeeded(18);doc.text(line,M,y);y+=18;});
    y+=2;

    const tableLines=sec.lines.filter(l=>l.includes("|")&&l.trim().startsWith("|")&&!l.trim().match(/^\|[\s|:-]+\|$/));
    const chartData=tableLines.length>=2?tableToChartData(tableLines):null;

    if(chartData){
      try{
        const chartType=chartData.series.length===1?"bar":"bar";
        const imgData=await renderChartToImage(chartData,chartType,800,450);
        const imgW=W-2*M;const imgH=imgW*(450/800);
        addPageIfNeeded(imgH+10);
        doc.addImage(imgData,"PNG",M,y,imgW,imgH);
        y+=imgH+12;
      }catch{
        // fallback to text table if chart rendering fails
      }
    }

    doc.setFont("helvetica","normal");doc.setFontSize(10);doc.setTextColor(45,45,45);
    const nonChartLines=chartData?sec.lines.filter(l=>!tableLines.includes(l)):sec.lines;
    nonChartLines.forEach(raw=>{
      const isBullet=/^[-*]\s/.test(raw)||/^•/.test(raw);
      const isTable=raw.includes("|")&&raw.trim().startsWith("|");
      let text=stripMd(raw);
      if(isTable){text=raw.split("|").filter(c=>c.trim()&&!c.trim().match(/^[-:]+$/)).map(c=>c.trim()).join("   |   ");if(!text)return;}
      text=text.replace(/₹/g,"Rs.").replace(/·/g,"-");
      const indent=isBullet?M+14:M;
      const lines=doc.splitTextToSize(text,W-indent-M);
      lines.forEach(line=>{addPageIfNeeded(14);doc.text(line,indent,y);y+=13;});
      y+=2;
    });
    y+=8;
  }
  // Footer page numbers
  const pages=doc.internal.getNumberOfPages();
  for(let i=1;i<=pages;i++){doc.setPage(i);doc.setFontSize(7);doc.setTextColor(150,150,150);doc.text((co.name||"")+" - Confidential - Page "+i+" of "+pages,M,H-20);}
  doc.save((co.name||"Report").replace(/\s+/g,"-")+"-"+typeLabel.replace(/\s+/g,"-")+"-"+Date.now()+".pdf");
}

// ─── POWERPOINT GENERATOR ───────────────────────────────────────────────────
async function generatePPTX(type,title,bodyText,co,cur){
  const PptxGenJS=await ensurePptx();
  const pptx=new PptxGenJS();
  pptx.defineLayout({name:"WIDE",width:13.333,height:7.5});pptx.layout="WIDE";
  const PAL={briefing:"14B8A6",strategy:"6366F1",investor:"A855F7",pitch:"F97316",roadmap:"8B5CF6",research:"06B6D4",operational:"F59E0B"};
  const A=PAL[type]||"14B8A6";
  const DARK="0A0E1A",LIGHT="F1F5F9",MUT="A0AAC0";
  const typeLabel=PPT_TYPES.find(t=>t.id===type)?.label||"Presentation";
  // Title slide
  const s0=pptx.addSlide();s0.background={color:DARK};
  s0.addShape(pptx.ShapeType.rect,{x:0,y:3.1,w:0.35,h:1.3,fill:{color:A}});
  s0.addText(co.name||"Company",{x:0.7,y:2.9,w:12,h:1,fontSize:40,bold:true,color:LIGHT,fontFace:"Arial"});
  s0.addText(title,{x:0.7,y:3.9,w:12,h:0.8,fontSize:22,color:A,fontFace:"Arial"});
  s0.addText(typeLabel+"   ·   "+(co.industry||"")+"   ·   "+(co.location||"")+"   ·   "+cur.code,{x:0.7,y:4.7,w:12,h:0.5,fontSize:13,color:MUT,fontFace:"Arial"});
  s0.addText("Generated "+new Date().toLocaleDateString()+"  ·  Confidential",{x:0.7,y:6.7,w:12,h:0.4,fontSize:10,color:"5A6480",fontFace:"Arial"});
  // Content slides from sections
const secs=parseSections(bodyText);
  secs.forEach((sec,idx)=>{
    const s=pptx.addSlide();s.background={color:DARK};
    s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:13.333,h:0.95,fill:{color:"131825"}});
    s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:0.25,h:0.95,fill:{color:A}});
    s.addText(sec.title,{x:0.55,y:0.12,w:11.5,h:0.7,fontSize:24,bold:true,color:LIGHT,fontFace:"Arial",valign:"middle"});
    s.addText(String(idx+1).padStart(2,"0"),{x:12.3,y:0.12,w:0.9,h:0.7,fontSize:18,color:A,align:"right",valign:"middle"});
    // Detect table rows
    const tableRows=sec.lines.filter(l=>l.includes("|")&&l.trim().startsWith("|")&&!l.trim().match(/^\|[\s|:-]+\|$/));
    const chartData=tableRows.length>=2?tableToChartData(tableRows):null;
    if(chartData){
      const chartType=chartData.series.length===1?"pie":"bar";
      const palette=["14B8A6","3B82F6","A855F7","F97316","EF4444","10B981"];
      if(chartType==="pie"){
        const chartRows=[{name:chartData.series[0].name,labels:chartData.labels,values:chartData.series[0].values}];
        s.addChart(pptx.ChartType.pie,chartRows,{x:1.5,y:1.3,w:7,h:5.2,showLegend:true,legendPos:"r",legendColor:MUT,dataLabelColor:DARK,dataLabelFontSize:11,chartColors:palette});
      }else{
        const chartRows=chartData.series.map((sr,i)=>({name:sr.name,labels:chartData.labels,values:sr.values}));
        s.addChart(pptx.ChartType.bar,chartRows,{x:0.55,y:1.3,w:12.2,h:5.2,barDir:"col",showLegend:chartData.series.length>1,legendPos:"b",legendColor:MUT,catAxisLabelColor:MUT,valAxisLabelColor:MUT,chartColors:palette});
      }
    }else if(tableRows.length>=2){
      const rows=tableRows.map(r=>r.split("|").filter((c,i,a)=>i>0&&i<a.length-1).map(c=>c.trim()));
      const tblData=rows.map((r,ri)=>r.map(c=>({text:c,options:{fill:{color:ri===0?A:"131825"},color:ri===0?DARK:MUT,bold:ri===0,fontSize:11,fontFace:"Arial"}})));
      s.addTable(tblData,{x:0.55,y:1.3,w:12.2,border:{type:"solid",color:"1a2030",pt:1},autoPage:false});
    }else{
      const bullets=sec.lines.filter(l=>!l.includes("|")).map(l=>stripMd(l)).filter(Boolean).slice(0,9);
      s.addText(bullets.map(b=>({text:b,options:{bullet:{code:"2022"},indentLevel:0}})).length?bullets.map(b=>({text:b.replace(/^•\s*/,""),options:{bullet:{code:"2022"},color:MUT,fontSize:15,fontFace:"Arial",paraSpaceAfter:8}})):[{text:"—",options:{color:MUT}}],{x:0.7,y:1.4,w:12,h:5.6,valign:"top"});
    }
    s.addText((co.name||"")+"  ·  Confidential",{x:0.55,y:7.0,w:12,h:0.3,fontSize:9,color:"3A4060",fontFace:"Arial"});
  });
  await pptx.writeFile({fileName:(co.name||"Deck").replace(/\s+/g,"-")+"-"+typeLabel.replace(/\s+/g,"-")+"-"+Date.now()+".pptx"});
}

// ─── QUALITY ENGINE: archetype renderers (pptxgenjs) ────────────────────────
const QE_PAL={bg:"0A0E1A",panel:"131825",border:"1A2030",text:"F1F5F9",textMuted:"A0AAC0",textDim:"5A6480",accent:"14B8A6",red:"EF4444",amber:"F59E0B",green:"10B981"};

// ─── PROJECT ENGINE — Template Library ─────────────────────────────────────
const PROJECT_TEMPLATES={
  "Business Plan":{modules:["Executive Summary","Market Analysis","Product Strategy","Financial Model","Go-to-Market","Risk Assessment"],formats:{default:"docx",financial:"xlsx",presentation:"pptx"}},
  "Pitch Deck":{modules:["Problem","Solution","Market Size","Product Demo","Business Model","Traction","Team","Financials","Ask"],formats:{default:"pptx",financial:"xlsx"}},
  "Financial Model":{modules:["Assumptions","Revenue Forecast","Cost Structure","P&L","Cash Flow","Balance Sheet","KPI Dashboard"],formats:{default:"xlsx",presentation:"pptx"}},
  "Marketing Plan":{modules:["Market Analysis","ICP & Segmentation","Campaign Strategy","Content Calendar","Budget Allocation","KPIs & Metrics"],formats:{default:"docx",calendar:"xlsx",deck:"pptx"}},
  "SOP":{modules:["Purpose & Scope","Roles & Responsibilities","Process Steps","Quality Checks","Exception Handling","Review Schedule"],formats:{default:"docx"}},
  "Consulting Report":{modules:["Executive Summary","Current State","Gap Analysis","Recommendations","Implementation Roadmap","Financial Impact"],formats:{default:"docx",model:"xlsx",deck:"pptx"}},
  "Business Proposal":{modules:["Cover","Executive Summary","Problem Statement","Proposed Solution","Timeline","Pricing","Terms"],formats:{default:"docx",pricing:"xlsx",deck:"pptx"}},
  "Launch Plan":{modules:["Launch Overview","Target Audience","Marketing Strategy","Content Plan","Channel Mix","Budget","Success Metrics"],formats:{default:"docx",budget:"xlsx",campaign:"pptx"}},
};
function detectTemplate(objective){
  const o=(objective||"").toLowerCase();
  if(o.includes("pitch")||o.includes("investor")||o.includes("deck"))return "Pitch Deck";
  if(o.includes("financial model")||o.includes("forecast")||o.includes("p&l"))return "Financial Model";
  if(o.includes("marketing plan")||o.includes("campaign plan"))return "Marketing Plan";
  if(o.includes("sop")||o.includes("standard operating"))return "SOP";
  if(o.includes("consulting")||o.includes("assessment")||o.includes("audit"))return "Consulting Report";
  if(o.includes("proposal")||o.includes("rfp")||o.includes("bid"))return "Business Proposal";
  if(o.includes("launch")||o.includes("go-to-market")||o.includes("gtm"))return "Launch Plan";
  if(o.includes("business plan")||o.includes("business case"))return "Business Plan";
  return null;
}


// Turns an extractPptxStyle() result into a render-ready palette. Returns null if no
// style was captured (callers then use QE_PAL exactly as before — no behavior change).
// Keeps our proven dark theme if the sample is dark; builds a readable light palette
// from the sample's own colors if it's light, so text never goes invisible.
function buildPalFromExtractedStyle(styleResult){
  if(!styleResult?.ok)return null;
  const colors=styleResult.colors||{};
  const clean=hex=>hex?hex.replace("#",""):null;
  const accent=clean(colors.accent1)||QE_PAL.accent;
  const bgHex=colors.lt1||colors.dk2||null;
  const lum=(()=>{const h=clean(bgHex);if(!h||h.length!==6)return null;
    const r=parseInt(h.slice(0,2),16),g=parseInt(h.slice(2,4),16),b=parseInt(h.slice(4,6),16);
    return (0.299*r+0.587*g+0.114*b)/255;})();
  const isLight=lum!==null&&lum>0.6;
  if(!isLight)return{...QE_PAL,accent};
  return{bg:clean(colors.lt1)||"FFFFFF",panel:clean(colors.lt2)||"F4F6FB",border:"E2E8F0",
    text:clean(colors.dk1)||"0F172A",textMuted:clean(colors.dk2)||"475569",textDim:"94A3B8",
    accent,red:QE_PAL.red,amber:QE_PAL.amber,green:QE_PAL.green};
}

function renderTitleSlideQE(pptx,slide,A,pal=QE_PAL){
  const s=pptx.addSlide();s.background={color:pal.bg};
  s.addShape(pptx.ShapeType.rect,{x:0,y:3.1,w:0.35,h:1.3,fill:{color:A}});
  s.addText(slide.companyName||"Company",{x:0.7,y:2.9,w:12,h:1,fontSize:40,bold:true,color:pal.text,fontFace:"Arial"});
  s.addText(slide.deckTitle||"",{x:0.7,y:3.9,w:12,h:0.8,fontSize:22,color:A,fontFace:"Arial"});
  s.addText(slide.subtitle||"",{x:0.7,y:4.7,w:12,h:0.5,fontSize:13,color:pal.textMuted,fontFace:"Arial"});
  s.addText("Generated "+new Date().toLocaleDateString()+"  ·  Confidential",{x:0.7,y:6.7,w:12,h:0.4,fontSize:10,color:pal.textDim,fontFace:"Arial"});
}

function renderExecSummarySlideQE(pptx,slide,A,idx,pal=QE_PAL){
  const s=pptx.addSlide();s.background={color:pal.bg};
  s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:13.333,h:1.6,fill:{color:pal.panel}});
  s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:0.25,h:1.6,fill:{color:A}});
  s.addText(slide.answerFirstTitle||"",{x:0.55,y:0.18,w:12.2,h:1.3,fontSize:22,bold:true,color:pal.text,fontFace:"Arial",valign:"middle"});
  let y=2.0;
  if(slide.headlineMetric){
    s.addText(slide.headlineMetric.value,{x:0.55,y,w:4,h:1.2,fontSize:44,bold:true,color:A,fontFace:"Arial"});
    s.addText(slide.headlineMetric.label,{x:0.55,y:y+1.2,w:4,h:0.5,fontSize:12,color:pal.textMuted,fontFace:"Arial"});
    const points=(slide.keyPoints||[]).map(p=>({text:p,options:{bullet:{code:"2022"},color:pal.textMuted,fontSize:15,fontFace:"Arial",paraSpaceAfter:10}}));
    s.addText(points,{x:5.0,y,w:7.7,h:4.5,valign:"top"});
  }else{
    const points=(slide.keyPoints||[]).map(p=>({text:p,options:{bullet:{code:"2022"},color:pal.textMuted,fontSize:16,fontFace:"Arial",paraSpaceAfter:12}}));
    s.addText(points,{x:0.7,y,w:12,h:4.5,valign:"top"});
  }
  s.addText(String(idx+1).padStart(2,"0"),{x:12.3,y:0.5,w:0.9,h:0.6,fontSize:14,color:A,align:"right"});
}

function renderMatrix2x2SlideQE(pptx,slide,A,idx,pal=QE_PAL){
  const s=pptx.addSlide();s.background={color:pal.bg};
  s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:13.333,h:0.95,fill:{color:pal.panel}});
  s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:0.25,h:0.95,fill:{color:A}});
  s.addText(slide.answerFirstTitle||"",{x:0.55,y:0.1,w:11.5,h:0.75,fontSize:18,bold:true,color:pal.text,fontFace:"Arial",valign:"middle"});
  s.addText(String(idx+1).padStart(2,"0"),{x:12.3,y:0.1,w:0.9,h:0.75,fontSize:14,color:A,align:"right",valign:"middle"});
  const gx=1.6,gy=1.5,gw=10.1,gh=5.3;
  const midX=gx+gw/2,midY=gy+gh/2;
  const quadColors=["3B82F622","10B98122","F59E0B22","EF444422"];
  s.addText(slide.xAxisLabel||"",{x:gx,y:gy+gh+0.1,w:gw,h:0.4,fontSize:11,color:pal.textDim,align:"center",fontFace:"Arial"});
  s.addText(slide.yAxisLabel||"",{x:0.2,y:gy,w:0.4,h:gh,fontSize:11,color:pal.textDim,align:"center",valign:"middle",rotate:270,fontFace:"Arial"});
  s.addShape(pptx.ShapeType.rect,{x:gx,y:gy,w:gw/2,h:gh/2,fill:{color:quadColors[0]},line:{color:pal.border,width:1}});
  s.addShape(pptx.ShapeType.rect,{x:midX,y:gy,w:gw/2,h:gh/2,fill:{color:quadColors[1]},line:{color:pal.border,width:1}});
  s.addShape(pptx.ShapeType.rect,{x:gx,y:midY,w:gw/2,h:gh/2,fill:{color:quadColors[2]},line:{color:pal.border,width:1}});
  s.addShape(pptx.ShapeType.rect,{x:midX,y:midY,w:gw/2,h:gh/2,fill:{color:quadColors[3]},line:{color:pal.border,width:1}});
  const q=slide.quadrants||{};
  const renderQuad=(data,x,y)=>{
    if(!data)return;
    s.addText(data.label||"",{x:x+0.15,y:y+0.1,w:gw/2-0.3,h:0.4,fontSize:12,bold:true,color:pal.text,fontFace:"Arial"});
    const items=(data.items||[]).slice(0,4).map(it=>({text:it,options:{bullet:{code:"2022"},fontSize:10.5,color:pal.textMuted,paraSpaceAfter:4}}));
    s.addText(items,{x:x+0.15,y:y+0.55,w:gw/2-0.3,h:gh/2-0.65,valign:"top",fontFace:"Arial"});
  };
  renderQuad(q.topLeft,gx,gy);
  renderQuad(q.topRight,midX,gy);
  renderQuad(q.bottomLeft,gx,midY);
  renderQuad(q.bottomRight,midX,midY);
}

function renderRagStatusSlideQE(pptx,slide,A,idx,pal=QE_PAL){
  const s=pptx.addSlide();s.background={color:pal.bg};
  s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:13.333,h:0.95,fill:{color:pal.panel}});
  s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:0.25,h:0.95,fill:{color:A}});
  s.addText(slide.answerFirstTitle||"",{x:0.55,y:0.1,w:11.5,h:0.75,fontSize:18,bold:true,color:pal.text,fontFace:"Arial",valign:"middle"});
  s.addText(String(idx+1).padStart(2,"0"),{x:12.3,y:0.1,w:0.9,h:0.75,fontSize:14,color:A,align:"right",valign:"middle"});
  const statusColor=st=>st==="green"?pal.green:st==="amber"?pal.amber:pal.red;
  const rows=slide.rows||[];
  const hasOwner=rows.some(r=>r.owner);
  const header=hasOwner
    ?[{text:"Item",options:{fill:{color:A},color:pal.bg,bold:true,fontSize:11}},{text:"Status",options:{fill:{color:A},color:pal.bg,bold:true,fontSize:11,align:"center"}},{text:"Owner",options:{fill:{color:A},color:pal.bg,bold:true,fontSize:11}},{text:"Note",options:{fill:{color:A},color:pal.bg,bold:true,fontSize:11}}]
    :[{text:"Item",options:{fill:{color:A},color:pal.bg,bold:true,fontSize:11}},{text:"Status",options:{fill:{color:A},color:pal.bg,bold:true,fontSize:11,align:"center"}},{text:"Note",options:{fill:{color:A},color:pal.bg,bold:true,fontSize:11}}];
  const body=rows.map(r=>{
    const cells=[
      {text:r.item||"",options:{fill:{color:pal.panel},color:pal.textMuted,fontSize:10.5}},
      {text:(r.status||"amber").toUpperCase(),options:{fill:{color:pal.panel},color:statusColor(r.status),bold:true,fontSize:10,align:"center"}},
    ];
    if(hasOwner)cells.push({text:r.owner||"",options:{fill:{color:pal.panel},color:pal.textMuted,fontSize:10.5}});
    cells.push({text:r.note||"",options:{fill:{color:pal.panel},color:pal.textMuted,fontSize:10.5}});
    return cells;
  });
  s.addTable([header,...body],{x:0.55,y:1.3,w:12.2,border:{type:"solid",color:pal.border,pt:1},autoPage:false,colW:hasOwner?[4.5,1.8,1.8,4.1]:[4.5,1.8,5.9]});
}

function renderStructuredDeck(pptx,slides,A,pal=QE_PAL){
  slides.forEach((slide,idx)=>{
    switch(slide.type){
      case "title":renderTitleSlideQE(pptx,slide,A,pal);break;
      case "exec_summary":renderExecSummarySlideQE(pptx,slide,A,idx,pal);break;
      case "matrix_2x2":renderMatrix2x2SlideQE(pptx,slide,A,idx,pal);break;
      case "rag_status":renderRagStatusSlideQE(pptx,slide,A,idx,pal);break;
    }
  });
}

// ─── ENGINE DOWNGRADE TRACKER ─────────────────────────────────────────────────
// Records every time the publication engine fails and a basic fallback produces
// the file instead. Without this, a downgraded deliverable looks identical to a
// finished one — which is exactly how quality problems went undetected.
// Surfaced in QA-Report.md and as an on-screen warning so a silent downgrade
// becomes impossible.
type EngineDowngrade={deliverable:string;format:string;reason:string;ts:string};
const engineDowngrades:{list:EngineDowngrade[]}={list:[]};
// Deterministic content-check warnings (zero-heavy data, under-count slides,
// leftover placeholders) — set by BEE after generation when a deliverable
// shipped despite failing its fact-based check even after one retry.
type QualityWarning={deliverable:string;warning:string};
const qualityWarnings:{list:QualityWarning[]}={list:[]};
function recordEngineDowngrade(deliverable:string,format:string,err:unknown){
  const reason=String((err as any)?.message||err||"unknown error").slice(0,180);
  try{
    engineDowngrades.list.push({deliverable,format,reason,ts:new Date().toISOString()});
    console.error("[OIQ] Publication engine ("+format+") fell back for \""+deliverable+"\":",reason);
    const uf=WorkspaceMemory.get<any[]>("cos-unfulfilled-log")||[];
    uf.unshift({ts:new Date().toISOString(),project:"engine-downgrade",deliverable,format:format+"-engine",error:reason});
    WorkspaceMemory.set("cos-unfulfilled-log",uf.slice(0,50));
  }catch{}
}

// ─── STYLE CONSTANTS (module scope) ────────────────────────────────────────────
// Created once at module load instead of on every component render.
const S={
  lbl:{fontSize:11,fontWeight:800,color:"#8A8378",textTransform:"uppercase",letterSpacing:"0.09em",marginBottom:8,display:"block"},
  inp:{width:"100%",background:"#FFFFFF",border:"1px solid #DDD8CE",borderRadius:9,padding:"13px 15px",color:"#1C2A24",fontSize:13.5,fontFamily:"Inter,system-ui,sans-serif"},
  pBtn:{background:"#C9A961",color:"#1C2A24",border:"none",borderRadius:7,padding:"11px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",marginTop:8,width:"100%"},
  hBtn:{background:"#FFFFFF",border:"1px solid #DDD8CE",borderRadius:7,padding:"8px 14px",color:"#4A453D",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif"},
  iBtn:{background:"#FFFFFF",border:"1px solid #DDD8CE",borderRadius:7,padding:"8px 13px",color:"#4A453D",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif"},
  cancelBtn:{background:"#FAE6E2",border:"1px solid #E8C4BC",borderRadius:7,padding:"8px 14px",color:"#9E3B2B",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif"},
  actBtn:{background:"#FFFFFF",border:"1px solid #DDD8CE",borderRadius:7,padding:"11px 14px",color:"#4A453D",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",textAlign:"center",width:"100%"},
  errB:{background:"#FAE6E2",border:"1px solid #E8C4BC",borderRadius:8,padding:"11px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,fontSize:12.5,color:"#9E3B2B",gap:10},
  retBtn:{background:"#B54A3A",color:"#fff",border:"none",borderRadius:5,padding:"5px 11px",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",whiteSpace:"nowrap"},
  dot:{width:6,height:6,borderRadius:"50%",background:"#4C7A5E",animation:"pulse 1s ease-in-out infinite",display:"inline-block"},
  uMsg:{background:"#F2EFE9",borderRadius:10,padding:"15px 18px",border:"1px solid #E4E0D8",color:"#4A453D"},
  aMsg:{background:"#FFFFFF",borderRadius:10,padding:"15px 18px",border:"1px solid #E4E0D8",color:"#4A453D"},
  mLbl:{fontSize:12,fontWeight:700,color:"#1C2A24",marginBottom:8,textTransform:"none",letterSpacing:"0"},
  qaBtn:{background:"#FFFFFF",border:"1px solid #E4E0D8",borderRadius:9,padding:"13px 15px",color:"#4A453D",fontSize:13,cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",textAlign:"left",lineHeight:1.55},
  pill:{background:"#FFFFFF",border:"1px solid #DDD8CE",borderRadius:999,padding:"7px 13px",fontSize:12,fontWeight:600,color:"#6B6459",cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",flex:1,textAlign:"center"},
  dH:{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"none",border:"none",cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",borderRadius:7},
  rBtn:{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 12px 9px 22px",background:"none",border:"1px solid transparent",cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",borderRadius:7,textAlign:"left"},
  nTab:{display:"flex",flexDirection:"column",alignItems:"center",gap:2,background:"#1C2A24",border:"1px solid transparent",borderRadius:6,padding:"7px 4px",color:"#7E9689",cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",fontSize:10,fontWeight:600},
  nrvTab:{display:"flex",flexDirection:"column",alignItems:"center",gap:2,flex:1,background:"#1C2A24",border:"1px solid transparent",borderRadius:6,padding:"7px 4px",color:"#7E9689",cursor:"pointer",fontFamily:"Inter,system-ui,sans-serif",fontSize:10,fontWeight:600},
  inpA:{padding:"14px 20px 16px",borderTop:"1px solid #E4E0D8",background:"#FBFAF8"},
  inpR:{display:"flex",alignItems:"flex-end",gap:6,background:"#FFFFFF",border:"1px solid #DDD8CE",borderRadius:9,padding:"4px 4px 4px 14px"},
  ta:{flex:1,background:"none",border:"none",color:"#1C2A24",fontSize:13.5,fontFamily:"Inter,system-ui,sans-serif",resize:"none",padding:"9px 0",lineHeight:1.6},
  sBtn:{width:32,height:32,borderRadius:7,border:"none",color:"#1C2A24",fontSize:15,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0},
  modalBg:{position:"fixed",inset:0,background:"rgba(28,42,36,0.55)",backdropFilter:"blur(3px)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
  modal:{background:"#FBFAF8",border:"1px solid #DDD8CE",borderRadius:14,padding:"26px 28px",width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 16px 46px rgba(60,50,35,.18)"},
};

// ─── MAIN APP ────────────────────────────────────────────────────────────────

export default function App(){
  const [page,setPage]=useState("landing");
  // THE FIX: this legacy onboarding screen (pre-dating the current
  // NVIDIA free tier - it only ever knew about Gemini/Claude/OpenAI) was
  // hard-blocking anyone with no key from ever finishing signup, even
  // with every company detail correctly filled in. This lets someone
  // explicitly acknowledge they're continuing without their own key,
  // rather than removing the requirement silently - they should know
  // what that choice means before making it.
  const [skipApiKeyAck,setSkipApiKeyAck]=useState(false);
  const [sbOpen,setSbOpen]=useState(false);
  const [showModules,setShowModules]=useState(false);
  const [me,setMe]=useState<{email:string;role:string}>({email:"",role:""});
  const [showExecs,setShowExecs]=useState(false);
  const [keysHydrated,setKeysHydrated]=useState(false);
  const [offP,setOffP]=useState<Record<string,boolean>>(()=>{try{return JSON.parse(localStorage.getItem("cos-offp")||"{}");}catch{return {};}});
  const [stageProfile,setStageProfileState]=useState<any>(()=>{try{return {...DEFAULT_PROFILE,...(JSON.parse(localStorage.getItem("cos-stage-profile")||"null")||{})};}catch{return {...DEFAULT_PROFILE};}});
  const [libraryOn,setLibraryOn]=useState<boolean>(()=>{try{return localStorage.getItem("cos-library")==="1";}catch{return false;}});
  // Data-handling consent, recorded against the EXACT set of providers active when
  // it was given. Switch a new provider on and consent is asked again, because the
  // answer to "where does my data go" has changed.
  const [dataConsent,setDataConsent]=useState<string>(()=>{try{return localStorage.getItem("oiq-data-consent")||"";}catch{return "";}});
  const [showConsent,setShowConsent]=useState(false);
  const [showDisclosure,setShowDisclosure]=useState(false);
  const [sbCollapsed,setSbCollapsed]=useState(()=>{try{return WorkspaceMemory.get<string>("oiq-sb-col")==="1";}catch{return false;}});
  const [keys,setKeys]=useState({claude:"",openai:"",gemini:"",groq:"",deepseek:"",kimi:"",stability:"",fal:"",serper:"",tavily:"",brave:"",dataforseo:""});

  // Save API keys to the signed-in user's own profile row. This MUST sit below
  // the keys declaration — referencing it earlier throws "Cannot access before
  // initialization" and takes the whole app down. Debounced so typing a key
  // doesn't write per keystroke; skipped on first pass so the empty initial
  // state can never wipe keys already saved.
  useEffect(()=>{
    if(!keysHydrated){ setKeysHydrated(true); return; }
    const t=setTimeout(async()=>{
      try{
        const {data:{user}}=await supabase.auth.getUser();
        if(!user)return;
        const clean:any={};
        Object.entries(keys||{}).forEach(([k,v])=>{ if(typeof v==="string"&&v.trim())clean[k]=v.trim(); });
        await supabase.rpc("save_my_api_keys",{p_keys:clean,p_admin:false});
      }catch{}
    },1500);
    return ()=>clearTimeout(t);
  },[keys]);  
  const [mediaMode,setMediaMode]=useState({image:"prompts",video:"veo"});
  const [showMediaPicker,setShowMediaPicker]=useState(false);
  const [defP,setDefP]=useState("nvidia"); // free, zero-setup default
  // THE ATTACHMENT STAGING AREA — a picked or pasted image sits here,
  // shown as a thumbnail, until the user actually sends the message. Held
  // separately from `input` (the text) since they have completely
  // different lifecycles: text accumulates as you type, an attachment is
  // added all at once and can be removed with a single click.
  const [pendingAttachment,setPendingAttachment]=useState<{dataUrl:string;name:string}|null>(null);
  const MAX_ATTACHMENT_MB=8;
  // THE ACTUAL BUG, CONFIRMED FROM YOUR SCREENSHOT: showToast is declared
  // further down in this same component (as a const, via its own
  // useCallback). Listing it in the dependency array below made this run
  // IMMEDIATELY, at the moment this hook is defined - which is BEFORE
  // showToast's own declaration line has executed. That is a genuine
  // "temporal dead zone" violation: JavaScript throws "Cannot access
  // 'showToast' before initialization" the instant that array is evaluated,
  // which is why this broke the entire page, not just this one feature.
  // Removing it from the array is the correct fix, not a workaround:
  // showToast is itself a stable useCallback reference that never changes
  // identity, so this function never actually needed to be recreated when
  // it "changed" - it doesn't.
  const handleIncomingFile=useCallback((file:File)=>{
    if(!file.type.startsWith("image/")){
      showToast("Only images can be attached to chat right now — PDF, Word, and Excel reading is coming next.","warning");
      return;
    }
    if(file.size>MAX_ATTACHMENT_MB*1024*1024){
      showToast("That image is over "+MAX_ATTACHMENT_MB+"MB — try a smaller one.","error");
      return;
    }
    const rd=new FileReader();
    rd.onload=ev=>setPendingAttachment({dataUrl:ev.target.result as string,name:file.name});
    rd.readAsDataURL(file);
  },[]);
  const [multiAI,setMultiAI]=useState(false);
  const [respQuality,setRespQuality]=useState<"standard"|"professional"|"excellent">("professional");
  const [co,setCo]=useState({name:"",industry:"",stage:"idea",location:"",markets:"",currency:"INR"});
  const [selRole,setSelRole]=useState(null);
  const [chats,setChats]=useState({});
  const [input,setInput]=useState("");
  // THE FIRST REAL PIECE OF THE TEAMS-STYLE REQUEST: hovering a message
  // reveals Reply/Copy actions, exactly like Teams. Reply quotes the
  // message above the input box, with a clear way to cancel it - a real,
  // working reply flow, not the full multi-user threading Live Boardroom
  // has (that only makes sense where multiple people are actually
  // speaking; this is one person and one AI).
  const [hoveredMsgIdx,setHoveredMsgIdx]=useState<number|null>(null);
  const [replyingTo,setReplyingTo]=useState<{role:string;content:string}|null>(null);
  const sendWithReply=(text:string,attachment?:any)=>{
    const quoted=replyingTo?`> ${replyingTo.content.slice(0,200).replace(/\n/g," ")}\n\n${text}`:text;
    send(quoted,attachment);
    setReplyingTo(null);
    setPendingAttachment(null);
  };
  const [searchMode,setSearchMode]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState(null);
  const [expD,setExpD]=useState({});
  const [compData,setCompData]=useState({});
  const [ledgerEntries,setLedgerEntries]=useState<JournalEntry[]>([]);
  const [customAccounts,setCustomAccounts]=useState<any[]>([]);
  const [dispatchTemplates,setDispatchTemplates]=useState<DispatchTemplate[]>([]);
  const [actionItems,setActionItems]=useState<ActionItem[]>([]);
  const [extractModal,setExtractModal]=useState<{items:ExtractedItem[];sourceType:ActionItem["source"];sourceLabel:string}|null>(null);
  const [extracting,setExtracting]=useState<string|null>(null); // which source is currently extracting
  const [adminConfig,setAdminConfig]=useState<{[k:string]:boolean}>({ledgerEnabled:true,dispatchEnabled:true,actionsEnabled:true});
  const [dataF,setDataF]=useState({k:"",v:""});
  const [view,setView]=useState("home");

  // ═══════════════════════════════════════════════════════════════════════
  // STEP 1 — REAL URLS PER MODULE. Deliberately isolated: this reads and
  // writes the browser address bar by WATCHING the existing `view` state,
  // and does not change a single one of the ~100 places elsewhere in this
  // file that already call setView(...). If this block had a bug, the
  // worst case is the address bar simply not updating — every existing
  // setView() call, every menu click, every screen keeps working exactly
  // as it does today, completely unaffected.
  const VIEW_TO_PATH: Record<string,string> = {
    home:"/", nerve:"/nerve-center", workflow:"/workflow", agentic:"/agentic-ai",
    agents:"/ai-agents", p3:"/autopilot", chat:"/chat", data:"/data-hub",
    costarch:"/cost-architecture", ledger:"/ledger", finance:"/finance",
    dispatch:"/pulse", actions:"/tasks", studio:"/studio", funding:"/funding",
    tokens:"/tokens", workspace:"/ai-workspace", liveboard:"/live-boardroom",
    account:"/account", admin:"/admin", servicedesk:"/support",
    compliance:"/compliance", spendgov:"/spend-governance", sheets:"/sheets",
  };
  const PATH_TO_VIEW: Record<string,string> = Object.fromEntries(
    Object.entries(VIEW_TO_PATH).map(([v,p])=>[p,v])
  );
  const didRestoreFromUrl=useRef(false);

  // Runs once, only after the user is genuinely inside the app (page==="app")
  // - never while still on the login/onboarding screens, so a deep link can
  // never skip past authentication. Reads whatever path is already in the
  // address bar (e.g. someone opened /cost-architecture directly, or hit
  // refresh) and opens that module instead of always defaulting to home.
  useEffect(()=>{
    if(page!=="app"||didRestoreFromUrl.current)return;
    didRestoreFromUrl.current=true;
    const path=window.location.pathname;
    const matchedView=PATH_TO_VIEW[path];
    if(matchedView&&matchedView!==view){
      setView(matchedView);
    }else{
      // No known path (or already "/") - normalise the address bar to match
      // whatever view is actually showing, without adding a history entry.
      window.history.replaceState(null,"",VIEW_TO_PATH[view]||"/");
    }
  },[page]);

  // Runs on every subsequent view change and keeps the address bar in sync.
  // Skipped on the very first run (handled above) to avoid pushing a
  // duplicate history entry the instant the app opens.
  const isFirstViewSync=useRef(true);
  useEffect(()=>{
    if(page!=="app")return;
    if(isFirstViewSync.current){isFirstViewSync.current=false;return;}
    const target=VIEW_TO_PATH[view]||"/";
    if(window.location.pathname!==target){
      window.history.pushState(null,"",target);
    }
  },[view,page]);

  // Browser back/forward buttons - the ONE other thing that can change the
  // address bar besides this code itself. Kept in sync the same way.
  useEffect(()=>{
    const onPop=()=>{
      const matchedView=PATH_TO_VIEW[window.location.pathname];
      if(matchedView)setView(matchedView);
    };
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[]);
  // ═══════════════════════════════════════════════════════════════════════
  const [nTab,setNTab]=useState("boardroom");
  const [brQ,setBrQ]=useState("");
  const [brAg,setBrAg]=useState(["ceo","cfo","cto","cmo"]);
  const [brSessions,setBrSessions]=useState([]);
  const [brCur,setBrCur]=useState({q:"",researchBrief:"",format:"threaded",stages:[]}); 
  const [brResearching,setBrResearching]=useState(false);
  const [brRun,setBrRun]=useState(false);
  const [brPh,setBrPh]=useState("");
  const [drillRole,setDrillRole]=useState(null);
  const [brShowHistory,setBrShowHistory]=useState(false);
  const [brFollowUp,setBrFollowUp]=useState("");
  // Research is reused for the same question unless the user explicitly asks for fresh research.
  const [brFreshResearch,setBrFreshResearch]=useState(false);
  // Deterministic Time Machine runs and Autopilot operating plans (persisted).
  const [tmSims,setTmSims]=useState<any[]>(()=>{try{return (WorkspaceMemory.get<any[]>("oiq-tm-sims")||[]) as any[];}catch{return [];}});
  const [apPlans,setApPlans]=useState<any[]>(()=>{try{return (WorkspaceMemory.get<any[]>("oiq-autopilot-plans")||[]) as any[];}catch{return [];}});
  // Journey shell: how much to explain (remembered across sessions).
  const [explainLevel,setExplainLevelState]=useState<ExplainLevel>(()=>{try{return (WorkspaceMemory.get<string>("oiq-explain-level")==="expert"?"expert":"new");}catch{return "new";}});
  const setExplainLevel=useCallback((l:ExplainLevel)=>{setExplainLevelState(l);try{sv("oiq-explain-level",l);}catch{}},[]);
  const [followUpExecIds,setFollowUpExecIds]=useState([]);
  const [followUpSuggestions,setFollowUpSuggestions]=useState([]);
  const [drillQ,setDrillQ]=useState("");
  const [drillRun,setDrillRun]=useState(false);
  const [tmDec,setTmDec]=useState("");
  const [tmRes,setTmRes]=useState("");
  const [tmRun,setTmRun]=useState(false);
  const [tmResearchBrief,setTmResearchBrief]=useState("");
  const [tmPh,setTmPh]=useState("");
  const [tmSessions,setTmSessions]=useState([]);
  const [apSessions,setApSessions]=useState<any[]>(()=>{try{return JSON.parse(localStorage.getItem("cos-ap")||"[]");}catch{return [];}});
  const [apShowHistory,setApShowHistory]=useState(false);
  const [tmShowHistory,setTmShowHistory]=useState(false);
  const [apRes,setApRes]=useState("");
  const [apRun,setApRun]=useState(false);
  const [apResearchBrief,setApResearchBrief]=useState("");
  const [apPh,setApPh]=useState("");
  const [showSettings,setShowSettings]=useState(false);
  const [sTab,setSTab]=useState("api");
  const [confirmReset,setConfirmReset]=useState(null);
  const [testSt,setTestSt]=useState({});
  const [vLang,setVLang]=useState("en-IN");
  const [dnCfg,setDnCfg]=useState({   ownerName:import.meta.env.VITE_OWNER_NAME||"",   ownerEmail:import.meta.env.VITE_OWNER_EMAIL||"",   upiId:import.meta.env.VITE_UPI_ID||"",   bankName:import.meta.env.VITE_BANK_NAME||"",   accountNo:import.meta.env.VITE_ACCOUNT_NO||"",   ifsc:import.meta.env.VITE_IFSC||"",   accountType:import.meta.env.VITE_ACCOUNT_TYPE||"",   paypalMe:"",   stripeLink:"",   note:"Scan the QR code below to support this project. Thank you!",   qrImage:DEFAULT_QR,   enabled:true });
  const [dnAmt,setDnAmt]=useState(100);
  const [dnCustom,setDnCustom]=useState("");
  const [showDonate,setShowDonate]=useState(false); 
  // WAS declared and never set or read - dead state since it was written.
  // It now holds whether this account has ANY administrative reach: the owner,
  // or a staff member the owner has appointed. The console re-checks with the
  // database itself, so this only decides whether the menu entry appears.
  const [isAdmin,setIsAdmin]=useState(false);
  // ACCESS RESTRICTION, AS REQUESTED: JARVIS defaults to owner-only. A
  // regular user only sees it if explicitly granted via
  // Admin Console → JARVIS Access, checked directly against the real
  // grant, not assumed.
  const [hasJarvisAccess,setHasJarvisAccess]=useState(false);
  // PHASE 1 SWITCHING MECHANISM: defaults to "stable" the instant this
  // component mounts, before the database is even asked - so if the
  // fetch below fails for any reason, the app has already chosen the
  // safe option and never silently sits in an undefined state.
  const [jarvisMode,setJarvisMode]=useState<"stable"|"experimental">("stable");
  // What this account's plan actually allows. Loaded once at sign-in from the
  // database, which is also where it is enforced - this copy only decides what
  // the menu shows.
  // ent.unlimited is true for you and for staff, so an owner can never be
  // locked out of their own product by a plan setting.
  const [ent,setEnt]=useState<any>({unlimited:true,features:{}});
  const [showHelp,setShowHelp]=useState(false);
  const [showSignOutConfirm,setShowSignOutConfirm]=useState(false);
  const [wfView,setWfView]=useState("new");
  const [projects,setProjects]=useState([]);
  const [activeProject,setActiveProject]=useState(null);
  const [projectPlanning,setProjectPlanning]=useState(false);
  const [projectPlan,setProjectPlan]=useState(null); // pending approval
  const [projectObjective,setProjectObjective]=useState("");
  const [projectExecution,setProjectExecution]=useState(null); // active execution project
  const [projectExecuting,setProjectExecuting]=useState(false);
  const [projectExecPhase,setProjectExecPhase]=useState(""); // live status message
  const [projectExecCancel,setProjectExecCancel]=useState(false);
  // Ref mirror of the cancel flag — async loops read THIS. React state alone is
  // frozen inside long-running closures (stale closure), which made the Cancel
  // button functionally dead: it set state the loop could never observe.
  const projectExecCancelRef=useRef(false);
  const runProjectExecutionRef=useRef(null); // ref to avoid TDZ with useCallback ordering
  const rawContentStore=useRef({}); // full content stored outside React state to prevent memory/render overload
  const [projectQARunning,setProjectQARunning]=useState(false);
  const [projectPackaging,setProjectPackaging]=useState(false);
  const [projectReviewMode,setProjectReviewMode]=useState(false);
  const [projectExcluded,setProjectExcluded]=useState({});
  // Phase 6: user-driven iteration state — replaces the automated QA gate.
  const [projectFeedback,setProjectFeedback]=useState<Record<string,string>>({});
  const [projectRegeneratingId,setProjectRegeneratingId]=useState<string|null>(null);
  const [projectDashboardOpen,setProjectDashboardOpen]=useState(false);
  const [projectArchiveView,setProjectArchiveView]=useState("recent");
  const [projectSearchQ,setProjectSearchQ]=useState("");
  const [wfCustomChain,setWfCustomChain]=useState([]);
  const [wfShowExtra,setWfShowExtra]=useState(false);
  const [wfPreflight,setWfPreflight]=useState<{questions:{persona:string;personaIc:string;q:string;placeholder:string}[];answers:string[];contextSummary:string}|null>(null);
  const [wfPreflightActive,setWfPreflightActive]=useState(false);
  const [wfPreflightLoading,setWfPreflightLoading]=useState(false);
  const [wfTask,setWfTask]=useState("");
  const [wfCat,setWfCat]=useState("finance");
  const [wfActive,setWfActive]=useState(null);
  const [wfRunning,setWfRunning]=useState(false);
  const [wfPhase,setWfPhase]=useState("");
  const [workflows,setWorkflows]=useState([]);
  const [tQueue,setTQueue]=useState([]);
  const [qRunning,setQRunning]=useState(false);
  const [p3View,setP3View]=useState("dashboard");
  const [pulseTab,setPulseTab]=useState("dispatch");
  const [p3Task,setP3Task]=useState("");
  const [p3Pri,setP3Pri]=useState("medium");
  const [p3Auto,setP3Auto]=useState(true);
  const [p3Cat,setP3Cat]=useState("finance");
  const [p3Running,setP3Running]=useState(null);
  const [p3Phase,setP3Phase]=useState("");
  const [p3Notify,setP3Notify]=useState([]);

  // Raw server row - holds the admin-controlled timing/targeting fields
  // (enabled, first_delay_minutes, interval_minutes, target_plan_ids) that
  // the old hardcoded 30-minute timer never had any concept of.
  const [dnPlatformCfg,setDnPlatformCfg]=useState<any>(null);
  const [myPlanId,setMyPlanId]=useState<string|null>(null);
  // localDn/saveDn/saveDnTiming removed: the editing UI they supported moved
  // to Admin Console's own self-contained Donation tab. dnCfg below is kept -
  // it still drives the actual donation popup shown to regular users.

  // Derived currency — declared early so callbacks (runExport, quickExport) can reference it safely
  // Hard ceiling on AI requests per board session. Sized for 9 executives with
  // research on, plus generous retries - but finite, which it was not before.
  // Current consent state, readable from callbacks that were created before the
  // state existed. Kept in step by an effect further down the file.
  const consentRef=useRef<{needed:boolean;signature:string}>({needed:false,signature:""});
  const BR_MAX_CALLS=120;
  const brCallBudget=useRef(BR_MAX_CALLS);
  const cur=useMemo(()=>CURRENCIES.find(cv=>cv.code===co.currency)||CURRENCIES[0],[co.currency]);
  // Recompute the published Ledger financials whenever the books, the plan
  // figures or the currency change.
  // POSITION MATTERS: this must sit BELOW the declarations of ledgerEntries,
  // customAccounts, compData and cur. A dependency array is evaluated during
  // render, so placing it above `cur` threw "Cannot access 'cur' before
  // initialization" and took the whole page down.
  useEffect(()=>{
    publishFinancials(ledgerEntries,customAccounts,compData,cur.sym,12);
  },[ledgerEntries,customAccounts,compData,cur]);
  // FIX BUG 7: toast state
  const [toasts,setToasts]=useState([]);
  // API health indicator
  const [apiHealth,setApiHealth]=useState(null); // null|'checking'|'ok'|'fail'

  // FEATURE 1: theme
  const [theme,setTheme]=useState("dark");
  // FEATURE 4/5: export studio
  const [showExport,setShowExport]=useState(false);
  const [showShare,setShowShare]=useState(false);
  const [showShareSB,setShowShareSB]=useState(false); // the sidebar-footer copy of the same control
  const [expMode,setExpMode]=useState("pdf"); // pdf | pptx
  const [expDocType,setExpDocType]=useState("executive");
  const [expPptType,setExpPptType]=useState("briefing");
  const [expSources,setExpSources]=useState({chats:true,boardroom:true,workflows:true,tasks:true,timeMachine:true,autopilot:true});
  const [expTitle,setExpTitle]=useState("");
  // WHO the document is for. Until now every export was hardcoded to "board",
  // so an investor deck and an operations review were generated identically.
  // "auto" derives it from the document type you picked.
  const [expAudience,setExpAudience]=useState<string>("auto");
  // WHO the document is for, asked at the moment of export.
  // Every export button outside Export Studio produced a board document
  // regardless of the real reader: the Session 45 audience system was built but
  // wired into Export Studio only. This holds the pending export while the user
  // answers, then releases it.
  const [xfer,setXfer]=useState<any>(null);
  const [xferAud,setXferAud]=useState<string>("auto");
  const [expGenerating,setExpGenerating]=useState(false);
  const [expSynthesis,setExpSynthesis]=useState("");
  const [expStep,setExpStep]=useState("");
  const [expStyleResult,setExpStyleResult]=useState(null);
  // FEATURE 2: resume banner
 const [resumeInfo,setResumeInfo]=useState(null);
const [wfResumeData,setWfResumeData]=useState<ResumeState|null>(null);
const [wfPauseMsg,setWfPauseMsg]=useState("");

  // Cancel refs for long-running operations
  const cancelRef=useRef({br:false,tm:false,ap:false,wf:false,q:false});

  const tQRef=useRef([]);
  const chatEnd=useRef(null);
  const brEnd=useRef(null);

  // FIX BUG 7: showToast
  const showToast=useCallback((msg,type="info")=>{
    const id=Date.now()+Math.random();
    setToasts(prev=>[...prev.slice(-4),{id,msg:String(msg),type}]);
    // THE ACTUAL FIX: error and warning toasts carry the one piece of
    // information a user needs to file an accurate report (the exact
    // provider/model error) — auto-dismissing them after 7 seconds meant
    // anyone not staring at the screen the instant it appeared never saw
    // it. Those two types now stay until the person clicks the × on them;
    // success/info remain auto-dismissing since nothing is lost if missed.
    if(type!=="error"&&type!=="warning"){
      setTimeout(()=>setToasts(prev=>prev.filter(t=>t.id!==id)),7000);
    }
  },[]);

  // Load persisted data
 useEffect(()=>{
  attachBenchmarkToWindow();
  (async()=>{
    try{const th=WorkspaceMemory.get<string>("cos-theme");const tid=th&&THEMES[th]?th:"dark";setTheme(tid);applyTheme(tid);}catch{applyTheme("dark");}
    try{const c=WorkspaceMemory.get<typeof co>("cos-co");if(c)setCo(p=>({...p,...c}));}catch{}
    // SUPER_ADMIN: auto-load API keys from Supabase on every login/refresh
    // so you never need to re-enter them. Other users unchanged.
    try{
      const {data:{user}}=await supabase.auth.getUser();
      if(user){
        try{loadCostContext(true);}catch(e){console.warn("[OIQ] cost context:",e);}
        // WAS selecting admin_api_keys and user_api_keys. Those columns are gone -
        // the keys are encrypted now and come back through get_my_api_keys(),
        // which decrypts only for the person who owns them.
        const {data:prof}=await supabase.from("profiles").select("full_name,role").eq("id",user.id).single();
        const {data:_mk}=await supabase.rpc("get_my_api_keys");
        setMe({email:(prof as any)?.full_name||user.email||"",role:prof?.role||"user"});
        try{setUsageUser((prof as any)?.full_name||user.email||"",prof?.role||"user");}catch{}
        // Show the console entry for the owner and for appointed staff. This is
        // presentation only - every admin action is re-authorised server-side
        // against the profiles table, so editing this value in a browser buys
        // nothing but an error message.
        try{setIsAdmin(prof?.role==="super_admin"||prof?.role==="admin");}catch{}
        try{
          if(prof?.role==="super_admin"){setHasJarvisAccess(true);}
          else{
            const {data:feat}=await supabase.from("features").select("id").eq("key","jarvis_access").maybeSingle();
            if(feat){
              const {data:grant}=await supabase.from("user_feature_grants").select("enabled")
                .eq("user_id",user.id).eq("feature_id",feat.id).eq("enabled",true).maybeSingle();
              setHasJarvisAccess(!!grant);
            }else{setHasJarvisAccess(false);}
          }
        }catch{setHasJarvisAccess(false);}
        // Reads the switch itself. Any failure here - network, RPC missing,
        // anything - leaves jarvisMode at its already-set default of
        // "stable", never at "experimental" by accident. Failing toward
        // the proven implementation, not the one being tested, is the
        // entire point of this mechanism.
        try{
          const {data:mode,error:jarvisModeError}=await supabase.rpc("get_jarvis_mode");
          console.log("[JARVIS MODE]", { data: mode, error: jarvisModeError });
          if(mode==="experimental")setJarvisMode("experimental");
        }catch(e){console.warn("[OIQ] jarvis mode check failed, staying on stable:",e);}
        // If this call fails for any reason we keep the default of unlimited.
        // Failing OPEN is deliberate: a database hiccup should never lock a
        // paying customer out of the product they bought. Session limits are
        // still enforced separately by the session gate, so nothing is free.
        try{
          const {data:_e}=await supabase.rpc("resolve_entitlements");
          if(_e&&typeof _e==="object")setEnt(_e);
        }catch(e){console.warn("[OIQ] entitlements unavailable, allowing all:",e);}
        // Load this user's saved keys. Row Level Security guarantees this row
        // is only ever returned to its owner.
        try{
          const uk=(_mk as any)?.user_api_keys;
          if(uk&&typeof uk==="object"&&Object.keys(uk).length){
            setKeys(prev=>{
              const merged:any={...prev};
              Object.keys(uk).forEach(k=>{ if(uk[k]) merged[k]=uk[k]; });
              return merged;
            });
          }
        }catch{}
        // get_my_api_keys returns admin keys ONLY to the owner. A staff member
        // or a normal user receives an empty object, whatever this check says.
        if(prof?.role==="super_admin"&&(_mk as any)?.admin_api_keys&&Object.keys((_mk as any).admin_api_keys).length){
          const ak=(_mk as any).admin_api_keys;
          const loadedKeys=ak.keys||ak;
          // BUG FIX: this used to RETURN A NEW OBJECT WITH ONLY THESE 8 FIELDS,
          // silently deleting serper / tavily / brave / dataforseo (and nvidiaModel)
          // from state on every login. The debounced save-back then wrote that
          // truncated object to Supabase, erasing the saved search keys for good —
          // which is why a Serper key never survived a refresh.
          // Spreading prev first preserves every key that is not listed here.
          setKeys(prev=>{
            const merged:any={...prev};
            Object.keys(loadedKeys||{}).forEach(k=>{
              if(typeof loadedKeys[k]==="string"&&loadedKeys[k].trim())merged[k]=loadedKeys[k];
            });
            return merged;
          });
          if(ak.defaultProvider)setDefP(ak.defaultProvider);
          if(ak.multiAI!==undefined)setMultiAI(ak.multiAI);
        }
      }
    }catch{}
    try{const k=WorkspaceMemory.get<any>("cos-keys");if(k){
      // WorkspaceMemory.get already returns a parsed object. Calling JSON.parse on
      // it threw, and the whole local key restore was silently swallowed by catch{}.
      const p=(typeof k==="string")?JSON.parse(k):k;
      const loadedKeys={claude:"",openai:"",gemini:"",groq:"",deepseek:"",kimi:"",stability:"",fal:"",serper:"",tavily:"",brave:"",dataforseo:"",...(p.keys||{})};
      setKeys(loadedKeys);setDefP(p.defaultProvider||"claude");setMultiAI(p.multiAI||false);
      if(Object.values(loadedKeys).some(v=>v?.trim()))setPage("app");
      const active=Object.keys(loadedKeys).filter(pid=>loadedKeys[pid]?.trim());
      if(active.length){
        const pid=(p.defaultProvider&&active.includes(p.defaultProvider))?p.defaultProvider:active[0];
        setApiHealth("checking");
        callAI(pid,loadedKeys[pid],"You are a test assistant.",[{role:"user",content:"Reply with one word: OK"}],5)
          .then(()=>setApiHealth("ok"))
          .catch(()=>setApiHealth("fail"));
      }
    }}catch{}
    try{const vl=WorkspaceMemory.get<string>("cos-vl");if(vl)setVLang(vl);}catch{}
    try{const rq=WorkspaceMemory.get<string>("cos-quality");if(rq==="standard"||rq==="professional"||rq==="excellent"){setRespQuality(rq);RESPONSE_QUALITY=rq;}}catch{}
    try{const h=WorkspaceMemory.get<any>("cos-ch");if(h)setChats(h);}catch{}
    try{const d=WorkspaceMemory.get<any>("cos-dp");if(d)setExpD(d);}catch{}
    try{const cd=WorkspaceMemory.get<any>("cos-cd");if(cd)setCompData(cd);}catch{}
    try{const le=WorkspaceMemory.get<any>("cos-ledger");if(le)setLedgerEntries(le);}catch{}
    try{const ca=WorkspaceMemory.get<any>("cos-accounts");if(ca)setCustomAccounts(ca);}catch{}
    try{const dt=WorkspaceMemory.get<any>("cos-dispatch-templates");if(dt)setDispatchTemplates(dt);}catch{}
    try{const acts=WorkspaceMemory.get<any>("cos-actions");if(acts)setActionItems(acts);}catch{}
    try{const ac=WorkspaceMemory.get<any>("cos-admin-config");if(ac)setAdminConfig(p=>({...p,...ac}));}catch{}
    try{const br=WorkspaceMemory.get<any>("cos-br");if(br)setBrSessions(br);}catch{}
    try{const projs=WorkspaceMemory.get<any[]>("cos-projects");if(projs){const cleaned=projs.map((p:any)=>p.status==="executing"||p.status==="qa"?{...p,status:"partial"}:p);setProjects(cleaned);WorkspaceMemory.set("cos-projects",cleaned);}}catch{}
    try{localStorage.removeItem("cos-project-plan");}catch{} // always start fresh
    try{const brLive=WorkspaceMemory.get<any>("cos-br-live");if(brLive?.q)setBrCur(brLive);}catch{}
    try{const tm=WorkspaceMemory.get<any>("cos-tm");if(tm)setTmSessions(tm);}catch{}
    try{const tmLive=WorkspaceMemory.get<any>("cos-tm-live");if(tmLive?.dec){setTmDec(tmLive.dec);setTmRes(tmLive.res||"");setTmResearchBrief(tmLive.brief||"");}}catch{}
    try{const ap=WorkspaceMemory.get<any>("cos-ap");if(ap)setApSessions(ap);}catch{}
    try{const apLive=WorkspaceMemory.get<any>("cos-ap-live");if(apLive?.res){setApRes(apLive.res);setApResearchBrief(apLive.brief||"");}}catch{}
    // THE FIX: donation config is now read from ONE server-side row, the
    // same for every user, editable only by super_admin - not a per-browser
    // local setting anyone could privately override.
    try{
      const {data:pd}=await supabase.from("platform_donation_settings").select("*").eq("id",1).maybeSingle();
      if(pd){
        const mapped={ownerName:pd.owner_name||"",ownerEmail:pd.owner_email||"",upiId:pd.upi_id||"",
          bankName:pd.bank_name||"",accountNo:pd.account_no||"",ifsc:pd.ifsc||"",accountType:pd.account_type||"",
          // THE FIX, FOUND WHILE MOVING THIS TO ADMIN CONSOLE: qr_image never
          // existed as a column - any QR code an owner uploaded was silently
          // discarded on every reload, replaced by this hardcoded placeholder.
          // The column now exists; read the real value, falling back to the
          // placeholder only when nothing has ever been uploaded.
          paypalMe:pd.paypal_me||"",stripeLink:pd.stripe_link||"",note:pd.note||"",qrImage:pd.qr_image||DEFAULT_QR,
          enabled:!!pd.enabled};
        setDnCfg(mapped);
        setDnPlatformCfg(pd);
      }
      // Needed to check target_plan_ids - fetched here rather than assuming
      // an existing variable already held this, since none did.
      const {data:auth2}=await supabase.auth.getUser();
      if(auth2?.user?.id){
        const {data:prof}=await supabase.from("profiles").select("plan_id").eq("id",auth2.user.id).maybeSingle();
        setMyPlanId(prof?.plan_id||null);
      }
    }catch{}
    try{const wf=WorkspaceMemory.get<any>("cos-wf");if(wf)setWorkflows(wf);}catch{}
    try{const tq=WorkspaceMemory.get<any>("cos-tq");if(tq){setTQueue(tq);tQRef.current=tq;}}catch{}
    try{const last=WorkspaceMemory.get<string>("cos-lastvisit");if(last){const days=Math.floor((Date.now()-parseInt(last))/86400000);if(days>=1)setResumeInfo({days});}WorkspaceMemory.set("cos-lastvisit",String(Date.now()));}catch{}
    const saved=loadResumeState();
    if(saved)setWfResumeData(saved);
    enrichEPFromSupabase();
    // fetchLiveRates() was DEFINED but never called anywhere in the app. LIVE_RATES
    // stayed {loaded:false}, so buildSys() always passed an empty rates block and no
    // executive ever saw a real exchange rate. The ticker fetched rates for DISPLAY
    // only; the prompts never received them. This one line is the root cause.
    try{fetchLiveRates();}catch{}
    setTimeout(()=>{
      initA11y();
      try{const r=document.getElementById("oiq-root");if(r&&WorkspaceMemory.get<string>("oiq-sb-col")==="1")r.classList.add("oiq-sb-collapsed");}catch{}
    },300);
  })();
},[]);
  
  // THE EXACT FIX REQUESTED: this used to be hardcoded - 30 minutes, then
  // every 30 minutes, for every signed-in user, forever, with no way for the
  // owner to change any of it. Now every part of that is controlled from
  // Admin Console: whether it shows at all, the delay and repeat interval,
  // and which plans it targets (empty/null target = everyone).
  useEffect(()=>{
    if(!dnPlatformCfg?.enabled)return; // super_admin has not turned this on - show nothing, ever
    const targets:string[]|null=dnPlatformCfg.target_plan_ids;
    if(targets&&targets.length&&!targets.includes(myPlanId||"")){return;} // this user's plan is not in the targeted list
    const firstMs=Math.max(1,dnPlatformCfg.first_delay_minutes||30)*60*1000;
    const repeatMs=Math.max(1,dnPlatformCfg.interval_minutes||30)*60*1000;
    const timer=setTimeout(()=>{ setShowDonate(true); },firstMs);
    const interval=setInterval(()=>{ setShowDonate(true); },repeatMs);
    return ()=>{ clearTimeout(timer); clearInterval(interval); };
  },[dnPlatformCfg,myPlanId]);

  useEffect(()=>{
  const TWENTY_MIN = 20 * 60 * 1000;
  const interval = setInterval(()=>{
    if(Object.keys(compData).length>0||ledgerEntries.length>0||Object.values(chats).some(c=>c?.length>0)){
      showToast("Reminder: save your workspace (Settings > Workspace) so you don't lose your data","info");
    }
  }, TWENTY_MIN);
  return ()=>clearInterval(interval);
},[compData,ledgerEntries,chats,showToast]);

  const changeTheme=useCallback((id)=>{
    setTheme(id);applyTheme(id);sv("cos-theme",id);
  },[]);

  const changeQuality=useCallback((tier:"standard"|"professional"|"excellent")=>{
    setRespQuality(tier);RESPONSE_QUALITY=tier;sv("cos-quality",tier);
  },[]);

  useEffect(()=>{chatEnd.current?.scrollIntoView({behavior:"smooth"});},[chats,selRole,loading]);
  useEffect(()=>{brEnd.current?.scrollIntoView({behavior:"smooth"});},[brCur,brPh]);

  // sv(): now routes through WorkspaceMemory so every save is caught by
  // Full Reset and future cloud sync automatically.
  const sv=useCallback((k:string,v:unknown)=>{
    try{WorkspaceMemory.set(k,v);}catch{}
  },[]);
  const cp=t=>{try{navigator.clipboard.writeText(t);showToast("Copied to clipboard","success");}catch{showToast("Copy failed","error");}};
  const addN=(msg,type)=>setP3Notify(prev=>[{id:Date.now(),msg,type:type||"info",ts:new Date().toISOString()},...prev].slice(0,20));

  const testKey=async(provider)=>{
    if(!keys[provider]?.trim())return;
    setTestSt(p=>({...p,[provider]:"testing"}));
    try{await callAI(provider,keys[provider],"You are a test.",[{role:"user",content:"Say OK."}],20);setTestSt(p=>({...p,[provider]:"ok"}));showToast(MODELS[provider].name+" key is working ✓","success");}
    catch(e){setTestSt(p=>({...p,[provider]:"fail:"+e.message}));showToast(MODELS[provider].name+": "+e.message,"error");}
  };

  const completeOnboard=async()=>{
    // THE ACTUAL BUG, CONFIRMED: the button's disabled state was fixed to
    // respect skipApiKeyAck, but this function had its OWN, separate check
    // that never knew that state existed - so clicking the now-enabled
    // button silently did nothing at all. Same guard, now aware of the
    // same acknowledgment.
    const hasAnyKey=Object.values(keys).some(k=>k?.trim())||!!EFF_GEMINI;
    if((!hasAnyKey&&!skipApiKeyAck)||!co.name.trim()||!co.industry.trim()||!co.location.trim())return;
    sv("cos-keys",{keys,defaultProvider:defP,multiAI});sv("cos-co",co);
    // SUPER_ADMIN: persist keys to Supabase so they survive page refresh,
    // code deploys, and new devices automatically.
    try{
      const {data:{user}}=await supabase.auth.getUser();
      if(user){
        const {data:prof}=await supabase.from("profiles").select("role").eq("id",user.id).single();
        if(prof?.role==="super_admin"){
          await supabase.rpc("save_my_api_keys",{
            p_keys:{keys,defaultProvider:defP,multiAI}, p_admin:true });
        }
      }
    }catch{}
    const e={};DEPTS.forEach(d=>e[d.id]=true);setExpD(e);sv("cos-dp",e);
    setPage("app");
  };

  // ── Stable AI call helpers ─────────────────────────────────────────────────
  // CRITICAL: All wrapped in useCallback with [keys,defP] deps.
  // Without useCallback these are recreated every render → AgenticWorkflows
  // useEffect([ask,...]) fires every render → infinite loop → stack overflow.
  const ask=useCallback(async(sys:any,msgs:any,maxT?:any,enableSearch?:any,taskType?:any)=>
    (await callMulti(keys,defP,sys,msgs,maxT,enableSearch,taskType)).primary,
  [keys,defP]);
 
  // The Universal Workspace lets the USER pick provider and model, so it needs
  // a way to override the app's normal routing. Everything else is unchanged:
  // the same callAI, the same keys, the same failure handling. This adds a
  // choice; it does not add a second AI layer.
  const askDirect=useCallback(async(sys:any,msgs:any,maxT?:any,enableSearch?:boolean,taskType?:any,provider?:string,model?:string)=>{
    // THE FIX: this previously hardcoded search off in both branches below,
    // regardless of what a caller asked for. JARVIS needs real search for
    // general questions (news, prices); this now honors the flag instead
    // of silently discarding it. Existing callers passing false/undefined
    // (Workspace's own calls) see no change in behavior.
    const featureLabel=taskType==="jarvis"?"JARVIS":"Universal Workspace";
    if(!provider)return (await callMulti(keys,defP,sys,msgs,maxT,!!enableSearch,taskType||"workspace")).primary;
    const k=providerKey(keys,provider);
    if(!k)throw new Error("No key configured for "+provider+". Add one in Settings \u2192 API.");
    setUsageFeature(featureLabel,"\u2733");
    const r=await callAI(provider,k,sys,msgs,maxT||4000,!!enableSearch,model);
    return typeof r==="string"?r:(r?.text||"");
  },[keys,defP]);

  // JARVIS TOOL LOOP — provider-neutral.
  // JARVIS must not depend on a user's Claude key just to inspect the repository.
  // The existing provider router is used for reasoning calls, so configured
  // providers can participate through the normal fallback chain.
  // Consequential tools are queued for owner approval and never executed silently.
  const askJarvisWithTools=useCallback(async(
    sys:string,
    userMsg:string,
    history:{role:string;content:string}[],
    tools:any[],
    onToolCall?:(name:string,input:any)=>void
  )=>{
    const MAX_ITER=12; // bounded provider-neutral JARVIS tool loop; consequential tools remain approval-gated
    let messages:any[]=[...history.map(h=>({role:h.role,content:h.content})),{role:"user",content:userMsg}];

    const parseToolDecision=(raw:string):any|null=>{
      if(!raw)return null;
      const cleaned=String(raw).replace(/```(?:json)?/gi,"").replace(/```/g,"").trim();
      try{const parsed=JSON.parse(cleaned);if(parsed&&typeof parsed==="object")return parsed;}catch{}
      const start=cleaned.indexOf("{");
      const end=cleaned.lastIndexOf("}");
      if(start>=0&&end>start){try{return JSON.parse(cleaned.slice(start,end+1));}catch{}}
      return null;
    };

    const toolContract=`
JARVIS TOOL PROTOCOL — INTERNAL
Return ONLY one JSON object. No markdown or commentary.
Tool call: {"type":"tool","name":"EXACT_TOOL_NAME","input":{}}
Final answer: {"type":"final","answer":"your answer"}
Never invent a tool. Never claim a tool ran unless its result is supplied.
Consequential tools are never executed automatically; they are queued for owner approval.

AVAILABLE TOOLS:
${tools.map((t:any)=>`- ${t.name}: ${t.description} [${t.riskLevel}; approval=${t.requiresApproval}]`).join("\n")}
`;

    for(let i=0;i<MAX_ITER;i++){
      const routed=await callMulti(
        keys,defP,sys+"\n\n"+toolContract,messages,1600,false,"jarvis_tool"
      );
      const raw=String(routed.primary||"").trim();
      const decision=parseToolDecision(raw);

      if(!decision || decision.type==="final" || !decision.type){
        return decision?.answer ? String(decision.answer) : raw;
      }
      if(decision.type!=="tool" || !decision.name)return raw;

      const tool=tools.find((candidate:any)=>candidate.name===String(decision.name));
      if(!tool){
        messages.push({role:"assistant",content:raw});
        messages.push({role:"user",content:`TOOL RESULT: ${JSON.stringify({error:`Unknown tool: ${decision.name}`})}`});
        continue;
      }

      const input=decision.input&&typeof decision.input==="object"?decision.input:{};
      onToolCall?.(tool.name,input);
      let result:any;
      let auditError:string|null=null;

      if(tool.riskLevel==="dangerous" || tool.riskLevel==="consequential" || tool.requiresApproval){
        try{
          const {data:{user}}=await supabase.auth.getUser();
          if(!user)throw new Error("Not signed in");
          const {error:insertError}=await supabase.from("jarvis_lab_approvals").insert({
            user_id:user.id,tool_name:tool.name,proposed_input:input,
            reasoning:input?.reasoning||`Owner approval required for: ${tool.name}`,
            risk_category:tool.riskCategory||"consequential",status:"pending",created_at:new Date().toISOString(),
          });
          if(insertError)throw insertError;
          result={status:"pending_approval",message:"Queued for owner approval. NOT executed."};
        }catch(e:any){
          auditError=e?.message||"Could not queue approval";
          result={error:auditError};
        }
      }else{
        try{result=await tool.handler(input);}catch(e:any){
          auditError=e?.message||"Tool execution failed";
          result={error:auditError};
        }
      }

      try{
        const {data:{user}}=await supabase.auth.getUser();
        if(user)await supabase.from("jarvis_lab_tool_calls").insert({
          user_id:user.id,tool_name:tool.name,risk_level:tool.riskLevel,input,
          output:auditError?null:result,error:auditError,
        });
      }catch{}

      messages.push({role:"assistant",content:raw});
      messages.push({role:"user",content:`TOOL RESULT FOR ${tool.name}:\n${JSON.stringify(result)}`});
    }

    return "JARVIS reached its safety limit before producing a final answer. Ask me to continue the investigation.";
  },[keys,defP]);
 
  // Only providers the user actually has a usable key for. A model they cannot
  // reach must never appear in the picker.
  const [nvLiveModels,setNvLiveModels]=useState<Array<{id:string;label:string;note:string}>|null>(null);
  useEffect(()=>{
    const k=(keys.nvidia&&keys.nvidia!=="nvidia"&&keys.nvidia.startsWith("nvapi-"))?keys.nvidia:"";
    if(!k){setNvLiveModels(null);return;}
    let cancelled=false;
    import("./lib/AIModels").then(({fetchNvidiaLiveCatalog})=>
      fetchNvidiaLiveCatalog(k).then(models=>{
        if(!cancelled&&models.length){
          setNvLiveModels(models.map((m:any)=>({id:m.id,label:m.label,note:(m.ctx==="long"?"long context":"")+(m.tier==="strong"?" \u00b7 strong":"")})));
        }
      })
    );
    return ()=>{cancelled=true;};
  },[keys.nvidia]);
 
  // Wraps the module-level MicButton with this user's actual OpenAI key,
  // resolved via the same providerKey() every other provider call already
  // uses (respecting the per-user on/off toggle correctly, rather than a
  // second, separate lookup that could drift from it). Passed to every
  // screen that receives MicButton as a prop (BoardroomView, Ledger,
  // Dispatch) so none of those files need to know this exists.
  const MicButtonWithKey=useCallback((props:any)=>
    <MicButton {...props} openaiKey={providerKey(keys,"openai")||undefined}/>,
  [keys]);

  const wsProviders=useMemo(()=>
    ["claude","openai","gemini","nvidia","deepseek","groq","kimi"]
      .filter(id=>providerKey(keys,id)&&wsModelsFor(id).length)
      .map(id=>({id,label:(MODELS as any)[id]?.name||id})),
  [keys]);
 
  const wsImageProviders=useMemo(()=>
    provAvailableFor("image",(id)=>providerKey(keys,id)).map(c=>({id:c.id,label:c.spec.label})),
  [keys]);
  const wsGenerateImage=useCallback(async(prompt:string):Promise<string>=>{
    const cands=provAvailableFor("image",(id)=>providerKey(keys,id));
    for(const c of cands){
      try{
        if(c.id==="openai"){const u=await callDallE(prompt,c.key); if(u)return u;}
        else if(c.id==="stability"){const u=await callStabilityAI(prompt,c.key); if(u)return u;}
        else if(c.id==="fal"){const u=await callFalImage(c.key,prompt); if(u)return u;}
      }catch(e){console.warn("[OIQ] workspace image provider "+c.id+" failed:",String((e as any)?.message||e).slice(0,110));}
    }
    throw new Error(provCannotDo(defP||"nvidia","image",[]));
  },[keys,defP]);
 
  // ── LIVE BOARDROOM WIRING ──────────────────────────────────────────────
  // Reuses everything that already exists rather than duplicating it:
  //   askDirect        the same provider call the Workspace uses
  //   AR                the full 84-role roster, unchanged
  //   buildBoardIdentity + ANALYST_STANDARD + CLARITY_PROTOCOL   the exact
  //       persona, standard of reasoning and clarifying-question rules every
  //       existing executive already uses — the Live Boardroom does not
  //       get a second, different personality.
  //   runResearchDesk   the same research pipeline the existing Boardroom uses
  const lbRunResearch=useCallback(async(q:string)=>{
    const r:any=await runResearchDesk((s:any,m:any,t:any)=>ask(s,m,t,true),co,compData,q,showToast,keys);
    const brief=typeof r==="string"?r:(r?.brief||"");
    const sources=(brief.match(/https?:\/\/[^\s)]+/g)||[]).slice(0,8).map((u:string)=>({url:u}));
    return {text:brief,sources};
  },[co,compData,keys,ask,showToast]);
  const lbRoute=useCallback(()=>({provider:defP,model:MODELS[defP]?.model||""}),[defP]);
  const lbBuildIdentity=useCallback((role:any)=>{
    const p=getExecutiveIntel(role.id)||{};
    return "You are "+role.f+" at \""+co.name+"\".\n"+buildBoardIdentity(role,p)+CLARITY_PROTOCOL;
  },[co]);
  const askFull=useCallback(async(sys:any,msgs:any,maxT?:any,enableSearch?:any,taskType?:any)=>
    callMulti(keys,defP,sys,msgs,maxT,enableSearch,taskType),
  [keys,defP]);
  const askImage=useCallback(async(prompt:string,size="landscape_4_3",model="fal-ai/flux-pro"):Promise<string>=>{
    const falKey=providerKey(keys,"fal");
    if(!falKey)throw new Error("Add your fal.ai API key in Settings → fal.ai to generate images.");
    return callFalImage(falKey,prompt,model);
  },[keys]);
  const askVideo=useCallback(async(prompt:string,durationSec=5,model="fal-ai/kling-video/v1.6/standard/text-to-video"):Promise<string>=>{
    const falKey=providerKey(keys,"fal");
    if(!falKey)throw new Error("Add your fal.ai API key in Settings → fal.ai to generate videos.");
    return callFalVideo(falKey,prompt,durationSec,model);
  },[keys]);

  // Extracts candidate action items from a Boardroom/Autopilot/TimeMachine output and opens the review modal
  // Resilient action-item parser: tries JSON, fenced JSON, embedded JSON,
// and finally plain bullet/numbered lists — so a usable answer is almost
// never thrown away as "could not extract".
const parseActionItemsResilient=(raw:string):ActionItem[]=>{
  if(!raw)return [];
  let text=String(raw).trim();
  const fence=text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if(fence)text=fence[1].trim();
  const shape=(p:any):ActionItem[]|null=>{
    if(Array.isArray(p))return p as ActionItem[];
    if(p&&Array.isArray(p.items))return p.items;
    if(p&&Array.isArray(p.actionItems))return p.actionItems;
    if(p&&Array.isArray(p.actions))return p.actions;
    return null;
  };
  const normalize=(arr:any[]):ActionItem[]=>arr.map((it:any)=>{
    if(typeof it==="string")return {text:it.trim(),ownerRoleId:"",dueHint:"",priority:"medium"} as any;
    const label=(it.text||it.title||it.task||it.action||it.name||it.description||"").toString().trim();
    return {
      text:label,
      title:label,
      ownerRoleId:(it.ownerRoleId||it.owner||it.assignee||it.who||"").toString().trim(),
      owner:(it.ownerRoleId||it.owner||it.assignee||it.who||"").toString().trim(),
      dueHint:(it.dueHint||it.deadline||it.due||it.date||it.when||"").toString().trim(),
      deadline:(it.dueHint||it.deadline||it.due||it.date||it.when||"").toString().trim(),
      priority:(it.priority||"medium").toString().trim().toLowerCase(),
    } as any;
  }).filter((x:any)=>x.text&&x.text.length>2);
  // 1: direct JSON
  try{const s=shape(JSON.parse(text));if(s)return normalize(s);}catch{}
  // 2: first [...] array anywhere in the text
  const a1=text.indexOf("["),a2=text.lastIndexOf("]");
  if(a1!==-1&&a2>a1){try{const s=shape(JSON.parse(text.slice(a1,a2+1)));if(s)return normalize(s);}catch{}}
  // 3: first {...} object anywhere in the text
  const o1=text.indexOf("{"),o2=text.lastIndexOf("}");
  if(o1!==-1&&o2>o1){try{const s=shape(JSON.parse(text.slice(o1,o2+1)));if(s)return normalize(s);}catch{}}
  // 4: plain bullet / numbered lines
  const bullets=text.split("\n").map(l=>l.trim()).filter(Boolean)
    .filter(l=>/^[-•*]|^\d+[.)]/.test(l))
    .map(l=>l.replace(/^[-•*]\s*|^\d+[.)]\s*/,"").trim())
    .filter(l=>l.length>2)
    .map(l=>({title:l,owner:"",deadline:"",priority:"medium"} as ActionItem));
  if(bullets.length)return bullets;
  return [];
};
  const extractActionItems=async(sourceType:ActionItem["source"],sourceLabel:string,content:string)=>{
    if(!content?.trim())return;
    setExtracting(sourceType);
    try{
      const sys="You are an execution analyst. "+EXTRACTION_PROMPT;
      // Cap the input so the model isn't overwhelmed, and give it enough
      // output room to actually return the list. DeepSeek returns an empty
      // completion (not an error) when the output budget is too tight for
      // the input size — which is why this silently failed.
      const trimmedContent=content.length>6000?content.slice(0,6000):content;
      let result=await ask(sys,[{role:"user",content:"STRATEGIC OUTPUT:\n\n"+trimmedContent}],2500);
      console.log("[OIQ-ACTIONS] Raw AI response for action extraction:", result);
      // Retry once if the first call came back empty (transient with some providers).
      if(!result||!result.trim()){
        console.log("[OIQ-ACTIONS] Empty response — retrying once.");
        result=await ask(sys,[{role:"user",content:"List the action items from this as a JSON array:\n\n"+trimmedContent.slice(0,4000)}],2500);
        console.log("[OIQ-ACTIONS] Retry response:", result);
      }
      const items=parseActionItemsResilient(result);
      console.log("[OIQ-ACTIONS] Parsed items count:", items?.length||0);
      if(!items||!items.length){showToast("Could not extract action items from this output","error");return;}
      setExtractModal({items,sourceType,sourceLabel});
    }catch(e:any){
      showToast("Extraction failed: "+e.message,"error");
    }finally{
      setExtracting(null);
    }
  };

  const confirmExtractedItems=(items:ActionItem[])=>{
    const updated=[...items,...actionItems];
    setActionItems(updated);sv("cos-actions",updated);
    setExtractModal(null);
    showToast(items.length+" action item(s) added to tracker","success");
  };

  // Vision-capable call for Pulse Agentic (Dispatch). Uses Claude directly since it
  // supports image content blocks; falls back with a clear error if no Claude key.
  const askVision=async(sys:string,userText:string,images:{data:string;mediaType:string}[])=>{
    const claudeKey=providerKey(keys,"claude");
    if(!claudeKey)throw new Error(isProviderOff("claude")?"Pulse Agentic needs Claude for image reading, but Claude is switched OFF in Settings. Switch it on to use this feature.":"Pulse Agentic requires a Claude API key (for image reading). Add one in Settings.");
    const content:any[]=images.map(img=>({type:"image",source:{type:"base64",media_type:img.mediaType,data:img.data}}));
    content.push({type:"text",text:userText});
    const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json","x-api-key":claudeKey.trim(),"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},body:JSON.stringify({model:MODELS.claude.model,max_tokens:2000,system:sys,messages:[{role:"user",content}]})});
    if(!r.ok){const t=await r.text().catch(()=>"");let m="";try{m=JSON.parse(t).error?.message;}catch{m=httpErrText(t,r.status);}throw new Error("Claude "+r.status+": "+(m||r.statusText));}
    const d=await r.json();return d.content?.map((b:any)=>b.text||"").join("\n")||"";
  };

  const send=useCallback(async(text,attachment)=>{
    // THE FIX: attachment is now optional and threaded through, matching
    // the request to have every chat surface accept images the way Claude
    // and ChatGPT's own chat windows do. Text is still required — an image
    // alone with no question is ambiguous about what the user actually
    // wants explained.
    if(!text.trim()||loading||!selRole)return;
    setError(null);
    const role=AR.find(r=>r.id===selRole);if(!role)return;
    const msgs=chats[selRole]||[];
    // The LOCAL chat history keeps a thumbnail-friendly shape (plain text +
    // a separate attachment field for rendering) - only the OUTGOING API
    // call needs the provider-specific multimodal array.
    const nm={role:"user",content:text,attachment:attachment||undefined};
    const upd={...chats,[selRole]:[...msgs,nm]};
    setChats(upd);setInput("");setLoading(true);announceLoading(true,role.t);
    setUsageFeature("Executive Chat — "+role.t,"💬");
    try{
      const sys=buildSys(role,co,compData,LIVE_RATES.loaded?LIVE_RATES.data:"");
      const apiM=[...msgs,nm].map(m=>({role:m.role==="user"?"user":"assistant",
        content:m.attachment?buildAttachedContent(m.content,m.attachment,defP):m.content})).slice(-16);
      const reply=await ask(sys,apiM,3500,searchMode);
      const fin={...upd,[selRole]:[...upd[selRole],{role:"assistant",content:reply}]};
      setChats(fin);sv("cos-ch",fin);
      announceAIResponse(reply,role.t);
    }catch(e){
      setError(e.message);
      showToast(e.message,"error");
      setChats({...upd,[selRole]:upd[selRole].slice(0,-1)});
    }
    finally{setLoading(false);}
  },[loading,selRole,chats,co,compData,keys,defP,showToast]);

  const consultRole=useCallback(async(consultId,lastQ,lastA)=>{
    const role=AR.find(r=>r.id===consultId);if(!role)return;
    setSelRole(consultId);setView("chat");setError(null);setLoading(true);
    const msgs=chats[consultId]||[];
    const refMsg="[Cross-functional consult]\nQuestion: \""+lastQ+"\"\nAnother exec said:\n"+lastA+"\n\nAs "+role.f+", give YOUR expert perspective.";
    const nm={role:"user",content:refMsg};
    const upd={...chats,[consultId]:[...msgs,nm]};setChats(upd);
    try{const reply=await ask(buildSys(role,co,compData),[{role:"user",content:refMsg}]);const fin={...upd,[consultId]:[...upd[consultId],{role:"assistant",content:reply}]};setChats(fin);sv("cos-ch",fin);}
    catch(e){setError(e.message);showToast(e.message,"error");}finally{setLoading(false);}
  },[chats,co,compData,keys,defP,showToast]);

  // FIX BUG 2: Time Machine — cancel support + robust error handling
  const runTM=useCallback(async()=>{
    if(!tmDec.trim()||tmRun)return;
    cancelRef.current.tm=false;
    const gate=await checkSessionGate();
    if(!gate.allowed){
      showToast(`Free trial limit reached (${gate.used}/${gate.limit} sessions used). Upgrade your plan or add your own API key in Settings.`,"warning");
      return;
    }
    setTmRun(true);setTmRes("");setError(null);setTmResearchBrief("");
    setUsageFeature("Time Machine","⏳");
    const tmCur=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];
    try{
      if(cancelRef.current.tm)return;
      setTmPh("📡 Research Desk is gathering current data…");
      const rdTM=await runResearchDesk(ask,co,compData,tmDec,showToast,keys);
      const researchBrief=rdTM.brief;
      const rdGroundedTM=rdTM.grounded;
      if(cancelRef.current.tm)return;
      setTmResearchBrief(researchBrief);
      setTmPh("Running simulation…");
      const researchContext=(rdGroundedTM
        ?"\nVERIFIED RESEARCH BRIEF (current data for this simulation - use these figures and cite this brief as your source where relevant; do not re-search):\n"+researchBrief+"\n"
        :"\nRESEARCH DESK UNAVAILABLE FOR THIS SIMULATION:\n"+researchBrief+"\n\nMANDATORY: No live figure was retrieved. Every price, cost, rate, salary, or market figure you produce MUST carry the tag [ESTIMATE - UNVERIFIED]. Do not present any number as fact. Open your response with a one-line warning that this simulation is ungrounded.\n");
      let tmHandoff="";try{tmHandoff=handoffContext(WorkspaceMemory.get<DecisionHandoff>("oiq-decision-handoff"));}catch{}
    const sys="You are a Business Simulation Engine for \""+co.name+"\". "+buildCtx(co,compData)+researchContext+tmHandoff+"\nSimulate TWO parallel 12-month timelines. ALL figures in "+tmCur.sym+tmCur.code+".\nSections: Decision, Baseline Assumptions, TIMELINE A PROCEED (table: Month/Revenue/OpEx/Cash/Key Event), TIMELINE B DO NOT PROCEED (same table), Divergence Summary, Best/Worst/Black Swan scenarios, Verdict table (Expected Value A&B, Cost of Waiting per week, Reversibility, Recommendation with confidence %), First 30 Days Action Plan, Confidence & Verification (state which figures came from the VERIFIED RESEARCH BRIEF, cite it, versus which are ESTIMATE (unverified)).\n\nVERIFICATION RULE: Any price, cost, rate, or market figure must either (a) come from the VERIFIED RESEARCH BRIEF (cite it), or (b) be explicitly labeled [Assumption]. Figures taken from the VERIFIED RESEARCH BRIEF must be labeled [Retrieved Evidence]. Derived figures must show the formula and be labeled [Calculation]. Do not present invented numbers as fact.";
      const res=await ask(sys,[{role:"user",content:"Simulate: \""+tmDec+"\""}],4500);
      if(!cancelRef.current.tm){
        setTmRes(res);
        sv("cos-tm-live",{dec:tmDec,res,brief:researchBrief});
        // (usage is now logged centrally inside callAI, with the real provider)
        const session={id:Date.now(),dec:tmDec,res,brief:researchBrief,ts:new Date().toISOString()};
        setTmSessions(prev=>{const ns=[session,...prev].slice(0,20);sv("cos-tm",ns);return ns;});
      }
    }catch(err){
      if(!cancelRef.current.tm){setError(err.message);showToast("Time Machine: "+err.message,"error");}
    }finally{
      setTmRun(false);setTmPh("");cancelRef.current.tm=false;
    }
  },[tmDec,tmRun,co,compData,keys,defP,showToast]);

  // FIX BUG 3: Autopilot — cancel support + robust error handling
  const runAP=useCallback(async()=>{
    if(apRun)return;
    cancelRef.current.ap=false;
    const gate=await checkSessionGate();
    if(!gate.allowed){
      showToast(`Free trial limit reached (${gate.used}/${gate.limit} sessions used). Upgrade your plan or add your own API key in Settings.`,"warning");
      return;
    }
    setApRun(true);setApRes("");setError(null);setApResearchBrief("");
    setUsageFeature("Decision Autopilot","🤖");
    const apCur=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];
    try{
      if(cancelRef.current.ap)return;
      setApPh("📡 Research Desk is gathering current data…");
      // This used to name the company, which is unsearchable - there is nothing on
      // the web about a business with no customers yet. It now describes the MARKET.
      const researchQuestion=[co.industry||"business","market conditions, competitors, pricing, regulation and demand in",co.location||"India","for an early-stage operator"].join(" ");
      const rdAP=await runResearchDesk(ask,co,compData,researchQuestion,showToast,keys);
      const researchBrief=rdAP.brief;
      if(cancelRef.current.ap)return;
      setApResearchBrief(researchBrief);
      setApPh("Scanning all decision vectors…");
      const researchContext=(rdAP.grounded
        ?"\nVERIFIED RESEARCH BRIEF (current data for this scan - use these figures and cite this brief as your source where relevant; do not re-search):\n"+researchBrief+"\n"
        :"\nRESEARCH DESK UNAVAILABLE FOR THIS SCAN:\n"+researchBrief+"\n\nMANDATORY: No live figure was retrieved. Every price, cost, rate, salary, or market figure you produce MUST carry the tag [ESTIMATE - UNVERIFIED]. Do not present any number as fact. Open your response with a one-line warning that this scan is ungrounded.\n");
      let apHandoff="";try{apHandoff=handoffContext(WorkspaceMemory.get<DecisionHandoff>("oiq-decision-handoff"));
      const lp=(WorkspaceMemory.get<any[]>("oiq-autopilot-plans")||[]).slice(-1)[0];
      if(lp)apHandoff+="\nLATEST OPERATING PLAN (calculated, advisory) under \u201c"+lp.constraint+"\u201d: "+lp.because+" Action: "+lp.recommendedAction+" Trigger: "+lp.trigger+".";}catch{}
    const sys="You are the Decision Intelligence Engine for \""+co.name+"\". "+buildCtx(co,compData)+researchContext+apHandoff
      +(apHandoff?"\nUse the persisted Boardroom decision above as the current state: answer 'if I were operating this business under these conditions, what would I do next?' with CURRENT STATE, RECOMMENDED ACTION, WHY, EXPECTED IMPACT, RISK, TRIGGER, OWNER, DEADLINE, STOP CONDITION and ESCALATION CONDITION for each decision. Do not re-decide what the Boardroom settled unless new evidence or a challenge justifies it.":"")+"\nIdentify 6 CRITICAL decisions the founder should make RIGHT NOW. ALL figures in "+apCur.sym+apCur.code+".\nFor each: Title, Urgency, Owner, Decide By, Cost of delay/week (with calculation), Options 1/2/3 with outcomes, Recommendation, "+co.location+" Context, Data Needed. End with: THE ONE DECISION THAT MATTERS MOST THIS WEEK. Then a final section: Confidence & Verification (state which figures came from the VERIFIED RESEARCH BRIEF, cite it, versus which are ESTIMATE (unverified)).\n\nVERIFICATION RULE: Any price, cost, rate, or market figure must either (a) come from the VERIFIED RESEARCH BRIEF (cite it), or (b) be explicitly labeled [Assumption]. Figures taken from the VERIFIED RESEARCH BRIEF must be labeled [Retrieved Evidence]. Derived figures must show the formula and be labeled [Calculation]. Do not present invented numbers as fact.";
      let res=await ask(sys,[{role:"user",content:"Run complete decision scan."}],4500);
      res+=ieEvidenceAudit(res);
      if(!cancelRef.current.ap){
        setApRes(res);sv("cos-ap-live",{res,brief:researchBrief});
        // Every scan is kept. Previously each run overwrote the last one and
        // the earlier result was unrecoverable.
        try{
          const entry={id:Date.now(),ts:Date.now(),res,brief:researchBrief,stage:co.stage,company:co.name};
          setApSessions(prev=>{const ns=[entry,...prev].slice(0,30);sv("cos-ap",ns);return ns;});
        }catch{}
        // (usage is now logged centrally inside callAI, with the real provider)
      }
    }catch(err){
      if(!cancelRef.current.ap){setError(err.message);showToast("Autopilot: "+err.message,"error");}
    }finally{
      setApRun(false);setApPh("");cancelRef.current.ap=false;
    }
  },[apRun,co,compData,keys,defP,showToast]);

  // Boardroom with cancel support
  const runBR=useCallback(async()=>{
    if(!brQ.trim()||brRun)return;
    cancelRef.current.br=false;
    const gate=await checkSessionGate();
    if(!gate.allowed){
      showToast(`Free trial limit reached (${gate.used}/${gate.limit} sessions used). Upgrade your plan at orchestriq.gorakhai.com or add your own API key in Settings to continue.`,"warning");
      return;
    }
    // First submission with this set of providers: show the disclosure and stop.
    // BUG FIX - THIS IS WHY THE BOARDROOM WOULD NOT START.
    // runBR is a useCallback whose dependency array does NOT list consentNeeded,
    // so it captured the FIRST value it ever saw: true. Accepting the disclosure
    // updated the state, but runBR was never rebuilt, so it kept reading the old
    // `true` forever - modal, accept, close, click Start, modal again.
    // It cannot simply be added to the dependency array either: consentNeeded is
    // declared at line ~6568, far BELOW runBR, and a dependency array is
    // evaluated during render - that is the same "cannot access before
    // initialization" crash we hit with `cur`. A ref reads the current value
    // without creating that ordering problem.
    if(consentRef.current.needed){setShowConsent(true);return;}
    brCallBudget.current=BR_MAX_CALLS;
    setBrRun(true);setError(null);
    setUsageFeature("AI Boardroom","🏛️");
    // RESUME: if the last saved run of THIS question has research but never
    // finished (e.g. paused because no reasoning provider was available), reuse
    // its research instead of searching the web again. Editing the question
    // forces fresh research.
    let brPrior:any=null;
    try{
      const pv=WorkspaceMemory.get<any>("cos-br-live");
      if(pv&&pv.q===brQ&&String(pv.researchBrief||"").trim()&&pv.researchState
         &&!(pv.stages||[]).some((st:any)=>String(st?.synthesis||"").trim()))brPrior=pv;
    }catch{}
    // RESEARCH CACHE: a completed session for the SAME question within 7 days supplies
    // its research brief - no new web search - unless "Fresh research" is ticked.
    let brCached:any=null;
    if(!brPrior&&!brFreshResearch){
      try{brCached=(brSessions||[]).find((x:any)=>x&&x.q===brQ&&String(x.researchBrief||"").trim()&&x.ts&&(Date.now()-new Date(x.ts).getTime())<7*864e5)||null;}catch{brCached=null;}
    }
    const brSessionId=brPrior?.sessionId||Date.now();
    setBrCur({q:brQ,researchBrief:"",format:"threaded",stages:[],sessionId:brSessionId});
    const agents=brAg.map(id=>AR.find(r=>r.id===id)).filter(Boolean);
    const res=[];
    const synCur=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];
    let researchBrief="";
    let brGrounded=false;
    let brResearchState:any=null;   // PHASE 14: carried locally so later rebuilds cannot drop it
    let brIntakeRegister="";
    const domain=classifyDomain(brQ);
    // Intent was hardcoded to "decide", so a boardroom asked to ANALYSE or PLAN
    // still received decision frameworks. classifyIntent reads the actual question.
    const brIntent=classifyIntent(brQ);
    const frameworks=selectFramework(domain,brIntent);
    // Universal nine-bucket cost + viability scaffold. Industry-agnostic: the
    // archetype only hints which buckets dominate, it never changes structure.
    const brArchetype=inferArchetype(brQ+" "+(co.industry||"")+" "+(co.name||""));
    // Is this a request for a DOCUMENT rather than a decision? A business plan is
    // not a debate, so if one is being asked for, the executives are told which
    // sections are theirs and the Chairman assembles the document instead of
    // summarising an argument. When no document is detected, nothing changes.
    const brDoc=detectDocumentRequest(brQ);
    const brDocBrief=brDoc.matched?("\n\n"+buildDocumentBrief(brDoc)+"\n"):"";
    try{
      if(brDoc.matched)showToast("Producing a "+brDoc.documentName+(brDoc.bundle.length?" (part of: "+brDoc.bundleLabel+")":"")+" \u2014 executives will write to that structure.","info");
    }catch{}
    const brScaffold="\n\n"+buildScaffoldPrompt({archetype:brArchetype})+"\n\n"+buildViabilityPrompt();
    // WORKSPACE DATA BRIDGE — the Boardroom previously received only the company
    // profile, Data Hub fields and Cost Architecture. It could not see the General
    // Ledger or prior Boardroom / Time Machine / Autopilot work, so executives
    // estimated figures that were already posted in the app. getModuleContext()
    // already supplies all of this to the workflow and task paths; the Boardroom
    // simply never called it. Domain is mapped onto the category keys it expects.
    const brModCat=(domain==="finance"||domain==="audit"||domain==="legal")
      ?(domain==="legal"?"audit":domain)
      :((domain==="strategy"||domain==="executive")?"strategy_task":domain);
    let brWorkspace="";
    try{
      brWorkspace=getModuleContext(brModCat,{
        ledgerEntries,brSessions,workflows,tQueue:tQRef.current,
        tmRes,apRes,tmSessions,apSessions,cur
      })||"";
      // Finance data is decision-critical in EVERY boardroom question, not only
      // finance-category ones, so pull the ledger snapshot in regardless of domain.
      if(brModCat!=="finance"&&ledgerEntries?.length){
        const snap=buildLedgerSnapshot(ledgerEntries,cur);
        if(snap)brWorkspace="=== LIVE LEDGER DATA (real transactions from this company's General Ledger) ===\n"+snap+(brWorkspace?"\n\n"+brWorkspace:"");
      }
    }catch(e){console.warn("[OIQ] boardroom workspace bridge:",e);}
    const brWorkspaceBlock=brWorkspace.trim()
      ?"\n\n=== LIVE WORKSPACE DATA (entered by the user in this app — these are REAL figures, not estimates) ===\n"+brWorkspace.trim()+"\n=== END WORKSPACE DATA ===\nUse these figures in preference to any estimate or benchmark. Where a workspace figure contradicts a researched benchmark, say so explicitly and explain which you are relying on and why.\n"
      :"\n\nNOTE: no General Ledger entries or prior session history exist in this workspace, so no internal actuals are available. Say so where a conclusion depends on internal figures.\n";
    // ── PHASE 0: INTAKE & VALIDATION ────────────────────────────────────────
    // Establishes what is KNOWN, UNKNOWN and ASSUMED before any executive speaks,
    // so the board reasons from an honest evidence base instead of quietly
    // inventing inputs. Runs with search OFF: it may only classify what already
    // exists, never introduce a figure. Failure is non-fatal — the board proceeds
    // with an explicit warning rather than silently ungrounded.
    let brRegisterBlock="";
    try{
      if(brPrior||brCached){
        brIntakeRegister=String((brPrior||brCached).intakeRegister||"");
        brRegisterBlock=brIntakeRegister?buildRegisterInjection(brIntakeRegister):"";
      }else if(!cancelRef.current.br){
        setBrPh("🔍 Establishing what is known and unknown…");
        const brScan=scanSuppliedInputs(brQ);
        const brIntakeSys=buildIntakePrompt(brQ,buildCtx(co,compData)+brWorkspaceBlock,brScan);
        // Session 27 raised the executive budget to 4.5 tokens per word because
        // reasoning models spend output tokens THINKING before they write. This
        // literal 2200 was missed. DeepSeek V4 needs ~3000 tokens just to think,
        // so the whole budget was gone before one word of the register was
        // written - which is the console warning you sent me: "DeepSeek ran out
        // of output budget while reasoning and produced no answer."
        // 5200 matches what the research angles already use.
        const brIntakeRaw=(await (async()=>{const _r=await callStage(keys,"research_synthesis",brIntakeSys,[{role:"user",content:"Produce the evidence register now."}],5200);return _r.text;})());
        const brRegister=(brIntakeRaw&&typeof brIntakeRaw==="object"&&"text" in brIntakeRaw)?brIntakeRaw.text:String(brIntakeRaw||"");
        brRegisterBlock=buildRegisterInjection(brRegister);
        brIntakeRegister=String(brRegister||"").trim();
        setBrCur(prev=>({...prev,intakeRegister:brIntakeRegister}));
      }
    }catch(e){
      console.warn("[OIQ] phase 0 intake:",e);
      brRegisterBlock="\n\n"+INTAKE_FAILED_NOTICE+"\n";
    }
 
    try{
      // RESEARCH STEP: one search-enabled call gathers current verifiable figures
      // relevant to the question. All agents then reference this shared brief
      // instead of each searching independently (cost + consistency).
      if(brPrior){
        researchBrief=String(brPrior.researchBrief||"");
        brGrounded=!!brPrior.grounded;
        brResearchState=normalizeResearchState(brPrior.researchState);
        setBrCur(prev=>({...prev,researchBrief,grounded:brGrounded,researchState:brResearchState,intakeRegister:brIntakeRegister}));
        try{showToast("Resuming the paused investigation using its saved research. Web research is NOT being repeated. (Edit the question if you want fresh research.)","info");}catch{}
      }else if(brCached){
        // New session, cached evidence: decomposition and brief are reused; the previous
        // session's executive conclusions are NOT carried in (a fresh board debates it).
        researchBrief=String(brCached.researchBrief||"");
        brGrounded=!!brCached.grounded;
        const cRS=brCached.researchState||{};
        brResearchState=normalizeResearchState({question:brQ,decomposition:cRS.decomposition||null,researchBrief,grounded:brGrounded,provider:cRS.provider,
          modelRegistry:cRS.modelRegistry||null,pendingUserQuestions:(cRS.pendingUserQuestions||[]).filter((u:any)=>u.status==="answered")});
        setBrCur(prev=>({...prev,researchBrief,grounded:brGrounded,researchState:brResearchState,intakeRegister:brIntakeRegister}));
        try{showToast("Reusing research from "+new Date(brCached.ts).toLocaleDateString("en-IN")+" for this question (no new web search). Tick \u201cFresh research\u201d to search again.","info");}catch{}
      }else if(!cancelRef.current.br){
        setBrResearching(true);
        setBrPh("📡 Research Desk is gathering current data…");
        const rdBR=await runResearchDesk(ask,co,compData,brQ,showToast,keys);
        researchBrief=rdBR.brief;
        brGrounded=rdBR.grounded;
        // PHASE 14: a structured Research State, kept with the session - not just
        // the brief text. Follow-ups read this instead of starting from zero.
        brResearchState=normalizeResearchState({question:brQ,decomposition:(rdBR as any).decomposition||null,
          researchBrief,grounded:rdBR.grounded,provider:rdBR.provider});
        setBrCur(prev=>({...prev,researchBrief,grounded:rdBR.grounded,researchState:brResearchState}));
        setBrResearching(false);
      }
      // Same wording as before; the brief part is now a parameter so each executive
      // can receive only the evidence relevant to its mandate.
      const mkResearchCtx=(briefPart:string)=>(brGrounded
        ?"\nVERIFIED RESEARCH BRIEF (current data gathered for this debate - use these figures and cite this brief as your source; do not re-search):\n"
          +briefPart
          +"\n\nEVIDENCE PROTOCOL (non-negotiable): The Research Brief above is your STARTING POINT, not your boundary. You have live web search and you are REQUIRED to independently verify or source the figures that matter most within your own research remit. Rules: (a) a figure taken from the brief is cited as 'per Research Brief'; (b) a figure you retrieve yourself must carry the source name and its URL; (c) a figure you cannot source carries [ESTIMATE - UNVERIFIED] and states the basis of the estimate; (d) an untagged number presented as fact is a failure of your role; (e) if a prior speaker stated a figure you believe is wrong, verify it and present the corrected figure with its source. Prefer vendor, government, regulator and recognised research-firm sources over blogs and aggregators.\n"
        :"\nRESEARCH DESK UNAVAILABLE FOR THIS DEBATE:\n"+briefPart
          +"\n\nMANDATORY: No live figure was retrieved for this session. Every price, cost, rate, salary, valuation, or market figure in this debate MUST carry the tag [ESTIMATE - UNVERIFIED]. Do not present any number as fact. If a prior speaker presented an untagged number, challenge it explicitly before continuing.\n"
        )+buildDecisionHistoryContext(brQ);
      const researchContext=mkResearchCtx(researchBrief);
      // Per-provider failure state for this whole run: a provider that fails
      // permanently for one executive is not retried for the next one.
      const brProvState:any={};
      let brNoProvider:string|null=null;
      // CONTEXT INTELLIGENCE: evidence with provenance (F#/S# ids), a compact
      // Decision Ledger instead of raw prior answers, and a log of every call.
      const brEvidence=parseResearchEvidence(researchBrief);
      const brLedger:LedgerEntry[]=[];
      const brCtxLog:any[]=[];
      // DECISION LEDGER V2: shared intelligence state - seeded with opportunities the
      // research itself surfaces (unrequested, with provenance), then grown after each
      // executive. A resumed session continues its existing state (stable ids).
      const brDims:string[]=(brResearchState&&brResearchState.dimensions)||[];
      let brIntel:IntelligenceState=brResearchState&&brResearchState.intelligence?JSON.parse(JSON.stringify(brResearchState.intelligence)):emptyIntelligence();
      brIntel=discoverResearchOpportunities(brIntel,brEvidence,brDims);
      // Value-chain lens for opportunity discovery, built from THIS problem's own
      // decomposition (nothing industry-specific is hard-coded).
      // SIMULATIONS FROM EARLIER RUNS of this question (Time Machine / cockpit) - shown as
      // SIMULATION RESULTS, never as facts.
      let brSimCtx="";
      try{const hh=WorkspaceMemory.get<DecisionHandoff>("oiq-decision-handoff");if(hh&&hh.question===brQ)brSimCtx=handoffContext(hh);}catch{}
      // CANONICAL USER INPUTS: the numbers the user gave, captured deterministically from
      // the question. Executives must use them exactly and flag (never replace) disagreement.
      let brCanon="";
      try{const pu=parseUserInputs(brQ);if(pu.inputs.length){brCanon="\n\nYOUR CLIENT'S NUMBERS (canonical - from the question; use exactly these, never restate them differently; if you believe one is wrong, say so explicitly and keep the original visible):\n"
        +pu.inputs.map((u:any)=>"- "+u.label+": "+fmtCanon(u.value,u.unit)+(u.ambiguous?" [AMBIGUOUS - "+u.interpretation+"; analyse BOTH readings, do not pick one]":"")).join("\n");}}catch{}
      const brOppLens=brCanon+brSimCtx+"\n\nOPPORTUNITY LENS: Beyond the literal question, inspect each stage of this problem's value chain"
        +(brDims.length?" ("+brDims.join(" \u2192 ")+")":"")+" for underserved needs, costly inefficiencies, delays, risks or information gaps that someone would pay to solve. "
        +"Mark each genuine one on its own line starting [Opportunity], with the evidence it rests on (cite F#/S# ids where available) - or label it [Expert Inference] if it is your reasoning. "
        +"An opportunity is not a recommendation: record it even if you advise against pursuing it. Do not invent novelty. "
        +"Mark each fact you need but cannot verify on its own line starting [Evidence Gap].";
      // RESPONSE CONTRACT - the final instruction each executive reads, so its own
      // identity and mandate come last (after other executives' ledger statements).
      const brContract=(a:any)=>"\n\nRESPONSE CONTRACT - you are the "+a.t+" ("+(a.d||a.dl||"")+"). Answer strictly from this mandate. "
        +"The Decision Ledger and intelligence state above contain OTHER executives' statements: never reproduce their text - refer to them by name or DL id. "
        +"Use these sections (at most 7): 1) POSITION (2-4 sentences); 2) KEY FINDINGS (tag each claim [Verified Fact] / [Expert Inference] / [Assumption] / [Estimate]); "
        +"3) OPPORTUNITIES & RISKS; 4) CONTRADICTIONS & EVIDENCE GAPS; 5) WHAT I NEED FROM THE USER (as questions); 6) RECOMMENDATION; 7) CONFIDENCE & LIMITATIONS.";
      for(let i=0;i<agents.length;i++){
        if(cancelRef.current.br){showToast("Boardroom cancelled","warning");break;}
        const ag=agents[i];const p=EP[ag.id]||{};
        // (Raw prior answers are no longer injected - see the Decision Ledger below.)
        // Context-size skips are re-evaluated per executive: a provider too small
        // for one prompt may fit the next one.
        for(const k of Object.keys(brProvState)){ if(/prompt too long/.test(brProvState[k].label))delete brProvState[k]; }
        const mandateText=[ag.t,ag.f,ag.d,ag.dl,(p as any).m,(BOARD_RESEARCH_REMIT as any)[ag.id]].filter(Boolean).join(" ");
        setBrPh(ag.ic+" "+ag.t+" is analyzing…");announceBoardroomPhase(ag.t+" is analyzing");
        const mkSys=(researchCtx:string,prior:string)=>"You are "+ag.f+" at \""+co.name+"\".\n"+buildBoardIdentity(ag,p)+CLARITY_PROTOCOL+"\n"+buildCtx(co,compData)+researchCtx+"\nBUSINESS DOMAIN: "+domain+"\nANALYTICAL FRAMEWORKS AVAILABLE: "+frameworks.map(f=>f.name+" ("+f.reason+")").join("; ")+"\nFRAMEWORK REQUIREMENT: you MUST explicitly apply at least one named framework and SHOW ITS OUTPUT — the populated table, the calculation, the scored matrix, the register. Naming a framework without producing its output does not count. Choose the framework that answers a specific question you actually need answered; if none of the above fits, name and apply a more appropriate one and say why you chose it. Never insert a framework to look thorough.\n"+brDocBrief+fxRuleBlock()+brScaffold+brWorkspaceBlock+brRegisterBlock+"\n"+"LIVE BOARDROOM DEBATE. "+(i===0?"Speak first. State your opening position with specific calculations in "+synCur.sym+".\n\n"+"EVIDENCE RULES — label every key statement with one of these tags:\n"+"[Verified Fact] — ONLY permitted when you give BOTH a named source AND a URL on the same line. No URL means it is NOT a verified fact, however confident you are.\n"+"[Recalled — Unverified] — you believe it from prior knowledge but cannot produce a source URL. Use this instead of [Verified Fact] whenever the URL is missing.\n"+"[Assumption] — an assumption you are making, stated explicitly\n"+"[Expert Inference] — reasoned from your domain expertise\n"+"[Estimate] — unverified figure, labeled as such\n"+"Never present an invented number without a label. Tagging a recalled figure as a Verified Fact is the most serious error you can make in this boardroom.":"Previous contributions:\n"+prior+"\n\n"+"YOUR TURN as "+ag.f+".\n"+"Step 1: Conduct your own independent analysis of the question from your "+ag.dl+" perspective, and discharge your standing mandate in full. If your mandate requires a financial model, build it. If it requires a process or capacity map, produce it. If it requires a risk or regulatory register, write it. Another executive having touched a topic does NOT relieve you of your own analysis of it.\n"+"Step 2: You MAY re-derive, recompute, or rebuild any figure a prior speaker presented. If your figure differs from theirs, show both side by side and explain the reason for the gap.\n"+"Step 3: Where an input you need is missing, name the missing variable and state how it changes your conclusion. Do not assume through a gap silently.\n"+"Step 4: Only after presenting your own analysis, state where it contradicts a prior speaker and why yours is better founded.\n\n"+"EVIDENCE RULES — label every key statement:\n"+"[Verified Fact] [Assumption] [Expert Inference] [Estimate]\n"+"If you have nothing genuinely new to add, say so in 2-3 sentences.")+"\nLENGTH AND SCOPE — HARD LIMITS, not suggestions:\n"+"You have approximately "+boardWordBudget(ag)+" words. You will be cut off at that point mid-sentence, so plan the whole response to fit and reach your conclusion inside it.\n"+"Produce AT MOST 8 sections. No appendices, annexures or addenda.\n"+"Stay strictly inside your own functional mandate — never write another executive\u2019s deliverable. A CFO does not write a marketing roadmap; a CTO does not write a hiring plan.\n"+"Depth means the reasoning behind your numbers, NOT more sections. One well-derived figure beats ten listed ones.\n"+"Never state or estimate your own word count.\n\n"+"VERIFICATION RULE: For any price, cost, rate, fee, salary benchmark, or market figure, use the VERIFIED RESEARCH BRIEF above where relevant (cite it as 'per Research Brief'). If you need a figure not covered by the brief and cannot verify it, label it [Estimate (unverified)]. Never present an invented number as fact.";
        let agText="";
        let agTruncated=false;
        let gotResponse=false;
        // IMMUTABLE EXECUTION IDENTITY for this executive's contribution.
        const agCallId=makeCallId(brSessionId,"stage-1",ag.id);
        let agProvider="";let agModel="";let agLastSystem="";let agLastBudget=boardMaxTokens(ag);
        // STRUCTURED PROVIDER ATTEMPTS (execution diagnostics - never shown as reasoning).
        const agAttempts:ProviderAttempt[]=[];
        // ── SMART RETRY: provider failover + pause-and-resume ──────────────
        // Cycle 1: try primary. On limit → try alternates → wait 65s → retry.
        // Accumulates partial text so responses never break mid-sentence.
        const isContextOrRateErr=(msg:string)=>isRateLimit(msg)||
          msg.toLowerCase().includes("context")||
          msg.toLowerCase().includes("reduce")||
          msg.toLowerCase().includes("maximum")||
          msg.toLowerCase().includes("413");
        let cyclesDone=0;
        // The real reason the last provider failed. Previously discarded, so
        // the user only ever saw a generic "at limit" message.
        let lastProvErr="";
        let sawRetryableErr=false;
        // SECURITY H-2: retries were bounded per executive but NOT globally.
        // 9 executives x 3 cycles x N providers x 2 continuations is an
        // unbounded-in-practice spend from a single click. One ceiling for the
        // whole session, so a stuck provider cannot run up a bill.
        if(brCallBudget.current<=0){
          agText="\u26a0 **Session call limit reached.**\n\nThis board session has already made "+BR_MAX_CALLS+" AI requests, which is the safety ceiling. Reduce the number of executives or switch off the Research Desk, then run it again.";
          gotResponse=true;
          break;
        }
        while(!gotResponse&&!cancelRef.current.br&&cyclesDone<3&&brCallBudget.current>0){
          cyclesDone++;
          // Build prompt: first attempt uses original question;
          // subsequent attempts ask to continue from partial text.
          const userMsg=agText.trim()
            ?("You are mid-response. You have said so far:\n\n"+agText
              +"\n\nContinue EXACTLY from where you stopped. Do not repeat anything above. Continue seamlessly.")
            :brQ;
          // Build list of providers to try this cycle
          // Gated: the debate failover chain now skips switched-off providers.
          const brKeys=enabledKeys(keys);
          // THE FIX: this was Object.keys(brKeys), i.e. AI_PROVIDER_IDS order -
          // nvidia, claude, openai, gemini, groq, deepseek. Every executive tried
          // Claude then OpenAI before ever reaching DeepSeek, and the image
          // providers (fal, stability) were in the list too. Now: the provider
          // chosen for the "executive" stage in Settings, then cheapest-first.
          const allProviders=costOrderedProviders(keys,"executive").map(r=>r.provider);
          // If this list is empty, no request is ever sent and every executive
          // reports "did not produce an answer" for a reason that has nothing to
          // do with the provider. Say what is actually wrong instead.
          if(!allProviders.length){
            agText="\u26a0 **No provider permitted for executive analysis is available.**\n\nYour routing mode allows only: "+(costOrderedProviders(keys,"executive").map(r=>r.provider).join(", ")||"none currently enabled")+". Economy and Balanced never send executive work to OpenAI or Claude without your permission, so nothing was sent and nothing was charged.\n\nFix: turn DeepSeek or Gemini back on, or choose a premium provider for Executive analysis in Settings \u2192 Model Routing.";
            gotResponse=true;
            break;
          }
          let cycleSuccess=false;
          for(const prov of providersUsableNow(allProviders,brProvState,Date.now())){
            let agAttempt:ProviderAttempt|null=null;let agT0=0;
            if(cancelRef.current.br)break;
            const pKey=brKeys[prov]||"";
            if(!pKey.trim())continue;
            try{
              setBrPh(ag.ic+" "+ag.t+(agText?" — resuming via "+prov+"…":" is analyzing… ("+prov+" \u00b7 "+providerTier(prov)+(allProviders.indexOf(prov)>0?" \u00b7 fallback":" \u00b7 primary")+")"));
              const agBudget=adaptiveOutputBudget(boardMaxTokens(ag),taskKindFor({isFirstSpeaker:i===0,dimensionCount:brDims.length}));
              const ctxA=assembleExecutiveContext({provider:prov,evidence:brEvidence,mandateText,ledger:brLedger,intel:brIntel,userText:userMsg,
                maxOutputTokens:agBudget,build:(evB:string,ledB:string)=>mkSys(mkResearchCtx(evB),ledB||"(no earlier contributions yet)")+brOppLens+brContract(ag)});
              brCtxLog.push({executive:ag.t,provider:prov,estInput:ctxA.check.estInput,requestedOutput:ctxA.check.requestedOutput,
                estTotal:ctxA.check.estTotal,limit:ctxA.check.limit,margin:ctxA.check.margin,basis:ctxA.check.basis,strategy:ctxA.strategy,
                evidenceIncluded:ctxA.evidenceIncluded,evidenceTotal:ctxA.evidenceTotal,rawPriorOutputsIncluded:ctxA.rawPriorOutputsIncluded,
                ledgerEntries:ctxA.ledgerEntries,ledgerTokens:ctxA.ledgerTokens,sent:ctxA.fits,outputBudget:agBudget});
              if(!ctxA.fits){
                agAttempts.push({provider:prov,model:(MODELS as any)[prov]?.model||prov,reason:agAttempts.length?"fallback":"primary",startedAt:new Date().toISOString(),durationMs:0,
                  estimatedInputTokens:ctxA.check.estInput,requestedOutputTokens:agBudget,result:"context_limit",detail:"pre-check: not sent"});
                // Not sent: even fully compacted it exceeds this provider. A skip, not a retry.
                brProvState[prov]={kind:"permanent",label:"prompt too long (pre-check)",retryAt:Infinity};
                lastProvErr=prov+": prompt too long for this provider even after compaction (~"+ctxA.check.estTotal+" > "+ctxA.check.usable+" usable tokens; not sent)";
                continue;
              }
              agLastSystem=ctxA.system;agLastBudget=agBudget;
              agT0=Date.now();
              agAttempt={provider:prov,model:(MODELS as any)[prov]?.model||prov,reason:agAttempts.length?"fallback":"primary",startedAt:new Date(agT0).toISOString(),durationMs:0,
                estimatedInputTokens:ctxA.check.estInput,requestedOutputTokens:agBudget,result:"other"};
              brCallBudget.current--;
              const replyFull=await callAI(prov,pKey,ctxA.system,[{role:"user",content:userMsg}],agBudget,boardCanSearch(prov)&&!agText.trim())
              // A provider can return HTTP 200 with an EMPTY body - DeepSeek does this
              // when the input is large relative to the output budget. The old code
              // accepted that as success, broke out of the loop, and wrote a BLANK
              // executive card. Seven of nine executives came back blank this way.
              // An empty answer is now a failure: try the next provider, then retry.
              const gotText=String(replyFull?.text||"").trim();
              if(gotText.length<40){
                try{showToast(ag.t+": "+prov+" returned an empty answer - trying another provider...","warning");}catch{}
                markProviderExhausted(prov);
                continue;
              }
              // Resuming mid-response: append only the new part (no repeated ending).
              agText=agText?appendContinuation(agText,replyFull.text).text:replyFull.text;
              agTruncated=!!replyFull.truncated;
              agProvider=prov;agModel=(MODELS as any)[prov]?.model||prov;
              if(agAttempt)agAttempts.push({...agAttempt,durationMs:Date.now()-agT0,result:"success"});
              gotResponse=true;
              cycleSuccess=true;
              delete brProvState[prov];
              break;
            }catch(provErr:any){
              lastProvErr=prov+": "+String(provErr?.message||provErr);
              // Classified individually. Was: any rate/quota/limit wording set ONE
              // global flag and every provider - including 402/403 ones - was
              // retried after a 65s countdown labelled "all providers at limit".
              const pst=recordProviderFailure(brProvState,prov,String(provErr?.message||provErr),Date.now());
              if(agAttempt)agAttempts.push({...agAttempt,durationMs:Date.now()-agT0,result:attemptResultFor(pst.label+" "+String(provErr?.message||"")),detail:pst.label});
              if(pst.kind==="retryable")markProviderExhausted(prov);
              continue; // next provider; this one is retried only if its own state allows
            }
          }
          if(cycleSuccess||cancelRef.current.br)break;
          // Re-evaluate every cycle: nothing that can recover -> stop now;
          // otherwise wait only for the provider(s) that can, as long as they said.
          const brWaitMs=nextRetryWaitMs(allProviders,brProvState,Date.now());
          if(brWaitMs===null){
            brNoProvider=providerStateSummary(allProviders,brProvState,Object.fromEntries(allProviders.map(p=>[p,(MODELS as any)[p]?.name||p])));
            break;
          }
          if(cyclesDone<3&&brWaitMs>0){
            const waitingFor=allProviders.filter(p=>brProvState[p]&&brProvState[p].retryAt!==Infinity)
              .map(p=>((MODELS as any)[p]?.name||p)+" ("+brProvState[p].label+")").join(", ");
            await waitWithCountdown(Math.max(1,Math.ceil(brWaitMs/1000)),(sec)=>{
              setBrPh(ag.ic+" "+ag.t+" \u2014 waiting for "+waitingFor+". Retrying in "+sec+"s\u2026 (Cancel to stop)");
            },()=>cancelRef.current.br);
            if(cancelRef.current.br)break;
          }
        }
        // If all 3 cycles failed, record placeholder
        // Final safety net: a card must never render empty. If every provider and
        // every retry produced nothing, say so plainly on the card itself.
        if(!gotResponse&&!cancelRef.current.br&&!brNoProvider){
          const chE=costOrderedProviders(keys,"executive").map(r=>r.provider);
          if(nextRetryWaitMs(chE,brProvState,Date.now())===null)
            brNoProvider=providerStateSummary(chE,brProvState,Object.fromEntries(chE.map(p=>[p,(MODELS as any)[p]?.name||p])));
        }
        if(brNoProvider)break; // stop the board; nothing can answer - see the pause below
        if(!gotResponse||String(agText||"").trim().length<40){
          agText=(String(agText||"").trim().length>=40?"[Response incomplete — API limit reached]\n\n"+agText
            :"\u26a0 **"+ag.t+" did not produce an answer.**\n\nEvery configured provider either failed or returned an empty response for this executive."+(lastProvErr?"\n\n**Actual error:** "+lastProvErr.slice(0,500):"")+"\n\nWhat to try: reduce the number of executives, switch off the Research Desk for this run, or add a second provider key in Settings. This executive contributed nothing to the synthesis below.");
          agTruncated=false;
          gotResponse=true;
          showToast(ag.t+" paused — API limit. Adding a second provider key in Settings prevents this.","warning");
        }
        if(cancelRef.current.br)break;
        // Session 8B cut continuations from 3 to 1 to stop 15,000-word runaways.
        // That fixed the runaway but overcorrected: the CHRO was cut mid-sentence.
        // The runaway came from the old "continue" wording inviting fresh material,
        // not from the attempt count - so 2 attempts with "finish now" is safe.
        let contAttempts=0;
        while(agTruncated&&contAttempts<2&&!cancelRef.current.br){
          contAttempts++;
          setBrPh(ag.ic+" "+ag.t+" is continuing response… (part "+(contAttempts+1)+")");
          try{
            const contSys="You are "+ag.f+" at \""+co.name+"\". You were speaking in a boardroom debate and your response was cut off. Here is what you wrote so far:\n\n"+agText+"\n\nContinue EXACTLY from where you left off. Do not repeat anything already written. Do not restart. Pick up mid-sentence if needed.";
            const cont=(await (async()=>{const _r=await callStage(keys,"executive",contSys,[{role:"user",content:"Finish your response now. Close out your current point, complete any table you started, state your conclusion, and stop. Do not open a new section."}],1800,brProvState);return {primary:_r.text,truncated:_r.truncated};})());
            agText=appendContinuation(agText,cont.primary).text;
            agTruncated=!!cont.truncated;
          }catch(contErr:any){
            // Same per-provider decision as the main loop (was a keyword match that
            // re-tried every provider, 402/403 ones included, behind "API limit").
            const chC=costOrderedProviders(keys,"executive").map(r=>r.provider);
            const wC=nextRetryWaitMs(chC,brProvState,Date.now());
            if(wC===null||wC>30000){
              agTruncated=false; // nothing can recover soon: keep the partial answer, stop continuing
            }else if(wC>0){
              const waitingForC=chC.filter(p=>brProvState[p]&&brProvState[p].retryAt!==Infinity)
                .map(p=>((MODELS as any)[p]?.name||p)+" ("+brProvState[p].label+")").join(", ");
              await waitWithCountdown(Math.max(1,Math.ceil(wC/1000)),(sec)=>{
                setBrPh(ag.ic+" "+ag.t+" is continuing \u2014 waiting for "+waitingForC+". Retrying in "+sec+"s\u2026");
              },()=>cancelRef.current.br);
            }
          }
        }
        // DUPLICATE GUARD: an answer near-identical to an earlier executive's is an
        // echo, not an independent analysis. Regenerate ONCE from this executive's own
        // mandate; if it is still a duplicate, mark it and keep it out of synthesis.
        const brPriors=res.filter((r:any)=>!isExcludedFromSynthesis(r.status||"COMPLETE")).map((r:any)=>({executiveId:r.ag.id,executiveKey:r.ag.t,text:r.fullText||r.text}));
        let agDup=agText&&!/^\u26a0 \*\*/.test(String(agText))?findDuplicate(agText,brPriors):null;
        if(agDup&&agLastSystem&&!cancelRef.current.br&&brCallBudget.current>0){
          try{
            showToast(ag.t+"'s answer was "+Math.round(agDup.similarity*100)+"% identical to "+agDup.executiveKey+"'s \u2014 regenerating once from the "+ag.t+"'s own mandate.","warning");
            brCallBudget.current--;
            const regen=await callStage(keys,"executive",agLastSystem+"\n\nINTEGRITY CHECK: your previous draft repeated the "+agDup.executiveKey+"'s analysis almost word for word. Write your OWN "+ag.t+" analysis strictly from your mandate ("+(ag.d||ag.dl||"")+"). Refer to other executives only by name or DL id; never reproduce their text.",[{role:"user",content:brQ}],agLastBudget,brProvState);
            if(!findDuplicate(regen.text,brPriors)){agText=regen.text;agTruncated=regen.truncated;agProvider=regen.provider;agModel=(MODELS as any)[regen.provider]?.model||regen.provider;agDup=null;}
          }catch{}
        }
        const agStatus=deriveStatus({text:agText,failed:/^\u26a0 \*\*/.test(String(agText||"")),noProvider:!!brNoProvider,truncated:agTruncated,continuationAttempts:contAttempts,duplicateOf:agDup?agDup.executiveKey:null});
        // The stored contribution: full text is canonical and never shortened; the
        // identity travels with it so it can only ever be shown under this executive.
        res.push({ag,text:agText,fullText:agText,truncated:agTruncated,status:agStatus,duplicateOf:agDup?agDup.executiveKey:null,
          identity:{sessionId:brSessionId,stageId:"stage-1",executiveId:ag.id,executiveKey:ag.t,callId:agCallId,provider:agProvider||"none",model:agModel||""},
          attempts:agAttempts,execution:agAttempts.length?executionSummary(agAttempts):"",
          metrics:{requestedOutput:agLastBudget,chars:String(agText||"").length,continuationRequired:contAttempts>0,continuationAttempts:contAttempts,continuationSucceeded:contAttempts>0&&!agTruncated}});
        // DECISION LEDGER: verbatim, attributed extract of this answer. Placeholder
        // cards (no real answer) never become ledger content.
        if(!isExcludedFromSynthesis(agStatus)){
          const brEntry=extractLedgerEntry(ag.t,ag.id,agText,brEvidence.sources);
          brLedger.push(brEntry);
          brIntel=mergeIntoIntelligence(brIntel,brEntry,agText,brEvidence,{question:brQ,dimensions:brDims});
          brResearchState=normalizeResearchState({...(brResearchState||{question:brQ,researchBrief,grounded:brGrounded}),intelligence:brIntel});
        }
        // Update threaded format during debate so cards render as they arrive
        const runningStage={stageNumber:1,type:"original",question:brQ,
          executiveIds:brAg,debate:[...res],synthesis:"",
          decisionStatus:null,completedAt:null,frozen:false,ledger:[...brLedger],contextLog:[...brCtxLog]};
        const updatedCur={q:brQ,researchBrief,format:"threaded",stages:[runningStage],sessionId:brSessionId,
          grounded:brGrounded,intakeRegister:brIntakeRegister,researchState:brResearchState};
        setBrCur(updatedCur);
        sv("cos-br-live",updatedCur);
      }
      if(brNoProvider&&!cancelRef.current.br){
        // PAUSED, NOT LOST: research brief + Research State saved with the session;
        // re-running the same question resumes from here without new web research.
        const pausedStage={stageNumber:1,type:"original",question:brQ,executiveIds:brAg,debate:[...res],synthesis:"",
          decisionStatus:null,completedAt:null,frozen:false,ledger:[...brLedger],contextLog:[...brCtxLog]};
        const pausedInfo={reason:brNoProvider,at:new Date().toISOString()};
        const pausedCur={q:brQ,researchBrief,format:"threaded",stages:[pausedStage],sessionId:brSessionId,
          grounded:brGrounded,intakeRegister:brIntakeRegister,researchState:brResearchState,paused:pausedInfo};
        setBrCur(pausedCur);
        try{sv("cos-br-live",pausedCur);}catch{}
        const pausedSession={id:brSessionId,q:brQ,agents:brAg,format:"threaded",stages:[pausedStage],researchBrief,
          ts:new Date().toISOString(),grounded:brGrounded,intakeRegister:brIntakeRegister,researchState:brResearchState,paused:pausedInfo};
        const nsP=[pausedSession,...brSessions.filter((x:any)=>x.id!==brSessionId)].slice(0,20);setBrSessions(nsP);sv("cos-br",nsP);
        const econ=!PREMIUM_PROVIDERS.includes((STAGE_PROFILE as any)?.executive);
        showToast("Boardroom reasoning paused: no "+(econ?"economical ":"")+"reasoning provider permitted by your routing mode is currently available.\n"
          +brNoProvider+"\n"+(String(researchBrief||"").trim()?"Your web research was retrieved and is saved. ":"")
          +"Run the same question again once a provider is available \u2014 the saved research will be reused, not searched again.","warning");
        setBrPh("");
      }
      if(!cancelRef.current.br&&res.length>0&&!brNoProvider){
        setBrPh("Synthesizing consensus…");
        let allPos=res.map(r=>r.ag.t+":\n"+r.text).join("\n\n---\n\n");
        const synSys="You are Chief of Staff at "+JSON.stringify(co.name)+". "+buildCtx(co,compData)+researchContext+"\nBUSINESS DOMAIN CLASSIFIED: "+domain+"\nRECOMMENDED FRAMEWORKS (for reference — apply where relevant to strengthen the synthesis): "+frameworks.map(f=>f.name).join(", ")+"\n\nSynthesize the boardroom debate into a board-ready executive report. Use this EXACT format with all sections present:\n\n"+"# Executive Summary\n"+"(3-4 sentences: the single decision, headline number in "+synCur.sym+", recommended action)\n\n"+"## Business Domain\n"+"Domain: "+domain+" | Frameworks referenced: "+frameworks.map(f=>f.name).join(" · ")+"\n\n"+"## Key Insights\n"+"(4-6 bullet points, each opening with a bold keyword. New synthesis only — do not restate individual exec arguments.)\n\n"+"## Conflicts Resolved\n"+"| Disagreement | Position A (who) | Position B (who) | RULING | Why | What would change this ruling |\n|---|---|---|---|---|---|\n"+"(One row per genuine disagreement. You are the ARBITRATOR, not a reporter: for every row you MUST state which position the board adopts and the evidence reason. Both are right is NOT a ruling and is not permitted — if the evidence genuinely cannot separate the two positions, rule for the one that fails more cheaply if it turns out wrong, and say that is why you ruled that way. If the executives did not disagree anywhere, say so explicitly and flag it as a warning sign that the debate lacked real challenge.)\n\n"+"## Evidence Quality Review\n"+"(Review the evidence labels used in the debate. List any [Assumption] or [Estimate] that materially affects the recommendation and note what validation is needed.)\n\n"+"## Cost Architecture\n"+"| Cost bucket | Low | Expected | High | Fixed/Variable | Reducible? | Lever |\n|---|---|---|---|---|---|---|\n"+"(Consolidate the executives nine-bucket work into ONE agreed cost stack. Where two executives gave different figures for the same bucket, choose one and note the other in brackets. Omit buckets that genuinely do not apply, but state which you omitted and why.)\n\n"+"## Break-Even and Viability\n"+"(State each of these with its formula shown: contribution per unit, contribution margin percent, total fixed cost per period, break-even volume, break-even revenue, and time to reach it. If an input is unknown, say which, and give the break-even at the low and the high end of its plausible range instead of a single false number.)\n\n"+"## What We Still Do Not Know\n"+"(Carry forward the UNKNOWN items from the Phase 0 evidence register that were NOT resolved during the debate. For each: the variable, why it is load-bearing, the cheapest way to obtain it, and how the recommendation changes if it lands at the bad end.)\n\n"+"## Quantified Recommendation\n"+"(Single recommended path. Show: formula, assumption, result for every figure in "+synCur.sym+")\n\n"+"## Financial Impact\n"+"| Phase | Actions | Investment "+synCur.sym+" | Expected Return | Owner |\n|-------|---------|--------------------------|-----------------|-------|\n"+"(30-60-90 day plan, one row per phase)\n\n"+"## Risk Register\n"+"| Risk | Likelihood | Impact | Mitigation | Owner |\n|------|------------|--------|------------|-------|\n"+"(max 5 rows)\n\n"+"## Opportunities\n"+"(3-5 bullets, each with upside in "+synCur.sym+", timeframe, and owner)\n\n"+"## This Week's Decision\n"+"(Single action required now. Cost of inaction: "+synCur.sym+" per week. Owner and deadline.)\n\n"+"## Recommendations\n"+"| Priority | Action | Impact | Effort | Deadline |\n|----------|--------|--------|--------|----------|\n"+"(ranked by priority)\n\n"+"## Sources and References\n"+"(every figure cited: Source name, figure, URL or evidence label)\n\n"+fxRuleBlock()+"FORMATTING RULES: Bold all key metrics. Use tables for all numbers. Never write unbroken paragraph blocks. Every number must have a unit ("+synCur.sym+" or %). Under 2600 words. All sections must be present and complete. Figures from VERIFIED RESEARCH BRIEF: cite source and URL. All others: label [Assumption] or [Estimate (unverified)].\n\nDECISION STATUS (mandatory final line). GATING RULE — apply this BEFORE you choose:\nCount the load-bearing figures in your Quantified Recommendation, meaning the figures the decision actually rests on. If MORE THAN HALF of them carry [Assumption], [Estimate] or [Recalled — Unverified] rather than a real source URL, you MAY NOT choose Proceed or Proceed with Conditions. You must choose Needs More Information, and the What We Still Do Not Know section becomes the primary output of this report. Confidence is earned by evidence, not by tone.\nThen write exactly:\nDECISION STATUS: [choose one: Proceed | Proceed with Conditions | Needs More Information | Do Not Proceed | No Consensus]\nReason: [one sentence explaining this status, and if the gating rule forced you to Needs More Information, say so and name the unverified figures]\n\nBOARD KPIS (mandatory, after DECISION STATUS). Output exactly this block and nothing after it:\n===BOARD_KPIS===\n[{\"label\":\"SHORT UPPERCASE LABEL\",\"value\":\"figure with unit\",\"why\":\"max 7 words on why this is the number the board must watch\"}]\n===END_KPIS===\nRules: exactly 4 objects. Choose the 4 figures that most determine the decision — capital required, break-even point, headline return, and the single largest risk figure. Value must be a complete figure with its unit, never a fragment. Label must describe the figure, never a job title. Only use figures that appear in your synthesis above.\n\nCRUX (mandatory, after the KPI block):\n===CRUX===\n(3 sentences: the decision, the number that drives it, and the one condition that must hold. No markdown.)\n===END_CRUX===";
        // When a document was requested, the Chairman's generic debate template is
        // replaced by that document's own required structure. This is the fix for a
        // business plan arriving as a debate summary with empty headings.
        let synSysFinal=brDoc.matched?(synSys+"\n\n"+buildSynthesisOverride(brDoc,synCur.sym)):synSys;
        // SYNTHESIS FROM VALIDATED CONTRIBUTIONS: every selected executive appears in a
        // deterministic completeness report; identity-mismatched, duplicate or failed
        // contributions are excluded (and named), partial ones are included and labelled.
        const brRows=completenessReport(res,agents.map((a:any)=>({executiveId:a.id,executiveKey:a.t})),"stage-1");
        const brIncluded=res.filter((r:any)=>!isExcludedFromSynthesis(r.status||"COMPLETE")&&validateContribution(r,{executiveId:r.ag.id,stageId:"stage-1"}).valid);
        // SYNTHESIS PACKET: the Chairman reasons over the structured packet (positions,
        // evidence-linked claims, canonical model, contradictions, gaps, user questions)
        // rather than every executive's full essay. Full texts stay in the transcript,
        // exports and "Read full analysis".
        const brSynCur={q:brQ,researchBrief,researchState:{...(brResearchState||{}),intelligence:brIntel},stages:[{question:brQ,debate:res}]};
        const brFullDebateTokens=Math.ceil(brIncluded.map((r:any)=>String(r.fullText||r.text||"")).join("").length/4);
        allPos=synthesisPacketFor(brSynCur,0,{location:co.location||""});
        brCtxLog.push({executive:"Chairman synthesis (packet)",provider:"-",strategy:"structured synthesis packet",rawPriorOutputsIncluded:0,
          estInput:Math.ceil(allPos.length/4),fullDebateTokensAvoided:brFullDebateTokens});
        synSysFinal+="\n\n"+renderCompletenessReport(brRows)
          +"\nSay plainly which executives were complete, partial or excluded - never omit one silently, never attribute one executive's view to another."
          +(brDoc.matched?"":"\nStructure the synthesis with these headings: BOARD CONSENSUS; EXECUTIVE DISSENT; UNRESOLVED; REQUIRES USER DECISION; REQUIRES EVIDENCE; REQUIRES FOLLOW-UP RESEARCH; RECOMMENDED NEXT ACTIONS.");
        // CONTEXT BUDGET FOR SYNTHESIS: the full debate is used when it fits the
        // Chairman stage's first permitted provider. Otherwise the verbatim Decision
        // Ledger and the full evidence (with F#/S# ids) replace the raw answers.
        // The raw answers stay in the transcript either way.
        {
          const synMaxT=brDoc.matched?9000:7000;
          const chairFirst=costOrderedProviders(keys,"chairman")[0]?.provider||"";
          if(chairFirst&&brLedger.length&&!budgetCheck(chairFirst,synSysFinal,allPos,synMaxT).fits){
            synSysFinal=synSysFinal.split(researchContext).join(mkResearchCtx(renderEvidence(brEvidence.items,brEvidence.sources)));
            allPos=renderLedger(brLedger,"full")+"\n\nSHARED DECISION INTELLIGENCE STATE:\n"+renderIntelligence(brIntel,brQ,"full")+"\n\n(The full executive answers are kept in the transcript. This synthesis received the verbatim Decision Ledger because the full debate exceeds the context of "+chairFirst+".)";
            brCtxLog.push({executive:"Chairman synthesis",provider:chairFirst,strategy:"decision ledger + full evidence (full debate too large)",rawPriorOutputsIncluded:0,ledgerEntries:brLedger.length,estInput:budgetCheck(chairFirst,synSysFinal,allPos,synMaxT).estInput});
          }else{
            brCtxLog.push({executive:"Chairman synthesis",provider:chairFirst,strategy:"full debate (fits)",rawPriorOutputsIncluded:res.length,ledgerEntries:brLedger.length,estInput:chairFirst?budgetCheck(chairFirst,synSysFinal,allPos,synMaxT).estInput:0});
          }
        }
        let syn=(await (async()=>{const _r=await callStage(keys,"chairman",synSysFinal,[{role:"user",content:(brDoc.matched?("Produce the "+brDoc.documentName+" now, using the executive contributions below as your source material.\n\nOriginal request: \""+brQ+"\""):("Question: \""+brQ+"\""))+"\nDebate:\n"+allPos}],brDoc.matched?9000:7000);try{showToast(_r.note,"info");}catch{}return _r.text;})());
        // Intelligence Engine quality review — same standard as Workflow and Task
        // Queue final levels. HARDENED: the reviewed version must prove it is a
        // complete, well-formed improvement (all structural markers present,
        // within 90s, adequate length) or the ORIGINAL synthesis is kept.
        // The review can only ever improve — never degrade, truncate, or hang.
        try{
          setBrPh("\ud83d\udd0d Quality Review \u2014 validating board synthesis...");
          const ieCompany={name:co.name||"the company",industry:co.industry||"",stage:co.stage||"",location:co.location||"",markets:co.markets||"",currency:co.currency||"INR",currencySymbol:synCur.sym||""};
          const reviewed:any=await Promise.race([
            selfReview(syn,brQ,ieCompany,(s,m,_t)=>callStage(keys,"chairman",s,m,6500).then(r=>r.text)),
            new Promise((_,rej)=>setTimeout(()=>rej(new Error("review timeout")),90000)),
          ]);
          const structurallyComplete=
            typeof reviewed==="string"&&
            reviewed.length>syn.length*0.7&&
            reviewed.includes("Executive Summary")&&
            reviewed.includes("DECISION STATUS");
          if(structurallyComplete){syn=reviewed;}
          else if(reviewed){showToast("Quality review returned an incomplete draft \u2014 original synthesis kept","info");}
        }catch(qErr:any){showToast("Quality review skipped ("+String(qErr?.message||qErr).slice(0,40)+") \u2014 original synthesis kept","info");}
        try{const fxw=fxPlausibilityWarnings(syn);if(fxw)syn=fxw+"\n"+syn;}catch{}
        syn+=ieEvidenceAudit(syn);
        // GUARANTEE: from this point the synthesis is persisted no matter what.
        // Every step below is individually guarded so a failure in decision
        // extraction, history saving, or token accounting can never blank the
        // synthesis the user just paid tokens to generate.
        let decisionStatus="No Consensus";
        try{decisionStatus=extractDecisionStatus(syn);}catch{}
        try{saveDecisionRecord({id:Date.now(),ts:new Date().toISOString(),question:brQ,executives:brAg,status:decisionStatus,recommendation:extractRecommendationSnippet(syn)});}catch{}
        const stage1={stageNumber:1,type:"original",question:brQ,
          executiveIds:brAg,debate:res,synthesis:syn,
          decisionStatus,completedAt:new Date().toISOString(),frozen:true,ledger:[...brLedger],contextLog:[...brCtxLog],completeness:brRows};
        // PHASE 14/29: the decision is recorded in the Research State itself.
        let brAnalysis:any=null;
        try{brAnalysis=analyseStage({q:brQ,researchBrief,researchState:{...(brResearchState||{}),intelligence:brIntel},stages:[{question:brQ,debate:res}]},0,{location:co.location||""});}catch{}
        const finalResearchState=normalizeResearchState({...(brResearchState||{question:brQ,researchBrief,grounded:brGrounded}),
          modelRegistry:brAnalysis?brAnalysis.registry:(brResearchState&&brResearchState.modelRegistry)||null,
          pendingUserQuestions:brAnalysis?brAnalysis.userQuestions:(brResearchState&&brResearchState.pendingUserQuestions)||[],
          decisions:[...((brResearchState&&brResearchState.decisions)||[]),
            {stage:1,question:brQ,status:decisionStatus,recommendation:(()=>{try{return extractRecommendationSnippet(syn);}catch{return "";}})(),at:new Date().toISOString()}]});
        const finalSession={id:brSessionId,q:brQ,agents:brAg,
          format:"threaded",stages:[stage1],researchBrief,ts:new Date().toISOString(),
          grounded:brGrounded,intakeRegister:brIntakeRegister,researchState:finalResearchState};
        const finalCur={q:brQ,researchBrief,format:"threaded",stages:[stage1],sessionId:brSessionId,
          grounded:brGrounded,intakeRegister:brIntakeRegister,researchState:finalResearchState};
        setBrCur(finalCur);
        try{sv("cos-br-live",finalCur);}catch{}
        // (usage is now logged centrally inside callAI, with the real provider)
        const ns=[finalSession,...brSessions.filter((x:any)=>x.id!==brSessionId)].slice(0,20);setBrSessions(ns);sv("cos-br",ns);
      }
    }catch(err){
      if(!cancelRef.current.br){setError(err.message);showToast("Boardroom error: "+err.message,"error");}
    }finally{setBrRun(false);setBrPh("");setBrResearching(false);cancelRef.current.br=false;}
  },[brQ,brAg,brRun,co,compData,brSessions,keys,defP,showToast,ledgerEntries,workflows,tmRes,apRes,tmSessions,apSessions,cur,brFreshResearch]);
  // GUIDED INTERVIEW hand-off: put the enriched question in the box, choose a board if
  // none is selected, then start the existing runBR once state has updated.
  const [brAutoRun,setBrAutoRun]=useState(false);
  useEffect(()=>{if(brAutoRun&&!brRun){setBrAutoRun(false);runBR();}},[brAutoRun,brRun,runBR]);
  const startBoardroom=useCallback((q:string,execs:string[])=>{setBrQ(q);if(brAg.length<2&&execs&&execs.length>=2)setBrAg(execs);setBrAutoRun(true);},[brAg]);
  // BANK PROJECT REPORT research: the existing Research Desk gathers sourced findings, then one
  // call structures them (compliance, developments, schemes). Only facts in the brief are used.
  const researchProjectReport=useCallback(async(q:string)=>{
    const rd:any=await runResearchDesk(ask,co,compData,q,showToast,keys);
    const text=await ask(RESEARCH_JSON_PROMPT,[{role:"user",content:"RESEARCH BRIEF:\n\n"+String(rd.brief||"").slice(0,14000)}],3500);
    return {text:String(text||""),grounded:!!rd.grounded};
  },[ask,co,compData,showToast,keys]);

  // Continue a reopened/finished debate with a follow-up. Same executives respond
  // again using the prior debate + synthesis as context. Appends to the live debate.
  const runBRContinue=useCallback(async()=>{
    if(!brFollowUp.trim()||brRun)return;
    cancelRef.current.br=false;
    setBrRun(true);setError(null);

    // Determine which execs respond — use followUpExecIds if set, else previous stage execs
    const prevStages=brCur.stages||[];
    const prevExecIds=followUpExecIds.length>0
      ?followUpExecIds
      :(prevStages.length>0?prevStages[prevStages.length-1].executiveIds:brAg);
    // PHASE 18: if the user names executives ("Ask the CFO", "Chairman, ..."),
    // only those answer. Otherwise the previous behaviour is unchanged.
    const addressedIds=detectAddressedExecs(brFollowUp).filter(id=>AR.some(r=>r.id===id));
    // PHASE 19 - INTELLIGENT ROUTING. Priority: executives the user names ->
    // executives chosen manually in the UI -> intelligent routing -> previous
    // stage. Routing first asks the AI, giving it the question AND the Research
    // State (objective, dimensions, prior decisions), so it routes on context,
    // not keywords. Only if that call fails does a multi-signal scorer decide.
    const boardIds=CS.map((r:any)=>r.id);
    let routedIds:string[]=[]; let routeReason="";
    if(!addressedIds.length&&!followUpExecIds.length){
      try{
        const rsR:any=(brCur as any).researchState||null;
        const routerSys="You route a follow-up question in a boardroom to the executives who should answer it. "+
          "Choose the ONE executive with primary responsibility, plus only those with a genuine secondary implication (most questions need 1-2, never more than 4). "+
          "Do not include an executive just to be thorough. Return ONLY JSON: {\"primary\":\"id\",\"secondary\":[\"id\"],\"reason\":\"one sentence\"}.\n"+
          "EXECUTIVES (id: remit):\n"+CS.map((r:any)=>r.id+": "+r.t+" - "+r.d).join("\n")+
          (rsR?"\nINVESTIGATION CONTEXT: objective: "+(rsR.objective||rsR.originalQuestion||"")+"; dimensions: "+(rsR.dimensions||[]).join(", ")+
            ((rsR.decisions||[]).length?"; prior decisions: "+rsR.decisions.map((d:any)=>d.status).join(", "):""):"");
        const rr=(await (async()=>{const _r=await callStage(keys,"research_extract",routerSys,[{role:"user",content:"FOLLOW-UP QUESTION: "+brFollowUp}],700);return {primary:_r.text,truncated:_r.truncated};})());
        const txt=String((rr as any)?.primary||"");
        const a=txt.indexOf("{"),b=txt.lastIndexOf("}");
        if(a>=0&&b>a){
          const j=JSON.parse(txt.slice(a,b+1));
          routedIds=[j.primary,...(Array.isArray(j.secondary)?j.secondary:[])].map((x:any)=>String(x||"").trim().toLowerCase())
            .filter((x:string,i:number,arr:string[])=>boardIds.includes(x)&&arr.indexOf(x)===i).slice(0,4);
          routeReason=String(j.reason||"").slice(0,160);
        }
      }catch{ routedIds=[]; }
      if(!routedIds.length){
        routedIds=scoreFollowUpRoute(brFollowUp,boardIds).ids;
        if(routedIds.length)routeReason="matched on the question's subject (AI routing unavailable)";
      }
    }
    const chosenIds=addressedIds.length?addressedIds:followUpExecIds.length?followUpExecIds:routedIds.length?routedIds:prevExecIds;
    const agents=chosenIds.map((id:string)=>AR.find(r=>r.id===id)).filter(Boolean);
    if(addressedIds.length){try{showToast("Routing this question to: "+agents.map((a:any)=>a.t).join(", "),"info");}catch{}}
    else if(routedIds.length&&!followUpExecIds.length){try{showToast("Answering: "+agents.map((a:any)=>a.t).join(", ")+(routeReason?" \u2014 "+routeReason:""),"info");}catch{}}

    // PHASE 14/15/26: THE GAP THIS FIXES - follow-ups previously received the
    // prior debate text but NOT the Research Brief, so every follow-up silently
    // lost the evidence and executives answered from memory. The stored
    // Research State is now passed in, so they answer from existing evidence
    // and say plainly when it does not cover the new question.
    const rs:any=(brCur as any).researchState?normalizeResearchState((brCur as any).researchState):null;
    const briefText=String((brCur as any).researchBrief||rs?.researchBrief||"");
    // DECISION LEDGER V2 IN FOLLOW-UPS: record any user decision/constraint, classify
    // what the follow-up needs (A existing evidence / B a known evidence gap / C new
    // research), and give executives the compact intelligence state - no new search
    // is run here; a B-type need is recorded as a targeted-research candidate.
    const fuEvidence=parseResearchEvidence(briefText);
    let fuIntel:IntelligenceState=rs&&rs.intelligence?JSON.parse(JSON.stringify(rs.intelligence)):emptyIntelligence();
    const fuUserDecision=recordUserDecision(fuIntel,brFollowUp);
    const fuNeed=classifyFollowUp(fuIntel,fuEvidence,brFollowUp);
    // A follow-up that answers an open user-only question closes it (never asked again)
    // and the answer is shown to every executive from now on.
    const fuPending:any[]=((rs&&rs.pendingUserQuestions)||[]).map((x:any)=>({...x,askedBy:[...(x.askedBy||[])]}));
    const fuAnswered=answerUserQuestion(fuPending,brFollowUp);
    if(fuAnswered){try{showToast("Recorded your answer to "+fuAnswered.id+": \u201c"+fuAnswered.question.slice(0,80)+"\u201d","success");}catch{}}
    if(fuNeed.need==="B"&&fuNeed.gap){const g=fuIntel.gaps.find((x:any)=>x.id===fuNeed.gap!.id);if(g)g.current_status="targeted_research_candidate";}
    try{
      showToast((fuNeed.need==="A"?"Answering from the saved research.":fuNeed.need==="B"?"This matches open evidence gap "+fuNeed.gap!.id+" \u2014 recorded as a targeted-research candidate (no new search run). Executives will answer with what is known and say what is missing."
        :"Not covered by the saved research \u2014 executives will flag what new research is needed.")+(fuUserDecision?" Recorded your "+fuUserDecision.type+" as "+fuUserDecision.id+".":""),"info");
    }catch{}
    // GATE A VISIBILITY: tells the user, on screen, whether this follow-up is
    // building on the saved investigation - so tests A2/A3/A4 can be verified
    // without opening developer tools. Shows the session it belongs to, so a
    // follow-up landing on the wrong session would be visible immediately.
    try{
      if(rs){
        showToast("Continuing saved investigation"+((brCur as any).sessionId?" #"+String((brCur as any).sessionId).slice(-5):"")+": "
          +String(rs.objective||rs.originalQuestion||"").slice(0,90)+" \u2014 "+rs.decisions.length+" prior decision(s), "
          +rs.followUps.length+" earlier follow-up(s), research "+(rs.grounded?"grounded":"UNGROUNDED")+". Original research is not being re-run.","info");
      }else if(briefText){
        showToast("This session has a research brief but no structured Research State (saved before the upgrade). Follow-up will use the brief only.","warning");
      }else{
        showToast("No saved research for this session \u2014 executives will answer without a research base.","warning");
      }
    }catch{}
    const researchStateBlock=(briefText||rs)
      ?"\n\nRESEARCH STATE FOR THIS INVESTIGATION (already established - build on it, do not restart):\n"
        +(rs?.originalQuestion?"Original question: "+rs.originalQuestion+"\n":"")
        +(rs?.objective?"Underlying objective: "+rs.objective+"\n":"")
        +(rs?.assumptions?.length?"Assumptions in the original question: "+rs.assumptions.join("; ")+"\n":"")
        +(rs?.dimensions?.length?"Problem dimensions: "+rs.dimensions.join("; ")+"\n":"")
        +(rs?.adjacentOpportunityQuestions?.length?"Adjacent opportunities examined: "+rs.adjacentOpportunityQuestions.join("; ")+"\n":"")
        +(rs?.opportunities?.length?"Opportunities identified so far: "+JSON.stringify(rs.opportunities).slice(0,2500)+"\n":"")
        +(rs?.unresolvedQuestions?.length?"Unresolved questions: "+rs.unresolvedQuestions.join("; ")+"\n":"")
        +(rs?.decisions?.length?"Prior decisions: "+rs.decisions.map((d:any)=>"[stage "+d.stage+"] "+d.question+" -> "+(d.status||"")+(d.recommendation?" ("+String(d.recommendation).slice(0,200)+")":"")).join(" | ")+"\n":"")
        +(rs?.followUps?.length?"Earlier follow-ups: "+rs.followUps.map((f:any)=>f.question).join(" | ")+"\n":"")
        +((rs&&rs.scenarios&&rs.scenarios.length)?"SIMULATION RESULTS (scenarios the user tested - not facts):\n"+rs.scenarios.slice(-5).map((sc:any)=>"  "+sc.name+": "+(sc.decisionChanged?"changed the decision from "+sc.baseDecision+" to ":"decision stays ")+sc.decisionResult).join("\n")+"\n":"")
        +(fuPending.filter((u:any)=>u.status==="answered").length?"ANSWERS THE USER HAS GIVEN (facts - use them, do not ask again):\n"+fuPending.filter((u:any)=>u.status==="answered").map((u:any)=>"  "+u.id+" "+u.question+" -> "+u.answer).join("\n")+"\n":"")
        +(fuPending.filter((u:any)=>u.status==="open").length?"STILL OPEN FOR THE USER (do not invent answers): "+fuPending.filter((u:any)=>u.status==="open").map((u:any)=>u.id+" "+u.question).join(" | ")+"\n":"")
        +(fuIntel&&(fuIntel.opportunities.length||fuIntel.gaps.length||fuIntel.userDecisions.length||fuIntel.contradictions.length)?"SHARED DECISION INTELLIGENCE STATE:\n"+renderIntelligence(fuIntel,brFollowUp,"core")+"\n":"")
        +(briefText?"Research Brief "+(((brCur as any).grounded??rs?.grounded)?"(grounded in sources)":"(UNGROUNDED - no verifiable sources)")+":\n"+briefText.slice(0,12000)+"\n":"")
        +"If this evidence does not cover the follow-up question, say exactly what evidence is missing instead of filling the gap from memory."
      :"";
    // PHASES 21-24, 43, 44: challenge protocol for follow-ups.
    const challengeProtocol="\n\nIF THE FOLLOW-UP CONTAINS A USER PROPOSAL OR ASSUMPTION, assess it with exactly one label - SUPPORTED, CONDITIONALLY SUPPORTED (state the conditions), CHALLENGED, CONTRADICTED BY AVAILABLE EVIDENCE, or INSUFFICIENT EVIDENCE - and give the evidence-based reason. If it is genuinely strong, say specifically why; do not flatter. If it is weak, say so plainly: the weak assumption, the contradicting evidence, the likely consequence, and what evidence would change your view. Add a short 'What you may be overlooking' only where there is a real logical or evidentiary basis. Do not disagree with other executives for effect; converge when the evidence converges. Never claim an opportunity is novel or that 'nobody is doing this' unless the evidence shows it.";

    // Build full prior context from ALL previous stages
    const priorStagesContext=prevStages.map((st,si)=>{
      const debateText=st.debate.map(d=>d.ag.t+": "+d.text).join("\n\n---\n\n");
      return "=== STAGE "+(si+1)+" QUESTION: \""+st.question+"\"\n"+debateText+(st.synthesis?"\n\nSTAGE "+(si+1)+" SYNTHESIS:\n"+st.synthesis:"");
    }).join("\n\n"+"=".repeat(60)+"\n\n");

    const followUpResponses=[];
    const failedAgents=[];

    try{
      for(let i=0;i<agents.length;i++){
        if(cancelRef.current.br){showToast("Cancelled","warning");break;}
        const ag=agents[i];const p=EP[ag.id]||{};
        setBrPh(ag.ic+" "+ag.t+" is responding…");

        const sys="You are "+ag.f+" at \""+co.name+"\".\n"
          +buildBoardIdentity(ag,p)+CLARITY_PROTOCOL+"\n"
          +buildCtx(co,compData)
          +"\n\nDECISION THREAD CONTEXT (all previous stages — do not repeat, only reference):\n"+priorStagesContext
          +researchStateBlock+challengeProtocol
          +"\n\n"+"=".repeat(60)+"\n\n"
          +"CURRENT FOLLOW-UP QUESTION: \""+brFollowUp+"\"\n"
          +"YOUR TURN as "+ag.f+". Respond ONLY to the follow-up question above.\n"
          +"Step 1: Reference any relevant conclusion from a prior stage if it informs your answer.\n"
          +"Step 2: Add your "+ag.dl+" perspective on the follow-up — be specific and new.\n"
          +"Step 3: Label every key statement: [Verified Fact] [Assumption] [Expert Inference] [Estimate]\n"
          +"LENGTH: up to "+boardWordBudget(ag)+" words. Never state your own word count.";

        let replyFull=null;let lastErr=null;
        for(let attempt=0;attempt<2;attempt++){
          if(cancelRef.current.br)break;
          try{replyFull=(await (async()=>{const _r=await callStage(keys,"executive",sys,[{role:"user",content:"FOLLOW-UP: "+brFollowUp}],adaptiveOutputBudget(4000,taskKindFor({isFirstSpeaker:false,dimensionCount:0,isFollowUp:true,followUpWords:String(brFollowUp).trim().split(/\s+/).length})));return {primary:_r.text,truncated:_r.truncated};})());lastErr=null;break;}
          catch(agentErr){lastErr=agentErr;if(attempt===0)await new Promise(res=>setTimeout(res,1500));}
        }
        if(cancelRef.current.br)break;
        if(replyFull){
          const fuStageId="stage-"+(prevStages.length+1);
          followUpResponses.push({ag,text:replyFull.primary,fullText:replyFull.primary,truncated:!!replyFull.truncated,
            status:deriveStatus({text:replyFull.primary,truncated:!!replyFull.truncated,continuationAttempts:0}),
            identity:{sessionId:(brCur as any).sessionId||"",stageId:fuStageId,executiveId:ag.id,executiveKey:ag.t,callId:makeCallId((brCur as any).sessionId||"",fuStageId,ag.id),provider:"see stage log",model:""}});
          try{fuIntel=mergeIntoIntelligence(fuIntel,extractLedgerEntry(ag.t,ag.id,replyFull.primary,fuEvidence.sources),replyFull.primary,fuEvidence,{question:brFollowUp,dimensions:(rs&&rs.dimensions)||[]});}catch{}
        }
        else if(lastErr){failedAgents.push(ag.t);followUpResponses.push({ag,text:"_"+ag.t+" could not respond ("+lastErr.message+")_",fullText:"",truncated:false,status:"FAILED",
          identity:{sessionId:(brCur as any).sessionId||"",stageId:"stage-"+(prevStages.length+1),executiveId:ag.id,executiveKey:ag.t,callId:makeCallId((brCur as any).sessionId||"","stage-"+(prevStages.length+1),ag.id),provider:"none",model:""}});}
      }

      // Chairman synthesis for this stage
      let stageSyn="";
      if(!cancelRef.current.br&&followUpResponses.length>0){
        setBrPh("🏛️ Chairman synthesising stage "+( prevStages.length+1)+"…");
        const synCur=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];
        const allPos=followUpResponses.map(r=>r.ag.t+":\n"+r.text).join("\n\n---\n\n");
        const stageSynSys="You are Chief of Staff at "+JSON.stringify(co.name)+". "+buildCtx(co,compData)
          +"\n\nDECISION THREAD — PRIOR STAGES (context only — do not re-analyse):\n"+priorStagesContext
          +"\n\n"+"=".repeat(60)+"\n\n"
          +"CURRENT FOLLOW-UP QUESTION: \""+brFollowUp+"\"\n"
          +"Synthesise ONLY this follow-up round into a concise board-ready summary.\n"
          +"Sections: # Summary | ## Key Findings | ## Evidence Quality | ## Decision Status\n"
          +"All figures in "+synCur.sym+". Reference prior stages briefly where relevant.\n"
          +"DECISION STATUS (mandatory final line):\n"
          +"DECISION STATUS: [Proceed | Proceed with Conditions | Needs More Information | Do Not Proceed | No Consensus]\n"
          +"Reason: [one sentence]";
        try{stageSyn=(await (async()=>{const _r=await callStage(keys,"chairman",stageSynSys,[{role:"user",content:"Follow-up: \""+brFollowUp+"\"\nResponses:\n"+allPos}],3000);try{showToast(_r.note,"info");}catch{}return _r.text;})());}
        catch(e){stageSyn="(Synthesis unavailable for this stage: "+e.message+")";}
      }

      if(!cancelRef.current.br){
        const newStage={
          stageNumber:prevStages.length+1,
          type:"followup",
          question:brFollowUp,
          executiveIds:agents.map((a:any)=>a.id),
          aiSuggestions:followUpSuggestions,
          debate:followUpResponses,
          synthesis:stageSyn,
          decisionStatus:extractDecisionStatus(stageSyn),
          completedAt:new Date().toISOString(),
          frozen:true,
        };
        try{saveDecisionRecord({id:Date.now(),ts:new Date().toISOString(),question:brFollowUp,executives:prevExecIds,status:newStage.decisionStatus,recommendation:extractRecommendationSnippet(stageSyn)});}catch{}
        const updatedStages=[...prevStages,newStage];
        const priorRS=(brCur as any).researchState;
        const updatedRS=priorRS?normalizeResearchState({...priorRS,
          intelligence:fuIntel,pendingUserQuestions:fuPending,
          followUps:[...(priorRS.followUps||[]),{question:brFollowUp,executives:agents.map((a:any)=>a.id),need:fuNeed.need,needReason:fuNeed.reason,at:new Date().toISOString()}],
          decisions:[...(priorRS.decisions||[]),{stage:updatedStages.length,question:brFollowUp,status:newStage.decisionStatus,
            recommendation:(()=>{try{return extractRecommendationSnippet(stageSyn);}catch{return "";}})(),at:new Date().toISOString()}]}):priorRS;
        const updatedCur={...brCur,stages:updatedStages,researchState:updatedRS};
        setBrCur(updatedCur);
        sv("cos-br-live",updatedCur);
        // Update most recent archive entry with new stage
        setBrSessions(prev=>{
          if(!prev.length)return prev;
          const updated=[...prev];
          // WAS updated[0] - the most recent session - even when an OLDER
          // session had been reopened, so its follow-up was written into a
          // different session. Now matched by id; [0] only for legacy sessions.
          const sid=(brCur as any).sessionId;
          const idx=sid?updated.findIndex((x:any)=>x.id===sid):0;
          if(idx<0)return prev;
          updated[idx]={...updated[idx],stages:updatedStages,researchState:updatedRS};
          sv("cos-br",updated);
          return updated;
        });
        setBrFollowUp("");
        setFollowUpExecIds([]);
        setFollowUpSuggestions([]);
      }
      if(failedAgents.length){showToast(failedAgents.length+" executive(s) couldn't respond: "+failedAgents.join(", ")+".","warning");}
    }catch(err){
      if(!cancelRef.current.br){setError(err.message);showToast("Continue error: "+err.message,"error");}
    }finally{setBrRun(false);setBrPh("");cancelRef.current.br=false;}
  },[brFollowUp,brRun,brCur,brAg,co,compData,keys,defP,showToast,followUpExecIds,followUpSuggestions]);
  
  // RESEARCH FOLLOW-UP LOOP: open gap -> prioritise (HIGH impact + HIGH uncertainty only,
  // max 2) -> targeted research -> evidence appended with ids -> gap closed when the
  // research is grounded -> claims/model/decision re-evaluated deterministically on
  // render. Executives are NOT re-run, which keeps the loop cheap.
  // DECISION COCKPIT persistence: scenarios/challenges/responses are written into the
  // session's Research State (same store as everything else) and a compact handoff is
  // saved for Time Machine, Autopilot and the next Boardroom run.
  const saveCockpit=useCallback((patch:any,handoff?:DecisionHandoff|null)=>{
    const cur0:any=brCur;const rs0:any=cur0.researchState||{};
    const newRS=normalizeResearchState({...rs0,...patch});
    const updated={...cur0,researchState:newRS};
    setBrCur(updated);try{sv("cos-br-live",updated);}catch{}
    if(cur0.sessionId)setBrSessions((prev:any[])=>{const n=(prev||[]).map((x:any)=>x.id===cur0.sessionId?{...x,researchState:newRS}:x);try{sv("cos-br",n);}catch{};return n;});
    if(handoff)try{sv("oiq-decision-handoff",handoff);}catch{}
  },[brCur,sv]);
  const addCockpitActions=useCallback((texts:string[],label:string,notes?:string[])=>{
    const now=new Date().toISOString();
    const fresh=texts.filter(t=>t&&!actionItems.some((a:any)=>a.text===t)).map((t,i)=>({id:Date.now()+i,text:t,source:"boardroom" as const,sourceLabel:label,ownerRoleId:null,
      status:"not_started" as const,priority:(i===0?"high":"medium") as "high"|"medium",createdAt:now,dueHint:null,notes:(notes&&notes[texts.indexOf(t)])||"From the Decision Cockpit"}));
    if(!fresh.length){showToast("These actions are already in the Action Tracker.","info");return;}
    const updated=[...fresh,...actionItems];setActionItems(updated);sv("cos-actions",updated);
    showToast(fresh.length+" action"+(fresh.length===1?"":"s")+" added to the Action Tracker.","success");
  },[actionItems,sv,showToast]);
  const openInTimeMachine=useCallback((text:string)=>{setTmDec(text);setNTab("timemachine");},[]);
  // The persisted Boardroom decision (with canonical inputs) for Time Machine and Autopilot.
  const journeyActuals=useMemo(()=>{try{return actualsFromLedgerJ(ledgerEntries||[],getAllAccounts(customAccounts||[]));}catch{return null;}},[ledgerEntries,customAccounts]);
  const decisionHandoff=useMemo(()=>{try{return WorkspaceMemory.get<DecisionHandoff>("oiq-decision-handoff")||null;}catch{return null;}},[nTab,brCur]);

  const researchPriorityGaps=useCallback(async(si:number)=>{
    if(brRun)return;
    const cur0:any=brCur;
    let a:any=null;try{a=analyseStage(cur0,si,{location:co.location||""});}catch{a=null;}
    if(!a||!a.priorityGaps.length){showToast("No high-impact, high-uncertainty evidence gaps need research right now.","info");return;}
    setBrRun(true);
    let brief=String(cur0.researchBrief||"");const closed:string[]=[];const tried:string[]=[];
    try{
      for(const g of a.priorityGaps){
        tried.push(g.id);
        setBrPh("\uD83D\uDCE1 Targeted research ("+g.id+"): "+g.researchQuestion);
        const rd:any=await runResearchDesk(ask,co,compData,g.researchQuestion,showToast,keys);
        if(rd&&String(rd.brief||"").trim()){
          brief+="\n\n### Targeted research \u2014 "+g.id+": "+g.researchQuestion+"\n"+rd.brief;
          if(rd.grounded)closed.push(g.id);
        }
      }
      const rs0:any=cur0.researchState||{};
      const intel0:any=rs0.intelligence?JSON.parse(JSON.stringify(rs0.intelligence)):null;
      if(intel0)intel0.gaps=(intel0.gaps||[]).map((g:any)=>closed.includes(g.id)?{...g,current_status:"closed"}:g);
      const newRS=normalizeResearchState({...rs0,researchBrief:brief,intelligence:intel0});
      const updated={...cur0,researchBrief:brief,researchState:newRS};
      setBrCur(updated);
      try{sv("cos-br-live",updated);}catch{}
      if(cur0.sessionId){
        setBrSessions((prev:any[])=>{const n=(prev||[]).map((x:any)=>x.id===cur0.sessionId?{...x,researchBrief:brief,researchState:newRS}:x);try{sv("cos-br",n);}catch{};return n;});
      }
      showToast("Targeted research: "+closed.length+" of "+tried.length+" priority gap"+(tried.length===1?"":"s")+" closed with sourced evidence"+(closed.length<tried.length?"; the rest stay open":"")+". The decision has been re-evaluated.",closed.length?"success":"warning");
    }catch(e:any){
      showToast("Targeted research stopped: "+String(e?.message||e).slice(0,200),"error");
    }finally{setBrRun(false);setBrPh("");}
  },[brCur,brRun,co,compData,keys,ask,showToast,sv]);

  const runDrill=useCallback(async()=>{
    if(!drillRole||!drillQ.trim()||drillRun)return;
    setDrillRun(true);setError(null);
    const ag=AR.find(r=>r.id===drillRole);
    const prevTake=(brCur.stages
      ?brCur.stages.flatMap(s=>s.debate||[]).find(d=>d.ag?.id===drillRole)?.text
      :(brCur.debate||[]).find(d=>d.ag?.id===drillRole)?.text)||"";
    try{
      const sys=buildSys(ag,co,compData)+"\n\nYour boardroom take:\n"+prevTake+"\n\nCEO is drilling deeper.";
      const reply=await ask(sys,[{role:"user",content:drillQ}]);
      setBrCur(prev=>({...prev,drilldown:{...prev.drilldown,[drillRole]:[...(prev.drilldown[drillRole]||[]),{q:drillQ,a:reply}]}}));
      setDrillQ("");
    }catch(err){setError(err.message);showToast(err.message,"error");}finally{setDrillRun(false);}
  },[drillRole,drillQ,drillRun,brCur,co,compData,keys,defP,showToast]);

  // FIX BUG 4: Workflow — per-level error handling + cancel + progress
  const runPreflight=useCallback(async()=>{
  const taskText=wfTask.trim();
  if(!taskText||!wfCat)return;
  const ch=CHAINS[wfCat];if(!ch)return;
  const activeChain=wfCustomChain.length?wfCustomChain:ch.chain;
  const seniorId=activeChain[activeChain.length-1];
  const seniorRole=AR.find(r=>r.id===seniorId)||AR.find(r=>r.id==="ceo");
  const p=EP[seniorRole.id]||{};
  setWfPreflightLoading(true);
  setWfPreflight(null);
  const existingData=Object.keys(compData).length?"\n\nCOMPANY DATA ALREADY PROVIDED:\n"+Object.entries(compData).map(([k,v])=>k+": "+v).join("\n"):"";
  const ledgerSummary=ledgerEntries.length?"\n\nLEDGER: "+ledgerEntries.length+" journal entries posted. Recent: "+ledgerEntries.slice(-3).map(e=>e.description||e.narration||"entry").join(", "):"";
  const sys="You are "+seniorRole.f+" at \""+co.name+"\". "+buildCtx(co,compData)+"\n\nThe user wants to run a workflow task. Your job is to ask 3-5 smart, simple questions that will allow the team to produce a HIGH QUALITY FINAL DELIVERABLE — not a report or plan, but the actual output the user needs.\n\nRULES:\n1. Each question must be asked AS YOUR PERSONA — speak as "+seniorRole.t+", not as a generic AI.\n2. Questions must be SHORT and conversational. One sentence maximum.\n3. Only ask what is GENUINELY missing. Do not ask for things already in Company Data or Ledger below.\n4. Focus on what directly shapes the deliverable quality — audience, tone, constraints, specific content.\n5. Output ONLY valid JSON, no preamble, no markdown fences.\n\nFORMAT (strict JSON array):\n[{\"persona\":\""+seniorRole.t+"\",\"personaIc\":\""+seniorRole.ic+"\",\"q\":\"question text\",\"placeholder\":\"example answer\"}]\n\nEXISTING CONTEXT (do NOT ask about these):"+existingData+ledgerSummary;
  try{
    const raw=await ask(sys,[{role:"user",content:"TASK: \""+taskText+"\"\nCATEGORY: "+ch.label+"\nEXECUTIVES SELECTED: "+activeChain.map(id=>{const r=AR.find(x=>x.id===id);return r?r.t:id;}).join(", ")+"\n\nGenerate the pre-flight questions now."}],800);
    let questions=[];
    try{
      const cleaned=raw.trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
      questions=JSON.parse(cleaned);
      if(!Array.isArray(questions))questions=[];
    }catch{questions=[];}
    if(!questions.length){
      // No questions needed — run directly
      setWfPreflightLoading(false);
      const activeChainFinal=wfCustomChain.length?wfCustomChain:ch.chain;
      runWorkflow(activeChainFinal,null);
      return;
    }
    setWfPreflight({questions,answers:questions.map(()=>""),contextSummary:existingData+ledgerSummary});
    setWfPreflightActive(true);
  }catch(e:any){
    showToast("Pre-flight check failed: "+e.message+". Running chain directly.","warning");
    const activeChainFinal=wfCustomChain.length?wfCustomChain:ch.chain;
    runWorkflow(activeChainFinal,null);
  }finally{
    setWfPreflightLoading(false);
  }
},[wfTask,wfCat,wfCustomChain,co,compData,ledgerEntries,ask,showToast]);
// ─── PROJECT ENGINE — Phase 1: Planning ──────────────────────────────────────
  // Assembles ProjectContext from all platform state and calls the Project
  // Architect AI to decompose the objective into an Execution Plan.

  const buildProjectContext=useCallback(()=>{
    const projCur=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];
    const boardroomDecisions=(brSessions||[]).slice(0,5).map(s=>({
      question:s.q,
      synthesis:(()=>{const sy=(Array.isArray(s?.stages)?s.stages:[]).map((st:any)=>st?.synthesis||"").filter(Boolean).join("\n")||s?.synthesis||"";return sy?stripMd(sy).slice(0,1200):"";})(),
      decisionStatus:s.stages?.[0]?.decisionStatus||"",
      ts:s.ts,
    }));
    const priorOutputs=(workflows||[]).filter(w=>w.status==="approved").slice(0,4).map(w=>({
      task:w.task,category:w.chainLabel,
      output:stripMd(w.steps?.[w.steps.length-1]?.output||"").slice(0,400),
    }));
    // ── Cross-module summaries (Project Engine redesign, Phase 2) ────────────
    // Each source is condensed to a few lines, never dumped in full — same
    // discipline already proven for Boardroom decisions above. Every read is
    // wrapped so a missing/empty module can never break plan generation.
    let ledgerSummary="";
    try{
      const entries=(ledgerEntries||[]).slice(-8);
      if(entries.length)ledgerSummary=entries.map((e:any)=>(e.date||"")+" "+(e.description||e.memo||"")+" "+(e.debit||e.credit||"")).join("; ").slice(0,500);
    }catch{}
    let financeSummary="";
    try{
      const apOpen=(WorkspaceMemory.get<any[]>("cos-fin-ap")||[]).filter((x:any)=>(Number(x.amount)||0)-(Number(x.settled)||0)>0);
      const arOpen=(WorkspaceMemory.get<any[]>("cos-fin-ar")||[]).filter((x:any)=>(Number(x.amount)||0)-(Number(x.settled)||0)>0);
      if(apOpen.length||arOpen.length)financeSummary=apOpen.length+" open payables, "+arOpen.length+" open receivables.";
    }catch{}
    let pulseSummary="";
    try{
      const openTickets=(WorkspaceMemory.get<any[]>("cos-pulse-sn")||[]).filter((t:any)=>String(t?.status||"").toLowerCase()!=="closed"&&String(t?.status||"").toLowerCase()!=="resolved");
      if(openTickets.length)pulseSummary=openTickets.length+" open governance tickets.";
    }catch{}
    let actionsSummary="";
    try{
      const openActions=(WorkspaceMemory.get<any[]>("cos-actions")||[]).filter((a:any)=>a.status!=="complete"&&a.status!=="done");
      if(openActions.length)actionsSummary=openActions.slice(0,5).map((a:any)=>a.title||a.name||"").filter(Boolean).join("; ").slice(0,300);
    }catch{}
    let autopilotSummary="";
    try{
      const apDec=WorkspaceMemory.get<string>("cos-ap-live");
      if(apDec)autopilotSummary=stripMd(String((apDec as any)?.res||apDec||"")).slice(0,400);
    }catch{}
    let fundingSummary="";
    try{
      const fundStage=WorkspaceMemory.get<string>("cos-funding-stage");
      if(fundStage)fundingSummary=String(fundStage);
    }catch{}
    return {
      company:{name:co.name,industry:co.industry,stage:co.stage,
        location:co.location,currency:projCur.code,
        currencySymbol:projCur.sym,markets:co.markets||""},
      brand:{name:co.name,tagline:"",tone:"professional, direct, founder-voice",
        verifiedClaims:[],prohibitedClaims:[]},
      dataHub:compData,
      boardroomDecisions,
      timeMachineForecasts:tmRes?stripMd(tmRes).slice(0,600):"",
      priorWorkflowOutputs:priorOutputs,
      ledgerSummary,
      financeSummary,
      pulseSummary,
      actionsSummary,
      autopilotSummary,
      fundingSummary,
      requiredFormats:["docx","xlsx","pdf","md","pptx","linkedin_post","facebook_post","instagram_post","whatsapp_message","email"],
      mediaGeneration:{
        imageEnabled:provAvailableFor("image",(id)=>providerKey(keys,id)).length>0,
        videoEnabled:provAvailableFor("video",(id)=>providerKey(keys,id)).length>0,
        promptsOnly:false,
      },
    };
  },[co,compData,brSessions,tmRes,workflows,keys,ledgerEntries]);

  const runProjectPlanning=useCallback(async()=>{
    if(!projectObjective.trim()||projectPlanning)return;
    setProjectPlanning(true);
    setProjectPlan(null);
    try{
      const detectedTemplate=detectTemplate(projectObjective);
      const templateHint=detectedTemplate?'\nDETECTED TEMPLATE: This objective matches the "'+detectedTemplate+'" template. Structure modules accordingly: '+( PROJECT_TEMPLATES[detectedTemplate]?.modules||[]).join(', ')+'.':'';
      const ctx=buildProjectContext();
      const nl="\n";
      // ── Pre-planning reasoning step (Project Engine redesign, Phase 3) ──────
      // Understand the goal and what data actually applies BEFORE deciding file
      // structure — same principle already proven for Excel and PowerPoint.
      // Fully fail-safe: any error here and planning proceeds exactly as
      // before, so a run can never be blocked by this step.
      let architectReasoning:any=null;
      try{
        const reasonSys="You are a senior business analyst preparing to plan a piece of work. Do not design the deliverables yet — first make sure you actually understand the request."+nl+
          "OBJECTIVE: "+projectObjective+nl+nl+
          "AVAILABLE CONTEXT FROM THIS COMPANY'S PLATFORM (use only what is genuinely relevant):"+nl+
          "Company: "+ctx.company.name+" ("+ctx.company.industry+", "+ctx.company.stage+")"+nl+
          (Object.keys(ctx.dataHub).length>0?"Data Hub has "+Object.keys(ctx.dataHub).length+" fields on record.\n":"")+
          (ctx.boardroomDecisions.length>0?ctx.boardroomDecisions.length+" recent Boardroom decisions on record.\n":"")+
          (ctx.ledgerSummary?"Ledger has recent activity on record.\n":"")+
          (ctx.financeSummary?"Finance: "+ctx.financeSummary+"\n":"")+
          (ctx.pulseSummary?"Governance: "+ctx.pulseSummary+"\n":"")+
          (ctx.actionsSummary?"Open actions on record.\n":"")+nl+
          "Reason through, briefly:"+nl+
          "1. Restate what this person actually wants, in plain language \u2014 the real business outcome, not just the literal words."+nl+
          "2. Which of the available context above is genuinely relevant to this specific request (name it specifically; do not claim relevance for everything just because it exists)."+nl+
          "3. Is there ONE piece of information that is genuinely necessary and cannot be reasonably assumed? If so, state the single clearest assumption you will make instead of asking \u2014 this platform delivers finished work, not a form, so prefer a sensible stated assumption over a blocking question whenever a reasonable one exists."+nl+nl+
          "Return ONLY this JSON, no prose: {\"goalRestated\":\"...\",\"relevantDataUsed\":[\"...\"],\"keyAssumptions\":[\"...\"]}";
        const reasonRaw=await ask(reasonSys,[{role:"user",content:"Analyse this request now."}],500,false,"general");
        const reasonText=typeof reasonRaw==="string"?reasonRaw:(reasonRaw as any)?.text||"";
        const reasonCleaned=reasonText.trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
        const parsedReasoning=JSON.parse(reasonCleaned);
        if(parsedReasoning?.goalRestated)architectReasoning=parsedReasoning;
      }catch{/* reasoning is additive — planning proceeds without it */}
      const reasoningContext=architectReasoning
        ?nl+"CONFIRMED UNDERSTANDING (already reasoned through \u2014 build the plan to serve this):"+nl+
          "Goal: "+architectReasoning.goalRestated+nl+
          (architectReasoning.relevantDataUsed?.length?"Relevant existing data to use: "+architectReasoning.relevantDataUsed.join("; ")+nl:"")+
          (architectReasoning.keyAssumptions?.length?"Assumptions being made: "+architectReasoning.keyAssumptions.join("; ")+nl:"")
        :"";
      const architectSys="You are the Project Architect for "+co.name+". Decompose the objective into a structured Execution Plan."+nl+nl+"COMPANY: "+ctx.company.name+" | "+ctx.company.industry+" | "+ctx.company.stage+" | "+ctx.company.location+" | "+ctx.company.currencySymbol+ctx.company.currency+nl+(Object.keys(ctx.dataHub).length>0?"DATA HUB:"+nl+Object.entries(ctx.dataHub).map(function(e){return e[0]+": "+e[1];}).join(nl)+nl:"")+( ctx.boardroomDecisions.length>0?"RECENT BOARDROOM DECISIONS:"+nl+ctx.boardroomDecisions.map(function(d){return "Q: "+d.question+" -> "+d.decisionStatus;}).join(nl)+nl:"")+(ctx.timeMachineForecasts?"TIME MACHINE FORECAST ON RECORD:"+nl+ctx.timeMachineForecasts+nl:"")+(ctx.priorWorkflowOutputs.length>0?"RECENT APPROVED WORKFLOW OUTPUTS:"+nl+ctx.priorWorkflowOutputs.map(function(p){return p.task+" ("+p.category+"): "+p.output;}).join(nl)+nl:"")+(ctx.ledgerSummary?"GENERAL LEDGER (recent entries): "+ctx.ledgerSummary+nl:"")+(ctx.financeSummary?"FINANCE SUITE: "+ctx.financeSummary+nl:"")+(ctx.pulseSummary?"PULSE GOVERNANCE: "+ctx.pulseSummary+nl:"")+(ctx.actionsSummary?"OPEN ACTION ITEMS: "+ctx.actionsSummary+nl:"")+(ctx.autopilotSummary?"AUTOPILOT RECENT DECISION SCAN: "+ctx.autopilotSummary+nl:"")+(ctx.fundingSummary?"FUNDING STAGE: "+ctx.fundingSummary+nl:"")+nl+"OUTPUT: Return ONLY valid JSON (no markdown fences) matching this exact schema:"+nl+'{"name":"short project name","objective":"one sentence","complexity":"simple|moderate|complex","estimatedDuration":"e.g. 2-3 hours","modules":[{"id":"module_id","name":"Module Name","icon":"emoji","capabilityType":"marketing|design|content|finance|legal|management|engineering|research|compliance","primaryPersona":"cmo","rationale":"why needed","deliverables":[{"id":"del_moduleid_001","name":"Deliverable Name","description":"what it must contain","outputFormat":"docx|xlsx|pdf|pptx|md|txt|image|video|linkedin_post|facebook_post|instagram_post|whatsapp_message|email","dependsOn":[],"verificationStatus":"AI Generated","confidenceScore":0,"sourceReferences":[]}]}]}'+nl+nl+templateHint+nl+nl+"RULES:"+nl+"1. Every deliverable must be a real, immediately usable file — never a discussion, never instructions for the user to do it themselves."+nl+"2. Use only modules genuinely required by the objective."+nl+"3. Min 2 max 8 modules. Min 1 max 5 deliverables each."+nl+"4. dependsOn IDs must reference earlier deliverable ids."+nl+"5. Currency symbol: "+ctx.company.currencySymbol+"\n6. IMPORTANT — Use outputFormat=image for any visual deliverable (ad image, logo, banner, infographic, social creative). Platform generates the actual image automatically."+"\n7. IMPORTANT — Use outputFormat=video for any video deliverable (promo video, explainer, reel, ad video). Platform generates the actual video automatically."+"\n8. NEVER use image_prompt or video_prompt. These are deprecated. Always use image or video."+"\n9. CRITICAL — ONE FILE, NOT ONE DELIVERABLE PER SHEET: if the objective describes ONE workbook, document, or deck that should contain multiple SHEETS, TABS, or SECTIONS that reference or build on each other (e.g. \"the workbook should contain sheets for data input, reconciliation, calculations, and dashboard\"), this is exactly ONE deliverable — a single xlsx/docx/pptx file with that internal structure. Do NOT create a separate deliverable per named sheet/tab/section. Splitting a single interrelated workbook into many small files breaks cross-sheet formulas and produces disconnected, low-quality output. Only create multiple deliverables when the objective genuinely asks for multiple standalone files (e.g. \"a Word report AND a separate PowerPoint deck\", or \"one workbook per region\")."+"\n10. Do NOT invent an image/video/mockup deliverable unless the objective explicitly asks for a picture, graphic, banner, or video asset. A request for \"interactive charts\" or \"a dashboard\" inside a workbook means charts BUILT INTO that file — not a separate standalone image or video deliverable."+"\n11. Social/messaging formats (linkedin_post, facebook_post, instagram_post, whatsapp_message, email) are each ONE short, ready-to-post/ready-to-send deliverable — never bundle multiple platforms' copy into a single deliverable, and never use these formats unless the objective actually asks for marketing/outreach copy or correspondence."+reasoningContext;
      // Root-cause fix: a detailed, comprehensive objective (many worksheets,
      // hundreds of sample rows, dozens of named formulas) needs real room to
      // describe the plan — 4000 tokens was silently truncating large plans
      // mid-generation, and the truncation-recovery below would keep only
      // whatever modules were complete before the cut, with no signal that
      // anything was missing. Raised substantially so a comprehensive request
      // actually gets a comprehensive plan instead of a truncated fragment.
      let raw=await ask(architectSys,[{role:"user",content:"Objective: "+projectObjective}],9000,false,"general");
      // Guard: empty or error-string responses (provider limit, consensus-merge
      // hiccup) are not plan failures — retry once, forcing a clean single call.
      const looksBad=(r:string)=>!r||r.trim().length<40||/^(error|rate limit|too many|unauthorized|invalid api)/i.test(r.trim());
      if(looksBad(raw)){
        await new Promise(r=>setTimeout(r,1500));
        raw=await ask(architectSys,[{role:"user",content:"Objective: "+projectObjective+"\n\nReturn ONLY the JSON plan, no other text."}],9000,false,"general");
      }
      let plan=null;
      let planWasTruncated=false; // surfaced to the user below — never silent again
      try{
        const cleaned=raw.trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
        try{plan=JSON.parse(cleaned);}catch{
          // Preamble tolerance: extract the first balanced JSON object
          const st=cleaned.indexOf("{");
          if(st>=0){try{plan=JSON.parse(cleaned.slice(st,cleaned.lastIndexOf("}")+1));}catch{}}
          // Truncation tolerance: universal repair recovers all complete modules.
          // Reaching this point at all means the response did not parse cleanly
          // as-is — for a plan this is the concrete signal that it was cut off.
          if(!plan?.modules){plan=repairTruncatedJson(raw);planWasTruncated=true;}
        }
        if(!plan||!plan.modules||!Array.isArray(plan.modules)||!plan.modules.length){
          const retryRaw=await ask(architectSys+"\n\nCRITICAL: Your entire response must be ONLY the JSON object. Start with { and end with }. No prose, no fences, no explanation.",[{role:"user",content:"Objective: "+projectObjective}],9500,false,"general");
          const rc=retryRaw.trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
          try{plan=JSON.parse(rc);}catch{const s2=rc.indexOf("{");if(s2>=0){try{plan=JSON.parse(rc.slice(s2,rc.lastIndexOf("}")+1));}catch{}}if(!plan?.modules){plan=repairTruncatedJson(retryRaw);planWasTruncated=true;}}
        }
        if(!plan||!plan.modules||!Array.isArray(plan.modules)||!plan.modules.length)throw new Error("Invalid plan structure");
        plan.modules=plan.modules.filter((m)=>m&&m.name&&Array.isArray(m.deliverables)&&m.deliverables.length);
        if(!plan.modules.length)throw new Error("Invalid plan structure");
        // Comprehensive objectives with a suspiciously thin result (1 module,
        // or 1 deliverable total) are very likely truncated even if JSON.parse
        // technically succeeded on a cleanly-closed-but-incomplete fragment.
        const totalDels=plan.modules.reduce((s:number,m:any)=>s+(m.deliverables?.length||0),0);
        if((planWasTruncated||plan.modules.length<2||totalDels<2)&&projectObjective.trim().length>400){
          plan._possiblyIncomplete=true;
          showToast("\u26a0 This plan may be incomplete \u2014 your objective was detailed and the response may have been cut short. Review the plan carefully before approving; you can also try splitting a very large request into smaller ones.","warning");
        }
      }catch(parseErr:any){
        const reason=looksBad(raw)?"the AI provider returned an empty response (check API keys / usage limits, and turn OFF Multi-AI Consensus)":"the plan format could not be parsed";
        console.error("[OIQ] Plan generation failed:",reason,"| raw head:",String(raw||"").slice(0,300));
        try{const uf=WorkspaceMemory.get<any[]>("cos-unfulfilled-log")||[];uf.unshift({ts:new Date().toISOString(),project:projectObjective.slice(0,80),deliverable:"Execution Plan",format:"plan",error:reason+" :: "+String(raw||"").slice(0,120)});WorkspaceMemory.set("cos-unfulfilled-log",uf.slice(0,50));}catch{}
        showToast("Plan generation failed \u2014 "+reason+".","error");
        setProjectPlanning(false);
        return;
      }
      // Enrich plan with metadata
      plan.id="proj_"+Date.now();
      plan.objective=projectObjective;
      plan.status="planning";
      plan.createdAt=new Date().toISOString();
      plan.context=ctx;
      plan.architectReasoning=architectReasoning;
      // Initialise all deliverable lifecycle fields
      (plan.modules||[]).forEach(mod=>{
        (mod.deliverables||[]).forEach(del=>{
          del.status="planned";
          del.moduleId=mod.id;
          del.moduleOwner=mod.name;
          del.aiPersona=mod.primaryPersona||"ceo";
          del.plannedAt=new Date().toISOString();
          del.startedAt=null;
          del.completedAt=null;
          del.rawContent=null;
          del.renderedFile=null;
          del.qaResult=null;
          del.verificationStatus=del.verificationStatus||"AI Generated";
          del.confidenceScore=del.confidenceScore||0;
          del.sourceReferences=del.sourceReferences||[];
        });
      });
      setProjectPlan(plan);
      sv("cos-project-plan",plan);
    }catch(err:any){
      // A timed-out request is still BILLED. Anthropic generated the tokens;
      // we simply stopped listening. Repeating it silently spends real money
      // for nothing, so the message has to say so rather than inviting a
      // fourth identical attempt.
      const _timedOut=/timed out|aborted|signal/i.test(String(err?.message||""));
      showToast(_timedOut
        ? "Planning timed out. Your Claude account is still charged for a timed-out request, so please do not retry the same objective repeatedly \u2014 shorten it instead, or switch Primary AI to NVIDIA which has no time limit."
        : "Project planning failed: "+err.message,"error");
    }finally{
      setProjectPlanning(false);
    }
  },[projectObjective,projectPlanning,buildProjectContext,co,ask,showToast,sv]);

  const approveProjectPlan=useCallback(()=>{
    if(!projectPlan)return;
    const approved={...projectPlan,status:"approved",approvedAt:new Date().toISOString()};
    const updated=[approved,...projects].slice(0,20);
    setProjects(updated);
    sv("cos-projects",updated);
    setActiveProject(approved);
    setProjectPlan(null);
    setProjectObjective("");
    showToast("Execution Plan approved — starting execution now.","success");
    setProjectDashboardOpen(true);
    runProjectExecutionRef.current&&runProjectExecutionRef.current(approved);
  },[projectPlan,projects,showToast,sv]);

  // ─── PROJECT ENGINE — Phase 2: Execution Engine ────────────────────────────
  const runProjectExecution=useCallback(async(project)=>{
    if(projectExecuting)return;
    setUsageFeature("Project Engine","🏗️");
    setProjectExecuting(true);
    setProjectExecCancel(false);
    projectExecCancelRef.current=false;
    setProjectExecution({...project,status:"executing"});

    // Helper: update a single deliverable across the execution state
    const updateDel=(projState,modId,delId,patch)=>{
      const mods=(projState.modules||[]).map(m=>{
        if(m.id!==modId)return m;
        return {...m,deliverables:(m.deliverables||[]).map(d=>d.id===delId?{...d,...patch}:d)};
      });
      const updated={...projState,modules:mods};
      setProjectExecution(updated);
      setProjects(prev=>{const ns=prev.map(p=>p.id===updated.id?updated:p);sv("cos-projects",ns);return ns;});
      return updated;
    };

    // Helper: check if all dependsOn deliverables are complete
    const allDepsComplete=(projState,del)=>{
      if(!del.dependsOn||del.dependsOn.length===0)return true;
      const allDels=(projState.modules||[]).flatMap(m=>m.deliverables||[]);
      return del.dependsOn.every(depId=>{
        const dep=allDels.find(d=>d.id===depId);
        return dep&&dep.status==="complete";
      });
    };

    // Helper: get rawContent of a dependency
    const getDepContent=(projState,depId)=>{
      const allDels=(projState.modules||[]).flatMap(m=>m.deliverables||[]);
      const dep=allDels.find(d=>d.id===depId);
      return dep?.rawContent?stripMd(dep.rawContent).slice(0,400):"";
    };

    // Helper: build the system prompt for a deliverable
    const buildDelSys=(ctx,mod,del,priorDepContent)=>{
      const nl="\n";
      const co=ctx.company||{};
      const fmt=(del.outputFormat||"md").toLowerCase();
      const sym=co.currencySymbol||"₹";
      const currency=co.currency||"INR";
      const coCtx=`${co.name} | ${co.industry} | ${co.stage} | ${co.location} | ${sym}${currency}`;

      // ── DOMAIN EXPERT PERSONA ─────────────────────────────────────────────
      // Assign the most qualified professional persona for this deliverable type
      const PERSONAS:{[k:string]:string}={
        finance:   "Senior FP&A Manager from McKinsey's CFO Advisory practice with 15 years experience in financial modelling, variance analysis, and management reporting for Fortune 500 companies",
        audit:     "Senior Manager from Deloitte's Internal Audit practice, expert in ITGC, SOX compliance, risk registers, and audit workpapers",
        strategy:  "Principal at BCG with expertise in corporate strategy, market entry, competitive analysis, and board-level presentations",
        marketing: "VP of Marketing with consulting background, expert in brand strategy, campaign design, ROI measurement, and executive presentations",
        design:    "Creative Director with B2B SaaS expertise, skilled in visual communication, infographic design, and brand-aligned assets",
        content:   "Senior Business Writer from Bain & Company, expert in executive communications, business cases, and client-facing deliverables",
        legal:     "Corporate counsel with M&A and compliance expertise, skilled in contract drafting and regulatory documentation",
        management:"Senior Partner with expertise in change management, operational excellence, and C-suite presentations",
        engineering:"Principal Solutions Architect with expertise in technical documentation, system design, and API specifications",
        research:  "Senior Research Analyst with expertise in primary and secondary market research, competitive intelligence, and insight reports",
        compliance:"Big4 Compliance Manager expert in regulatory frameworks, control testing, and assurance documentation",
        hr:        "CHRO Advisory specialist with expertise in HR analytics, talent strategy, and people management frameworks",
        operations:"McKinsey Operations Practice expert in process design, efficiency analysis, and operational dashboards",
        sales:     "Sales Strategy Director with expertise in pipeline analysis, revenue modelling, and go-to-market planning",
      };
      const persona=PERSONAS[mod.capabilityType]||PERSONAS[mod.capabilityType?.split("_")[0]]||"Senior Business Consultant with Big4 experience";

      // ── FORMAT-SPECIFIC OUTPUT INSTRUCTIONS ───────────────────────────────
      const FORMAT_RULES:{[k:string]:string}={
        xlsx:`
EXCEL OUTPUT RULES (CRITICAL — follow exactly):
You are building a PROFESSIONAL FINANCIAL WORKBOOK, not a table.
Structure your output as SHEETS separated by === SHEET: [Name] ===

Required sheets:
=== SHEET: Dashboard ===
(KPI summary: key metrics with formula references to data sheets)
KEY METRICS: label | value | vs_target | trend
CRITICAL: Use realistic invented numbers. NEVER write 0 or placeholder.
MRR | ${sym}42,50,000 | ${sym}38,00,000 | ▲ 11.8%
ARR | ${sym}5,10,00,000 | ${sym}4,56,00,000 | ▲
Gross Margin | 67% | 65% | ▲
Burn Rate | ${sym}18,50,000 | ${sym}20,00,000 | ✓
Cash Runway | 16 months | 18 months | ⚠
CAC | ${sym}48,500 | ${sym}45,000 | ▼
NRR | 118% | 110% | ▲
Customers | 87 | 80 | ▲

=== SHEET: Data ===
(Structured data table with headers in row 1)
Row 1: HEADERS (bold, freeze this row)
Row 2+: Data rows
Totals row: Use =SUM() formulas — example: =SUM(B2:B13)
Variances: Use =B14-C14 or =IFERROR((B14-C14)/C14,"N/A")
Conditional: Flag variances >10% with [FLAG]

=== SHEET: Analysis ===
(Derived calculations, trends, ratios)
Use formula strings: =IF(B5>0,"▲ Growth","▼ Decline")
Ratios: =IFERROR(B8/B9,0)

=== SHEET: Assumptions ===
Named | Value | Basis | Confidence
Each assumption on its own row, labeled [VERIFIED] or [ESTIMATE]

=== VBA: Refresh Macro ===
(Optional VBA code if automation adds value — copy-paste instructions)

FORMULA REQUIREMENTS:
- ALL totals must use =SUM() not hardcoded numbers
- ALL ratios must use =IFERROR(numerator/denominator,"N/A")
- ALL variances must use =Actual-Budget or =(Actual-Budget)/Budget
- Prefix formula cells with = sign
- Reference between sheets: ='Sheet Name'!B5
- Named ranges: use descriptive names in formulas where possible
- Currency: format numbers as ${sym} values
- DO NOT output pre-calculated numbers as text — use formulas`,

        pptx:`
POWERPOINT OUTPUT RULES (CRITICAL — follow exactly):
⛔ DO NOT write a slide design specification, blueprint, or brief. DO NOT write tables like "Element | Specification | Notes" describing what slides should contain. DO NOT write section headers like "SLIDE 1: COVER SLIDE" followed by specifications.
✅ Write ACTUAL slide content: real numbers, real headlines, real bullet points — content a CEO sees ON the slide itself, not instructions about how to build the slide.
You are creating a CONSULTING-GRADE PRESENTATION (McKinsey/BCG standard).
Output as structured JSON inside a code block:

\`\`\`json
{
  "title": "Presentation Title",
  "theme": "navy",
  "slides": [
    {
      "layout": "title",
      "title": "Main Title",
      "subtitle": "Subtitle",
      "meta": "Company | Date | Confidential"
    },
    {
      "layout": "exec_summary",
      "title": "SO WHAT headline — not a topic label",
      "bullets": ["Key insight 1", "Key insight 2", "Key insight 3"],
      "so_what": "The one sentence the CEO must remember"
    },
    {
      "layout": "chart",
      "title": "SO WHAT headline about this chart",
      "chart_type": "bar|line|pie|waterfall",
      "chart_title": "Chart title",
      "categories": ["Q1","Q2","Q3","Q4"],
      "series": [
        {"name": "Actual", "values": [100,120,115,140], "color": "teal"},
        {"name": "Budget", "values": [110,110,120,130], "color": "navy"}
      ],
      "insight": "One sentence explaining what the chart shows"
    },
    {
      "layout": "two_col",
      "title": "SO WHAT headline",
      "left_header": "Left Column Header",
      "left": ["Bullet 1", "Bullet 2", "Bullet 3"],
      "right_header": "Right Column Header",
      "right": ["Bullet 1", "Bullet 2", "Bullet 3"]
    },
    {
      "layout": "table",
      "title": "SO WHAT headline",
      "headers": ["Column 1", "Column 2", "Column 3"],
      "rows": [["R1C1","R1C2","R1C3"],["R2C1","R2C2","R2C3"]],
      "highlight_row": 0
    },
    {
      "layout": "closing",
      "title": "Recommendation / Next Steps",
      "actions": ["Action 1: Owner | Timeline", "Action 2: Owner | Timeline"],
      "bottom_line": "The single most important takeaway"
    }
  ]
}
\`\`\`

SLIDE QUALITY RULES:
- Every slide title must be a "SO WHAT" sentence, not a topic label
  BAD: "Revenue Analysis" | GOOD: "Revenue grew 23% YoY driven by enterprise segment"
- Include at least 2 chart slides with real numerical data
- Every chart must have actual numbers that tell a story
- Speaker notes: add as "notes" field on each slide
- Minimum 8 slides, maximum 15 slides
- Closing slide must have specific, numbered action items`,

        pdf:`
PDF / REPORT OUTPUT RULES:
You are producing a PUBLICATION-QUALITY business report (Big4/consulting standard).
Structure:

# COVER
Title | Author | Date | Classification: Confidential

# EXECUTIVE SUMMARY (max 250 words)
3-5 key findings. What the reader must know before anything else.

# KEY FINDINGS
1. **Finding headline** — Supporting evidence. [VERIFIED/ESTIMATE]
2. **Finding headline** — Supporting evidence.

# [NUMBERED SECTIONS — each section earns its place]
## 1.0 [Section Title]
### 1.1 [Subsection]
Content with evidence-based statements.
Tables formatted as:
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data     | Data     | Data     |

# RECOMMENDATIONS
1. **[Specific action]** — Rationale. Owner: [role]. Timeline: [period].
2. **[Specific action]** — Rationale. Owner: [role]. Timeline: [period].

# APPENDIX
Supporting data, methodology, assumptions

QUALITY RULES:
- Every claim must have evidence or be labeled [ESTIMATE]
- No generic statements — every sentence must add information
- Tables must have headers and be properly formatted
- Recommendations must be specific, measurable, and assigned`,

        docx:`
WORD DOCUMENT OUTPUT RULES:
Produce a SUBMISSION-READY document.
Use the same structure as PDF above.
Include:
- Professional heading hierarchy (H1 → H2 → H3)
- Properly formatted tables with bold headers
- Executive Summary always first
- Numbered recommendations always last
- Appendix for supporting data`,

        md:`
REPORT OUTPUT RULES:
Produce a COMPLETE, professional business document.
- Use proper markdown: ## for sections, ### for subsections
- Every table must have headers and alignment
- Every claim must be evidence-based or labeled [ESTIMATE/ASSUMPTION]
- Executive Summary first, Recommendations last
- Minimum 800 words for any analytical deliverable`,
      };

      const needsFictional=(del.description||"").toLowerCase().includes("fictional")||(del.name||"").toLowerCase().includes("fictional")||(priorDepContent||"").toLowerCase().includes("fictional data");
      const fictionalNote=needsFictional?`\n\n⚠ FICTIONAL DATA MANDATE: This deliverable requires invented realistic data. DO NOT use this company's real figures which may be ₹0. Invent specific, plausible, internally consistent numbers for a ${co.industry} company at ${co.stage} stage. Example ranges: MRR ₹18L–₹85L, customers 25–180, gross margin 55–75%, burn ₹12L–₹35L/month, runway 12–24 months. Every cell, every slide, every table must have a specific invented number — never a placeholder.\n\n`:"";
      const formatRules=FORMAT_RULES[fmt]||FORMAT_RULES.md;

      // ── QUALITY STANDARD ─────────────────────────────────────────────────
      const qualityStandard=`
QUALITY STANDARD:
Could this output be presented directly to a CFO, Board, or senior client WITHOUT manual editing?
If no, it is not complete. Do not submit incomplete work.
Every number must be sourced or labeled. Every recommendation must be specific and actionable.
This is McKinsey/Deloitte standard — not a draft, not a template, not a starting point.
ANTI-PLACEHOLDER RULE: NEVER write [X], [XXXX], [TBD], [INSERT], [PENDING], [UNKNOWN], [DATA GAP], [MISSING DATA], [CRITICAL GAP], [UNDEFINED], [ESTIMATE PENDING], [NOT FOUND], [Current Month/Year], or ANY bracket placeholder for any number, name, date, or value. If exact data is unavailable, INVENT a specific plausible value for a ${co.industry} ${co.stage}-stage company and label it (A: assumed). A placeholder is a failure. An invented labeled assumption is always preferred.`;

      // ── BUILD THE FULL SYSTEM PROMPT ─────────────────────────────────────
      return `You are a ${persona}.

ENGAGEMENT CONTEXT:
Company: ${coCtx}
${Object.keys(ctx.dataHub||{}).length?`DATA AVAILABLE:\n${Object.entries(ctx.dataHub).map(([k,v])=>`${k}: ${v}`).join(nl)}\n`:""}${ctx.timeMachineForecasts?`FORECASTS: ${ctx.timeMachineForecasts}\n`:""}${(ctx.boardroomDecisions||[]).length?`BOARD DECISIONS: ${ctx.boardroomDecisions.map((d:any)=>`${d.question} → ${d.decisionStatus}`).join(" | ")}\n`:""}${priorDepContent?`\nPRIOR WORK (use as input — do not repeat):\n${priorDepContent}\n`:""}

DELIVERABLE TO PRODUCE:
Name: ${del.name}
Purpose: ${del.description}
Output Format: ${fmt.toUpperCase()}
${fictionalNote}${formatRules}

DATA LABELING:
- [VERIFIED]: confirmed data from provided sources
- [ESTIMATE]: calculated or inferred
- [ASSUMPTION]: stated assumption requiring validation

${qualityStandard}

Now produce the complete ${del.name}. Start with content immediately — no preamble.`;
    };

    // Helper: parse markdown content into slide schema when AI doesn't output JSON
    const parseMdToSlides=(md:string,coName:string,title:string)=>{
      const lines=md.split("\n").filter((l:string)=>l.trim());
      const slides:any[]=[{layout:"title",title,subtitle:coName,meta:`${coName} · ${new Date().toLocaleDateString("en-GB")}`}];
      let currentSlide:any=null;
      const pushCurrent=()=>{if(currentSlide?.bullets?.length||currentSlide?.content)slides.push(currentSlide);};
      for(const line of lines){
        if(line.startsWith("# ")){
          pushCurrent();
          currentSlide={layout:"exec_summary",title:line.replace(/^#+\s*/,""),bullets:[]};
        } else if(line.startsWith("## ")){
          pushCurrent();
          currentSlide={layout:"full_text",title:line.replace(/^#+\s*/,""),bullets:[]};
        } else if(line.startsWith("### ")){
          pushCurrent();
          currentSlide={layout:"full_text",title:line.replace(/^#+\s*/,""),bullets:[]};
        } else if(currentSlide){
          const clean=line.replace(/^[-*•]\s*/,"").trim();
          if(clean) currentSlide.bullets=(currentSlide.bullets||[]).concat([clean]);
        }
      }
      pushCurrent();
      slides.push({layout:"closing",title:"Next Steps & Recommendations",actions:["Review findings with leadership","Assign owners and timelines","Implement priority actions"]});
      return {title,slides};
    };

    // Helper: call AI with retry + failover + waiting
    const callDelAI=async(sys,userMsg,maxT,delLabel,delTask)=>{
      const providerOrder=[];
      // Gated: Project Engine deliverables skip switched-off providers.
      const allKeys:any=enabledKeys(keys);
      // Primary-aware routing (honors the user's Primary AI + specialist chains).
      // The old hardcoded "deepseek first" ladder bypassed the router entirely.
      const preferred=resolveRoute(delTask||"general",defP);
      if(!preferred.includes("kimi")&&allKeys.kimi?.trim())preferred.push("kimi");
      preferred.forEach(p=>{if(allKeys[p]?.trim())providerOrder.push(p);});
      if(!providerOrder.length)throw new Error("No API keys configured.");

      let lastErr=null;
      const delDeadline=Date.now()+240000; // hard 4-minute cap per deliverable — fail fast, never hang
      for(let attempt=0;attempt<2;attempt++){
        for(const prov of providerOrder){
          if(projectExecCancelRef.current)throw new Error("CANCELLED");
          if(Date.now()>delDeadline)throw new Error("Deliverable timed out after 4 minutes (providers unresponsive): "+delLabel);
          const key=allKeys[prov]?.trim();
          if(!key)continue;
          try{
            setProjectExecPhase("Generating: "+delLabel+" ("+prov+")...");
            const r=await callAI(prov,key,sys,[{role:"user",content:userMsg}],maxT);
            return r.text||r;
          }catch(e){
            lastErr=e;
            if(e.message==="CANCELLED")throw e;
            if(isRateLimit(e.message)){
              markProviderExhausted(prov);
              continue; // try next provider
            }
            continue; // try next provider on any error
          }
        }
        // All providers tried — wait before retry
        if(attempt===0){
          setProjectExecPhase("All providers at limit — waiting 65s before retry for: "+delLabel);
          // Same fix, same reason. Every long wait in the product must be
          // interruptible or Cancel is decoration. projectExecCancelRef is the
          // flag the existing Stop button already sets - this wait simply never
          // looked at it.
          await waitWithCountdown(65,(s)=>setProjectExecPhase("Retry in "+s+"s \u2014 "+delLabel+" (Stop to cancel)"),
            ()=>projectExecCancelRef.current);
          if(projectExecCancelRef.current)break;
        }
      }
      throw lastErr||new Error("All providers exhausted for: "+delLabel);
    };

    // Helper: generate deliverable (with chunking for large deliverables)
    const generateDeliverable=async(projState,mod,del)=>{
      const ctx=projState.context||buildProjectContext();
      const priorDepContent=(del.dependsOn||[]).map(id=>getDepContent(projState,id)).filter(Boolean).join("\n\n");
      const sys=buildDelSys(ctx,mod,del,priorDepContent);
      let userMsg="Generate the complete deliverable now: "+del.name;

      // Estimate if chunking is needed based on description length
      const needsChunking=del.description.length>300||["blog","landing page","press release","terms","privacy","contract","roadmap","forecast","excel","xlsx","financial","pptx","presentation","deck","report","analysis","audit","strategy"].some(k=>del.name.toLowerCase().includes(k)||del.outputFormat==="xlsx"||del.outputFormat==="pptx"||del.outputFormat==="pdf");

      if(!needsChunking){
        const isLargeDeliverable=["xlsx","pptx","pdf","docx"].includes(del.outputFormat?.toLowerCase()||"")||del.description.length>200;
      const delTask=({xlsx:"excel_advanced",pptx:"powerpoint",pdf:"financial",docx:"financial"})[del.outputFormat]||"general";
      if(del.outputFormat==="pptx")userMsg+="\n\nThe deck MUST contain 12-16 substantive slides covering the full narrative arc (title, executive summary, agenda, 6-10 content/analysis slides with data, recommendations, next steps, closing).";
      // Phase 4: platform-specific writing conventions for social/messaging deliverables —
      // each platform has a genuinely different register; write to that convention, not a generic post.
      const SOCIAL_GUIDANCE:Record<string,string>={
        linkedin_post:"\n\nWrite ONE ready-to-post LinkedIn post. Professional register, short paragraphs (1-2 sentences each) with line breaks for readability, a strong opening hook line, 150-300 words, end with 3-5 relevant hashtags on their own line. No markdown formatting symbols — this gets copy-pasted directly into LinkedIn's editor.",
        facebook_post:"\n\nWrite ONE ready-to-post Facebook post. Warmer and more conversational than LinkedIn, storytelling angle, 80-200 words, can include 1-2 emojis naturally, end with a clear call to action. No markdown formatting symbols.",
        instagram_post:"\n\nWrite ONE ready-to-post Instagram caption. Punchy opening line, short and scannable, emoji-friendly throughout, 50-150 words, end with a block of 8-15 relevant hashtags on their own line separated by spaces. No markdown formatting symbols.",
        whatsapp_message:"\n\nWrite ONE ready-to-send WhatsApp business message. Very short (under 60 words), direct and conversational, no hashtags, no formal structure, one clear call to action, sounds like a real person typing on their phone.",
        email:"\n\nWrite ONE ready-to-send email. First line must be exactly \"Subject: \" followed by a compelling subject line, then a blank line, then the email body with a professional greeting, clear body paragraphs, and a signed closing. No markdown formatting symbols.",
      };
      if(SOCIAL_GUIDANCE[del.outputFormat])userMsg+=SOCIAL_GUIDANCE[del.outputFormat];
      return await callDelAI(sys,userMsg,del.outputFormat==="pptx"?6500:isLargeDeliverable?4500:2500,del.name,delTask);
      }

      // Chunked generation — split into logical sections
      setProjectExecPhase("Planning sections for: "+del.name);
      const planSys=sys+"\n\nFirst, list the logical sections for this deliverable as a JSON array of strings. Output ONLY the JSON array, no other text. Maximum 4 sections.";
      let sections=[];
      try{
        const planRaw=await callDelAI(planSys,"List sections for: "+del.name,400,del.name+" (planning)",({xlsx:"excel_advanced",pptx:"powerpoint",pdf:"financial",docx:"financial"})[del.outputFormat]||"general");
        const cleaned=planRaw.trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
        sections=JSON.parse(cleaned);
        if(!Array.isArray(sections)||sections.length===0)throw new Error("No sections");
      }catch{
        // If planning fails, generate as single chunk
        return await callDelAI(sys,userMsg,2500,del.name,({xlsx:"excel_advanced",pptx:"powerpoint",pdf:"financial",docx:"financial"})[del.outputFormat]||"general");
      }

      const chunks=[];
      for(let si=0;si<sections.length;si++){
        if(projectExecCancelRef.current)throw new Error("CANCELLED");
        const section=sections[si];
        setProjectExecPhase("Generating section "+(si+1)+"/"+sections.length+": "+section);
        const chunkSys=sys+"\n\nYou are generating SECTION "+(si+1)+" of "+sections.length+" of this deliverable."+(chunks.length?"\n\nCOMPLETED SECTIONS SO FAR:\n"+chunks.join("\n\n"):"");
        const chunkContent=await callDelAI(chunkSys,"Generate section: "+section,1800,del.name+" §"+(si+1),({xlsx:"excel_advanced",pptx:"powerpoint",pdf:"financial",docx:"financial"})[del.outputFormat]||"general");
        chunks.push("## "+section+"\n\n"+chunkContent);
      }
      return chunks.join("\n\n");
    };

    // ── MAIN EXECUTION LOOP ──────────────────────────────────────────────────
    let currentProj={...project,status:"executing"};

    // Build flat list of all deliverables for dependency tracking
    const getAllDels=()=>(currentProj.modules||[]).flatMap(m=>(m.deliverables||[]).map(d=>({...d,_modId:m.id,_modName:m.name})));

    // Queue all deliverables
    currentProj.modules=(currentProj.modules||[]).map(m=>({
      ...m,
      status:"running",
      deliverables:(m.deliverables||[]).map(d=>({...d,status:"queued"}))
    }));
    setProjectExecution({...currentProj});

    let totalDels=getAllDels().length;
    let completedCount=0;
    let failedCount=0;

    // Process in waves — each wave processes all deliverables whose deps are met
    const MAX_WAVES=20;
    for(let wave=0;wave<MAX_WAVES;wave++){
      if(projectExecCancelRef.current){
        currentProj={...currentProj,status:"cancelled"};
        break;
      }

      const pending=getAllDels().filter(d=>d.status==="queued"||d.status==="waiting");
      if(pending.length===0)break;

      const ready=pending.filter(d=>allDepsComplete(currentProj,d));
      const blocked=pending.filter(d=>!allDepsComplete(currentProj,d));

      if(ready.length===0&&blocked.length>0){
        // All remaining are blocked — check if any blocker is failed
        const blockerIds=blocked.flatMap(d=>d.dependsOn||[]);
        const allDels=getAllDels();
        const failedBlockers=blockerIds.filter(id=>{
          const dep=allDels.find(d=>d.id===id);
          return dep&&dep.status==="failed";
        });
        if(failedBlockers.length===blockerIds.length){
          // All blockers failed — mark blocked as failed too
          for(const bd of blocked){
            const mod=currentProj.modules.find(m=>m.id===bd._modId);
            if(mod){currentProj=updateDel(currentProj,bd._modId,bd.id,{status:"failed",rawContent:"Blocked by failed dependency."});}
            failedCount++;
          }
        }
        break;
      }

      // Process all ready deliverables (sequentially to avoid rate limits)
      for(const del of ready){
        if(projectExecCancelRef.current)break;
        const mod=currentProj.modules.find(m=>m.id===del._modId);
        if(!mod)continue;

        // Mark as generating
        currentProj=updateDel(currentProj,del._modId,del.id,{
          status:"generating",startedAt:new Date().toISOString()
        });

        let rawContent=null;
        let genFailed=false;

        try{
          rawContent=await generateDeliverable(currentProj,mod,del);
        }catch(e){
          if(e.message==="CANCELLED")break;
          genFailed=true;
          currentProj=updateDel(currentProj,del._modId,del.id,{
            status:"failed",
            rawContent:"Generation failed: "+e.message,
            completedAt:new Date().toISOString(),
            confidenceScore:0,
            verificationStatus:"Failed"
          });
          failedCount++;
          showToast("⚠ "+del.name+" failed: "+e.message.slice(0,60),"warning");
          continue;
        }

        if(!genFailed&&rawContent){
          // ── QUALITY ANNOTATION ────────────────────────────────────────────
          // Records what was requested vs what was delivered so the user can
          // benchmark the output. No blocking, no retry loop — the prompt
          // improvements are what drive quality, not retry gates.
          const hasFatalPlaceholder=(t:string)=>/\[INSERT\]|\[TBD\]|PLACEHOLDER|Lorem ipsum|\[X+\]|TODO:/i.test(t);
          // Quick QA: detect placeholders
          const hasPlaceholders=/\[INSERT\]|\[TBD\]|PLACEHOLDER|Lorem ipsum/i.test(rawContent);
          const hasAssumptions=/\[ASSUMPTION\]|\[ESTIMATE\]/i.test(rawContent);
          const confidenceScore=hasPlaceholders?30:hasAssumptions?65:85;
          const verificationStatus=hasPlaceholders?"Requires User Input":hasAssumptions?"AI Generated":"AI Generated";

          rawContentStore.current[del.id]=rawContent;
          // Store full content — no artificial cap. Try/catch handles storage quota.
          try{
            localStorage.setItem("cos-rc-"+del.id,rawContent);
          }catch(e){
            // Storage quota reached — try with truncated version
            try{localStorage.setItem("cos-rc-"+del.id,rawContent.slice(0,20000));}catch{}
            console.warn("[OIQ] localStorage quota reached for deliverable",del.id);
          }
          const rawPreview=rawContent.slice(0,800)+(rawContent.length>800?"\n\n[...full content in export backup...]":"");
          currentProj=updateDel(currentProj,del._modId,del.id,{
            status:"complete",
            rawContent:rawPreview,
            completedAt:new Date().toISOString(),
            confidenceScore,
            verificationStatus,
            qaResult:{
              passed:!hasPlaceholders,
              checkedAt:new Date().toISOString(),
              flags:hasPlaceholders?[{type:"placeholder",location:"content",original:"[placeholder detected]",suggestion:"Fill in with real data"}]:[],
              score:confidenceScore
            }
          });
          completedCount++;
          setProjectExecPhase("✓ "+del.name+" complete ("+completedCount+"/"+totalDels+")");
        }
      }
    }

    // Finalize project status
    const execStatus=completedCount===totalDels?"executed":failedCount>0?"partial":"executed";
    currentProj={...currentProj,status:execStatus,completedAt:new Date().toISOString()};
    setProjectExecution(currentProj);
    setProjects(prev=>{const ns=prev.map(p=>p.id===currentProj.id?currentProj:p);sv("cos-projects",ns);return ns;});
    setProjectExecuting(false);
    setProjectExecPhase("");
    // Phase 6: the automated post-execution QA pass is removed entirely — it was
    // a second AI call per deliverable asking the AI to grade its own work, and
    // could flip an already-successful deliverable to "qa_failed" on nothing
    // more than its own unreliable self-assessment. The instant, fact-based
    // per-deliverable label computed above (placeholders/assumptions detected
    // directly from the content) already gives an honest signal without a
    // slow, blocking, self-grading extra pass. Quality now improves through
    // your own review and specific feedback below, not an automated gate.
    if(completedCount===0)showToast("⚠ No deliverables completed","warning");

  // THE FIX: same bug class as the one already documented in
  // runProjectPackage below (keys missing from its list once caused every
  // image to fail) — here it's defP, the setting that decides which AI
  // provider gets tried for EVERY deliverable this function generates.
  // Without it listed, this function could keep using whatever defP was on
  // the very first render, forever - even after you changed your actual
  // default provider in Settings. If your one working key isn't the
  // provider this stale value points at, every deliverable fails, which
  // matches "anything I try gives an error" exactly.
  },[projectExecuting,keys,defP,buildProjectContext,showToast,sv,isRateLimit,markProviderExhausted,waitWithCountdown,stripMd,callAI]);
  // Keep ref current so approveProjectPlan can call it without dep ordering issues
  runProjectExecutionRef.current=runProjectExecution;

  // Phase 6: user-driven iteration — replaces the automated self-grading QA
  // pass. You review what was generated; if something needs changing, say
  // exactly what and this makes ONLY that change, leaving everything else
  // that already worked untouched. Self-contained: does not depend on any
  // function scoped inside runProjectExecution, so it is safe to call from
  // the review panel independently of an active execution run.
  const regenerateDeliverableWithFeedback=useCallback(async(modId:string,delId:string,delName:string,currentContent:string)=>{
    const feedback=(projectFeedback[delId]||"").trim();
    if(!feedback){showToast("Enter what you'd like changed first","warning");return;}
    setProjectRegeneratingId(delId);
    try{
      const sys="You are revising a business deliverable based on specific user feedback. Make ONLY the change the feedback asks for \u2014 keep everything else that already works exactly as it is. Output ONLY the complete revised content: no preamble, no explanation of what changed, no markdown fences.";
      const msg="DELIVERABLE: "+delName+"\n\nCURRENT CONTENT:\n"+currentContent.slice(0,4000)+"\n\nUSER FEEDBACK \u2014 apply this specific change:\n"+feedback;
      const revised=await ask(sys,[{role:"user",content:msg}],3000,false,"general");
      const revisedText=(typeof revised==="string"?revised:(revised as any)?.text||"").trim();
      if(!revisedText){showToast("Regeneration returned nothing \u2014 try again","error");setProjectRegeneratingId(null);return;}
      rawContentStore.current[delId]=revisedText;
      try{localStorage.setItem("cos-rc-"+delId,revisedText);}catch{}
      const preview=revisedText.slice(0,800)+(revisedText.length>800?"\n\n[...full content in export backup...]":"");
      setProjectExecution((prev:any)=>prev?{...prev,modules:(prev.modules||[]).map((m:any)=>m.id!==modId?m:{...m,deliverables:(m.deliverables||[]).map((d:any)=>d.id!==delId?d:{...d,rawContent:preview})})}:prev);
      setProjectFeedback(prev=>({...prev,[delId]:""}));
      showToast("\u2713 Updated: "+delName,"success");
    }catch(e:any){
      showToast("Regeneration failed: "+(e?.message||"unknown error"),"error");
    }
    setProjectRegeneratingId(null);
  },[projectFeedback,ask,showToast]);

  const runProjectQA=useCallback(async(proj)=>{
    if(projectQARunning||!proj)return;
    setProjectQARunning(true);
    setProjectExecPhase("🔍 QA Agent reviewing deliverables...");
    let qaProj={...proj,status:"qa"};
    setProjectExecution(qaProj);
    const allDels=(qaProj.modules||[]).flatMap(m=>(m.deliverables||[]).map(d=>({...d,_modId:m.id})));
    const completedDels=allDels.filter(d=>d.status==="complete"&&d.rawContent);
    const ctx=qaProj.context||{};
    const brandName=ctx.company?.name||"the company";
    const currency=ctx.company?.currencySymbol||"₹";
    const updateDelQA=(modId,delId,qaResult,newStatus)=>{
      qaProj={...qaProj,modules:(qaProj.modules||[]).map(m=>{
        if(m.id!==modId)return m;
        return{...m,deliverables:(m.deliverables||[]).map(d=>d.id===delId?{...d,qaResult,status:newStatus,verificationStatus:qaResult.passed?"AI Generated":"Requires User Input",confidenceScore:qaResult.score}:d)};
      })};
      setProjectExecution({...qaProj});
    };
    for(let i=0;i<completedDels.length;i++){
      const del=completedDels[i];
      setProjectExecPhase("🔍 QA: "+del.name+" ("+(i+1)+"/"+completedDels.length+")");
      try{
        // Gated: the QA reviewer skips switched-off providers.
        const allKeys:any=enabledKeys(keys);
        const provs=["deepseek","claude","openai","gemini","groq","kimi"].filter(p=>allKeys[p]?.trim());
        if(!provs.length){updateDelQA(del._modId,del.id,{passed:true,score:70,checkedAt:new Date().toISOString(),flags:[],summary:"QA skipped — no key"},del.status);continue;}
        const qaProv=provs[0];
        const qaKey=allKeys[qaProv];
        const qaSys="You are a QA reviewer for "+brandName+". Review the deliverable and output ONLY a JSON object with this exact schema: "+
          '{"passed":true,"score":85,"summary":"one sentence","flags":[{"type":"placeholder","location":"where","original":"text","suggestion":"fix"}]}\n'+ 
          "Rules: passed=true if score>=70 and no placeholders. "+
          "Check: placeholders ([INSERT],[TBD],Lorem ipsum), unverified stats, incomplete sections, brand name ("+brandName+"), currency ("+currency+"). "+
          "Score: 90+=excellent, 70-89=good, 50-69=needs revision, <50=major issues.";
        const fullContent=rawContentStore.current[del.id]||del.rawContent||"";
        const qaMsg="DELIVERABLE: "+del.name+"\nFORMAT: "+del.outputFormat+"\n\nCONTENT:\n"+fullContent.slice(0,3000);
        const qaRaw=await callAI(qaProv,qaKey,qaSys,[{role:"user",content:qaMsg}],800);
        let qaResult={passed:true,score:80,checkedAt:new Date().toISOString(),flags:[],summary:"QA passed"};
        try{
          const cleaned=(qaRaw.text||"").trim().replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/,"");
          const parsed=JSON.parse(cleaned);
          qaResult={...parsed,checkedAt:new Date().toISOString()};
        }catch{}
        const newStatus=qaResult.passed?"complete":"qa_failed";
        updateDelQA(del._modId,del.id,qaResult,newStatus);
        if(!qaResult.passed&&qaResult.score<50){
          setProjectExecPhase("♻ Regenerating: "+del.name);
          try{
            const fixSys="Rewrite this deliverable fixing all QA issues. Output corrected deliverable only.";
            const fixFullContent=rawContentStore.current[del.id]||del.rawContent||"";
            const fixMsg="DELIVERABLE: "+del.name+"\nORIGINAL:\n"+fixFullContent.slice(0,2000)+"\n\nFIX THESE:\n"+qaResult.flags.map(f=>f.type+": "+f.suggestion).join("\n");
            const fixRaw=await callAI(qaProv,qaKey,fixSys,[{role:"user",content:fixMsg}],2000);
            const fixedContent=fixRaw.text||del.rawContent;
            rawContentStore.current[del.id]=fixedContent;
            const fixedPreview=fixedContent.slice(0,800)+(fixedContent.length>800?"\n\n[...content stored, available in ZIP...]":"");
            qaProj={...qaProj,modules:(qaProj.modules||[]).map(m=>m.id!==del._modId?m:{...m,deliverables:(m.deliverables||[]).map(d=>d.id!==del.id?d:{...d,rawContent:fixedPreview,status:"complete",confidenceScore:75,qaResult:{...qaResult,passed:true,score:75,summary:"Auto-regenerated after QA failure"}})})};
            setProjectExecution({...qaProj});
          }catch{}
        }
      }catch(e){
        updateDelQA(del._modId,del.id,{passed:true,score:70,checkedAt:new Date().toISOString(),flags:[],summary:"QA error: "+e.message.slice(0,60)},del.status);
      }
    }
    const qaFailed=(qaProj.modules||[]).flatMap(m=>m.deliverables||[]).filter(d=>d.status==="qa_failed").length;
    const finalStatus=qaFailed===0?"complete":"partial";
    qaProj={...qaProj,status:finalStatus};
    setProjectExecution({...qaProj});
    setProjects(prev=>{const ns=prev.map(p=>p.id===qaProj.id?qaProj:p);sv("cos-projects",ns);return ns;});
    setProjectQARunning(false);
    setProjectExecPhase("");
    showToast("✅ QA complete — "+completedDels.length+" deliverables reviewed","success");
  },[projectQARunning,keys,showToast,sv,callAI]);

  // ─── PROJECT ENGINE — Phase 5: Media Generation ─────────────────────────
  const buildMediaPrompt=useCallback((del,ctx)=>{
    const company  = ctx?.company?.name || "the company";
    const industry = ctx?.company?.industry || "business";
    const location = ctx?.company?.location || "";
    const rawContent = (rawContentStore.current[del.id]||del.rawContent||del.description||"").trim();
    const isVideo = del.outputFormat==="video"||["video","animation","reel","promotional","promo","overview","film"].some((k:string)=>del.name.toLowerCase().includes(k));
    if(isVideo){
      const coreMessage = rawContent.includes("VOICEOVER")
        ? rawContent.split("\n").filter((l:string)=>l.includes("VOICEOVER")).map((l:string)=>l.replace(/\*\*VOICEOVER[^:]*:\*\*/i,"").trim()).filter(Boolean).join(" ").slice(0,300)
        : rawContent.slice(0,300);
      const kling = `Cinematic promotional video for ${company}. Core message: "${coreMessage.slice(0,200)}". Style: modern tech corporate, smooth camera, 4K, professional. Industry: ${industry}. ${location?"Location context: "+location+".":""} No text overlays. Aspirational premium feel.`;
      const veo   = `Professional ${industry} company (${company}) video. Message: ${coreMessage.slice(0,200)}. Visual: Modern office, AI interfaces, confident professionals. Colors: deep navy, teal, white. Smooth dolly shots. 4K. No text.`;
      const runway = `${company} AI platform promo video. ${coreMessage.slice(0,150)}. Premium tech brand. Clean modern aesthetic. 16:9.`;
      return {type:"video",kling,veo,runway};
    }
    const isComparison = ["comparison","diagram","vs","before","after","difference"].some((k:string)=>del.name.toLowerCase().includes(k));
    const isHero = ["hero","banner","cover","header","main","feature"].some((k:string)=>del.name.toLowerCase().includes(k));
    const colorMatch = rawContent.match(/#[0-9A-Fa-f]{6}/g);
    const colors = colorMatch?colorMatch.slice(0,3).join(", "):"deep navy, teal, white";
    const visualSpec = rawContent.split("\n").filter((l:string)=>l.length>20&&["visual","style","color","design","image","show","background"].some(kw=>l.toLowerCase().includes(kw))).slice(0,4).join(". ").slice(0,300);
    const isLinkedInDel=del.name.toLowerCase().includes("linkedin")||(del.description||"").toLowerCase().includes("linkedin");
    let dalle = "";
    if(isLinkedInDel){
      dalle=`Professional LinkedIn post banner for ${company}, ${industry} company. Deep navy background. Bold white headline text showing key business metric. Teal accent cards with growth KPIs. Abstract upward data visualization. Premium executive brand. Clean minimalist. Highly legible at small size. No people. 16:9 landscape 1200x627. Corporate premium quality.`;
    } else if(isComparison){
      dalle = `Clean professional infographic comparison for ${company}. Split: left=old manual workflow (warm reds/grays), right=AI-automated workflow (cool teals/white). Central transition arrow. McKinsey consulting aesthetic. ${colors} palette. Abstract icons only, no real text. White background. 16:9.`;
    } else if(isHero){
      dalle = `Premium hero image for ${company}, an AI ${industry} platform. ${visualSpec||"Futuristic glowing AI interface connected to business workflows."}. Professional aspirational enterprise aesthetic. ${colors}. Cinematic lighting, depth of field. No text overlays. 16:9 landscape.`;
    } else {
      dalle = `Professional marketing visual for ${company}, ${industry} AI platform. Deliverable: ${del.name}. ${visualSpec.slice(0,200)||"Modern corporate aesthetic, premium feel."}. Color palette: ${colors}. Ultra high quality, 16:9 ratio. No text overlays.`;
    }
    return {type:"image",dalle,midjourney:dalle+" --ar 16:9 --style raw --q 2",stability:dalle};
  },[]);

  // THE FIX: this was the THIRD independent copy of the same bug found across
  // this file - calling dall-e-3, a model OpenAI removed from its API on
  // May 12, 2026. This one specifically is used in FOUR places in Project
  // Engine, so this single fix repairs all four call sites at once. Switched
  // to gpt-image-2 (current flagship), and to base64 parsing - confirmed
  // against OpenAI's own official API reference that GPT Image models never
  // return a hosted url the way the old DALL-E models did.
  const callDallE=useCallback(async(prompt,openaiKey)=>{
    const r=await fetch("https://api.openai.com/v1/images/generations",{
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+openaiKey.trim()},
      body:JSON.stringify({model:"gpt-image-2",prompt:prompt.slice(0,32000),n:1,size:"1536x1024",quality:"high"})
    });
    if(!r.ok){const t=await r.text().catch(()=>"");throw new Error("OpenAI image: "+r.status+" "+t.slice(0,100));}
    const d=await r.json();
    const b64=d.data?.[0]?.b64_json;
    return b64?("data:image/png;base64,"+b64):null;
  },[]);

  const callStabilityAI=useCallback(async(prompt,stabilityKey)=>{
    const r=await fetch("https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image",{
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+stabilityKey.trim(),"Accept":"application/json"},
      body:JSON.stringify({text_prompts:[{text:prompt.slice(0,2000),weight:1}],cfg_scale:7,height:1024,width:1024,samples:1,steps:30})
    });
    if(!r.ok){const t=await r.text().catch(()=>"");throw new Error("Stability: "+r.status+" "+t.slice(0,100));}
    const d=await r.json();
    const b64=d.artifacts?.[0]?.base64;
    if(!b64)return null;
    const bytes=atob(b64);
    const arr=new Uint8Array(bytes.length);
    for(let i=0;i<bytes.length;i++)arr[i]=bytes.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr],{type:"image/png"}));
  },[]);

  const runProjectPackage=useCallback(async(proj)=>{
    if(projectPackaging||!proj)return;
    setProjectPackaging(true);
    setProjectExecPhase("📦 Packaging files...");
    try{
      const JSZip=await ensureJSZip();
      const zip=new JSZip();
      const allDels=(proj.modules||[]).flatMap(m=>(m.deliverables||[]).map(d=>({...d,_modName:m.name})));
      const excluded=proj._excluded||{};
      const done=allDels.filter(d=>d.rawContent&&d.status!=="failed"&&!excluded[d.id]);
      // Execute deliverables serially — yield UI between each to prevent blocking
      const yieldUI=()=>new Promise<void>(r=>setTimeout(r,0));
      engineDowngrades.list=[]; // reset per packaging run
      qualityWarnings.list=[]; // reset per packaging run
      for(const del of done){
        if(projectExecCancelRef.current)break;
        await yieldUI(); // Release UI thread before each deliverable
        const folder=del._modName.replace(/[^a-zA-Z0-9]/g,"-");
        const fname=del.name.replace(/[^a-zA-Z0-9]/g,"-");
        const fmt=(del.outputFormat||"md").toLowerCase();
        const lsContent=(()=>{try{return localStorage.getItem("cos-rc-"+del.id)||"";}catch{return "";}})();
        const content=(rawContentStore.current[del.id]||lsContent||del.rawContent||"").replace(/^\s*(Here is|Here's|Below is|I've created|I have created|I'll create)[^\n]*\n+/i,"").replace(/^\s*(Here is|Here's) SECTION[^\n]*$/gmi,"").replace(/^\s*Here is Section \d+[^\n]*$/gmi,"").replace(/^\s*Here is the (complete|final)[^\n]*$/gmi,"");
        if(fmt==="docx"){
          // ── RAILWAY PYTHON SERVICE: Real .docx binary ────────────────────
          let _docDone=false;
          try{
            const _railwayUrl="https://orchestriq-gen-service-production.up.railway.app";
            const _allContent=(rawContentStore.current[del.id]||del.rawContent||"").replace(/^\s*(Here is|Here's)[^\n]*\n+/i,"");
            const _coCtx=[proj.context?.company?.name||co.name||"",proj.context?.company?.industry||co.industry||"",proj.context?.company?.stage||co.stage||""].filter(Boolean).join(" | ");
            setProjectExecPhase("📝 Building submission-ready DOCX via Python engine: "+del.name+"...");
            const _r=await fetch(_railwayUrl+"/generate/docx",{
              method:"POST",
              headers:{"Content-Type":"application/json"},
              body:JSON.stringify({
                objective:del.description||del.name,
                company_context:_coCtx,
                access_token:(await supabase.auth.getSession()).data?.session?.access_token||"",
                available_data:_allContent.slice(0,120000),
                currency:proj.context?.company?.currency||co.currency||"INR",
                currency_symbol:proj.context?.company?.currencySymbol||co.currencySymbol||"₹",
                api_key:docServiceKey(keys)
              }),
              signal:AbortSignal.timeout(120000)
            });
            if(!_r.ok)throw new Error("Railway DOCX: HTTP "+_r.status);
            const _buf=await _r.arrayBuffer();
            if(_buf.byteLength<5000)throw new Error("Railway returned empty file");
            zip.folder(folder).file(fname+".docx",assertRealOfficeFile(_buf,"docx","The Word engine"));
            _docDone=true;
          }catch(_beeErr:any){
            recordEngineDowngrade(del.name,"docx",_beeErr);
          }
          if(!_docDone)try{
          // Publication engine first (branded typography, TOC, callouts);
          // the inline Word-HTML path below remains as automatic fallback.
          try{
            const _pubCtx=["Company: "+(proj.context?.company?.name||co.name||""),"Industry: "+(proj.context?.company?.industry||co.industry||""),"Stage: "+(proj.context?.company?.stage||co.stage||"")].join("\n");
            let _w=false;
            const _bee=new BusinessExecutionEngine(
              async(sys:any,msgs:any,maxT?:any,_es?:any,tt?:any)=>(await callMulti({...keys,...(EFF_CLAUDE?.trim()?{claude:EFF_CLAUDE}:{}),...(EFF_GEMINI?.trim()?{gemini:EFF_GEMINI}:{}),...(EFF_GROQ?.trim()?{groq:EFF_GROQ}:{})},defP,sys,msgs,maxT||6000,false,tt||"general")).primary,
              ensureXLSX,ensurePptx,ensureJsPDF,
              (name:any,buf:any)=>{zip.folder(folder).file(String(name).replace(/[^a-zA-Z0-9._-]/g,"-"),buf);_w=true;},
              stripMd);
            const _spec:DeliverableSpec={type:"excel",title:del.name,purpose:del.description||del.name,audience:_delAudience(del),qualityStandard:"cfo_model",priority:"primary"};
    const _plan:ExecutionPlan={objectiveRestated:del.description||del.name,domain:(["finance","audit","strategy","marketing","operations","hr","legal","technology","sales","risk"] as const).find(d=>(del.capabilityType||"").toLowerCase().includes(d)||del.name.toLowerCase().includes(d))||"finance",persona:"Senior FP&A Director",audience:_delAudience(del),qualityStandard:"cfo_model",decisionContext:del.description||del.name,deliverables:[_spec],missingInfo:[],executionOrder:[del.name],validationCriteria:[]};
            setProjectExecPhase("\ud83d\udcc4 Building publication-quality Word document: "+del.name);
            const _beeRes:any=await _bee.generateDocx(_plan,_spec,_pubCtx,content,(m:string)=>setProjectExecPhase(m));
            _docDone=_w;
            if(!_w&&_beeRes?.error){throw new Error(_beeRes.error);}
          }catch(_beeErr:any){
            recordEngineDowngrade(del.name,"docx",_beeErr);
          }
          if(!_docDone)try{
            const secs=parseSections(content);
            const coName=proj.context?.company?.name||"";
            let html="<html xmlns:o=\"urn:schemas-microsoft-com:office:office\" xmlns:w=\"urn:schemas-microsoft-com:office:word\" xmlns=\"http://www.w3.org/TR/REC-html40\">"
              +"<head><meta charset=\"UTF-8\"><style>"
              +"@page{mso-page-orientation:portrait;margin:2.54cm;}body{font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#1a1a1a;text-align:left;}p{text-align:left;}"
              +"h1{font-size:20pt;color:#0D6EFD;border-bottom:2pt solid #0D6EFD;padding-bottom:4pt;margin-top:18pt;}"
              +"h2{font-size:14pt;color:#14B8A6;margin-top:14pt;}"
              +"h3{font-size:12pt;color:#333;margin-top:10pt;}"
              +"p{line-height:1.5;margin:4pt 0;}"
              +"table{border-collapse:collapse;width:100%;margin:8pt 0;}"
              +"th{background:#14B8A6;color:#fff;padding:5pt 8pt;font-weight:bold;text-align:left;}"
              +"td{padding:4pt 8pt;border-bottom:1pt solid #e2e2e2;}"
              +"tr:nth-child(even) td{background:#f6f8fb;}"
              +"</style></head><body>"
              +"<h1>"+del.name+"</h1>"
              +(coName?"<p><em>"+coName+" &middot; "+new Date().toLocaleDateString()+"</em></p>":"");
            for(const sec of secs){
              html+="<h2>"+sec.title+"</h2>";
              let inTable=false;let tableRows=[];
              for(const ln of sec.lines){
                if(ln.includes("|")&&ln.trim().startsWith("|")){
                  if(!inTable){inTable=true;tableRows=[];}
                  const cells=ln.split("|").filter((c,ii,a)=>ii>0&&ii<a.length-1).map(c=>c.trim());
                  if(!cells.every(c=>c.match(/^[-:]+$/)))tableRows.push(cells);
                } else {
                  if(inTable&&tableRows.length){
                    html+="<table><thead><tr>"+tableRows[0].map(c=>"<th>"+c+"</th>").join("")+"</tr></thead><tbody>";
                    tableRows.slice(1).forEach(r=>{html+="<tr>"+r.map(c=>"<td>"+c+"</td>").join("")+"</tr>";});
                    html+="</tbody></table>";inTable=false;tableRows=[];
                  }
                  const t=stripMd(ln).trim();
                  if(t)html+="<p>"+t+"</p>";
                }
              }
              if(inTable&&tableRows.length){
                html+="<table><thead><tr>"+tableRows[0].map(c=>"<th>"+c+"</th>").join("")+"</tr></thead><tbody>";
                tableRows.slice(1).forEach(r=>{html+="<tr>"+r.map(c=>"<td>"+c+"</td>").join("")+"</tr>";});
                html+="</tbody></table>";
              }
            }
            html+="</body></html>";
            // Word HTML saved as .doc opens natively in Microsoft Word
            zip.folder(folder).file(fname+".doc",html);
          }catch{zip.folder(folder).file(fname+".md",content);}
        }catch(e:any){zip.folder(folder).file(fname+".md",content);}
        // ── IMPROVED 2: Enterprise multi-sheet XLSX ──
        } else if(fmt==="xlsx"){
          // ── RAILWAY PYTHON SERVICE: CFO-grade Excel ──────────────────────
          try{
            const _railwayUrl="https://orchestriq-gen-service-production.up.railway.app";
            const _allContent=(rawContentStore.current[del.id]||del.rawContent||"").replace(/^\s*(Here is|Here's|Below is)[^\n]*\n+/i,"");
            const _coCtx=[proj.context?.company?.name||co.name||"",proj.context?.company?.industry||co.industry||"",proj.context?.company?.stage||co.stage||"",proj.context?.company?.location||co.location||""].filter(Boolean).join(" | ");
            setProjectExecPhase("📊 Building CFO-grade Excel via Python engine: "+del.name+"...");
            const _r=await fetch(_railwayUrl+"/generate/excel",{
              method:"POST",
              headers:{"Content-Type":"application/json"},
              body:JSON.stringify({
                objective:del.description||del.name,
                company_context:_coCtx,
                access_token:(await supabase.auth.getSession()).data?.session?.access_token||"",
                available_data:_allContent.slice(0,120000),
                currency:proj.context?.company?.currency||co.currency||"INR",
                currency_symbol:proj.context?.company?.currencySymbol||co.currencySymbol||"₹",
                api_key:docServiceKey(keys)
              }),
              signal:AbortSignal.timeout(120000)
            });
            if(!_r.ok)throw new Error("Railway Excel: HTTP "+_r.status);
            const _buf=await _r.arrayBuffer();
            // WAS: only a size check. A server error page is easily bigger than
            // 1,000 bytes, sailed through, and was saved as .xlsx - which is
            // exactly the file Excel refused to open for you.
            const _ok=assertRealOfficeFile(_buf,"xlsx","The Excel engine");
            zip.folder(folder).file(fname+".xlsx",_ok);
            // WE CANNOT EMBED A REAL VBA PROJECT FROM A BROWSER, AND WE WILL NOT
            // PRETEND OTHERWISE BY RENAMING A FILE TO .xlsm. A macro-enabled
            // workbook needs a compiled vbaProject.bin, which is a Windows Excel
            // artefact - nothing in this pipeline can produce one.
            //
            // So we do the honest thing your own standard asks for: deliver a
            // genuine .xlsx, and put the macro source beside it as a .bas file
            // that imports into Excel in about thirty seconds.
            zip.folder(folder).file(fname+"-Macros.bas",VBA_MODULE_SOURCE(fname));
            zip.folder(folder).file(fname+"-HOW-TO-ENABLE-MACROS.md",VBA_README(fname));
          }catch(xlsxErr:any){
            // Fallback: SheetJS basic
            try{
            // BusinessExecutionEngine generates: Dashboard tab, formula cells,
            // conditional formatting, freeze panes, auto-filter, named ranges.
            const _xlsxCurrency = proj.context?.company?.currency||co.currency||"INR";
            const _xlsxSymbol   = proj.context?.company?.currencySymbol||co.currencySymbol||"₹";
            const _xlsxCoCtx    = [
              "Company: "+(proj.context?.company?.name||co.name||""),
              "Industry: "+(proj.context?.company?.industry||co.industry||""),
              "Stage: "+(proj.context?.company?.stage||co.stage||""),
              "Location: "+(proj.context?.company?.location||co.location||""),
              "Currency: "+_xlsxSymbol+_xlsxCurrency,
            ].join("\n");
            // Buffer into ZIP — BEE dlFile injects here
            let _xlsxWritten = false;
            const _xlsxBEE = new BusinessExecutionEngine(
              async(sys:any,msgs:any,maxT?:any,_es?:any,tt?:any)=>(await callMulti({...keys,...(EFF_CLAUDE?.trim()?{claude:EFF_CLAUDE}:{}),...(EFF_GEMINI?.trim()?{gemini:EFF_GEMINI}:{}),...(EFF_GROQ?.trim()?{groq:EFF_GROQ}:{})},defP,sys,msgs,maxT||4000,false,tt||"excel_advanced")).primary,
              ensureXLSX, ensurePptx, ensureJsPDF,
              (name:any, xlsxContent:any) => {
                // Route into ZIP instead of direct download
                const _safeName = String(name).replace(/[^a-zA-Z0-9._-]/g,"-");
                if(xlsxContent instanceof ArrayBuffer){
                  zip.folder(folder).file(_safeName, xlsxContent);
                  _xlsxWritten = true;
                } else if(xlsxContent && typeof xlsxContent === "object" && xlsxContent.buffer){
                  zip.folder(folder).file(_safeName, xlsxContent.buffer || xlsxContent);
                  _xlsxWritten = true;
                } else {
                  // Blob — need to read it
                  zip.folder(folder).file(_safeName, xlsxContent);
                  _xlsxWritten = true;
                }
              },
              stripMd,
              { image:async(p:string)=>{
                  // WAS: read keys.openai directly, then keys.fal directly. Both
                  // ignored the on/off switch, so a provider you had switched OFF
                  // was still used - and a provider you HAD configured was
                  // invisible if it was not one of those two.
                  // The registry answers "who can make an image" in one place.
                  const cands=provAvailableFor("image",(id)=>providerKey(keys,id));
                  for(const c of cands){
                    try{
                      if(c.id==="openai"){const u=await callDallE(p,c.key); if(u)return u;}
                      else if(c.id==="stability"){const u=await callStabilityAI(p,c.key); if(u)return u;}
                      else if(c.id==="fal"){const u=await callFalImage(c.key,p); if(u)return u;}
                    }catch(e){console.warn("[OIQ] image provider "+c.id+" failed:",String((e as any)?.message||e).slice(0,110));}
                  }
                  throw new Error(provCannotDo(defP||"nvidia","image",[]));
                },
                video:(p:string)=>callFalVideo(providerKey(keys,"fal"),p) },
            );
            const _xlsxDel: DeliverableSpec = {
              type: "excel",
              title: del.name,
              purpose: del.description || del.name,
              audience: (del.aiPersona||del.capabilityType||"cfo").toLowerCase().includes("board") ? "board" : "cfo",
              qualityStandard: "cfo_model",
              priority: "primary",
            };
            const _xlsxPlan: ExecutionPlan = {
              objectiveRestated: del.description||del.name,
              domain: (["finance","audit","strategy","marketing","operations","hr","legal","technology","sales","risk"] as const).find(d=>(del.capabilityType||"").toLowerCase().includes(d)||del.name.toLowerCase().includes(d))||"finance",
              persona: "Senior FP&A Manager",
              audience: "cfo",
              qualityStandard: "cfo_model",
              decisionContext: del.description||del.name,
              deliverables: [_xlsxDel],
              missingInfo: [],
              executionOrder: [del.name],
              validationCriteria: [],
            };
            setProjectExecPhase("📊 Building professional Excel workbook: "+del.name);
            const _xlsxRes:any=await _xlsxBEE.generateExcel(_xlsxPlan, _xlsxDel, _xlsxCoCtx, content, _xlsxCurrency, _xlsxSymbol, (msg:string)=>setProjectExecPhase(msg));
            if(_xlsxRes?.qualityWarning)qualityWarnings.list.push({deliverable:del.name,warning:_xlsxRes.qualityWarning});
            if(!_xlsxWritten){
              // BEE fallback: write basic structured XLSX
              throw new Error("BEE did not write file");
            }
          }catch(xlsxErr:any){
            // Fallback: structured XLSX with formula detection
            try{
              const XLSX=await ensureXLSX();
              const wb=XLSX.utils.book_new();
              const secs=parseSections(content);
              const cleanCellV=(v:string):string|number=>{if(v.startsWith("="))return v;const c=v.replace(/\*\*([^*]+)\*\*/g,"$1").replace(/\*([^*]+)\*/g,"$1").replace(/`([^`]+)`/g,"$1").replace(/^#+\s*/,"").replace(/^[-*]\s*/,"").trim();const n=Number(c.replace(/[₹$£€,\s%]/g,""));return(!isNaN(n)&&c!=""&&/^[₹$£€]?[\d,.]+[%]?$/.test(c))?n:c;};
              const allRows:any[][]=[];
              secs.forEach(sec=>{
                allRows.push([stripMd(sec.title)]); allRows.push([]);
                const tRows=sec.lines.filter((l:string)=>l.includes("|")&&l.trim().startsWith("|")&&!l.trim().match(/^\|[\s|:-]+\|$/)).map((r:string)=>r.split("|").filter((c:string,ii:number,a:string[])=>ii>0&&ii<a.length-1).map((c:string)=>cleanCellV(c.trim())));
                if(tRows.length>0){tRows.forEach((r:any[])=>allRows.push(r));allRows.push([]);}
                else{sec.lines.filter((l:string)=>l.trim()).forEach((l:string)=>allRows.push([cleanCellV(stripMd(l))]));}
                allRows.push([]);
              });
              const wsMain=XLSX.utils.aoa_to_sheet(allRows.length>2?allRows:[[del.name],[""],[ content]]);
              // Apply formula detection: cells starting with = become formulas
              Object.keys(wsMain).filter(k=>!k.startsWith("!")).forEach(k=>{
                const cell=wsMain[k];
                if(cell&&typeof cell.v==="string"&&cell.v.startsWith("=")){
                  cell.f=cell.v.slice(1); cell.t="n"; delete cell.v;
                }
              });
              // Freeze first row, add auto-filter
              wsMain["!freeze"]={xSplit:0,ySplit:1};
              if(allRows[0]?.length) wsMain["!autofilter"]={ref:`A1:${String.fromCharCode(64+allRows[0].length)}1`};
              wsMain["!cols"]=allRows[0]?.map?.(()=>({wch:18}))||[];
              // Excel forbids : \\ / ? * [ ] in a sheet name and refuses an empty
              // one. An unsanitised deliverable name here throws, and the whole
              // workbook is lost to the catch below.
              XLSX.utils.book_append_sheet(wb,wsMain,safeSheetName(del.name));
              // Assumptions sheet
              const assumptions=content.split("\n").filter((l:string)=>/\[ASSUMPTION\]|\[ESTIMATE\]/i.test(l)).map((l:string)=>[stripMd(l)]);
              if(assumptions.length){const wsA=XLSX.utils.aoa_to_sheet([["Assumption/Estimate"],...assumptions]);XLSX.utils.book_append_sheet(wb,wsA,"Assumptions");}
              const buf=XLSX.write(wb,{type:"array",bookType:"xlsx"});
              zip.folder(folder).file(fname+".xlsx",assertRealOfficeFile(buf,"xlsx","The built-in Excel builder"));
            }catch(xe:any){
              // If a workbook genuinely cannot be built, say so in the folder
              // rather than leaving a file that will not open. A clear note is
              // more use than a broken spreadsheet.
              zip.folder(folder).file(fname+"-EXCEL-COULD-NOT-BE-BUILT.md",
                "# Excel could not be generated\n\n**Reason:** "+String(xe?.message||xe).slice(0,300)+
                "\n\nThe analysis itself is below and is complete. Only the spreadsheet packaging failed.\n\n---\n\n"+content);
            }
            }
          }
          // ── PPTX: JSON-driven consulting-grade presentation ─────────────
        } else if(fmt==="pptx"){
          // ── RAILWAY PYTHON SERVICE: McKinsey-grade PPTX ─────────────────
          let _pptxDone=false;
          try{
            const _railwayUrl="https://orchestriq-gen-service-production.up.railway.app";
            const _allContent=(rawContentStore.current[del.id]||del.rawContent||"").replace(/^\s*(Here is|Here's)[^\n]*\n+/i,"");
            const _coCtx=[proj.context?.company?.name||co.name||"",proj.context?.company?.industry||co.industry||"",proj.context?.company?.stage||co.stage||""].filter(Boolean).join(" | ");
            setProjectExecPhase("📊 Building McKinsey-grade PPTX via Python engine: "+del.name+"...");
            const _r=await fetch(_railwayUrl+"/generate/pptx",{
              method:"POST",
              headers:{"Content-Type":"application/json"},
              body:JSON.stringify({
                objective:del.description||del.name,
                company_context:_coCtx,
                access_token:(await supabase.auth.getSession()).data?.session?.access_token||"",
                available_data:_allContent.slice(0,120000),
                currency:proj.context?.company?.currency||co.currency||"INR",
                currency_symbol:proj.context?.company?.currencySymbol||co.currencySymbol||"₹",
                api_key:docServiceKey(keys)
              }),
              signal:AbortSignal.timeout(120000)
            });
            if(!_r.ok)throw new Error("Railway PPTX: HTTP "+_r.status);
            const _buf=await _r.arrayBuffer();
            if(_buf.byteLength<5000)throw new Error("Railway returned empty file");
            zip.folder(folder).file(fname+".pptx",assertRealOfficeFile(_buf,"pptx","The PowerPoint engine"));
            _pptxDone=true;
          }catch(_beeErr:any){
            recordEngineDowngrade(del.name,"pptx",_beeErr);
          }
          if(!_pptxDone)try{
          // 12-16 slide mandate, decision-first planning, native charts).
          // The inline PptxGenJS path below stays as the automatic fallback —
          // and any use of it is now recorded and surfaced to the user.
          try{
            const _pubCtx=["Company: "+(proj.context?.company?.name||co.name||""),"Industry: "+(proj.context?.company?.industry||co.industry||""),"Stage: "+(proj.context?.company?.stage||co.stage||"")].join("\n");
            let _w=false;
            const _bee=new BusinessExecutionEngine(
              async(sys:any,msgs:any,maxT?:any,_es?:any,tt?:any)=>(await callMulti({...keys,...(EFF_CLAUDE?.trim()?{claude:EFF_CLAUDE}:{}),...(EFF_GEMINI?.trim()?{gemini:EFF_GEMINI}:{}),...(EFF_GROQ?.trim()?{groq:EFF_GROQ}:{})},defP,sys,msgs,maxT||6000,false,tt||"general")).primary,
              ensureXLSX,ensurePptx,ensureJsPDF,
              (name:any,buf:any)=>{zip.folder(folder).file(String(name).replace(/[^a-zA-Z0-9._-]/g,"-"),buf);_w=true;},
              stripMd);
            const _spec:DeliverableSpec={type:"pptx",title:del.name,purpose:del.description||del.name,audience:_delAudience(del),qualityStandard:"mckinsey_deck",priority:"primary"};
            const _plan:ExecutionPlan={objectiveRestated:del.description||del.name,domain:(["finance","audit","strategy","marketing","operations","hr","legal","technology","sales","risk"] as const).find(d=>(del.capabilityType||"").toLowerCase().includes(d)||del.name.toLowerCase().includes(d))||"strategy",persona:"Senior Consultant",audience:_delAudience(del),qualityStandard:"mckinsey_deck",decisionContext:del.description||del.name,deliverables:[_spec],missingInfo:[],executionOrder:[del.name],validationCriteria:[]};
            setProjectExecPhase("\ud83d\udcca Building publication-quality presentation: "+del.name);
            const _beeRes:any=await _bee.generatePPTX(_plan,_spec,_pubCtx,content,(m:string)=>setProjectExecPhase(m));
            _pptxDone=_w;
            if(!_w&&_beeRes?.error){throw new Error(_beeRes.error);}
            if(_beeRes?.qualityWarning)qualityWarnings.list.push({deliverable:del.name,warning:_beeRes.qualityWarning});
          }catch(_beeErr:any){
            recordEngineDowngrade(del.name,"pptx",_beeErr);
          }
          if(!_pptxDone)try{
            const PptxGenJS=await ensurePptx();
            const pptx=new PptxGenJS();
            pptx.defineLayout({name:"WIDE",width:13.333,height:7.5}); pptx.layout="WIDE";
            const coN=proj.context?.company?.name||co.name||"Company";
            const sym=proj.context?.company?.currencySymbol||co.currencySymbol||"₹";

            // ── PARSE JSON OUTPUT FROM AI ──────────────────────────────────
            // AI now outputs structured JSON with slide specs
            let schema:any=null;
            const jsonMatch=content.match(/```json([\s\S]*?)```/i)||content.match(/\{[\s\S]*"slides"[\s\S]*\}/);
            if(jsonMatch){
              try{ schema=JSON.parse((jsonMatch[1]||jsonMatch[0]).trim()); }catch{}
            }
            if(!schema?.slides?.length){
              // Salvage complete slides from truncated JSON before giving up
              const salvaged=salvageSlidesJson(content);
              if(salvaged.length>=2){schema={title:del.name,slides:salvaged};}
              else{schema=parseMdToSlidesGlobal(content,coN,del.name);}
            }
            const slides:any[]=schema.slides||[];

            // ── THEME COLOURS ──────────────────────────────────────────────
            const THEME={
              dark:"0A0E1A", primary:"1E3A5F", accent:"14B8A6",
              white:"FFFFFF", muted:"94A3B8", light:"F1F5F9",
              amber:"D97706", red:"DC2626", green:"16A34A",
            };
            const CHART_COLORS=["14B8A6","1E3A5F","3B82F6","F59E0B","EF4444","10B981","A855F7"];

            // ── SLIDE BUILDERS ─────────────────────────────────────────────
            const addStdHeader=(slide:any,title:string,slideNum:number,total:number)=>{
              slide.addShape(pptx.ShapeType.rect,{x:0,y:0,w:13.333,h:0.85,fill:{color:"131825"}});
              slide.addShape(pptx.ShapeType.rect,{x:0,y:0,w:0.22,h:0.85,fill:{color:THEME.accent}});
              slide.addText(title,{x:0.4,y:0.08,w:11.5,h:0.7,fontSize:19,bold:true,color:THEME.white,fontFace:"Calibri",valign:"middle"});
              slide.addText(`${slideNum}/${total}`,{x:12.2,y:0.08,w:1.0,h:0.7,fontSize:9,color:THEME.muted,align:"right",fontFace:"Calibri"});
            };

            for(let si=0;si<slides.length;si++){
              const sd=slides[si];
              const slide=pptx.addSlide();
              const total=slides.length;

              if(sd.layout==="title"){
                slide.background={color:THEME.dark};
                slide.addShape(pptx.ShapeType.rect,{x:0,y:2.9,w:0.4,h:1.8,fill:{color:THEME.accent}});
                slide.addText(sd.title||del.name,{x:0.7,y:2.8,w:11,h:1.1,fontSize:34,bold:true,color:THEME.white,fontFace:"Calibri"});
                slide.addText(sd.subtitle||"",{x:0.7,y:3.95,w:11,h:0.7,fontSize:18,color:THEME.accent,fontFace:"Calibri"});
                slide.addText(sd.meta||`${coN}  ·  ${new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}  ·  Confidential`,{x:0.7,y:6.9,w:11,h:0.4,fontSize:10,color:THEME.muted,fontFace:"Calibri"});
              }
              else if(sd.layout==="exec_summary"){
                slide.background={color:THEME.dark};
                addStdHeader(slide,sd.title||"Executive Summary",si+1,total);
                const bullets=(sd.bullets||[]).slice(0,5);
                bullets.forEach((b:string,bi:number)=>{
                  const y=1.1+bi*1.1;
                  slide.addShape(pptx.ShapeType.ellipse,{x:0.35,y:y+0.08,w:0.5,h:0.5,fill:{color:THEME.accent}});
                  slide.addText(String(bi+1),{x:0.35,y:y+0.02,w:0.5,h:0.5,fontSize:14,bold:true,color:THEME.white,align:"center",fontFace:"Calibri"});
                  slide.addText(b.replace(/^[-•*]\s*/,""),{x:1.0,y,w:11.8,h:0.9,fontSize:14,color:THEME.white,fontFace:"Calibri",valign:"middle"});
                });
                if(sd.so_what) slide.addText(`Key takeaway: ${sd.so_what}`,{x:0.4,y:6.8,w:12.5,h:0.5,fontSize:11,color:THEME.accent,italic:true,fontFace:"Calibri"});
                if(sd.notes) slide.addNotes(sd.notes);
              }
              else if(sd.layout==="chart"&&sd.series?.length){
                slide.background={color:THEME.dark};
                addStdHeader(slide,sd.title||"Analysis",si+1,total);
                try{
                  const ctMap:any={bar:pptx.ChartType?.bar,line:pptx.ChartType?.line,pie:pptx.ChartType?.pie,waterfall:pptx.ChartType?.bar,scatter:pptx.ChartType?.scatter};
                  const chartData=sd.series.map((s:any,ci:number)=>({name:s.name,labels:sd.categories||[],values:(s.values||[]).map(Number)}));
                  slide.addChart(ctMap[sd.chart_type]||pptx.ChartType?.bar,chartData,{
                    x:0.3,y:1.0,w:8.5,h:5.8,
                    showLegend:true,legendPos:"b",legendFontSize:10,legendColor:THEME.muted,
                    chartColors:CHART_COLORS,plotAreaBkgndColor:THEME.dark,
                    valAxisLabelColor:THEME.muted,catAxisLabelColor:THEME.muted,
                    showTitle:false,dataLabelFontSize:9,
                  });
                  if(sd.insight) slide.addText(sd.insight,{x:9.0,y:1.0,w:4.1,h:5.8,fontSize:12,color:THEME.white,fontFace:"Calibri",valign:"top",wrap:true});
                }catch{
                  // Chart failed - render as text
                  slide.addText(content.slice(0,500),{x:0.4,y:1.0,w:12.5,h:5.8,fontSize:11,color:THEME.white,fontFace:"Calibri",wrap:true});
                }
                if(sd.notes) slide.addNotes(sd.notes);
              }
              else if(sd.layout==="two_col"){
                slide.background={color:THEME.dark};
                addStdHeader(slide,sd.title||"Analysis",si+1,total);
                slide.addShape(pptx.ShapeType.rect,{x:6.65,y:1.0,w:0.03,h:6.0,fill:{color:"263050"}});
                if(sd.left_header) slide.addText(sd.left_header,{x:0.4,y:1.0,w:5.9,h:0.5,fontSize:12,bold:true,color:THEME.accent,fontFace:"Calibri"});
                if(sd.right_header) slide.addText(sd.right_header,{x:7.0,y:1.0,w:6.0,h:0.5,fontSize:12,bold:true,color:THEME.accent,fontFace:"Calibri"});
                (sd.left||[]).forEach((b:string,bi:number)=>slide.addText("▸  "+b.replace(/^[-•*]\s*/,""),{x:0.4,y:1.65+bi*0.8,w:5.9,h:0.7,fontSize:12,color:THEME.white,fontFace:"Calibri"}));
                (sd.right||[]).forEach((b:string,bi:number)=>slide.addText("▸  "+b.replace(/^[-•*]\s*/,""),{x:7.0,y:1.65+bi*0.8,w:6.0,h:0.7,fontSize:12,color:THEME.white,fontFace:"Calibri"}));
                if(sd.notes) slide.addNotes(sd.notes);
              }
              else if(sd.layout==="table"&&sd.headers?.length){
                slide.background={color:THEME.dark};
                addStdHeader(slide,sd.title||"Data",si+1,total);
                try{
                  const colW=Math.floor(12.5/sd.headers.length);
                  const tblData=[
                    sd.headers.map((h:string)=>({text:h,options:{bold:true,color:THEME.white,fill:{color:THEME.primary},fontSize:11,align:"center"}})),
                    ...(sd.rows||[]).map((row:string[],ri:number)=>row.map((cell:string)=>({text:String(cell),options:{color:THEME.white,fill:{color:ri%2===0?"131825":"0A0E1A"},fontSize:10,align:"center"}})))
                  ];
                  slide.addTable(tblData,{x:0.4,y:1.1,w:12.5,colW:sd.headers.map(()=>colW),rowH:0.42,border:{type:"solid",color:"263050",pt:0.5}});
                }catch{
                  const tableText=(sd.headers.join(" | ")+"\n"+(sd.rows||[]).map((r:string[])=>r.join(" | ")).join("\n"));
                  slide.addText(tableText,{x:0.4,y:1.1,w:12.5,h:5.8,fontSize:10,color:THEME.white,fontFace:"Calibri",wrap:true});
                }
                if(sd.notes) slide.addNotes(sd.notes);
              }
              else if(sd.layout==="closing"){
                slide.background={color:THEME.primary};
                slide.addShape(pptx.ShapeType.rect,{x:0,y:3.0,w:0.4,h:2.2,fill:{color:THEME.accent}});
                slide.addText(sd.title||"Recommendations & Next Steps",{x:0.7,y:2.9,w:11,h:1.0,fontSize:28,bold:true,color:THEME.white,fontFace:"Calibri"});
                (sd.actions||[]).forEach((a:string,ai:number)=>slide.addText(`${ai+1}. ${a}`,{x:0.7,y:4.05+ai*0.7,w:11,h:0.65,fontSize:14,color:THEME.white,fontFace:"Calibri"}));
                if(sd.bottom_line) slide.addText(sd.bottom_line,{x:0.7,y:6.9,w:11,h:0.4,fontSize:11,color:THEME.accent,italic:true,fontFace:"Calibri"});
                if(sd.notes) slide.addNotes(sd.notes);
              }
              else{
                // Generic text slide fallback
                slide.background={color:THEME.dark};
                addStdHeader(slide,sd.title||del.name,si+1,total);
                const bulletText=(sd.bullets||sd.content||(typeof sd=="string"?[sd]:[JSON.stringify(sd)]));
                const bullets=Array.isArray(bulletText)?bulletText:[bulletText];
                bullets.slice(0,8).forEach((b:string,bi:number)=>
                  slide.addText("▸  "+(b||"")||"",{x:0.4,y:1.15+bi*0.73,w:12.5,h:0.65,fontSize:13,color:THEME.white,fontFace:"Calibri",valign:"middle"})
                );
                if(sd.notes) slide.addNotes(sd.notes);
              }
            }

            const buf=await pptx.write({outputType:"arraybuffer"});
            zip.folder(folder).file(fname+".pptx",buf);
          }catch(pptxErr:any){
            zip.folder(folder).file(fname+"-pptx-error.md","PPTX generation error: "+pptxErr.message+"\n\nRaw content:\n"+content);
          }
        }catch(e:any){zip.folder(folder).file(fname+"-pptx-error.md","PPTX fallback error: "+(e?.message||"").slice(0,80));}
        } else if(fmt==="pdf"){
          // ── RAILWAY PYTHON SERVICE: Board-grade PDF ──────────────────────
          let _pdfDone=false;
          try{
            const _railwayUrl="https://orchestriq-gen-service-production.up.railway.app";
            const _allContent=(rawContentStore.current[del.id]||del.rawContent||"").replace(/^\s*(Here is|Here's)[^\n]*\n+/i,"");
            const _coCtx=[proj.context?.company?.name||co.name||"",proj.context?.company?.industry||co.industry||"",proj.context?.company?.stage||co.stage||""].filter(Boolean).join(" | ");
            setProjectExecPhase("📄 Building board-grade PDF via Python engine: "+del.name+"...");
            const _r=await fetch(_railwayUrl+"/generate/pdf",{
              method:"POST",
              headers:{"Content-Type":"application/json"},
              body:JSON.stringify({
                objective:del.description||del.name,
                company_context:_coCtx,
                access_token:(await supabase.auth.getSession()).data?.session?.access_token||"",
                available_data:_allContent.slice(0,120000),
                currency:proj.context?.company?.currency||co.currency||"INR",
                currency_symbol:proj.context?.company?.currencySymbol||co.currencySymbol||"₹",
                api_key:docServiceKey(keys)
              }),
              signal:AbortSignal.timeout(120000)
            });
            if(!_r.ok)throw new Error("Railway PDF: HTTP "+_r.status);
            const _buf=await _r.arrayBuffer();
            if(_buf.byteLength<2000)throw new Error("Railway returned empty file");
            zip.folder(folder).file(fname+".pdf",assertRealOfficeFile(_buf,"pdf","The PDF engine"));
            _pdfDone=true;
          }catch(_beeErr:any){
            recordEngineDowngrade(del.name,"pdf",_beeErr);
          }
          if(!_pdfDone)try{
          // the inline jsPDF path below remains as automatic fallback.
          try{
            const _pubCtx=["Company: "+(proj.context?.company?.name||co.name||""),"Industry: "+(proj.context?.company?.industry||co.industry||""),"Stage: "+(proj.context?.company?.stage||co.stage||"")].join("\n");
            let _w=false;
            const _bee=new BusinessExecutionEngine(
              async(sys:any,msgs:any,maxT?:any,_es?:any,tt?:any)=>(await callMulti({...keys,...(EFF_CLAUDE?.trim()?{claude:EFF_CLAUDE}:{}),...(EFF_GEMINI?.trim()?{gemini:EFF_GEMINI}:{}),...(EFF_GROQ?.trim()?{groq:EFF_GROQ}:{})},defP,sys,msgs,maxT||6000,false,tt||"general")).primary,
              ensureXLSX,ensurePptx,ensureJsPDF,
              (name:any,buf:any)=>{zip.folder(folder).file(String(name).replace(/[^a-zA-Z0-9._-]/g,"-"),buf);_w=true;},
              stripMd);
            const _spec:DeliverableSpec={type:"pdf",title:del.name,purpose:del.description||del.name,audience:_delAudience(del),qualityStandard:"cfo_model",priority:"primary"};
            const _plan:ExecutionPlan={objectiveRestated:del.description||del.name,domain:(["finance","audit","strategy","marketing","operations","hr","legal","technology","sales","risk"] as const).find(d=>(del.capabilityType||"").toLowerCase().includes(d)||del.name.toLowerCase().includes(d))||"strategy",persona:"Senior Consultant",audience:_delAudience(del),qualityStandard:"cfo_model",decisionContext:del.description||del.name,deliverables:[_spec],missingInfo:[],executionOrder:[del.name],validationCriteria:[]};
            const _beeRes:any=await _bee.generatePDF(_plan,_spec,_pubCtx,content,()=>{});
    _pdfDone=_w;
    if(!_w&&_beeRes?.error){throw new Error(_beeRes.error);}
  }catch(_beeErr:any){
    recordEngineDowngrade(del.name,"pdf",_beeErr);
  }
  if(!_pdfDone)try{
    var _pdfContentSafe=pdfSafeText((content||"").replace(/^\s*---+\s*$/gm,""));const pdfBuf=await(async()=>{
              const content=_pdfContentSafe; // shadow: entire inline renderer uses sanitized text
            const jsPDF=await ensureJsPDF();const doc=new jsPDF({orientation:"portrait",unit:"pt",format:"a4"});
            const W=doc.internal.pageSize.getWidth(),H=doc.internal.pageSize.getHeight(),ML=54,MR=54,MT=54,MB=54,CW=W-ML-MR;
            const TEAL:any=[20,184,166],NAVY:any=[30,58,95],DARK:any=[30,30,30],GREY:any=[100,100,100],WHITE:any=[255,255,255];
            const TBL=16;const y={v:MT+40};
            const hdr=()=>{doc.setFillColor(...NAVY);doc.rect(0,0,W,36,"F");doc.setFontSize(8);doc.setFont("helvetica","normal");doc.setTextColor(...WHITE);doc.text(del.name.slice(0,55),ML,24);doc.text(proj.context?.company?.name||co.name||"",W-MR,24,{align:"right"});};
            const safeT=(t:string,x:number,fs:number,col:any[],bd=false,mw=CW)=>{doc.setFontSize(fs);doc.setFont("helvetica",bd?"bold":"normal");doc.setTextColor(...col);doc.splitTextToSize(String(t),mw).forEach((l:string)=>{if(y.v+fs*1.4>H-MB){doc.addPage();y.v=MT+40;hdr();}doc.text(l,x,y.v);y.v+=fs*1.4;});};
            doc.setFillColor(...NAVY);doc.rect(0,0,W,H,"F");doc.setFillColor(...TEAL);doc.rect(0,255,W,4,"F");
            doc.setFont("helvetica","bold");doc.setFontSize(26);doc.setTextColor(...WHITE);doc.splitTextToSize(del.name,CW).forEach((l:string,i:number)=>doc.text(l,ML,285+i*34));
            doc.setFontSize(13);doc.setFont("helvetica","normal");doc.setTextColor(...TEAL);doc.text(proj.context?.company?.name||co.name||"",ML,340);
            doc.setFontSize(10);doc.setTextColor(180,190,200);doc.text(new Date().toLocaleDateString("en-GB")+"  ·  Confidential",ML,362);
            doc.addPage();hdr();
            const SKIPRE=/^(cover|table of contents?|toc|contents?)$/i;
            parseSections(content).filter((s:any)=>!SKIPRE.test(s.title.trim())).forEach(sec=>{
              if(y.v+30>H-MB){doc.addPage();y.v=MT+40;hdr();}
              doc.setFillColor(244,246,250);doc.rect(ML-6,y.v-14,CW+12,20,"F");doc.setFillColor(...TEAL);doc.rect(ML-6,y.v-14,3,20,"F");
              safeT(sec.title,ML+4,11,NAVY,true,CW-8);y.v+=4;
              const tR=sec.lines.filter((l:string)=>l.includes("|")||l.trim().startsWith("|")).filter((l:string)=>!l.match(/^\|[-:\s|]+\|$/)).map((r:string)=>r.split("|").filter((c:string,ii:number,a:string[])=>ii>0&&ii<a.length-1).map((c:string)=>stripMd(c).trim()));
              if(tR.length>=2){
                const cc=Math.max(...tR.map((r:string[])=>r.length),1),cw=CW/cc;
                if(y.v+TBL>H-MB){doc.addPage();y.v=MT+40;hdr();}
                doc.setFillColor(...NAVY);doc.rect(ML,y.v-11,CW,TBL,"F");doc.setFontSize(8);doc.setFont("helvetica","bold");doc.setTextColor(...WHITE);
                tR[0].forEach((h:string,ci:number)=>doc.text(h.slice(0,22),ML+ci*cw+3,y.v));y.v+=TBL+1;doc.setFont("helvetica","normal");
                tR.slice(1).forEach((row:string[],ri:number)=>{
                  if(y.v+TBL>H-MB){doc.addPage();y.v=MT+40;hdr();}
                  if(ri%2===0){doc.setFillColor(248,250,252);doc.rect(ML,y.v-11,CW,TBL,"F");}
                  doc.setTextColor(...DARK);doc.setFontSize(8);row.forEach((c:string,ci:number)=>doc.text(String(c).slice(0,26),ML+ci*cw+3,y.v));y.v+=TBL;
                });y.v+=8;
              } else {
                sec.lines.forEach((ln:string)=>{const raw=ln.trim();if(!raw||raw.match(/^\|[-:\s|]+\|$/))return;const txt=stripMd(raw).replace(/[\u2019\u2018]/g,"'").replace(/[\u201c\u201d]/g,'"').replace(/\[VERIFIED\]/gi,"✓").replace(/\[ESTIMATE\]/gi,"~").replace(/\[ASSUMPTION\]/gi,"*");if(!txt.trim())return;const ib=raw.startsWith("-")||raw.startsWith("*")||raw.startsWith("•");if(ib&&y.v+10<H-MB){doc.setFontSize(8);doc.setTextColor(...TEAL);doc.text("▸",ML,y.v);}safeT(txt,ib?ML+12:ML,9.5,DARK,false,CW-(ib?12:0));y.v+=2;});
              }
              y.v+=10;
            });
            const tot=doc.internal.getNumberOfPages();for(let p=1;p<=tot;p++){doc.setPage(p);doc.setFontSize(7);doc.setTextColor(...GREY);doc.text("Page "+p+" of "+tot,W/2,H-18,{align:"center"});doc.text("Confidential",ML,H-18);}
            return doc.output("arraybuffer");
          })();zip.folder(folder).file(fname+".pdf",pdfBuf);
          }catch(pdfErr:any){zip.folder(folder).file(fname+"-error.md","PDF error: "+(pdfErr?.message||String(pdfErr))+"\n\n"+content.slice(0,2000));}
        }catch(e:any){zip.folder(folder).file(fname+"-pdf-error.md","PDF outer error: "+(e?.message||"").slice(0,80));}
        } else if(fmt==="image"||fmt==="video"){
          // REAL media generation — deliver the actual PNG/MP4, never a prompt file.
          // Script/brief is included alongside as reference. A prompt file is written
          // ONLY on genuine failure, and it states the exact error and fix.
          // WAS a direct read that ignored the on/off switch entirely.
          // providerKey is the one function that respects it.
          const falK=providerKey(keys,"fal");
          try{
            setProjectExecPhase((fmt==="video"?"\ud83c\udfac Generating video: ":"\ud83c\udfa8 Generating image: ")+del.name+"...");
            const mp:any=buildMediaPrompt(del,proj.context||{});
            const genPrompt=fmt==="video"?(mp.kling||mp.veo||content.slice(0,500)):(mp.dalle||content.slice(0,500));
            let url="";
            if(fmt==="video"){
              url=await callFalVideo(falK,genPrompt);
            }else{
              // DALL\u00b7E is the PRIMARY image provider (fal.ai billing on hold);
              // fal.ai remains the automatic fallback when no OpenAI key exists.
              const _oa=providerKey(keys,"openai");
              if(_oa){
                try{
                  setProjectExecPhase("\ud83c\udfa8 Generating image via DALL\u00b7E: "+del.name+"...");
                  url=await callDallE(genPrompt,_oa);
                  if(!url)throw new Error("DALL\u00b7E returned no image URL");
                }catch(dErr:any){
                  setProjectExecPhase("\ud83c\udfa8 DALL\u00b7E unavailable \u2014 trying fal.ai: "+del.name);
                  url=await callFalImage(falK,genPrompt);
                }
              }else{
                url=await callFalImage(falK,genPrompt);
              }
            }
            if(!url)throw new Error("fal.ai returned no media URL");
            setProjectExecPhase("\u2b07 Downloading generated "+fmt+": "+del.name+"...");
            if(projectExecCancelRef.current)throw new Error("CANCELLED");
            const resp=await fetch(url,{signal:AbortSignal.timeout(120000)});
            if(!resp.ok)throw new Error("media download failed: HTTP "+resp.status);
            const blob=await resp.blob();
            const ext=fmt==="video"?"mp4":(((blob.type||"image/png").split("/")[1]||"png").split("+")[0]);
            zip.folder(folder).file(fname+"."+ext,blob);
            zip.folder(folder).file(fname+(fmt==="video"?"-script.md":"-brief.md"),(fmt==="video"?"## Video Script\n\n":"## Image Brief\n\n")+content);
          }catch(mediaErr:any){
            // Phase 5: graceful capability-limit fallback. Rather than just
            // naming the error, use the Research Desk (already live-web-search
            // capable) to find CURRENT tools and real pricing, and hand back a
            // clear, non-technical, step-by-step alternative — never leave the
            // user with only a raw error and no path forward.
            let alternativeGuide="";
            try{
              setProjectExecPhase("\ud83d\udd0d "+(fmt==="video"?"Video":"Image")+" generation unavailable \u2014 researching alternatives...");
              const researchQ="current best "+(fmt==="video"?"AI video generation tools 2026 with pricing (e.g. Runway, Pika, Kling, Luma)":"AI image generation tools 2026 with pricing (e.g. Midjourney, DALL-E, Ideogram)")+" for a "+(del.description||del.name).slice(0,150);
              // runResearchDesk returns an object like {brief, grounded, provider}.
              // It was being glued straight into a string, so the file showed the
              // words "[object Object]" where the guidance should have been.
              const _rd:any=await runResearchDesk((s,m,t)=>ask(s,m,t,true),co,compData,researchQ,showToast,keys);
              alternativeGuide=typeof _rd==="string"?_rd:(_rd?.brief||"");
            }catch{/* research is best-effort — the honest error below always ships regardless */}
            zip.folder(folder).file(fname+"-GENERATION-FAILED.md",
              "\u26a0 "+fmt.toUpperCase()+" COULD NOT BE GENERATED\n\n"+
              "WHY: "+(mediaErr?.message||String(mediaErr))+"\n\n"+
              "DALL\u00b7E fallback: "+(fmt==="image"?(((keys as any).openai||"").trim()?"attempted (see error above)":"NOT attempted \u2014 no OpenAI key saved in Settings"):"not applicable (video)")+"\n\n"+
              "TO FIX IT ON THIS PLATFORM: verify the fal.ai API key (Settings \u2192 fal.ai), account credits, and network access to fal.run, then re-run this deliverable.\n\n"+
              (alternativeGuide?"## Or Get It Done Right Now, Elsewhere\n\n"+alternativeGuide+"\n\nCopy the prompt below into whichever tool you choose above.\n\n":"")+
              "## Prompt (use on this platform after fixing, or on an alternative tool above)\n\n"+content);
          }
        } else if(fmt==="svg"){
          // SVG diagrams — save as proper .svg file that opens in browsers
          const svgContent=content.includes("<svg")?content:"<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 800 500\">\n"+content+"\n</svg>";
          zip.folder(folder).file(fname+".svg",svgContent);
        } else if(["linkedin_post","facebook_post","instagram_post","whatsapp_message","email"].includes(fmt)){
          // Phase 4: social/messaging deliverables — plain, clean text, ready to
          // copy-paste straight into the target app. No markdown syntax, no
          // file-format complexity needed for these.
          const platformLabel:Record<string,string>={linkedin_post:"LinkedIn Post",facebook_post:"Facebook Post",instagram_post:"Instagram Caption",whatsapp_message:"WhatsApp Message",email:"Email"}[fmt]||fmt;
          const cleanText=stripMd(content).trim();
          zip.folder(folder).file(fname+".txt","── "+platformLabel+" — ready to copy and paste ──\n\n"+cleanText);
        } else {
          zip.folder(folder).file(fname+"."+(fmt||"txt"),content);
        }
      } // end for(const del of done)
      // Phase 5: Media generation
      const mediaDels=done.filter(d=>d.outputFormat==="image"||d.outputFormat==="video"||d.outputFormat==="image_prompt"||d.outputFormat==="video_prompt"||d.name.toLowerCase().includes("image")||d.name.toLowerCase().includes("video")||d.name.toLowerCase().includes("design")||d.name.toLowerCase().includes("banner")||d.name.toLowerCase().includes("creative")||d.name.toLowerCase().includes("visual")||d.name.toLowerCase().includes("diagram"));
      const mediaPromptLines=["# Media Generation Prompts","Generated: "+new Date().toLocaleString(),"","These prompts are ready to paste into your chosen tool.",""];
      for(const del of mediaDels){
        const ctx=proj.context||{};
        const prompts=buildMediaPrompt(del,ctx);
        const folder=del._modName.replace(/[^a-zA-Z0-9]/g,"-");
        const fname=del.name.replace(/[^a-zA-Z0-9]/g,"-");
        mediaPromptLines.push("## "+del.name);
        const falKey=providerKey(keys,"fal");
        if(prompts.type==="video"||del.outputFormat==="video"){
          // ── VIDEO: Generate via fal.ai (Kling), fall back to prompts ───────
          if(falKey){
            try{
              setProjectExecPhase("🎬 Generating video: "+del.name+" (this may take 60-90s)...");
              const scriptContent=rawContentStore.current[del.id]||del.rawContent||"";
              const voiceLines=scriptContent.split("\n").filter((l:string)=>l.includes("VOICEOVER")).map((l:string)=>l.replace(/\*\*VOICEOVER[^:]*:\*\*/i,"").trim()).filter(Boolean).join(" ").slice(0,400);
              const cinematicP=voiceLines?`Cinematic ${del.name} for ${proj.context?.company?.name||""}. Key message: "${voiceLines}". Style: modern tech corporate, smooth camera, 4K, professional, aspirational. No text overlays. 16:9.`:(prompts.kling||prompts.veo||del.name);
              const videoUrl=await callFalVideo(falKey,cinematicP,5);
              if(videoUrl){
                const vr=await fetch(videoUrl);
                const vBlob=await vr.blob();
                const vBuf=await vBlob.arrayBuffer();
                zip.folder(folder).file(fname+".mp4",vBuf);
                mediaPromptLines.push("","**✅ Video generated and saved as: "+fname+".mp4**","");
              }
            }catch(e:any){
              // Generation failed — save prompts as fallback
              mediaPromptLines.push("","*⚠️ Video generation failed ("+e.message.slice(0,80)+"). Prompts saved below.*","");
              mediaPromptLines.push("","### Kling / fal.ai Prompt","```",prompts.kling||"","```","");
              mediaPromptLines.push("### Google Veo Prompt","```",prompts.veo||"","```","");
              mediaPromptLines.push("### Runway ML Prompt","```",prompts.runway||"","```","");
            }
          } else {
            // No fal key — save high-quality prompts ready to use
            mediaPromptLines.push("","### Kling / fal.ai Prompt (add fal.ai key to auto-generate)","```",prompts.kling||"","```","");
            mediaPromptLines.push("### Google Veo Prompt","```",prompts.veo||"","```","");
            mediaPromptLines.push("### Runway ML Prompt","```",prompts.runway||"","```","");
          }
        } else {
          // ── IMAGE: fal.ai first, then DALL-E, then Stability, then prompts ─
          let imgGenerated=false;
          if(falKey&&!imgGenerated){
            try{
              setProjectExecPhase("🖼 Generating image via fal.ai: "+del.name);
              const _isLinkedIn=del.name.toLowerCase().includes("linkedin")||(del.description||"").toLowerCase().includes("linkedin");
              const imgModel=del.name.toLowerCase().includes("diagram")||del.name.toLowerCase().includes("infographic")?"fal-ai/ideogram/v3":_isLinkedIn?"fal-ai/ideogram/v3":"fal-ai/flux-pro";
              const _imgSize=_isLinkedIn?"landscape_16_9":"landscape_4_3";
              const _coName=proj.context?.company?.name||co.name||"Company";
              const _coIndustry=proj.context?.company?.industry||co.industry||"AI SaaS";
              const imgPromptText=_isLinkedIn?`Professional LinkedIn announcement banner for ${_coName}, a ${_coIndustry} company. Deep navy blue background. Bold white headline text on left side showing business achievement. Three teal-accented metric cards showing growth KPIs. Abstract upward data visualization on right side. Premium executive brand design. Clean minimalist corporate aesthetic. Text readable at thumbnail size. 16:9 landscape. No people. No stock photos. Fortune 500 quality.`:(prompts.dalle||prompts.stability||del.description||(rawContentStore.current[del.id]||del.rawContent||"").slice(0,600)||del.name);
              const imgUrl=await callFalImage(falKey,imgPromptText,imgModel,_imgSize);
              if(imgUrl){
                const ir=await fetch(imgUrl);
                const ib=await ir.blob();
                const ibuf=await ib.arrayBuffer();
                zip.folder(folder).file(fname+".png",ibuf);
                mediaPromptLines.push("","**✅ Image generated via fal.ai and saved as: "+fname+".png**","");
                imgGenerated=true;
              }
            }catch(e:any){
              mediaPromptLines.push("","*⚠️ fal.ai image failed ("+e.message.slice(0,60)+"), trying next provider...*","");
            }
          }
          if(!imgGenerated&&mediaMode.image==="dalle"&&providerKey(keys,"openai")){
            try{
              setProjectExecPhase("🖼 Generating image via DALL-E: "+del.name);
              const imgUrl=await callDallE(prompts.dalle,providerKey(keys,"openai"));
              if(imgUrl){
                const ir=await fetch(imgUrl);const ib=await ir.blob();const ibuf=await ib.arrayBuffer();
                zip.folder(folder).file(fname+".png",ibuf);
                mediaPromptLines.push("","**✅ Image generated via DALL-E and saved as: "+fname+".png**","");
                imgGenerated=true;
              }
            }catch(e:any){mediaPromptLines.push("","*DALL-E failed: "+e.message.slice(0,60)+"*","");}
          }
          if(!imgGenerated&&mediaMode.image==="stability"&&providerKey(keys,"stability")){
            try{
              setProjectExecPhase("🖼 Generating image via Stability: "+del.name);
              const imgUrl=await callStabilityAI(prompts.stability,providerKey(keys,"stability"));
              if(imgUrl){
                const ir=await fetch(imgUrl);const ib=await ir.blob();const ibuf=await ib.arrayBuffer();
                zip.folder(folder).file(fname+".png",ibuf);
                mediaPromptLines.push("","**✅ Image generated via Stability AI and saved as: "+fname+".png**","");
                imgGenerated=true;
              }
            }catch(e:any){mediaPromptLines.push("","*Stability AI failed: "+e.message.slice(0,60)+"*","");}
          }
          if(!imgGenerated){
            // All providers failed or no key — save quality prompts
            mediaPromptLines.push("","### DALL-E 3 Prompt","```",prompts.dalle||"","```","");
            mediaPromptLines.push("### Midjourney Prompt","```",prompts.midjourney||"","```","");
            mediaPromptLines.push("*(Add fal.ai key in Settings to auto-generate images)*","");
          }
        }
      }
      if(mediaDels.length>0)zip.file("Media-Prompts.md",mediaPromptLines.join("\n"));

      const qaLines=[
        "# QA Report \u2014 "+proj.name,
        "Generated: "+new Date().toLocaleString(),"",
        "## REQUESTED (your prompt)",
        "**Objective:** "+proj.objective,"",
        "## DELIVERED",
        ...done.map(d=>{
          const badge=d.qaResult?.passed?"\u2705":"\u26a0";
          const score=d.qaResult?.score||0;
          const summary=d.qaResult?.summary||"";
          return badge+" **"+d.name+"** ("+d.outputFormat+") \u2014 "+score+"% \u2014 "+summary;
        }),
      ];
      if(engineDowngrades.list.length>0){
        qaLines.push("","## \u26a0 ENGINE DOWNGRADES (lower quality \u2014 fallback was used)","");
        engineDowngrades.list.forEach(d=>qaLines.push("- **"+d.deliverable+"** ("+d.format+"): "+d.reason));
        qaLines.push("","Fix: check Settings \u2192 API keys and retry.");
      }
      if(qualityWarnings.list.length>0){
        qaLines.push("","## \u26a0 CONTENT CHECK WARNINGS (shipped, but flagged \u2014 verify before use)","");
        qaLines.push("These deliverables were regenerated once to fix the issue, but still had it after the retry. They were shipped anyway rather than blocked \u2014 double-check the flagged area before relying on the file.","");
        qualityWarnings.list.forEach(d=>qaLines.push("- **"+d.deliverable+"**: "+d.warning));
      }
      if(engineDowngrades.list.length>0||qualityWarnings.list.length>0){
        try{showToast("\u26a0 "+(engineDowngrades.list.length+qualityWarnings.list.length)+" deliverable(s) flagged \u2014 see QA-Report.md","warning");}catch{}
      }
      zip.file("QA-Report.md",qaLines.join("\n"));
      const sumLines=["# "+proj.name,"","Objective: "+proj.objective,"Generated: "+new Date().toLocaleString(),"Deliverables: "+done.length,"","## Modules",...(proj.modules||[]).map(m=>"- "+m.name+" ("+m.capabilityType+"): "+(m.deliverables||[]).length+" deliverables")];
      zip.file("Project-Summary.md",sumLines.join("\n"));
      const zipBlob=await zip.generateAsync({type:"blob",compression:"DEFLATE",compressionOptions:{level:6}});
      const zipName=(proj.name||"Project").replace(/[^a-zA-Z0-9]/g,"-")+"-"+Date.now()+".zip";
      const zipUrl=URL.createObjectURL(zipBlob);
      const a=document.createElement("a");a.href=zipUrl;a.download=zipName;a.style.display="none";
      document.body.appendChild(a);a.click();document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(zipUrl),200);
      showToast("✅ ZIP downloaded — "+done.length+" files packaged","success");
    }catch(e){showToast("Packaging failed: "+e.message,"error");}
    finally{setProjectPackaging(false);setProjectExecPhase("");}
  // THIS ONE LINE IS WHY NO IMAGE WAS EVER PRODUCED.
  // This function reads keys.fal and keys.openai to generate images. But the
  // list below tells React which values the function is allowed to see. "keys"
  // was not in it - so the function kept using the EMPTY keys object from the
  // very first moment the page loaded, before your saved keys had arrived from
  // the database.
  //
  // Your fal.ai key was there. Your OpenAI key was there. The sidebar showed
  // both. This function simply could not see either, and reported
  // "fal.ai key required" and "no OpenAI key saved" - about keys you had
  // definitely saved. Exactly the message in the ZIP you sent me.
  },[projectPackaging,showToast,keys,defP,co,compData,ask,mediaMode,cur]);

const runWorkflow=useCallback(async(customChainOverride?:string[],preflightAnswers?:{questions:{persona:string;q:string}[];answers:string[]}|null)=>{
  const taskText=wfTask.trim();
  const taskCat=wfCat;
  if(!taskText||wfRunning)return;
  const ch=CHAINS[taskCat];if(!ch)return;
  const activeChain=customChainOverride&&customChainOverride.length?customChainOverride:ch.chain;
  const preflightContext=preflightAnswers?.questions?.length
    ?"\n\n=== PRE-FLIGHT CONTEXT (user answers to executive questions) ===\n"+preflightAnswers.questions.map((q,i)=>"Q ("+q.persona+"): "+q.q+"\nA: "+(preflightAnswers.answers[i]||"Not provided")).join("\n\n")+"\n\nCRITICAL: Use these answers as the primary input for the deliverable. Build everything around what the user said here."
    :"";
  cancelRef.current.wf=false;
  setUsageFeature("Flow: "+(ch?.label||wfCat),"⚡");
  setWfRunning(true);setError(null);setWfPauseMsg("");

  const wfId=Date.now();
  const newWf={
    id:wfId,task:taskText,category:taskCat,chainLabel:ch.label,
    steps:[],status:"running",startedAt:new Date().toISOString(),
    finalOutput:null,approved:false
  };
  setWfActive(newWf);setWfView("active");

  const steps:any[]=[];
  const wfCurr=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];

  // Same relevance-scoped context injection as processTask — Flow chains
  // get real Ledger/Boardroom data rather than reasoning from scratch.
  const wfModuleContext=getModuleContext(taskCat,{
    ledgerEntries,brSessions,workflows,tQueue:tQRef.current,tmRes,apRes,tmSessions,apSessions,cur:wfCurr
  });

  for(let i=0;i<activeChain.length;i++){
    if(cancelRef.current.wf){
      showToast("Workflow cancelled at Level "+(i+1),"warning");
      setWfActive(prev=>prev?{...prev,status:"cancelled"}:null);
      setWfRunning(false);setWfPhase("");
      return;
    }

    const roleId=activeChain[i];
    const role=AR.find(r=>r.id===roleId);
if(!role){
  console.error("[OrchestrIQ] Chain config error: role id '"+roleId+"' not found in AR. Level "+(i+1)+" of "+ch.label+" skipped.");
  showToast("Configuration error: role '"+roleId+"' missing for "+ch.label+" Level "+(i+1)+". Please report this.","error");
  continue;
}
    const p=EP[roleId]||{};
    const isFirst=i===0;
    const isLast=i===activeChain.length-1;
    setWfPhase(role.ic+" "+role.t+" - Level "+(i+1)+"/"+activeChain.length);

    const prevWork=steps.length>0
  ?"PREVIOUS LEVEL (build on this, do not repeat it):\nCompleted by "+steps[steps.length-1].role.t+":\n"+steps[steps.length-1].output+
    "\n\nCHAIN PROGRESSION SO FAR: "+steps.map(s=>s.role.t).join(" -> ")
  :"";

    const sys=
  "You are "+role.f+" at \""+co.name+"\".\n"+
  buildBoardIdentity(role,p)+CLARITY_PROTOCOL+"\n"+
  buildCtx(co,compData)+"\n"+
  preflightContext+"\n"+
  "WORKFLOW CHAIN: \""+ch.label+"\" Level "+(i+1)+"/"+activeChain.length+"\n"+
  "TASK: \""+taskText+"\"\n"+
  (steps.length>0?prevWork+"\n\n":"")+
  (isFirst
    ?"YOUR MANDATE: Produce the ACTUAL DELIVERABLE immediately. Not a plan. Not a strategy. The real thing the user asked for.\n\nSTEP 1: Read the PRE-FLIGHT CONTEXT above carefully — those are the user's own words about what they need. Build everything around those answers.\nSTEP 2: Check COMPANY DATA for any relevant figures (budget, product details, pricing, audience). Use them exactly.\nSTEP 3: Produce the deliverable now. Lead with it. Put it first.\n\nFORMAT YOUR OUTPUT AS:\n[DELIVERABLE]\n(the actual thing — post, document, analysis, SOP, whatever was asked — complete and ready to use)\n\n[ASSUMPTIONS MADE]\n(only list what you assumed because it was genuinely missing — keep this short)\n\n[WHAT WOULD MAKE THIS BETTER]\n(one sentence — what additional info would improve the next draft)"
    +(wfModuleContext?"\n\nPLATFORM DATA:\n"+wfModuleContext:"")
    :isLast
    ?"YOUR MANDATE: Deliver the FINAL, POLISHED executive-grade deliverable. This is what the user hands to a board, client, or investor.\n\nLook at all previous levels. Take the best content and present it as a professional consulting report.\n\nUSE THIS EXACT FORMAT:\n\n# Executive Summary\n(2-4 sentences: core finding, key number in "+wfCurr.sym+", recommended action)\n---\n## Key Insights\n(4-6 bullets, each opening with a **bold keyword**)\n---\n## Detailed Analysis\n(logical subsections with headers; use tables for all comparative or numerical data)\n---\n## Financial Impact\n(all figures in "+wfCurr.sym+"; show: formula → assumption → result for every number)\n---\n## Risks\n| Risk | Likelihood | Impact | Mitigation |\n|------|------------|--------|------------|\n---\n## Opportunities\n(3-5 bullets with upside in "+wfCurr.sym+", timeframe, and owner)\n---\n## Recommendations\n| Priority | Action | Impact | Effort | Deadline |\n|----------|--------|--------|--------|----------|\n---\n## Sources & References\n(every figure cited with source and date)\n\nFORMATTING RULES:\n- Bold all key metrics and decision points\n- Never write unbroken paragraph blocks\n- Every number must carry a unit ("+wfCurr.sym+" or %)\n- Specific to "+co.name+" — no generic placeholders\n\nAFTER finishing, on its own new line write exactly: ===CAPABILITY_BRIEF===\nThen output ONLY a single valid JSON object:\n{\"info_needed\":[\"...\"],\"tools_required\":[{\"name\":\"...\",\"available\":true,\"why\":\"...\"}],\"manual_steps\":[\"...\"],\"automated_steps\":[\"...\"],\"est_cost_usd\":0,\"notes\":\"...\"}"
    :"YOUR MANDATE: Improve the deliverable from the previous level. Do not write reports or strategy sections.\n\nDO:\n- Make the actual deliverable better (sharper copy, stronger numbers, better structure)\n- Add one thing only YOU as "+role.t+" in "+role.dl+" would know that genuinely improves the output\n- Flag if anything is off-brand, unrealistic, or weak — then fix it in the deliverable itself\n\nDO NOT:\n- Write strategy documents or planning sections\n- Repeat what previous levels already said\n- Add budget tables unless the task specifically requires financials\n\nFORMAT: Output the improved deliverable directly, then a brief note on what you changed."
  )+"\nAll figures in "+wfCurr.sym+wfCurr.code+".";

    let reply="";
    let stepFailed=false;
    let failMsg="";

    try{
      const replyFull=await askFull(sys,[{role:"user",content:"Process: \""+taskText+"\""}],isLast?2800:1500);
      reply=replyFull.primary;
      const usedProv=replyFull.usedProvider||defP;
      const usedModel=MODELS[usedProv]?.model||usedProv;
      // (usage is now logged centrally inside callAI, with the real provider)
    }catch(err:any){
      stepFailed=true;
      failMsg=err.message||"Unknown error";
    }

    if(stepFailed){
      const step={role,output:"⚠ This level failed: "+failMsg,level:i+1,isFirst,isLast,failed:true,ts:new Date().toISOString(),capability:null};
      steps.push(step);
      setWfActive({...newWf,steps:[...steps],status:"error"});
      setError(failMsg);
      showToast("Level "+(i+1)+" ("+role.t+") failed: "+failMsg,"error");
      setWfRunning(false);setWfPhase("");
      return;
    }

    let stepOutput=reply;
    let stepCapability=null;
    if(isLast){
      const cleanReply=reply.replace(/===\s*\n?CAPABILITY_BRIEF/g,"===CAPABILITY_BRIEF===");
      const parsed=parseCapabilityBrief(cleanReply);
      stepOutput=parsed.output;
      // Intelligence Engine — quality validation at final level (fail-safe: keeps original on any error)
      try{
        setWfPhase("🔍 Quality Review — validating final deliverable");
        const ieCompany={name:co.name||"the company",industry:co.industry||"",stage:co.stage||"",location:co.location||"",markets:co.markets||"",currency:co.currency||"INR",currencySymbol:wfCurr.sym||""};
        const reviewed=await selfReview(stepOutput,taskText,ieCompany,(s,m,t)=>ask(s,m,t,false));
        if(reviewed&&reviewed.length>stepOutput.length*0.5)stepOutput=reviewed;
        const evTags=classifyEvidence(stepOutput);
        if(evTags.length>0){
          const cnt={};evTags.forEach(t=>{cnt[t.category]=(cnt[t.category]||0)+1;});
          stepOutput+="\n\n---\n**Evidence audit (Intelligence Engine):** "+Object.entries(cnt).map(([k,v])=>v+" "+k).join(" · ");
        }
      }catch(ieErr){/* keep original output */}
      if(parsed.capability){
        const fee=computeServiceFee(parsed.capability.est_cost_usd||0);
        const rate=await fetchExchangeRate(wfCurr.code);
        stepCapability={
          ...parsed.capability,
          fee_usd:fee.fee,total_usd:fee.total,
          converted_total: rate?Math.ceil(fee.total*rate):null,
          converted_currency: wfCurr.code, converted_symbol: wfCurr.sym,
        };
      }
    }

    const step={role,output:stepOutput,level:i+1,isFirst,isLast,failed:false,ts:new Date().toISOString(),capability:stepCapability};
    steps.push(step);
    setWfActive({...newWf,steps:[...steps],status:isLast?"awaiting_approval":"running"});
  }

  setWfRunning(false);setWfPhase("");
  cancelRef.current.wf=false;
},[wfTask,wfCat,wfRunning,co,compData,keys,defP,showToast,ledgerEntries,brSessions,tmRes,apRes]);

  const approveWF=useCallback(()=>{
    if(!wfActive)return;
    const approved={...wfActive,status:"approved",approved:true,approvedAt:new Date().toISOString()};
    const updated=[approved,...workflows].slice(0,50);
    setWorkflows(updated);sv("cos-wf",updated);
    setWfActive(null);setWfTask("");setWfView("history");
    showToast("Workflow approved and archived ✓","success");
  },[wfActive,workflows,showToast]);

  const rejectWF=useCallback((note)=>{
    if(!wfActive)return;
    const rejected={...wfActive,status:"rejected",rejectionNote:note||"Sent back for revision",rejectedAt:new Date().toISOString()};
    const updated=[rejected,...workflows].slice(0,50);
    setWorkflows(updated);sv("cos-wf",updated);
    setWfActive(null);setWfTask("");setWfView("new");
    showToast("Workflow rejected — task returned","warning");
  },[wfActive,workflows,showToast]);

  const enqueue=useCallback((taskText,category,priority,autoRouted)=>{
    const cat=autoRouted?autoRoute(taskText):category;
    const ch=CHAINS[cat];if(!ch)return;
    const po={high:0,medium:1,low:2};
    const task={id:Date.now()+Math.random(),task:taskText,category:cat,chainLabel:ch.label,priority:priority||"medium",autoRouted:!!autoRouted,status:TS.QUEUED,steps:[],finalOutput:null,approved:false,createdAt:new Date().toISOString(),startedAt:null,completedAt:null,currentLevel:0};
    const sorted=[...tQRef.current,task].sort((a,b)=>po[a.priority]!==po[b.priority]?po[a.priority]-po[b.priority]:new Date(a.createdAt)-new Date(b.createdAt));
    tQRef.current=sorted;setTQueue([...sorted]);sv("cos-tq",sorted);
    addN("Task queued: \""+taskText.slice(0,50)+(taskText.length>50?"...":"")+"\"","info");
    showToast("Task added to queue","info");
    return task.id;
  },[showToast]);

const processTask=useCallback(async(task:any)=>{
  const ch=CHAINS[task.category];if(!ch)return;
  const p3Curr=CURRENCIES.find(c=>c.code===co.currency)||CURRENCIES[0];
  const steps=[...task.steps];

  // Phase 1: compressed context accumulator for autonomous queue
  const compressedContexts:CompressedContext[]=[];

  // Phase 1: benchmark session for queue tasks
  let benchSession:BenchmarkSession|null=null;
  if(BENCHMARK_MODE){
    benchSession=startBenchmarkSession({
      workflowId:String(task.id),
      chainLabel:ch.label,
      task:task.task,
      compressionEnabled:COMPRESSION_ENABLED,
    });
  }

  const upd=(updates:any)=>{
    tQRef.current=tQRef.current.map(t=>t.id===task.id?{...t,...updates}:t);
    setTQueue([...tQRef.current]);
    setP3Running((prev:any)=>prev?.id===task.id?{...prev,...updates}:prev);
    sv("cos-tq",tQRef.current);
  };

  setUsageFeature("Task Queue: "+ch.label,"🤖");
  upd({status:TS.RUNNING,startedAt:new Date().toISOString()});
  addN("Chain started: "+ch.label,"running");

  // Retrieve relevant cross-module context scoped to this task's category.
  // Finance tasks get Ledger data. Strategy tasks get Boardroom sessions.
  // All tasks get prior approved outputs from the same category.
  // Nothing irrelevant enters the prompt.
  const moduleContext=getModuleContext(task.category,{
    ledgerEntries,brSessions,workflows,tQueue:tQRef.current,tmRes,apRes,tmSessions,apSessions,cur:p3Curr
  });

  for(let i=0;i<ch.chain.length;i++){
    if(cancelRef.current.q){
      upd({status:TS.FAILED,error:"Cancelled by user"});
      addN("Queue cancelled","warning");
      if(BENCHMARK_MODE&&benchSession)completeBenchmarkSession(benchSession);
      return;
    }

    const roleId=ch.chain[i];
    const role=AR.find(r=>r.id===roleId);
    if(!role)continue;

    const p=EP[roleId]||{};
    const isFirst=i===0;
    const isLast=i===ch.chain.length-1;
    setP3Phase(role.ic+" "+role.t+" — Level "+(i+1)+"/"+ch.chain.length);
    upd({currentLevel:i+1,status:TS.RUNNING});

    // MODE A (COMPRESSION_ENABLED=false): full output — existing behavior, zero change
    // MODE B (COMPRESSION_ENABLED=true):  compressed summaries only
    let prevWork="";
    if(!isFirst){
      if(COMPRESSION_ENABLED&&compressedContexts.length>0){
        prevWork=formatCompressedContextForPrompt(compressedContexts);
      }else{
        prevWork=steps
          .filter(s=>!s.failed)
          .map((s:any,si:number)=>"Level "+(si+1)+": "+s.role.t+"\n"+s.output)
          .join("\n\n");
      }
    }

    // Benchmark: calculate both mode contexts for comparison
    const modeAContext=isFirst?"":steps
      .filter(s=>!s.failed)
      .map((s:any,si:number)=>"Level "+(si+1)+": "+s.role.t+"\n"+s.output)
      .join("\n\n");
    const modeBContext=isFirst?"":(compressedContexts.length>0
      ?formatCompressedContextForPrompt(compressedContexts)
      :modeAContext);

    // System prompt — identical in both modes except prevWork variable
    const sys=
      "You are "+role.f+" at \""+co.name+"\".\n"+
      buildBoardIdentity(role,p)+CLARITY_PROTOCOL+"\n"+
      "OPERATING STYLE: Think critically, not agreeably — challenge weak assumptions including your own from prior drafts. When current real-world data would strengthen your answer (rates, regulations, market data, competitor moves, benchmarks), search for it and use it — don't rely on memory for anything time-sensitive. Be decisive and specific; avoid generic frameworks restated without numbers.\n"+
      buildCtx(co,compData)+"\n"+
      "AUTONOMOUS CHAIN Level "+(i+1)+"/"+ch.chain.length+" - "+ch.label+"\n"+
      "Priority: "+task.priority.toUpperCase()+"\n"+
      "Task: \""+task.task+"\"\n"+
      (!isFirst?prevWork+"\n":"")+
      (isFirst
        ?"INITIATING: Acknowledge task, produce FIRST DRAFT. Use "+p3Curr.sym+p3Curr.code+"."+
          (moduleContext?"\n\n=== PLATFORM DATA — USE THESE REAL FIGURES ===\n"+moduleContext+"\n\nDATA RULE: The figures above come from real modules in this platform (Ledger transactions, prior Boardroom decisions, approved task history). Start your analysis from this real data. Only declare an assumption if a specific figure is genuinely absent from the platform data above — and label it explicitly as an assumption.":"")
        :isLast
        ?"FINAL APPROVAL: Review all levels. Produce DEFINITIVE FINAL OUTPUT as a professional consulting report.\n\nUSE THIS EXACT FORMAT (McKinsey/BCG/Deloitte standard):\n\n# Executive Summary\n(2-4 sentences: core finding, key number in "+p3Curr.sym+p3Curr.code+", recommended action)\n---\n## Key Insights\n(4-6 bullets, each opening with a **bold keyword**)\n---\n## Detailed Analysis\n(logical subsections with headers; use tables for all comparative or numerical data)\n---\n## Financial Impact\n(all figures in "+p3Curr.sym+"; show formula → assumption → result for every number)\n---\n## Risks\n| Risk | Likelihood | Impact | Mitigation |\n|------|------------|--------|------------|\n---\n## Opportunities\n(3-5 bullets with upside, timeframe, and owner)\n---\n## Recommendations\n| Priority | Action | Impact | Effort | Deadline |\n|----------|--------|--------|--------|----------|\n---\n## Sources & References\n(every figure cited with source and date)\n\nFORMATTING: Bold key metrics. Use tables for numbers. Never write unbroken paragraphs. Every number must have a unit."
        :"MID-LEVEL: Review previous level, add "+role.dl+" expertise. Enhanced output in "+p3Curr.sym+p3Curr.code+"."
      );

    // Execute level call
    const levelStartTime=Date.now();
    let providerFailures=0;

    try{
      let reply=await ask(sys,[{role:"user",content:"Process: \""+task.task+"\""}],2800);
      // Intelligence Engine — quality validation at final level (fail-safe: keeps original on any error)
      if(isLast){
        try{
          setP3Phase("🔍 Quality Review — validating final deliverable");
          const ieCompany={name:co.name||"the company",industry:co.industry||"",stage:co.stage||"",location:co.location||"",markets:co.markets||"",currency:co.currency||"INR",currencySymbol:p3Curr.sym||""};
          const reviewed=await selfReview(reply,task.task,ieCompany,(s,m,t)=>ask(s,m,t,false));
          if(reviewed&&reviewed.length>reply.length*0.5)reply=reviewed;
          reply+=ieEvidenceAudit(reply);
        }catch(ieErr){/* keep original output */}
      }
      const levelDuration=Date.now()-levelStartTime;

      // Phase 1: compress this level's output for next level context
      // Only runs when COMPRESSION_ENABLED=true and not the last level
      // If compression fails workflow continues unaffected
      let compressed:CompressedContext|null=null;
      if(COMPRESSION_ENABLED&&!isLast){
        compressed=await compressLevelOutput({
          fullOutput:reply,
          agentRole:role.t,
          agentFullTitle:role.f,
          agentDepartment:role.dl||"General",
          level:i+1,
          currency:p3Curr.code,
          callAI,
          keys,
          defaultProvider:defP,
          effectiveGroqKey:EFF_GROQ,
          effectiveGeminiKey:EFF_GEMINI,
        });
        if(compressed){
          compressedContexts.push(compressed);
        }else if(BENCHMARK_MODE){
          console.warn(
            "[BENCHMARK] Queue Level "+(i+1)+" compression failed. "+
            "Full output used for next level."
          );
        }
      }

      // Store completed step
      const step={
        role,output:reply,level:i+1,isFirst,isLast,
        ts:new Date().toISOString(),
        compressed:compressed||null,
      };
      steps.push(step);
      upd({steps:[...steps],currentLevel:i+1});
      addN("Level "+(i+1)+" ("+role.t+") complete","success");

      // Phase 1: log benchmark entry
      if(BENCHMARK_MODE&&benchSession){
        const modeAInputTokens=estimateTokens(sys.replace(prevWork,modeAContext));
        const modeAOutputTokens=estimateTokens(reply);
        const modeATotal=modeAInputTokens+modeAOutputTokens;
        const modeBInputTokens=estimateTokens(sys.replace(prevWork,modeBContext));
        const modeBOutputTokens=modeAOutputTokens;
        const modeBTotal=modeBInputTokens+modeBOutputTokens;
        const compressionCost=compressed?.compression_tokens_used||0;
        const netSaving=modeATotal-modeBTotal-compressionCost;

        logLevelBenchmark(benchSession,{
          level:i+1,
          agent_role:role.t,
          agent_full_title:role.f,
          provider:defP,
          mode_a_input_tokens:modeAInputTokens,
          mode_a_output_tokens:modeAOutputTokens,
          mode_a_total_tokens:modeATotal,
          mode_a_context_chars:modeAContext.length,
          mode_b_input_tokens:modeBInputTokens,
          mode_b_output_tokens:modeBOutputTokens,
          mode_b_total_tokens:modeBTotal,
          mode_b_context_chars:modeBContext.length,
          compression_tokens_used:compressionCost,
          compression_ratio:compressed?.compression_ratio||1,
          net_token_saving:netSaving,
          execution_duration_ms:levelDuration,
          provider_failures:providerFailures,
          retry_count:0,
          quality_score:null,
          quality_notes:"",
        });
      }

    }catch(err:any){
      if(BENCHMARK_MODE&&benchSession&&
         err.message?.toLowerCase().includes("rate limit")){
        markRateLimitHit(benchSession);
        providerFailures++;
      }
      upd({status:TS.FAILED,error:err.message});
      addN("Failed at Level "+(i+1)+" ("+role.t+"): "+err.message,"error");
      showToast("Queue task failed at Level "+(i+1)+": "+err.message,"error");
      if(BENCHMARK_MODE&&benchSession)completeBenchmarkSession(benchSession);
      return;
    }
  }

  upd({
    steps:[...steps],
    status:TS.REVIEWING,
    finalOutput:steps[steps.length-1]?.output||"",
    completedAt:new Date().toISOString(),
    currentLevel:ch.chain.length
  });
  addN("Chain complete — awaiting your approval","complete");
  showToast("Task chain complete — review and approve","success");
  setP3View("completed");

  if(BENCHMARK_MODE&&benchSession){
    completeBenchmarkSession(benchSession);
  }

},[co,compData,keys,defP,showToast,ledgerEntries,brSessions,workflows,tmRes,apRes]);

  const runQueue=useCallback(async()=>{
    if(qRunning)return;
    const pending=tQRef.current.filter(t=>t.status===TS.QUEUED);
    if(!pending.length){showToast("No queued tasks to process.","info");return;}
    cancelRef.current.q=false;
    setQRunning(true);
    for(const task of pending){
      if(cancelRef.current.q)break;
      setP3Running(task);setP3View("running");
      await processTask(task);
    }
    setP3Running(null);setQRunning(false);setP3Phase("");cancelRef.current.q=false;
  },[qRunning,processTask,showToast]);

  const approveQ=useCallback((taskId)=>{
    const updated=tQRef.current.map(t=>t.id===taskId?{...t,status:TS.APPROVED,approved:true,approvedAt:new Date().toISOString()}:t);
    tQRef.current=updated;setTQueue([...updated]);sv("cos-tq",updated);
    showToast("Task approved and archived ✓","success");
  },[showToast]);

  const rejectQ=useCallback((taskId,note)=>{
    const updated=tQRef.current.map(t=>t.id===taskId?{...t,status:TS.REJECTED,rejectionNote:note||"Sent back for revision"}:t);
    tQRef.current=updated;setTQueue([...updated]);sv("cos-tq",updated);
    showToast("Task rejected","warning");
  },[showToast]);

  const requeueT=useCallback((taskId)=>{
    const updated=tQRef.current.map(t=>t.id===taskId?{...t,status:TS.QUEUED,steps:[],finalOutput:null,approved:false,currentLevel:0,startedAt:null,completedAt:null}:t);
    tQRef.current=updated;setTQueue([...updated]);sv("cos-tq",updated);
    showToast("Task re-queued","info");
  },[showToast]);

  const deleteT=useCallback((taskId)=>{
    const updated=tQRef.current.filter(t=>t.id!==taskId);
    tQRef.current=updated;setTQueue([...updated]);sv("cos-tq",updated);
  },[]);

  const addD=()=>{if(!dataF.k.trim())return;const d={...compData,[dataF.k]:dataF.v};setCompData(d);sv("cos-cd",d);setDataF({k:"",v:""});};
  const delD=k=>{const d={...compData};delete d[k];setCompData(d);sv("cos-cd",d);};

  // saveDn/saveDnTiming removed: editing now happens entirely inside Admin
  // Console's own Donation tab, which reads and writes this same table
  // directly. dnCfg/dnPlatformCfg here still drive the real popup shown to
  // regular users - only the admin-facing editing form moved.

  const resetData=async()=>{
    // THE CONFIRMED BUG: everything below this line only ever cleared
    // localStorage and React state - it never issued a single DELETE
    // against the real Supabase tables. That is exactly why Cost
    // Architecture (and everything else here) kept showing old data after
    // a "reset": nothing in the actual database was ever touched. This is
    // the real fix for Cost Architecture specifically, using the new,
    // tested reset_cost_architecture function.
    try{
      const {data:{user}}=await supabase.auth.getUser();
      if(user){await supabase.rpc("reset_cost_architecture",{p_user_id:user.id});}
    }catch(e){console.warn("[OIQ] Cost Architecture reset error:",e);}
    // Clear all user-generated data from every module
    const dataKeys=[
      "cos-ch","cos-cd","cos-br","cos-br-live","cos-wf","cos-tq",
      "cos-tm","cos-tm-live","cos-ap","cos-ap-live",
      "cos-ledger","cos-accounts","cos-actions","cos-dispatch-templates",
      "cos-projects","cos-project-plan","cos-dp","oiq-token-records",
    ];
    for(const k of dataKeys){try{localStorage.removeItem(k);}catch{}}
    // Also remove all deliverable content keys (cos-rc-*)
    try{
      const toRemove=Object.keys(localStorage).filter(k=>k.startsWith("cos-rc-"));
      toRemove.forEach(k=>localStorage.removeItem(k));
    }catch{}
    // Reset all module state
    setChats({});
    setCompData({});
    setBrSessions([]);
    setBrCur({q:"",researchBrief:"",format:"threaded",stages:[]});
    setWorkflows([]);
    setWfActive(null);
    tQRef.current=[];
    setTQueue([]);
    setSelRole(null);
    setTmSessions([]);
    setTmRes("");
    setTmDec("");
    setTmResearchBrief("");
    setApSessions([]);
    setApRes("");
    setApResearchBrief("");
    setLedgerEntries([]);
    setCustomAccounts([]);
    setDispatchTemplates([]);
    setActionItems([]);
    setProjects([]);
    setProjectPlan(null);
    setProjectExecution(null);
    setProjectDashboardOpen(false);
    rawContentStore.current={};
    setConfirmReset(null);
    showToast("All data reset — API keys preserved","warning");
  };
  const fullReset=async()=>{
    try{clearCostContext();}catch{}
    // clearAll() only removes the CURRENT user's namespace. A full reset that
    // signs you out must leave nothing behind for the next person either.
    WorkspaceMemory.clearAll();
    try{WorkspaceMemory.clearDevice();}catch{}
    try{await supabase.auth.signOut();}catch(e){console.warn("[OIQ] Sign out error:",e);}
    window.location.href="/";
  };
  const exportAll=()=>{
    // Enrich projects with full deliverable content from ref/localStorage
    // The JSON download is a Blob with no size limit — include complete content
    const enrichedProjects=(projects||[]).map(proj=>({
      ...proj,
      modules:(proj.modules||[]).map(mod=>({
        ...mod,
        deliverables:(mod.deliverables||[]).map(del=>{
          // Priority: memory ref (full) > localStorage (full) > state (preview)
          const fullContent=
            rawContentStore.current[del.id]||
            (()=>{try{return localStorage.getItem("cos-rc-"+del.id)||"";}catch{return "";}})();
          return fullContent
            ?{...del,rawContent:fullContent,_contentSource:"full"}
            :{...del,_contentSource:"preview"};
        })
      }))
    }));
    const payload={version:VERSION,exported:new Date().toISOString(),
      company:co,companyData:compData,
      ledgerEntries,customAccounts,dispatchTemplates,adminConfig,actionItems,
      chats,boardroomSessions:brSessions,workflows,taskQueue:tQueue,
      projects:enrichedProjects,
      timeMachineSessions:tmSessions,
      timeMachineLive:{dec:tmDec,res:tmRes,brief:tmResearchBrief},
      autopilotSessions:apSessions,
      autopilotLive:{res:apRes,brief:apResearchBrief}};
    dlFile("OrchestrIQ-"+co.name.replace(/\s+/g,"-")+"-"+Date.now()+".json",payload);
  };
  const handleSignOut=useCallback(async(saveFirst:boolean)=>{
    if(saveFirst){
      try{exportAll();}catch(e){showToast("Save failed: "+(e as Error).message+" — signing out anyway","warning");}
      await new Promise(r=>setTimeout(r,600));
    }
    // THIS LINE DID NOT EXIST, AND ITS ABSENCE IS THE BUG YOU FOUND.
    // Ordinary sign-out left every API key, boardroom session, ledger entry and
    // company record sitting in this browser's storage. The next person to sign
    // in on the same machine read it as their own. Only the "full reset" button
    // ever cleared anything.
    // clearDevice() removes OrchestrIQ data for EVERY user on this device, not
    // just the one leaving. A shared or demo machine must not hold one person's
    // credentials for the next person to find. The cost is that a returning
    // user re-enters their keys; for a product handling financial data that is
    // the right way round.
    try{WorkspaceMemory.clearDevice();}catch(e){console.warn("[OIQ] clearDevice:",e);}
    try{clearCostContext();}catch{}
    try{await supabase.auth.signOut();}catch(e){console.warn("[OIQ] Sign out error:",e);}
    setShowSignOutConfirm(false);
    window.location.reload();
  },[showToast]);

  const importData=file=>{const r=new FileReader();r.onload=e=>{try{const d=JSON.parse(e.target.result);if(d.company)setCo(d.company);sv("cos-co",d.company);
if(d.adminConfig){setAdminConfig({...adminConfig,...d.adminConfig});sv("cos-admin-config",d.adminConfig);}
if(d.actionItems){setActionItems(d.actionItems);sv("cos-actions",d.actionItems);}if(d.chats){setChats(d.chats);sv("cos-ch",d.chats);}if(d.boardroomSessions){setBrSessions(d.boardroomSessions);sv("cos-br",d.boardroomSessions);}if(d.workflows){setWorkflows(d.workflows);sv("cos-wf",d.workflows);}if(d.taskQueue){tQRef.current=d.taskQueue;setTQueue(d.taskQueue);sv("cos-tq",d.taskQueue);}if(d.projects){
  const cp=(d.projects||[]).map(p=>p.status==="executing"||p.status==="qa"?{...p,status:"partial"}:p);
  setProjects(cp);sv("cos-projects",cp);
  // Restore full deliverable content to ref and localStorage
  cp.forEach(proj=>{
    (proj.modules||[]).forEach(mod=>{
      (mod.deliverables||[]).forEach(del=>{
        if(del.rawContent&&del._contentSource==="full"){
          rawContentStore.current[del.id]=del.rawContent;
          try{localStorage.setItem("cos-rc-"+del.id,del.rawContent);}catch{}
        }
      });
    });
  });
}
if(d.companyData){setCompData(d.companyData);sv("cos-cd",d.companyData);}
if(d.ledgerEntries){setLedgerEntries(d.ledgerEntries);sv("cos-ledger",d.ledgerEntries);}
if(d.customAccounts){setCustomAccounts(d.customAccounts);sv("cos-accounts",d.customAccounts);}
if(d.dispatchTemplates){setDispatchTemplates(d.dispatchTemplates);sv("cos-dispatch-templates",d.dispatchTemplates);}
if(d.timeMachineSessions){setTmSessions(d.timeMachineSessions);sv("cos-tm",d.timeMachineSessions);}
if(d.timeMachineLive?.dec){setTmDec(d.timeMachineLive.dec);setTmRes(d.timeMachineLive.res||"");setTmResearchBrief(d.timeMachineLive.brief||"");sv("cos-tm-live",{dec:d.timeMachineLive.dec,res:d.timeMachineLive.res,brief:d.timeMachineLive.brief});}
if(d.autopilotSessions){setApSessions(d.autopilotSessions);sv("cos-ap",d.autopilotSessions);}
if(d.autopilotLive?.res){setApRes(d.autopilotLive.res);setApResearchBrief(d.autopilotLive.brief||"");sv("cos-ap-live",{res:d.autopilotLive.res,brief:d.autopilotLive.brief});}
setResumeInfo(null);
showToast("Workspace loaded — all modules restored","success");}catch{showToast("Invalid workspace file","error");}};r.readAsText(file);};

  // ─── RAILWAY DOCUMENT ENGINE — the ONLY document generator in the app ───
  // ─── EXPORT NORMALISER ──────────────────────────────────────────────────────
  // What the document service receives is what it renders. Two of the three faults
  // in your generated files start HERE, in what we send, and can be fixed today
  // without touching the Python service:
  //
  // 1. LITERAL ASTERISKS. PowerPoint slide 7 shows, on the slide itself:
  //        "**Segment: Manufacturing SMEs (UP/Bihar)**"
  //    The PPTX engine writes text frames verbatim and never parses markdown.
  //    Emphasis markers are stripped for slides, keeping the words.
  //
  // 2. TRUNCATED TABLE CELLS. Slide 6 gave six columns 2.02 INCHES EACH regardless
  //    of content, so "Typical Annual Cost per Seat" was cut mid-word. Until the
  //    Python engine computes widths from content, over-long cells are shortened
  //    here at a WORD boundary with an ellipsis, so nothing is chopped mid-word.
  //
  // NOT fixable from here: the rupee symbol rendering as a black box in PDF
  // TABLES. Proven from your file - 118 correct in body text, 40 boxes, and every
  // one of the 40 inside a table. DejaVu IS embedded; the TableStyle still uses
  // Helvetica, which has no rupee glyph. That needs the one Python change.
  const normaliseForExport=useCallback((body:string,format:string)=>{
    let t=String(body||"");
    if(format==="pptx"){
      t=t.replace(/\*\*(.+?)\*\*/g,"$1").replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g,"$1$2");
    }
    t=t.split("\n").map(line=>{
      if(!line.trim().startsWith("|"))return line;
      const cells=line.trim().replace(/^\|/,"").replace(/\|\s*$/,"").split("|");
      if(cells.length<4)return line;                 // narrow tables already fit
      const cap=cells.length>=6?42:cells.length===5?56:72;
      const cut=cells.map(c=>{
        const s=c.trim();
        if(s.length<=cap)return " "+s+" ";
        const w=s.slice(0,cap).lastIndexOf(" ");
        return " "+s.slice(0,w>cap*0.6?w:cap).trim()+"\u2026 ";
      });
      return "|"+cut.join("|")+"|";
    }).join("\n");
    return t;
  },[]);
 
  const railwayGenerate=useCallback(async(format:string,opts:{title?:string;objective?:string;body?:string;currency?:string;currencySymbol?:string;audience?:string;docPurpose?:string;brief?:string})=>{
    const RAILWAY_URL="https://orchestriq-gen-service-production.up.railway.app";
    const claudeKey=providerKey(keys,"claude");
    const openaiKey=providerKey(keys,"openai");
    const deepseekKey=providerKey(keys,"deepseek");
 
    // ─── BROWSER-FIRST PATH ────────────────────────────────────────────────
    // Two problems solved by one change.
    //
    //  1. NVIDIA could not generate documents. Its key lives in Cloudflare and
    //     is reachable only from THIS browser via /api/nvidia. Railway is a
    //     different machine with no NVIDIA key, so the old path could only ever
    //     use Claude, OpenAI or DeepSeek - which is why the guard below used to
    //     hard-fail when none of those three had a key.
    //  2. Customer API keys were POSTed to Railway. TLS covers them in flight;
    //     the exposure is at rest, in any request log. Open HIGH finding since
    //     Session 47.
    //
    // Building the blueprint HERE fixes both: every provider is reachable, and
    // Railway receives a finished structure with no credentials attached.
    //
    // If ANY of this fails - bad JSON, provider down, unusable structure - we
    // fall through to the original /generate/* path untouched. This can only
    // add a better outcome; it cannot remove the existing one.
    // Carries the ACTUAL failure reason out of the try block. Without this the
    // user saw only "could not build in your browser" with no cause, and the
    // real explanation was buried in a console warning nobody reads.
    let _bpWhy="";
    // MY MISTAKE IN SESSION 53B, STATED PLAINLY: I made an unproven path the
    // SILENT DEFAULT for every user and every provider. It should have been
    // opt-in from the first line. A beta path must never quietly replace one
    // that works - that is the no-downgrade rule we agreed, and I broke it.
    //
    // Default OFF. With this unset - which it is for everyone, nobody has ever
    // set it - railwayGenerate behaves EXACTLY as it did in Session 52, the
    // version you confirmed working. No settings change is needed from you;
    // deploying this is the restoration.
    const _browserDocs=(()=>{try{return localStorage.getItem("oiq-browser-docs")==="1";}catch{return false;}})();
    if(_browserDocs&&format!=="xlsx"){
      try{
        const _bpBrief=opts.brief||[
          "Produce a "+format.toUpperCase()+" titled: "+(opts.title||"Document"),
          "Company: "+[co.name||"",co.industry||"",co.stage||""].filter(Boolean).join(" | "),
          "",
          "SOURCE MATERIAL:",
          normaliseForExport(opts.body||"",format).slice(0,120000),
        ].join("\n");
        // WHY THE TASK TYPE MATTERS HERE, AND WHY THIS FAILED FOR YOU.
        //
        // The call went to NVIDIA with reasoning ON. nvidiaTokenBudget then adds
        // the model's 2,500-token reasoning overhead, so a 7,000-token request
        // became a 9,000-token generation - on a 120B reasoning model, over a
        // prompt carrying up to 120,000 characters, inside the 75-second proxy
        // timeout from Session 49. It could not finish in time.
        //
        // nvidiaShouldReason() already has the switch: any task name beginning
        // with "format" disables reasoning. Designing a JSON structure is
        // formatting work - the thinking that matters happened upstream, in the
        // boardroom and the executive conversations. So we name the task
        // honestly and the model stops paying for deliberation it does not need.
        const _bpRes=await buildBlueprint(format as BlueprintFormat,
          {brief:_bpBrief,title:opts.title||"Document",currencySymbol:opts.currencySymbol||cur.sym},
          (sys,msgs,maxT)=>ask(sys,msgs,maxT,false,"format_blueprint"),
          {onProgress:(m)=>setExpStep(m)});
        if(_bpRes.ok&&_bpRes.blueprint){
          setExpStep("\uD83D\uDCC4 Rendering the document\u2026");
          const rr=await fetch(RAILWAY_URL+"/render/"+format,{
            method:"POST",headers:{"Content-Type":"application/json"},
            // No claude_key. No openai_key. No deepseek_key. Nothing to leak.
            body:JSON.stringify({blueprint:_bpRes.blueprint,title:opts.title||"",
              subtitle:opts.objective||"",currency_symbol:opts.currencySymbol||cur.sym||"\u20b9",
              access_token:(await supabase.auth.getSession()).data?.session?.access_token||""}),
            signal:AbortSignal.timeout(120000)});
          if(rr.ok){
            const bb=await rr.arrayBuffer();
            if(bb.byteLength>2000){
              setExpStep("");
              return bb;
            }
            _bpWhy="the renderer returned an empty file";
          }else{
            const _t=await rr.text().catch(()=>"");
            _bpWhy="the renderer refused it ("+httpErrText(_t,rr.status)+")";
          }
        }else{
          _bpWhy=_bpRes.reason||"unknown";
        }
        console.warn("[OIQ] browser blueprint unavailable, using server path:",_bpWhy);
      }catch(bpErr:any){
        _bpWhy=String(bpErr?.message||bpErr).slice(0,200);
        console.warn("[OIQ] browser blueprint failed, using server path:",_bpWhy);
      }
    }
    // ─── SERVER PATH (unchanged) ───────────────────────────────────────────
    if(!claudeKey&&!openaiKey&&!deepseekKey)throw new Error(
      "Could not build the document in your browser"+(_bpWhy?": "+_bpWhy:"")+
      ". There is no Claude, OpenAI or DeepSeek key configured for the server fallback either. "+
      (/timed out|did not finish|did not respond|75 seconds/i.test(_bpWhy)
        ? "Your NVIDIA model was too slow for this amount of content. In Settings \u2192 API, switch the NVIDIA model to GPT-OSS 120B (fast, no reasoning) and try again."
        : "Try again, or add a provider key in Settings."));
    // Excellent tier prefers Claude first (highest quality); Standard/Professional prefer DeepSeek first (cheaper).
    const providerOrder = RESPONSE_QUALITY==="excellent" ? "claude,openai,deepseek" : "deepseek,claude,openai";
    const coCtx=[co.name||"",co.industry||"",co.stage||"",co.location||""].filter(Boolean).join(" | ");
    // PPTX/DOCX need the largest AI blueprint + slowest render, so they get a
    // longer budget and one automatic retry (Railway cold starts + slow provider
    // responses were causing PPT to abort at 120s while faster PDF succeeded).
    const TIMEOUT_MS:{[k:string]:number}={pptx:220000,docx:200000,pdf:180000,xlsx:180000};
    const budget=TIMEOUT_MS[format]||150000;
    const MIN_SIZE:{[k:string]:number}={xlsx:1000,pdf:2000,pptx:5000,docx:5000};
    const _payload=JSON.stringify({
        objective:opts.objective||opts.title||"Document",
        company_context:coCtx,
        // WAS .slice(0,8000). 8,000 characters is roughly three pages. The whole
        // workspace - every executive conversation, the boardroom debate, the
        // ledger - was cut to three pages before the model ever saw it, and the
        // model then filled the gap with plausible invention. That is the
        // "missing key information" problem at its source. 120,000 characters is
        // comfortably inside the context window of every provider we route to.
        // Proof of who is asking. Railway checks this with Supabase and refuses
        // anyone who is not a signed-in user of your app. Until now that service
        // would answer ANYONE who knew its web address.
        access_token:(await supabase.auth.getSession()).data?.session?.access_token||"",
        available_data:normaliseForExport(opts.body||"",format).slice(0,120000),
        audience:opts.audience||"general",
        doc_purpose:opts.docPurpose||"",
        generation_brief:opts.brief||"",
        currency:opts.currency||co.currency||"INR",
        currency_symbol:opts.currencySymbol||cur.sym||"₹",
        claude_key:isProviderOff("claude")?"":claudeKey,
        openai_key:isProviderOff("openai")?"":openaiKey,
        deepseek_key:isProviderOff("deepseek")?"":deepseekKey,
        provider_order:providerOrder
    });
    const _attempt=async()=>{
      const r=await fetch(RAILWAY_URL+"/generate/"+format,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:_payload,
        signal:AbortSignal.timeout(budget)
      });
      if(!r.ok)throw new Error("Railway "+format+": HTTP "+r.status);
      const b=await r.arrayBuffer();
      if(b.byteLength<(MIN_SIZE[format]||1000))throw new Error("Railway returned an empty/incomplete file");
      return b;
    };
    showToast("Building "+format.toUpperCase()+" via Python engine — this can take up to 3 minutes…","info");
    let buf:ArrayBuffer;
    try{
      buf=await _attempt();
    }catch(firstErr:any){
      // One retry: cold-start/transient timeouts usually succeed on the second try
      // because the Railway container is now warm.
      showToast("Still working — retrying once…","info");
      buf=await _attempt();
    }
    const MIME:{[k:string]:string}={xlsx:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",pdf:"application/pdf",pptx:"application/vnd.openxmlformats-officedocument.presentationml.presentation",docx:"application/vnd.openxmlformats-officedocument.wordprocessingml.document"};
    const blob=new Blob([buf],{type:MIME[format]||"application/octet-stream"});
    const fname=(opts.title||"Document").replace(/[^a-zA-Z0-9]/g,"-")+"-"+Date.now()+"."+format;
    const u=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=u;a.download=fname;a.style.display="none";
    document.body.appendChild(a);a.click();document.body.removeChild(a);
    setTimeout(()=>URL.revokeObjectURL(u),200);
  // ask and setExpStep are used by the browser-first path above. Omitting them
  // would capture the FIRST render's ask() forever - the same stale-closure
  // class of bug as the stuck consent button in Session 43, where the callback
  // kept reading a value that had long since changed.
  },[keys,co,cur,showToast,normaliseForExport,ask,setExpStep]);

  // FEATURE 4 & 5: Export Studio generation
  const runExport=useCallback(async()=>{
    if(expGenerating)return;
    setExpGenerating(true);setExpSynthesis("");
    try{
      const corpus=gatherWorkspace(co,compData,chats,brSessions,workflows,tQueue,{
      ledger:ledgerEntries,cur,
        chats:expSources.chats,boardroom:expSources.boardroom,workflows:expSources.workflows,
        tasks:expSources.tasks,timeMachine:expSources.timeMachine?tmRes:null,autopilot:expSources.autopilot?apRes:null,
      });
      if(corpus.trim().split("\n").length<3){throw new Error("No workspace content selected. Use the platform first, then export.");}
      const isPdf=expMode==="pdf";
      const dtype=isPdf?expDocType:expPptType;
      const tLabel=isPdf?(PDF_TYPES.find(t=>t.id===dtype)?.label):(PPT_TYPES.find(t=>t.id===dtype)?.label);
      setExpStep("🎨 Presentation Architect is synthesizing your workspace…");
      const pa=AR.find(r=>r.id==="pres_arch");
      const structureHint=isPdf
        ?"Produce a "+tLabel+". Use ## for each major section heading. Under each, use concise bullet points and markdown tables where data is comparative. Sections must suit a "+tLabel+": "+(dtype==="summary"?"single tight executive summary with key metrics and the one recommendation.":dtype==="investor"?"Executive Summary (3-4 sentence overview of the opportunity and ask - never leave empty or use placeholder dashes), Problem, Market & TAM, Solution, Traction/Metrics, Business Model, Financials, Risks, The Ask.":dtype==="executive"?"Executive Summary (3-4 sentence overview - never leave empty or use placeholder dashes), Key Findings, Strategic Recommendations, Financials, Risk Register, 90-Day Action Plan.":"full detailed report covering every theme found in the workspace.")
        :"Produce a "+tLabel+" as a slide deck. Each ## heading = one slide title. Under each, 3-6 short punchy bullet points (slide-ready, not paragraphs) OR a markdown table. Aim for 8-12 slides. Structure for a "+tLabel+": "+(dtype==="investor"?"Title, Problem, Solution, Market, Traction, Business Model, Competition, Financials, Team, The Ask.":dtype==="pitch"?"Hook, Problem, Solution, Why Now, Market, Product, Traction, Ask.":dtype==="roadmap"?"Vision, Now, Next, Later, Milestones, Metrics.":dtype==="research"?"Objective, Method, Findings, Analysis, Implications, Next Steps.":dtype==="operational"?"Overview, KPIs, Wins, Issues, Actions, Outlook.":dtype==="strategy"?"Context, Strategic Goals, Initiatives, Roadmap, Risks, Metrics.":"Agenda, Key Themes, Insights, Recommendations, Risks, Next Steps.");
      const userTitle=expTitle.trim()||(tLabel+" — "+co.name);

      // RAILWAY FIRST — everything below only runs as a backup if this fails.
      let usedRailway=false;
      try{
        const railFmt=isPdf?"pdf":"pptx";
        // Build the audience brief: who reads this, what they need, what this
        // format demands. Previously the document type you selected above was
        // discarded here and never reached the generator at all - which is why
        // "Investor Deck" and "Operational Review" produced the same document.
        const _aud=(expAudience==="auto"?DocIQ.inferAudience(dtype):expAudience) as AudienceId;
        const _brief=DocIQ.buildGenerationBrief({
          format:railFmt as any,docPurpose:dtype,audience:_aud,title:userTitle,
          companyContext:[co.name||"",co.industry||"",co.stage||"",co.location||""].filter(Boolean).join(" | "),
          evidence:corpus,currencySymbol:cur.sym});
        setExpStep("🎯 Writing for: "+(DocIQ.AUDIENCE_SPECS[_aud]?.label||_aud)+"…");
        await railwayGenerate(railFmt,{title:userTitle,objective:userTitle,body:corpus,
          currency:co.currency,currencySymbol:cur.sym,
          audience:_aud,docPurpose:dtype,brief:_brief});
        usedRailway=true;
        showToast((isPdf?"PDF":"PowerPoint")+" generated via Python engine ✓","success");
      }catch(railErr:any){
        recordEngineDowngrade(userTitle,isPdf?"pdf":"pptx",railErr);
        showToast("Could not generate the "+(isPdf?"PDF":"PowerPoint")+": "+String(railErr?.message||railErr||"unknown error").slice(0,180),"error");
        setExpStep("");setExpGenerating(false);
        return;
      }
      if(!usedRailway){
      // QUALITY ENGINE: for PPTX only, try the structured archetype pipeline first.
      // Falls back to the original markdown pipeline if the AI response doesn't
      // parse as valid structured JSON — this can never make PPTX generation worse,
      // only better when it succeeds.
      let usedQualityEngine=false;
      if(!isPdf){
        try{
          const qeSys=buildSys(pa,co,compData)+"\n\nWORKSPACE CORPUS TO SYNTHESIZE:\n"+corpus+"\n\n"+buildStructuredDeckPrompt(userTitle,tLabel);
          const qeRaw=await ask(qeSys,[{role:"user",content:"Build the structured slide JSON now."}],4000,false,"general");
          const slides=parseStructuredDeck(qeRaw);
          if(slides){
            setExpStep("📊 Building PowerPoint (consulting-grade)…");
            const PptxGenJS=await ensurePptx();
            const pptx=new PptxGenJS();
            const useStandard=expStyleResult?.ok&&expStyleResult.slideSize&&!expStyleResult.slideSize.isWidescreen;
            if(useStandard){pptx.defineLayout({name:"STANDARD",width:10,height:7.5});pptx.layout="STANDARD";}
            else{pptx.defineLayout({name:"WIDE",width:13.333,height:7.5});pptx.layout="WIDE";}
            const PAL={briefing:"14B8A6",strategy:"6366F1",investor:"A855F7",pitch:"F97316",roadmap:"8B5CF6",research:"06B6D4",operational:"F59E0B"};
            const customPal=buildPalFromExtractedStyle(expStyleResult);
            const A=customPal?.accent||PAL[dtype]||"14B8A6";
            slides.forEach(s=>{if(s.type==="title"){s.companyName=s.companyName||co.name;s.deckTitle=s.deckTitle||userTitle;}});
            renderStructuredDeck(pptx,slides,A,customPal||QE_PAL);
            await pptx.writeFile({fileName:(co.name||"Deck").replace(/\s+/g,"-")+"-"+tLabel.replace(/\s+/g,"-")+"-"+Date.now()+".pptx"});
            setExpSynthesis(slides.map(s=>"## "+(s.answerFirstTitle||s.deckTitle||s.type)+"\n"+(s.keyPoints||[]).join("\n")).join("\n\n"));
            usedQualityEngine=true;
          }
        }catch(qeErr){
          console.warn("[OrchestrIQ] Quality engine pipeline failed, falling back to legacy:",qeErr);
        }
      }

      if(!usedQualityEngine){
        const sys=buildSys(pa,co,compData)+"\n\nWORKSPACE CORPUS TO SYNTHESIZE:\n"+corpus+"\n\nOUTPUT INSTRUCTIONS: "+structureHint+" Be specific and use real numbers from the corpus in "+cur.sym+cur.code+". Do not include any preamble - start directly with the first ## section. EVERY section must contain real content - never output a section with only a horizontal rule, dash, or placeholder. If data for a section is genuinely unavailable, write 1-2 sentences explaining what is needed instead of leaving it blank.";
        const synth=await ask(sys,[{role:"user",content:"Build: \""+userTitle+"\". Synthesize across the entire workspace corpus above."}],4000);
        setExpSynthesis(synth);
        setExpStep(isPdf?"📄 Rendering PDF…":"📊 Building PowerPoint…");
        if(isPdf)await generatePDFv2(dtype,userTitle,synth,co,cur);
        else await generatePPTX(dtype,userTitle,synth,co,cur);
        showToast((isPdf?"PDF":"PowerPoint")+" generated (backup renderer)","success");
      }
      }
      setExpStep("");
    }catch(e){showToast("Export failed: "+e.message,"error");setExpStep("");}
    finally{setExpGenerating(false);}
  },[expGenerating,expMode,expDocType,expPptType,expAudience,expSources,expTitle,co,compData,chats,brSessions,workflows,tQueue,tmRes,apRes,cur,keys,defP,showToast,expStyleResult,railwayGenerate]);

  // Quick single-source export (used inline in chat/boardroom/etc.)
  // RAILWAY ONLY. We never emit a browser-rendered (lower-quality) file.
  // If the Python engine is unavailable, the user gets a clear retry message.
  // Opens the audience dialog. Nothing is generated until the user answers, so
  // no document is ever produced without knowing who will read it.
  const quickExport=useCallback((mode,dtype,title,body)=>{
    setXferAud("auto");
    setXfer({mode,dtype:dtype||"detailed",title,body});
  },[]);
 
  // Runs AFTER the user has chosen. The old quickExport body lives here.
  const runQuickExport=useCallback(async(mode,dtype,title,body,audience)=>{
    const fmt=mode==="pdf"?"pdf":"pptx";
    try{
      // Build the same audience brief Export Studio builds. Before this, these
      // buttons sent raw text with no audience, no document purpose and no
      // format doctrine - which is why a chat exported for an investor and one
      // exported for the operations team came out as the same document.
      const _aud=(audience==="auto"?DocIQ.inferAudience(dtype):audience) as AudienceId;
      const _brief=DocIQ.buildGenerationBrief({
        format:fmt as any,docPurpose:dtype,audience:_aud,title,
        companyContext:[co.name||"",co.industry||"",co.stage||"",co.location||""].filter(Boolean).join(" | "),
        evidence:body,currencySymbol:cur.sym});
      await railwayGenerate(fmt,{title,objective:title,body,currency:co.currency,currencySymbol:cur.sym,
        audience:_aud,docPurpose:dtype,brief:_brief});
      showToast("Downloaded for "+(DocIQ.AUDIENCE_SPECS[_aud]?.label||_aud)+" ✓","success");
    }catch(railErr:any){
      // MY BUG, from Session 33: this block catches `railErr` but referenced `e`,
      // which does not exist here. The catch block itself threw a ReferenceError,
      // so nothing was shown and nothing was downloaded - PDF and PPT appeared to
      // do nothing at all while MD kept working.
      try{recordEngineDowngrade(title,fmt,railErr);}catch{}
      showToast("Could not generate the "+(mode==="pdf"?"PDF":"PowerPoint")+": "+String(railErr?.message||railErr||"unknown error").slice(0,180),"error");
    }
  },[co,cur,showToast,railwayGenerate]);
 
  // The dialog itself. Deliberately small: two questions, sensible defaults, and
  // it never blocks - "Auto" is always one click away, so a user who does not
  // care is not slowed down, while a user who does gets a different document.
  const ExportAudienceDialog=()=>{
    if(!xfer)return null;
    const fmtLabel=xfer.mode==="pdf"?"PDF":"PowerPoint";
    const purposeList=xfer.mode==="pdf"?PDF_TYPES:PPT_TYPES;
    const effective=xferAud==="auto"?DocIQ.inferAudience(xfer.dtype):xferAud;
    const spec=DocIQ.AUDIENCE_SPECS[effective];
    return(
      <div style={{position:"fixed",inset:0,background:"rgba(4,7,15,0.78)",zIndex:9000,
        display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
        onClick={()=>setXfer(null)}>
        <div onClick={e=>e.stopPropagation()} style={{background:"var(--oiq-panel,#0f1420)",
          border:"1px solid var(--oiq-line,#1a2030)",borderRadius:10,padding:18,
          width:"100%",maxWidth:520,maxHeight:"88vh",overflowY:"auto"}}>
          <div style={{fontSize:14,fontWeight:800,color:"var(--oiq-ink,#F1F5F9)",marginBottom:3}}>
            Who is this {fmtLabel} for?</div>
          <div style={{fontSize:10,color:"#5A6480",marginBottom:14,lineHeight:1.55}}>
            This changes what goes IN the document, not just its tone. An investor pack
            leads with market size and unit economics. An operations review leads with
            steps, owners and dates. Same evidence, different document.
          </div>
 
          <label style={S.lbl}>Document type</label>
          <select value={xfer.dtype} onChange={e=>setXfer({...xfer,dtype:e.target.value})}
            style={{...S.inp,marginBottom:12,cursor:"pointer"}}>
            {purposeList.map((t:any)=><option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
 
          <label style={S.lbl}>Audience</label>
          <select value={xferAud} onChange={e=>setXferAud(e.target.value)}
            style={{...S.inp,marginBottom:8,cursor:"pointer"}}>
            <option value="auto">Auto — match the document type</option>
            {AUDIENCE_OPTIONS.map(a=><option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
 
          {spec&&(
            <div style={{background:"rgba(20,184,166,0.06)",border:"1px solid rgba(20,184,166,0.22)",
              borderRadius:6,padding:"9px 11px",marginBottom:14}}>
              <div style={{fontSize:9,fontWeight:800,color:"#14B8A6",marginBottom:4,
                textTransform:"uppercase",letterSpacing:0.6}}>Writing for: {spec.label}</div>
              <div style={{fontSize:9.5,color:"#A0AAC0",lineHeight:1.55,marginBottom:5}}>
                Opens with: {spec.opensWith}</div>
              <div style={{fontSize:9,color:"#5A6480",lineHeight:1.5}}>
                Evidence standard: <b style={{color:"#A0AAC0"}}>{spec.evidenceBar}</b>
                {spec.evidenceBar==="auditable"?" — every figure must be traceable to a source."
                 :spec.evidenceBar==="defensible"?" — every figure must survive a challenge."
                 :" — directional figures acceptable if labelled."}
              </div>
            </div>
          )}
 
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setXfer(null)} style={{...S.hBtn,flex:1,textAlign:"center",padding:"9px"}}>
              Cancel</button>
            <button onClick={()=>{const x=xfer;const a=xferAud;setXfer(null);
              runQuickExport(x.mode,x.dtype,x.title,x.body,a);}}
              style={{...S.hBtn,flex:2,textAlign:"center",padding:"9px",background:"#14B8A6",
                color:"#04070F",fontWeight:800,border:"1px solid #14B8A6"}}>
              Generate {fmtLabel}</button>
          </div>
        </div>
      </div>);
  };
 
  // ONE place decides whether a module is available. Everything else asks this.
  const canUse=useCallback((moduleId:string)=>{
    if(ent?.unlimited)return true;                 // owner and staff
    const map=ent?.module_map||{};
    const key=map[moduleId];
    if(!key)return true;                           // no rule defined = allowed
    return !!ent?.features?.[key]?.enabled;
  },[ent]);
 
  const curRole=AR.find(r=>r.id===selRole);
  const curMsgs=selRole?(chats[selRole]||[]):[];
  // A provider is usable only if it has a key AND is switched on. Everything
  // downstream (Primary AI, fallback chain, specialist routing) reads cfgP, so
  // switching one off behaves exactly as if its key were never entered.
  // "NVIDIA - 2 keys" with one key entered, and the provider card rendered
  // twice. This line HARDCODES "nvidia" at position 0, because the free tier is
  // always available, then appends every provider that has a key. Before
  // Session 58 nobody COULD have an NVIDIA key, so the collision was
  // impossible. Adding the key field created it.
  // Beyond the wrong label it also duplicated a React key on the provider cards
  // and made the failover chain try NVIDIA twice before moving on.
  const cfgP=Array.from(new Set(["nvidia",...Object.keys(keys).filter(p=>isAiProviderKey(p)&&keys[p]?.trim()&&!offP[p])]));
  // AI providers PLUS any web-search service with a key: everything that receives
  // user text. Search services get only the query, and the disclosure says so.
  const activeProviderIds=["nvidia",...Object.keys(keys).filter(p=>keys[p]?.trim()&&!offP[p])]
    .filter(p=>!!getDisclosure(p)).filter((v,i,a)=>a.indexOf(v)===i);
  const consentSignature=activeProviderIds.slice().sort().join("|");
  const consentNeeded=activeProviderIds.length>0&&dataConsent!==consentSignature;
  useEffect(()=>{consentRef.current={needed:consentNeeded,signature:consentSignature};},[consentNeeded,consentSignature]);
 
  // AUDIT TRAIL. An acknowledgement that is not recorded is not evidence. This
  // writes an append-only row naming the user, the exact providers shown, the
  // stance and policy URL displayed for each, and when. The table has no UPDATE
  // and no DELETE policy, so it cannot be altered after the fact.
  const recordConsent=useCallback(async(ids:string[])=>{
    try{
      const {data:{user}}=await supabase.auth.getUser();
      if(!user)return;
      const terms:any={};
      ids.forEach(pid=>{
        const d=getDisclosure(pid);
        if(d)terms[pid]={name:d.name,company:d.company,residency:d.dataResidency,
                         training:d.training,policy:d.policyUrl,checked:d.verified};
      });
      await supabase.from("consent_log").insert({
        user_id:user.id,user_email:user.email||"",
        consent_type:"ai_provider_data_handling",
        providers:ids,provider_terms:terms,
        disclosure_ver:"2026-08",app_version:VERSION,
        user_agent:(navigator?.userAgent||"").slice(0,300),
      });
    }catch(e){console.warn("[OIQ] consent log:",e);}
  },[]);
  const sColor=s=>s===TS.APPROVED?"#10B981":s===TS.REVIEWING?"#8B5CF6":s===TS.RUNNING?"#14B8A6":s===TS.REJECTED||s===TS.FAILED?"#EF4444":"#F59E0B";
  const sBg=s=>s===TS.APPROVED?"rgba(16,185,129,0.12)":s===TS.REVIEWING?"rgba(139,92,246,0.1)":s===TS.RUNNING?"rgba(20,184,166,0.1)":s===TS.REJECTED||s===TS.FAILED?"rgba(239,68,68,0.1)":"rgba(245,158,11,0.1)";

  // S: defined at module scope below

  const healthDot=apiHealth==="ok"?"🟢":apiHealth==="fail"?"🔴":apiHealth==="checking"?"🟡":"⚪";
  const healthTip=apiHealth==="ok"?"API connected":apiHealth==="fail"?"API check failed — verify key in Settings":apiHealth==="checking"?"Checking API…":"API not tested";

  if(page==="landing") return(
    <div id="oiq-root" style={{minHeight:"100vh",background:"#0a0e1a",fontFamily:"Manrope,sans-serif",color:"#A0AAC0",overflowY:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 28px",borderBottom:"1px solid #14192a"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}><span style={{fontSize:20,color:"#14B8A6",fontWeight:900}}>◆</span><span style={{fontSize:18,fontWeight:800,color:"#F1F5F9"}}>{BRAND}</span><span style={{fontSize:9,color:"#5A6480",marginLeft:4}}>v{VERSION}</span></div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>setShowDonate(true)} style={{background:"rgba(20,184,166,0.08)",border:"1px solid rgba(20,184,166,0.25)",borderRadius:6,padding:"6px 14px",color:"#14B8A6",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Support</button>
          <button onClick={()=>setPage("onboard")} style={{background:"#14B8A6",color:"#0a0e1a",border:"none",borderRadius:6,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Get Started</button>
        </div>
      </div>
      <div style={{textAlign:"center",padding:"60px 28px 30px"}}>
        <div style={{fontSize:10,fontWeight:700,color:"#14B8A6",textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>AI Company Operating System</div>
        <h1 style={{fontSize:34,fontWeight:900,color:"#F1F5F9",lineHeight:1.15,marginBottom:14,letterSpacing:-1,maxWidth:680,margin:"0 auto 14px"}}>Full AI executive hierarchy.<br/>Autonomous chains. Real answers.</h1>
        <p style={{fontSize:14,color:"#8892B0",maxWidth:540,margin:"0 auto 24px",lineHeight:1.6}}>From Chairman to Accounts Executive — every role, every department, 3 AI providers, location-aware, currency-native. Now with 7 themes, local workspace ownership, and investor-grade PDF & PowerPoint generation.</p>
        <button onClick={()=>setPage("onboard")} style={{background:"#14B8A6",color:"#0a0e1a",border:"none",borderRadius:8,padding:"14px 36px",fontSize:16,fontWeight:800,cursor:"pointer",fontFamily:"Manrope,sans-serif",marginBottom:8}}>Launch {BRAND}</button>
        <div style={{fontSize:12,color:"#5A6480",marginTop:8}}>Free Gemini tier works · All data stays in your browser</div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"10px 28px 20px"}}>
        <div style={{textAlign:"center",marginBottom:16}}><h2 style={{fontSize:20,fontWeight:900,color:"#F1F5F9"}}>API Setup Guide — Get your key in 3 minutes</h2></div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
          {Object.entries(MODELS).map(([id,m])=>(
            <div key={id} style={{background:"#131825",border:"1px solid "+m.color+"22",borderRadius:12,padding:"18px 16px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}><div style={{width:10,height:10,borderRadius:"50%",background:m.color}}/><span style={{fontSize:14,fontWeight:800,color:m.color}}>{m.name}</span>{id==="gemini"&&<span style={{fontSize:8,background:"rgba(16,185,129,0.12)",color:"#10B981",padding:"1px 6px",borderRadius:8,fontWeight:700}}>FREE</span>}</div>
              <ol style={{paddingLeft:16,fontSize:11,color:"#A0AAC0",lineHeight:2,margin:0}}>
                <li>Go to <a href={m.keyUrl} target="_blank" rel="noopener noreferrer" style={{color:m.color}}>{m.company} console</a></li>
                <li>Create / generate an API key</li>
                <li>Copy and paste into onboarding</li>
                {id!=="gemini"&&<li>Add billing credits to use</li>}
              </ol>
              <div style={{fontSize:10,color:"#3A4060",marginTop:8}}>{id==="claude"&&"~$3/M input · $15/M output"}{id==="openai"&&"~$2.50/M input · $10/M output"}{id==="gemini"&&"Free: 50 req/day"}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"0 28px 50px"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
          {[["🏛️","AI Boardroom","Live C-suite debate with synthesis and CEO-ready recommendations.","#14B8A6"],["🎨","Presentation Studio","Synthesize your whole workspace into investor-grade PDFs and PowerPoint decks.","#A855F7"],["🤖","Decision Autopilot","Surfaces 6 critical decisions you should be making right now.","#F59E0B"],["🎭","7 Themes","Dark, Light, Enlightened, Pro Blue, Executive Gray, High Contrast — instant, persistent.","#3B82F6"],["💾","Local Workspace","Save and load your entire workspace to your own device. No cloud required.","#10B981"],["⚡","Autonomous Chains","Tasks processed through the full hierarchy — you just approve at the end.","#84CC16"]].map(([ic,t,d,c])=>(
            <div key={t} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:10,padding:"16px 14px"}}><div style={{fontSize:24,marginBottom:6}}>{ic}</div><div style={{fontSize:13,fontWeight:700,color:c,marginBottom:4}}>{t}</div><div style={{fontSize:11,color:"#8892B0",lineHeight:1.6}}>{d}</div></div>
          ))}
        </div>
      </div>
      {showDonate&&<DonateModal cfg={dnCfg} presets={DONATION_PRESETS} onClose={()=>setShowDonate(false)} cur={cur} amt={dnAmt} setAmt={setDnAmt} custom={dnCustom} setCustom={setDnCustom} S={S}/>}
      <Toaster toasts={toasts} onDismiss={id=>setToasts(prev=>prev.filter(t=>t.id!==id))}/>
      <style>{CSS}</style>
    </div>
  );

  if(page==="onboard"){
    const hasKey=Object.values(keys).some(k=>k?.trim())||!!EFF_GEMINI;
    return(
      <div id="oiq-root" style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#0a0e1a",fontFamily:"Manrope,sans-serif",padding:20}}>
        <div style={{background:"#131825",borderRadius:14,padding:"26px 24px",width:"100%",maxWidth:520,border:"1px solid #1a2030",maxHeight:"95vh",overflowY:"auto"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}><span style={{fontSize:22,color:"#14B8A6"}}>◆</span><span style={{fontSize:18,fontWeight:800,color:"#F1F5F9"}}>Setup {BRAND}</span></div>
          <label style={S.lbl}>Step 1: API Keys {EFF_GEMINI?"(Gemini powered — optional)":"(need at least one)"}</label>
          <div style={{background:"rgba(16,185,129,0.05)",border:"1px solid rgba(16,185,129,0.2)",borderRadius:6,padding:"8px 10px",marginBottom:10,fontSize:10,color:"#10B981"}}>Gemini is free — no credit card needed. Get key at <a href={MODELS.gemini.keyUrl} target="_blank" rel="noopener noreferrer" style={{color:"#4285F4"}}>aistudio.google.com</a>.</div>
          {Object.entries(MODELS).map(([id,m])=>(
            <div key={id} style={{marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:keys[id]?.trim()?m.color:"#3A4060"}}/>
                <span style={{fontSize:11,fontWeight:700,color:"var(--oiq-ink)"}}>{m.name}</span>
                {id==="gemini"&&<span style={{fontSize:8,color:"#10B981",fontWeight:700}}>FREE</span>}{m.note&&<span style={{fontSize:9,color:"#5A6480",marginLeft:4}}>{m.note}</span>}
                <a href={m.keyUrl} target="_blank" rel="noopener noreferrer" style={{marginLeft:"auto",fontSize:9,color:m.color,textDecoration:"none"}}>Get key</a>
                {keys[id]?.trim()&&<button onClick={()=>testKey(id)} style={{...S.iBtn,fontSize:9,padding:"1px 6px"}}>{testSt[id]==="testing"?"…":testSt[id]==="ok"?"✓ OK":testSt[id]?.startsWith("fail:")?"✗ Fail":"Test"}</button>}
              </div>
              <input style={S.inp} type="password" value={keys[id]} onChange={e=>{setKeys({...keys,[id]:e.target.value});setTestSt(p=>({...p,[id]:undefined}));}} placeholder={m.placeholder}/>
              {testSt[id]?.startsWith("fail:")&&<div style={{fontSize:9,color:"#EF4444",marginTop:2,lineHeight:1.4}}>{testSt[id].slice(5)}</div>}
            </div>
          ))}
                {cfgP.length>1&&(
            <div style={{padding:"8px 10px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030",marginBottom:10}}>
              <label style={{...S.lbl,marginBottom:5}}>Default Model</label>
              <div style={{display:"flex",gap:4}}>{cfgP.map(p=><button key={p} onClick={()=>setDefP(p)} style={{flex:1,padding:"5px",borderRadius:4,fontSize:10,fontWeight:600,border:"1px solid "+(defP===p?(MODELS[p]?.color||"#14B8A6"):"#1a2030"),background:defP===p?(MODELS[p]?.color||"#14B8A6")+"15":"transparent",color:defP===p?(MODELS[p]?.color||"#14B8A6"):"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{(MODELS[p]?.name||p)}</button>)}</div>
            </div>
          )}
          {[["name","Company Name *","e.g. Nexus Technologies"],["industry","Industry *","e.g. EdTech, FinTech, SaaS"],["location","HQ Location *","e.g. Gurugram, India"],["markets","Target Markets (optional)","e.g. India + UAE"]].map(([f,lb,ph])=>(
            <div key={f} style={{marginBottom:8}}><label style={S.lbl}>{lb}</label><input style={S.inp} value={co[f]} onChange={e=>setCo({...co,[f]:e.target.value})} placeholder={ph}/></div>
          ))}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
            <div><label style={S.lbl}>Currency</label><select style={{...S.inp,padding:"8px"}} value={co.currency} onChange={e=>setCo({...co,currency:e.target.value})}>{CURRENCIES.map(c=><option key={c.code} value={c.code} style={{background:"#0a0e1a"}}>{c.sym} {c.code}</option>)}</select></div>
            <div><label style={S.lbl}>Stage</label><select style={{...S.inp,padding:"8px"}} value={co.stage} onChange={e=>setCo({...co,stage:e.target.value})}>{STAGES.map(st=><option key={st.id} value={st.id} style={{background:"#0a0e1a"}}>{st.ic} {st.l}</option>)}</select></div>
          </div>
          {/* THE ACTUAL FIX: previously there was no way past this screen at
              all without a key - a real signup blocker, exactly as
              reported. Company details are still required either way;
              only the API key requirement becomes optional, and only once
              explicitly acknowledged. */}
          {!hasKey && !skipApiKeyAck && (
            <div style={{background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:8,padding:"12px 14px",marginBottom:12}}>
              <div style={{fontSize:11.5,color:"#F1F5F9",lineHeight:1.6,marginBottom:10}}>
                Don't have an API key yet? You can still continue — you'll get free access to the platform's shared AI tier.
                Some heavier or premium capabilities are reserved for Enterprise members with their own key, but you can explore
                and use the core product right away, and add your own key later in Settings whenever you're ready.
              </div>
              <button onClick={()=>setSkipApiKeyAck(true)} style={{...S.btnGhost,fontSize:11}}>I understand — continue without an API key</button>
            </div>
          )}
          <button onClick={completeOnboard} disabled={!(hasKey||skipApiKeyAck)||!co.name.trim()||!co.industry.trim()||!co.location.trim()} style={{...S.pBtn,opacity:(hasKey||skipApiKeyAck)&&co.name.trim()&&co.industry.trim()&&co.location.trim()?1:0.3}}>Launch {BRAND}</button>
          <button onClick={()=>setPage("landing")} style={{background:"none",border:"none",color:"#5A6480",fontSize:12,cursor:"pointer",fontFamily:"Manrope,sans-serif",marginTop:8,display:"block"}}>Back to home</button>
        </div>
        <Toaster toasts={toasts} onDismiss={id=>setToasts(prev=>prev.filter(t=>t.id!==id))}/>
        <style>{CSS}</style>
      </div>
    );
  }

  return(
    <div id="oiq-root" style={{display:"flex",flexDirection:"column",width:"100vw",height:"100vh",background:"#EDEAE3",fontFamily:"Inter,system-ui,sans-serif",color:"#4A453D",overflow:"hidden"}}>
      <a href="#oiq-main" className="skip-link">Skip to main content</a>
      <div id="oiq-live" aria-live="polite" aria-atomic="false" className="sr-only"/>
      <header id="oiq-header" role="banner" aria-label="OrchestrIQ navigation">
        <button id="oiq-hamburger" aria-label="Toggle navigation menu" aria-expanded={sbOpen}
          onClick={()=>{const open=!sbOpen;setSbOpen(open);const r=document.getElementById("oiq-root");if(r){r.classList.toggle("oiq-sb-open",open);}const ov=document.getElementById("oiq-sb-overlay");if(ov){ov.classList.toggle("oiq-overlay-visible",open);}}}>
          {sbOpen?"✕":"☰"}
        </button>
        <div id="oiq-header-logo">
          <span className="oiq-brand-mark">◆</span>
          <span className="oiq-brand-name">OrchestrIQ</span>
        </div>
        <div id="oiq-header-workspace" title={co.name||"Set company name in Settings"}>
          {co.name||"Set workspace"}
        </div>
        <div id="oiq-header-actions">
          <select value={theme} onChange={e=>changeTheme(e.target.value)} aria-label="Select theme"
            style={{background:"none",border:"1px solid #1a2030",borderRadius:20,color:"#A0AAC0",fontSize:12,cursor:"pointer",padding:"4px 8px",fontFamily:"Inter,sans-serif",height:32}}>
            {Object.entries(THEMES).map(([id,t])=><option key={id} value={id} style={{background:"#0a0e1a"}}>{t.ic} {t.name}</option>)}
          </select>
          <button aria-label={sbCollapsed?"Expand sidebar":"Collapse sidebar"}
            onClick={()=>{const next=!sbCollapsed;setSbCollapsed(next);try{WorkspaceMemory.set("oiq-sb-col",next?"1":"0");}catch{}const r=document.getElementById("oiq-root");if(r)r.classList.toggle("oiq-sb-collapsed",next);}}
            style={{background:"none",border:"1px solid #1a2030",borderRadius:8,padding:"4px 10px",color:"#A0AAC0",cursor:"pointer",fontSize:14,fontFamily:"Inter,sans-serif",height:32,display:"flex",alignItems:"center",justifyContent:"center"}}>
            {sbCollapsed?"»":"«"}
          </button>
          <button onClick={()=>setShowExport(true)} aria-label="Export Studio" title="Export Studio — PDF and PowerPoint"
            style={{background:"none",border:"1px solid #1a2030",borderRadius:8,padding:"4px 10px",color:"#A855F7",cursor:"pointer",fontSize:16,height:32,display:"flex",alignItems:"center",justifyContent:"center"}}>🎨</button>
          {/* SOCIAL SHARING. Global, one integration point rather than
              touching every module's own header individually - reaches
              everywhere at once, with far less surface area to risk
              breaking something. Sharing a link never exposes anything
              private: it is the current page's URL plus a plain marketing
              line, nothing about the signed-in session travels with it, and
              anyone who opens the shared link still has to sign in normally. */}
          <div style={{position:"relative",display:"inline-block"}}>
            <button onClick={()=>setShowShare(v=>!v)} aria-label="Share" title="Share on social media"
              style={{background:showShare?"rgba(20,184,166,0.12)":"none",border:"1px solid "+(showShare?"#14B8A6":"#1a2030"),borderRadius:8,padding:"4px 10px",color:"#14B8A6",cursor:"pointer",fontSize:16,height:32,display:"flex",alignItems:"center",justifyContent:"center"}}>🔗</button>
            {showShare && (
              <div style={{position:"absolute",top:38,right:0,zIndex:200,background:"#0A0E1A",border:"1px solid #1a2030",borderRadius:10,padding:10,boxShadow:"0 8px 24px rgba(0,0,0,0.4)",whiteSpace:"nowrap"}}>
                <ShareButtons message={"Check out " + (co?.name ? co.name + "'s" : "my") + " work on OrchestrIQ"} />
              </div>
            )}
          </div>
          <button onClick={()=>{setShowSettings(true);setSTab("api");}} aria-label="Settings" title="Settings"
            style={{background:"none",border:"1px solid #1a2030",borderRadius:8,padding:"4px 10px",color:"#A0AAC0",cursor:"pointer",fontSize:16,height:32,display:"flex",alignItems:"center",justifyContent:"center"}}>⚙</button>
          <button onClick={()=>setShowSignOutConfirm(true)} aria-label="Sign out" title="Sign out"
            style={{background:"none",border:"1px solid #1a2030",borderRadius:8,padding:"4px 10px",color:"#EF4444",cursor:"pointer",fontSize:16,height:32,display:"flex",alignItems:"center",justifyContent:"center"}}>⎋</button>
        </div>
      </header>
      <div id="oiq-sb-overlay" aria-hidden="true"
        onClick={()=>{setSbOpen(false);const r=document.getElementById("oiq-root");if(r)r.classList.remove("oiq-sb-open");const ov=document.getElementById("oiq-sb-overlay");if(ov)ov.classList.remove("oiq-overlay-visible");}}/>
      <GlobalTicker />
      {/* ── BODY ROW: sidebar + main side by side ── */}
      <div className="oiq-body-row" style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>
      {/* ── SIDEBAR ── */}
      <div id="oiq-sidebar" className="oiq-sidebar" role="navigation" aria-label="Executive roster and module navigation" style={{width:210,background:"#0c1120",borderRight:"1px solid #14192a",display:"flex",flexDirection:"column",flexShrink:0,overflow:"hidden",paddingTop:'0'}}>
        {/* ── WORDMARK ── */}
        <div style={{padding:"16px 14px 13px",borderBottom:"1px solid var(--sb-bdr)",flexShrink:0}}>
          <div style={{fontFamily:"var(--font-head)",fontSize:19,fontWeight:700,color:"var(--oiq-sbText)",letterSpacing:"-.01em",lineHeight:1.1}}>{BRAND}</div>
          <div style={{fontSize:9,fontWeight:700,color:"var(--oiq-sbDim)",letterSpacing:".12em",textTransform:"uppercase",marginTop:3}}>Decision Intelligence</div>
        </div>

        {/* ── PROVIDER + CURRENCY ── */}
        <div style={{padding:"7px 14px",fontSize:9,color:"var(--oiq-sbDim)",display:"flex",alignItems:"center",gap:5,borderBottom:"1px solid var(--sb-bdr)",flexShrink:0}}>
          <span style={{width:5,height:5,borderRadius:"50%",background:cfgP.length?"var(--oiq-accent)":"#EF4444",flexShrink:0}}/>
          <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{!cfgP.length?"No key — add in Settings":cfgP.length===1?MODELS[cfgP[0]]?.name:MODELS[defP]?.name+" · "+cfgP.length+" keys"}</span>
          <span style={{marginLeft:"auto",flexShrink:0}}>{cur.sym}{co.currency}</span>
        </div>
        {/* Always visible, on EVERY module: who receives what you type. */}
        {!!activeProviderIds.length&&(
          <button onClick={()=>setShowDisclosure(true)} title="See exactly where your data goes"
            style={{padding:"6px 14px",fontSize:8.5,lineHeight:1.5,color:"var(--oiq-sbDim)",background:"none",border:"none",borderBottom:"1px solid var(--sb-bdr)",textAlign:"left",cursor:"pointer",fontFamily:"inherit",width:"100%",flexShrink:0,display:"flex",alignItems:"center",gap:5}}>
            <span style={{flexShrink:0}}>{providersNeedingCaution(activeProviderIds).length?"\u26a0":"\ud83d\udd12"}</span>
            <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>
              {activeProviderIds.map(p=>getDisclosure(p)?.name||p).join(" \u00b7 ")}
            </span>
            <span style={{flexShrink:0,textDecoration:"underline"}}>details</span>
          </button>
        )}

        {/* ── MODULE DROPDOWN TRIGGER ── */}
        <div style={{position:"relative",padding:"10px 10px",flexShrink:0}}>
          <button onClick={()=>setShowModules(v=>!v)}
            style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:"var(--oiq-sbBorder)",border:"1px solid var(--sb-bdr)",borderRadius:8,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
            <span style={{fontSize:15}}>{[["nerve","🧠"],["workflow","⚡"],["agentic","🔗"],["agents","🤖"],["p3","🤖"],["chat","💬"],["data","🗄️"],["ledger","📒"],["dispatch","📡"],["actions","✅"],["studio","🎨"],["funding","💰"],["tokens","🔢"]].find(([v])=>v===view)?.[1]||"🧠"}</span>
            <span style={{flex:1,fontSize:11.5,fontWeight:700,color:"var(--oiq-sbText)",textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em"}}>{[["nerve","Nerve Center"],["jarvis","JARVIS"],["workflow","Workflow"],["agentic","Agentic AI"],["agents","AI Agents"],["p3","Autopilot"],["chat","Chat"],["data","Data Hub"],["costarch","Cost Architecture"],["compliance","Compliance & Controls"],["spendgov","Spend Governance & Financial Control"],["sheets","Sheets"],["home","Command Center"],["ledger","Ledger"],["finance","Finance"],["dispatch","Pulse"],["actions","Tasks"],["studio","Studio"],["funding","Funding"],["tokens","Tokens"],["workspace","AI Workspace"],["liveboard","Live Boardroom"],["account","My Account"],["admin","Admin Console"]].find(([v])=>v===view)?.[1]||"Nerve Center"}</span>
            <span style={{fontSize:9,color:"var(--oiq-sbDim)",transition:"transform 0.2s",transform:showModules?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
          </button>
          {showModules&&(
            <div style={{position:"absolute",top:"calc(100% - 2px)",left:10,right:10,background:"var(--oiq-sbBg,var(--oiq-surface2,#0c1120))",backdropFilter:"none",border:"1px solid var(--sb-bdr)",maxHeight:"60vh",overflowY:"auto",borderRadius:10,zIndex:200,padding:7,boxShadow:"0 8px 32px rgba(0,0,0,0.4)"}}>
              {[["home","🎛️","Command Center"],["jarvis","◈","JARVIS"],["nerve","🧠","Nerve Center"],["workflow","⚡","Workflow"],["agentic","🔗","Agentic AI"],["agents","🤖","AI Agents"],["p3","🤖","Autopilot"],["chat","💬","Chat"],["data","🗄️","Data Hub"],["costarch","🧮","Cost Architecture"],["compliance","🛡️","Compliance & Controls"],["spendgov","🏛️","Spend Governance"],["sheets","📊","Sheets"],["ledger","📒","Ledger"],["finance","🏦","Finance"],["dispatch","📡","Pulse"],["actions","✅","Tasks"],["studio","🎨","Studio"],["funding","💰","Funding"],["tokens","🔢","Tokens"],["workspace","✳️","AI Workspace"],["liveboard","🗣️","Live Boardroom"],["account","👤","My Account"],["admin","🛡️","Admin Console"]].filter(([v])=>v!=="tokens"||me.role==="super_admin").filter(([v])=>v!=="jarvis"||hasJarvisAccess).filter(([v])=>v!=="admin"||isAdmin).filter(([v])=>v!=="ledger"||adminConfig.ledgerEnabled).filter(([v])=>v!=="dispatch"||adminConfig.dispatchEnabled).filter(([v])=>v!=="actions"||adminConfig.actionsEnabled).map(([v,ic,lb])=>{
                // WAS: modules the plan did not include were REMOVED from the
                // menu entirely, so a user had no idea they existed or that
                // upgrading would unlock them. A customer who cannot see what
                // they are missing has no reason to pay more.
                // They are now shown, dimmed, with the plan that unlocks them.
                const _ok=canUse(v as string);
                const _need=(!_ok&&ent?.plan_upgrade_hint)||"a higher plan";
                return (
                <button key={v} onClick={()=>{setView(v);setShowModules(false);}}
                  title={_ok?"":"Not included in your plan"}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 11px",background:view===v?"var(--oiq-accent)":"none",border:"none",borderRadius:6,cursor:"pointer",fontFamily:"inherit",marginBottom:2,transition:"background 0.12s",opacity:_ok?1:0.45}}>
                  <span style={{fontSize:15,width:22,textAlign:"center"}}>{_ok?ic:"\uD83D\uDD12"}</span>
                  <span style={{flex:1,textAlign:"left"}}>
                    <span style={{display:"block",fontSize:11.5,fontWeight:600,color:view===v?"var(--oiq-accentText)":"var(--oiq-sbText)"}}>{lb}</span>
                    {!_ok&&<span style={{display:"block",fontSize:8,color:"#F59E0B",fontWeight:700,marginTop:1}}>Upgrade to unlock</span>}
                  </span>
                </button>);})}
            </div>
          )}
        </div>

        {/* ── STAGE PANEL — 3×2 grid ── */}
        <div style={{margin:"0 10px 12px",background:"var(--oiq-sbBorder)",border:"1px solid var(--sb-bdr)",borderRadius:9,padding:11,flexShrink:0}}>
          <div style={{fontSize:9,fontWeight:800,color:"var(--oiq-accent)",letterSpacing:".12em",textTransform:"uppercase",marginBottom:9,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>Nerve Center · {co.name||BRAND}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:5}}>
            {STAGES.map(st=>{const on=co.stage===st.id;return(
              <button key={st.id} onClick={()=>{const n={...co,stage:st.id};setCo(n);sv("cos-co",n);}} title={st.l}
                style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,padding:"7px 3px",borderRadius:6,cursor:"pointer",overflow:"hidden",fontFamily:"inherit",
                  border:"1px solid "+(on?"var(--oiq-accent)":"transparent"),
                  background:on?"var(--oiq-accent)":"var(--sb-bg)",
                  color:on?"var(--oiq-accentText)":"var(--oiq-sbDim)",fontWeight:on?700:600}}>
                <span style={{fontSize:13,lineHeight:1}}>{st.ic}</span>
                <span style={{fontSize:9,lineHeight:1.1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:"100%"}}>{st.l}</span>
              </button>);})}
          </div>
        </div>

        {/* ── EXECUTIVE ROSTER ── */}
        <div style={{flex:1,overflowY:"auto",padding:"0 10px 10px"}}>
          <button onClick={()=>setShowExecs(v=>!v)}
            style={{width:"100%",display:"flex",alignItems:"center",gap:7,padding:"8px 4px",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit"}}>
            <span style={{fontSize:9,fontWeight:800,color:"var(--oiq-sbDim)",letterSpacing:".14em",textTransform:"uppercase"}}>Executives</span>
            <span style={{marginLeft:"auto",fontSize:9,color:"var(--oiq-sbDim)",transition:"transform .15s",transform:showExecs?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
          </button>
          {showExecs&&DEPTS.map(dept=>(
            <div key={dept.id} style={{marginBottom:2}}>
              <button onClick={()=>{const n={...expD,[dept.id]:!expD[dept.id]};setExpD(n);sv("cos-dp",n);}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:7,padding:"7px 8px",background:"none",border:"none",borderRadius:6,cursor:"pointer",fontFamily:"inherit"}}>
                <span style={{fontSize:7,color:"var(--oiq-sbDim)",transition:"transform .15s",transform:expD[dept.id]?"rotate(90deg)":"rotate(0)"}}>▶</span>
                <span style={{fontSize:10,fontWeight:700,color:"var(--oiq-sbText)",letterSpacing:".04em"}}>{dept.l}</span>
                <span style={{marginLeft:"auto",fontSize:9,fontWeight:700,color:"var(--oiq-sbDim)"}}>{dept.roles.length}</span>
              </button>
              {expD[dept.id]&&dept.roles.map(r=>(
                <button key={r.id} onClick={()=>{setSelRole(r.id);setView("chat");setError(null);}}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:9,padding:"7px 8px 7px 20px",borderRadius:6,cursor:"pointer",textAlign:"left",fontFamily:"inherit",border:"none",
                    background:selRole===r.id?"var(--oiq-accent)":"none"}}>
                  <span style={{fontSize:12,flexShrink:0}}>{r.ic}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:10.5,fontWeight:600,color:selRole===r.id?"var(--oiq-accentText)":"var(--oiq-sbText)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.t}</div>
                    <div style={{fontSize:8.5,color:selRole===r.id?"var(--oiq-accentText)":"var(--oiq-sbDim)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",opacity:.85}}>{r.d}</div>
                  </div>
                  {chats[r.id]?.length>0&&<span style={{width:4,height:4,borderRadius:"50%",background:"var(--oiq-accent)",flexShrink:0}}/>}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* ── FOOTER — identity, settings, sign out ── */}
        <div style={{borderTop:"1px solid var(--sb-bdr)",padding:"11px 14px",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11.5,fontWeight:600,color:"var(--oiq-sbText)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{me.email||"Signed in"}</div>
            <div style={{fontSize:9.5,color:"var(--oiq-sbDim)",marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {(me.role==="super_admin"?"Super Admin":me.role==="admin"?"Admin":"User")}{co.location?" · "+co.location:""}
            </div>
          </div>
          {/* THE REAL FIX: this sidebar footer - "Anubhav Srivastava · Super
              Admin · Gurgaon" - is the control cluster visible in every
              screenshot shared throughout this entire engagement. The share
              button was added to a DIFFERENT, top-right header row, which
              this codebase apparently renders as a second, separate toolbar.
              Added here too, so it is visible exactly where attention
              actually is, not just where I assumed it would be. */}
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowShareSB(v=>!v)} title="Share on social media" style={{background:"none",border:"none",cursor:"pointer",fontSize:14,padding:2,color:"var(--oiq-sbDim)"}}>🔗</button>
            {showShareSB && (
              <div style={{position:"absolute",bottom:24,left:0,zIndex:200,background:"#0A0E1A",border:"1px solid #1a2030",borderRadius:10,padding:10,boxShadow:"0 8px 24px rgba(0,0,0,0.4)",whiteSpace:"nowrap"}}>
                <ShareButtons message={"Check out " + (co?.name ? co.name + "'s" : "my") + " work on OrchestrIQ"} />
              </div>
            )}
          </div>
          <button onClick={()=>{setShowSettings(true);setSTab("api");}} title="Settings" style={{background:"none",border:"none",cursor:"pointer",fontSize:14,padding:2,color:"var(--oiq-sbDim)"}}>⚙</button>
          <button onClick={()=>setShowSignOutConfirm(true)} title="Sign Out" style={{background:"none",border:"none",cursor:"pointer",fontSize:14,padding:2,color:"#EF4444"}}>⎋</button>
        </div>
      </div>
        {/* ── MODULE DROPDOWN TRIGGER ── */}
        {/* ── MAIN PANEL ── */}
      <div id="oiq-main" role="main" tabIndex={-1} style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* NERVE CENTER */}
        {view==="nerve"&&(
          <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
            <div className="oiq-nerve-tabs" style={{display:"flex",gap:3,padding:"8px 14px",borderBottom:"1px solid #14192a",background:"#0c1120"}}>
              {[["boardroom","🏛️","AI Boardroom","#14B8A6"],["timemachine","⏳","Time Machine","#8B5CF6"],["autopilot","🤖","Autopilot","#F59E0B"]].map(([id,ic,lb,c])=>(
                <button key={id} className="oiq-nerve-btn" onClick={()=>setNTab(id)} style={{...S.nrvTab,...(nTab===id?{background:c+"10",color:c,borderColor:c+"30"}:{})}}><span style={{fontSize:15}}>{ic}</span><span style={{fontSize:10,fontWeight:700}}>{lb}</span></button>
              ))}
            </div>
            <JourneyShell nTab={nTab} setNTab={setNTab} brCur={brCur} brRun={brRun} tmSims={tmSims} apPlans={apPlans} explainLevel={explainLevel} setExplainLevel={setExplainLevel}
              exportDeps={{company:co.name||"",location:co.location||"",actuals:journeyActuals,exportVerbatimPDF:(title:string,md:string)=>generatePDFv2("detailed",title,md,co,cur),quickExport,dlFile,cp,showToast}}/>
            <div style={{flex:1,overflowY:"auto",padding:"12px 16px"}}>
              {/* BOARDROOM */}
              {nTab==="boardroom"&&(
  <BoardroomView
    exportVerbatimPDF={(title:string,md:string)=>generatePDFv2("detailed",title,md,co,cur)}
    researchPriorityGaps={researchPriorityGaps} brFreshResearch={brFreshResearch} setBrFreshResearch={setBrFreshResearch}
    saveCockpit={saveCockpit} addCockpitActions={addCockpitActions} openInTimeMachine={openInTimeMachine}
    ledgerEntries={ledgerEntries} customAccounts={customAccounts} explainLevel={explainLevel} startBoardroom={startBoardroom} researchProjectReport={researchProjectReport}
    brQ={brQ} setBrQ={setBrQ}
    brAg={brAg} setBrAg={setBrAg}
    brCur={brCur} brRun={brRun} brPh={brPh}
    brSessions={brSessions} setBrSessions={setBrSessions}
    brShowHistory={brShowHistory} setBrShowHistory={setBrShowHistory}
    brFollowUp={brFollowUp} setBrFollowUp={setBrFollowUp}
    drillRole={drillRole} setDrillRole={setDrillRole}
    drillQ={drillQ} setDrillQ={setDrillQ}
    drillRun={drillRun} brEnd={brEnd}
    runBR={runBR} runBRContinue={runBRContinue} runDrill={runDrill}
    cancelBR={()=>{cancelRef.current.br=true;}}
    followUpExecIds={followUpExecIds} setFollowUpExecIds={setFollowUpExecIds}
    followUpSuggestions={followUpSuggestions} setFollowUpSuggestions={setFollowUpSuggestions}
    suggestFollowUpExecs={suggestFollowUpExecs}
    dlFile={dlFile} cp={cp}
    quickExport={quickExport}
    extractActionItems={extractActionItems}
    extracting={extracting}
    showToast={showToast} sv={sv} setBrCur={setBrCur}
    CS={CS} co={co} cur={cur}
    isDark={theme==="dark"||theme==="blue"||theme==="gray"}
    MicButton={MicButtonWithKey} vLang={vLang}
  />
)}

              {/* TIME MACHINE */}
              {nTab==="timemachine"&&(
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                    <div style={{fontSize:13,fontWeight:800,color:"#F1F5F9"}}>Business Time Machine</div>
                    {tmSessions.length>0&&<button onClick={()=>setTmShowHistory(s=>!s)} style={{...S.hBtn,...(tmShowHistory?{color:"#8B5CF6",borderColor:"#8B5CF644"}:{})}}>{tmShowHistory?"✕ Close":"🕓 Past Runs ("+tmSessions.length+")"}</button>}
                  </div>
                  <TimeMachineSimulator handoff={decisionHandoff} saved={tmSims} onSave={(runs:any[])=>{setTmSims(runs);try{sv("oiq-tm-sims",runs);}catch{}}} onSendToAutopilot={addCockpitActions}/>
                  <p style={{fontSize:10,color:"#5A6480",marginBottom:10}}>Two parallel 12-month futures · {co.location||"Set location"} · {cur.code} · 60s timeout</p>
                  {tmShowHistory&&(
                    <div style={{marginBottom:10,background:"#0c1120",border:"1px solid #1a2030",borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Saved Simulations</div>
                      {tmSessions.map(s=>(
                        <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"#131825",border:"1px solid #1a2030",borderRadius:6,marginBottom:5}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:11,fontWeight:600,color:"#F1F5F9",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.dec}</div>
                            <div style={{fontSize:8,color:"#5A6480",marginTop:2}}>{new Date(s.ts).toLocaleString()}</div>
                          </div>
                          <button onClick={()=>{setTmDec(s.dec);setTmRes(s.res);setTmResearchBrief(s.brief||"");sv("cos-tm-live",{dec:s.dec,res:s.res,brief:s.brief||""});setTmShowHistory(false);showToast("Simulation reopened","success");}} style={{...S.hBtn,color:"#8B5CF6",borderColor:"#8B5CF633",flexShrink:0}}>Reopen</button>
                          <button onClick={()=>{if(confirm("Delete this saved simulation?")){setTmSessions(prev=>{const ns=prev.filter(x=>x.id!==s.id);sv("cos-tm",ns);return ns;});}}} style={{...S.hBtn,color:"#EF4444",borderColor:"#EF444433",flexShrink:0}}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{display:"flex",gap:5,marginBottom:12}}>
                    <textarea style={{...S.inp,flex:1,minHeight:55}} value={tmDec} onChange={e=>setTmDec(e.target.value)} placeholder={"Describe the decision… e.g. Hire 5 engineers and launch in "+(co.location||"Delhi")+" Q3 with "+cur.sym+"50L budget"} disabled={tmRun}/>
                    <div style={{display:"flex",flexDirection:"column",gap:4,alignSelf:"flex-end"}}>
                      <div style={{display:"flex",gap:3}}><LangPick value={vLang} onChange={vl=>{setVLang(vl);sv("cos-vl",vl);}}/><MicButton lang={vLang} onResult={t=>setTmDec(prev=>(prev?prev+" ":"")+t)} disabled={tmRun} openaiKey={providerKey(keys,"openai")||undefined}/></div>
                      <div style={{display:"flex",gap:3}}>
                        {tmRun&&<button onClick={()=>{cancelRef.current.tm=true;}} style={S.cancelBtn}>Cancel</button>}
                        <button onClick={runTM} disabled={tmRun||!tmDec.trim()} style={{...S.pBtn,width:"auto",padding:"7px 12px",marginTop:0,fontSize:11,background:"#8B5CF6",opacity:tmRun||!tmDec.trim()?0.3:1}}>{tmRun?"Simulating…":"Simulate"}</button>
                      </div>
                    </div>
                  </div>
                  {tmRun&&tmPh&&<div style={{fontSize:10,color:"#8B5CF6",marginBottom:8,display:"flex",alignItems:"center",gap:5}}><span style={{width:5,height:5,borderRadius:"50%",background:"#8B5CF6",display:"inline-block",animation:"pulse 1s infinite"}}/> {tmPh} (up to 100s)</div>}
                  <ResearchBriefCard brief={tmResearchBrief} accent="#8B5CF6" label="Research Brief - Time Machine"/>
                  {tmRes&&<div style={{animation:"fadeIn 0.3s"}}><div style={{display:"flex",justifyContent:"flex-end",marginBottom:4,gap:4,flexWrap:"wrap"}}><button onClick={()=>cp(tmRes)} style={S.hBtn}>Copy</button><ReadAloudButton text={tmRes} id="timemachine-result" /><button onClick={()=>quickExport("pdf","detailed","Time Machine — "+tmDec,tmRes)} style={S.hBtn}>📄 PDF</button><button onClick={()=>quickExport("pptx","strategy","Time Machine Simulation",tmRes)} style={S.hBtn}>📊 PPT</button><button onClick={()=>dlFile("TimeMachine-"+Date.now()+".md",tmDec+"\n\n"+tmRes,"text/markdown")} style={S.hBtn}>MD</button><button onClick={()=>extractActionItems("timemachine","Time Machine — \""+tmDec.slice(0,40)+"\"",tmRes)} disabled={extracting==="timemachine"} style={{...S.hBtn,color:"#14B8A6",borderColor:"#14B8A633"}}>{extracting==="timemachine"?"Extracting...":"✅ Extract Action Items"}</button></div><div style={{background:"#131825",borderRadius:8,padding:"14px 16px",border:"1px solid rgba(139,92,246,0.18)"}}><div style={{fontSize:11,lineHeight:1.7,color:"#A0AAC0"}}><Md text={tmRes} ac="#8B5CF6"/></div></div></div>}
                  {error&&nTab==="timemachine"&&<div style={S.errB}>⚠️ {error}<div style={{display:"flex",gap:4}}><button onClick={runTM} style={S.retBtn}>Retry</button><button onClick={()=>setError(null)} style={{...S.retBtn,background:"#3A4060"}}>Dismiss</button></div></div>}
                </div>
              )}

              {/* AUTOPILOT */}
              {nTab==="autopilot"&&(
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
                    <div style={{fontSize:13,fontWeight:800,color:"var(--oiq-ink)"}}>Decision Autopilot</div>
                    {apSessions.length>0&&<button onClick={()=>setApShowHistory(s=>!s)} style={{...S.hBtn,...(apShowHistory?{color:"#F59E0B",borderColor:"#F59E0B44"}:{})}}>{apShowHistory?"✕ Hide":"🕘 History ("+apSessions.length+")"}</button>}
                  </div>
                  <OperatingPlanner handoff={decisionHandoff} onAddActions={addCockpitActions} onSave={(r:any)=>{const n=[...apPlans,r].slice(-10);setApPlans(n);try{sv("oiq-autopilot-plans",n);}catch{}}}/>
                  {apShowHistory&&(
                    <div style={{marginBottom:10,background:"var(--oiq-surface2)",border:"1px solid var(--oiq-border)",borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:9,fontWeight:700,color:"var(--oiq-muted)",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Saved Scans</div>
                      {apSessions.map((s:any)=>(
                        <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:"var(--oiq-surface)",border:"1px solid var(--oiq-border)",borderRadius:6,marginBottom:5}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:11,fontWeight:600,color:"var(--oiq-ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.company||"Scan"} · {s.stage||"—"}</div>
                            <div style={{fontSize:8,color:"var(--oiq-muted)",marginTop:2}}>{new Date(s.ts).toLocaleString()}</div>
                          </div>
                          <button onClick={()=>{setApRes(s.res);setApResearchBrief(s.brief||"");sv("cos-ap-live",{res:s.res,brief:s.brief||""});setApShowHistory(false);}} style={{...S.iBtn,fontSize:9,padding:"3px 9px"}}>Reopen</button>
                          <button onClick={()=>{if(confirm("Delete this saved scan?")){setApSessions(prev=>{const ns=prev.filter((x:any)=>x.id!==s.id);sv("cos-ap",ns);return ns;});}}} style={{...S.iBtn,fontSize:9,padding:"3px 7px",color:"#EF4444"}}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p style={{fontSize:10,color:"#5A6480",marginBottom:10}}>Surfaces decisions you should be making but aren't · {co.location||"Set location"} · {cur.code}</p>
                  <div style={{display:"flex",gap:5,marginBottom:14}}>
                    <button onClick={runAP} disabled={apRun} style={{...S.pBtn,width:"auto",padding:"10px 24px",marginTop:0,fontSize:13,background:"#F59E0B",opacity:apRun?0.4:1}}>{apRun?"Scanning…":"Run Decision Scan"}</button>
                    {apRun&&<button onClick={()=>{cancelRef.current.ap=true;}} style={{...S.cancelBtn,alignSelf:"flex-end",marginBottom:6}}>Cancel</button>}
                  </div>
                  {apRun&&apPh&&<div style={{fontSize:10,color:"#F59E0B",marginBottom:8,display:"flex",alignItems:"center",gap:5}}><span style={{width:5,height:5,borderRadius:"50%",background:"#F59E0B",display:"inline-block",animation:"pulse 1s infinite"}}/> {apPh} (up to 100s)</div>}
                  <ResearchBriefCard brief={apResearchBrief} accent="#F59E0B" label="Research Brief - Decision Autopilot"/>
                  {apRes&&<div style={{animation:"fadeIn 0.3s"}}><div style={{display:"flex",justifyContent:"flex-end",marginBottom:4,gap:4,flexWrap:"wrap"}}><button onClick={()=>cp(apRes)} style={S.hBtn}>Copy</button><ReadAloudButton text={apRes} id="autopilot-result" /><button onClick={()=>quickExport("pdf","executive","Decision Autopilot Scan",apRes)} style={S.hBtn}>📄 PDF</button><button onClick={()=>quickExport("pptx","briefing","Decision Autopilot",apRes)} style={S.hBtn}>📊 PPT</button><button onClick={()=>dlFile("Autopilot-"+Date.now()+".md",apRes,"text/markdown")} style={S.hBtn}>MD</button><button onClick={()=>extractActionItems("autopilot","Decision Autopilot Scan",apRes)} disabled={extracting==="autopilot"} style={{...S.hBtn,color:"#14B8A6",borderColor:"#14B8A633"}}>{extracting==="autopilot"?"Extracting...":"✅ Extract Action Items"}</button></div><div style={{background:"#131825",borderRadius:8,padding:"14px 16px",border:"1px solid rgba(245,158,11,0.18)"}}><div style={{fontSize:11,lineHeight:1.7,color:"#A0AAC0"}}><Md text={apRes} ac="#F59E0B"/></div></div></div>}
                  {error&&nTab==="autopilot"&&<div style={S.errB}>⚠️ {error}<div style={{display:"flex",gap:4}}><button onClick={runAP} style={S.retBtn}>Retry</button><button onClick={()=>setError(null)} style={{...S.retBtn,background:"#3A4060"}}>Dismiss</button></div></div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* WORKFLOW */}
        {view==="workflow"&&(
          <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
            <div style={{display:"flex",gap:3,padding:"8px 14px",borderBottom:"1px solid #14192a",background:"#0c1120",alignItems:"center",flexWrap:"wrap"}}>
              {[["new","New Task"],["project","⚡ Project Engine"+(projectPlanning?" ●":"")],["active","Active"+(wfActive?" ●":"")],["history","History ("+workflows.length+")"]].map(([id,lb])=>(
                <button key={id} onClick={()=>setWfView(id)} style={{padding:"5px 12px",borderRadius:5,fontSize:10,fontWeight:600,border:"1px solid "+(wfView===id?"#14B8A6":"#1a2030"),background:wfView===id?"rgba(20,184,166,0.08)":"transparent",color:wfView===id?"#14B8A6":"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{lb}</button>
              ))}
              <div style={{marginLeft:"auto",fontSize:9,color:"#3A4060"}}>Flow · v2 · 60s/level</div>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"14px 16px"}}>
              {wfView==="new"&&(
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#F1F5F9",marginBottom:4}}>Workflow Task Engine</div>
                  <p style={{fontSize:10,color:"#8892B0",marginBottom:12,lineHeight:1.6}}>Pick a category, customize which executives you want, then describe your task. You get the actual deliverable — not a discussion.</p>
                  <label style={S.lbl}>Step 1: Select Task Category</label>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:5,marginBottom:12}}>
                    {Object.entries(CHAINS).map(([key,ch])=>(
                      <button key={key} onClick={()=>{setWfCat(key);}} style={{display:"flex",alignItems:"flex-start",gap:7,padding:"9px 10px",borderRadius:7,border:"1px solid "+(wfCat===key?ch.color+"66":"#1a2030"),background:wfCat===key?ch.color+"08":"#0a0e1a",cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left"}}>
                        <span style={{fontSize:16,flexShrink:0}}>{ch.ic}</span>
                        <div><div style={{fontSize:10,fontWeight:700,color:wfCat===key?ch.color:"#A0AAC0"}}>{ch.label}</div><div style={{fontSize:8,color:"#3A4060",lineHeight:1.4,marginTop:1}}>{ch.desc}</div></div>
                      </button>
                    ))}
                  </div>
                  {wfCat&&(()=>{
                    const ch=CHAINS[wfCat];
                    const defaultIds=ch.chain;
                    const currentSelected=wfCustomChain.length?wfCustomChain:defaultIds;
                    const allOtherRoles=AR.filter(r=>!defaultIds.includes(r.id)&&r.id!=="pres_arch"&&EP[r.id]);
                    return(
                      <div style={{marginBottom:12}}>
                        <div style={{padding:"10px 12px",background:"#0a0e1a",borderRadius:7,border:"1px solid "+ch.color+"22",marginBottom:8}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                            <div style={{fontSize:9,fontWeight:700,color:ch.color,textTransform:"uppercase",letterSpacing:1}}>Step 2: Choose Your Executives</div>
                            <button onClick={()=>setWfCustomChain(defaultIds)} style={{...S.hBtn,fontSize:8,color:ch.color,borderColor:ch.color+"44"}}>Reset to Default</button>
                          </div>
                          <div style={{fontSize:8,color:"#5A6480",marginBottom:8,lineHeight:1.5}}>Toggle to include or exclude. Drag order doesn't matter — selected executives run sequentially top to bottom. You can also add executives from other departments below.</div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
                            {defaultIds.map((id)=>{
                              const r=AR.find(x=>x.id===id);if(!r)return null;
                              const sel=currentSelected.includes(id);
                              return(
                                <button key={id} onClick={()=>{const next=sel?currentSelected.filter(x=>x!==id):[...currentSelected,id];setWfCustomChain(next.length?next:currentSelected);}} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 9px",borderRadius:5,border:"1px solid "+(sel?r.dc+"66":"#1a2030"),background:sel?r.dc+"12":"#131825",cursor:"pointer",fontFamily:"Manrope,sans-serif",opacity:sel?1:0.45,transition:"all 0.15s"}}>
                                  <span style={{fontSize:11}}>{r.ic}</span>
                                  <span style={{fontSize:9,fontWeight:700,color:sel?r.dc:"#5A6480"}}>{r.t}</span>
                                  <span style={{fontSize:8,color:sel?"#10B981":"#3A4060",fontWeight:700}}>{sel?"✓":"+"}</span>
                                </button>
                              );
                            })}
                          </div>
                          {currentSelected.length>0&&(
                            <div style={{background:"#131825",borderRadius:5,padding:"7px 10px",border:"1px solid #1a2030"}}>
                              <div style={{fontSize:8,color:"#5A6480",marginBottom:5,textTransform:"uppercase",letterSpacing:0.8}}>Your Chain ({currentSelected.length} executives)</div>
                              <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:3}}>
                                {currentSelected.map((id,i)=>{const r=AR.find(x=>x.id===id);if(!r)return null;return(<span key={id} style={{display:"flex",alignItems:"center",gap:3}}><span style={{background:r.dc+"15",border:"1px solid "+r.dc+"33",borderRadius:4,padding:"3px 7px",fontSize:9,fontWeight:600,color:r.dc}}>{r.ic} {r.t}</span>{i<currentSelected.length-1&&<span style={{color:"#3A4060",fontSize:10}}>→</span>}</span>);})}
                              </div>
                            </div>
                          )}
                        </div>
                        <div style={{background:"#0a0e1a",borderRadius:7,border:"1px solid #1a2030",overflow:"hidden"}}>
                          <button onClick={()=>setWfShowExtra(s=>!s)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"none",border:"none",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>
                            <span style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8}}>+ Add Executives from Other Departments</span>
                            <span style={{fontSize:9,color:"#3A4060"}}>{wfShowExtra?"▲":"▼"}</span>
                          </button>
                          {wfShowExtra&&(
                            <div style={{padding:"0 12px 12px",maxHeight:260,overflowY:"auto"}}>
                              {DEPTS.filter(d=>d.id!=="presentation").map(dept=>{
                                const extra=dept.roles.filter(r=>!defaultIds.includes(r.id));
                                if(!extra.length)return null;
                                return(
                                  <div key={dept.id} style={{marginBottom:10}}>
                                    <div style={{fontSize:8,fontWeight:700,color:dept.c,textTransform:"uppercase",letterSpacing:0.8,marginBottom:5}}>{dept.l}</div>
                                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                                      {extra.map(r=>{
                                        const sel=currentSelected.includes(r.id);
                                        return(
                                          <button key={r.id} onClick={()=>{const next=sel?currentSelected.filter(x=>x!==r.id):[...currentSelected,r.id];setWfCustomChain(next);}} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 8px",borderRadius:5,border:"1px solid "+(sel?r.dc+"66":"#1a2030"),background:sel?r.dc+"12":"#131825",cursor:"pointer",fontFamily:"Manrope,sans-serif",transition:"all 0.15s"}}>
                                            <span style={{fontSize:10}}>{r.ic}</span>
                                            <span style={{fontSize:9,fontWeight:600,color:sel?r.dc:"#8892B0"}}>{r.t}</span>
                                            <span style={{fontSize:7,color:dept.c,opacity:0.7}}>{dept.l}</span>
                                            <span style={{fontSize:8,color:sel?"#10B981":"#3A4060",fontWeight:700}}>{sel?"✓":"+"}</span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  <label style={S.lbl}>Step 3: Describe the Task</label>
                  <div style={{display:"flex",gap:4,alignItems:"flex-start",marginBottom:12}}>
                    <textarea style={{...S.inp,flex:1,minHeight:80,resize:"vertical"}} value={wfTask} onChange={e=>setWfTask(e.target.value)} placeholder="e.g. Prepare Q1 FY2026 P&L statement, highlight variances greater than 10% from budget, and recommend 3 cost reduction measures for board review" disabled={wfRunning}/>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}><LangPick value={vLang} onChange={vl=>{setVLang(vl);sv("cos-vl",vl);}}/><MicButton lang={vLang} onResult={t=>setWfTask(prev=>(prev?prev+" ":"")+t)} disabled={wfRunning} openaiKey={providerKey(keys,"openai")||undefined}/></div>
                  </div>
                  {!wfPreflightActive&&(<div style={{display:"flex",gap:4}}>
                    <button onClick={runPreflight} disabled={wfRunning||wfPreflightLoading||!wfTask.trim()||!wfCat} style={{...S.pBtn,background:"linear-gradient(135deg,#14B8A6,#3B82F6)",opacity:wfRunning||wfPreflightLoading||!wfTask.trim()?0.3:1,marginTop:0,flex:1}}>{wfPreflightLoading?"Preparing questions…":wfRunning?"Chain Running…":"Start Workflow Chain"}</button>
                    {wfRunning&&<button onClick={()=>cancelRef.current.wf=true} style={{...S.cancelBtn,alignSelf:"flex-end",marginBottom:0}}>Cancel</button>}
                  </div>)}
                  {wfRunning&&wfPhase&&<div style={{fontSize:10,color:"#14B8A6",marginTop:8,display:"flex",alignItems:"center",gap:5}}><span style={{width:5,height:5,borderRadius:"50%",background:"#14B8A6",display:"inline-block",animation:"pulse 1s infinite"}}/>{wfPhase}</div>}
                  {wfPreflightActive&&wfPreflight&&!wfRunning&&(
                    <div style={{marginTop:12,background:"linear-gradient(135deg,rgba(20,184,166,0.06),rgba(59,130,246,0.04))",border:"1px solid rgba(20,184,166,0.25)",borderRadius:10,padding:"16px 16px 12px"}}>
                      <div style={{fontSize:12,fontWeight:800,color:"#F1F5F9",marginBottom:4}}>Quick questions before we begin</div>
                      <p style={{fontSize:10,color:"#8892B0",marginBottom:14,lineHeight:1.6}}>Answer these so the team produces your actual deliverable — not a generic report. Skip any you want.</p>
                      {wfPreflight.questions.map((q,i)=>(
                        <div key={i} style={{marginBottom:12,background:"#131825",borderRadius:8,padding:"10px 12px",border:"1px solid #1a2030"}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                            <span style={{fontSize:16}}>{q.personaIc||"👔"}</span>
                            <span style={{fontSize:10,fontWeight:700,color:"#14B8A6"}}>{q.persona}</span>
                          </div>
                          <div style={{fontSize:11,color:"#F1F5F9",marginBottom:6,lineHeight:1.5}}>{q.q}</div>
                          <div style={{display:"flex",gap:4,alignItems:"flex-end"}}>
                            <textarea style={{...S.inp,flex:1,minHeight:36,resize:"vertical",fontSize:11}} value={wfPreflight.answers[i]||""} onChange={e=>{const na=[...wfPreflight.answers];na[i]=e.target.value;setWfPreflight({...wfPreflight,answers:na});}} placeholder={q.placeholder||"Your answer…"}/>
                            <MicButton lang={vLang} onResult={t=>{const na=[...wfPreflight.answers];na[i]=(na[i]?na[i]+" ":"")+t;setWfPreflight({...wfPreflight,answers:na});}} disabled={false} openaiKey={providerKey(keys,"openai")||undefined}/>
                          </div>
                        </div>
                      ))}
                      <div style={{display:"flex",gap:6,marginTop:4}}>
                        <button onClick={()=>{const ch=CHAINS[wfCat];const finalChain=wfCustomChain.length?wfCustomChain:ch.chain;setWfPreflightActive(false);runWorkflow(finalChain,wfPreflight);}} style={{...S.pBtn,marginTop:0,flex:1,background:"linear-gradient(135deg,#14B8A6,#3B82F6)"}}>Build My Deliverable</button>
                        <button onClick={()=>{const ch=CHAINS[wfCat];const finalChain=wfCustomChain.length?wfCustomChain:ch.chain;setWfPreflightActive(false);runWorkflow(finalChain,null);}} style={{...S.hBtn,padding:"10px 14px",fontSize:10}}>Skip & Run</button>
                        <button onClick={()=>{setWfPreflightActive(false);setWfPreflight(null);}} style={{...S.hBtn,padding:"10px 14px",fontSize:10}}>← Back</button>
                      </div>
                    </div>
                  )}
                  {wfPauseMsg&&(
  <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:8,padding:"10px 14px",marginBottom:10,fontSize:11,color:"#F59E0B",display:"flex",alignItems:"center",gap:8}}>
    <span style={{fontSize:14,flexShrink:0}}>⏸</span>
    <span style={{flex:1,lineHeight:1.6}}>{wfPauseMsg}</span>
  </div>
)}
{wfResumeData&&!wfRunning&&(
  <div style={{background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:8,padding:"12px 14px",marginBottom:12}}>
    <div style={{fontSize:12,fontWeight:700,color:"#F59E0B",marginBottom:3}}>⏸ Paused Workflow Found</div>
    <div style={{fontSize:11,color:"#A0AAC0",marginBottom:2}}>"{wfResumeData.task.slice(0,60)}{wfResumeData.task.length>60?"…":""}"</div>
    <div style={{fontSize:9,color:"#5A6480",marginBottom:10}}>{wfResumeData.chainLabel} · Completed {wfResumeData.completedLevels} of {CHAINS[wfResumeData.category]?.chain.length||0} levels · Saved {new Date(wfResumeData.savedAt).toLocaleTimeString()}</div>
    <div style={{display:"flex",gap:6}}>
      <button onClick={()=>runWorkflow(wfResumeData)} style={{...S.pBtn,marginTop:0,flex:1,background:"#F59E0B",color:"#0a0e1a",fontSize:11}}>▶ Resume from Level {wfResumeData.completedLevels+1}</button>
      <button onClick={()=>{clearResumeState();setWfResumeData(null);}} style={{...S.hBtn,padding:"8px 12px",fontSize:10}}>Discard</button>
    </div>
  </div>
)}
                  {error&&<div style={{...S.errB,marginTop:8}}>⚠️ {error}<button onClick={()=>setError(null)} style={{...S.retBtn,background:"#3A4060"}}>Dismiss</button></div>}
                </div>
              )}
              
              {wfView==="project"&&(
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#F1F5F9",marginBottom:2}}>⚡ Project Engine</div>
                  <p style={{fontSize:10,color:"#8892B0",marginBottom:14,lineHeight:1.6}}>Describe a complex business objective. The engine decomposes it into an Execution Plan with modules and deliverables — you approve before anything runs.</p>

                  {/* ── EXECUTION DASHBOARD ── */}
                  {projectDashboardOpen&&projectExecution&&(
                    <div style={{animation:"fadeIn 0.3s"}}>
                      {/* Header */}
                      <div style={{marginBottom:6}}>
                        <button onClick={()=>{setProjectDashboardOpen(false);setProjectExecution(null);setProjectReviewMode(false);setProjectExcluded({});}} style={{...S.hBtn,fontSize:9,color:"#14B8A6",borderColor:"#14B8A633"}}>← Back to Projects</button>
                      </div>
                      <div style={{background:"#131825",border:"1px solid #1a2030",borderRadius:8,padding:"12px 14px",marginBottom:10}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                          <span style={{fontSize:16}}>⚡</span>
                          <div style={{flex:1}}><div style={{fontSize:12,fontWeight:800,color:"#F1F5F9"}}>{projectExecution.name}</div><div style={{fontSize:9,color:"#5A6480"}}>{projectExecution.modules?.length||0} modules · {(projectExecution.modules||[]).reduce((a,m)=>a+(m.deliverables?.length||0),0)} deliverables</div></div>
                          <span style={{fontSize:8,padding:"3px 8px",borderRadius:8,fontWeight:700,background:projectExecution.status==="complete"?"rgba(16,185,129,0.12)":projectExecution.status==="partial"?"rgba(245,158,11,0.1)":projectExecution.status==="qa"?"rgba(59,130,246,0.1)":"rgba(20,184,166,0.08)",color:projectExecution.status==="complete"?"#10B981":projectExecution.status==="partial"?"#F59E0B":projectExecution.status==="qa"?"#3B82F6":"#14B8A6"}}>{projectExecution.status==="qa"?"🔍 QA IN PROGRESS":projectExecution.status?.toUpperCase()}</span>
                        </div>
                        {/* Progress bar */}
                        {(()=>{
                          const allD=(projectExecution.modules||[]).flatMap(m=>m.deliverables||[]);
                          const done=allD.filter(d=>d.status==="complete").length;
                          const total=allD.length||1;
                          const pct=Math.round(done/total*100);
                          return(
                            <div>
                              <div style={{display:"flex",justifyContent:"space-between",fontSize:8,color:"#5A6480",marginBottom:3}}><span>{done}/{total} deliverables</span><span>{pct}%</span></div>
                              <div style={{background:"#1a2030",borderRadius:20,height:4}}><div style={{background:"linear-gradient(90deg,#14B8A6,#6366F1)",height:"100%",borderRadius:20,width:pct+"%",transition:"width 0.5s"}}/></div>
                            </div>
                          );
                        })()}
                        {projectExecPhase&&(
                          <div style={{fontSize:9,color:projectQARunning?"#3B82F6":"#14B8A6",marginTop:6,display:"flex",alignItems:"center",gap:4}}>
                            <span style={{width:5,height:5,borderRadius:"50%",background:projectQARunning?"#3B82F6":"#14B8A6",display:"inline-block",animation:"pulse 1s infinite",flexShrink:0}}/>{projectExecPhase}
                          </div>
                        )}
                        {projectExecuting&&<button onClick={()=>{setProjectExecCancel(true);projectExecCancelRef.current=true;setProjectExecPhase("\u23f9 Stopping \u2014 finishing current step, completed deliverables will be kept...");}} style={{...S.cancelBtn,marginTop:8,fontSize:9}}>⏹ Stop Execution</button>}
                      </div>
                      {/* Module cards */}
                      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
                        {(projectExecution.modules||[]).map((mod,mi)=>{
                          const modDels=mod.deliverables||[];
                          const modDone=modDels.filter(d=>d.status==="complete").length;
                          const modFailed=modDels.filter(d=>d.status==="failed").length;
                          const statusColor=modDone===modDels.length?"#10B981":modFailed>0?"#EF4444":"#14B8A6";
                          return(
                            <div key={mi} style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:7,padding:"10px 12px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                                <span style={{fontSize:14}}>{mod.icon||"⚙️"}</span>
                                <div style={{flex:1}}><div style={{fontSize:10,fontWeight:700,color:"#F1F5F9"}}>{mod.name}</div><div style={{fontSize:8,color:"#5A6480"}}>{modDone}/{modDels.length} complete</div></div>
                                <span style={{fontSize:8,color:statusColor,fontWeight:700}}>{modDone===modDels.length?"✓ Done":modFailed>0?modFailed+" failed":"Running"}</span>
                              </div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                                {modDels.map((del,di)=>{
                                  const sc={queued:"#3A4060",generating:"#14B8A6",waiting:"#F59E0B",retrying:"#8B5CF6",qa:"#3B82F6",complete:"#10B981",failed:"#EF4444"}[del.status]||"#3A4060";
                                  return(
                                    <div key={di} style={{background:sc+"15",border:"1px solid "+sc+"44",borderRadius:4,padding:"3px 7px",fontSize:8}}>
                                      <div style={{color:sc,fontWeight:700,marginBottom:1}}>{del.status==="generating"?"⟳ ":del.status==="complete"?"✓ ":del.status==="failed"?"✗ ":""}{del.name}</div>
                                      {del.status==="complete"&&<div style={{color:"#5A6480"}}>{del.outputFormat} · {del.confidenceScore}%</div>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Completed deliverables — view content */}
                      {(()=>{
                        const completed=(projectExecution.modules||[]).flatMap(m=>(m.deliverables||[]).filter(d=>d.status==="complete").map(d=>({...d,_mod:m.name})));
                        if(!completed.length)return null;
                        return(
                          <div style={{marginBottom:10}}>
                            <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:6}}>Completed Deliverables</div>
                            {completed.map((del,ci)=>(
                              <div key={ci} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:6,padding:"9px 11px",marginBottom:5}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:del.rawContent?4:0}}>
                                  <span style={{fontSize:9,fontWeight:700,color:"#10B981"}}>✓</span>
                                  <div style={{flex:1}}><div style={{fontSize:10,fontWeight:600,color:"#F1F5F9"}}>{del.name}</div><div style={{fontSize:8,color:"#5A6480"}}>{del._mod} · {del.outputFormat} · {del.confidenceScore}% confidence · {del.verificationStatus}</div></div>
                                  <button onClick={()=>cp(del.rawContent||"")} style={{...S.hBtn,fontSize:8}}>Copy</button>
                                  <button onClick={async()=>{
                                    const nm=del.name.replace(/\s+/g,"-");
                                    const lsC=(()=>{try{return localStorage.getItem("cos-rc-"+del.id)||"";}catch{return "";}})();
                                    const cnt=rawContentStore.current[del.id]||lsC||del.rawContent||"";
                                    const fmt=(del.outputFormat||"md").toLowerCase();
                                    if(fmt==="pdf"){
                                      // Publication engine first; existing inline renderer stays as fallback.
                                      let _pdfOk=false;
                                      try{
                                        const _pubCtx=[projectExecution?.context?.company?.name||co.name||"",projectExecution?.context?.company?.industry||co.industry||"",projectExecution?.context?.company?.stage||co.stage||""].filter(Boolean).join(" | ");
                                        let _w=false;
                                        const _bee=new BusinessExecutionEngine(
                                          async(sys:any,msgs:any,maxT?:any,_es?:any,tt?:any)=>(await callMulti({...keys,...(EFF_CLAUDE?.trim()?{claude:EFF_CLAUDE}:{}),...(EFF_GEMINI?.trim()?{gemini:EFF_GEMINI}:{}),...(EFF_GROQ?.trim()?{groq:EFF_GROQ}:{})},defP,sys,msgs,maxT||6000,false,tt||"general")).primary,
                                          ensureXLSX,ensurePptx,ensureJsPDF,
                                          (_fname:any,buf:any)=>{dlFile(nm+".pdf",buf,"application/pdf");_w=true;},
                                          stripMd);
                                        const _spec:DeliverableSpec={type:"pdf",title:del.name,purpose:del.name,audience:_delAudience(del),qualityStandard:"cfo_model",priority:"primary"};
                                        const _plan:ExecutionPlan={objectiveRestated:del.name,domain:"strategy",persona:"Senior Consultant",audience:_delAudience(del),qualityStandard:"cfo_model",decisionContext:del.name,deliverables:[_spec],missingInfo:[],executionOrder:[del.name],validationCriteria:[]};
                                        await _bee.generatePDF(_plan,_spec,_pubCtx,cnt,()=>{});
                                        _pdfOk=_w;
                                      }catch(_e:any){recordEngineDowngrade(del.name,"pdf",_e);}
                                      if(!_pdfOk)try{
                                      const jsPDF=await ensureJsPDF();const doc=new jsPDF({unit:"pt",format:"a4"});
                                      const W=doc.internal.pageSize.getWidth(),H=doc.internal.pageSize.getHeight(),M=48;let y=M;
                                      doc.setFillColor(20,184,166);doc.rect(0,0,W,72,"F");
                                      doc.setTextColor(255,255,255);doc.setFont("helvetica","bold");doc.setFontSize(15);
                                      doc.text(del.name,M,44,{maxWidth:W-2*M});
                                      doc.setFontSize(9);doc.setFont("helvetica","normal");
                                      doc.text((projectExecution?.context?.company?.name||"")+" · "+new Date().toLocaleDateString(),M,62);
                                      y=90;parseSections(cnt).forEach(sec=>{
                                        if(y>H-M){doc.addPage();y=M;}
                                        doc.setFont("helvetica","bold");doc.setFontSize(12);doc.setTextColor(20,184,166);
                                        doc.splitTextToSize(sec.title,W-2*M).forEach(l=>{if(y>H-M){doc.addPage();y=M;}doc.text(l,M,y);y+=15;});
                                        doc.setFont("helvetica","normal");doc.setFontSize(10);doc.setTextColor(45,45,45);
                                        sec.lines.forEach(ln=>{const t=stripMd(ln);if(!t.trim())return;if(y>H-M){doc.addPage();y=M;}
                                          doc.splitTextToSize(t,W-2*M).forEach(w=>{if(y>H-M){doc.addPage();y=M;}doc.text(w,M,y);y+=13;});});
                                        y+=5;});
                                      const pg=doc.internal.getNumberOfPages();
                                      for(let p=1;p<=pg;p++){doc.setPage(p);doc.setFontSize(7);doc.setTextColor(150,150,150);doc.text("Page "+p+" of "+pg,M,H-18);}
                                      doc.save(nm+".pdf");
                                    }catch(e){showToast("PDF: "+e.message,"error");}}
                                    else if(fmt==="xlsx"){
                                      // FIX: this path never called the real engine — it did a naive
                                      // pipe-character split with zero formulas, zero styling, zero
                                      // charts, none of the Excel Intelligence Engine work. It has been
                                      // producing a worse file than the main package this whole time.
                                      // Same publication-engine-first pattern as the pdf/pptx branches
                                      // right next to it — inline is now only the last-resort fallback.
                                      let _xlsxOk=false;
                                      try{
                                        const _pubCtx=[projectExecution?.context?.company?.name||co.name||"",projectExecution?.context?.company?.industry||co.industry||"",projectExecution?.context?.company?.stage||co.stage||""].filter(Boolean).join(" | ");
                                        let _w=false;
                                        const _bee=new BusinessExecutionEngine(
                                          async(sys:any,msgs:any,maxT?:any,_es?:any,tt?:any)=>(await callMulti({...keys,...(EFF_CLAUDE?.trim()?{claude:EFF_CLAUDE}:{}),...(EFF_GEMINI?.trim()?{gemini:EFF_GEMINI}:{}),...(EFF_GROQ?.trim()?{groq:EFF_GROQ}:{})},defP,sys,msgs,maxT||6000,false,tt||"general")).primary,
                                          ensureXLSX,ensurePptx,ensureJsPDF,
                                          (_fname:any,buf:any)=>{dlFile(nm+".xlsx",buf,"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");_w=true;},
                                          stripMd);
                                        const _spec:DeliverableSpec={type:"xlsx",title:del.name,purpose:del.description||del.name,audience:_delAudience(del),qualityStandard:"cfo_model",priority:"primary"};
                                        const _plan:ExecutionPlan={objectiveRestated:del.description||del.name,domain:(["finance","audit","strategy","marketing","operations","hr","legal","technology","sales","risk"] as const).find(d=>(del.capabilityType||"").toLowerCase().includes(d)||del.name.toLowerCase().includes(d))||"finance",persona:"Senior FP&A Director",audience:_delAudience(del),qualityStandard:"cfo_model",decisionContext:del.description||del.name,deliverables:[_spec],missingInfo:[],executionOrder:[del.name],validationCriteria:[]};
                                        const _res:any=await _bee.generateExcel(_plan,_spec,_pubCtx,cnt,projectExecution?.context?.company?.currency||"INR",projectExecution?.context?.company?.currencySymbol||"₹",()=>{});
                                        _xlsxOk=_w;
                                        if(!_w&&_res?.error)throw new Error(_res.error);
                                      }catch(_e:any){recordEngineDowngrade(del.name,"xlsx",_e);}
                                      if(!_xlsxOk)try{
                                      const XLSX=await ensureXLSX();const wb=XLSX.utils.book_new();
                                      const rows=cnt.split("\n").filter(Boolean).map(r=>r.split("|").filter((c,ii,a)=>ii>0&&ii<a.length-1).map(c=>c.trim())).filter(r=>r.length>1&&!r.every(c=>c.match(/^[-:]+$/)));
                                      const ws=rows.length>0?XLSX.utils.aoa_to_sheet(rows):XLSX.utils.aoa_to_sheet([[del.name],[""],[ cnt.slice(0,500)]]);
                                      XLSX.utils.book_append_sheet(wb,ws,safeSheetName(del.name));
                                      const buf=XLSX.write(wb,{type:"array",bookType:"xlsx"});
                                      const blob=new Blob([buf],{type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
                                      const u=URL.createObjectURL(blob);
                                      const a=document.createElement("a");a.href=u;a.download=nm+".xlsx";a.style.display="none";
                                      document.body.appendChild(a);a.click();document.body.removeChild(a);setTimeout(()=>URL.revokeObjectURL(u),200);
                                    }catch(e:any){showToast("Excel: "+e.message,"error");}}
                                    else if(fmt==="docx"){
                                      const secs=parseSections(cnt);
                                      let html="<html xmlns:o=\"urn:schemas-microsoft-com:office:office\" xmlns:w=\"urn:schemas-microsoft-com:office:word\" xmlns=\"http://www.w3.org/TR/REC-html40\"><head><meta charset=\"UTF-8\"><style>@page{mso-page-orientation:portrait;margin:2.54cm;}body{font-family:Calibri,sans-serif;font-size:11pt;text-align:left;}h1{font-size:18pt;color:#14B8A6;}h2{font-size:13pt;}p{line-height:1.5;text-align:left;}table{border-collapse:collapse;width:100%;}th{background:#14B8A6;color:#fff;padding:5pt 8pt;}td{padding:4pt 8pt;border-bottom:1pt solid #ddd;}</style></head><body><h1>"+del.name+"</h1>";
                                      for(const sec of secs){html+="<h2>"+sec.title+"</h2>";sec.lines.forEach(ln=>{const t=stripMd(ln).trim();if(t)html+="<p>"+t+"</p>";});}
                                      html+="</body></html>";dlFile(nm+".doc",html,"application/msword");
                                    } else if(fmt==="pptx"){
                                      // Publication engine first; existing inline renderer stays as fallback.
                                      let _pptxOk=false;
                                      try{
                                        const _pubCtx=[projectExecution?.context?.company?.name||co.name||"",projectExecution?.context?.company?.industry||co.industry||"",projectExecution?.context?.company?.stage||co.stage||""].filter(Boolean).join(" | ");
                                        let _w=false;
                                        const _bee=new BusinessExecutionEngine(
                                          async(sys:any,msgs:any,maxT?:any,_es?:any,tt?:any)=>(await callMulti({...keys,...(EFF_CLAUDE?.trim()?{claude:EFF_CLAUDE}:{}),...(EFF_GEMINI?.trim()?{gemini:EFF_GEMINI}:{}),...(EFF_GROQ?.trim()?{groq:EFF_GROQ}:{})},defP,sys,msgs,maxT||6000,false,tt||"general")).primary,
                                          ensureXLSX,ensurePptx,ensureJsPDF,
                                          (_fname:any,buf:any)=>{dlFile(nm+".pptx",buf,"application/vnd.openxmlformats-officedocument.presentationml.presentation");_w=true;},
                                          stripMd);
                                        const _spec:DeliverableSpec={type:"pptx",title:del.name,purpose:del.description||del.name,audience:_delAudience(del),qualityStandard:"cfo_model",priority:"primary"};
                                        const _plan:ExecutionPlan={objectiveRestated:del.description||del.name,domain:(["finance","audit","strategy","marketing","operations","hr","legal","technology","sales","risk"] as const).find(d=>(del.capabilityType||"").toLowerCase().includes(d)||del.name.toLowerCase().includes(d))||"strategy",persona:"Senior Consultant",audience:_delAudience(del),qualityStandard:"cfo_model",decisionContext:del.description||del.name,deliverables:[_spec],missingInfo:[],executionOrder:[del.name],validationCriteria:[]};
                                        await _bee.generatePPTX(_plan,_spec,_pubCtx,cnt,()=>{});
                                        _pptxOk=_w;
                                      }catch(_e:any){recordEngineDowngrade(del.name,"pptx",_e);}
                                      if(!_pptxOk)try{
                                      const PptxGenJS=await ensurePptx();const pptx=new PptxGenJS();
                                      pptx.defineLayout({name:"WIDE",width:13.333,height:7.5});pptx.layout="WIDE";
                                      const s0=pptx.addSlide();s0.background={color:"0A0E1A"};
                                      s0.addText(del.name,{x:0.7,y:2.8,w:12,h:1.2,fontSize:26,bold:true,color:"F1F5F9"});
                                      parseSections(cnt).slice(0,10).forEach(sec=>{
                                        const s=pptx.addSlide();s.background={color:"0A0E1A"};
                                        s.addShape(pptx.ShapeType.rect,{x:0,y:0,w:13.333,h:0.9,fill:{color:"131825"}});
                                        s.addText(sec.title,{x:0.5,y:0.1,w:12,h:0.7,fontSize:18,bold:true,color:"F1F5F9",valign:"middle"});
                                        const b=sec.lines.map(l=>stripMd(l)).filter(Boolean).slice(0,8);
                                        if(b.length)s.addText(b.map(t=>({text:t.replace(/^[*-]\s*/,""),options:{bullet:{code:"2022"},color:"A0AAC0",fontSize:13,paraSpaceAfter:6}})),{x:0.7,y:1.2,w:12,h:5.8,valign:"top"});
                                      });
                                      await pptx.writeFile({fileName:nm+".pptx"});
                                    }catch(e){showToast("PPT: "+e.message,"error");}}
                                    else if(["linkedin_post","facebook_post","instagram_post","whatsapp_message","email"].includes(fmt)){dlFile(nm+".txt",stripMd(cnt).trim(),"text/plain");}
                                    else if(fmt==="image"||fmt==="video"){
                                      // THE ACTUAL BUG, CONFIRMED FROM THE SCREENSHOT: this branch did not
                                      // exist at all - an image/video deliverable fell straight into the
                                      // generic text-dump below, producing a file literally named
                                      // "...-Image.image" containing a raw text prompt, which is why
                                      // Windows had no app that could open it. It was never a real image.
                                      // This now does what the ZIP-package flow already does correctly:
                                      // try to generate the real file, OpenAI first.
                                      try{
                                        const _ctx=projectExecution?.context;
                                        const mp=buildMediaPrompt(del,_ctx);
                                        const genPrompt=fmt==="video"?(mp.veo||mp.kling||del.name):(mp.dalle||del.description||del.name);
                                        const falK=providerKey(keys,"fal");
                                        const _oa=providerKey(keys,"openai");
                                        let url:string|null=null;
                                        if(fmt==="video"){
                                          if(falK){setProjectExecPhase("🎬 Generating video via fal.ai: "+del.name+"...");url=await callFalVideo(falK,genPrompt);}
                                        }else if(_oa){
                                          try{setProjectExecPhase("🎨 Generating image via OpenAI: "+del.name+"...");url=await callDallE(genPrompt,_oa);if(!url)throw new Error("no image returned");}
                                          catch{if(falK){setProjectExecPhase("🎨 OpenAI unavailable — trying fal.ai: "+del.name);url=await callFalImage(falK,genPrompt);}}
                                        }else if(falK){
                                          setProjectExecPhase("🎨 Generating image via fal.ai: "+del.name+"...");url=await callFalImage(falK,genPrompt);
                                        }
                                        if(!url)throw new Error("No image/video provider configured — add an OpenAI or fal.ai key in Settings.");
                                        const resp=await fetch(url,{signal:AbortSignal.timeout(120000)});
                                        if(!resp.ok)throw new Error("media download failed: HTTP "+resp.status);
                                        const blob=await resp.blob();
                                        const ext=fmt==="video"?"mp4":(((blob.type||"image/png").split("/")[1]||"png").split("+")[0]);
                                        const u=URL.createObjectURL(blob);const a=document.createElement("a");a.href=u;a.download=nm+"."+ext;a.style.display="none";
                                        document.body.appendChild(a);a.click();document.body.removeChild(a);setTimeout(()=>URL.revokeObjectURL(u),200);
                                      }catch(e:any){
                                        // Never fall back to a fake ".image"/".video" file again — an
                                        // honest .md with the real prompt and the real reason it failed.
                                        dlFile(nm+"-GENERATION-FAILED.md","⚠ "+fmt.toUpperCase()+" COULD NOT BE GENERATED\n\nWHY: "+(e?.message||String(e))+"\n\nFIX: add an OpenAI or fal.ai key in Settings → API, then try again.\n\n## Prompt\n\n"+cnt,"text/markdown");
                                      }
                                    }
                                    else{dlFile(nm+"."+(fmt||"txt"),cnt,"text/plain");}
                                  }} style={{...S.hBtn,fontSize:8}}>↓</button>
                                </div>
                                {del.qaResult&&(
                                  <div style={{display:"flex",alignItems:"center",gap:5,marginTop:3,flexWrap:"wrap"}}>
                                    <span style={{fontSize:8,padding:"1px 6px",borderRadius:4,fontWeight:700,background:del.qaResult.passed?"rgba(16,185,129,0.1)":"rgba(245,158,11,0.1)",color:del.qaResult.passed?"#10B981":"#F59E0B"}}>{del.qaResult.passed?"✓ QA":"⚠ QA"} {del.qaResult.score}%</span>
                                    <span style={{fontSize:8,color:"#5A6480",flex:1}}>{del.qaResult.summary}</span>
                                    {(del.qaResult.flags||[]).slice(0,1).map((f,fi)=>(
                                      <span key={fi} style={{fontSize:7,padding:"1px 5px",borderRadius:3,background:"rgba(239,68,68,0.08)",color:"#EF4444"}}>{f.suggestion?.slice(0,35)}</span>
                                    ))}
                                  </div>
                                )}
                                {/* Phase 6: user-driven iteration — say what you'd like changed, get exactly that change */}
                                <div style={{display:"flex",gap:4,marginTop:5,alignItems:"center"}}>
                                  <input
                                    value={projectFeedback[del.id]||""}
                                    onChange={e=>setProjectFeedback(prev=>({...prev,[del.id]:e.target.value}))}
                                    placeholder="What would you like changed? (optional)"
                                    disabled={projectRegeneratingId===del.id}
                                    style={{...S.inp,flex:1,fontSize:9,padding:"4px 8px"}}
                                  />
                                  <button
                                    onClick={()=>regenerateDeliverableWithFeedback(del.moduleId,del.id,del.name,rawContentStore.current[del.id]||del.rawContent||"")}
                                    disabled={projectRegeneratingId===del.id||!(projectFeedback[del.id]||"").trim()}
                                    style={{...S.hBtn,fontSize:8,opacity:(projectRegeneratingId===del.id||!(projectFeedback[del.id]||"").trim())?0.4:1,whiteSpace:"nowrap"}}
                                  >{projectRegeneratingId===del.id?"Updating...":"Apply"}</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      {/* New project button */}
                      {/* ── REVIEW PANEL ── */}
                      {projectReviewMode&&projectExecution&&!projectExecuting&&!projectQARunning&&(
                        <div style={{background:"#131825",border:"1px solid #1a2030",borderRadius:8,padding:"12px 14px",marginTop:8,marginBottom:8}}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                            <div style={{fontSize:11,fontWeight:700,color:"#F1F5F9"}}>Review Deliverables Before Packaging</div>
                            <button onClick={()=>setProjectReviewMode(false)} style={{...S.hBtn,fontSize:9}}>Done Reviewing</button>
                          </div>
                          {(projectExecution.modules||[]).flatMap(m=>(m.deliverables||[]).filter(d=>d.rawContent).map(d=>({...d,_mod:m.name}))).map((del,di)=>(
                            <div key={di} style={{background:"#0a0e1a",borderRadius:6,padding:"8px 10px",marginBottom:6,border:"1px solid "+(projectExcluded[del.id]?"#EF444433":"#1a2030")}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                                <input type="checkbox" checked={!projectExcluded[del.id]} onChange={()=>setProjectExcluded(prev=>({...prev,[del.id]:!prev[del.id]}))} style={{accentColor:"#14B8A6",flexShrink:0}}/>
                                <div style={{flex:1}}>
                                  <div style={{fontSize:10,fontWeight:600,color:projectExcluded[del.id]?"#5A6480":"#F1F5F9"}}>{del.name}</div>
                                  <div style={{fontSize:8,color:"#5A6480"}}>{del._mod} · {del.outputFormat} · QA {del.qaResult?.score||0}%</div>
                                </div>
                                {del.qaResult?.passed===false&&<span style={{fontSize:8,padding:"1px 6px",borderRadius:4,background:"rgba(245,158,11,0.1)",color:"#F59E0B"}}>⚠ Review</span>}
                                <button onClick={()=>cp(del.rawContent||"")} style={{...S.hBtn,fontSize:8}}>Copy</button>
                              </div>
                              {!projectExcluded[del.id]&&del.rawContent&&(
                                <div style={{background:"#131825",borderRadius:4,padding:"6px 8px",maxHeight:120,overflowY:"auto",fontSize:9,color:"#8892B0",lineHeight:1.5}}>
                                  {del.rawContent.slice(0,400)}{del.rawContent.length>400?"...":""}
                                </div>
                              )}
                            </div>
                          ))}
                          <div style={{fontSize:9,color:"#5A6480",marginTop:6}}>
                            {Object.keys(projectExcluded).filter(k=>projectExcluded[k]).length} excluded · {(projectExecution.modules||[]).flatMap(m=>m.deliverables||[]).filter(d=>d.rawContent&&!projectExcluded[d.id]).length} will be packaged
                          </div>
                        </div>
                      )}
                      {!projectExecuting&&!projectQARunning&&(
                        <div style={{marginTop:8}}>
                          {/* Media picker */}
                          <div style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:7,padding:"10px 12px",marginBottom:8}}>
                            <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Media Generation</div>
                            <div style={{marginBottom:8}}>
                              <div style={{fontSize:9,fontWeight:600,color:"#A0AAC0",marginBottom:4}}>🖼 Images</div>
                              {[["prompts","Export prompts only","free",""],["dalle","Generate with DALL-E 3","~₹8/image","openai"],["stability","Generate with Stability AI","~₹3/image","stability"]].map(([val,lb,cost,reqKey])=>(
                                <label key={val} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,cursor:"pointer",opacity:reqKey&&!keys[reqKey]?.trim()?0.4:1}}>
                                  <input type="radio" name="imgMode" checked={mediaMode.image===val} onChange={()=>setMediaMode(m=>({...m,image:val}))} disabled={!!reqKey&&!keys[reqKey]?.trim()} style={{accentColor:"#14B8A6"}}/>
                                  <span style={{fontSize:9,color:"#A0AAC0"}}>{lb}</span>
                                  <span style={{fontSize:8,padding:"1px 5px",borderRadius:4,background:val==="prompts"?"rgba(16,185,129,0.1)":"rgba(245,158,11,0.08)",color:val==="prompts"?"#10B981":"#F59E0B"}}>{cost}</span>
                                  {reqKey&&!keys[reqKey]?.trim()&&(
                                  <span style={{fontSize:7,color:"#EF4444",display:"block",marginTop:2}}>⚠ Add {reqKey==="openai"?"OpenAI":reqKey==="stability"?"Stability AI":reqKey} key in Settings → API Keys</span>
                                )}
                                </label>
                              ))}
                            </div>
                            <div>
                              <div style={{fontSize:9,fontWeight:600,color:"#A0AAC0",marginBottom:4}}>🎬 Video</div>
                              {[["veo","Google Veo prompts","free"],["runway","Runway ML prompts","free"],["kling","Kling AI prompts","free"]].map(([val,lb,cost])=>(
                                <label key={val} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,cursor:"pointer"}}>
                                  <input type="radio" name="vidMode" checked={mediaMode.video===val} onChange={()=>setMediaMode(m=>({...m,video:val}))} style={{accentColor:"#14B8A6"}}/>
                                  <span style={{fontSize:9,color:"#A0AAC0"}}>{lb}</span>
                                  <span style={{fontSize:8,padding:"1px 5px",borderRadius:4,background:"rgba(16,185,129,0.1)",color:"#10B981"}}>{cost}</span>
                                </label>
                              ))}
                              <div style={{fontSize:8,color:"#5A6480",marginTop:4,lineHeight:1.5}}>Video prompts are included in the ZIP — paste into your chosen tool to generate.</div>
                            </div>
                            <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid #1a2030",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <span style={{fontSize:9,color:"#5A6480"}}>Estimated cost:</span>
                              <span style={{fontSize:10,fontWeight:700,color:mediaMode.image==="prompts"?"#10B981":"#F59E0B"}}>{mediaMode.image==="prompts"?"₹0 — Free":"Paid — check key balance"}</span>
                            </div>
                          </div>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>runProjectPackage({...projectExecution,_excluded:projectExcluded})} disabled={projectPackaging}
                              style={{...S.pBtn,marginTop:0,flex:2,fontSize:11,background:"linear-gradient(135deg,#14B8A6,#6366F1)",opacity:projectPackaging?0.5:1}}>
                              {projectPackaging?"📦 Packaging...":"📦 Download All Files (ZIP)"}
                            </button>
                            <button onClick={()=>{setProjectReviewMode(r=>!r);setProjectExcluded({});}}
                              style={{...S.hBtn,flex:1,textAlign:"center",padding:"10px 8px",fontSize:10,color:projectReviewMode?"#14B8A6":"#A0AAC0",borderColor:projectReviewMode?"#14B8A633":"#1a2030"}}>Review</button>
                            <button onClick={()=>{setProjectExecution(null);setProjectExecPhase("");setProjectReviewMode(false);setProjectExcluded({});setProjectDashboardOpen(false);rawContentStore.current={};}}
                              style={{...S.hBtn,textAlign:"center",padding:"10px 8px",fontSize:10}}>New</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── PLANNING / INPUT UI ── */}
                  {!projectDashboardOpen&&(
                  <div>
                  {!projectPlan&&!projectPlanning&&(
                    <div>
                      <label style={S.lbl}>Business Objective</label>
                      <div style={{display:"flex",gap:4,alignItems:"flex-start",marginBottom:10}}>
                        <textarea style={{...S.inp,flex:1,minHeight:80,resize:"vertical"}} value={projectObjective} onChange={e=>setProjectObjective(e.target.value)} placeholder="e.g. Launch Orchestriq to market. Or: Build a complete compliance audit programme for our EMEA operations."/>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}><LangPick value={vLang} onChange={vl=>{setVLang(vl);sv("cos-vl",vl);}}/><MicButton lang={vLang} onResult={t=>setProjectObjective(prev=>(prev?prev+" ":"")+t)} openaiKey={providerKey(keys,"openai")||undefined}/></div>
                      </div>
                      <div style={{background:"rgba(20,184,166,0.04)",border:"1px solid rgba(20,184,166,0.15)",borderRadius:7,padding:"10px 12px",marginBottom:12,fontSize:10,color:"#A0AAC0",lineHeight:1.7}}>
                        <strong style={{color:"#14B8A6"}}>What happens next:</strong> The Project Architect analyses your objective and builds an Execution Plan listing every module and deliverable. You review and approve the plan before any AI execution begins.
                      </div>
                      <button onClick={runProjectPlanning} disabled={!projectObjective.trim()} style={{...S.pBtn,marginTop:0,background:"linear-gradient(135deg,#14B8A6,#6366F1)",opacity:projectObjective.trim()?1:0.3}}>Generate Execution Plan</button>
                    </div>
                  )}
                  {projectPlanning&&(
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"20px",background:"#131825",borderRadius:8,border:"1px solid #1a2030"}}>
                      <span style={{width:8,height:8,borderRadius:"50%",background:"#14B8A6",display:"inline-block",animation:"pulse 1s infinite",flexShrink:0}}/>
                      <div><div style={{fontSize:12,fontWeight:700,color:"#14B8A6"}}>Project Architect is analysing your objective…</div><div style={{fontSize:10,color:"#5A6480",marginTop:3}}>Decomposing into modules and deliverables. This takes 15-30 seconds.</div></div>
                    </div>
                  )}
                  {projectPlan&&!projectPlanning&&(
                    <div style={{animation:"fadeIn 0.3s"}}>
                      <div style={{background:"#131825",border:"1px solid #1a2030",borderRadius:8,padding:"14px 16px",marginBottom:12}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                          <span style={{fontSize:18}}>📋</span>
                          <div><div style={{fontSize:13,fontWeight:800,color:"#F1F5F9"}}>{projectPlan.name}</div><div style={{fontSize:9,color:"#5A6480",marginTop:1}}>{projectPlan.modules?.length||0} execution modules · {(projectPlan.modules||[]).reduce((a,m)=>a+(m.deliverables?.length||0),0)} deliverables planned</div></div>
                          <span style={{marginLeft:"auto",fontSize:8,padding:"3px 8px",borderRadius:8,background:"rgba(245,158,11,0.1)",color:"#F59E0B",fontWeight:700}}>AWAITING APPROVAL</span>
                        </div>
                        <div style={{fontSize:10,color:"#8892B0",lineHeight:1.6,marginBottom:10,fontStyle:"italic"}}>Objective: "{projectPlan.objective}"</div>
                        {projectPlan.architectReasoning&&(
                          <div style={{background:"rgba(20,184,166,0.05)",border:"1px solid rgba(20,184,166,0.2)",borderRadius:6,padding:"9px 11px",marginBottom:10}}>
                            <div style={{fontSize:9,fontWeight:700,color:"#14B8A6",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Here's what I understood</div>
                            <div style={{fontSize:10.5,color:"#C5CCDC",lineHeight:1.6}}>{projectPlan.architectReasoning.goalRestated}</div>
                            {projectPlan.architectReasoning.relevantDataUsed?.length>0&&(
                              <div style={{fontSize:9,color:"#8892B0",marginTop:5}}>📎 Used: {projectPlan.architectReasoning.relevantDataUsed.join(" · ")}</div>
                            )}
                            {projectPlan.architectReasoning.keyAssumptions?.length>0&&(
                              <div style={{fontSize:9,color:"#F59E0B",marginTop:4}}>⚠ Assuming: {projectPlan.architectReasoning.keyAssumptions.join(" · ")} — edit your objective above and regenerate if this isn't right.</div>
                            )}
                          </div>
                        )}
                        {(projectPlan.modules||[]).map((mod,mi)=>(
                          <div key={mi} style={{marginBottom:8,background:"#0a0e1a",borderRadius:6,padding:"10px 12px",border:"1px solid #1a2030"}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                              <span style={{fontSize:14}}>{mod.icon||"⚙️"}</span>
                              <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:"#14B8A6"}}>{mod.name}</div><div style={{fontSize:9,color:"#5A6480"}}>{mod.capabilityType} · {mod.deliverables?.length||0} deliverables</div></div>
                            </div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                              {(mod.deliverables||[]).map((del,di)=>(
                                <div key={di} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:4,padding:"4px 8px",fontSize:9}}>
                                  <span style={{color:"#A0AAC0"}}>{del.name}</span>
                                  <span style={{marginLeft:4,color:"#3A4060",fontSize:8}}>{del.outputFormat}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{background:"rgba(20,184,166,0.04)",border:"1px solid rgba(20,184,166,0.15)",borderRadius:7,padding:"10px 12px",marginBottom:12,fontSize:10,color:"#8892B0",lineHeight:1.7}}>
                        Approving this plan saves it as a reusable Execution Plan template. Future similar objectives will be matched to this plan automatically.
                      </div>
                      <div style={{display:"flex",gap:6}}>
                        <button onClick={approveProjectPlan} style={{flex:1,...S.pBtn,marginTop:0,background:"#10B981"}}>Approve Plan and Begin Execution</button>
                        <button onClick={()=>{setProjectPlan(null);setProjectObjective("");}} style={{...S.hBtn,padding:"10px 14px",fontSize:11}}>Revise</button>
                        <button onClick={()=>{setProjectPlan(null);setProjectObjective("");try{localStorage.removeItem("cos-project-plan");}catch{}}} style={{...S.hBtn,padding:"10px 14px",fontSize:11,color:"#EF4444",borderColor:"#EF444433"}}>✕ Cancel</button>
                      </div>
                    </div>
                  )}
                  {projects.length>0&&!projectPlan&&!projectPlanning&&!projectDashboardOpen&&(
                    <div style={{marginTop:14}}>
                      <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:8,flexWrap:"wrap"}}>
                        {[["recent","Recent"],["complete","Completed"],["partial","Partial"],["archived","Archived"]].map(([v,lb])=>(
                          <button key={v} onClick={()=>setProjectArchiveView(v)} style={{padding:"3px 9px",borderRadius:5,fontSize:9,fontWeight:600,border:"1px solid "+(projectArchiveView===v?"#14B8A6":"#1a2030"),background:projectArchiveView===v?"rgba(20,184,166,0.08)":"transparent",color:projectArchiveView===v?"#14B8A6":"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{lb}</button>
                        ))}
                        <input value={projectSearchQ} onChange={e=>setProjectSearchQ(e.target.value)} placeholder="Search..." style={{...S.inp,flex:1,minWidth:70,fontSize:9,padding:"3px 7px",marginLeft:"auto"}}/>
                      </div>
                      {(()=>{
                        const q=projectSearchQ.toLowerCase();
                        return projects.filter(p=>{
                          if(q&&!p.name.toLowerCase().includes(q)&&!(p.objective||"").toLowerCase().includes(q))return false;
                          if(projectArchiveView==="recent")return !p.archived;
                          if(projectArchiveView==="complete")return p.status==="complete"&&!p.archived;
                          if(projectArchiveView==="partial")return p.status==="partial"&&!p.archived;
                          if(projectArchiveView==="archived")return !!p.archived;
                          return !p.archived;
                        }).slice(0,10).map(p=>(
                          <div key={p.id} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:6,padding:"9px 11px",marginBottom:5}}>
                            <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:5}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:10,fontWeight:600,color:"#F1F5F9",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                                <div style={{fontSize:8,color:"#5A6480",marginTop:1}}>{p.modules?.length||0} modules · {new Date(p.createdAt).toLocaleDateString()}</div>
                              </div>
                              <span style={{fontSize:8,padding:"2px 7px",borderRadius:8,fontWeight:700,flexShrink:0,background:p.status==="complete"?"rgba(16,185,129,0.12)":p.status==="partial"?"rgba(245,158,11,0.08)":"rgba(20,184,166,0.08)",color:p.status==="complete"?"#10B981":p.status==="partial"?"#F59E0B":"#14B8A6"}}>{p.status}</span>
                            </div>
                            <div style={{display:"flex",gap:4}}>
                              <button onClick={()=>{setProjectExecution(p);setProjectReviewMode(false);setProjectExcluded({});setProjectDashboardOpen(true);}} style={{...S.hBtn,flex:2,textAlign:"center",fontSize:8,color:"#14B8A6",borderColor:"#14B8A633"}}>📂 Open</button>
                              {p.status==="complete"&&<button onClick={()=>runProjectPackage({...p,_excluded:{}})} style={{...S.hBtn,flex:2,textAlign:"center",fontSize:8}}>📦 ZIP</button>}
                              <button onClick={()=>{const u=projects.map(x=>x.id===p.id?{...x,archived:!x.archived}:x);setProjects(u);sv("cos-projects",u);}} style={{...S.hBtn,flex:1,textAlign:"center",fontSize:8}}>{p.archived?"↩":"📁"}</button>
                              <button onClick={()=>{if(window.confirm("Delete \""+p.name+"\"? This cannot be undone.")){const u=projects.filter(x=>x.id!==p.id);setProjects(u);sv("cos-projects",u);}}} style={{...S.hBtn,flex:1,textAlign:"center",fontSize:8,color:"#EF4444",borderColor:"#EF444433"}}>🗑</button>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  </div>
                  )}
                </div>
              )}
              {wfView==="active"&&(
                <div>
                  {!wfActive?(
                    <div style={{textAlign:"center",padding:"40px 20px",color:"#5A6480"}}><div style={{fontSize:32,marginBottom:8}}>⚡</div><div style={{fontSize:13,fontWeight:600,color:"#A0AAC0",marginBottom:6}}>No active workflow</div><button onClick={()=>setWfView("new")} style={{...S.pBtn,width:"auto",padding:"8px 20px",marginTop:14,fontSize:12}}>Start New Task</button></div>
                  ):(
                    <div>
                      <div style={{background:"#131825",borderRadius:8,padding:"12px 14px",marginBottom:12,border:"1px solid "+(CHAINS[wfActive.category]?.color+"33"||"#1a2030")}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                          <span style={{fontSize:16}}>{CHAINS[wfActive.category]?.ic||"⚡"}</span>
                          <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:"#F1F5F9"}}>{wfActive.chainLabel}</div><div style={{fontSize:9,color:"#5A6480"}}>Started {new Date(wfActive.startedAt).toLocaleString()}</div></div>
                          <span style={{fontSize:9,padding:"3px 8px",borderRadius:10,fontWeight:700,background:wfActive.status==="awaiting_approval"?"rgba(245,158,11,0.1)":wfActive.status==="error"?"rgba(239,68,68,0.1)":"rgba(20,184,166,0.08)",color:wfActive.status==="awaiting_approval"?"#F59E0B":wfActive.status==="error"?"#EF4444":"#14B8A6"}}>{wfActive.status==="awaiting_approval"?"Awaiting Approval":wfActive.status==="error"?"Error":wfRunning?"Running":"Complete"}</span>
                        </div>
                        <div style={{fontSize:11,color:"#A0AAC0",fontStyle:"italic"}}>"{wfActive.task}"</div>
                      </div>
                      <div style={{marginBottom:14}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#5A6480",marginBottom:4}}><span>Chain Progress</span><span>{wfActive.steps.length}/{CHAINS[wfActive.category]?.chain.length||0} levels</span></div>
                        <div style={{background:"#1a2030",borderRadius:20,height:4}}><div style={{background:"linear-gradient(90deg,#14B8A6,#3B82F6)",height:"100%",borderRadius:20,width:((wfActive.steps.length/(CHAINS[wfActive.category]?.chain.length||1))*100)+"%",transition:"width 0.5s"}}/></div>
                      </div>
                      {wfActive.steps.map((step,i)=>(
                        <div key={i} style={{marginBottom:8,animation:"fadeIn 0.3s"}}>
                          <div style={{background:step.failed?"rgba(239,68,68,0.06)":"#131825",borderRadius:7,padding:"10px 12px",borderLeft:"3px solid "+(step.failed?"#EF4444":step.role.dc)}}>
                            <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:5}}>
                              <span style={{fontSize:14}}>{step.role.ic}</span>
                              <span style={{fontSize:10,fontWeight:700,color:step.failed?"#EF4444":step.role.dc}}>{step.role.t} — Level {step.level}</span>
                              {step.isLast&&!step.failed&&<span style={{marginLeft:"auto",fontSize:8,background:"rgba(245,158,11,0.1)",color:"#F59E0B",padding:"2px 7px",borderRadius:8,fontWeight:700}}>FINAL</span>}
                              {step.failed&&<span style={{marginLeft:"auto",fontSize:8,background:"rgba(239,68,68,0.1)",color:"#EF4444",padding:"2px 7px",borderRadius:8,fontWeight:700}}>FAILED</span>}
                              {!step.failed&&<button onClick={()=>cp(step.output)} style={{...S.hBtn,marginLeft:"auto"}}>Copy</button>}
                            </div>
                            <div style={{fontSize:11,lineHeight:1.7,color:step.failed?"#EF9999":"#A0AAC0"}}><Md text={step.output} ac={step.failed?"#EF4444":step.role.dc}/></div>
                          </div>
                          {step.capability&&(
                            <div style={{marginTop:8,background:"linear-gradient(135deg,rgba(168,85,247,0.06),rgba(20,184,166,0.04))",border:"1px solid rgba(168,85,247,0.2)",borderRadius:8,padding:"12px 14px"}}>
                              <div style={{fontSize:12,fontWeight:800,color:"#A855F7",marginBottom:8}}>📋 Capability & Cost Brief</div>
                              {step.capability.info_needed?.length>0&&<div style={{marginBottom:8}}><div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",marginBottom:3}}>Information Still Needed</div>{step.capability.info_needed.map((x,k)=><div key={k} style={{fontSize:10,color:"#A0AAC0",padding:"2px 0"}}>• {x}</div>)}</div>}
                              {step.capability.tools_required?.length>0&&<div style={{marginBottom:8}}><div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",marginBottom:3}}>Tools / Integrations Required</div>{step.capability.tools_required.map((t,k)=><div key={k} style={{fontSize:10,padding:"2px 0",color:t.available?"#10B981":"#F59E0B"}}>{t.available?"✓":"⚠"} {t.name} — {t.why}{!t.available&&" (not yet integrated — contact admin)"}</div>)}</div>}
                              {step.capability.automated_steps?.length>0&&<div style={{marginBottom:8}}><div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",marginBottom:3}}>✅ Completed By This Chain</div>{step.capability.automated_steps.map((x,k)=><div key={k} style={{fontSize:10,color:"#10B981",padding:"2px 0"}}>• {x}</div>)}</div>}
                              {step.capability.manual_steps?.length>0&&<div style={{marginBottom:8}}><div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",marginBottom:3}}>👤 Your Next Steps</div>{step.capability.manual_steps.map((x,k)=><div key={k} style={{fontSize:10,color:"#A0AAC0",padding:"2px 0"}}>{k+1}. {x}</div>)}</div>}
                              {step.capability.est_cost_usd>0&&<div style={{marginTop:8,paddingTop:8,borderTop:"1px dashed #1a2030"}}><div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",marginBottom:3}}>Estimated Cost</div><div style={{fontSize:11,color:"#A0AAC0"}}>External services: ~${step.capability.est_cost_usd.toFixed(2)} · Service fee: ~${step.capability.fee_usd.toFixed(2)}</div><div style={{fontSize:14,fontWeight:800,color:"#14B8A6",marginTop:4}}>{step.capability.converted_total?cur.sym+step.capability.converted_total:"$"+step.capability.total_usd.toFixed(2)+" total"}</div></div>}
                              {step.capability.notes&&<div style={{marginTop:8,fontSize:10,color:"#8892B0",fontStyle:"italic"}}>{step.capability.notes}</div>}
                            </div>
                          )}
                          {i<wfActive.steps.length-1&&<div style={{display:"flex",justifyContent:"center",margin:"3px 0"}}><span style={{fontSize:9,color:"#3A4060"}}>↓ escalated</span></div>}
                        </div>
                      ))}
                      {wfRunning&&wfPhase&&<div style={{fontSize:10,color:"#14B8A6",margin:"8px 0",display:"flex",alignItems:"center",gap:5}}><span style={{width:5,height:5,borderRadius:"50%",background:"#14B8A6",display:"inline-block",animation:"pulse 1s infinite"}}/>{wfPhase}</div>}
                      {wfActive.status==="awaiting_approval"&&!wfRunning&&(
                        <div style={{marginTop:14,padding:"14px",background:"linear-gradient(135deg,rgba(20,184,166,0.06),rgba(59,130,246,0.04))",border:"1px solid rgba(20,184,166,0.2)",borderRadius:8}}>
                          <div style={{fontSize:12,fontWeight:800,color:"#14B8A6",marginBottom:4}}>Chain Complete — Your Decision</div>
                          <p style={{fontSize:11,color:"#8892B0",marginBottom:12,lineHeight:1.6}}>All {wfActive.steps.length} levels have reviewed and enhanced this task. Do you approve this output?</p>
                          <div style={{display:"flex",gap:8}}>
                            <button onClick={approveWF} style={{flex:1,background:"#10B981",color:"#fff",border:"none",borderRadius:7,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Approve and Archive</button>
                            <button onClick={()=>{const note=prompt("Reason for rejection?");rejectWF(note||"");}} style={{flex:1,background:"transparent",color:"#EF4444",border:"1px solid #EF444444",borderRadius:7,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Send Back</button>
                          </div>
                          <button onClick={()=>dlFile("Workflow-"+wfActive.id+".md",wfActive.steps.map(s=>"## Level "+s.level+": "+s.role.t+"\n"+s.output).join("\n\n---\n\n"),"text/markdown")} style={{...S.hBtn,width:"100%",marginTop:8,textAlign:"center"}}>Export Full Chain as Markdown</button>
<div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #1a2030"}}>
  <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:6}}>⚡ Generate CFO-Grade File (Python Service)</div>
  <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
    {[["📊 Excel","excel"],["📑 PPT","pptx"],["📄 PDF","pdf"],["📝 Word","docx"]].map(([lb,type])=>(
      <button key={type} onClick={async()=>{
        const body=wfActive.steps.map(s=>s.output).join("\n\n");
        const fn=type==="excel"?generateExcel:type==="pptx"?generatePptx:type==="pdf"?generatePdf:generateDocx;
        showToast("Building "+lb.replace(/[^\w]/g,"")+"…","info");
        const r=await fn({objective:wfActive.task,company_context:co.name+" | "+co.industry+" | "+co.stage+" | "+co.location,available_data:body,currency:co.currency,currency_symbol:cur.sym,api_key:docServiceKey(keys)});
        if(!r.success)showToast(r.error||"Generation failed","error");
      }} style={{...S.hBtn,color:"#14B8A6",borderColor:"#14B8A633",flex:1,textAlign:"center"}}>{lb}</button>
    ))}
  </div>
  <div style={{fontSize:8,color:"#5A6480",marginTop:5}}>Uses Railway Python service → CFO-grade Excel with formulas, consulting-grade PPT/PDF/Word</div>
</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {wfView==="history"&&(
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <div><div style={{fontSize:13,fontWeight:800,color:"#F1F5F9"}}>Workflow History</div><p style={{fontSize:10,color:"#5A6480"}}>{workflows.length} completed</p></div>
                    {workflows.length>0&&<button onClick={()=>{if(confirm("Clear all history?")){setWorkflows([]);sv("cos-wf",[]);}}} style={{...S.hBtn,color:"#EF4444",borderColor:"#EF444433"}}>Clear All</button>}
                  </div>
                  {!workflows.length?(<div style={{textAlign:"center",padding:"30px",color:"#5A6480"}}><div style={{fontSize:28,marginBottom:8}}>📁</div><div style={{fontSize:12}}>No completed workflows yet.</div></div>):(
                    workflows.map(wf=>(
                      <div key={wf.id} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:8,padding:"12px 14px",marginBottom:8}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:6}}>
                          <span style={{fontSize:16}}>{CHAINS[wf.category]?.ic||"⚡"}</span>
                          <div style={{flex:1,minWidth:0}}><div style={{fontSize:10,fontWeight:700,color:"#F1F5F9",marginBottom:1}}>{wf.chainLabel}</div><div style={{fontSize:10,color:"#8892B0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>"{wf.task}"</div><div style={{fontSize:8,color:"#3A4060",marginTop:3}}>{new Date(wf.startedAt).toLocaleDateString()} · {wf.steps.length} levels</div></div>
                          <span style={{fontSize:8,padding:"3px 8px",borderRadius:10,fontWeight:700,background:wf.status==="approved"?"rgba(16,185,129,0.12)":"rgba(239,68,68,0.1)",color:wf.status==="approved"?"#10B981":"#EF4444"}}>{wf.status==="approved"?"Approved":"Rejected"}</span>
                        </div>
                        {wf.rejectionNote&&<div style={{fontSize:9,color:"#EF4444",padding:"4px 8px",background:"rgba(239,68,68,0.05)",borderRadius:4,marginBottom:6}}>{wf.rejectionNote}</div>}
                        <div style={{display:"flex",gap:4}}>
                          <button onClick={()=>{setWfActive(wf);setWfView("active");}} style={{...S.hBtn,flex:1,textAlign:"center"}}>View</button>
                          <button onClick={()=>dlFile("Workflow-"+wf.id+".md",wf.steps.map(s=>"## Level "+s.level+": "+s.role.t+"\n"+s.output).join("\n\n---\n\n"),"text/markdown")} style={{...S.hBtn,flex:1,textAlign:"center"}}>Export</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AUTOPILOT P3 */}
        {view==="p3"&&(
          <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
            <div style={{display:"flex",gap:2,padding:"8px 14px",borderBottom:"1px solid #14192a",background:"#0c1120",alignItems:"center",flexWrap:"wrap"}}>
              {[["dashboard","Dashboard"],["new","New Task"],["running","Running"+(qRunning?" ●":"")],["completed","Review ("+tQueue.filter(t=>t.status===TS.REVIEWING).length+")"],["completed_arc","Archive ("+tQueue.filter(t=>[TS.APPROVED,TS.REJECTED].includes(t.status)).length+")"]].map(([id,lb])=>(
                <button key={id} onClick={()=>setP3View(id)} style={{padding:"4px 8px",borderRadius:5,fontSize:9,fontWeight:600,border:"1px solid "+(p3View===id?"#14B8A6":"#1a2030"),background:p3View===id?"rgba(20,184,166,0.08)":"transparent",color:p3View===id?"#14B8A6":"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"}}>{lb}</button>
              ))}
              <button onClick={()=>setP3Notify([])} title="Clear notifications" style={{...S.hBtn,marginLeft:"auto",fontSize:13,position:"relative"}}>
                🔔{p3Notify.length>0&&<span style={{position:"absolute",top:-4,right:-4,background:"#EF4444",color:"#fff",fontSize:7,borderRadius:"50%",width:12,height:12,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>{p3Notify.length>9?"9+":p3Notify.length}</span>}
              </button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"12px 16px"}}>
              {p3View==="dashboard"&&(
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#F1F5F9",marginBottom:2}}>Autonomous Task Dashboard</div>
                  <p style={{fontSize:10,color:"#8892B0",marginBottom:14,lineHeight:1.6}}>Phase 3: Full automation. Queue tasks, hit Run All, the entire hierarchy processes them. You approve at the end.</p>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:14}}>
                    {[["Queued",tQueue.filter(t=>t.status===TS.QUEUED).length,"#F59E0B"],["Running",tQueue.filter(t=>t.status===TS.RUNNING).length,"#14B8A6"],["Review",tQueue.filter(t=>t.status===TS.REVIEWING).length,"#8B5CF6"],["Approved",tQueue.filter(t=>t.status===TS.APPROVED).length,"#10B981"]].map(([lb,count,c])=>(
                      <div key={lb} style={{background:"#131825",border:"1px solid "+c+"22",borderRadius:8,padding:"12px 10px",textAlign:"center"}}><div style={{fontSize:20,fontWeight:900,color:c}}>{count}</div><div style={{fontSize:9,color:"#5A6480",fontWeight:600,textTransform:"uppercase"}}>{lb}</div></div>
                    ))}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
                    <button onClick={()=>setP3View("new")} style={{...S.pBtn,marginTop:0,fontSize:12}}>Add Task to Queue</button>
                    <div style={{display:"flex",gap:4}}>
                      <button onClick={runQueue} disabled={qRunning||!tQueue.filter(t=>t.status===TS.QUEUED).length} style={{...S.pBtn,marginTop:0,background:"linear-gradient(135deg,#8B5CF6,#3B82F6)",fontSize:12,flex:1,opacity:qRunning||!tQueue.filter(t=>t.status===TS.QUEUED).length?0.3:1}}>{qRunning?"Processing…":"Run All Queued"}</button>
                      {qRunning&&<button onClick={()=>{cancelRef.current.q=true;}} style={{...S.cancelBtn,alignSelf:"stretch"}}>Stop</button>}
                    </div>
                  </div>
                  {qRunning&&p3Phase&&(
                    <div style={{background:"rgba(20,184,166,0.06)",border:"1px solid rgba(20,184,166,0.2)",borderRadius:7,padding:"10px 14px",marginBottom:12,fontSize:11,color:"#14B8A6",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{width:8,height:8,borderRadius:"50%",background:"#14B8A6",display:"inline-block",animation:"pulse 1s infinite",flexShrink:0}}/>
                      <div><div style={{fontWeight:700}}>{p3Phase}</div>{p3Running&&<div style={{fontSize:9,color:"#5A6480",marginTop:2}}>Task: "{p3Running.task?.slice(0,60)}…"</div>}</div>
                    </div>
                  )}
                  {p3Notify.length>0&&(
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:5}}>Activity Feed</div>
                      {p3Notify.slice(0,8).map(n=>(
                        <div key={n.id} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",background:"#0a0e1a",borderRadius:5,marginBottom:3,fontSize:10,color:n.type==="error"?"#EF4444":n.type==="success"?"#10B981":n.type==="complete"?"#8B5CF6":"#A0AAC0"}}>
                          <span style={{fontSize:8,color:"#3A4060",flexShrink:0}}>{new Date(n.ts).toLocaleTimeString()}</span>{n.msg}
                        </div>
                      ))}
                    </div>
                  )}
                  {tQueue.length>0?(
                    <div>
                      <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:6}}>All Tasks</div>
                      {tQueue.slice().reverse().map(t=>(
                        <div key={t.id} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:6,padding:"9px 11px",marginBottom:5,display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:14}}>{CHAINS[t.category]?.ic||"⚡"}</span>
                          <div style={{flex:1,minWidth:0}}><div style={{fontSize:10,fontWeight:600,color:"#F1F5F9",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>"{t.task.slice(0,55)}{t.task.length>55?"…":""}"</div><div style={{fontSize:8,color:"#5A6480",marginTop:1}}>{t.chainLabel} · {t.priority.toUpperCase()} · Level {t.currentLevel||0}/{CHAINS[t.category]?.chain.length||0}</div></div>
                          <span style={{fontSize:8,padding:"2px 7px",borderRadius:8,fontWeight:700,background:sBg(t.status),color:sColor(t.status)}}>{t.status.replace("_"," ").toUpperCase()}</span>
                          <button onClick={()=>deleteT(t.id)} style={{background:"none",border:"none",color:"#3A4060",fontSize:10,cursor:"pointer",flexShrink:0}}>×</button>
                        </div>
                      ))}
                    </div>
                  ):(<div style={{textAlign:"center",padding:"30px",color:"#5A6480"}}><div style={{fontSize:32,marginBottom:8}}>🤖</div><div style={{fontSize:13,fontWeight:600,color:"#A0AAC0",marginBottom:4}}>Queue is empty</div><button onClick={()=>setP3View("new")} style={{...S.pBtn,width:"auto",padding:"8px 20px",marginTop:14,fontSize:12}}>Add First Task</button></div>)}
                </div>
              )}
              {p3View==="new"&&(
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#F1F5F9",marginBottom:6}}>Add Task to Autonomous Queue</div>
                  <div style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:7,padding:"10px 12px",marginBottom:10}}>
                    <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                      <input type="checkbox" checked={p3Auto} onChange={e=>setP3Auto(e.target.checked)} style={{accentColor:"#14B8A6",width:14,height:14}}/>
                      <div><div style={{fontSize:11,fontWeight:700,color:"#F1F5F9"}}>Auto-Route (Recommended)</div><div style={{fontSize:9,color:"#5A6480"}}>AI analyses task description and selects the best escalation chain automatically</div></div>
                    </label>
                    {p3Auto&&p3Task.trim()&&(()=>{const detected=autoRoute(p3Task);const ch=CHAINS[detected];return<div style={{marginTop:8,padding:"5px 8px",background:ch.color+"08",borderRadius:4,fontSize:9,color:ch.color,fontWeight:600}}>Detected: {ch.ic} {ch.label}</div>;})()}
                  </div>
                  {!p3Auto&&(<div style={{marginBottom:10}}><label style={S.lbl}>Select Chain Manually</label><select style={{...S.inp,padding:"8px"}} value={p3Cat} onChange={e=>setP3Cat(e.target.value)}>{Object.entries(CHAINS).map(([k,ch])=><option key={k} value={k} style={{background:"#0a0e1a"}}>{ch.ic} {ch.label}</option>)}</select></div>)}
                  <div style={{marginBottom:10}}><label style={S.lbl}>Priority</label><div style={{display:"flex",gap:5}}>{[["high","High","#EF4444"],["medium","Medium","#F59E0B"],["low","Low","#10B981"]].map(([p,lb,c])=>(<button key={p} onClick={()=>setP3Pri(p)} style={{flex:1,padding:"7px",borderRadius:5,fontSize:10,fontWeight:600,border:"1px solid "+(p3Pri===p?c+"66":"#1a2030"),background:p3Pri===p?c+"10":"transparent",color:p3Pri===p?c:"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{lb}</button>))}</div></div>
                  <label style={S.lbl}>Task Description</label>
                  <div style={{display:"flex",gap:4,alignItems:"flex-start",marginBottom:10}}>
                    <textarea style={{...S.inp,flex:1,minHeight:80,resize:"vertical"}} value={p3Task} onChange={e=>setP3Task(e.target.value)} placeholder="Describe the task in detail…"/>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}><LangPick value={vLang} onChange={vl=>{setVLang(vl);sv("cos-vl",vl);}}/><MicButton lang={vLang} onResult={t=>setP3Task(prev=>(prev?prev+" ":"")+t)} openaiKey={providerKey(keys,"openai")||undefined}/></div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>{if(!p3Task.trim())return;enqueue(p3Task,p3Cat,p3Pri,p3Auto);setP3Task("");setP3View("dashboard");}} disabled={!p3Task.trim()} style={{...S.pBtn,marginTop:0,flex:1,opacity:p3Task.trim()?1:0.3}}>Add to Queue</button>
                    <button onClick={()=>{if(!p3Task.trim())return;enqueue(p3Task,p3Cat,p3Pri,p3Auto);setP3Task("");if(!qRunning)runQueue();setP3View("running");}} disabled={!p3Task.trim()||qRunning} style={{...S.pBtn,marginTop:0,flex:1,background:"linear-gradient(135deg,#14B8A6,#3B82F6)",opacity:p3Task.trim()&&!qRunning?1:0.3}}>Add and Run Now</button>
                  </div>
                </div>
              )}
              {p3View==="running"&&(
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#F1F5F9",marginBottom:6}}>Live Chain Processing</div>
                  {!qRunning&&!p3Running?(<div style={{textAlign:"center",padding:"30px",color:"#5A6480"}}><div style={{fontSize:28,marginBottom:8}}>✅</div><div style={{fontSize:12}}>No tasks currently running.</div><button onClick={()=>setP3View("dashboard")} style={{...S.pBtn,width:"auto",padding:"8px 18px",marginTop:14,fontSize:12}}>Back to Dashboard</button></div>):(
                    p3Running&&(
                      <div>
                        <div style={{background:"rgba(20,184,166,0.06)",border:"1px solid rgba(20,184,166,0.2)",borderRadius:8,padding:"12px",marginBottom:12}}>
                          <div style={{fontSize:11,fontWeight:700,color:"#14B8A6",marginBottom:3}}>Currently Processing</div>
                          <div style={{fontSize:11,color:"#F1F5F9",marginBottom:4}}>"{p3Running.task}"</div>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <div style={{flex:1,background:"#1a2030",borderRadius:20,height:4}}><div style={{background:"linear-gradient(90deg,#14B8A6,#3B82F6)",height:"100%",borderRadius:20,width:((p3Running.currentLevel||0)/(CHAINS[p3Running.category]?.chain.length||1)*100)+"%",transition:"width 0.5s"}}/></div>
                            <span style={{fontSize:9,color:"#5A6480",whiteSpace:"nowrap"}}>{p3Running.currentLevel||0}/{CHAINS[p3Running.category]?.chain.length||0} levels</span>
                          </div>
                          {p3Phase&&<div style={{fontSize:10,color:"#14B8A6",marginTop:6,display:"flex",alignItems:"center",gap:4}}><span style={{width:5,height:5,borderRadius:"50%",background:"#14B8A6",display:"inline-block",animation:"pulse 1s infinite"}}/>{p3Phase}</div>}
                        </div>
                        {(()=>{const liveTask=tQRef.current.find(t=>t.id===p3Running.id)||p3Running;return liveTask.steps.map((step,i)=>(
                          <div key={i} style={{marginBottom:8,animation:"fadeIn 0.3s"}}>
                            <div style={{background:"#131825",borderRadius:7,padding:"10px 12px",borderLeft:"3px solid "+step.role.dc}}>
                              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:4}}><span style={{fontSize:14}}>{step.role.ic}</span><div style={{flex:1}}><div style={{fontSize:10,fontWeight:700,color:step.role.dc}}>{step.role.t} — Level {step.level}</div></div>{step.isLast&&<span style={{fontSize:8,background:"rgba(245,158,11,0.1)",color:"#F59E0B",padding:"2px 7px",borderRadius:8,fontWeight:700}}>FINAL</span>}<button onClick={()=>cp(step.output)} style={S.hBtn}>Copy</button></div>
                              <div style={{fontSize:10,lineHeight:1.65,color:"#A0AAC0",maxHeight:200,overflowY:"auto"}}><Md text={step.output} ac={step.role.dc}/></div>
                            </div>
                            {i<liveTask.steps.length-1&&<div style={{display:"flex",justifyContent:"center",margin:"3px 0"}}><span style={{fontSize:9,color:"#3A4060"}}>escalated ↓</span></div>}
                          </div>
                        ));})()}
                      </div>
                    )
                  )}
                </div>
              )}
              {p3View==="completed"&&(
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:"#F1F5F9",marginBottom:6}}>Tasks Awaiting Your Approval</div>
                  {tQueue.filter(t=>t.status===TS.REVIEWING).length===0?(<div style={{textAlign:"center",padding:"30px",color:"#5A6480"}}><div style={{fontSize:28,marginBottom:8}}>🎉</div><div style={{fontSize:12}}>No tasks pending review.</div></div>):(
                    tQueue.filter(t=>t.status===TS.REVIEWING).map(t=>(
                      <div key={t.id} style={{marginBottom:14}}>
                        <div style={{background:"linear-gradient(135deg,rgba(139,92,246,0.06),rgba(20,184,166,0.04))",border:"1px solid rgba(139,92,246,0.25)",borderRadius:9,padding:"12px 14px",marginBottom:4}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                            <span style={{fontSize:16}}>{CHAINS[t.category]?.ic||"⚡"}</span>
                            <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:"#F1F5F9"}}>{t.chainLabel}</div><div style={{fontSize:9,color:"#5A6480"}}>{t.steps.length} levels · {t.completedAt?new Date(t.completedAt).toLocaleString():""}</div></div>
                            <span style={{fontSize:8,padding:"2px 8px",borderRadius:10,background:"rgba(139,92,246,0.12)",color:"#8B5CF6",fontWeight:700}}>AWAITING APPROVAL</span>
                          </div>
                          <div style={{fontSize:10,color:"#A0AAC0",fontStyle:"italic",marginBottom:10}}>"{t.task}"</div>
                          {t.finalOutput&&(<div style={{background:"#0a0e1a",borderRadius:6,padding:"10px",marginBottom:10,maxHeight:300,overflowY:"auto",border:"1px solid #1a2030"}}><div style={{fontSize:9,fontWeight:700,color:"#8B5CF6",marginBottom:5,textTransform:"uppercase",letterSpacing:0.8}}>Final Output — {t.steps[t.steps.length-1]?.role.t}</div><div style={{fontSize:10,lineHeight:1.65,color:"#A0AAC0"}}><Md text={t.finalOutput} ac="#8B5CF6"/></div></div>)}
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>approveQ(t.id)} style={{flex:1,background:"#10B981",color:"#fff",border:"none",borderRadius:6,padding:"9px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Approve</button>
                            <button onClick={()=>{const note=prompt("Reason for rejection?");rejectQ(t.id,note||"");}} style={{flex:1,background:"transparent",color:"#EF4444",border:"1px solid #EF444444",borderRadius:6,padding:"9px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Reject</button>
                            <button onClick={()=>dlFile("Task-"+t.id+".md",t.steps.map(s=>"## Level "+s.level+": "+s.role.t+"\n"+s.output).join("\n\n---\n\n"),"text/markdown")} style={{...S.hBtn,padding:"8px 10px"}}>Export</button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {p3View==="completed_arc"&&(
                <div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div><div style={{fontSize:13,fontWeight:800,color:"#F1F5F9"}}>Task Archive</div><p style={{fontSize:10,color:"#5A6480"}}>{tQueue.filter(t=>[TS.APPROVED,TS.REJECTED].includes(t.status)).length} tasks</p></div>
                    {tQueue.filter(t=>[TS.APPROVED,TS.REJECTED].includes(t.status)).length>0&&(<button onClick={()=>{if(confirm("Clear archive?")){const updated=tQueue.filter(t=>![TS.APPROVED,TS.REJECTED].includes(t.status));tQRef.current=updated;setTQueue(updated);sv("cos-tq",updated);}}} style={{...S.hBtn,color:"#EF4444",borderColor:"#EF444433"}}>Clear</button>)}
                  </div>
                  {tQueue.filter(t=>[TS.APPROVED,TS.REJECTED].includes(t.status)).length===0?(<div style={{textAlign:"center",padding:"30px",color:"#5A6480"}}><div style={{fontSize:28,marginBottom:8}}>📁</div><div style={{fontSize:12}}>Archive is empty.</div></div>):(
                    tQueue.filter(t=>[TS.APPROVED,TS.REJECTED].includes(t.status)).slice().reverse().map(t=>(
                      <div key={t.id} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:7,padding:"10px 12px",marginBottom:6}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:7,marginBottom:5}}>
                          <span style={{fontSize:14}}>{CHAINS[t.category]?.ic||"⚡"}</span>
                          <div style={{flex:1,minWidth:0}}><div style={{fontSize:10,fontWeight:600,color:"#F1F5F9",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>"{t.task.slice(0,60)}{t.task.length>60?"…":""}"</div><div style={{fontSize:8,color:"#5A6480",marginTop:1}}>{t.chainLabel} · {t.steps.length} levels</div>{t.rejectionNote&&<div style={{fontSize:8,color:"#EF4444",marginTop:2}}>{t.rejectionNote}</div>}</div>
                          <span style={{fontSize:8,padding:"2px 7px",borderRadius:8,fontWeight:700,background:t.status===TS.APPROVED?"rgba(16,185,129,0.12)":"rgba(239,68,68,0.1)",color:t.status===TS.APPROVED?"#10B981":"#EF4444"}}>{t.status===TS.APPROVED?"Approved":"Rejected"}</span>
                        </div>
                        <div style={{display:"flex",gap:4}}>
                          <button onClick={()=>dlFile("Task-"+t.id+".md",t.steps.map(s=>"## Level "+s.level+": "+s.role.t+"\n"+s.output).join("\n\n---\n\n"),"text/markdown")} style={{...S.hBtn,flex:1,textAlign:"center"}}>Export</button>
                          {t.status===TS.REJECTED&&<button onClick={()=>requeueT(t.id)} style={{...S.hBtn,flex:1,textAlign:"center",color:"#14B8A6",borderColor:"#14B8A633"}}>Requeue</button>}
                          <button onClick={()=>deleteT(t.id)} style={{...S.hBtn,color:"#EF4444",borderColor:"#EF444433"}}>×</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* GENERAL LEDGER */}
{view==="ledger"&&(
  <Ledger cur={cur} entries={ledgerEntries} setEntries={setLedgerEntries} customAccounts={customAccounts} setCustomAccounts={setCustomAccounts} sv={sv} S={S} showToast={showToast} ask={ask} MicButton={MicButtonWithKey} vLang={vLang}/>
)}

{view==="finance"&&(
  <FinanceSuite curSym={cur.sym} ask={(s,m,t)=>ask(s,m,t)} showToast={showToast}/>
)}

{view==="jarvis"&&hasJarvisAccess&&(
  // SAFE SWITCH: experimental only ever renders for the owner, regardless
  // of jarvisMode's value — a stray flag flip can never expose an
  // unfinished build to anyone else with JARVIS access. And even for the
  // owner, any render failure inside JarvisLab falls back to the real,
  // proven Jarvis component automatically via ErrorBoundary's fallback —
  // experimental code can misbehave without ever taking JARVIS itself down.
  jarvisMode==="experimental"&&me.role==="super_admin"
    ? <ErrorBoundary fallback={<Jarvis ask={askDirect} isOwner={true} availableProviders={wsProviders}/>}>
        <JarvisLab ask={askDirect} askWithTools={askJarvisWithTools} isOwner={true} availableProviders={wsProviders} ledgerEntries={ledgerEntries} gatewayBaseUrl="/api/jarvis"/>
      </ErrorBoundary>
    : <Jarvis ask={askDirect} isOwner={me.role==="super_admin"} availableProviders={wsProviders}/>
)}

{view==="home"&&(
  <CommandCenter co={co} curSym={cur.sym} ledgerEntries={ledgerEntries} workflows={workflows} tQueue={tQueue} brSessions={brSessions} setView={setView}/>
)}

        {/* PULSE AGENTIC */}
{view==="dispatch"&&<div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}><div style={{display:"flex",gap:6,padding:"8px 14px",borderBottom:"1px solid #14192a",background:"#0c1120",flexShrink:0}}>{[["dispatch","📡","Dispatch"],["servicenow","🎫","ServiceNow"],["concur","🧾","Concur Audit"],["email","📧","Email"]].map(([id,ic,lb])=><button key={id} onClick={()=>setPulseTab(id)} style={{padding:"6px 14px",borderRadius:6,border:"1px solid "+(pulseTab===id?"#14B8A6":"#1a2030"),background:pulseTab===id?"rgba(20,184,166,0.08)":"transparent",color:pulseTab===id?"#14B8A6":"#5A6480",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{ic} {lb}</button>)}</div><div style={{flex:1,overflow:"auto"}}>{pulseTab==="dispatch"&&<Dispatch templates={dispatchTemplates} setTemplates={setDispatchTemplates} sv={sv} S={S} showToast={showToast} ask={ask} askVision={askVision} MicButton={MicButtonWithKey} vLang={vLang}/>}{pulseTab!=="dispatch"&&<PulseGovernance callAI={(prompt)=>ask("You are a governance analyst.",[{role:"user",content:prompt}],4000)} askVision={askVision} companyName={co.name} defaultModule={pulseTab}/>}</div></div>}

        {/* ACTION TRACKER */}
{view==="actions"&&(
  <ActionTracker items={actionItems} setItems={setActionItems} sv={sv} S={S} showToast={showToast} AR={AR}/>
)}

        {/* COMPLIANCE & CONTROLS - SOX / COSO / Companies Act IFC workbench (deterministic) */}
        {/* SPEND GOVERNANCE & FINANCIAL CONTROL - teacher, executor, error checker, guide */}
        {/* SHEETS - Excel-compatible spreadsheet with approve-before-apply AI automation (loaded on demand) */}
        {view==="sheets"&&(
          <div style={{flex:1,display:"flex",flexDirection:"column",minHeight:0,overflow:"hidden"}}>
            <Suspense fallback={<div style={{padding:24,color:"var(--oiq-muted)"}}>Loading Sheets…</div>}>
              <SheetsView ask={ask} showToast={showToast} ledgerEntries={ledgerEntries} accountName={(code:string)=>{try{const a=getAllAccounts(customAccounts).find((x:any)=>x.code===code);return a?a.name:code;}catch{return code;}}} boardroomCurrent={brCur} location={co.location||""}/>
            </Suspense>
          </div>
        )}
        {view==="spendgov"&&(
          <div style={{flex:1,overflowY:"auto"}}><SpendGovernanceView/></div>
        )}
        {view==="compliance"&&(
          <div style={{flex:1,overflowY:"auto"}}><ComplianceView company={co.name||""} showToast={showToast} boardroomSessions={brSessions} boardroomCurrent={brCur} location={co.location||""} onAskBoardroom={(q:string)=>{setBrQ(q);setNTab("boardroom");setView("nerve");try{showToast("Question placed in the AI Boardroom. Review it and press Run when ready.","info");}catch{}}}/></div>
        )}
        {/* COST ARCHITECTURE */}
        {view==="costarch"&&(
          <div style={{flex:1,overflowY:"auto"}}>
            {/* THE MISSING LINK: Cost Architecture never received a way to
                check for an OpenAI key, so its voice input could only ever
                be the free browser engine - confirmed and fixed. Same
                lookup already used by Workspace and Live Boardroom. */}
            {/* THE CONFIRMED ROOT CAUSE: 8000 tokens is not enough for a
                genuinely complex product - a laptop's real component list,
                sub-assemblies and suppliers need far more room than a
                pickle jar's does. A truncated response fails to parse as
                JSON, every real research attempt falls through, and the
                system correctly (by design) falls back to a generic,
                no-AI placeholder template - which is exactly what looked
                like "not working." Raised to give complex products enough
                space to come back as a complete, valid response. */}
            <CostArchitecture showToast={showToast} companyName={co.name} onDiagnosis={publishCostDiagnosis} callAI={(prompt,useWebSearch)=>ask("You are a senior cost engineering and unit economics researcher. Return ONLY the exact JSON structure requested. No commentary, no markdown fences.",[{role:"user",content:prompt}],16000,!!useWebSearch)}
              getProviderKey={(id:string)=>providerKey(keys,id)||undefined}/>
          </div>
        )}

        {/* DATA HUB */}
        {view==="data"&&(
          <div style={{flex:1,padding:"14px 18px",overflowY:"auto"}}>
            <h2 style={{fontSize:15,fontWeight:800,color:"#F1F5F9",marginBottom:2}}>Company Data Hub</h2>
            <p style={{fontSize:10,color:"#5A6480",marginBottom:10}}>All agents use this data. Currency: <strong style={{color:"#14B8A6"}}>{cur.sym} {cur.code}</strong></p>
            <div style={{display:"flex",gap:4,marginBottom:10,flexWrap:"wrap"}}>
              <input style={{...S.inp,width:190}} placeholder="Label (e.g. Monthly Revenue)" value={dataF.k} onChange={e=>setDataF({...dataF,k:e.target.value})} onKeyDown={e=>e.key==="Enter"&&addD()}/>
              <input style={{...S.inp,flex:1,minWidth:120}} placeholder={"Value (e.g. "+cur.sym+"5,00,000)"} value={dataF.v} onChange={e=>setDataF({...dataF,v:e.target.value})} onKeyDown={e=>e.key==="Enter"&&addD()}/>
              <LangPick value={vLang} onChange={vl=>{setVLang(vl);sv("cos-vl",vl);}}/>
              <MicButton lang={vLang} onResult={t=>setDataF(p=>({...p,v:(p.v?p.v+" ":"")+t}))} openaiKey={providerKey(keys,"openai")||undefined}/>
              <button onClick={addD} style={{...S.pBtn,padding:"6px 14px",marginTop:0,fontSize:11,width:"auto"}}>+ Add</button>
            </div>
{(
  <div style={{marginBottom:14}}>
    {[
      {cat:"Financial - Income",items:["Monthly Revenue","Monthly Operating Costs","COGS (Cost of Goods Sold)","Marketing Budget (Quarterly)"]},
      {cat:"Financial - Balance Sheet",items:["Current Assets","Current Liabilities","Total Debt","Total Equity","Total Assets","Inventory","Accounts Receivable","Accounts Payable","Cash in Bank"]},
      {cat:"Equity & Cap Table",items:["Total Shares Outstanding","Founder Shareholding %","ESOP Pool %","Current Valuation"]},
      {cat:"Customer Metrics",items:["Total Customers (Current)","Total Customers (Previous Period)","New Customers This Period","Churned Customers This Period","Customer Acquisition Cost (CAC)","Average Revenue Per User (ARPU)"]},
      {cat:"Operations",items:["Team Size (Total)","Key Vendors/Suppliers","Monthly Burn Rate"]},
      {cat:"Market",items:["Target Markets/Cities","Primary Customer Segment","Top 3 Competitors"]},
      {cat:"Product",items:["Core Product/Service","Pricing (per unit/service)","Unique Selling Proposition"]},
    ].map(grp=>(
      <div key={grp.cat} style={{marginBottom:10}}>
        <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:4}}>{grp.cat}</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
          {grp.items.map(k=>(
            <button key={k} onClick={()=>setDataF({k,v:""})} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:5,padding:"8px 10px",cursor:"pointer",textAlign:"left",fontFamily:"Manrope,sans-serif"}}><div style={{fontSize:10,fontWeight:600,color:"#14B8A6"}}>+ {k}</div></button>
          ))}
        </div>
        </div>
      ))}
    </div>
      )}

            {Object.entries(compData).map(([k,v])=>(
  <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 10px",background:"#131825",border:"1px solid #1a2030",borderRadius:5,marginBottom:3}}>
    <div style={{flex:1,cursor:"pointer"}} onClick={()=>setDataF({k,v})}><span style={{fontSize:11,fontWeight:600,color:"#A0AAC0"}}>{k}:</span> <span style={{fontSize:11,color:"#F1F5F9"}}>{v}</span></div>
    <div style={{display:"flex",gap:4}}>
      <button onClick={()=>setDataF({k,v})} style={{background:"none",border:"none",color:"#5A6480",fontSize:10,cursor:"pointer"}}>Edit</button>
      <button onClick={()=>delD(k)} style={{background:"none",border:"none",color:"#3A4060",fontSize:10,cursor:"pointer"}}>×</button>
    </div>
  </div>
))}
          </div>
        )}

        {view==="funding"&&<FundingIntelligence co={co} compData={compData} ask={ask}/>}
        {/* THE EXACT BUG THE OWNER FOUND: "workspace" and "liveboard" were
            hard-excluded from this check, so plan_features rows for them were
            silently ignored - a Free-plan restriction already configured in
            the Admin Console (module_workspace = false) has never actually
            been enforced, and Live AI Boardroom had no gate at all. Removed:
            both now go through canUse() exactly like every other module. */}
        {!canUse(view)&&view!=="admin"&&view!=="tokens"&&view!=="account"&&(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
            <div style={{textAlign:"center",maxWidth:400}}>
              <div style={{fontSize:34,marginBottom:10}}>✨</div>
              <div style={{fontSize:14,fontWeight:800,color:"var(--oiq-ink)",marginBottom:6}}>Not included in your plan</div>
              <div style={{fontSize:11,color:"#5A6480",lineHeight:1.6,marginBottom:14}}>
                You are on the <b style={{color:"#14B8A6"}}>{ent?.plan_name||"Free"}</b> plan.
                This module is available on a higher plan. Use Support to ask about upgrading.
              </div>
              <button onClick={()=>setView("nerve")} style={{...S.hBtn,padding:"8px 14px"}}>Back</button>
            </div>
          </div>)}
        {view==="workspace"&&<Workspace ask={askDirect} availableProviders={wsProviders}
          canUse={canUse} showToast={showToast}
          generateImage={wsGenerateImage} imageProviders={wsImageProviders}
          nvidiaUserKey={(keys.nvidia&&keys.nvidia!=="nvidia"&&keys.nvidia.startsWith("nvapi-"))?keys.nvidia:""}
          getProviderKey={(id:string)=>providerKey(keys,id)||undefined}/>}
        {/* PHASE 2 — DECISION -> TASK TRACKER.
            Reuses the SAME extraction pipeline every other module already
            uses (extractActionItems -> the AI turns text into structured
            items -> the owner reviews and confirms via ExtractReviewModal
            before anything is added). No new task system, exactly as
            instructed - the Live Boardroom is simply a new SOURCE feeding
            infrastructure that already exists. */}
        {view==="liveboard"&&<LiveBoardroom ask={askDirect} AR={AR} buildIdentity={lbBuildIdentity}
          routerProviderModel={lbRoute} runResearch={lbRunResearch} showToast={showToast}
          onDecisionAccepted={(content:string,label:string)=>extractActionItems("liveboard",label,content)}
          getProviderKey={(id:string)=>providerKey(keys,id)||undefined}/>}
        {view==="account"&&<MyAccount onOpenHelp={()=>setShowHelp(true)}/>}
        {view==="admin"&&(isAdmin
          ?<AdminConsole onClose={()=>setView("nerve")}/>
          :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
             <div style={{textAlign:"center",maxWidth:380}}>
               <div style={{fontSize:34,marginBottom:10}}>\uD83D\uDD12</div>
               <div style={{fontSize:14,fontWeight:800,color:"var(--oiq-ink)",marginBottom:6}}>Restricted</div>
               <div style={{fontSize:11,color:"#5A6480",lineHeight:1.6}}>This area is for administrators. If you need something changed on your account, use Support to send a message.</div>
             </div>
           </div>)}
        {view==="tokens"&&(me.role==="super_admin"
          ?<TokenAnalytics defP={defP} keys={keys} me={me}/>
          :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
             <div style={{textAlign:"center",maxWidth:380}}>
               <div style={{fontSize:34,marginBottom:10}}>🔒</div>
               <div style={{fontSize:14,fontWeight:800,color:"var(--oiq-ink)",marginBottom:6}}>Restricted</div>
               <div style={{fontSize:11.5,color:"var(--oiq-muted)",lineHeight:1.7}}>Token Analytics shows API spend across every account on this workspace. It is available to workspace owners only.</div>
             </div>
           </div>)}
        {view==="agents"&&(
          <AIAgents
            co={co}
            compData={compData}
            keys={keys}
            defP={defP}
            ask={ask}
            askImage={askImage}
            askVideo={askVideo}
            showToast={showToast}
            dlFile={dlFile}
            ensureJsPDF={ensureJsPDF}
            ensureXLSX={ensureXLSX}
            ensurePptx={ensurePptx}
            parseSections={parseSections}
            stripMd={stripMd}
            ensureJSZip={ensureJSZip}
            brSessions={brSessions}
            setBrSessions={setBrSessions}
            actionItems={actionItems}
            setActionItems={setActionItems}
            sv={sv}
          />
        )}
        {view==="agentic"&&(
          <AgenticWorkflows
            co={co} compData={compData} keys={keys} defP={defP} ask={ask}
            askImage={askImage} askVideo={askVideo}
            showToast={showToast} dlFile={dlFile}
            ensureJsPDF={ensureJsPDF} ensureXLSX={ensureXLSX}
            ensurePptx={ensurePptx} ensureJSZip={ensureJSZip}
            parseSections={parseSections} stripMd={stripMd}
            actionItems={actionItems} setActionItems={setActionItems}
            brSessions={brSessions} setBrSessions={setBrSessions}
            sv={sv}
          />
        )}
        {view==="servicedesk"&&<ServiceDesk co={co} compData={compData} ask={ask} supabase={supabase}/>}
        {view==="studio"&&(
          <div style={{flex:1,padding:"14px 18px",overflowY:"auto"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <span style={{fontSize:20}}>🎨</span>
              <div><h2 style={{fontSize:15,fontWeight:800,color:"#F1F5F9"}}>Presentation Studio</h2><p style={{fontSize:10,color:"#5A6480"}}>The Presentation Architect synthesizes your entire workspace into investor-grade decks and reports.</p></div>
            </div>
            <div style={{background:"linear-gradient(135deg,rgba(168,85,247,0.06),rgba(20,184,166,0.04))",border:"1px solid rgba(168,85,247,0.2)",borderRadius:9,padding:"12px 14px",marginTop:10,marginBottom:14}}>
              <div style={{fontSize:11,color:"#A0AAC0",lineHeight:1.7}}>Choose a format, select which parts of your workspace to include, and the Architect builds a structured narrative — then renders it to a downloadable PDF or PowerPoint. You can also chat with the Architect directly from the <strong style={{color:"#A855F7"}}>Presentation Studio</strong> department in the sidebar.</div>
              <button onClick={()=>{setSelRole("pres_arch");setView("chat");}} style={{...S.hBtn,marginTop:8,color:"#A855F7",borderColor:"#A855F733"}}>💬 Chat with Presentation Architect</button>
            </div>
            <div style={{display:"flex",gap:6,marginBottom:14}}>
              <button onClick={()=>{setShowExport(true);setExpMode("pptx");}} style={{...S.pBtn,marginTop:0,background:"linear-gradient(135deg,#A855F7,#6366F1)",flex:1}}>📊 Generate PowerPoint</button>
              <button onClick={()=>{setShowExport(true);setExpMode("pdf");}} style={{...S.pBtn,marginTop:0,background:"linear-gradient(135deg,#3B82F6,#14B8A6)",flex:1}}>📄 Generate PDF</button>
            </div>
            <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Workspace Knowledge Available</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
              {[["💬 Conversations",Object.values(chats).filter(c=>c?.length).length,"#14B8A6"],["🏛️ Boardroom",brSessions.length,"#3B82F6"],["⚡ Workflows",workflows.length,"#10B981"],["🤖 Auto Tasks",tQueue.filter(t=>t.finalOutput).length,"#F59E0B"],["⏳ Time Machine",tmRes?1:0,"#8B5CF6"],["📊 Autopilot",apRes?1:0,"#A855F7"]].map(([lb,n,c])=>(
                <div key={lb} style={{background:"#131825",border:"1px solid "+c+"22",borderRadius:8,padding:"10px",textAlign:"center"}}><div style={{fontSize:18,fontWeight:900,color:c}}>{n}</div><div style={{fontSize:8,color:"#5A6480",fontWeight:600}}>{lb}</div></div>
              ))}
            </div>
            <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Output Formats</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div style={{background:"#131825",border:"1px solid #1a2030",borderRadius:8,padding:"12px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#3B82F6",marginBottom:6}}>📄 PDF Reports</div>
                {PDF_TYPES.map(t=><div key={t.id} style={{fontSize:10,color:"#8892B0",padding:"2px 0"}}>{t.ic} {t.label} — <span style={{color:"#5A6480"}}>{t.desc}</span></div>)}
              </div>
              <div style={{background:"#131825",border:"1px solid #1a2030",borderRadius:8,padding:"12px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#A855F7",marginBottom:6}}>📊 PowerPoint Decks</div>
                {PPT_TYPES.map(t=><div key={t.id} style={{fontSize:10,color:"#8892B0",padding:"2px 0"}}>{t.ic} {t.label}</div>)}
              </div>
            </div>
          </div>
        )}

        {/* CHAT — no role selected */}
        {view==="chat"&&!selRole&&(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"0 20px"}}>
              <span style={{fontSize:36,color:"#14B8A6"}}>◆</span>
              <h1 style={{fontSize:18,fontWeight:800,color:"#F1F5F9",marginTop:8,marginBottom:2}}>{co.name}</h1>
              <p style={{fontSize:11,color:"#5A6480",marginBottom:12}}>{co.location} · {cur.code} · {co.industry} · {STAGES.find(st=>st.id===co.stage)?.l}</p>
              <p style={{fontSize:11,color:"#8892B0",marginBottom:14}}>Select any executive from the sidebar, or start with the C-suite:</p>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5,maxWidth:440}}>
                {CS.map(r=><button key={r.id} onClick={()=>setSelRole(r.id)} style={{background:"#131825",border:"1px solid #1a2030",borderRadius:8,padding:"12px 8px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,fontFamily:"Manrope,sans-serif"}}><span style={{fontSize:20}}>{r.ic}</span><span style={{fontSize:9,fontWeight:700,color:r.dc}}>{r.t}</span></button>)}
              </div>
            </div>
          </div>
        )}

        {/* CHAT — with role */}
        {view==="chat"&&selRole&&curRole&&(
          <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 12px",borderBottom:"1px solid #14192a",background:"#0c1120"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>{curRole.ic}</span>
                <div><div style={{fontWeight:700,fontSize:12,color:curRole.dc}}>{curRole.f}</div><div style={{fontSize:8,color:"#3A4060"}}>{curRole.dl} · {co.name} · {co.location} · {cur.sym}{co.currency} · 60s timeout</div></div>
              </div>
              <div style={{display:"flex",gap:4}}>
                {curMsgs.length>0&&<>
                  <ReadAloudButton text={curMsgs.filter(m=>m.role==="assistant").map(m=>stripMd(m.content)).join("\n\n")} id="chat-conversation" />
                  <button onClick={()=>quickExport("pdf","detailed",curRole.t+" Consultation",curMsgs.map(m=>(m.role==="user"?"## Question\n":"## "+curRole.t+"\n")+stripMd(m.content)).join("\n\n"))} style={S.hBtn} title="Export conversation as PDF">📄 PDF</button>
                  <button onClick={()=>quickExport("pptx","briefing",curRole.t+" Briefing",curMsgs.filter(m=>m.role==="assistant").map(m=>m.content).join("\n\n"))} style={S.hBtn} title="Export conversation as PowerPoint">📊 PPT</button>
                  <button onClick={()=>dlFile("Chat-"+curRole.t+"-"+Date.now()+".md",curMsgs.map(m=>"### "+(m.role==="user"?"You":curRole.t)+"\n"+m.content).join("\n\n---\n\n"),"text/markdown")} style={S.hBtn}>MD</button>
                </>}
                <button onClick={()=>{if(confirm("Clear chat with "+curRole.t+"?")){const u={...chats};delete u[selRole];setChats(u);sv("cos-ch",u);}}} style={S.hBtn}>Clear</button>
              </div>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"8px 12px"}}>
              {!curMsgs.length&&(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",animation:"fadeIn 0.3s ease",padding:"20px"}}>
                  <span style={{fontSize:32,marginBottom:4}}>{curRole.ic}</span>
                  <div style={{fontWeight:700,fontSize:14,color:curRole.dc,marginBottom:3}}>{curRole.t} Ready</div>
                  <div style={{fontSize:9,color:"#5A6480",marginBottom:4,textAlign:"center",maxWidth:340,lineHeight:1.5}}>{EP[curRole.id]?.b?.split("\n")[0]||"World-class "+curRole.f}</div>
                  <div style={{fontSize:10,color:"#5A6480",marginBottom:12}}>Answering in {cur.sym}{co.currency} · {co.location} context</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,maxWidth:400,width:"100%"}}>
                    {curRole.qa?.map((a,i)=><button key={i} onClick={()=>send(a)} style={S.qaBtn}>{a}</button>)}
                  </div>
                </div>
              )}
              {curMsgs.map((msg,i)=>(
                <div key={i} onMouseEnter={()=>setHoveredMsgIdx(i)} onMouseLeave={()=>setHoveredMsgIdx(h=>h===i?null:h)}
                  style={{marginBottom:8,animation:"fadeIn 0.2s",display:"flex",justifyContent:msg.role==="user"?"flex-end":"flex-start",gap:6,alignItems:"flex-start"}}>
                  {/* THE HOVER ACTION BAR — Teams-style: appears only on the
                      message you're pointing at, sits on the opposite side
                      from the bubble so it never covers the text. */}
                  {hoveredMsgIdx===i&&msg.role!=="user"&&(
                    <div style={{display:"flex",gap:3,alignSelf:"center",opacity:0.9}}>
                      <button onClick={()=>setReplyingTo({role:msg.role,content:msg.content})} title="Reply" style={{background:"#131825",border:"1px solid #232838",borderRadius:5,padding:"4px 6px",cursor:"pointer",fontSize:11}}>↩</button>
                      <button onClick={()=>cp(msg.content)} title="Copy" style={{background:"#131825",border:"1px solid #232838",borderRadius:5,padding:"4px 6px",cursor:"pointer",fontSize:11}}>⧉</button>
                    </div>
                  )}
                  {msg.role==="user"
                    ?<div style={{...S.uMsg,maxWidth:"78%",minWidth:0}}><div style={{...S.mLbl,textAlign:"right"}}>YOU</div>
                        {msg.attachment&&<img src={msg.attachment.dataUrl} style={{maxWidth:180,maxHeight:180,borderRadius:8,marginBottom:6,display:"block",marginLeft:"auto"}}/>}
                        <div style={{fontSize:11,lineHeight:1.65,color:"#A0AAC0",whiteSpace:"pre-wrap"}}>{msg.content}</div></div>
                    :<div style={{...S.aMsg,maxWidth:"78%",minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><div style={{...S.mLbl,color:curRole.dc}}>{curRole.ic} {curRole.t}</div><button onClick={()=>cp(msg.content)} style={{background:"none",border:"none",color:"#3A4060",fontSize:8,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Copy</button></div>
                      <div style={{fontSize:11,lineHeight:1.7,color:"#A0AAC0"}}><Md text={msg.content} ac={curRole.dc}/></div>
                      {i===curMsgs.length-1&&(
                        <div style={{marginTop:8,paddingTop:6,borderTop:"1px dashed #1a2030",display:"flex",flexWrap:"wrap",gap:3,alignItems:"center"}}>
                          <span style={{fontSize:8,color:"#5A6480",marginRight:2}}>Consult:</span>
                          {CS.filter(c=>c.id!==curRole.id).map(c=><button key={c.id} onClick={()=>consultRole(c.id,curMsgs[i-1]?.content||"question",msg.content)} style={{background:"transparent",border:"1px solid "+c.dc+"33",color:c.dc,padding:"2px 6px",borderRadius:4,fontSize:9,fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{c.ic} {c.t}</button>)}
                        </div>
                      )}
                    </div>
                  }
                </div>
              ))}
              {/* FIX BUG 5: loading skeleton with animation */}
              {loading&&(
                <div style={S.aMsg}>
                  <div style={{...S.mLbl,color:curRole.dc}}>{curRole.ic} {curRole.t}</div>
                  <div style={{display:"flex",gap:4,padding:"3px 0",alignItems:"center"}}>
                    <span style={{...S.dot,animationDelay:"0s"}}/><span style={{...S.dot,animationDelay:"0.15s"}}/><span style={{...S.dot,animationDelay:"0.3s"}}/>
                    <span style={{fontSize:9,color:"#3A4060",marginLeft:6}}>Thinking… (up to 60s)</span>
                  </div>
                </div>
              )}
              {error&&(
                <div style={S.errB}>
                  ⚠️ {error}
                  <div style={{display:"flex",gap:4}}>
                    <button onClick={()=>{const l=curMsgs.filter(m=>m.role==="user").pop();if(l)send(l.content);}} style={S.retBtn}>Retry</button>
                    <button onClick={()=>setError(null)} style={{...S.retBtn,background:"#3A4060"}}>Dismiss</button>
                  </div>
                </div>
              )}
              <div ref={chatEnd}/>
            </div>
            <div style={S.inpA}>
              {/* THE REPLY PREVIEW — Teams-style: quotes what you're
                  replying to, with a clear way to cancel before sending. */}
              {replyingTo&&(
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",marginBottom:6,background:"rgba(255,255,255,0.03)",borderLeft:"2px solid "+curRole.dc,borderRadius:6}}>
                  <span style={{fontSize:9,fontWeight:700,color:curRole.dc}}>↩ Replying to {curRole.t}</span>
                  <span style={{fontSize:10.5,color:"#5A6480",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{replyingTo.content.slice(0,80)}</span>
                  <button onClick={()=>setReplyingTo(null)} style={{background:"none",border:"none",color:"#F87171",cursor:"pointer",fontSize:14}}>×</button>
                </div>
              )}
              {/* THE STAGED ATTACHMENT — shown before sending, exactly like
                  Claude and ChatGPT's own chat windows, with a one-click
                  remove before it's ever sent anywhere. */}
              {pendingAttachment&&(
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",marginBottom:6,background:"rgba(20,184,166,0.06)",border:"1px solid #14B8A633",borderRadius:8}}>
                  <img src={pendingAttachment.dataUrl} style={{width:36,height:36,objectFit:"cover",borderRadius:6}}/>
                  <span style={{fontSize:11,color:"#A0AAC0",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pendingAttachment.name}</span>
                  <button onClick={()=>setPendingAttachment(null)} style={{background:"none",border:"none",color:"#F87171",cursor:"pointer",fontSize:14}}>×</button>
                </div>
              )}
              <div style={S.inpR}>
                <input type="file" id="exec-chat-file-input" accept="image/*" style={{display:"none"}}
                  onChange={e=>{const f=e.target.files?.[0];if(f)handleIncomingFile(f);e.target.value="";}}/>
                <button onClick={()=>document.getElementById("exec-chat-file-input")?.click()}
                  title="Attach an image — paste one directly into the box, or click to browse"
                  style={{background:"none",border:"1px solid #1a2030",borderRadius:6,padding:"4px 8px",color:"#5A6480",cursor:"pointer",fontSize:14,height:28,display:"flex",alignItems:"center",flexShrink:0}}>📎</button>
                <textarea style={S.ta} value={input} onChange={e=>setInput(e.target.value)}
                  onPaste={e=>{
                    // THE ACTUAL "paste a screenshot" FEATURE — the exact
                    // behaviour requested: copy an image anywhere, paste it
                    // straight into the chat box, no separate upload step.
                    const items=e.clipboardData?.items;
                    if(!items)return;
                    for(const item of items){
                      if(item.type.startsWith("image/")){
                        const file=item.getAsFile();
                        if(file){e.preventDefault();handleIncomingFile(file);}
                        break;
                      }
                    }
                  }}
                  onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();if(input.trim()||pendingAttachment){sendWithReply(input||"What's in this image?",pendingAttachment);}}}}
                  placeholder={pendingAttachment?"Ask something about this image… (Enter to send)":"Message "+curRole.t+"… (Enter to send, Shift+Enter for newline, or paste an image)"} rows={1} disabled={loading}/>
                <LangPick value={vLang} onChange={vl=>{setVLang(vl);sv("cos-vl",vl);}}/>
                <button onClick={()=>setSearchMode(v=>!v)} title={searchMode?"Live web search ON — click to turn off":"Turn on live web search for this message"} style={{background:searchMode?"rgba(20,184,166,0.15)":"none",border:"1px solid "+(searchMode?"#14B8A6":"#1a2030"),borderRadius:6,padding:"4px 8px",color:searchMode?"#14B8A6":"#5A6480",cursor:"pointer",fontSize:13,fontFamily:"Manrope,sans-serif",height:28,display:"flex",alignItems:"center",gap:4,flexShrink:0,transition:"all 0.15s"}}>🔍{searchMode&&<span style={{fontSize:9,fontWeight:700}}>LIVE</span>}</button>
                <VoiceEngine send={send} setInput={setInput} lang={vLang} roleColor={curRole?.dc||"#14B8A6"} disabled={loading}/>
                <button onClick={()=>sendWithReply(input||"What's in this image?",pendingAttachment)} disabled={(!input.trim()&&!pendingAttachment)||loading} style={{...S.sBtn,background:curRole.dc,opacity:(input.trim()||pendingAttachment)&&!loading?1:0.2}}>↑</button>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* FEATURE 4 & 5: EXPORT STUDIO MODAL */}
      {showExport&&(
        <div style={S.modalBg} onClick={()=>!expGenerating&&setShowExport(false)}>
          <div style={{...S.modal,maxWidth:560}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <h2 style={{fontSize:16,fontWeight:800,color:"#F1F5F9"}}>🎨 Export Studio</h2>
              <button onClick={()=>!expGenerating&&setShowExport(false)} style={S.iBtn}>×</button>
            </div>
            <div style={{display:"flex",gap:6,marginBottom:14}}>
              <button onClick={()=>setExpMode("pdf")} style={{flex:1,padding:"9px",borderRadius:6,fontSize:12,fontWeight:700,border:"1px solid "+(expMode==="pdf"?"#3B82F6":"#1a2030"),background:expMode==="pdf"?"rgba(59,130,246,0.1)":"#0a0e1a",color:expMode==="pdf"?"#3B82F6":"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>📄 PDF Report</button>
              <button onClick={()=>setExpMode("pptx")} style={{flex:1,padding:"9px",borderRadius:6,fontSize:12,fontWeight:700,border:"1px solid "+(expMode==="pptx"?"#A855F7":"#1a2030"),background:expMode==="pptx"?"rgba(168,85,247,0.1)":"#0a0e1a",color:expMode==="pptx"?"#A855F7":"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>📊 PowerPoint</button>
            </div>
            {expMode==="pptx"&&(
              <div style={{marginBottom:14,padding:"10px 12px",background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:7}}>
                <label style={S.lbl}>Match a Sample PPT's Style (optional)</label>
                <p style={{fontSize:9,color:"#5A6480",marginBottom:8,lineHeight:1.5}}>Upload a .pptx and we'll match its colors, fonts, and slide shape. Nothing else from the file is read or stored.</p>
                <label style={{...S.inp,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#5A6480",fontSize:11,padding:"10px"}}>
                  {expStyleResult?.extracting?"⏳ Reading style…":expStyleResult?.ok?"📎 "+expStyleResult.sourceFileName:"📎 Upload a .pptx to match its style"}
                  <input type="file" accept=".pptx" style={{display:"none"}} onChange={async e=>{
                    const f=e.target.files[0];
                    if(!f)return;
                    setExpStyleResult({extracting:true});
                    setExpStyleResult(await extractPptxStyle(f));
                  }}/>
                </label>
                {expStyleResult?.ok&&(
                  <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                    <div style={{display:"flex",gap:3}}>
                      {Object.values(expStyleResult.colors||{}).slice(0,6).map((c,i)=><span key={i} style={{width:14,height:14,borderRadius:3,background:c,border:"1px solid #1a2030"}}/>)}
                    </div>
                    <span style={{fontSize:9,color:"#10B981"}}>✓ Style captured{expStyleResult.fonts?.heading?" — "+expStyleResult.fonts.heading:""}</span>
                    <button onClick={()=>setExpStyleResult(null)} style={{...S.hBtn,marginLeft:"auto"}}>Remove</button>
                  </div>
                )}
                {expStyleResult?.error&&<div style={{fontSize:9,color:"#EF4444",marginTop:6}}>{expStyleResult.error}</div>}
              </div>
            )}
            <label style={S.lbl}>Type</label>
            <div style={{display:"grid",gridTemplateColumns:expMode==="pdf"?"repeat(2,1fr)":"repeat(2,1fr)",gap:5,marginBottom:12}}>
              {(expMode==="pdf"?PDF_TYPES:PPT_TYPES).map(t=>{const sel=expMode==="pdf"?expDocType===t.id:expPptType===t.id;const c=expMode==="pdf"?"#3B82F6":"#A855F7";return(
                <button key={t.id} onClick={()=>expMode==="pdf"?setExpDocType(t.id):setExpPptType(t.id)} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 10px",borderRadius:6,border:"1px solid "+(sel?c+"66":"#1a2030"),background:sel?c+"0d":"#0a0e1a",cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left"}}>
                  <span style={{fontSize:15}}>{t.ic}</span><div><div style={{fontSize:10,fontWeight:700,color:sel?c:"#A0AAC0"}}>{t.label}</div>{t.desc&&<div style={{fontSize:8,color:"#5A6480"}}>{t.desc}</div>}</div>
                </button>);})}
            </div>
            <div style={{padding:"9px 11px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030",marginBottom:12}}>
              <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer"}}>
                <input type="checkbox" defaultChecked={(()=>{try{return localStorage.getItem("oiq-browser-docs")==="1";}catch{return false;}})()}
                  onChange={e=>{try{localStorage.setItem("oiq-browser-docs",e.target.checked?"1":"0");}catch{}
                    showToast(e.target.checked?"Browser builder ON — NVIDIA can now generate documents":"Browser builder OFF — using the server engine","info");}}
                  style={{marginTop:2,cursor:"pointer"}}/>
                <span>
                  <span style={{fontSize:10,fontWeight:700,color:"#F1F5F9"}}>Build documents in your browser (beta)</span>
                  <span style={{display:"block",fontSize:8.5,color:"#5A6480",marginTop:3,lineHeight:1.5}}>
                    OFF by default. When ON, the document structure is designed in your browser —
                    this lets NVIDIA generate documents and stops your API keys being sent to the
                    render server. When OFF, the proven server engine is used. If exports fail or
                    look worse after enabling it, switch it back off; nothing else changes.
                  </span>
                </span>
              </label>
            </div>
            {/* You asked for a way for users to tell you which provider they
                want next. This files it straight into your Support inbox. */}
            <div style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:6,padding:"9px 11px",marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:700,color:"#F1F5F9",marginBottom:3}}>Want a provider that is not listed?</div>
              <div style={{fontSize:9,color:"#5A6480",lineHeight:1.55,marginBottom:7}}>
                Tell us which one. We review every request — if enough people need it, we add it.
              </div>
              <div style={{display:"flex",gap:6}}>
                <input id="oiq-provider-req" placeholder="e.g. Mistral, Perplexity, Cohere…"
                  style={{...S.inp,flex:1,fontSize:10.5}}/>
                <button style={{...S.hBtn,padding:"7px 12px",whiteSpace:"nowrap"}} onClick={async()=>{
                  const el=document.getElementById("oiq-provider-req") as HTMLInputElement|null;
                  const v=(el?.value||"").trim();
                  if(v.length<2){showToast("Type the provider name first","warning");return;}
                  try{
                    const {data:s}=await supabase.auth.getSession();
                    const uid=s?.session?.user?.id;
                    if(!uid){showToast("Please sign in first","warning");return;}
                    const {data:th,error}=await supabase.from("support_threads")
                      .insert({user_id:uid,subject:"Provider request: "+v.slice(0,80),category:"feedback"})
                      .select().single();
                    if(error)throw error;
                    await supabase.from("support_messages").insert({thread_id:th.id,author_id:uid,
                      body:"Please add support for: "+v+". Sent from Settings, API keys."});
                    if(el)el.value="";
                    showToast("Request sent — thank you","success");
                  }catch(e:any){showToast(e.message||"Could not send","error");}
                }}>Request</button>
              </div>
            </div>
            <label style={S.lbl}>Audience — who is this for?</label>
            <div style={{fontSize:8.5,color:"#5A6480",marginBottom:6,lineHeight:1.5}}>This changes what the document contains, not just its tone. An investor pack leads with market size and unit economics; an operations review leads with steps, owners and dates. Same evidence, different document.</div>
            <select value={expAudience} onChange={e=>setExpAudience(e.target.value)} style={{...S.inp,marginBottom:4,cursor:"pointer"}}>
              <option value="auto">Auto — match the document type</option>
              {AUDIENCE_OPTIONS.map(a=><option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <div style={{fontSize:8,color:"#5A6480",marginBottom:12,lineHeight:1.4}}>
              {expAudience==="auto"
                ?"Will use: "+(DocIQ.AUDIENCE_SPECS[DocIQ.inferAudience(expMode==="pdf"?expDocType:expPptType)]?.label||"General")
                :(AUDIENCE_OPTIONS.find(a=>a.id===expAudience)?.hint||"")}
            </div>
            <label style={S.lbl}>Document Title (optional)</label>
            <input style={{...S.inp,marginBottom:12}} value={expTitle} onChange={e=>setExpTitle(e.target.value)} placeholder={(expMode==="pdf"?PDF_TYPES.find(t=>t.id===expDocType)?.label:PPT_TYPES.find(t=>t.id===expPptType)?.label)+" — "+co.name}/>
            <label style={S.lbl}>Include from Workspace</label>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:5,marginBottom:14}}>
              {[["chats","💬 Conversations",Object.values(chats).filter(c=>c?.length).length],["boardroom","🏛️ Boardroom",brSessions.length],["workflows","⚡ Workflows",workflows.length],["tasks","🤖 Auto Tasks",tQueue.filter(t=>t.finalOutput).length],["timeMachine","⏳ Time Machine",tmRes?1:0],["autopilot","📊 Autopilot",apRes?1:0]].map(([k,lb,n])=>(
                <label key={k} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 9px",background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:5,cursor:"pointer",opacity:n?1:0.45}}>
                  <input type="checkbox" checked={expSources[k]} onChange={e=>setExpSources(p=>({...p,[k]:e.target.checked}))} style={{accentColor:"#14B8A6"}} disabled={!n}/>
                  <span style={{fontSize:10,color:"#A0AAC0",flex:1}}>{lb}</span>
                  <span style={{fontSize:9,color:n?"#14B8A6":"#3A4060",fontWeight:700}}>{n}</span>
                </label>
              ))}
            </div>
            {expStep&&<div style={{fontSize:11,color:"#A855F7",marginBottom:10,display:"flex",alignItems:"center",gap:6}}><span style={{width:6,height:6,borderRadius:"50%",background:"#A855F7",display:"inline-block",animation:"pulse 1s infinite"}}/>{expStep}</div>}
            <button onClick={runExport} disabled={expGenerating} style={{...S.pBtn,marginTop:0,background:expMode==="pdf"?"linear-gradient(135deg,#3B82F6,#14B8A6)":"linear-gradient(135deg,#A855F7,#6366F1)",opacity:expGenerating?0.5:1}}>
              {expGenerating?"Generating…":"Generate "+(expMode==="pdf"?"PDF":"PowerPoint")}
            </button>
            {expSynthesis&&!expGenerating&&(
              <div style={{marginTop:12}}>
                <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",marginBottom:5}}>Synthesized Content Preview</div>
                <div style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:6,padding:"10px",maxHeight:200,overflowY:"auto",fontSize:10,lineHeight:1.6,color:"#A0AAC0"}}><Md text={expSynthesis} ac="#A855F7"/></div>
                <div style={{display:"flex",gap:4,marginTop:6}}>
                  <button onClick={()=>cp(expSynthesis)} style={{...S.hBtn,flex:1,textAlign:"center"}}>Copy Text</button>
                  <button onClick={()=>dlFile("Synthesis-"+Date.now()+".md",expSynthesis,"text/markdown")} style={{...S.hBtn,flex:1,textAlign:"center"}}>Save as Markdown</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FEATURE 2: RESUME BANNER */}
      {resumeInfo&&(
        <div style={{position:"fixed",bottom:20,left:20,zIndex:9998,background:"#131825",border:"1px solid rgba(20,184,166,0.3)",borderLeft:"3px solid #14B8A6",borderRadius:8,padding:"12px 16px",maxWidth:340,boxShadow:"0 4px 24px rgba(0,0,0,0.5)",fontFamily:"Manrope,sans-serif"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#F1F5F9",marginBottom:3}}>Welcome back 👋</div>
          <div style={{fontSize:10,color:"#8892B0",lineHeight:1.6,marginBottom:8}}>It's been {resumeInfo.days} {resumeInfo.days===1?"day":"days"}. Your workspace is right where you left it. You can also load a saved workspace file.</div>
          <div style={{display:"flex",gap:6}}>
            <label style={{...S.hBtn,cursor:"pointer",flex:1,textAlign:"center",color:"#14B8A6",borderColor:"#14B8A633"}}>Load Workspace<input type="file" accept=".json" onChange={e=>e.target.files[0]&&importData(e.target.files[0])} style={{display:"none"}}/></label>
            <button onClick={()=>setResumeInfo(null)} style={{...S.hBtn,flex:1,textAlign:"center"}}>Dismiss</button>
          </div>
        </div>
      )}

      {/* ── DATA HANDLING: DISCLOSURE + FIRST-USE CONSENT ── */}
      {(showConsent||showDisclosure)&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.72)",backdropFilter:"blur(3px)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"var(--oiq-surface2,#0c1120)",border:"1px solid var(--oiq-border,#1a2030)",borderRadius:12,width:"100%",maxWidth:720,maxHeight:"88vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid var(--oiq-border,#1a2030)"}}>
              <div style={{fontSize:15,fontWeight:800,color:"var(--oiq-ink,#F1F5F9)"}}>Where your data goes</div>
              <div style={{fontSize:10.5,color:"var(--oiq-muted,#5A6480)",marginTop:3,lineHeight:1.6}}>
                OrchestrIQ does not run its own AI models. What you type is sent to the third-party providers switched on in Settings, and handled under their terms, not ours.
              </div>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"14px 20px"}}>
              {activeProviderIds.map((pid:string)=>{
                const d=getDisclosure(pid); if(!d)return null;
                const t=trainingLabel(d.training);
                const col=t.tone==="good"?"#10B981":t.tone==="bad"?"#EF4444":"#F59E0B";
                return (
                  <div key={pid} style={{border:"1px solid var(--oiq-border,#1a2030)",borderRadius:8,padding:"11px 13px",marginBottom:9}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
                      <span style={{fontSize:12,fontWeight:800,color:"var(--oiq-ink,#F1F5F9)"}}>{d.name}</span>
                      <span style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:col+"22",color:col,fontWeight:700}}>{t.text}</span>
                      <span style={{marginLeft:"auto",fontSize:9,color:"var(--oiq-muted,#5A6480)"}}>checked {d.verified}</span>
                    </div>
                    <div style={{fontSize:10,color:"var(--oiq-muted,#8892B0)",lineHeight:1.65}}>
                      <div><strong>{d.company}</strong> &middot; processed in {d.dataResidency}</div>
                      <div style={{marginTop:3}}>{d.trainingNote}</div>
                      {d.disputed&&<div style={{marginTop:4,color:"#F59E0B"}}>&#9888; {d.disputed}</div>}
                      {d.regulatoryNote&&<div style={{marginTop:4,color:"#F59E0B"}}>&#9888; {d.regulatoryNote}</div>}
                      <div style={{marginTop:4}}><a href={d.policyUrl} target="_blank" rel="noopener noreferrer" style={{color:"#14B8A6"}}>Their privacy policy &#8599;</a></div>
                    </div>
                  </div>
                );
              })}
              <div style={{fontSize:9.5,color:"var(--oiq-muted,#5A6480)",lineHeight:1.7,marginTop:6}}>
                Do not paste anything you would not be willing to send to the companies named above. Where a stance is marked disputed or not published, read the provider&rsquo;s terms yourself &mdash; we summarise, we do not warrant. You can switch any provider off in Settings at any time.<br/><br/>
                This is a factual summary to help you make an informed choice. It is not legal advice, and provider terms can change without notice.
              </div>
            </div>
            <div style={{padding:"12px 20px",borderTop:"1px solid var(--oiq-border,#1a2030)",display:"flex",gap:8,justifyContent:"flex-end"}}>
              {showConsent
                ?<>
                  <button onClick={()=>{setShowConsent(false);setShowSettings(true);}} style={{background:"none",border:"1px solid var(--oiq-border,#1a2030)",borderRadius:6,padding:"8px 14px",color:"var(--oiq-muted,#A0AAC0)",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Change providers</button>
                  <button onClick={()=>{try{localStorage.setItem("oiq-data-consent",consentSignature);}catch{}setDataConsent(consentSignature);consentRef.current={needed:false,signature:consentSignature};recordConsent(activeProviderIds);setShowConsent(false);}} style={{background:"var(--oiq-accent,#14B8A6)",border:"none",borderRadius:6,padding:"8px 16px",color:"#04141B",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>I understand &mdash; continue</button>
                </>
                :<button onClick={()=>setShowDisclosure(false)} style={{background:"none",border:"1px solid var(--oiq-border,#1a2030)",borderRadius:6,padding:"8px 16px",color:"var(--oiq-muted,#A0AAC0)",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Close</button>}
            </div>
          </div>
        </div>
      )}
 
      {/* ── SETTINGS MODAL ── */}
      {showSettings&&(
        <div style={S.modalBg} onClick={()=>setShowSettings(false)}>
          <div style={{...S.modal,maxWidth:520}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><h2 style={{fontSize:16,fontWeight:800,color:"#F1F5F9"}}>Settings</h2><button onClick={()=>setShowSettings(false)} style={S.iBtn}>×</button></div>
            <div style={{display:"flex",flexWrap:"wrap",gap:3,marginBottom:14,paddingBottom:12,borderBottom:"1px solid #1a2030"}}>
              {/* THE FIX: "Donation" moved to Admin Console, as requested -
                  real money and bank details belong with every other
                  owner-level control, not tucked inside personal settings. */}
              {[["api","API"],["theme","Theme"],["company","Company"],["workspace","Workspace"],
                ["backup","Backup"],["danger","Reset"]].map(([id,lb])=><button key={id} onClick={()=>setSTab(id)} style={{padding:"5px 10px",borderRadius:5,fontSize:10,fontWeight:600,border:"1px solid "+(sTab===id?"#14B8A6":"#1a2030"),background:sTab===id?"rgba(20,184,166,0.08)":"transparent",color:sTab===id?"#14B8A6":"#5A6480",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{lb}</button>)}
            </div>
            {sTab==="api"&&(
              <div>
                <div style={{padding:"10px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030",marginBottom:12}}>
                  <label style={{...S.lbl,marginBottom:2}}>Response Quality</label>
                  <div style={{fontSize:8.5,color:"#5A6480",marginBottom:8,lineHeight:1.5}}>Controls which provider leads for demanding tasks (Excel, financial reports, audits, PowerPoint, research). The app always tries your best available key first and falls back automatically if it's missing or fails — this setting only changes what "best" means.</div>
                  <div style={{display:"flex",gap:4}}>
                    {[["standard","Standard","Free/cheapest first — everyday chat and drafts"],["professional","Professional","DeepSeek Pro / Claude — board-ready documents"],["excellent","Excellent","Claude / OpenAI lead — highest-stakes deliverables"]].map(([id,lb,desc])=>(
                      <button key={id} onClick={()=>changeQuality(id as any)} style={{flex:1,padding:"7px 6px",borderRadius:5,border:"1px solid "+(respQuality===id?"#14B8A6":"#1a2030"),background:respQuality===id?"rgba(20,184,166,0.1)":"transparent",cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left"}}>
                        <div style={{fontSize:10,fontWeight:700,color:respQuality===id?"#14B8A6":"#A0AAC0"}}>{lb}{respQuality===id?" ✓":""}</div>
                        <div style={{fontSize:7.5,color:"#5A6480",marginTop:2,lineHeight:1.4}}>{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{background:"rgba(16,185,129,0.05)",border:"1px solid rgba(16,185,129,0.2)",borderRadius:6,padding:"8px 10px",marginBottom:12,fontSize:10,color:"#10B981"}}>
                  Gemini is free — no billing needed. Get key at <a href={MODELS.gemini.keyUrl} target="_blank" rel="noopener noreferrer" style={{color:"#4285F4"}}>aistudio.google.com</a>
                </div>
                {/* A user who does not know what an API key is, or what the free
                    tier costs them, will not use either. This says both plainly. */}
                <div style={{background:"rgba(118,185,0,0.06)",border:"1px solid rgba(118,185,0,0.30)",borderRadius:7,padding:"11px 13px",marginBottom:14}}>
                  <div style={{fontSize:11.5,fontWeight:800,color:"#76B900",marginBottom:5}}>
                    Run this platform for free — no card, no bill
                  </div>
                  <div style={{fontSize:10,color:"#A0AAC0",lineHeight:1.6,marginBottom:9}}>
                    NVIDIA gives away access to 8 large models. It works right now with no key at all,
                    limited to 25 requests a day. Add your own free key and that limit disappears,
                    and you can generate PDFs, decks and spreadsheets. You are not billed for either.
                  </div>
                  <details>
                    <summary style={{fontSize:10,fontWeight:700,color:"#76B900",cursor:"pointer",marginBottom:6}}>
                      How to get your free NVIDIA key — about 3 minutes
                    </summary>
                    <ol style={{fontSize:9.5,color:"#A0AAC0",lineHeight:1.75,margin:"7px 0 0 16px",padding:0}}>
                      <li>Go to <a href="https://build.nvidia.com" target="_blank" rel="noopener noreferrer" style={{color:"#76B900"}}>build.nvidia.com</a> and sign up. An email address is all you need — no payment card.</li>
                      <li>Open any model on that page, for example Nemotron.</li>
                      <li>Click <b>Get API Key</b> on the right of the code panel.</li>
                      <li>Copy the key. It starts with <code style={{color:"#76B900"}}>nvapi-</code>.</li>
                      <li>Paste it into the NVIDIA box below. It saves by itself.</li>
                    </ol>
                    <div style={{fontSize:9,color:"#5A6480",lineHeight:1.65,marginTop:9,paddingTop:8,borderTop:"1px solid rgba(118,185,0,0.18)"}}>
                      <b style={{color:"#F59E0B"}}>What free actually costs you.</b> NVIDIA offers this tier
                      to improve its models, so prompts sent on it may be used for that. It is genuinely
                      free in money, not in privacy. For routine analysis that is a fair trade. For
                      anything commercially sensitive — unreleased figures, client names, a live deal —
                      use a paid provider below instead. Claude, OpenAI and Gemini do not train on API
                      traffic by default. Decide per piece of work, not once and forget.
                    </div>
                  </details>
                </div>
                {/* WAS filtered out entirely. NVIDIA was the only provider with no
                    key field, because the free tier runs on a shared key held in
                    Cloudflare that only the deployment owner can set. That left every
                    other user capped by the daily quota and unable to generate
                    documents at all. The field is OPTIONAL: leave it empty and the
                    free tier works exactly as before. */}
                {Object.entries(MODELS).map(([id,m])=>(
                  <div key={id} style={{marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:keys[id]?.trim()?m.color:"#3A4060"}}/>
                      <span style={{fontSize:11,fontWeight:700,color:"var(--oiq-ink)"}}>{m.name}</span>
                      {keys[id]?.trim()&&(
                        <button onClick={()=>{
                          const next={...offP,[id]:!offP[id]};
                          if(!next[id])delete next[id];
                          setOffP(next);
                          setOffProviders(next); // keep the gate in sync immediately
                          try{showToast(MODELS[id]?.name+(next[id]?" switched OFF — it will not be used or billed anywhere":" switched ON"),next[id]?"warning":"success");}catch{}
                          if(next[id]&&defP===id){
                            const fall=Array.from(new Set(["nvidia",...Object.keys(keys).filter(p=>isAiProviderKey(p)&&keys[p]?.trim()&&!next[p])]));
                            setDefP(fall[1]||"nvidia");
                          }
                        }}
                          title={offP[id]?"Switched off — key saved but not used":"Switched on"}
                          style={{width:34,height:18,borderRadius:999,border:"none",cursor:"pointer",padding:0,flexShrink:0,
                            background:offP[id]?"var(--oiq-border)":m.color,position:"relative",transition:"background .15s"}}>
                          <span style={{position:"absolute",top:2,left:offP[id]?2:18,width:14,height:14,borderRadius:"50%",background:"#fff",transition:"left .15s"}}/>
                        </button>
                      )}
                      {id==="gemini"&&<span style={{fontSize:8,color:"#10B981",fontWeight:700}}>FREE</span>}
                      <a href={m.keyUrl} target="_blank" rel="noopener noreferrer" style={{marginLeft:"auto",fontSize:9,color:m.color,textDecoration:"none"}}>Get key ↗</a>
                      {keys[id]?.trim()&&<button onClick={()=>testKey(id)} style={{...S.iBtn,fontSize:9,padding:"1px 6px"}}>{testSt[id]==="testing"?"…":testSt[id]==="ok"?"✓ OK":testSt[id]?.startsWith("fail:")?"✗ Fail":"Test"}</button>}
                    </div>
                    <input style={{...S.inp,fontSize:11,opacity:offP[id]?0.45:1}} type="password" value={keys[id]} onChange={e=>{const nk={...keys,[id]:e.target.value};setKeys(nk);sv("cos-keys",{keys:nk,defaultProvider:defP,multiAI});setTestSt(p=>({...p,[id]:undefined}));if(id==="claude"||id==="openai")saveBYOKeyToSupabase(e.target.value);supabase.auth.getUser().then(({data:{user}})=>{if(user)supabase.from("profiles").select("role").eq("id",user.id).single().then(({data:prof})=>{if(prof?.role==="super_admin")supabase.from("profiles").update({admin_api_keys:{...nk,defaultProvider:defP,multiAI}}).eq("id",user.id).catch(()=>{});});}).catch(()=>{});}} placeholder={m.placeholder}/>
                    {testSt[id]?.startsWith("fail:")&&<div style={{fontSize:9,color:"#EF4444",marginTop:2,lineHeight:1.4}}>{testSt[id].slice(5)}</div>}
                  </div>
                ))}
          <div style={{padding:"10px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030",marginBottom:10}}>
                  <label style={{...S.lbl,marginBottom:2}}>Web Search Service (optional, but strongly recommended)</label>
                  <div style={{fontSize:8.5,color:"#5A6480",marginBottom:8,lineHeight:1.5}}>
                    Without a key here, Claude or Gemini searches for you at roughly $10 per 1,000 searches, and decides for itself whether to show a source link.
                    With a key, the app searches directly, costs far less, and every finding keeps a real clickable URL. The first service with a key is used; the rest are fallbacks.
                  </div>
                  {SEARCH_PROVIDERS.filter(sp=>sp.id!=="native").map(sp=>(
                    <div key={sp.id} style={{marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                        <span style={{fontSize:10,fontWeight:700,color:"#A0AAC0"}}>{sp.label}</span>
                        <span style={{fontSize:8.5,color:"#10B981"}}>{sp.freeTier}</span>
                        <span style={{fontSize:8.5,color:"#5A6480"}}>{sp.costPer1000Usd!==null?("~$"+sp.costPer1000Usd+"/1k"):""}</span>
                        {sp.signupUrl&&<a href={sp.signupUrl} target="_blank" rel="noopener noreferrer" style={{marginLeft:"auto",fontSize:9,color:"#14B8A6",textDecoration:"none"}}>Get key ↗</a>}
                      </div>
                      <input style={{...S.inp,fontSize:11}} type="password" placeholder={sp.keyPlaceholder}
                        value={keys[sp.id]||""}
                        onChange={e=>{const nk={...keys,[sp.id]:e.target.value};setKeys(nk);sv("cos-keys",{keys:nk,defaultProvider:defP,multiAI});}}/>
                      <div style={{fontSize:8,color:"#3A4060",marginTop:2}}>{sp.notes}</div>
                    </div>
                  ))}
                </div>
                {/* WAS cfgP.length>1. If NVIDIA was your ONLY provider - no other
                    keys entered, which is exactly how a new user starts - cfgP had
                    one entry and this entire panel disappeared, taking the NVIDIA
                    model list with it. A single-provider user could not see or
                    change anything about the provider they were actually using. */}
                {cfgP.length>0&&(
                  <div style={{padding:"10px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030",marginBottom:10}}>
                    <label style={{...S.lbl,marginBottom:2}}>Primary AI</label>
                    <div style={{fontSize:8.5,color:"#5A6480",marginBottom:6,lineHeight:1.5}}>Your Primary AI handles all general reasoning, research, writing and orchestration. Specialist tasks are automatically routed to the best available model, then control returns to your Primary AI.</div>
                    <div style={{display:"flex",gap:4,marginBottom:8,flexWrap:"wrap"}}>
                      {cfgP.map(p=>{const pm=PROVIDER_META[p];return(
                        <button key={p} onClick={()=>{setDefP(p);sv("cos-keys",{keys,defaultProvider:p,multiAI});}} style={{flex:"1 1 110px",padding:"7px 6px",borderRadius:5,border:"1px solid "+(defP===p?(MODELS[p]?.color||"#14B8A6"):"#1a2030"),background:defP===p?((MODELS[p]?.color||"#14B8A6")+"15"):"transparent",cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left"}}>
                          <div style={{fontSize:10,fontWeight:700,color:defP===p?(MODELS[p]?.color||"#14B8A6"):"#A0AAC0"}}>{MODELS[p]?.name||p}{defP===p?" ✓":""}</div>
                          {pm&&<div style={{fontSize:7.5,color:"#5A6480",marginTop:2}}>Cost {pm.cost} · {pm.speed} · {pm.quality}</div>}
                          {pm&&<div style={{fontSize:7.5,color:"#5A6480",marginTop:1,lineHeight:1.4}}>{pm.blurb}</div>}
                          {p==="nvidia"&&<div style={{fontSize:7.5,color:"#76B900",marginTop:2,fontWeight:700}}>✨ Free tier · key optional</div>}
                        </button>);})}
                    </div>
                    {/* WAS defP==="nvidia". The model list only appeared when NVIDIA
                        was your PRIMARY AI. With DeepSeek primary and NVIDIA still in
                        the fallback chain - which is a completely normal setup - the
                        control vanished, and there was no way to see or change which
                        NVIDIA model you were on. NVIDIA is ALWAYS available (it needs
                        no key), so the list should always be reachable. */}
                    {cfgP.includes("nvidia")&&(
                      <div style={{marginBottom:8}}>
                        <label style={{...S.lbl,marginBottom:3}}>
                          NVIDIA model{defP!=="nvidia"?" (used for fallback and free-tier calls)":""}
                        </label>
                        <select value={keys.nvidiaModel||MODELS.nvidia.model} onChange={e=>{const nk={...keys,nvidiaModel:e.target.value};setKeys(nk);sv("cos-keys",{keys:nk,defaultProvider:defP,multiAI});}} style={{...S.inp,cursor:"pointer"}}>
                          {(nvLiveModels||NVIDIA_MODELS).map((m:any)=><option key={m.id} value={m.id}>{m.label + " — " + m.note}</option>)}
                        </select>
                        {nvLiveModels && (
                          <div style={{fontSize:8,color:"#22C55E",marginTop:3}}>
                            \u25CF Live from NVIDIA \u00b7 {nvLiveModels.length} models currently available
                          </div>
                        )}
                        {/* The dropdown label alone does not tell you what you are
                            running. NVIDIA models differ sharply: the 550B reasons
                            deeply but is slow enough to hit the 75-second proxy
                            timeout on long prompts, while llama-3.3-70b is fast and
                            does not reason at all. Showing the characteristics here
                            means the choice is informed rather than a guess. */}
                        {(()=>{const sel=NVIDIA_MODELS.find(m=>m.id===(keys.nvidiaModel||MODELS.nvidia.model));
                          return sel?<div style={{fontSize:8,color:"#5A6480",marginTop:3,lineHeight:1.45}}>
                            {sel.note}{/reasoning/.test(sel.note)?" \u00b7 slower on long prompts":" \u00b7 fast"}
                          </div>:null;})()}
                        {!nvLiveModels && (
                          <div style={{fontSize:8,color:"#5A6480",marginTop:3,lineHeight:1.45}}>
                            {NVIDIA_MODELS.length} models available, free, no key required.
                            {" "}Add your own free NVIDIA key for the complete live catalogue.
                          </div>
                        )}
                      </div>
                    )}
                    <label style={{...S.lbl,marginBottom:4}}>Automatic Specialist Routing</label>
                    <div style={{border:"1px solid #1a2030",borderRadius:5,overflow:"hidden",marginBottom:8}}>
                      {(()=>{
                        const hasK=(p)=>providerEnabled(keys,p);
                        const CAPS=[["General & Writing","general"],["Research","research"],["Code","code"],["Excel & Financial","excel_advanced"],["PowerPoint","powerpoint"],["Word / PDF","financial"],["Image Generation","image_gen"],["Video Generation","video_gen"]];
                        return CAPS.map(([label,task],i)=>{
                          const route=resolveRoute(task,defP);
                          const act=route.find(hasK);
                          const fb=route.filter(p=>p!==act&&hasK(p)).slice(0,2);
                          return(
                            <div key={task} style={{display:"flex",alignItems:"center",padding:"4px 8px",fontSize:8.5,background:i%2?"#0a0e1a":"transparent",borderBottom:i<CAPS.length-1?"1px solid #141a28":"none"}}>
                              <span style={{flex:1,color:"#A0AAC0",fontWeight:600}}>{label}</span>
                              <span style={{color:act?(MODELS[act]?.color||"#14B8A6"):"#EF4444",fontWeight:700}}>{act?(MODELS[act]?.name||act):"⚠ No provider"}</span>
                              {fb.length>0&&<span style={{color:"#3A4060",marginLeft:6}}>→ {fb.map(p=>MODELS[p]?.name||p).join(" → ")}</span>}
                            </div>);
                        });
                      })()}
                    </div>
                    <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
                      <input type="checkbox" checked={multiAI} onChange={e=>{setMultiAI(e.target.checked);sv("cos-keys",{keys,defaultProvider:defP,multiAI:e.target.checked});}} style={{accentColor:"#14B8A6"}}/>
                      <span style={{fontSize:11,color:"#A0AAC0"}}>Multi-AI Consensus Mode</span>
                    </label>
                  </div>
                )}
                <div style={{padding:"10px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030",marginBottom:10}}>
                  <label style={{...S.lbl,marginBottom:2}}>Cost Control &mdash; which model does which job</label>
                  <div style={{fontSize:8.5,color:"#5A6480",marginBottom:8,lineHeight:1.5}}>
                    Turning search results into bullets is copying &mdash; the cheapest model does it as well as the dearest one.
                    Ruling on nine conflicting executives is judgement, and there a stronger model earns its price.
                    Spend where the output is actually read.
                  </div>
                  <div style={{display:"flex",gap:4,marginBottom:9}}>
                    {Object.entries(PRESETS).map(([pid,pr]:any)=>{
                      const on=STAGES.every((s:any)=>stageProfile[s.id]===pr.stages[s.id]);
                      return (
                        <button key={pid} onClick={()=>{setStageProfileState({...pr.stages});setStageProfile(pr.stages);}}
                          style={{flex:1,padding:"7px 6px",borderRadius:5,border:"1px solid "+(on?"#14B8A6":"#1a2030"),background:on?"rgba(20,184,166,0.1)":"transparent",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                          <div style={{fontSize:10,fontWeight:700,color:on?"#14B8A6":"#A0AAC0"}}>{pr.label}{on?" ✓":""}</div>
                          <div style={{fontSize:7.5,color:"#5A6480",marginTop:2,lineHeight:1.4}}>{pr.note}</div>
                        </button>
                      );
                    })}
                  </div>
                  {STAGES.map((s:any)=>(
                    <div key={s.id} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:10,fontWeight:600,color:"#A0AAC0"}}>{s.label}</div>
                        <div style={{fontSize:7.5,color:"#3A4060"}}>{s.why}</div>
                      </div>
                      <select value={stageProfile[s.id]||""} onChange={e=>{const np={...stageProfile,[s.id]:e.target.value};setStageProfileState(np);setStageProfile(np);}}
                        style={{...S.inp,width:130,padding:"4px 6px",fontSize:10,cursor:"pointer",flexShrink:0}}>
                        {cfgP.filter((p:string)=>p!=="nvidia"||true).map((p:string)=>(
                          <option key={p} value={p}>{MODELS[p]?.name||p}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {(()=>{
                    const est=estimateSessionCost(stageProfile,{executives:brAg.length||4,researchOn:true},(id:string)=>providerEnabled(keys,id));
                    const estNoR=estimateSessionCost(stageProfile,{executives:brAg.length||4,researchOn:false},(id:string)=>providerEnabled(keys,id));
                    const fx=FX_RATES.USD||95;
                    return (
                      <div style={{marginTop:9,paddingTop:9,borderTop:"1px solid #1a2030"}}>
                        <div style={{fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:6}}>Estimated cost per board session &mdash; {brAg.length||4} executives</div>
                        {est.lines.map((l:any)=>(
                          <div key={l.stage} style={{display:"flex",fontSize:9.5,color:"#8892B0",padding:"2px 0"}}>
                            <span style={{flex:1}}>{l.label}</span>
                            <span style={{width:70,color:"#5A6480"}}>{MODELS[l.provider]?.name||l.provider}</span>
                            <span style={{width:70,textAlign:"right",color:"#A0AAC0"}}>{fmtMoney(l.usd,co.currency,cur.sym,fx)}</span>
                          </div>
                        ))}
                        <div style={{display:"flex",fontSize:9.5,color:"#8892B0",padding:"2px 0"}}>
                          <span style={{flex:1}}>Web search</span><span style={{width:70,color:"#5A6480"}}>serper</span>
                          <span style={{width:70,textAlign:"right",color:"#A0AAC0"}}>{fmtMoney(est.searchUsd,co.currency,cur.sym,fx)}</span>
                        </div>
                        <div style={{display:"flex",fontSize:12,fontWeight:800,color:"#14B8A6",paddingTop:6,marginTop:4,borderTop:"1px dashed #1a2030"}}>
                          <span style={{flex:1}}>Total per session</span>
                          <span>{fmtMoney(est.totalUsd,co.currency,cur.sym,fx)}</span>
                        </div>
                        <div style={{fontSize:8.5,color:"#5A6480",marginTop:4}}>
                          Without the Research Desk: {fmtMoney(estNoR.totalUsd,co.currency,cur.sym,fx)}. Estimates from your own measured sessions; actuals appear in Tokens.
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div style={{padding:"10px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030",marginBottom:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{flex:1}}>
                      <label style={{...S.lbl,marginBottom:2}}>Knowledge Library</label>
                      <div style={{fontSize:8.5,color:"#5A6480",lineHeight:1.5}}>
                        Keeps the sourced FACTS from each research run &mdash; never your question, your company or your figures &mdash;
                        so the next question about the same market starts from what is already established. Off by default.
                      </div>
                    </div>
                    <button onClick={()=>{const on=!libraryOn;setLibraryOn(on);setLibraryEnabled(on);showToast(on?"Knowledge Library ON — sourced market facts will be saved and reused":"Knowledge Library OFF — nothing will be saved",on?"success":"info");}}
                      style={{width:38,height:20,borderRadius:10,border:"none",background:libraryOn?"#14B8A6":"#1a2030",cursor:"pointer",position:"relative",flexShrink:0}}>
                      <span style={{position:"absolute",top:2,left:libraryOn?20:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left .2s"}}/>
                    </button>
                  </div>
                </div>
                <div style={{padding:"10px",background:"#0a0e1a",borderRadius:6,border:"1px solid #1a2030"}}>
                  <label style={{...S.lbl,marginBottom:6}}>Voice Language</label>
                  <select style={{...S.inp,padding:"8px"}} value={vLang} onChange={e=>{setVLang(e.target.value);sv("cos-vl",e.target.value);}}>
                    {VOICE_LANGS.map(l=><option key={l.code} value={l.code} style={{background:"#0a0e1a"}}>{l.label}</option>)}
                  </select>
                </div>
              </div>
            )}
            {sTab==="theme"&&(
              <DesignCentre onApplied={()=>{}} />
            )}
            {sTab==="workspace"&&(
              <div>
                <div style={{background:"rgba(20,184,166,0.04)",border:"1px solid rgba(20,184,166,0.15)",borderRadius:7,padding:"10px 12px",marginBottom:12,fontSize:10,color:"#A0AAC0",lineHeight:1.7}}>
                  Own your knowledge locally. Save your full workspace — conversations, dashboard data, boardroom sessions, workflows, and AI outputs with timestamps — to a file on your device. Return any time, load it, and continue seamlessly. No cloud required.
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <button onClick={()=>{exportAll();showToast("Workspace saved to your device","success");}} style={{...S.actBtn,borderColor:"#14B8A633",color:"#14B8A6"}}>💾 Save Workspace to Device</button>
                  <label style={{...S.actBtn,display:"flex",alignItems:"center",justifyContent:"center",gap:6,cursor:"pointer",borderColor:"#3B82F633",color:"#3B82F6"}}>
                    📂 Load Workspace from Device
                    <input type="file" accept=".json" onChange={e=>e.target.files[0]&&importData(e.target.files[0])} style={{display:"none"}}/>
                  </label>
                </div>
                <div style={{marginTop:14,fontSize:9,fontWeight:700,color:"#5A6480",textTransform:"uppercase",letterSpacing:0.8,marginBottom:6}}>Currently Stored</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6}}>
                  {[["Conversations",Object.values(chats).filter(c=>c?.length).length],["Data points",Object.keys(compData).length],["Ledger entries",ledgerEntries.length],["Custom accounts",customAccounts.length],["Boardroom sessions",brSessions.length],["Workflows",workflows.length],["Queue tasks",tQueue.length]].map(([lb,n])=>(
                    <div key={lb} style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:5,padding:"8px 10px",display:"flex",justifyContent:"space-between"}}><span style={{fontSize:10,color:"#8892B0"}}>{lb}</span><span style={{fontSize:10,fontWeight:700,color:"#14B8A6"}}>{n}</span></div>
                  ))}
                </div>
                <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid #1a2030"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#5A6480",textTransform:"uppercase",marginBottom:8}}>Admin - Section Visibility</div>
                  {[["ledgerEnabled","General Ledger"],["dispatchEnabled","Pulse Agentic"],["actionsEnabled","Action Tracker"]].map(([key,lb])=>(
                    <div key={key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0"}}>
                      <span style={{fontSize:11,color:"#A0AAC0"}}>{lb}</span>
                      <button onClick={()=>{const nc={...adminConfig,[key]:!adminConfig[key]};setAdminConfig(nc);sv("cos-admin-config",nc);showToast(lb+(nc[key]?" enabled":" disabled"),"info");}} style={{width:38,height:20,borderRadius:10,border:"none",background:adminConfig[key]?"#14B8A6":"#1a2030",cursor:"pointer",position:"relative",transition:"background 0.2s"}}>
                        <span style={{position:"absolute",top:2,left:adminConfig[key]?20:2,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sTab==="company"&&(
              <div>
                {[["name","Company Name"],["industry","Industry"],["location","HQ Location"],["markets","Target Markets"]].map(([f,lb])=>(
                  <div key={f} style={{marginBottom:8}}>
                    <label style={S.lbl}>{lb}</label>
                    <input style={S.inp} value={co[f]||""} onChange={e=>{const n={...co,[f]:e.target.value};setCo(n);sv("cos-co",n);}} placeholder={lb}/>
                  </div>
                ))}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div><label style={S.lbl}>Currency</label><select style={{...S.inp,padding:"8px"}} value={co.currency} onChange={e=>{const n={...co,currency:e.target.value};setCo(n);sv("cos-co",n);}}>{CURRENCIES.map(c=><option key={c.code} value={c.code} style={{background:"#0a0e1a"}}>{c.sym} {c.code}</option>)}</select></div>
                  <div><label style={S.lbl}>Stage</label><select style={{...S.inp,padding:"8px"}} value={co.stage} onChange={e=>{const n={...co,stage:e.target.value};setCo(n);sv("cos-co",n);}}>{STAGES.map(st=><option key={st.id} value={st.id} style={{background:"#0a0e1a"}}>{st.ic} {st.l}</option>)}</select></div>
                </div>
              </div>
            )}
            {sTab==="backup"&&(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <button onClick={exportAll} style={S.actBtn}>Export All Data (JSON)</button>
                <label style={{...S.actBtn,display:"flex",alignItems:"center",justifyContent:"center",gap:6,cursor:"pointer"}}>
                  Import Data
                  <input type="file" accept=".json" onChange={e=>e.target.files[0]&&importData(e.target.files[0])} style={{display:"none"}}/>
                </label>
                <p style={{fontSize:10,color:"#5A6480",lineHeight:1.6}}>Exports all chats, data, sessions, workflows, and task queue to a JSON file.</p>
              </div>
            )}
            {sTab==="danger"&&(
              <div>
                <p style={{fontSize:11,color:"#8892B0",marginBottom:10,lineHeight:1.6}}>These actions are irreversible. Your data is stored securely on our servers, not just this device — deleting it here removes it everywhere, permanently.</p>
                {confirmReset===null&&(
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <button onClick={()=>setConfirmReset("data")} style={{...S.actBtn,borderColor:"#F59E0B33",color:"#F59E0B"}}>Reset Chats and Data (keep API keys)</button>
                    <button onClick={()=>setConfirmReset("full")} style={{...S.actBtn,borderColor:"#EF444433",color:"#EF4444"}}>Full Reset — Delete Everything and Log Out</button>
                  </div>
                )}
                {confirmReset==="data"&&(
                  <div style={{background:"rgba(245,158,11,0.05)",border:"1px solid #F59E0B33",borderRadius:6,padding:10}}>
                    <p style={{fontSize:11,color:"#F59E0B",marginBottom:8,fontWeight:600}}>Clears all chats, data, sessions. API keys are kept.</p>
                    <div style={{display:"flex",gap:4}}>
                      <button onClick={resetData} style={{...S.actBtn,background:"#F59E0B",color:"#0a0e1a",border:"none",flex:1}}>Yes, Reset Data</button>
                      <button onClick={()=>setConfirmReset(null)} style={{...S.actBtn,flex:1}}>Cancel</button>
                    </div>
                  </div>
                )}
                {confirmReset==="full"&&(
                  <div style={{background:"rgba(239,68,68,0.05)",border:"1px solid #EF444433",borderRadius:6,padding:10}}>
                    <p style={{fontSize:11,color:"#EF4444",marginBottom:8,fontWeight:600}}>Removes EVERYTHING. You will be logged out immediately.</p>
                    <div style={{display:"flex",gap:4}}>
                      <button onClick={fullReset} style={{...S.actBtn,background:"#EF4444",color:"#fff",border:"none",flex:1}}>Yes, Wipe Everything</button>
                      <button onClick={()=>setConfirmReset(null)} style={{...S.actBtn,flex:1}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showSignOutConfirm&&(
        <div style={S.modalBg} onClick={()=>setShowSignOutConfirm(false)}>
          <div style={{...S.modal,maxWidth:420,textAlign:"center"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:32,marginBottom:8}}>⎋</div>
            <h2 style={{fontSize:16,fontWeight:800,color:"#F1F5F9",marginBottom:8}}>Sign Out</h2>
            <p style={{fontSize:12,color:"#8892B0",marginBottom:18,lineHeight:1.6}}>
              Signing out clears anything stored only on this device — like the API keys you've typed in here — so the next person on a shared computer can't see them. Anything already saved (your projects, ledger entries, conversations) is safe on our servers and will be there when you sign back in. "Save Workspace" downloads a personal backup file first, in case you want one.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <button onClick={()=>handleSignOut(true)} style={{...S.pBtn,marginTop:0,background:"#14B8A6"}}>Save Workspace, Then Sign Out</button>
              <button onClick={()=>handleSignOut(false)} style={{...S.pBtn,marginTop:0,background:"transparent",border:"1px solid #EF444466",color:"#EF4444"}}>Sign Out Without Saving</button>
              <button onClick={()=>setShowSignOutConfirm(false)} style={{background:"none",border:"none",color:"#5A6480",fontSize:12,cursor:"pointer",fontFamily:"Manrope,sans-serif",marginTop:4}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {extractModal&&<ExtractReviewModal extracted={extractModal.items} sourceType={extractModal.sourceType} sourceLabel={extractModal.sourceLabel} onConfirm={confirmExtractedItems} onCancel={()=>setExtractModal(null)} AR={AR} S={S}/>}
      {showDonate&&<DonateModal cfg={dnCfg} presets={DONATION_PRESETS} onClose={()=>setShowDonate(false)} cur={cur} amt={dnAmt} setAmt={setDnAmt} custom={dnCustom} setCustom={setDnCustom} S={S}/>}
      {/* Every signed-in user can reach this, on every plan. A customer who
          cannot report a problem is a customer you lose without ever knowing
          why - so this is deliberately not gated by any subscription. */}
      <button onClick={()=>setShowHelp(true)} title="Help and support"
        style={{position:"fixed",right:16,bottom:74,zIndex:8000,width:42,height:42,
          borderRadius:"50%",border:"1px solid rgba(20,184,166,0.45)",
          background:"#0F1420",color:"#14B8A6",fontSize:17,fontWeight:800,
          cursor:"pointer",boxShadow:"0 4px 14px rgba(0,0,0,0.45)"}}>?</button>
      <SupportWidget open={showHelp} onClose={()=>setShowHelp(false)}/>
      <ExportAudienceDialog/>
      <TokenBadge defP={defP} setDefP={p=>{setDefP(p);sv("cos-keys",{keys,defaultProvider:p,multiAI});}} keys={keys} onOpen={()=>{setView("tokens");}} />
      <Toaster toasts={toasts} onDismiss={id=>setToasts(prev=>prev.filter(t=>t.id!==id))}/>
      <style>{CSS}</style>
      </div>{/* end oiq-body-row */}
    </div>
  );
}

function DonateModal({cfg,presets,onClose,cur,amt,setAmt,custom,setCustom,S}){
  const [thanked,setThanked]=useState(false);
  const finalAmt=custom?parseInt(custom)||0:amt;
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(3px)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:"#131825",border:"1px solid #1a2030",borderRadius:12,padding:"20px 22px",width:"100%",maxWidth:420,maxHeight:"92vh",overflowY:"auto",textAlign:"center",fontFamily:"Manrope,sans-serif"}} onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:36,marginBottom:6}}>💙</div>
        <h2 style={{fontSize:18,fontWeight:800,color:"#F1F5F9",marginBottom:4}}>Support {BRAND}</h2>
        <p style={{fontSize:12,color:"#8892B0",marginBottom:16,lineHeight:1.6}}>{cfg.note||"Your contribution helps keep this project free and improving."}</p>
        {cfg.ownerName&&<p style={{fontSize:11,color:"#5A6480",marginBottom:16}}>Beneficiary: <strong style={{color:"#F1F5F9"}}>{cfg.ownerName}</strong></p>}
        {cfg.qrImage&&(
          <div style={{marginBottom:16}}>
            <img src={cfg.qrImage} alt="Donation QR code" style={{width:180,height:180,objectFit:"contain",background:"#fff",borderRadius:10,padding:8,maxWidth:"100%"}}/>
            <div style={{fontSize:10,color:"#5A6480",marginTop:6}}>Scan to pay</div>
          </div>
        )}
        <div style={{display:"flex",flexWrap:"wrap",gap:6,justifyContent:"center",marginBottom:10}}>
          {presets.map(p=><button key={p} onClick={()=>{setAmt(p);setCustom("");}} style={{padding:"8px 14px",borderRadius:6,fontSize:12,fontWeight:700,border:"1px solid "+(amt===p&&!custom?"#14B8A6":"#1a2030"),background:amt===p&&!custom?"rgba(20,184,166,0.1)":"#0a0e1a",color:amt===p&&!custom?"#14B8A6":"#A0AAC0",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>{cur.sym}{p}</button>)}
        </div>
        <input style={{width:"100%",background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:6,padding:"9px 11px",color:"#F1F5F9",fontSize:12,fontFamily:"Manrope,sans-serif",textAlign:"center",marginBottom:14,boxSizing:"border-box"}} value={custom} onChange={e=>setCustom(e.target.value.replace(/\D/,""))} placeholder="Or enter custom amount…"/>
        <div style={{fontSize:13,fontWeight:700,color:"#F1F5F9",marginBottom:14}}>Total: {cur.sym}{finalAmt}</div>
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {cfg.upiId&&<a href={"upi://pay?pa="+cfg.upiId+"&pn="+encodeURIComponent(cfg.ownerName||BRAND)+"&am="+finalAmt+"&cu=INR"} style={{display:"block",background:"#14B8A6",color:"#0a0e1a",borderRadius:7,padding:"11px",fontSize:13,fontWeight:700,textDecoration:"none"}}>Pay via UPI ({cfg.upiId})</a>}
          {cfg.accountNo&&(
            <div style={{background:"#0a0e1a",border:"1px solid #1a2030",borderRadius:7,padding:"12px",textAlign:"left",fontSize:11,color:"#A0AAC0",lineHeight:1.8}}>
              <div style={{fontWeight:700,color:"#F1F5F9",marginBottom:4}}>Bank Transfer (NEFT / IMPS / RTGS)</div>
              {cfg.bankName&&<div>Bank: <strong style={{color:"#F1F5F9"}}>{cfg.bankName}</strong></div>}
              <div>Account: <strong style={{color:"#F1F5F9"}}>{cfg.accountNo}</strong></div>
              {cfg.ifsc&&<div>IFSC: <strong style={{color:"#F1F5F9"}}>{cfg.ifsc}</strong></div>}
              {cfg.accountType&&<div>Type: <strong style={{color:"#F1F5F9"}}>{cfg.accountType}</strong></div>}
            </div>
          )}
          {cfg.paypalMe&&<a href={"https://paypal.me/"+cfg.paypalMe.replace("paypal.me/","")+"/"+finalAmt} target="_blank" rel="noopener noreferrer" style={{display:"block",background:"#003087",color:"#fff",borderRadius:7,padding:"11px",fontSize:13,fontWeight:700,textDecoration:"none"}}>Pay via PayPal</a>}
          {cfg.stripeLink&&<a href={cfg.stripeLink} target="_blank" rel="noopener noreferrer" style={{display:"block",background:"#635BFF",color:"#fff",borderRadius:7,padding:"11px",fontSize:13,fontWeight:700,textDecoration:"none"}}>Pay via Card (Stripe)</a>}
          {!cfg.upiId&&!cfg.accountNo&&!cfg.paypalMe&&!cfg.stripeLink&&!cfg.qrImage&&(
            <div style={{background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:7,padding:"12px",fontSize:11,color:"#F59E0B"}}>
              Configure payment methods in Settings › Donation tab.
            </div>
          )}
        </div>
        {(cfg.upiId||cfg.accountNo||cfg.paypalMe||cfg.stripeLink||cfg.qrImage)&&(
          thanked
            ?<div style={{marginTop:14,fontSize:12,color:"#10B981",fontWeight:600}}>🙏 Thank you for your support!</div>
            :<button onClick={()=>setThanked(true)} style={{marginTop:14,background:"none",border:"1px solid #1a2030",borderRadius:6,padding:"7px 14px",color:"#5A6480",fontSize:10,cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>I've donated — mark as complete</button>
        )}
        {cfg.ownerEmail&&<p style={{fontSize:10,color:"#3A4060",marginTop:14}}>Questions? <a href={"mailto:"+cfg.ownerEmail} style={{color:"#14B8A6"}}>{cfg.ownerEmail}</a></p>}
        <button onClick={onClose} style={{background:"none",border:"none",color:"#5A6480",fontSize:12,cursor:"pointer",marginTop:12,fontFamily:"Manrope,sans-serif"}}>Close</button>
      </div>
    </div>
  );
}

const CSS=`@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800;900&display=swap');
:root{--bg:#0a0e1a;--bg2:#0c1120;--panel:#131825;--panel2:#0a0e1a;--border:#1a2030;--border2:#14192a;--text:#F1F5F9;--text2:#A0AAC0;--text3:#8892B0;--muted:#5A6480;--muted2:#3A4060;--accent:#14B8A6;--code:#080c18;--scroll:#1a2030;}
*{box-sizing:border-box;margin:0;padding:0}
@keyframes pulse{0%,100%{opacity:.2}50%{opacity:1}}
@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
textarea:focus,input:focus,select:focus{outline:none;border-color:var(--accent)!important}
::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--scroll);border-radius:3px}
button:hover{filter:brightness(1.1)}select{appearance:auto}html,body{background:var(--bg)}a{color:var(--accent)}
/* THEME BRIDGE: map legacy hex inline-styles onto theme variables, non-destructively */
[style*="#0a0e1a"]{}
.oiq-themed{transition:background-color .25s,color .25s,border-color .25s}
/* P6: Reduce ticker height */
#oiq-root .global-ticker,#oiq-root [class*="ticker"]{transform:scaleY(0.88);transform-origin:top;}
#oiq-root header+*{margin-top:-3px;}`;
